// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseURL: "http://127.0.0.1:8000" | (string & {});
};

/**
 * AIJobHandle
 *
 * Handle for a deferred AI operation; poll the jobs endpoint for the result.
 */
export type AiJobHandle = {
  /**
   * Job Id
   */
  job_id: number;
};

/**
 * AIJobStatusResponse
 */
export type AiJobStatusResponse = {
  /**
   * Status
   */
  status: string;
  /**
   * Result
   */
  result?: {
    [key: string]: unknown;
  } | null;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Identifier
   */
  identifier?: string | null;
};

/**
 * ActionToolConfig
 *
 * Configuration for hardcoded values and exposed parameters in action tools.
 *
 * For each config parameter or input connector, you either:
 * 1. Hardcode it (store value in overrides, don't expose to LLM)
 * 2. Expose it to LLM (add to exposed list, LLM provides value)
 */
export type ActionToolConfig = {
  /**
   * Config Overrides
   */
  config_overrides?: {
    [key: string]: unknown;
  };
  /**
   * Input Overrides
   */
  input_overrides?: {
    [key: string]: unknown;
  };
  /**
   * Exposed Configs
   */
  exposed_configs?: Array<string>;
  /**
   * Exposed Inputs
   */
  exposed_inputs?: Array<string>;
};

/**
 * ActionToolSettings
 */
export type ActionToolSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "action";
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Node Name
   *
   * The name of the node/action to execute
   */
  node_name: string;
  tool_config?: ActionToolConfig | null;
};

/**
 * ActionToolSettings
 */
export type ActionToolSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "action";
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Node Name
   *
   * The name of the node/action to execute
   */
  node_name: string;
  tool_config?: ActionToolConfig | null;
};

/**
 * AddColumnRequest
 */
export type AddColumnRequest = {
  column: DataTableColumn;
};

/**
 * AddLimitRequest
 *
 * Request to update limits.
 */
export type AddLimitRequest = {
  /**
   * Limit
   */
  limit:
    | ({
        metric: "operations";
      } & OperationsLimit)
    | ({
        metric: "provider";
      } & ProviderCostLimit);
};

/**
 * AddMessageRequest
 */
export type AddMessageRequest = {
  /**
   * Content
   */
  content: string | null;
  /**
   * Tool
   */
  tool?: "web_research" | "kb_qa" | string | null;
  /**
   * Kb Id
   */
  kb_id?: string | null;
  /**
   * Files
   */
  files?: Array<ConversationFile> | null;
  /**
   * Model Selection
   */
  model_selection?: Array<string> | null;
};

/**
 * AddProviderRegionRequest
 */
export type AddProviderRegionRequest = {
  /**
   * Region
   */
  region: string;
};

/**
 * AddSecretRequest
 */
export type AddSecretRequest = {
  /**
   * Value
   */
  value: string;
};

/**
 * AddUserToGroupRequest
 */
export type AddUserToGroupRequest = {
  /**
   * User Id
   */
  user_id: string;
  /**
   * Role Id
   */
  role_id?: string | null;
};

/**
 * AdminCreateTenantRequest
 */
export type AdminCreateTenantRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Admin Email
   */
  admin_email: string;
  /**
   * Admin Display Name
   */
  admin_display_name?: string | null;
  /**
   * Domain
   */
  domain?: string | null;
  /**
   * Reserve Domain
   */
  reserve_domain?: boolean;
  /**
   * Metadata
   */
  metadata_?: {
    [key: string]: unknown;
  } | null;
};

/**
 * AdminDashboardStats
 */
export type AdminDashboardStats = {
  stats: AdminStats;
  /**
   * Recent Activity
   */
  recent_activity: Array<RecentActivity>;
};

/**
 * AdminDeleteTenantRequest
 */
export type AdminDeleteTenantRequest = {
  /**
   * Tenant Id
   */
  tenant_id: string;
  /**
   * Confirmation
   */
  confirmation: "DELETE";
};

/**
 * AdminDeleteTenantResponse
 */
export type AdminDeleteTenantResponse = {
  /**
   * Job Id
   */
  job_id: number;
  /**
   * Message
   */
  message: string;
};

/**
 * AdminIntegrationNodeInfo
 */
export type AdminIntegrationNodeInfo = {
  /**
   * Node Name
   */
  node_name: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Category
   */
  category: string;
  /**
   * Image
   */
  image: string | null;
  /**
   * Admin Enabled
   */
  admin_enabled: boolean;
  /**
   * Visible
   */
  visible: boolean;
};

/**
 * AdminIntegrationTriggerInfo
 */
export type AdminIntegrationTriggerInfo = {
  /**
   * Trigger Name
   */
  trigger_name: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Image
   */
  image: string | null;
  /**
   * Admin Enabled
   */
  admin_enabled: boolean;
  /**
   * Visible
   */
  visible: boolean;
};

/**
 * AdminInviteUserRequest
 */
export type AdminInviteUserRequest = {
  /**
   * Email
   */
  email: string;
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Role
   */
  role?: string;
  /**
   * Workspace Id
   */
  workspace_id?: string | null;
  /**
   * Workspace Role Id
   */
  workspace_role_id?: string | null;
  /**
   * Create Personal Workspace
   */
  create_personal_workspace?: boolean;
  /**
   * Send Email
   */
  send_email?: boolean;
};

/**
 * AdminStats
 */
export type AdminStats = {
  /**
   * Total Tenants
   */
  total_tenants: number;
  /**
   * Total Users
   */
  total_users: number;
  /**
   * Active Tenants
   */
  active_tenants: number;
  /**
   * Total Workflows
   */
  total_workflows: number;
  /**
   * Total Runs Last 7 Days
   */
  total_runs_last_7_days: number;
};

/**
 * AgentChanges
 */
export type AgentChanges = {
  /**
   * Changes
   */
  changes: Array<
    | ({
        kind: "value";
      } & ValueChange)
    | ({
        kind: "text";
      } & TextChange)
    | ({
        kind: "list";
      } & ListChange)
  >;
  /**
   * Capabilities
   */
  capabilities?: Array<CapabilityMove>;
};

/**
 * AgentChangesRequest
 *
 * A change to an agent's name or settings, previewed before it is made, in
 * the shape ``agents_update`` takes it.
 */
export type AgentChangesRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Image Url
   */
  image_url?: string | null;
  /**
   * Patch
   */
  patch?: {
    [key: string]: JsonValueInput;
  } | null;
  /**
   * Preview
   */
  preview?: boolean;
  /**
   * Pins
   */
  pins?: {
    [key: string]: number;
  } | null;
};

/**
 * AgentDatabaseConfig
 *
 * A SQL database, referenced by stored credential id, the agent can query
 * read-only. ``selected_tables=None`` means every table is visible.
 */
export type AgentDatabaseConfigInput = {
  /**
   * Credential Id
   */
  credential_id: string;
  context?: NoxusTipTapDocumentInput | null;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Selected Tables
   */
  selected_tables?: Array<string> | null;
  /**
   * Max Rows
   */
  max_rows?: number;
  /**
   * Query Timeout Seconds
   */
  query_timeout_seconds?: number;
};

/**
 * AgentDatabaseConfig
 *
 * A SQL database, referenced by stored credential id, the agent can query
 * read-only. ``selected_tables=None`` means every table is visible.
 */
export type AgentDatabaseConfigOutput = {
  /**
   * Credential Id
   */
  credential_id: string;
  context?: NoxusTipTapDocumentOutput | null;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Selected Tables
   */
  selected_tables?: Array<string> | null;
  /**
   * Max Rows
   */
  max_rows?: number;
  /**
   * Query Timeout Seconds
   */
  query_timeout_seconds?: number;
};

/**
 * AgentFileConfig
 *
 * A file pre-loaded into every chat of an agent, with context.
 *
 * Stored directly in the agent's ``definition`` JSONB column. The file is
 * uploaded in the agent settings (``file.uri`` is a ``spot://`` reference);
 * ``context`` is shown to the agent so it knows what the file is and when to
 * read it via the always-on session-file tools.
 */
export type AgentFileConfigInput = {
  file: FileInput;
  context?: NoxusTipTapDocumentInput | null;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Delimiter
   */
  delimiter?: string | null;
};

/**
 * AgentFileConfig
 *
 * A file pre-loaded into every chat of an agent, with context.
 *
 * Stored directly in the agent's ``definition`` JSONB column. The file is
 * uploaded in the agent settings (``file.uri`` is a ``spot://`` reference);
 * ``context`` is shown to the agent so it knows what the file is and when to
 * read it via the always-on session-file tools.
 */
export type AgentFileConfigOutput = {
  file: NoxusTypesFile;
  context?: NoxusTipTapDocumentOutput | null;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Delimiter
   */
  delimiter?: string | null;
};

/**
 * AgentLanguage
 *
 * One language the agent greets in, keyed by ISO 639-1 code.
 */
export type AgentLanguageInput = {
  /**
   * Code
   */
  code: string;
  /**
   * Introductory Message
   */
  introductory_message?: Array<NoxusTipTapDocumentInput> | null;
};

/**
 * AgentLanguage
 *
 * One language the agent greets in, keyed by ISO 639-1 code.
 */
export type AgentLanguageOutput = {
  /**
   * Code
   */
  code: string;
  /**
   * Introductory Message
   */
  introductory_message?: Array<NoxusTipTapDocumentOutput> | null;
};

/**
 * AgentMemoryEntry
 *
 * A single persistent memory entry stored in agent settings.
 */
export type AgentMemoryEntry = {
  /**
   * Id
   */
  id: string;
  /**
   * Content
   */
  content: string;
  /**
   * Category
   */
  category?: string;
  /**
   * Created At
   */
  created_at?: string;
  /**
   * Updated At
   */
  updated_at?: string;
};

/**
 * AgentMemorySettings
 *
 * Settings for the persistent agent memory toolset.
 *
 * Memories are stored in assistant.definition["memories"] JSONB
 * and persist across all conversations with this agent.
 */
export type AgentMemorySettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "agent_memory";
};

/**
 * AgentMemorySettings
 *
 * Settings for the persistent agent memory toolset.
 *
 * Memories are stored in assistant.definition["memories"] JSONB
 * and persist across all conversations with this agent.
 */
export type AgentMemorySettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "agent_memory";
};

/**
 * AgentTableConfig
 *
 * A single workspace custom table the agent can query read-only, with its
 * own context and limits. Queries run as the workspace's read-only tenant role
 * and are restricted to this table.
 */
export type AgentTableConfigInput = {
  /**
   * Table Id
   */
  table_id: string;
  context?: NoxusTipTapDocumentInput | null;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Read Only
   */
  read_only?: boolean;
  /**
   * Max Rows
   */
  max_rows?: number;
  /**
   * Query Timeout Seconds
   */
  query_timeout_seconds?: number;
};

/**
 * AgentTableConfig
 *
 * A single workspace custom table the agent can query read-only, with its
 * own context and limits. Queries run as the workspace's read-only tenant role
 * and are restricted to this table.
 */
export type AgentTableConfigOutput = {
  /**
   * Table Id
   */
  table_id: string;
  context?: NoxusTipTapDocumentOutput | null;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Read Only
   */
  read_only?: boolean;
  /**
   * Max Rows
   */
  max_rows?: number;
  /**
   * Query Timeout Seconds
   */
  query_timeout_seconds?: number;
};

/**
 * AgentTool
 */
export type AgentTool = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Parameters
   */
  parameters: Array<AgentToolParameter>;
};

/**
 * AgentToolMetadata
 */
export type AgentToolMetadata = {
  /**
   * Type
   */
  type?: "agent";
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Display Message Start
   */
  display_message_start?: string | null;
  /**
   * Display Message End
   */
  display_message_end?: string | null;
  /**
   * Agent Id
   */
  agent_id: string;
  /**
   * Agent Name
   */
  agent_name: string;
  /**
   * Sub Conversation Id
   */
  sub_conversation_id?: string | null;
};

/**
 * AgentToolParameter
 */
export type AgentToolParameter = {
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type?: string | null;
};

/**
 * AgentToolSettings
 */
export type AgentToolSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "agent_tool";
  /**
   * Agent Id
   *
   * The ID of the agent to call. Comes unselected, and should be selected by the user.
   */
  agent_id: string;
  /**
   * Version Id
   */
  version_id?: string | null;
  /**
   * Max Clarification Turns
   *
   * How many times this co-worker can ask follow-up questions before giving a final answer. Set to 0 to require an immediate response with no back-and-forth.
   */
  max_clarification_turns?: number;
};

/**
 * AgentToolSettings
 */
export type AgentToolSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "agent_tool";
  /**
   * Agent Id
   *
   * The ID of the agent to call. Comes unselected, and should be selected by the user.
   */
  agent_id: string;
  /**
   * Version Id
   */
  version_id?: string | null;
  /**
   * Max Clarification Turns
   *
   * How many times this co-worker can ask follow-up questions before giving a final answer. Set to 0 to require an immediate response with no back-and-forth.
   */
  max_clarification_turns?: number;
};

/**
 * AgentToolVersionUsage
 */
export type AgentToolVersionUsage = {
  /**
   * Assistant Id
   */
  assistant_id: string;
  /**
   * Assistant Name
   */
  assistant_name: string;
  /**
   * Assistant Icon
   */
  assistant_icon: string | null;
  /**
   * Version Id
   */
  version_id: string | null;
  /**
   * Version Name
   */
  version_name: string | null;
};

/**
 * AnalyticsBarChartValue
 */
export type AnalyticsBarChartValue = {
  /**
   * Name
   */
  name: string;
  /**
   * Values
   */
  values: {
    [key: string]: number | number;
  };
  /**
   * Value Mapping
   */
  value_mapping?: {
    [key: string]: AnalyticsWorkflow | AnalyticsUser | AnalyticsKb;
  } | null;
};

/**
 * AnalyticsBarChartsValue
 */
export type AnalyticsBarChartsValue = {
  /**
   * Values
   */
  values: Array<AnalyticsBarChartValue>;
};

/**
 * AnalyticsChip
 */
export type AnalyticsChip = {
  /**
   * Value
   */
  value: string | number | number;
  /**
   * Type
   */
  type:
    | "success"
    | "outline"
    | "destructive"
    | "warning"
    | "default"
    | "secondary";
};

/**
 * AnalyticsCostValue
 */
export type AnalyticsCostValue = {
  /**
   * Value
   */
  value: number;
  /**
   * Estimated Additional Cost
   */
  estimated_additional_cost: number;
  /**
   * Estimated In Progress Cost
   */
  estimated_in_progress_cost: number;
  /**
   * Estimated Interrupted Cost
   */
  estimated_interrupted_cost: number;
  /**
   * Estimated Other Cost
   */
  estimated_other_cost: number;
  /**
   * Message
   */
  message: string;
  /**
   * Currency
   *
   * Currency of every amount in this value, already converted
   */
  currency: "USD" | "EUR" | "GBP";
  /**
   * Currency Note
   */
  currency_note?: string | null;
};

/**
 * AnalyticsKB
 */
export type AnalyticsKb = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * AnalyticsKey
 */
export const AnalyticsKey = {
  EMPTY: "empty",
  FLOW_RUNS: "flow_runs",
  TRIGGER_RUNS: "trigger_runs",
  MANUAL_RUNS: "manual_runs",
  AGENT_RUNS: "agent_runs",
  EVALUATION_RUNS: "evaluation_runs",
  ACTIVE_USERS: "active_users",
  AI_MODELS_TOTAL_COST: "ai_models_total_cost",
  ACTIVE_RUNS: "active_runs",
  ACTIVE_TRIGGERS: "active_triggers",
  ERRORS: "errors",
  AVERAGE_COST_PER_RUN: "average_cost_per_run",
  AVERAGE_RUN_TIME: "average_run_time",
  FLOW_RUNS_DISTRIBUTION: "flow_runs_distribution",
  FLOW_PER_USER: "flow_per_user",
  ERRORS_OVER_TIME: "errors_over_time",
  RUN_TIME_OVER_TIME: "run_time_over_time",
  TOKENS_COST: "tokens_cost",
  TOKENS_COST_BY_WORKFLOW: "tokens_cost_by_workflow",
  TOKENS_COST_BY_USER: "tokens_cost_by_user",
  TOKENS_COST_BY_NODE: "tokens_cost_by_node",
  CONVERSATIONS_STARTED: "conversations_started",
  MESSAGES_SENT: "messages_sent",
  MESSAGES_PER_CONVERSATION: "messages_per_conversation",
  CONVERSATION_USERS: "conversation_users",
  CONVERSATIONS_STARTED_OVER_TIME: "conversations_started_over_time",
  MESSAGES_SENT_OVER_TIME: "messages_sent_over_time",
  CONVERSATIONS_STARTED_BY_USER: "conversations_started_by_user",
  MESSAGES_SENT_BY_USER: "messages_sent_by_user",
  CONVERSATION_TOKENS_COST: "conversation_tokens_cost",
  CONVERSATION_TOKENS_COST_BY_USER: "conversation_tokens_cost_by_user",
  CONVERSATION_CHAT_COSTS_TOTAL: "conversation_chat_costs_total",
  API_CALLS_PER_DATE: "api_calls_per_date",
  API_CALLS_PER_ENDPOINT: "api_calls_per_endpoint",
  API_CALLS_LOG: "api_calls_log",
  PENDING_TASKS: "pending_tasks",
  COMPLETED_TASKS: "completed_tasks",
  TIMED_OUT_TASKS: "timed_out_tasks",
  AVG_TIME_TO_SOLVE_TASK: "avg_time_to_solve_task",
  TASKS_DISTRIBUTION: "tasks_distribution",
  TASKS_PER_USER: "tasks_per_user",
  PENDING_TASKS_PER_USER: "pending_tasks_per_user",
  COMPLETED_TASKS_PER_USER: "completed_tasks_per_user",
  TIMED_OUT_TASKS_PER_USER: "timed_out_tasks_per_user",
  ONLINE_WORKER_COUNT: "online_worker_count",
  RUNNING_TASKS: "running_tasks",
  RECENT_TASKS: "recent_tasks",
  ERRORED_TASKS: "errored_tasks",
  AI_OPERATIONS: "ai_operations",
  CONVERSATION_AI_OPERATIONS: "conversation_ai_operations",
  KB_AI_OPERATIONS: "kb_ai_operations",
  AVERAGE_AI_OPERATIONS_PER_RUN: "average_ai_operations_per_run",
  AI_OPERATIONS_OVERAGE_COST: "ai_operations_overage_cost",
  AI_OPERATIONS_OVER_LIMIT: "ai_operations_over_limit",
  AI_OPERATIONS_OVER_TIME: "ai_operations_over_time",
  MODEL_TOKENS_OVER_TIME: "model_tokens_over_time",
  AI_MODELS_TOTAL_TOKENS: "ai_models_total_tokens",
  AI_OPERATIONS_PER_WORKSPACE: "ai_operations_per_workspace",
  AI_OPERATIONS_PER_TOOL: "ai_operations_per_tool",
  MODEL_TOKENS_PER_WORKSPACE: "model_tokens_per_workspace",
  MODEL_TOKENS_PER_TOOL: "model_tokens_per_tool",
  AI_OPERATIONS_PER_USER: "ai_operations_per_user",
  MODEL_TOKENS_PER_USER: "model_tokens_per_user",
  MODEL_TOKENS_TOTAL_COST: "model_tokens_total_cost",
  MODEL_TOKENS_TOTAL_COST_PER_WORKSPACE:
    "model_tokens_total_cost_per_workspace",
  KB_TOTAL_DOCUMENTS: "kb_total_documents",
  KB_PROCESSED_SUCCESSFULLY: "kb_processed_successfully",
  KB_CONTRIBUTORS: "kb_contributors",
  KB_FAILED_DOCUMENTS: "kb_failed_documents",
  KB_DOCUMENTS_OVER_TIME: "kb_documents_over_time",
  KB_DOCUMENTS_PER_KB: "kb_documents_per_kb",
  KB_DOCUMENTS_BY_USER: "kb_documents_by_user",
  KB_DOCUMENT_TYPES: "kb_document_types",
  KB_USER_ACTIVITY: "kb_user_activity",
  KB_OVERVIEW: "kb_overview",
} as const;

/**
 * AnalyticsKey
 */
export type AnalyticsKey = (typeof AnalyticsKey)[keyof typeof AnalyticsKey];

/**
 * AnalyticsMetadata
 */
export type AnalyticsMetadata = {
  /**
   * Title
   */
  title: string;
  key: AnalyticsKey;
  type: AnalyticsType;
  /**
   * Colspan
   */
  colspan?: number;
  /**
   * Options
   */
  options?: {
    [key: string]: unknown;
  };
  /**
   * Tooltip
   */
  tooltip?: string | null;
  /**
   * Learn More
   *
   * Documentation explaining the widget
   */
  learn_more?: string | null;
  /**
   * Paginated
   */
  paginated?: boolean;
};

/**
 * AnalyticsMetadataItems
 */
export type AnalyticsMetadataItems = {
  /**
   * Analytics
   */
  analytics: Array<AnalyticsMetadata>;
};

/**
 * AnalyticsNode
 */
export type AnalyticsNode = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Node Type
   */
  node_type: string;
};

/**
 * AnalyticsPaginatedTableValue
 */
export type AnalyticsPaginatedTableValue = {
  /**
   * Headings
   */
  headings: Array<string>;
  /**
   * Items
   */
  items: Array<{
    [key: string]:
      | string
      | number
      | number
      | AnalyticsWorkflow
      | AnalyticsUser
      | AnalyticsChip
      | AnalyticsNode;
  }>;
  /**
   * Prefixes
   */
  prefixes?: {
    [key: string]: string;
  } | null;
  /**
   * Suffixes
   */
  suffixes?: {
    [key: string]: string;
  } | null;
  /**
   * Total
   */
  total: number;
  /**
   * Pages
   */
  pages: number;
  /**
   * Size
   */
  size: number;
  /**
   * Page
   */
  page: number;
};

/**
 * AnalyticsResponse
 */
export type AnalyticsResponse = {
  type: AnalyticsType;
  /**
   * Value
   */
  value:
    | AnalyticsSimpleValue
    | AnalyticsBarChartsValue
    | AnalyticsTableValue
    | AnalyticsPaginatedTableValue;
};

/**
 * AnalyticsSimpleValue
 */
export type AnalyticsSimpleValue = {
  /**
   * Value
   */
  value: string | number | number;
  /**
   * Suffix
   */
  suffix?: string | null;
  /**
   * Prefix
   */
  prefix?: string | null;
  /**
   * Currency
   *
   * Set when value is an amount of money in this currency
   */
  currency?: "USD" | "EUR" | "GBP" | null;
  cost_details?: AnalyticsCostValue | null;
};

/**
 * AnalyticsTableValue
 */
export type AnalyticsTableValue = {
  /**
   * Headings
   */
  headings: Array<string>;
  /**
   * Rows
   */
  rows: Array<{
    [key: string]:
      | string
      | number
      | number
      | AnalyticsWorkflow
      | AnalyticsUser
      | AnalyticsKb
      | AnalyticsChip
      | AnalyticsNode
      | AnalyticsCostValue;
  }>;
  /**
   * Prefixes
   */
  prefixes?: {
    [key: string]: string;
  } | null;
  /**
   * Suffixes
   */
  suffixes?: {
    [key: string]: string;
  } | null;
};

/**
 * AnalyticsType
 */
export const AnalyticsType = {
  SIMPLE: "simple",
  BARCHART: "barchart",
  TABLE: "table",
  HORIZONTAL_BARCHART: "horizontal-barchart",
  EMPTY: "empty",
} as const;

/**
 * AnalyticsType
 */
export type AnalyticsType = (typeof AnalyticsType)[keyof typeof AnalyticsType];

/**
 * AnalyticsUser
 */
export type AnalyticsUser = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Img
   */
  img: string;
};

/**
 * AnalyticsWorkflow
 */
export type AnalyticsWorkflow = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Icon
   */
  icon: string;
  /**
   * Type
   */
  type?: string | null;
};

/**
 * AnswerQualityDatasetRequest
 */
export type AnswerQualityDatasetRequest = {
  /**
   * Knowledge Base Id
   */
  knowledge_base_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Questions Per Chunk
   */
  questions_per_chunk?: number;
  /**
   * Max Chunks
   */
  max_chunks?: number;
  /**
   * Refusal Questions
   */
  refusal_questions?: number;
};

/**
 * AnswerQualityDatasetResponse
 */
export type AnswerQualityDatasetResponse = {
  /**
   * Agent Id
   */
  agent_id: string;
  /**
   * Knowledge Base Id
   */
  knowledge_base_id: string;
  /**
   * Status
   */
  status?: "queued";
};

/**
 * AnswerQualityRunSummary
 */
export type AnswerQualityRunSummary = {
  /**
   * Evaluation Run Id
   */
  evaluation_run_id: string;
  /**
   * Status
   */
  status:
    | "pending"
    | "running"
    | "completed"
    | "failed"
    | "invalid"
    | "cancelled";
  /**
   * Total Cases
   */
  total_cases: number;
  /**
   * Evaluators
   */
  evaluators: Array<EvaluatorSummary>;
  quadrant: QuadrantCounts | null;
  /**
   * Mean Latency Ms
   */
  mean_latency_ms: number | null;
  /**
   * Mean Agent Cost
   */
  mean_agent_cost: number | null;
  /**
   * Costed Conversations
   */
  costed_conversations: number;
  /**
   * Total Judge Cost
   */
  total_judge_cost: number | null;
  /**
   * Baseline Run Id
   */
  baseline_run_id: string | null;
  search_loop_ms: StatSummary | null;
  search_embed_cost_usd: StatSummary | null;
  search_rerank_cost_usd: StatSummary | null;
  search_total_cost_usd: StatSummary | null;
};

/**
 * AnthropicProviderData
 */
export type AnthropicProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "anthropic";
  /**
   * Anthropic Api Key
   */
  ANTHROPIC_API_KEY: string;
  /**
   * Base Url
   */
  base_url: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * AnyOfRule
 */
export type AnyOfRule = {
  /**
   * Kind
   */
  kind: "any_of";
  /**
   * Fields
   */
  fields?: Array<string>;
  /**
   * Min Filled
   */
  min_filled?: number;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Strict
   */
  strict?: boolean;
};

/**
 * ApiKey
 */
export type ApiKey = {
  /**
   * Name
   */
  name: string;
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Value
   */
  value: string;
  /**
   * Tenant Admin
   */
  tenant_admin?: boolean;
  /**
   * Permissions
   */
  permissions?: Array<string> | null;
  /**
   * Last Used
   */
  last_used?: string | null;
};

/**
 * ApiToggleState
 */
export const ApiToggleState = {
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  REQUESTING: "requesting",
  REQUESTING_CONNECT: "requesting_connect",
  REQUESTING_DISCONNECT: "requesting_disconnect",
} as const;

/**
 * ApiToggleState
 */
export type ApiToggleState =
  (typeof ApiToggleState)[keyof typeof ApiToggleState];

/**
 * App
 */
export type App = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type?: "assistant" | "flow" | null;
  /**
   * Icon
   */
  icon?: string | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Deployment Id
   */
  deployment_id?: string | null;
  /**
   * Channel Type
   */
  channel_type?: string | null;
  /**
   * Published At
   */
  published_at?: string | null;
};

/**
 * AppInterfaceType
 */
export const AppInterfaceType = {
  SLACK: "slack",
  TEAMS: "teams",
  CHAT: "chat",
  FORM: "form",
  WHATSAPP: "whatsapp",
  EMBED_WIDGET: "embed_widget",
  TELEGRAM: "telegram",
} as const;

/**
 * AppInterfaceType
 */
export type AppInterfaceType =
  (typeof AppInterfaceType)[keyof typeof AppInterfaceType];

/**
 * AppSettingsV2
 */
export type AppSettingsV2 = {
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Version Id
   */
  version_id?: string | null;
  interface_type?: AppInterfaceType | null;
  /**
   * Extra Settings
   */
  extra_settings?: {
    [key: string]: unknown;
  } | null;
  /**
   * Published At
   */
  published_at?: string | null;
};

/**
 * AppVersionUsage
 */
export type AppVersionUsage = {
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Workflow Name
   */
  workflow_name: string;
};

/**
 * ArtifactKind
 *
 * Config entities only — never runtime data (runs, conversations, documents).
 */
export const ArtifactKind = {
  FLOW: "flow",
  AGENT: "agent",
  KNOWLEDGE_BASE: "knowledge_base",
  INBOX: "inbox",
  TABLE: "table",
  FILE: "file",
  CONNECTION: "connection",
  TRIGGER: "trigger",
  DEPLOYMENT: "deployment",
  SECRET: "secret",
  FLOW_VERSION: "flow_version",
  AGENT_VERSION: "agent_version",
} as const;

/**
 * ArtifactKind
 *
 * Config entities only — never runtime data (runs, conversations, documents).
 */
export type ArtifactKind = (typeof ArtifactKind)[keyof typeof ArtifactKind];

/**
 * ArtifactSyncStatus
 */
export type ArtifactSyncStatus = {
  /**
   * Linked
   */
  linked: boolean;
  /**
   * State
   */
  state:
    | "in_sync"
    | "local_changes"
    | "remote_changes"
    | "diverged"
    | "unlinked";
  /**
   * Canonical Id
   */
  canonical_id?: string | null;
  /**
   * Repository
   */
  repository?: string | null;
  /**
   * Branch
   */
  branch?: string | null;
  /**
   * Path
   */
  path?: string | null;
  /**
   * Last Commit
   */
  last_commit?: string | null;
  /**
   * Head Commit
   */
  head_commit?: string | null;
  /**
   * Web Url
   */
  web_url?: string | null;
  /**
   * Last Synced At
   */
  last_synced_at?: string | null;
};

/**
 * AssignLabellingTaskRequest
 */
export type AssignLabellingTaskRequest = {
  /**
   * Assignee Id
   */
  assignee_id?: string | null;
};

/**
 * AssignRoleRequest
 *
 * Schema for assigning a role to a user.
 */
export type AssignRoleRequest = {
  /**
   * Role Id
   *
   * Role ID to assign (required in full roles mode)
   */
  role_id: string;
};

/**
 * Assistant
 */
export type Assistant = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Image Url
   */
  image_url?: string | null;
  /**
   * Last Updated At
   */
  last_updated_at?: string | null;
  /**
   * Deleted At
   */
  deleted_at?: string | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Is App Published
   */
  is_app_published?: boolean | null;
  /**
   * Allow Public Access
   */
  allow_public_access?: boolean;
  meta?: AssistantMeta | null;
};

/**
 * AssistantMeta
 */
export type AssistantMeta = {
  /**
   * Template Id
   */
  template_id?: string | null;
  /**
   * Is Genie
   */
  is_genie?: boolean | null;
  source?: EntitySource | null;
};

/**
 * AssistantTools
 */
export type AssistantTools = {
  /**
   * Tools
   */
  tools: Array<AgentTool>;
  /**
   * Knowledge Base Ids
   */
  knowledge_base_ids: Array<string>;
};

/**
 * AssistantVersion
 */
export type AssistantVersion = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Created By
   */
  created_by?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  meta?: VersionMeta | null;
};

/**
 * AssistantVersionUsages
 */
export type AssistantVersionUsages = {
  /**
   * Deployments
   */
  deployments: Array<DeploymentVersionUsage>;
  /**
   * Triggers
   */
  triggers: Array<TriggerVersionUsage>;
};

/**
 * AssistantWithTags
 */
export type AssistantWithTags = {
  /**
   * Tags
   */
  tags?: Array<Tag> | null;
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Image Url
   */
  image_url?: string | null;
  /**
   * Last Updated At
   */
  last_updated_at?: string | null;
  /**
   * Deleted At
   */
  deleted_at?: string | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Is App Published
   */
  is_app_published?: boolean | null;
  /**
   * Allow Public Access
   */
  allow_public_access?: boolean;
  meta?: AssistantMeta | null;
  /**
   * Deployments
   */
  deployments?: Array<DeploymentSummary>;
};

/**
 * AttachFileSettings
 */
export type AttachFileSettings = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   *
   * The lucide icon to display for this tool
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   *
   * The message to display to the user when the tool is called.
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   *
   * The message to display to the user when the tool completes.
   */
  tool_display_message_end?: string;
  /**
   * Extra Instructions
   */
  extra_instructions?: string | null;
  /**
   * Requires Approval
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "attach_file";
};

/**
 * AudioContainer
 */
export type AudioContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "Audio";
  /**
   * Value
   */
  value: unknown;
};

/**
 * AudioUrl
 *
 * A URL to an audio file.
 */
export type AudioUrl = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "audio-url";
  /**
   * Media Type
   *
   * Return the media type of the file, based on the URL or the provided `media_type`.
   */
  readonly media_type: string;
  /**
   * Identifier
   *
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `FileUrl`.
   *
   * This identifier is only automatically passed to the model when the `FileUrl` is returned by a tool.
   * If you're passing the `FileUrl` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `FileUrl`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  readonly identifier: string;
};

/**
 * AuditLogEntry
 */
export type AuditLogEntry = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * User Id
   */
  user_id: string | null;
  /**
   * Email
   */
  email: string | null;
  /**
   * Action
   */
  action: string;
  /**
   * Resource Type
   */
  resource_type: string | null;
  /**
   * Resource Id
   */
  resource_id: string | null;
  /**
   * Method
   */
  method: string;
  /**
   * Route
   */
  route: string;
  body?: PydanticTypesJsonValue | null;
};

/**
 * AuditLogFacets
 */
export type AuditLogFacets = {
  /**
   * Methods
   */
  methods: Array<string>;
  /**
   * Resource Types
   */
  resource_types: Array<string>;
};

/**
 * AuditLogStorageStats
 */
export type AuditLogStorageStats = {
  /**
   * Size Bytes
   */
  size_bytes: number;
  /**
   * Size Pretty
   */
  size_pretty: string;
};

/**
 * Auth0Connection
 */
export type Auth0Connection = {
  /**
   * Name
   */
  name: string;
  /**
   * Strategy
   */
  strategy: string;
  /**
   * Display Name
   */
  display_name?: string | null;
};

/**
 * Auth0EmailChange
 */
export type Auth0EmailChange = {
  /**
   * Id
   */
  id: string;
  /**
   * Auth Id
   */
  auth_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Email
   */
  email: string;
  /**
   * Auth0 Email
   */
  auth0_email: string;
  /**
   * Status
   */
  status: "ready" | "blocked";
  /**
   * Reason
   */
  reason: string;
};

/**
 * Auth0EmailReview
 */
export type Auth0EmailReview = {
  /**
   * Checked
   */
  checked: number;
  /**
   * Missing
   */
  missing: number;
  /**
   * Changes
   */
  changes: Array<Auth0EmailChange>;
};

/**
 * Auth0EmailUpdate
 */
export type Auth0EmailUpdate = {
  /**
   * Id
   */
  id: string;
  /**
   * Email
   */
  email: string;
};

/**
 * Auth0EmailUpdateInput
 */
export type Auth0EmailUpdateInput = {
  /**
   * Changes
   */
  changes: Array<Auth0EmailUpdate>;
};

/**
 * Auth0EmailUpdateResult
 */
export type Auth0EmailUpdateResult = {
  /**
   * Updated
   */
  updated: Array<string>;
  /**
   * Skipped
   */
  skipped: Array<string>;
};

/**
 * AuthBootstrapStatus
 */
export type AuthBootstrapStatus = {
  /**
   * Required
   */
  required: boolean;
};

/**
 * AuthConfigJSON
 *
 * Auth configuration stored in server_settings with key='auth'
 */
export type AuthConfigJson = {
  /**
   * Whitelabel Logo
   */
  whitelabel_logo?: string | null;
  /**
   * Whitelabel Logo Size
   *
   * Logo size in pixels used on the Welcome/Login/Signup screens.
   */
  whitelabel_logo_size?: number;
  /**
   * Whitelabel Name
   */
  whitelabel_name?: string;
  /**
   * Whitelabel Primary Color
   */
  whitelabel_primary_color?: string | null;
  /**
   * Autojoin Tenant Id
   */
  autojoin_tenant_id?: string | null;
  /**
   * Force Email Verification
   */
  force_email_verification?: boolean;
  /**
   * Skip Onboarding
   */
  skip_onboarding?: boolean;
  /**
   * Allowed Domains
   */
  allowed_domains?: Array<string>;
  /**
   * Allowed Email Pattern
   */
  allowed_email_pattern?: string | null;
  /**
   * Allow Personal Workspace
   */
  allow_personal_workspace?: boolean;
  /**
   * Fallback Workspace Id
   */
  fallback_workspace_id?: string | null;
  /**
   * Fallback Workspace Role
   *
   * Role granted to autojoin users assigned to the fallback workspace.
   */
  fallback_workspace_role?: string;
  /**
   * Allow User Password
   */
  allow_user_password?: boolean;
  /**
   * Allow Signup
   */
  allow_signup?: boolean;
  /**
   * Allow Password Reset
   */
  allow_password_reset?: boolean;
  /**
   * Redirect After Login
   */
  redirect_after_login?: string;
  /**
   * Redirect Map
   */
  redirect_map?: {
    [key: string]: string;
  };
  /**
   * Session Duration Hours
   */
  session_duration_hours?: number;
  /**
   * Require Mfa
   */
  require_mfa?: boolean;
  /**
   * Enabled Connections
   */
  enabled_connections?: Array<AuthConnection>;
  /**
   * Custom Error Messages
   */
  custom_error_messages?: {
    [key: string]: string;
  };
  saml_attribute_mapping?: SamlAttributeMappingConfig | null;
};

/**
 * AuthConfigResponse
 *
 * GET /auth/config response — adds a derived lock flag.
 *
 * The flag is computed from LOCK_PRIMARY_AUTH_CONFIG on every request and is
 * never persisted. It mirrors the same value the backend uses to enforce
 * immutability on POST, so the UI can disable the corresponding inputs.
 */
export type AuthConfigResponse = {
  /**
   * Whitelabel Logo
   */
  whitelabel_logo?: string | null;
  /**
   * Whitelabel Logo Size
   *
   * Logo size in pixels used on the Welcome/Login/Signup screens.
   */
  whitelabel_logo_size?: number;
  /**
   * Whitelabel Name
   */
  whitelabel_name?: string;
  /**
   * Whitelabel Primary Color
   */
  whitelabel_primary_color?: string | null;
  /**
   * Autojoin Tenant Id
   */
  autojoin_tenant_id?: string | null;
  /**
   * Force Email Verification
   */
  force_email_verification?: boolean;
  /**
   * Skip Onboarding
   */
  skip_onboarding?: boolean;
  /**
   * Allowed Domains
   */
  allowed_domains?: Array<string>;
  /**
   * Allowed Email Pattern
   */
  allowed_email_pattern?: string | null;
  /**
   * Allow Personal Workspace
   */
  allow_personal_workspace?: boolean;
  /**
   * Fallback Workspace Id
   */
  fallback_workspace_id?: string | null;
  /**
   * Fallback Workspace Role
   *
   * Role granted to autojoin users assigned to the fallback workspace.
   */
  fallback_workspace_role?: string;
  /**
   * Allow User Password
   */
  allow_user_password?: boolean;
  /**
   * Allow Signup
   */
  allow_signup?: boolean;
  /**
   * Allow Password Reset
   */
  allow_password_reset?: boolean;
  /**
   * Redirect After Login
   */
  redirect_after_login?: string;
  /**
   * Redirect Map
   */
  redirect_map?: {
    [key: string]: string;
  };
  /**
   * Session Duration Hours
   */
  session_duration_hours?: number;
  /**
   * Require Mfa
   */
  require_mfa?: boolean;
  /**
   * Enabled Connections
   */
  enabled_connections?: Array<AuthConnection>;
  /**
   * Custom Error Messages
   */
  custom_error_messages?: {
    [key: string]: string;
  };
  saml_attribute_mapping?: SamlAttributeMappingConfig | null;
  /**
   * Primary Config Locked
   */
  primary_config_locked?: boolean;
};

/**
 * AuthConnection
 */
export type AuthConnection = {
  type: ConnectionType;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Logo Url
   */
  logo_url?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Connection Identifier
   */
  connection_identifier?: string | null;
  /**
   * Priority
   */
  priority?: number;
};

/**
 * AuthPolicy
 */
export type AuthPolicy = {
  /**
   * Phase
   */
  phase?: "inactive" | "migrating" | "better-auth" | "complete";
  /**
   * Primary
   */
  primary?: "auth0" | "better-auth";
  /**
   * Auth0
   */
  auth0?: boolean;
  /**
   * Better Auth
   */
  better_auth?: boolean;
  /**
   * Password Setup
   */
  password_setup?: "reset" | "send-links" | "hashes" | null;
  /**
   * Revision
   */
  revision?: number;
};

/**
 * AuthTransitionInput
 */
export type AuthTransitionInput = {
  /**
   * Action
   */
  action:
    | "start"
    | "prefer-auth0"
    | "prefer-better-auth"
    | "complete"
    | "disable-better-auth"
    | "password-setup";
  /**
   * Revision
   */
  revision: number;
  /**
   * Password Setup
   */
  password_setup?: "reset" | "send-links" | "hashes" | null;
};

/**
 * AuthorizationContext
 *
 * Who the caller is and what they may see, as these routes answer it:
 * /users/me, /tenant/me, /groups/{id}, /groups/me, /me/permissions,
 * /tenant/feature-flags, /beta-features and /status/storage. A part the route
 * would refuse is null.
 */
export type AuthorizationContext = {
  user: User | null;
  tenant: TenantWithBilling | null;
  group: GroupWithUsers | null;
  my_groups: PageGroupWithUsers | null;
  permissions: UserPermissionsResponse | null;
  feature_flags: FeatureFlags | null;
  /**
   * Beta Features
   */
  beta_features: Array<"genie" | "agent_tests">;
  storage: StorageStatus | null;
};

/**
 * AuthorizeRequest
 */
export type AuthorizeRequest = {
  /**
   * Redirect Uri
   */
  redirect_uri: string;
  /**
   * Code Challenge
   */
  code_challenge: string;
  /**
   * Label
   */
  label?: string | null;
};

/**
 * AuthorizeResponse
 */
export type AuthorizeResponse = {
  /**
   * Code
   */
  code: string;
};

/**
 * AutomationRef
 */
export type AutomationRef = {
  /**
   * Key
   */
  key: string;
  /**
   * Spec
   */
  spec: string;
};

/**
 * AzureFoundryProviderData
 */
export type AzureFoundryProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "azure-foundry";
  /**
   * Auth Mode
   */
  auth_mode?: string;
  /**
   * Azure Foundry Api Key
   */
  AZURE_FOUNDRY_API_KEY?: string;
  /**
   * Use User Assigned Identity
   *
   * Off: use the system-assigned identity attached to this resource. On: specify a user-assigned identity by its client ID.
   */
  use_user_assigned_identity?: boolean;
  /**
   * Managed Identity Client Id
   */
  managed_identity_client_id?: string;
  /**
   * Azure Foundry Endpoint
   */
  AZURE_FOUNDRY_ENDPOINT: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * AzureProviderData
 */
export type AzureProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "azure";
  /**
   * Auth Mode
   */
  auth_mode?: string;
  /**
   * Azure Openai Api Key
   */
  AZURE_OPENAI_API_KEY?: string;
  /**
   * Use User Assigned Identity
   *
   * Off: use the system-assigned identity attached to this resource. On: specify a user-assigned identity by its client ID.
   */
  use_user_assigned_identity?: boolean;
  /**
   * Managed Identity Client Id
   */
  managed_identity_client_id?: string;
  /**
   * Azure Openai Endpoint
   *
   * Full URL of the Azure OpenAI v1 API surface, including the trailing /openai/v1/ path. Copy the endpoint from your Azure resource and append /openai/v1/, e.g. https://my-resource.openai.azure.com/openai/v1/ (regional resources look like https://swedencentral.api.cognitive.microsoft.com/openai/v1/).
   */
  AZURE_OPENAI_ENDPOINT: string;
  /**
   * Azure Openai Api Version
   *
   * The v1 API surface accepts only 'preview' (newest models and features) or 'latest' (GA-stable). Dated versions are not supported.
   */
  AZURE_OPENAI_API_VERSION?: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * BBoxEvent
 *
 * Reply to a ``bbox`` command.  ``bbox`` is null if the selector
 * didn't resolve to a visible element.
 */
export type BBoxEvent = {
  /**
   * Type
   */
  type: "bbox";
  /**
   * Request Id
   */
  request_id: string;
  bbox: BBoxRect | null;
};

/**
 * BBoxRect
 */
export type BBoxRect = {
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * Width
   */
  width: number;
  /**
   * Height
   */
  height: number;
};

/**
 * BackfillRunDataRequest
 */
export type BackfillRunDataRequest = {
  /**
   * Months
   */
  months?: number;
};

/**
 * BaseModelContainer
 */
export type BaseModelContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "basemodel";
  /**
   * Value
   */
  value: unknown;
};

/**
 * BatchNodeConfigItem
 */
export type BatchNodeConfigItem = {
  /**
   * Instance Id
   */
  instance_id: string;
  /**
   * Node Type
   */
  node_type: string;
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
};

/**
 * BatchNodeConfigRequest
 */
export type BatchNodeConfigRequest = {
  /**
   * Items
   */
  items: Array<BatchNodeConfigItem>;
  /**
   * Workflow Id
   */
  workflow_id?: string | null;
};

/**
 * BatchNodeConfigResponse
 */
export type BatchNodeConfigResponse = {
  /**
   * Results
   */
  results: {
    [key: string]: ConfigResponse | ConfigResponseV2 | null;
  };
};

/**
 * BedrockProviderData
 */
export type BedrockProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "bedrock";
  /**
   * Auth Mode
   */
  auth_mode?: string;
  /**
   * Api Key
   */
  api_key?: string;
  /**
   * Aws Access Key Id
   */
  aws_access_key_id?: string;
  /**
   * Aws Secret Access Key
   */
  aws_secret_access_key?: string;
  /**
   * Aws Session Token
   */
  aws_session_token?: string;
  /**
   * Locations
   */
  locations?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Base Url
   */
  base_url?: string;
  /**
   * Allow Global Inference
   *
   * Off: requests stay in the geography of each region (US, EU, Japan, Australia, India). On: a region with no geography profile for a model uses the global profile, which AWS may serve from any commercial region.
   */
  allow_global_inference?: boolean;
  connect_button?: ApiToggleState;
  /**
   * Region Info
   */
  region_info?: string;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * BetaFeatureState
 */
export type BetaFeatureState = {
  /**
   * Feature
   */
  feature: "genie" | "agent_tests";
  /**
   * Stage
   */
  stage: "alpha" | "beta";
  opt_in: BetaOptIn | null;
};

/**
 * BetaFeatureUpdate
 */
export type BetaFeatureUpdate = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * BetaOptIn
 *
 * Who accepted the feature's terms for the deployment, and when.
 */
export type BetaOptIn = {
  /**
   * Enabled At
   */
  enabled_at: string;
  /**
   * Enabled By
   */
  enabled_by: string;
  /**
   * Enabled By Email
   */
  enabled_by_email: string;
};

/**
 * BillingCharge
 */
export type BillingCharge = {
  /**
   * Id
   */
  id: string;
  /**
   * Amount
   */
  amount: number;
  /**
   * Currency
   */
  currency: "USD" | "EUR" | "GBP";
  /**
   * Recorded At
   */
  recorded_at: string;
  /**
   * Period Start
   */
  period_start: string;
  metric?: ConstraintType | null;
  /**
   * Credit Applied
   */
  credit_applied?: number;
};

/**
 * BillingCurrencyInformation
 */
export type BillingCurrencyInformation = {
  /**
   * Currency
   */
  currency?: "USD" | "EUR" | "GBP";
  exchange_rate?: BillingExchangeRate | null;
};

/**
 * BillingCycle
 *
 * Billing cycle period.
 */
export const BillingCycle = { MONTHLY: "monthly", ANNUAL: "annual" } as const;

/**
 * BillingCycle
 *
 * Billing cycle period.
 */
export type BillingCycle = (typeof BillingCycle)[keyof typeof BillingCycle];

/**
 * BillingExchangeRate
 */
export type BillingExchangeRate = {
  /**
   * Base
   */
  base?: "USD";
  /**
   * Quote
   */
  quote: "USD" | "EUR" | "GBP";
  /**
   * Rate
   *
   * Quote currency units per USD; display conversion only
   */
  rate: number;
  /**
   * Rate Date
   */
  rate_date: string;
  /**
   * Snapshot Date
   */
  snapshot_date: string;
  /**
   * Stale
   */
  stale?: boolean;
  /**
   * Source
   */
  source: "ECB" | "identity";
};

/**
 * BillingInfo
 *
 * Embedded billing information for tenant endpoint.
 */
export type BillingInfo = {
  /**
   * Has Payment Method
   */
  has_payment_method: boolean;
  /**
   * Plan
   */
  plan?: string | null;
  /**
   * Billing Cycle
   */
  billing_cycle?: string | null;
};

/**
 * BillingInformationResponse
 *
 * Complete billing information for a tenant.
 */
export type BillingInformationResponse = {
  /**
   * Currency
   */
  currency?: "USD" | "EUR" | "GBP";
  exchange_rate?: BillingExchangeRate | null;
  billing_period?: BillingPeriod;
  /**
   * Base Price
   */
  base_price?: number;
  /**
   * Credit Currency
   */
  credit_currency?: "USD" | "EUR" | "GBP";
  /**
   * Billing Active
   */
  billing_active?: boolean;
  /**
   * Charges
   */
  charges?: Array<BillingCharge>;
  /**
   * Recorded Overage
   *
   * Recorded charges in the requested time range, grouped by metric and original currency; null means the license server does not provide these totals
   */
  recorded_overage?: Array<BillingOverageTotal> | null;
  /**
   * Plan
   *
   * Current billing label
   */
  plan: string | null;
  /**
   * Current billing cycle
   */
  billing_cycle?: BillingCycle | null;
  /**
   * Has Payment Method
   *
   * Whether tenant has a payment method on file
   */
  has_payment_method: boolean;
  /**
   * Pay-as-you-go settings
   */
  payg_settings: PayGSettings;
  /**
   * Current usage metrics
   */
  current_billing_metrics: BillingMetrics;
  /**
   * Additional Rules
   *
   * Billing rules beyond the organization-wide AI Operations and model usage allowances: workspace scopes, filtered rules and other metrics
   */
  additional_rules?: Array<BillingRuleValue>;
  /**
   * Credits
   *
   * Remaining prepaid credit in credit_currency
   */
  credits?: number;
  /**
   * Next Billing Date
   */
  next_billing_date: string;
  /**
   * Billing Email
   */
  billing_email?: string | null;
  /**
   * Trial End Date
   */
  trial_end_date?: string | null;
  /**
   * Payg Total Cost This Cycle
   *
   * Total PAYG cost spent in current billing cycle
   */
  payg_total_cost_this_cycle?: number;
  /**
   * Payg Operations From Credits
   *
   * AI Operations available from remaining PAYG credits
   */
  payg_operations_from_credits?: number;
  /**
   * Payment Method Issues
   *
   * Whether there are issues with the payment method
   */
  payment_method_issues?: boolean;
  /**
   * Currency Prefix
   *
   * Currency prefix
   */
  currency_prefix: string;
  /**
   * Currency Suffix
   *
   * Currency suffix
   */
  currency_suffix: string;
  /**
   * Over Limit Metrics
   *
   * Metrics whose license constraints are at/over capacity and blocking execution
   */
  over_limit_metrics?: Array<ConstraintType>;
  /**
   * Over Limit Rule Ids
   *
   * Ids of the billing rules, primary or additional, that are at/over capacity and blocking execution
   */
  over_limit_rule_ids?: Array<string>;
};

/**
 * BillingInvoiceInformation
 */
export type BillingInvoiceInformation = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Id
   */
  id: string;
  /**
   * Url
   */
  url?: string | null;
  /**
   * Status
   */
  status: string;
};

/**
 * BillingMetricValue
 *
 * Billing metric with included and PayG values.
 */
export type BillingMetricValue = {
  /**
   * Next Reset At
   *
   * Next reset of this rule's usage, in UTC
   */
  next_reset_at?: string | null;
  period?: BillingPeriod;
  /**
   * Overage Enabled
   */
  overage_enabled?: boolean;
  /**
   * Max Spend
   */
  max_spend?: number | null;
  /**
   * Included
   *
   * Included capacity; null is unlimited, zero allows no included usage
   */
  included: number | null;
  /**
   * Usage
   *
   * Usage
   */
  usage: number;
  /**
   * Reserved
   *
   * Held by work still running
   */
  reserved?: number;
  /**
   * Payg Usage
   *
   * Pay-as-you-go usage
   */
  payg_usage?: number;
  /**
   * Payg Cost
   *
   * Pay-as-you-go cost
   */
  payg_cost?: Array<BillingTier> | null;
  /**
   * Payg Package Operations
   *
   * Pay-as-you-go package operations
   */
  payg_package_operations?: number | null;
  /**
   * Id
   *
   * Billing rule ID, if configured
   */
  id?: string | null;
};

/**
 * BillingMetrics
 *
 * Current billing metrics showing value vs capacity.
 */
export type BillingMetrics = {
  ai_operations?: BillingMetricValue | null;
  model_usage?: BillingMetricValue | null;
  workspaces?: BillingMetricValue | null;
  coworkers?: BillingMetricValue | null;
  storage_gb?: BillingMetricValue | null;
};

/**
 * BillingOverageTotal
 */
export type BillingOverageTotal = {
  metric: ConstraintType;
  /**
   * Currency
   */
  currency: "USD" | "EUR" | "GBP";
  /**
   * Amount
   */
  amount: number;
};

/**
 * BillingPeriod
 */
export const BillingPeriod = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  YEARLY: "yearly",
} as const;

/**
 * BillingPeriod
 */
export type BillingPeriod = (typeof BillingPeriod)[keyof typeof BillingPeriod];

/**
 * BillingRectificationFilterRequest
 */
export type BillingRectificationFilterRequest = {
  /**
   * Model
   */
  model?: string | null;
  /**
   * Tenant Id
   */
  tenant_id?: string | null;
  /**
   * Date From
   */
  date_from?: string | null;
  /**
   * Date To
   */
  date_to?: string | null;
};

/**
 * BillingRuleValue
 */
export type BillingRuleValue = {
  /**
   * Next Reset At
   *
   * Next reset of this rule's usage, in UTC
   */
  next_reset_at?: string | null;
  period?: BillingPeriod;
  /**
   * Overage Enabled
   */
  overage_enabled?: boolean;
  /**
   * Max Spend
   */
  max_spend?: number | null;
  /**
   * Included
   *
   * Included capacity; null is unlimited, zero allows no included usage
   */
  included: number | null;
  /**
   * Usage
   *
   * Usage
   */
  usage: number;
  /**
   * Reserved
   *
   * Held by work still running
   */
  reserved?: number;
  /**
   * Payg Usage
   *
   * Pay-as-you-go usage
   */
  payg_usage?: number;
  /**
   * Payg Cost
   *
   * Pay-as-you-go cost
   */
  payg_cost?: Array<BillingTier> | null;
  /**
   * Payg Package Operations
   *
   * Pay-as-you-go package operations
   */
  payg_package_operations?: number | null;
  /**
   * Id
   */
  id: string;
  metric: ConstraintType;
  /**
   * Scope
   */
  scope: "installation" | "organization" | "workspace";
  /**
   * Scope Name
   *
   * Workspace name; null unless the scope is a workspace
   */
  scope_name?: string | null;
  /**
   * Filter
   */
  filter?: {
    [key: string]: string;
  };
  /**
   * Overage Cap
   *
   * Maximum usage above the allowance; null is unlimited, zero disables overage
   */
  overage_cap?: number | null;
};

/**
 * BillingTier
 */
export type BillingTier = {
  /**
   * Price
   */
  price: number;
  /**
   * Tier Max
   */
  tier_max: number | null;
};

/**
 * BinaryContent
 *
 * Binary content, e.g. an audio or image file.
 */
export type BinaryContent = {
  /**
   * Data
   */
  data: Blob | File;
  /**
   * Media Type
   */
  media_type:
    | "audio/wav"
    | "audio/mpeg"
    | "audio/ogg"
    | "audio/flac"
    | "audio/aiff"
    | "audio/aac"
    | "image/jpeg"
    | "image/png"
    | "image/gif"
    | "image/webp"
    | "application/pdf"
    | "text/plain"
    | "text/csv"
    | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    | "text/html"
    | "text/markdown"
    | "application/msword"
    | "application/vnd.ms-excel"
    | string;
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "binary";
  /**
   * Identifier
   *
   * Identifier for the binary content, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `BinaryContent`.
   *
   * This identifier is only automatically passed to the model when the `BinaryContent` is returned by a tool.
   * If you're passing the `BinaryContent` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `BinaryContent`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  readonly identifier: string;
};

/**
 * Body_add_image
 */
export type BodyAddImage = {
  /**
   * File
   */
  file: Blob | File;
  /**
   * Client Last Modified
   */
  client_last_modified?: string | null;
};

/**
 * Body_add_public_image
 */
export type BodyAddPublicImage = {
  /**
   * File
   */
  file: Blob | File;
};

/**
 * Body_dictate_audio
 */
export type BodyDictateAudio = {
  /**
   * Audio
   */
  audio: Blob | File;
  /**
   * Language
   */
  language?: string | null;
};

/**
 * Body_import_csv
 */
export type BodyImportCsv = {
  /**
   * File
   */
  file: Blob | File;
  /**
   * Spec
   */
  spec: string;
};

/**
 * Body_import_csv_into_table
 */
export type BodyImportCsvIntoTable = {
  /**
   * File
   */
  file: Blob | File;
  /**
   * Spec
   */
  spec: string;
};

/**
 * Body_import_csv_rows
 */
export type BodyImportCsvRows = {
  /**
   * File
   */
  file: Blob | File;
  /**
   * Spec
   */
  spec: string;
};

/**
 * Body_import_preview
 */
export type BodyImportPreview = {
  /**
   * File
   */
  file: Blob | File;
};

/**
 * Body_receive_email
 */
export type BodyReceiveEmail = {
  /**
   * To
   */
  to?: string | null;
  /**
   * Cc
   */
  cc?: string | null;
  /**
   * From
   */
  from?: string | null;
  /**
   * Subject
   */
  subject?: string | null;
  /**
   * Text
   */
  text?: string | null;
  /**
   * Html
   */
  html?: string | null;
  /**
   * Email
   */
  email?: string | null;
  /**
   * Spf
   */
  SPF?: string | null;
  /**
   * Attachments
   */
  attachments?: number | null;
  /**
   * Headers
   */
  headers?: string | null;
};

/**
 * Body_simulate_email
 */
export type BodySimulateEmail = {
  /**
   * Subject
   */
  subject: string;
  /**
   * From Email
   */
  from_email: string;
  /**
   * Text Body
   */
  text_body?: string;
  /**
   * From Name
   */
  from_name?: string | null;
  /**
   * Attachments
   */
  attachments?: Array<Blob | File>;
};

/**
 * Body_upload_train_knowledge_base
 */
export type BodyUploadTrainKnowledgeBase = {
  /**
   * Files
   */
  files: Array<Blob | File>;
  /**
   * File Metadata
   *
   * JSON list of SourceMetadata objects, one per file (positionally matched)
   */
  file_metadata?: string | null;
};

/**
 * BoolContainer
 */
export type BoolContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "bool";
  /**
   * Value
   */
  value: unknown;
};

/**
 * BootstrapStatus
 */
export type BootstrapStatus = {
  /**
   * Ready
   */
  ready: boolean;
  /**
   * Conversation Count
   */
  conversation_count: number;
  /**
   * Min Conversations
   */
  min_conversations?: number;
  /**
   * Days Collecting
   */
  days_collecting?: number;
  /**
   * Min Days
   */
  min_days?: number;
  /**
   * Bootstrapped
   */
  bootstrapped?: boolean;
};

/**
 * BuildVersionResponse
 */
export type BuildVersionResponse = {
  /**
   * Version
   */
  version: string;
  /**
   * Last Updated At
   */
  last_updated_at?: string | null;
};

/**
 * BulkDeleteRowsRequest
 */
export type BulkDeleteRowsRequest = {
  /**
   * Row Ids
   */
  row_ids?: Array<string> | null;
  /**
   * All Matching
   */
  all_matching?: boolean;
  /**
   * Search
   */
  search?: string | null;
  /**
   * Filters
   */
  filters?: Array<RowFilterSpec> | null;
  /**
   * Exclude Row Ids
   */
  exclude_row_ids?: Array<string> | null;
};

/**
 * BulkDeleteRowsResponse
 */
export type BulkDeleteRowsResponse = {
  /**
   * Deleted
   */
  deleted: number;
};

/**
 * BulkDuplicateRowsRequest
 */
export type BulkDuplicateRowsRequest = {
  /**
   * Row Ids
   */
  row_ids?: Array<string> | null;
  /**
   * All Matching
   */
  all_matching?: boolean;
  /**
   * Search
   */
  search?: string | null;
  /**
   * Filters
   */
  filters?: Array<RowFilterSpec> | null;
  /**
   * Exclude Row Ids
   */
  exclude_row_ids?: Array<string> | null;
};

/**
 * BulkDuplicateRowsResponse
 */
export type BulkDuplicateRowsResponse = {
  /**
   * Inserted
   */
  inserted: number;
};

/**
 * BulkRunActionRequest
 */
export type BulkRunActionRequest = {
  /**
   * Run Ids
   */
  run_ids?: Array<string> | null;
  filters?: RunFilters | null;
  /**
   * Expected Count
   */
  expected_count?: number | null;
};

/**
 * BulkRunActionResult
 */
export type BulkRunActionResult = {
  /**
   * Succeeded
   */
  succeeded: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Errors
   */
  errors: {
    [key: string]: string;
  };
};

/**
 * BulkSetDisabledRequest
 *
 * Replace the disabled set for one kind. Keys not listed become enabled.
 */
export type BulkSetDisabledRequest = {
  /**
   * Disabled
   */
  disabled: Array<string>;
};

/**
 * BulkTagEntitiesRequest
 */
export type BulkTagEntitiesRequest = {
  /**
   * Entity Ids
   *
   * List of entity IDs to tag
   */
  entity_ids: Array<string>;
  /**
   * Type of entities (workflow, assistant, knowledge_base)
   */
  entity_type: EntityType;
};

/**
 * CSATFeedback
 */
export type CsatFeedback = {
  /**
   * Rating
   */
  rating: number;
  /**
   * Comment
   */
  comment?: string | null;
};

/**
 * CSATRatingCount
 */
export type CsatRatingCount = {
  /**
   * Rating
   */
  rating: number;
  /**
   * Count
   */
  count: number;
};

/**
 * CSATScore
 *
 * CX score: the share of conversations rated 4 or 5 (the bar plot).
 */
export type CsatScore = {
  /**
   * Total
   */
  total: number;
  /**
   * Positive
   */
  positive: number;
  /**
   * Score Pct
   */
  score_pct: number;
  /**
   * Delta Pct
   */
  delta_pct: number | null;
  /**
   * Ratings
   */
  ratings: Array<CsatRatingCount>;
  /**
   * Days
   */
  days: number;
};

/**
 * CacheDeletePatternRequest
 */
export type CacheDeletePatternRequest = {
  /**
   * Pattern
   */
  pattern: string;
};

/**
 * CacheDeletePatternResponse
 */
export type CacheDeletePatternResponse = {
  /**
   * Deleted Count
   */
  deleted_count: number;
};

/**
 * CacheInvalidateTagRequest
 */
export type CacheInvalidateTagRequest = {
  /**
   * Tag
   */
  tag: string;
};

/**
 * CachePoint
 *
 * A cache point marker for prompt caching.
 *
 * Can be inserted into UserPromptPart.content to mark cache boundaries.
 * Models that don't support caching will filter these out.
 *
 * Supported by:
 *
 * - Anthropic
 * - Amazon Bedrock (Converse API)
 * - OpenAI (GPT-5.6 models)
 * - OpenRouter (Anthropic and Gemini models via `OpenRouterModel`, plus OpenAI GPT-5.6 models when
 * using `OpenAIChatModel` or `OpenAIResponsesModel` with `OpenRouterProvider`)
 */
export type CachePoint = {
  /**
   * Kind
   */
  kind?: "cache-point";
  /**
   * Ttl
   */
  ttl?: "5m" | "1h";
};

/**
 * CacheStats
 */
export type CacheStats = {
  /**
   * Total Keys
   */
  total_keys: number;
};

/**
 * CancelEvaluationRunRequest
 *
 * Request to cancel pending evaluation results.
 */
export type CancelEvaluationRunRequest = {
  /**
   * Test Case Ids
   */
  test_case_ids?: Array<string> | null;
};

/**
 * CapabilityMove
 *
 * One capability an update moves, and from which version to which.
 */
export type CapabilityMove = {
  /**
   * Name
   */
  name: string;
  /**
   * Title
   */
  title: string;
  /**
   * From Version
   */
  from_version: number;
  /**
   * To Version
   */
  to_version: number;
};

/**
 * CapabilityPins
 *
 * A version per capability. Absent means "whatever that capability says
 * production runs", which for an opt-in one is off.
 */
export type CapabilityPins = {
  [key: string]: number;
};

/**
 * CapabilitySelection
 */
export type CapabilitySelection = {
  /**
   * Name
   */
  name: string;
  /**
   * Version
   */
  version: number;
};

/**
 * CapabilityStatus
 *
 * One capability: where the agent sits, and everywhere it could sit.
 *
 * One shape for two questions, because they are the same facts read
 * differently — what this agent runs, and what it would gain. It was called
 * an *update*, which was true of only one of them.
 */
export type CapabilityStatus = {
  /**
   * Name
   */
  name: string;
  /**
   * Title
   */
  title: string;
  /**
   * Icon
   */
  icon: string;
  /**
   * Current Version
   */
  current_version: number;
  /**
   * Latest Version
   */
  latest_version: number;
  /**
   * Recommended Version
   */
  recommended_version: number;
  /**
   * Versions
   */
  versions: Array<number>;
  /**
   * Has Breaking
   */
  has_breaking: boolean;
  /**
   * Changes
   */
  changes: Array<CapabilityVersionChange>;
  /**
   * Requires
   */
  requires: {
    [key: string]: {
      [key: string]: number;
    };
  };
};

/**
 * CapabilityVersionChange
 */
export type CapabilityVersionChange = {
  /**
   * Title
   */
  title: string;
  /**
   * Details
   */
  details?: string;
  /**
   * Breaking
   */
  breaking?: boolean;
  /**
   * Beta
   */
  beta?: boolean;
  /**
   * Version
   */
  version: number;
};

/**
 * CaseActivityResponse
 */
export type CaseActivityResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Case Id
   */
  case_id: string;
  /**
   * Activity Type
   */
  activity_type: string;
  /**
   * Title
   */
  title: string;
  /**
   * Data
   */
  data: Array<CaseTimelineEvent> | null;
  /**
   * User Id
   */
  user_id: string | null;
  /**
   * Created At
   */
  created_at: string;
};

/**
 * CaseCreateResponse
 */
export type CaseCreateResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Display Id
   */
  display_id?: string | null;
  /**
   * Inbox Id
   */
  inbox_id: string;
  /**
   * Subject
   */
  subject: string;
  /**
   * Status
   */
  status: "open" | "pending" | "closed" | "cancelled" | "excluded";
  /**
   * Priority
   */
  priority: "low" | "medium" | "high" | "urgent";
  /**
   * Data
   */
  data: {
    [key: string]: unknown;
  };
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Email Thread
   */
  email_thread?: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * CaseLayout
 */
export type CaseLayout = {
  /**
   * Version
   */
  version?: 1;
  /**
   * Sections
   */
  sections?: Array<LayoutSection>;
};

/**
 * CaseLayoutUpdateRequest
 */
export type CaseLayoutUpdateRequest = {
  /**
   * Case-view field layout; null restores auto-placement
   */
  layout?: CaseLayout | null;
};

/**
 * CaseListResponse
 */
export type CaseListResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Display Id
   */
  display_id?: string | null;
  /**
   * Inbox Id
   */
  inbox_id: string;
  /**
   * Inbox Name
   */
  inbox_name?: string | null;
  /**
   * Subject
   */
  subject: string;
  /**
   * Status
   */
  status: "open" | "pending" | "closed" | "cancelled" | "excluded";
  /**
   * Priority
   */
  priority: "low" | "medium" | "high" | "urgent";
  /**
   * Data
   */
  data: {
    [key: string]: unknown;
  };
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Email Thread
   */
  email_thread?: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Closed At
   */
  closed_at?: string | null;
  /**
   * Is Processing
   */
  is_processing?: boolean;
};

/**
 * CaseMetric
 */
export type CaseMetric = {
  /**
   * Evaluator Id
   */
  evaluator_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type: string;
  /**
   * Score
   */
  score: number | null;
  /**
   * Passed
   */
  passed: boolean | null;
};

/**
 * CaseMove
 */
export type CaseMove = {
  /**
   * Target Inbox Id
   *
   * ID of the inbox to move the case to
   */
  target_inbox_id: string;
  /**
   * Clear History
   *
   * Remove all prior activities and email thread
   */
  clear_history?: boolean;
};

/**
 * CasePatch
 */
export type CasePatch = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Priority
   */
  priority?: "low" | "medium" | "high" | "critical" | null;
  /**
   * Preconditions
   */
  preconditions?: string | null;
  /**
   * Clear Preconditions
   */
  clear_preconditions?: boolean;
  /**
   * Steps
   */
  steps?: Array<TestStep> | null;
};

/**
 * CaseResponse
 */
export type CaseResponse = {
  /**
   * Inbox Id
   *
   * ID of the inbox this case belongs to
   */
  inbox_id: string;
  /**
   * Subject
   *
   * Case subject/title
   */
  subject: string;
  /**
   * Priority
   *
   * Case priority (low, medium, high, urgent)
   */
  priority?: "low" | "medium" | "high" | "urgent";
  /**
   * Data
   *
   * Case data
   */
  data?: {
    [key: string]: unknown;
  } | null;
  /**
   * Id
   */
  id: string;
  /**
   * Display Id
   */
  display_id?: string | null;
  /**
   * Status
   */
  status: "open" | "pending" | "closed" | "cancelled" | "excluded";
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Email Thread
   */
  email_thread?: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Closed At
   */
  closed_at?: string | null;
  /**
   * Tasks
   */
  tasks?: Array<TaskResponse> | null;
  /**
   * Activities
   */
  activities?: Array<CaseActivityResponse> | null;
  inbox?: InboxResponse | null;
  /**
   * Process Run Id
   */
  process_run_id?: string | null;
  /**
   * Process Workflow Id
   */
  process_workflow_id?: string | null;
  /**
   * Process Run Status
   */
  process_run_status?: string | null;
  /**
   * Is Processing
   */
  is_processing?: boolean;
  /**
   * Field Warnings
   */
  field_warnings?: Array<FieldWarning> | null;
};

/**
 * CaseTimelineEvent
 *
 * Generic fallback for backwards compat — allows any shape to serialize.
 */
export type CaseTimelineEvent = {
  [key: string]: unknown;
};

/**
 * CaseUpdate
 */
export type CaseUpdate = {
  /**
   * Subject
   */
  subject?: string | null;
  /**
   * Status
   */
  status?: "open" | "pending" | "closed" | "cancelled" | "excluded" | null;
  /**
   * Priority
   */
  priority?: "low" | "medium" | "high" | "urgent" | null;
  /**
   * Data
   */
  data?: {
    [key: string]: unknown;
  } | null;
};

/**
 * ChangeColumnTypeRequest
 */
export type ChangeColumnTypeRequest = {
  /**
   * New Type
   */
  new_type: string;
};

/**
 * ChangeColumnTypeResponse
 */
export type ChangeColumnTypeResponse = {
  table: TableResponse;
  /**
   * Separators Removed
   */
  separators_removed: number;
};

/**
 * ChangelogEntry
 */
export type ChangelogEntry = {
  /**
   * Title
   */
  title: string;
  /**
   * Content
   */
  content: string;
  /**
   * Summary
   */
  summary?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Version
   */
  version?: string | null;
  /**
   * Draft
   */
  draft?: boolean;
};

/**
 * ChannelMetadata
 *
 * Public metadata for a registered deployment channel.
 *
 * Mirrors ``ChannelDefinition.to_metadata()`` — typed so the OpenAPI
 * spec is precise and the frontend generated types are useful.
 */
export type ChannelMetadata = {
  /**
   * Channel Type
   */
  channel_type: string;
  /**
   * Label
   */
  label: string;
  /**
   * Description
   */
  description: string;
  /**
   * Icon Url
   */
  icon_url: string;
  /**
   * Preview Image Url
   */
  preview_image_url: string;
  /**
   * Color
   */
  color: string;
  /**
   * Required Integration
   */
  required_integration: string | null;
  /**
   * Order
   */
  order: number;
  /**
   * Supports App Url
   */
  supports_app_url: boolean;
  /**
   * Supports Embed
   */
  supports_embed: boolean;
  /**
   * Supports Transport Display
   */
  supports_transport_display: boolean;
  /**
   * Supports Customization
   */
  supports_customization: boolean;
  /**
   * Is External
   */
  is_external: boolean;
};

/**
 * ChaosProviderData
 *
 * Credential-free provider backing the fault-injection models.
 *
 * ``ChaosModel`` never makes a network call, so this exists only to satisfy
 * the shape everything else assumes — a model needs a provider to be linked
 * to, listed under, and activated through.
 *
 * Being in ``AnyProviderData`` is harmless on prod: the union only decides
 * how an existing row deserializes, and the row is never created there (see
 * ``spotflow.chaos_models`` and the CLI bootstrap).
 */
export type ChaosProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "test";
};

/**
 * ChaosSimulationResult
 */
export type ChaosSimulationResult = {
  /**
   * Ok
   */
  ok: number;
  /**
   * Fail
   */
  fail: number;
};

/**
 * ChaosTestResponse
 */
export type ChaosTestResponse = {
  simulated: ChaosSimulationResult;
  transitions: ChaosTransitionResult;
};

/**
 * ChaosTestingConfigResponse
 */
export type ChaosTestingConfigResponse = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * ChaosTestingConfigUpdate
 */
export type ChaosTestingConfigUpdate = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * ChaosTestingFlag
 */
export type ChaosTestingFlag = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * ChaosTransitionResult
 */
export type ChaosTransitionResult = {
  /**
   * Suspended
   */
  suspended: number;
  /**
   * Auto Deactivated
   */
  auto_deactivated: number;
  /**
   * Recovered
   */
  recovered: number;
  /**
   * Unchanged
   */
  unchanged: number;
};

/**
 * ChatTiming
 */
export type ChatTiming = {
  /**
   * Surface
   */
  surface: "genie" | "agent";
  /**
   * First Frame Ms
   */
  first_frame_ms?: number | null;
  /**
   * First Visible Ms
   */
  first_visible_ms: number;
};

/**
 * CheckResult
 */
export type CheckResult = {
  /**
   * Evaluator Id
   */
  evaluator_id: string;
  /**
   * Check
   */
  check: string;
  /**
   * Type
   */
  type: string;
  /**
   * Status
   */
  status: string;
  /**
   * Passed
   */
  passed: boolean | null;
  /**
   * Score
   */
  score: number | null;
  /**
   * Reason
   */
  reason: string | null;
};

/**
 * CheckStatus
 */
export const CheckStatus = {
  OK: "ok",
  DEGRADED: "degraded",
  DOWN: "down",
} as const;

/**
 * CheckStatus
 */
export type CheckStatus = (typeof CheckStatus)[keyof typeof CheckStatus];

/**
 * ClaimToMetadataMapping
 *
 * Maps a SAML/OIDC claim from the IdP to a key on ``user.metadata_``.
 *
 * Applied during autojoin (and on every login if ``sync_on_login`` is set):
 * the value of the claim ``claim`` is written to ``user.metadata_[metadata_key]``.
 * Multi-valued claims (e.g. ``groups``) are stringified by joining with ", ".
 * Missing claims are silently skipped — admins can layer rules to require
 * presence.
 */
export type ClaimToMetadataMapping = {
  /**
   * Claim
   */
  claim: string;
  /**
   * Metadata Key
   */
  metadata_key: string;
};

/**
 * ClientItem
 */
export type ClientItem = {
  /**
   * Id
   */
  id: string;
  /**
   * Label
   */
  label: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Last Used At
   */
  last_used_at: string | null;
};

/**
 * ClipboardDataEvent
 *
 * Remote→local clipboard sync.  Emitted after a ``clipboard_read``
 * command resolves the remote browser's clipboard.  ``text`` is None
 * on read failure (no permission, transient activation lost, etc.) —
 * the frontend should silently no-op rather than clobbering the user's
 * local clipboard with empty data.
 */
export type ClipboardDataEvent = {
  /**
   * Type
   */
  type: "clipboard_data";
  /**
   * Text
   */
  text?: string | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * CoWorkerSettings
 */
export type CoWorkerSettingsInput = {
  /**
   * Pii Banner
   *
   * Information about PII anonymization
   */
  pii_banner?: string;
  /**
   * Pii Info
   *
   * Information about PII anonymization
   */
  pii_info?: string;
  /**
   * Pii Lock
   *
   * Information about the guardrails
   */
  pii_lock?: boolean;
  /**
   * Pii Anonimization
   *
   * Preferred anonymization method for PII values.
   */
  pii_anonimization?: Array<{
    [key: string]: string;
  }>;
  /**
   * Divider1
   */
  divider1?: string;
  /**
   * Guardrail Info
   *
   * Information about the guardrails
   */
  guardrail_info?: string;
  /**
   * Guardrail Lock
   *
   * Locks the guardrails settings.
   */
  guardrail_lock?: boolean;
  /**
   * Guardrail Latency Banner
   */
  guardrail_latency_banner?: string;
  violence_and_hate?: GuardrailValue;
  sexual_content?: GuardrailValue;
  guns_and_weapons?: GuardrailValue;
  suicide?: GuardrailValue;
  crime?: GuardrailValue;
  toxicity?: GuardrailValue;
  bias?: GuardrailValue;
  profanity?: GuardrailValue;
  /**
   * Guardrails Scan Output
   *
   * Scan the model's response with the same semantic guardrails applied to input. Catches sensitive content sourced from Knowledge Base documents or tools that the input scan cannot see.
   */
  guardrails_scan_output?: boolean;
  /**
   * Guardrails Output Mode
   *
   * How output guardrails are enforced. 'post_stream' streams the response in real-time and warns/clears after the fact. 'pre_stream' buffers the full response, validates it against guardrails, and only emits it if it passes.
   */
  guardrails_output_mode?: "post_stream" | "pre_stream";
  /**
   * Divider2
   */
  divider2?: string;
  /**
   * Guardrail Action
   *
   * Choose the action to take when a guardrail is triggered.
   */
  guardrail_action?:
    | {
        [key: string]: string;
      }
    | string;
  /**
   * Guardrail Action Replace
   */
  guardrail_action_replace?: string;
  /**
   * Guardrail Action Human
   */
  guardrail_action_human?: string;
  /**
   * Block After Violations
   *
   * Block the chat after this many turns with guardrail violations. Set to 0 to never block (per-turn warnings still surface).
   */
  block_after_violations?: number;
  /**
   * Block Message
   *
   * Message shown to the user when the chat is locked.
   */
  block_message?: string;
  /**
   * Divider3
   */
  divider3?: string;
  /**
   * Prompt Defense
   */
  prompt_defense?: string;
  /**
   * Prompt Lock
   *
   * Locks the prompt defense settings
   */
  prompt_lock?: boolean;
  /**
   * Jailbreak
   *
   * Prevents attempts to manipulate or "jailbreak" the model into ignoring safety rules or system instructions (e.g., prompts like "ignore previous instructions").
   */
  jailbreak?: boolean;
  /**
   * Prompt Injection
   *
   * Detects and blocks strategies where users attempt to get around restrictions indirectly (e.g., asking the model to 'pretend' or speak as a fictional character to evade policies).
   */
  prompt_injection?: boolean;
  /**
   * Context Leak
   *
   * Prevents the model from unintentionally revealing internal context, instructions, or system messages that are not meant to be shared with the user.
   */
  context_leak?: boolean;
  /**
   * Auto Model Pick
   *
   * AI selects the optimal model for performance, cost savings or length
   */
  auto_model_pick?: boolean;
  /**
   * Auto Model Preference
   */
  auto_model_preference?: string;
  /**
   * Model
   *
   * Model configuration
   */
  model?: Array<string>;
  /**
   * Parameter Preset
   *
   * Controls randomness, diversity, and repetition of the model's output.
   */
  parameter_preset?: "default" | "creative" | "precise" | "custom";
  /**
   * Intro
   */
  intro?: string;
  /**
   * Basic Params Section
   */
  basic_params_section?: string;
  /**
   * Max Tokens
   *
   * The maximum number of tokens per request
   */
  max_tokens?: number;
  /**
   * Temperature
   *
   * The temperature of the model
   */
  temperature?: number;
  /**
   * Top P
   *
   * The top P of the model
   */
  top_p?: number;
  /**
   * Top K
   *
   * The top K of the model
   */
  top_k?: number;
  /**
   * Timeout
   *
   * The base timeout of the model
   */
  timeout?: number;
  /**
   * Divider 7
   */
  divider_7?: string;
  /**
   * Penalty Section
   */
  penalty_section?: string;
  /**
   * Presence Penalty
   *
   * The presence penalty of the model
   */
  presence_penalty?: number;
  /**
   * Frequency Penalty
   *
   * The frequency penalty of the model
   */
  frequency_penalty?: number;
  /**
   * Stop Sequences
   *
   * The model will stop generating text when it encounters any of these stop sequences
   */
  stop_sequences?: Array<string>;
  /**
   * Divider 8
   */
  divider_8?: string;
  /**
   * Reasoning Section
   */
  reasoning_section?: string;
  /**
   * Enable Reasoning
   *
   * Whether to enable reasoning
   */
  enable_reasoning?: boolean;
  /**
   * Reasoning Effort
   *
   * The reasoning effort of the model
   */
  reasoning_effort?: string;
  /**
   * Divider
   */
  divider?: string;
  /**
   * Reliability Section
   */
  reliability_section?: string;
  /**
   * Max Retries Per Model
   *
   * The maximum number of retries per model
   */
  max_retries_per_model?: number;
  /**
   * Retry On Status Codes
   *
   * The status codes to retry on
   */
  retry_on_status_codes?: Array<number>;
  /**
   * Datacenter Fallback
   *
   * Whether to fallback to a different datacenter of the same model if the current one is down
   */
  datacenter_fallback?: boolean;
  /**
   * Exclude User Identity From Prompt
   *
   * Omit the user's name from the system prompt. Keeps the cached instruction prefix identical across users so it can be reused, and avoids sending user identity to the model.
   */
  exclude_user_identity_from_prompt?: boolean;
  /**
   * Enable Prompt Caching
   *
   * Cache the stable instruction prefix at the model provider to cut input cost and latency on repeated turns. Disable to send every request uncached.
   */
  enable_prompt_caching?: boolean;
  /**
   * Protect Instructions In Preview
   *
   * Deployed conversations always keep the agent's system instructions, persona, tools, and configuration confidential. Turn this on to also protect them in the preview/dev page; otherwise the safeguard is dropped there so builders can inspect the agent they are configuring.
   */
  protect_instructions_in_preview?: boolean;
  /**
   * Usage Limits Section
   */
  usage_limits_section?: string;
  /**
   * Usage Limits Maximum Request
   *
   * The maximum number of requests
   */
  usage_limits_maximum_request?: number;
  /**
   * Usage Limits Maximum Tokens
   *
   * The maximum number of tokens
   */
  usage_limits_maximum_tokens?: number;
  /**
   * Usage Limits Max Messages
   *
   * The maximum number of messages
   */
  usage_limits_max_messages?: number;
  /**
   * Usage Limits Agent Timeout
   *
   * The maximum number of seconds the agent can run for
   */
  usage_limits_agent_timeout?: number;
  /**
   * Divider 11
   */
  divider_11?: string;
  /**
   * Compaction Enabled
   *
   * Automatically summarize old messages when context limit is approached.
   */
  compaction_enabled?: boolean;
  /**
   * Compaction Strategy
   *
   * How to compact chat history when it grows too long.
   */
  compaction_strategy?: string;
  /**
   * Compaction Threshold
   *
   * How full the context window may get before compaction runs (0.01 to 0.95). Lower compacts sooner and more often.
   */
  compaction_threshold?: number;
  /**
   * Compaction Tool Enabled
   *
   * Expose a tool that lets the agent request compaction manually.
   */
  compaction_tool_enabled?: boolean;
  /**
   * Tool Calling Section
   */
  tool_calling_section?: string;
  /**
   * Parallel Tool Calls
   *
   * Whether to allow the agent to call multiple tools at the same time.
   */
  parallel_tool_calls?: boolean;
  /**
   * Tool Timeout
   *
   * Maximum seconds a single tool call can run before timing out.
   */
  tool_timeout?: number;
  /**
   * Visuals Section
   */
  visuals_section?: string;
  /**
   * Hide Reasoning
   *
   * Whether to hide the reasoning messages
   */
  hide_reasoning?: boolean;
  /**
   * Group Into Chain Of Thought
   *
   * Group reasoning and tool calls into a collapsible chain of thought
   */
  group_into_chain_of_thought?: boolean;
  /**
   * Chain Of Thought Final Answer Only
   *
   * The agent only responds at the end, keeping all intermediate messages in its chain of thought.
   */
  chain_of_thought_final_answer_only?: boolean;
  /**
   * Hide Workflow Progress
   *
   * Whether to hide the flow progress
   */
  hide_workflow_progress?: boolean;
  /**
   * Hide Error Banners
   *
   * When a turn fails on the web widget or an embed, hide the error banner from end users; the agent's own reply still explains what it could not do, and a turn that fails before it replies still offers a retry. The platform's own chat always shows the error while you build. Slack, Teams and the other channels never rendered these banners.
   */
  hide_error_banners?: boolean;
  /**
   * Tool Display Preset
   *
   * Controls how tool executions are shown during conversations
   */
  tool_display_preset?: string;
  /**
   * Minimal Caret Only
   *
   * Show a loading message while the agent is working.
   */
  minimal_caret_only?: boolean;
  /**
   * Message Footer Enabled
   *
   * Show a fixed footer under the agent's final reply in every channel.
   */
  message_footer_enabled?: boolean;
  /**
   * Message Footer
   *
   * Shown under the agent's final reply in every channel. Never sent to the model, so it cannot be reworded or dropped.
   */
  message_footer?: Array<NoxusTipTapDocumentInput> | null;
  /**
   * Minimal Tool Message
   */
  minimal_tool_message?: string;
  /**
   * Citation Style
   *
   * How inline citation references are displayed
   */
  citation_style?: string;
  /**
   * Deduplicate Sources By Url
   *
   * Group sources by unique URL in the sources panel
   */
  deduplicate_sources_by_url?: boolean;
  /**
   * Omit Citation Quotes
   *
   * Hide quoted text in citation bubbles, show only the URL
   */
  omit_citation_quotes?: boolean;
  /**
   * Show Source Images
   *
   * Show document page images presented by the agent expanded by default instead of collapsed behind the source images toggle
   */
  show_source_images?: boolean;
  /**
   * Chat Input Placeholder
   */
  chat_input_placeholder?: string;
  /**
   * Divider Visuals
   */
  divider_visuals?: string;
  /**
   * Transport Show Tool Calls
   */
  transport_show_tool_calls?: boolean;
  /**
   * Transport Show Sources
   */
  transport_show_sources?: boolean;
  /**
   * Transport Show Thinking
   */
  transport_show_thinking?: boolean;
  /**
   * Transport Allow Noop
   */
  transport_allow_noop?: boolean;
  /**
   * Transport Thinking Text
   */
  transport_thinking_text?: string;
  /**
   * Conversation Title Section
   */
  conversation_title_section?: string;
  /**
   * Agent Title Mode
   *
   * Configure how the agent chat title is displayed in the chat history
   */
  agent_title_mode?: "generate" | "first_message" | "last_message" | "custom";
  /**
   * Agent Title Prompt
   *
   * Custom instructions for the generated conversation title.
   */
  agent_title_prompt?: NoxusTipTapDocumentInput | string;
  /**
   * Divider Conversation Title
   */
  divider_conversation_title?: string;
  /**
   * Feedback Section
   */
  feedback_section?: string;
  /**
   * Feedback Placeholder
   *
   * Placeholder shown in the inline feedback box.
   */
  feedback_placeholder?: string;
  /**
   * Feedback Submit Label
   */
  feedback_submit_label?: string;
  /**
   * Feedback Cancel Label
   */
  feedback_cancel_label?: string;
  /**
   * Pinned behaviour version per agent capability
   */
  capability_versions?: CapabilityPins;
  /**
   * Is Coworker
   *
   * Whether the agent is a co-worker
   */
  is_coworker?: boolean;
  /**
   * Preset Selector
   *
   * Choose a preset to get started quickly.
   */
  preset_selector?: string | null;
  /**
   * Languages
   *
   * Welcome message per language. The deployed widget's language picks one, otherwise the visitor's browser locale; the default language is used when none matches.
   */
  languages?: Array<AgentLanguageInput>;
  /**
   * Default Language
   *
   * ISO 639-1 code of the language used when the visitor's locale matches none.
   */
  default_language?: string | null;
  /**
   * Persona
   *
   * Describe who the co-worker is: their background, role, or point of view.
   */
  persona?: Array<NoxusTipTapDocumentInput> | null;
  /**
   * Tone
   *
   * Define how the co-worker should communicate: its voice, attitude, and style.
   */
  tone?: Array<NoxusTipTapDocumentInput> | null;
  /**
   * Extra Instructions
   *
   * Provide specific rules, goals, or context to guide the co-worker's behavior.
   */
  extra_instructions?: Array<NoxusTipTapDocumentInput> | null;
  /**
   * Tools
   */
  tools?: Array<
    | ({
        type: "web_research";
      } & WebResearchSettingsInput)
    | ({
        type: "noxus_qa";
      } & NoxusQaSettingsInput)
    | ({
        type: "kb_selector";
      } & KnowledgeBaseSelectorSettingsInput)
    | ({
        type: "kb_qa";
      } & KnowledgeBaseQaSettingsInput)
    | ({
        type: "workflow";
      } & WorkflowSettingsInput)
    | ({
        type: "attach_file";
      } & AttachFileSettings)
    | ({
        type: "human_in_the_loop";
      } & HumanInTheLoopSettingsInput)
    | ({
        type: "agent_tool";
      } & AgentToolSettingsInput)
    | ({
        type: "memory";
      } & MemorySettingsInput)
    | ({
        type: "action";
      } & ActionToolSettingsInput)
    | ({
        type: "excel";
      } & ExcelSettingsInput)
    | ({
        type: "docx";
      } & DocxSettingsInput)
    | ({
        type: "filesystem";
      } & FileSystemSettingsInput)
    | ({
        type: "files";
      } & FileSettingsInput)
    | ({
        type: "todos";
      } & TodoSettingsInput)
    | ({
        type: "sandbox";
      } & SandboxSettingsInput)
    | ({
        type: "schedule_tool";
      } & ScheduleToolSettingsInput)
    | ({
        type: "subagent";
      } & SubAgentSettingsInput)
    | ({
        type: "code_execution";
      } & CodeExecutionSettingsInput)
    | ({
        type: "agent_memory";
      } & AgentMemorySettingsInput)
    | ({
        type: "create_files";
      } & CreateFilesSettingsInput)
    | ({
        type: "documentation";
      } & DocumentationSettingsInput)
    | ({
        type: "image_generation";
      } & ImageGenerationSettingsInput)
    | ({
        type: "failure_probe";
      } & FailureProbeSettingsInput)
    | ({
        type: "code_mode";
      } & CodeModeSettingsInput)
    | ({
        type: "overflow";
      } & OverflowSettingsInput)
    | ({
        type: "guardrails";
      } & GuardrailsSettingsInput)
  >;
  /**
   * Workflows Parallel Enabled
   *
   * Let the agent start flows without blocking on them, then poll for the result. Off keeps every flow call blocking.
   */
  workflows_parallel_enabled?: boolean;
  /**
   * Mcp Servers
   *
   * Workspace MCP credentials this agent can call tools on.
   */
  mcp_servers?: Array<McpServerBinding>;
  /**
   * Convert Unsupported Files To Text
   *
   * When an attached file's type can't be sent to the model as a native attachment (e.g. JSON, CSV, XML, Markdown), inline its text content into the prompt instead of failing the run. PDFs and images are always sent as attachments.
   */
  convert_unsupported_files_to_text?: boolean;
  /**
   * Skills
   */
  skills?: Array<SkillConfig>;
  /**
   * Subagents Enabled
   *
   * Let the agent hand self-contained tasks to a built-in general-purpose subagent, which runs in its own fresh context on the Cost-Effective Models preset. Off keeps every task in the agent's own context.
   */
  subagents_enabled?: boolean;
  /**
   * Subagents
   */
  subagents?: Array<SubAgentDefinition>;
  /**
   * Guidelines Enabled
   *
   * Enable behavioral guidelines that shape how the agent responds.
   */
  guidelines_enabled?: boolean;
  /**
   * Guidelines
   */
  guidelines?: Array<GuidelineConfig>;
  /**
   * Glossary
   */
  glossary?: Array<GlossaryTermConfig>;
  /**
   * Memories
   *
   * Persistent memory across all conversations
   */
  memories?: Array<AgentMemoryEntry>;
  /**
   * Hidden Tools
   *
   * Tool calls that never appear in the chat activity feed.
   */
  hidden_tools?: Array<string>;
  /**
   * Deployment Custom Instructions
   */
  deployment_custom_instructions?: string | null;
  /**
   * Variables Enabled
   *
   * Create external values that can be mapped to flow inputs. The agent receives these external values through its widget deployment.
   */
  variables_enabled?: boolean;
  /**
   * Variables
   */
  variables?: Array<VariableDefinition>;
  /**
   * Workflows
   *
   * Workflows the agent can run
   */
  workflows?: Array<WorkflowSettingsInput>;
  /**
   * Knowledge Bases
   *
   * Knowledge bases the agent can search
   */
  knowledge_bases?: Array<KnowledgeBaseQaSettingsInput>;
  /**
   * Multi Kb Search
   *
   * Let one search cover several knowledge bases at once, merging their results. Off means every search targets a single one.
   */
  multi_kb_search?: boolean;
  /**
   * Citation Instructions Enabled
   *
   * Write your own rules for what the co-worker cites from its knowledge bases. Off keeps the built-in citation behaviour.
   */
  citation_instructions_enabled?: boolean;
  /**
   * Citation Instructions
   *
   * Tell the co-worker what to cite from its knowledge bases, when a claim needs a source, and how much to quote. The reference tag format itself is fixed and always applies.
   */
  citation_instructions?: Array<NoxusTipTapDocumentInput> | null;
  /**
   * Agent Files
   *
   * Files pre-loaded into every chat. Each is added to the session (readable via the file tools) with its context shown to the agent.
   */
  agent_files?: Array<AgentFileConfigInput>;
  /**
   * Databases
   *
   * SQL databases the agent can query read-only. Each references a database credential; access is deferred, so the agent connects only when a query is needed.
   */
  databases?: Array<AgentDatabaseConfigInput>;
  /**
   * Tables
   *
   * The workspace tables this agent can use. Add context for each so the agent knows what it holds and when to use it.
   */
  tables?: Array<AgentTableConfigInput>;
  /**
   * Coworkers
   *
   * Co-workers the agent can call
   */
  coworkers?: Array<AgentToolSettingsInput>;
  /**
   * Base Tools
   *
   * Quick action tools the agent can use
   */
  base_tools?: Array<
    | ({
        type: "web_research";
      } & WebResearchSettingsInput)
    | ({
        type: "human_in_the_loop";
      } & HumanInTheLoopSettingsInput)
    | ({
        type: "memory";
      } & MemorySettingsInput)
    | ({
        type: "attach_file";
      } & AttachFileSettings)
    | ({
        type: "filesystem";
      } & FileSystemSettingsInput)
    | ({
        type: "files";
      } & FileSettingsInput)
    | ({
        type: "code_execution";
      } & CodeExecutionSettingsInput)
    | ({
        type: "excel";
      } & ExcelSettingsInput)
    | ({
        type: "docx";
      } & DocxSettingsInput)
    | ({
        type: "noxus_qa";
      } & NoxusQaSettingsInput)
    | ({
        type: "kb_selector";
      } & KnowledgeBaseSelectorSettingsInput)
    | ({
        type: "todos";
      } & TodoSettingsInput)
    | ({
        type: "sandbox";
      } & SandboxSettingsInput)
    | ({
        type: "schedule_tool";
      } & ScheduleToolSettingsInput)
    | ({
        type: "subagent";
      } & SubAgentSettingsInput)
    | ({
        type: "agent_memory";
      } & AgentMemorySettingsInput)
    | ({
        type: "create_files";
      } & CreateFilesSettingsInput)
    | ({
        type: "documentation";
      } & DocumentationSettingsInput)
    | ({
        type: "image_generation";
      } & ImageGenerationSettingsInput)
    | ({
        type: "failure_probe";
      } & FailureProbeSettingsInput)
    | ({
        type: "code_mode";
      } & CodeModeSettingsInput)
    | ({
        type: "overflow";
      } & OverflowSettingsInput)
    | ({
        type: "guardrails";
      } & GuardrailsSettingsInput)
  >;
  /**
   * Actions
   *
   * Action nodes the agent can execute
   */
  actions?: Array<ActionToolSettingsInput>;
  /**
   * Insights Enabled
   *
   * Enable conversation analysis and insights dashboard.
   */
  insights_enabled?: boolean;
  /**
   * Insights Csat Enabled
   *
   * Collect customer satisfaction (CSAT) ratings at end of conversations.
   */
  insights_csat_enabled?: boolean;
  /**
   * Insights Csat Sampling Rate
   *
   * Fraction of conversations that get a CSAT prompt (1.0 = every conversation).
   */
  insights_csat_sampling_rate?: number;
  /**
   * Insights Csat Agent Tool Enabled
   *
   * Let the agent decide when to show the CSAT form via a tool call.
   */
  insights_csat_agent_tool_enabled?: boolean;
  /**
   * Insights Csat Agent Tool Instructions
   *
   * Custom instructions for when the agent should request customer feedback.
   */
  insights_csat_agent_tool_instructions?: NoxusTipTapDocumentInput | string;
  /**
   * Insights Csat Auto Enabled
   *
   * Automatically show the CSAT form at the end of an agent turn (rule-based, no tool call). Independent of the agent feedback tool.
   */
  insights_csat_auto_enabled?: boolean;
  /**
   * Insights Csat Auto Min Messages
   *
   * Only auto-request feedback once the user has sent at least this many messages.
   */
  insights_csat_auto_min_messages?: number;
  /**
   * Insights Custom Definitions
   *
   * User-defined situations to detect in each conversation.
   */
  insights_custom_definitions?: Array<CustomInsightDefinition>;
  /**
   * Tool Grant Scope
   *
   * Whether 'Always Allow' tool grants apply to all workspace users or only the granting user
   */
  tool_grant_scope?: "workspace" | "user";
};

/**
 * CoWorkerSettings
 */
export type CoWorkerSettingsOutput = {
  /**
   * Pii Banner
   *
   * Information about PII anonymization
   */
  pii_banner?: string;
  /**
   * Pii Info
   *
   * Information about PII anonymization
   */
  pii_info?: string;
  /**
   * Pii Lock
   *
   * Information about the guardrails
   */
  pii_lock?: boolean;
  /**
   * Pii Anonimization
   *
   * Preferred anonymization method for PII values.
   */
  pii_anonimization?: Array<{
    [key: string]: string;
  }>;
  /**
   * Divider1
   */
  divider1?: string;
  /**
   * Guardrail Info
   *
   * Information about the guardrails
   */
  guardrail_info?: string;
  /**
   * Guardrail Lock
   *
   * Locks the guardrails settings.
   */
  guardrail_lock?: boolean;
  /**
   * Guardrail Latency Banner
   */
  guardrail_latency_banner?: string;
  violence_and_hate?: GuardrailValue;
  sexual_content?: GuardrailValue;
  guns_and_weapons?: GuardrailValue;
  suicide?: GuardrailValue;
  crime?: GuardrailValue;
  toxicity?: GuardrailValue;
  bias?: GuardrailValue;
  profanity?: GuardrailValue;
  /**
   * Guardrails Scan Output
   *
   * Scan the model's response with the same semantic guardrails applied to input. Catches sensitive content sourced from Knowledge Base documents or tools that the input scan cannot see.
   */
  guardrails_scan_output?: boolean;
  /**
   * Guardrails Output Mode
   *
   * How output guardrails are enforced. 'post_stream' streams the response in real-time and warns/clears after the fact. 'pre_stream' buffers the full response, validates it against guardrails, and only emits it if it passes.
   */
  guardrails_output_mode?: "post_stream" | "pre_stream";
  /**
   * Divider2
   */
  divider2?: string;
  /**
   * Guardrail Action
   *
   * Choose the action to take when a guardrail is triggered.
   */
  guardrail_action?:
    | {
        [key: string]: string;
      }
    | string;
  /**
   * Guardrail Action Replace
   */
  guardrail_action_replace?: string;
  /**
   * Guardrail Action Human
   */
  guardrail_action_human?: string;
  /**
   * Block After Violations
   *
   * Block the chat after this many turns with guardrail violations. Set to 0 to never block (per-turn warnings still surface).
   */
  block_after_violations?: number;
  /**
   * Block Message
   *
   * Message shown to the user when the chat is locked.
   */
  block_message?: string;
  /**
   * Divider3
   */
  divider3?: string;
  /**
   * Prompt Defense
   */
  prompt_defense?: string;
  /**
   * Prompt Lock
   *
   * Locks the prompt defense settings
   */
  prompt_lock?: boolean;
  /**
   * Jailbreak
   *
   * Prevents attempts to manipulate or "jailbreak" the model into ignoring safety rules or system instructions (e.g., prompts like "ignore previous instructions").
   */
  jailbreak?: boolean;
  /**
   * Prompt Injection
   *
   * Detects and blocks strategies where users attempt to get around restrictions indirectly (e.g., asking the model to 'pretend' or speak as a fictional character to evade policies).
   */
  prompt_injection?: boolean;
  /**
   * Context Leak
   *
   * Prevents the model from unintentionally revealing internal context, instructions, or system messages that are not meant to be shared with the user.
   */
  context_leak?: boolean;
  /**
   * Auto Model Pick
   *
   * AI selects the optimal model for performance, cost savings or length
   */
  auto_model_pick?: boolean;
  /**
   * Auto Model Preference
   */
  auto_model_preference?: string;
  /**
   * Model
   *
   * Model configuration
   */
  model?: Array<string>;
  /**
   * Parameter Preset
   *
   * Controls randomness, diversity, and repetition of the model's output.
   */
  parameter_preset?: "default" | "creative" | "precise" | "custom";
  /**
   * Intro
   */
  intro?: string;
  /**
   * Basic Params Section
   */
  basic_params_section?: string;
  /**
   * Max Tokens
   *
   * The maximum number of tokens per request
   */
  max_tokens?: number;
  /**
   * Temperature
   *
   * The temperature of the model
   */
  temperature?: number;
  /**
   * Top P
   *
   * The top P of the model
   */
  top_p?: number;
  /**
   * Top K
   *
   * The top K of the model
   */
  top_k?: number;
  /**
   * Timeout
   *
   * The base timeout of the model
   */
  timeout?: number;
  /**
   * Divider 7
   */
  divider_7?: string;
  /**
   * Penalty Section
   */
  penalty_section?: string;
  /**
   * Presence Penalty
   *
   * The presence penalty of the model
   */
  presence_penalty?: number;
  /**
   * Frequency Penalty
   *
   * The frequency penalty of the model
   */
  frequency_penalty?: number;
  /**
   * Stop Sequences
   *
   * The model will stop generating text when it encounters any of these stop sequences
   */
  stop_sequences?: Array<string>;
  /**
   * Divider 8
   */
  divider_8?: string;
  /**
   * Reasoning Section
   */
  reasoning_section?: string;
  /**
   * Enable Reasoning
   *
   * Whether to enable reasoning
   */
  enable_reasoning?: boolean;
  /**
   * Reasoning Effort
   *
   * The reasoning effort of the model
   */
  reasoning_effort?: string;
  /**
   * Divider
   */
  divider?: string;
  /**
   * Reliability Section
   */
  reliability_section?: string;
  /**
   * Max Retries Per Model
   *
   * The maximum number of retries per model
   */
  max_retries_per_model?: number;
  /**
   * Retry On Status Codes
   *
   * The status codes to retry on
   */
  retry_on_status_codes?: Array<number>;
  /**
   * Datacenter Fallback
   *
   * Whether to fallback to a different datacenter of the same model if the current one is down
   */
  datacenter_fallback?: boolean;
  /**
   * Exclude User Identity From Prompt
   *
   * Omit the user's name from the system prompt. Keeps the cached instruction prefix identical across users so it can be reused, and avoids sending user identity to the model.
   */
  exclude_user_identity_from_prompt?: boolean;
  /**
   * Enable Prompt Caching
   *
   * Cache the stable instruction prefix at the model provider to cut input cost and latency on repeated turns. Disable to send every request uncached.
   */
  enable_prompt_caching?: boolean;
  /**
   * Protect Instructions In Preview
   *
   * Deployed conversations always keep the agent's system instructions, persona, tools, and configuration confidential. Turn this on to also protect them in the preview/dev page; otherwise the safeguard is dropped there so builders can inspect the agent they are configuring.
   */
  protect_instructions_in_preview?: boolean;
  /**
   * Usage Limits Section
   */
  usage_limits_section?: string;
  /**
   * Usage Limits Maximum Request
   *
   * The maximum number of requests
   */
  usage_limits_maximum_request?: number;
  /**
   * Usage Limits Maximum Tokens
   *
   * The maximum number of tokens
   */
  usage_limits_maximum_tokens?: number;
  /**
   * Usage Limits Max Messages
   *
   * The maximum number of messages
   */
  usage_limits_max_messages?: number;
  /**
   * Usage Limits Agent Timeout
   *
   * The maximum number of seconds the agent can run for
   */
  usage_limits_agent_timeout?: number;
  /**
   * Divider 11
   */
  divider_11?: string;
  /**
   * Compaction Enabled
   *
   * Automatically summarize old messages when context limit is approached.
   */
  compaction_enabled?: boolean;
  /**
   * Compaction Strategy
   *
   * How to compact chat history when it grows too long.
   */
  compaction_strategy?: string;
  /**
   * Compaction Threshold
   *
   * How full the context window may get before compaction runs (0.01 to 0.95). Lower compacts sooner and more often.
   */
  compaction_threshold?: number;
  /**
   * Compaction Tool Enabled
   *
   * Expose a tool that lets the agent request compaction manually.
   */
  compaction_tool_enabled?: boolean;
  /**
   * Tool Calling Section
   */
  tool_calling_section?: string;
  /**
   * Parallel Tool Calls
   *
   * Whether to allow the agent to call multiple tools at the same time.
   */
  parallel_tool_calls?: boolean;
  /**
   * Tool Timeout
   *
   * Maximum seconds a single tool call can run before timing out.
   */
  tool_timeout?: number;
  /**
   * Visuals Section
   */
  visuals_section?: string;
  /**
   * Hide Reasoning
   *
   * Whether to hide the reasoning messages
   */
  hide_reasoning?: boolean;
  /**
   * Group Into Chain Of Thought
   *
   * Group reasoning and tool calls into a collapsible chain of thought
   */
  group_into_chain_of_thought?: boolean;
  /**
   * Chain Of Thought Final Answer Only
   *
   * The agent only responds at the end, keeping all intermediate messages in its chain of thought.
   */
  chain_of_thought_final_answer_only?: boolean;
  /**
   * Hide Workflow Progress
   *
   * Whether to hide the flow progress
   */
  hide_workflow_progress?: boolean;
  /**
   * Hide Error Banners
   *
   * When a turn fails on the web widget or an embed, hide the error banner from end users; the agent's own reply still explains what it could not do, and a turn that fails before it replies still offers a retry. The platform's own chat always shows the error while you build. Slack, Teams and the other channels never rendered these banners.
   */
  hide_error_banners?: boolean;
  /**
   * Tool Display Preset
   *
   * Controls how tool executions are shown during conversations
   */
  tool_display_preset?: string;
  /**
   * Minimal Caret Only
   *
   * Show a loading message while the agent is working.
   */
  minimal_caret_only?: boolean;
  /**
   * Message Footer Enabled
   *
   * Show a fixed footer under the agent's final reply in every channel.
   */
  message_footer_enabled?: boolean;
  /**
   * Message Footer
   *
   * Shown under the agent's final reply in every channel. Never sent to the model, so it cannot be reworded or dropped.
   */
  message_footer?: Array<NoxusTipTapDocumentOutput> | null;
  /**
   * Minimal Tool Message
   */
  minimal_tool_message?: string;
  /**
   * Citation Style
   *
   * How inline citation references are displayed
   */
  citation_style?: string;
  /**
   * Deduplicate Sources By Url
   *
   * Group sources by unique URL in the sources panel
   */
  deduplicate_sources_by_url?: boolean;
  /**
   * Omit Citation Quotes
   *
   * Hide quoted text in citation bubbles, show only the URL
   */
  omit_citation_quotes?: boolean;
  /**
   * Show Source Images
   *
   * Show document page images presented by the agent expanded by default instead of collapsed behind the source images toggle
   */
  show_source_images?: boolean;
  /**
   * Chat Input Placeholder
   */
  chat_input_placeholder?: string;
  /**
   * Divider Visuals
   */
  divider_visuals?: string;
  /**
   * Transport Show Tool Calls
   */
  transport_show_tool_calls?: boolean;
  /**
   * Transport Show Sources
   */
  transport_show_sources?: boolean;
  /**
   * Transport Show Thinking
   */
  transport_show_thinking?: boolean;
  /**
   * Transport Allow Noop
   */
  transport_allow_noop?: boolean;
  /**
   * Transport Thinking Text
   */
  transport_thinking_text?: string;
  /**
   * Conversation Title Section
   */
  conversation_title_section?: string;
  /**
   * Agent Title Mode
   *
   * Configure how the agent chat title is displayed in the chat history
   */
  agent_title_mode?: "generate" | "first_message" | "last_message" | "custom";
  /**
   * Agent Title Prompt
   *
   * Custom instructions for the generated conversation title.
   */
  agent_title_prompt?: NoxusTipTapDocumentOutput | string;
  /**
   * Divider Conversation Title
   */
  divider_conversation_title?: string;
  /**
   * Feedback Section
   */
  feedback_section?: string;
  /**
   * Feedback Placeholder
   *
   * Placeholder shown in the inline feedback box.
   */
  feedback_placeholder?: string;
  /**
   * Feedback Submit Label
   */
  feedback_submit_label?: string;
  /**
   * Feedback Cancel Label
   */
  feedback_cancel_label?: string;
  /**
   * Pinned behaviour version per agent capability
   */
  capability_versions?: CapabilityPins;
  /**
   * Is Coworker
   *
   * Whether the agent is a co-worker
   */
  is_coworker?: boolean;
  /**
   * Preset Selector
   *
   * Choose a preset to get started quickly.
   */
  preset_selector?: string | null;
  /**
   * Languages
   *
   * Welcome message per language. The deployed widget's language picks one, otherwise the visitor's browser locale; the default language is used when none matches.
   */
  languages?: Array<AgentLanguageOutput>;
  /**
   * Default Language
   *
   * ISO 639-1 code of the language used when the visitor's locale matches none.
   */
  default_language?: string | null;
  /**
   * Persona
   *
   * Describe who the co-worker is: their background, role, or point of view.
   */
  persona?: Array<NoxusTipTapDocumentOutput> | null;
  /**
   * Tone
   *
   * Define how the co-worker should communicate: its voice, attitude, and style.
   */
  tone?: Array<NoxusTipTapDocumentOutput> | null;
  /**
   * Extra Instructions
   *
   * Provide specific rules, goals, or context to guide the co-worker's behavior.
   */
  extra_instructions?: Array<NoxusTipTapDocumentOutput> | null;
  /**
   * Tools
   */
  tools?: Array<
    | ({
        type: "web_research";
      } & WebResearchSettingsOutput)
    | ({
        type: "noxus_qa";
      } & NoxusQaSettingsOutput)
    | ({
        type: "kb_selector";
      } & KnowledgeBaseSelectorSettingsOutput)
    | ({
        type: "kb_qa";
      } & KnowledgeBaseQaSettingsOutput)
    | ({
        type: "workflow";
      } & WorkflowSettingsOutput)
    | ({
        type: "attach_file";
      } & AttachFileSettings)
    | ({
        type: "human_in_the_loop";
      } & HumanInTheLoopSettingsOutput)
    | ({
        type: "agent_tool";
      } & AgentToolSettingsOutput)
    | ({
        type: "memory";
      } & MemorySettingsOutput)
    | ({
        type: "action";
      } & ActionToolSettingsOutput)
    | ({
        type: "excel";
      } & ExcelSettingsOutput)
    | ({
        type: "docx";
      } & DocxSettingsOutput)
    | ({
        type: "filesystem";
      } & FileSystemSettingsOutput)
    | ({
        type: "files";
      } & FileSettingsOutput)
    | ({
        type: "todos";
      } & TodoSettingsOutput)
    | ({
        type: "sandbox";
      } & SandboxSettingsOutput)
    | ({
        type: "schedule_tool";
      } & ScheduleToolSettingsOutput)
    | ({
        type: "subagent";
      } & SubAgentSettingsOutput)
    | ({
        type: "code_execution";
      } & CodeExecutionSettingsOutput)
    | ({
        type: "agent_memory";
      } & AgentMemorySettingsOutput)
    | ({
        type: "create_files";
      } & CreateFilesSettingsOutput)
    | ({
        type: "documentation";
      } & DocumentationSettingsOutput)
    | ({
        type: "image_generation";
      } & ImageGenerationSettingsOutput)
    | ({
        type: "failure_probe";
      } & FailureProbeSettingsOutput)
    | ({
        type: "code_mode";
      } & CodeModeSettingsOutput)
    | ({
        type: "overflow";
      } & OverflowSettingsOutput)
    | ({
        type: "guardrails";
      } & GuardrailsSettingsOutput)
  >;
  /**
   * Workflows Parallel Enabled
   *
   * Let the agent start flows without blocking on them, then poll for the result. Off keeps every flow call blocking.
   */
  workflows_parallel_enabled?: boolean;
  /**
   * Mcp Servers
   *
   * Workspace MCP credentials this agent can call tools on.
   */
  mcp_servers?: Array<McpServerBinding>;
  /**
   * Convert Unsupported Files To Text
   *
   * When an attached file's type can't be sent to the model as a native attachment (e.g. JSON, CSV, XML, Markdown), inline its text content into the prompt instead of failing the run. PDFs and images are always sent as attachments.
   */
  convert_unsupported_files_to_text?: boolean;
  /**
   * Skills
   */
  skills?: Array<SkillConfig>;
  /**
   * Subagents Enabled
   *
   * Let the agent hand self-contained tasks to a built-in general-purpose subagent, which runs in its own fresh context on the Cost-Effective Models preset. Off keeps every task in the agent's own context.
   */
  subagents_enabled?: boolean;
  /**
   * Subagents
   */
  subagents?: Array<SubAgentDefinition>;
  /**
   * Guidelines Enabled
   *
   * Enable behavioral guidelines that shape how the agent responds.
   */
  guidelines_enabled?: boolean;
  /**
   * Guidelines
   */
  guidelines?: Array<GuidelineConfig>;
  /**
   * Glossary
   */
  glossary?: Array<GlossaryTermConfig>;
  /**
   * Memories
   *
   * Persistent memory across all conversations
   */
  memories?: Array<AgentMemoryEntry>;
  /**
   * Hidden Tools
   *
   * Tool calls that never appear in the chat activity feed.
   */
  hidden_tools?: Array<string>;
  /**
   * Deployment Custom Instructions
   */
  deployment_custom_instructions?: string | null;
  /**
   * Variables Enabled
   *
   * Create external values that can be mapped to flow inputs. The agent receives these external values through its widget deployment.
   */
  variables_enabled?: boolean;
  /**
   * Variables
   */
  variables?: Array<VariableDefinition>;
  /**
   * Workflows
   *
   * Workflows the agent can run
   */
  workflows?: Array<WorkflowSettingsOutput>;
  /**
   * Knowledge Bases
   *
   * Knowledge bases the agent can search
   */
  knowledge_bases?: Array<KnowledgeBaseQaSettingsOutput>;
  /**
   * Multi Kb Search
   *
   * Let one search cover several knowledge bases at once, merging their results. Off means every search targets a single one.
   */
  multi_kb_search?: boolean;
  /**
   * Citation Instructions Enabled
   *
   * Write your own rules for what the co-worker cites from its knowledge bases. Off keeps the built-in citation behaviour.
   */
  citation_instructions_enabled?: boolean;
  /**
   * Citation Instructions
   *
   * Tell the co-worker what to cite from its knowledge bases, when a claim needs a source, and how much to quote. The reference tag format itself is fixed and always applies.
   */
  citation_instructions?: Array<NoxusTipTapDocumentOutput> | null;
  /**
   * Agent Files
   *
   * Files pre-loaded into every chat. Each is added to the session (readable via the file tools) with its context shown to the agent.
   */
  agent_files?: Array<AgentFileConfigOutput>;
  /**
   * Databases
   *
   * SQL databases the agent can query read-only. Each references a database credential; access is deferred, so the agent connects only when a query is needed.
   */
  databases?: Array<AgentDatabaseConfigOutput>;
  /**
   * Tables
   *
   * The workspace tables this agent can use. Add context for each so the agent knows what it holds and when to use it.
   */
  tables?: Array<AgentTableConfigOutput>;
  /**
   * Coworkers
   *
   * Co-workers the agent can call
   */
  coworkers?: Array<AgentToolSettingsOutput>;
  /**
   * Base Tools
   *
   * Quick action tools the agent can use
   */
  base_tools?: Array<
    | ({
        type: "web_research";
      } & WebResearchSettingsOutput)
    | ({
        type: "human_in_the_loop";
      } & HumanInTheLoopSettingsOutput)
    | ({
        type: "memory";
      } & MemorySettingsOutput)
    | ({
        type: "attach_file";
      } & AttachFileSettings)
    | ({
        type: "filesystem";
      } & FileSystemSettingsOutput)
    | ({
        type: "files";
      } & FileSettingsOutput)
    | ({
        type: "code_execution";
      } & CodeExecutionSettingsOutput)
    | ({
        type: "excel";
      } & ExcelSettingsOutput)
    | ({
        type: "docx";
      } & DocxSettingsOutput)
    | ({
        type: "noxus_qa";
      } & NoxusQaSettingsOutput)
    | ({
        type: "kb_selector";
      } & KnowledgeBaseSelectorSettingsOutput)
    | ({
        type: "todos";
      } & TodoSettingsOutput)
    | ({
        type: "sandbox";
      } & SandboxSettingsOutput)
    | ({
        type: "schedule_tool";
      } & ScheduleToolSettingsOutput)
    | ({
        type: "subagent";
      } & SubAgentSettingsOutput)
    | ({
        type: "agent_memory";
      } & AgentMemorySettingsOutput)
    | ({
        type: "create_files";
      } & CreateFilesSettingsOutput)
    | ({
        type: "documentation";
      } & DocumentationSettingsOutput)
    | ({
        type: "image_generation";
      } & ImageGenerationSettingsOutput)
    | ({
        type: "failure_probe";
      } & FailureProbeSettingsOutput)
    | ({
        type: "code_mode";
      } & CodeModeSettingsOutput)
    | ({
        type: "overflow";
      } & OverflowSettingsOutput)
    | ({
        type: "guardrails";
      } & GuardrailsSettingsOutput)
  >;
  /**
   * Actions
   *
   * Action nodes the agent can execute
   */
  actions?: Array<ActionToolSettingsOutput>;
  /**
   * Insights Enabled
   *
   * Enable conversation analysis and insights dashboard.
   */
  insights_enabled?: boolean;
  /**
   * Insights Csat Enabled
   *
   * Collect customer satisfaction (CSAT) ratings at end of conversations.
   */
  insights_csat_enabled?: boolean;
  /**
   * Insights Csat Sampling Rate
   *
   * Fraction of conversations that get a CSAT prompt (1.0 = every conversation).
   */
  insights_csat_sampling_rate?: number;
  /**
   * Insights Csat Agent Tool Enabled
   *
   * Let the agent decide when to show the CSAT form via a tool call.
   */
  insights_csat_agent_tool_enabled?: boolean;
  /**
   * Insights Csat Agent Tool Instructions
   *
   * Custom instructions for when the agent should request customer feedback.
   */
  insights_csat_agent_tool_instructions?: NoxusTipTapDocumentOutput | string;
  /**
   * Insights Csat Auto Enabled
   *
   * Automatically show the CSAT form at the end of an agent turn (rule-based, no tool call). Independent of the agent feedback tool.
   */
  insights_csat_auto_enabled?: boolean;
  /**
   * Insights Csat Auto Min Messages
   *
   * Only auto-request feedback once the user has sent at least this many messages.
   */
  insights_csat_auto_min_messages?: number;
  /**
   * Insights Custom Definitions
   *
   * User-defined situations to detect in each conversation.
   */
  insights_custom_definitions?: Array<CustomInsightDefinition>;
  /**
   * Tool Grant Scope
   *
   * Whether 'Always Allow' tool grants apply to all workspace users or only the granting user
   */
  tool_grant_scope?: "workspace" | "user";
};

/**
 * CodeExecutionSettings
 */
export type CodeExecutionSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "code_execution";
  /**
   * Timeout
   *
   * Maximum execution time in seconds
   */
  timeout?: number;
};

/**
 * CodeExecutionSettings
 */
export type CodeExecutionSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "code_execution";
  /**
   * Timeout
   *
   * Maximum execution time in seconds
   */
  timeout?: number;
};

/**
 * CodeGenerationRequest
 */
export type CodeGenerationRequest = {
  /**
   * User Request
   */
  user_request: string;
  /**
   * Existing Code
   */
  existing_code?: string | null;
  /**
   * Input Variables
   */
  input_variables: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Output Variables
   */
  output_variables: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Files As Objects
   */
  files_as_objects?: boolean;
};

/**
 * CodeGenerationResponse
 */
export type CodeGenerationResponse = {
  /**
   * Code
   */
  code: string;
  /**
   * Reasoning
   */
  reasoning: string;
};

/**
 * CodeModeSettings
 *
 * Route selected tools through a Monty ``run_code`` sandbox.
 *
 * The tools each capability declared read-only leave the model's native
 * tool list and become async functions inside one ``run_code`` tool:
 * the model writes the orchestration (loops, filters, chained calls) instead
 * of round-tripping every intermediate result through its context. Tool
 * bodies still execute host-side.
 */
export type CodeModeSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "code_mode";
};

/**
 * CodeModeSettings
 *
 * Route selected tools through a Monty ``run_code`` sandbox.
 *
 * The tools each capability declared read-only leave the model's native
 * tool list and become async functions inside one ``run_code`` tool:
 * the model writes the orchestration (loops, filters, chained calls) instead
 * of round-tripping every intermediate result through its context. Tool
 * bodies still execute host-side.
 */
export type CodeModeSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "code_mode";
};

/**
 * CombinedReport
 */
export type CombinedReport = {
  /**
   * Answer Quality Run Id
   */
  answer_quality_run_id: string;
  /**
   * Retrieval Run Id
   */
  retrieval_run_id: string;
  /**
   * Retrieval Config Hash
   */
  retrieval_config_hash: string;
  /**
   * Rows
   */
  rows: Array<CombinedRow>;
  measured_quadrant: QuadrantCounts | null;
  /**
   * Unmatched Answer Cases
   */
  unmatched_answer_cases: number;
  /**
   * Unmatched Retrieval Cases
   */
  unmatched_retrieval_cases: number;
};

/**
 * CombinedRow
 */
export type CombinedRow = {
  /**
   * Question
   */
  question: string;
  /**
   * Source Chunk Ids
   */
  source_chunk_ids: Array<string>;
  /**
   * Retrieval Metrics
   */
  retrieval_metrics: Array<CaseMetric>;
  /**
   * Retrieved Chunk Ids
   */
  retrieved_chunk_ids: Array<string>;
  /**
   * Retrieval Sufficient
   */
  retrieval_sufficient: boolean;
  /**
   * Latency Ms
   */
  latency_ms: number | null;
  /**
   * Rerank Ms
   */
  rerank_ms: number | null;
  /**
   * Embed Cost Usd
   */
  embed_cost_usd: number | null;
  /**
   * Rerank Cost Usd
   */
  rerank_cost_usd: number | null;
  /**
   * Quality Metrics
   */
  quality_metrics: Array<CaseMetric>;
  /**
   * Search Loop Ms
   */
  search_loop_ms: number | null;
  /**
   * Search Embed Cost Usd
   */
  search_embed_cost_usd: number | null;
  /**
   * Search Rerank Cost Usd
   */
  search_rerank_cost_usd: number | null;
  /**
   * Search Total Cost Usd
   */
  search_total_cost_usd: number | null;
};

/**
 * CommitRef
 */
export type CommitRef = {
  /**
   * Sha
   */
  sha: string;
  /**
   * Short Sha
   */
  short_sha: string;
  /**
   * Date
   */
  date: string;
  /**
   * Author
   */
  author: string;
  /**
   * Subject
   */
  subject: string;
  /**
   * Url
   */
  url: string | null;
  /**
   * Pr Number
   */
  pr_number: number | null;
  /**
   * Pr Url
   */
  pr_url: string | null;
  /**
   * Branch
   */
  branch?: string | null;
  /**
   * Tickets
   */
  tickets?: Array<TicketRef>;
};

/**
 * CompactionPart
 *
 * A compaction part that summarizes previous conversation history.
 *
 * Compaction parts contain an opaque or readable summary of prior messages,
 * produced by provider-specific compaction mechanisms. They must be round-tripped
 * back to the same provider in subsequent requests.
 *
 * For Anthropic, `content` contains a readable text summary.
 * For OpenAI, `content` is `None` and the encrypted data is stored in `provider_details`.
 */
export type CompactionPart = {
  /**
   * Content
   */
  content?: string | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "compaction";
};

/**
 * CompareRule
 */
export type CompareRule = {
  /**
   * Kind
   */
  kind: "compare";
  /**
   * Left
   */
  left: string;
  /**
   * Op
   */
  op: "lt" | "lte" | "gt" | "gte" | "eq" | "neq";
  /**
   * Right
   */
  right: string;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Strict
   */
  strict?: boolean;
};

/**
 * ComponentInfo
 */
export type ComponentInfo = {
  kind: ComponentKind;
  /**
   * Key
   */
  key: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * ComponentKind
 *
 * Registry surfaces that can be enabled/disabled.
 */
export const ComponentKind = {
  NODE: "node",
  TRIGGER: "trigger",
  DATASOURCE: "datasource",
  INTEGRATION: "integration",
  CHANNEL: "channel",
  TOOL: "tool",
} as const;

/**
 * ComponentKind
 *
 * Registry surfaces that can be enabled/disabled.
 */
export type ComponentKind = (typeof ComponentKind)[keyof typeof ComponentKind];

/**
 * ComponentListResponse
 */
export type ComponentListResponse = {
  /**
   * Nodes
   */
  nodes: Array<ComponentInfo>;
  /**
   * Triggers
   */
  triggers: Array<ComponentInfo>;
  /**
   * Datasources
   */
  datasources: Array<ComponentInfo>;
  /**
   * Integrations
   */
  integrations: Array<ComponentInfo>;
  /**
   * Channels
   */
  channels: Array<ComponentInfo>;
  /**
   * Tools
   */
  tools: Array<ComponentInfo>;
};

/**
 * ConfigLinkType
 */
export const ConfigLinkType = {
  MODEL: "model",
  EMBEDDING: "embedding",
  RERANKER: "reranker",
  CLASSIFIER: "classifier",
  VOICE: "voice",
} as const;

/**
 * ConfigLinkType
 */
export type ConfigLinkType =
  (typeof ConfigLinkType)[keyof typeof ConfigLinkType];

/**
 * ConfigMetricScore
 */
export type ConfigMetricScore = {
  /**
   * Evaluator Id
   */
  evaluator_id: string;
  /**
   * Evaluator Name
   */
  evaluator_name: string;
  /**
   * Evaluator Type
   */
  evaluator_type: string;
  /**
   * Mean Score
   */
  mean_score: number;
  /**
   * Pass Rate
   */
  pass_rate: number;
  /**
   * Scored Cases
   */
  scored_cases: number;
};

/**
 * ConfigResponse
 */
export type ConfigResponse = {
  /**
   * Config
   */
  config: {
    [key: string]: {
      [key: string]: unknown;
    };
  };
  /**
   * Inputs
   */
  inputs: Array<
    | Connector
    | VariableConnector
    | VariableTypeConnector
    | VariableTypeSizeConnector
    | FlowInputConnector
    | FlowOutputConnector
    | VariableTypeFlowInputConnector
    | VariableTypeFlowOutputConnector
  >;
  /**
   * Outputs
   */
  outputs: Array<
    | Connector
    | VariableConnector
    | VariableTypeConnector
    | VariableTypeSizeConnector
    | FlowInputConnector
    | FlowOutputConnector
    | VariableTypeFlowInputConnector
    | VariableTypeFlowOutputConnector
  >;
  /**
   * Details
   */
  details?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Ready
   */
  ready?: boolean;
  /**
   * Config Values
   */
  config_values?: {
    [key: string]: unknown;
  };
  /**
   * Config Values Overrides
   */
  config_values_overrides?: {
    [key: string]: unknown;
  };
  validation?: ValidationResponse | null;
  /**
   * Integrations
   */
  integrations?: Array<string | Array<string>>;
  /**
   * Credential Errors
   */
  credential_errors?: {
    [key: string]: string;
  };
  /**
   * Referenced Assets
   */
  referenced_assets?: Array<ReferencedAssetField>;
};

/**
 * ConfigResponseV2
 */
export type ConfigResponseV2 = {
  /**
   * Config
   */
  config: {
    [key: string]: {
      [key: string]: unknown;
    };
  };
  /**
   * Outputs
   */
  outputs?: Array<NodeOutputV2>;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Ready
   */
  ready?: boolean;
  /**
   * Config Values
   */
  config_values?: {
    [key: string]: unknown;
  };
  /**
   * Config Values Overrides
   */
  config_values_overrides?: {
    [key: string]: unknown;
  };
  validation?: ValidationResponse | null;
};

/**
 * ConfigResult
 */
export type ConfigResult = {
  /**
   * Retrieval Config Hash
   */
  retrieval_config_hash: string;
  /**
   * Retrieval Config
   */
  retrieval_config: {
    [key: string]: unknown;
  };
  /**
   * Metrics
   */
  metrics: Array<ConfigMetricScore>;
  /**
   * Mean Latency Ms
   */
  mean_latency_ms: number | null;
  latency_ms?: StatSummary | null;
  rerank_ms?: StatSummary | null;
  embed_cost_usd?: StatSummary | null;
  rerank_cost_usd?: StatSummary | null;
  total_cost_usd?: StatSummary | null;
};

/**
 * ConnectionState
 */
export type ConnectionState = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * ConnectionType
 */
export const ConnectionType = {
  GOOGLE_OAUTH2: "google-oauth2",
  GITHUB: "github",
  SAMLP: "samlp",
  AUTH0: "auth0",
  WINDOWSLIVE: "windowslive",
  OIDC: "oidc",
} as const;

/**
 * ConnectionType
 */
export type ConnectionType =
  (typeof ConnectionType)[keyof typeof ConnectionType];

/**
 * ConnectionsResponse
 */
export type ConnectionsResponse = {
  /**
   * Connections
   */
  connections: Array<Auth0Connection>;
  /**
   * Password Connection
   */
  password_connection: string | null;
  /**
   * Social Connections
   */
  social_connections: Array<Auth0Connection>;
  /**
   * Enterprise Connections
   */
  enterprise_connections: Array<Auth0Connection>;
};

/**
 * Connector
 */
export type Connector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "connector";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
};

/**
 * ConnectorAddress
 */
export type ConnectorAddress = {
  /**
   * Connector Name
   */
  connector_name: string;
  /**
   * Node Id
   */
  node_id: string;
  /**
   * Key
   */
  key?: string | null;
  /**
   * Optional
   */
  optional?: boolean;
};

/**
 * ConstraintType
 */
export const ConstraintType = {
  AIOPERATIONS: "aioperations",
  MESSAGES: "messages",
  KBFILES: "kbfiles",
  TOKENS: "tokens",
  WORKFLOWS: "workflows",
  TRIGGERS: "triggers",
  NCSSEARCH: "ncssearch",
  NCSSCRAPE: "ncsscrape",
} as const;

/**
 * ConstraintType
 */
export type ConstraintType =
  (typeof ConstraintType)[keyof typeof ConstraintType];

/**
 * Conversation
 */
export type Conversation = {
  /**
   * Id
   */
  id: string;
  /**
   * User Id
   */
  user_id: string | null;
  /**
   * Api Key Id
   */
  api_key_id: string | null;
  /**
   * Name
   */
  name: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Last Updated At
   */
  last_updated_at: string;
  /**
   * Etag
   */
  etag: string | null;
  settings: CoWorkerSettingsOutput;
  /**
   * Assistant Id
   */
  assistant_id?: string | null;
  status: RunStatus;
  /**
   * Manual
   */
  manual?: boolean;
  /**
   * Preview
   */
  preview?: boolean;
  /**
   * Assistant Version Id
   */
  assistant_version_id?: string | null;
  /**
   * Chat Transport
   */
  chat_transport?: string | null;
  /**
   * Channel Ref
   */
  channel_ref?: {
    [key: string]: unknown;
  } | null;
  /**
   * Deployment Id
   */
  deployment_id?: string | null;
  /**
   * Public Access Token
   */
  public_access_token?: string | null;
  /**
   * Todos
   */
  todos?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Is Locked
   */
  is_locked?: boolean;
  /**
   * Lock Message
   */
  lock_message?: string | null;
};

/**
 * ConversationBulkRequest
 *
 * Selects conversations for a bulk action either by explicit ids OR by the
 * same filter scope as the list view (for a "select all N" action).
 */
export type ConversationBulkRequest = {
  /**
   * Conversation Ids
   */
  conversation_ids?: Array<string> | null;
  /**
   * Assistant Id
   */
  assistant_id?: string | null;
  /**
   * Deployment Id
   */
  deployment_id?: string | null;
  /**
   * From Date
   */
  from_date?: string | null;
  /**
   * To Date
   */
  to_date?: string | null;
  /**
   * Has Feedback
   */
  has_feedback?: boolean;
  /**
   * Expected Count
   */
  expected_count?: number | null;
};

/**
 * ConversationBulkResult
 */
export type ConversationBulkResult = {
  /**
   * Succeeded
   */
  succeeded: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Errors
   */
  errors?: {
    [key: string]: string;
  };
};

/**
 * ConversationCostItem
 */
export type ConversationCostItem = {
  /**
   * Conversation Id
   */
  conversation_id: string;
  /**
   * Cost
   */
  cost: number;
};

/**
 * ConversationCostSummary
 */
export type ConversationCostSummary = {
  /**
   * Conversation Id
   */
  conversation_id: string;
  /**
   * Total Cost
   */
  total_cost: number;
  /**
   * Input Tokens
   */
  input_tokens: number;
  /**
   * Cached Input Tokens
   */
  cached_input_tokens: number;
  /**
   * Output Tokens
   */
  output_tokens: number;
  /**
   * Messages
   */
  messages: Array<MessageCostRow>;
};

/**
 * ConversationFile
 */
export type ConversationFile = {
  /**
   * Id
   */
  id: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Size
   */
  size: number | null;
  /**
   * Type
   */
  type: string | null;
  /**
   * Status
   */
  status: "pending" | "success" | "error";
  /**
   * Url
   */
  url: string | null;
  /**
   * B64 Content
   */
  b64_content?: string | null;
};

/**
 * ConversationFunnel
 *
 * The conversation funnel: stage drop-off + terminal outcomes.
 */
export type ConversationFunnel = {
  /**
   * Started
   */
  started: number;
  /**
   * Engaged
   */
  engaged: number;
  /**
   * Reached Outcome
   */
  reached_outcome: number;
  /**
   * Total
   */
  total: number;
  /**
   * Outcomes
   */
  outcomes: Array<FunnelOutcome>;
  /**
   * Resolution Rate
   */
  resolution_rate: number;
};

/**
 * ConversationHistoryPage
 *
 * Where a paged conversation read stopped.
 */
export type ConversationHistoryPage = {
  /**
   * Has More
   */
  has_more: boolean;
  /**
   * Before
   */
  before?: string | null;
};

/**
 * ConversationWithMessages
 */
export type ConversationWithMessages = {
  /**
   * Id
   */
  id: string;
  /**
   * User Id
   */
  user_id: string | null;
  /**
   * Api Key Id
   */
  api_key_id: string | null;
  /**
   * Name
   */
  name: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Last Updated At
   */
  last_updated_at: string;
  /**
   * Etag
   */
  etag: string | null;
  settings: CoWorkerSettingsOutput;
  /**
   * Assistant Id
   */
  assistant_id?: string | null;
  status: RunStatus;
  /**
   * Manual
   */
  manual?: boolean;
  /**
   * Preview
   */
  preview?: boolean;
  /**
   * Assistant Version Id
   */
  assistant_version_id?: string | null;
  /**
   * Chat Transport
   */
  chat_transport?: string | null;
  /**
   * Channel Ref
   */
  channel_ref?: {
    [key: string]: unknown;
  } | null;
  /**
   * Deployment Id
   */
  deployment_id?: string | null;
  /**
   * Public Access Token
   */
  public_access_token?: string | null;
  /**
   * Todos
   */
  todos?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Is Locked
   */
  is_locked?: boolean;
  /**
   * Lock Message
   */
  lock_message?: string | null;
  /**
   * Messages
   */
  messages: Array<Message>;
  history?: ConversationHistoryPage | null;
  /**
   * Vercel Messages
   */
  vercel_messages?: Array<UiMessage>;
};

/**
 * ConvertPlaywrightRequest
 *
 * Request to convert Playwright code to playbook JSON.
 */
export type ConvertPlaywrightRequest = {
  /**
   * Code
   *
   * Playwright codegen output (Python or JavaScript)
   */
  code: string;
};

/**
 * ConvertPlaywrightResponse
 *
 * Response with converted playbook JSON.
 */
export type ConvertPlaywrightResponse = {
  /**
   * Playbook
   */
  playbook: {
    [key: string]: unknown;
  };
  /**
   * Steps Count
   */
  steps_count: number;
  /**
   * Warnings
   */
  warnings?: Array<string>;
};

/**
 * CreateApiKeyRequest
 */
export type CreateApiKeyRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Tenant Admin
   */
  tenant_admin?: boolean;
  /**
   * Permissions
   */
  permissions?: Array<string> | null;
};

/**
 * CreateAssistant
 */
export type CreateAssistant = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Image Url
   */
  image_url?: string | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Author Name
   */
  author_name?: string | null;
  definition?: CoWorkerSettingsInput | null;
  [key: string]:
    | unknown
    | string
    | string
    | null
    | string
    | null
    | boolean
    | string
    | null
    | CoWorkerSettingsInput
    | null
    | undefined;
};

/**
 * CreateAssistantVersionRequest
 */
export type CreateAssistantVersionRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
};

/**
 * CreateCheckoutSessionRequest
 */
export type CreateCheckoutSessionRequest = {
  /**
   * Product Name
   */
  product_name: string;
};

/**
 * CreateConversation
 */
export type CreateConversation = {
  /**
   * Name
   */
  name?: string | null;
  settings?: CoWorkerSettingsInput | null;
  /**
   * Preview
   */
  preview?: boolean;
  /**
   * Assistant Version Id
   */
  assistant_version_id?: string | null;
  /**
   * Variables
   */
  variables?: {
    [key: string]: string;
  } | null;
};

/**
 * CreateDocument
 */
export type CreateDocument = {
  status: DocumentStatus;
  /**
   * Prefix
   */
  prefix?: string;
  /**
   * Name
   */
  name: string;
  /**
   * Doc Metadata
   */
  doc_metadata?: {
    [key: string]: unknown;
  };
  /**
   * Run Id
   */
  run_id?: string | null;
};

/**
 * CreateEvaluatorRequest
 *
 * Request to create an evaluator.
 *
 * Only behavioral config is passed - display_name/description are derived from the type.
 */
export type CreateEvaluatorRequest = {
  /**
   * Type
   */
  type: string;
  /**
   * Name
   */
  name: string;
  /**
   * Config
   */
  config?: {
    [key: string]: JsonValueInput;
  };
  /**
   * Test Case Id
   *
   * The agent test this check belongs to (agent suites).
   */
  test_case_id?: string | null;
  /**
   * Expected Output
   *
   * An agent check's expected value.
   */
  expected_output?: {
    [key: string]: JsonValueInput;
  } | null;
};

/**
 * CreateFilesSettings
 *
 * Unified setting for the file creation + presentation tools.
 *
 * Enables ``create_file`` (generate text/markdown/html/json/csv/code into the
 * session) and ``present_file`` (surface a session file as a preview/download
 * card in the chat). They share one toggle because they're useless apart —
 * creating a file the user can't see is dead weight, and presenting nothing
 * is a no-op.
 */
export type CreateFilesSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "create_files";
  /**
   * Allow Html Graphs
   *
   * Instruct the agent to use self-contained HTML (Plotly, Chart.js, ...) when generating charts and visualizations.
   */
  allow_html_graphs?: boolean;
  /**
   * Show Inline
   *
   * Render HTML and images directly in the chat instead of as a card.
   */
  show_inline?: boolean;
};

/**
 * CreateFilesSettings
 *
 * Unified setting for the file creation + presentation tools.
 *
 * Enables ``create_file`` (generate text/markdown/html/json/csv/code into the
 * session) and ``present_file`` (surface a session file as a preview/download
 * card in the chat). They share one toggle because they're useless apart —
 * creating a file the user can't see is dead weight, and presenting nothing
 * is a no-op.
 */
export type CreateFilesSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "create_files";
  /**
   * Allow Html Graphs
   *
   * Instruct the agent to use self-contained HTML (Plotly, Chart.js, ...) when generating charts and visualizations.
   */
  allow_html_graphs?: boolean;
  /**
   * Show Inline
   *
   * Render HTML and images directly in the chat instead of as a card.
   */
  show_inline?: boolean;
};

/**
 * CreateGroupRequest
 */
export type CreateGroupRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
};

/**
 * CreateKnowledgeBase
 */
export type CreateKnowledgeBase = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Document Types
   */
  document_types?: Array<string>;
  status?: TrainStatus;
  kb_type?: KbType;
  /**
   * Workflow Id
   */
  workflow_id?: string | null;
  /**
   * Node Id
   */
  node_id?: string | null;
  settings_?: KbConfig | null;
  /**
   * Version
   */
  version?: "v2" | "v3";
};

/**
 * CreatePeriodicTaskRequest
 */
export type CreatePeriodicTaskRequest = {
  /**
   * Message
   */
  message: string;
  /**
   * Frequency
   */
  frequency: "hourly" | "daily" | "weekly" | "monthly";
  /**
   * New Conversation
   */
  new_conversation?: boolean;
  /**
   * One Shot
   */
  one_shot?: boolean;
  /**
   * Time Of Day
   */
  time_of_day?: string | null;
  /**
   * Day Of Week
   */
  day_of_week?: string | null;
  /**
   * Day Of Month
   */
  day_of_month?: number | null;
  /**
   * Offset
   */
  offset?: number | null;
};

/**
 * CreateRunRequest
 */
export type CreateRunRequest = {
  /**
   * Input
   */
  input: {
    [key: string]: unknown;
  };
  /**
   * Node Ids
   */
  node_ids?: Array<string> | null;
  /**
   * Single Node Id
   */
  single_node_id?: string | null;
  /**
   * Group Id
   */
  group_id?: string | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
  /**
   * Tracing
   */
  tracing?: boolean;
  /**
   * Debugger
   */
  debugger?: string | null;
  /**
   * Callback Url
   */
  callback_url?: string | null;
};

/**
 * CreateTableRequest
 */
export type CreateTableRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Id Type
   */
  id_type?: "uuid" | "autoincrement";
  /**
   * Description
   */
  description?: string | null;
  /**
   * Columns
   */
  columns?: Array<DataTableColumn>;
};

/**
 * CreateTagRequest
 */
export type CreateTagRequest = {
  /**
   * Name
   *
   * Tag name
   */
  name: string;
  /**
   * Color
   *
   * Hex color code
   */
  color: string;
  /**
   * Description
   *
   * Optional tag description
   */
  description?: string | null;
};

/**
 * CreateTemplateRequest
 */
export type CreateTemplateRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Category
   */
  category: string;
  /**
   * Color
   */
  color: string;
  /**
   * Color2
   */
  color2: string;
};

/**
 * CreateTestCaseFromMessageRequest
 *
 * Request to create a test case from conversation messages up to a specified message.
 */
export type CreateTestCaseFromMessageRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Evaluators
   *
   * The test's checks, made with the test.
   */
  evaluators?: Array<NewCheck>;
};

/**
 * CreateTestCaseFromRunRequest
 *
 * Request to create a test case from a completed workflow run.
 */
export type CreateTestCaseFromRunRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Include Output
   */
  include_output?: boolean;
};

/**
 * CreateTestCaseRequest
 *
 * Request to create a test case.
 *
 * Accepts any format — normalization is handled by the service layer.
 */
export type CreateTestCaseRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Inputs
   */
  inputs: {
    [key: string]: unknown;
  };
  /**
   * Expected Outputs
   */
  expected_outputs?: {
    [key: string]: unknown;
  } | null;
  /**
   * Evaluators
   *
   * An agent test's checks, made with the test.
   */
  evaluators?: Array<NewCheck>;
};

/**
 * CreateTriggerRequest
 */
export type CreateTriggerRequest = {
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Name
   */
  name?: string | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
};

/**
 * CreateWorkflowRequest
 */
export type CreateWorkflowRequest = {
  /**
   * Name
   */
  name: string;
  type: WorkflowType;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Error Handler
   */
  error_handler?: string | null;
  meta?: WorkflowMeta | null;
};

/**
 * CreateWorkflowVersionRequest
 */
export type CreateWorkflowVersionRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
};

/**
 * CredentialEntry
 *
 * A single connected credential (one row in the credentials table).
 */
export type CredentialEntry = {
  /**
   * Scope
   */
  scope?: "workspace" | "server";
  /**
   * Id
   */
  id: string;
  /**
   * Display Name
   */
  display_name: string | null;
  /**
   * Status
   */
  status: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Connected By
   */
  connected_by?: string | null;
  /**
   * Connected By User Id
   */
  connected_by_user_id?: string | null;
  /**
   * Connected At
   */
  connected_at?: string | null;
  /**
   * Disabled Nodes
   */
  disabled_nodes?: Array<string>;
  /**
   * Disabled Triggers
   */
  disabled_triggers?: Array<string>;
};

/**
 * CredentialPayloadResponse
 */
export type CredentialPayloadResponse = {
  /**
   * Payload
   */
  payload: {
    [key: string]: unknown;
  };
};

/**
 * CustomContainer
 */
export type CustomContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "Custom";
  /**
   * Value
   */
  value: unknown;
};

/**
 * CustomInsightCount
 */
export type CustomInsightCount = {
  /**
   * Name
   */
  name: string;
  /**
   * Count
   */
  count: number;
  /**
   * Days
   */
  days?: Array<CustomInsightDay>;
};

/**
 * CustomInsightCounts
 */
export type CustomInsightCounts = {
  /**
   * Insights
   */
  insights: Array<CustomInsightCount>;
};

/**
 * CustomInsightDay
 */
export type CustomInsightDay = {
  /**
   * Date
   */
  date: string;
  /**
   * Count
   */
  count: number;
};

/**
 * CustomInsightDefinition
 *
 * A user-defined situation to detect in a conversation (binary).
 */
export type CustomInsightDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Scope
   */
  scope?:
    | "all"
    | "resolved"
    | "unresolved"
    | "escalated"
    | "abandoned"
    | "rated-high"
    | "rated-low";
};

/**
 * CustomMCPHeadersConnectRequest
 */
export type CustomMcpHeadersConnectRequest = {
  /**
   * Server Url
   */
  server_url: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Transport
   */
  transport?: "url" | "stdio";
  /**
   * Headers
   */
  headers?: {
    [key: string]: string;
  };
  /**
   * Send Identity
   */
  send_identity?: boolean;
};

/**
 * CustomMCPHeadersConnectResponse
 */
export type CustomMcpHeadersConnectResponse = {
  /**
   * Credential Id
   */
  credential_id: string;
};

/**
 * CustomMCPStartRequest
 */
export type CustomMcpStartRequest = {
  /**
   * Server Url
   */
  server_url: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Transport
   */
  transport?: "url" | "stdio";
  /**
   * Redirect Uri
   */
  redirect_uri: string;
  /**
   * Headers
   */
  headers?: {
    [key: string]: string;
  };
  /**
   * Client Id
   */
  client_id?: string | null;
};

/**
 * CustomMCPStartResponse
 */
export type CustomMcpStartResponse = {
  /**
   * Authorize Url
   */
  authorize_url: string;
  /**
   * State
   */
  state: string;
};

/**
 * CustomModelDraft
 *
 * What a user has to supply to add a model.
 *
 * Short on purpose. Everything omitted here has a defensible default; every
 * field present changes whether the model works or how it bills.
 */
export type CustomModelDraft = {
  /**
   * Model Id
   *
   * Platform-wide identifier used in presets and flow configs. Derived from the API slug when omitted: one fewer thing to invent, and the two are the same string often enough that asking twice was noise.
   */
  model_id?: string | null;
  /**
   * Name
   *
   * Display name.
   */
  name: string;
  /**
   * Model Api Name
   *
   * The exact slug sent to the endpoint. Verified against it before the model is saved.
   */
  model_api_name: string;
  /**
   * Provider Id
   *
   * The OpenAI-compatible provider whose base URL serves this model.
   */
  provider_id: string;
  /**
   * Max Input Tokens
   */
  max_input_tokens: number;
  /**
   * Max Output Tokens
   */
  max_output_tokens: number;
  /**
   * Pricing
   */
  pricing:
    | ({
        mode: "genai_prices";
      } & CustomModelPublishedPricing)
    | ({
        mode: "token_prices";
      } & CustomModelTokenPricing);
  /**
   * Description
   */
  description?: string;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
};

/**
 * CustomModelEdit
 *
 * What a user may change after the fact.
 *
 * ``model_id`` is absent on purpose: flows, agents and presets store it, so
 * renaming it would silently unpick every reference. ``provider_id`` is absent
 * too — which providers serve a model is the provider-link surface, not this
 * one.
 */
export type CustomModelEdit = {
  /**
   * Name
   */
  name: string;
  /**
   * Model Api Name
   */
  model_api_name: string;
  /**
   * Max Input Tokens
   */
  max_input_tokens: number;
  /**
   * Max Output Tokens
   */
  max_output_tokens: number;
  /**
   * Pricing
   */
  pricing:
    | ({
        mode: "genai_prices";
      } & CustomModelPublishedPricing)
    | ({
        mode: "token_prices";
      } & CustomModelTokenPricing);
  /**
   * Description
   */
  description?: string;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
};

/**
 * CustomModelPublishedPricing
 *
 * Bill from the published rates for a known model.
 *
 * Preferred over typing numbers in: the reference stays live, so a vendor
 * price change is picked up rather than frozen at whatever was true the day
 * the model was added. The reference is the pair ``(provider_ref, model_ref)``
 * — genai_prices lists one model id under several providers at different
 * rates, so the model id alone does not name a price.
 */
export type CustomModelPublishedPricing = {
  /**
   * Mode
   */
  mode?: "genai_prices";
  /**
   * Provider Ref
   *
   * genai_prices provider id.
   */
  provider_ref: string;
  /**
   * Model Ref
   *
   * genai_prices model id under that provider.
   */
  model_ref: string;
};

/**
 * CustomModelRepriceQueued
 */
export type CustomModelRepriceQueued = {
  /**
   * Job Id
   */
  job_id: number;
  /**
   * Message
   */
  message: string;
};

/**
 * CustomModelRepriceRequest
 */
export type CustomModelRepriceRequest = {
  /**
   * Since
   */
  since?: string | null;
};

/**
 * CustomModelSummary
 *
 * Present only on user-created models. Its absence is what tells the UI a
 * row is a built-in it must not offer to edit or delete.
 */
export type CustomModelSummary = {
  /**
   * Model Api Name
   */
  model_api_name: string;
  /**
   * Pricing
   */
  pricing:
    | ({
        mode: "genai_prices";
      } & CustomModelPublishedPricing)
    | ({
        mode: "token_prices";
      } & CustomModelTokenPricing);
};

/**
 * CustomModelTokenPricing
 *
 * The user told us what the endpoint charges.
 *
 * For anything the published catalogue doesn't know — a fine-tune, a private
 * deployment, a negotiated rate. Per million tokens, the unit every vendor's
 * price page uses; converted to the per-1k the runtime stores at the row.
 */
export type CustomModelTokenPricing = {
  /**
   * Mode
   */
  mode?: "token_prices";
  /**
   * Input Price Per Mtok
   *
   * USD per million input tokens.
   */
  input_price_per_mtok: number;
  /**
   * Output Price Per Mtok
   *
   * USD per million output tokens.
   */
  output_price_per_mtok: number;
  /**
   * Cache Read Price Per Mtok
   *
   * USD per million cached input tokens, when the endpoint discounts them. Absent means cached tokens bill at the input price.
   */
  cache_read_price_per_mtok?: number | null;
};

/**
 * DataTableColumn
 */
export type DataTableColumn = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Type
   */
  type: "string" | "number" | "boolean" | "datetime" | "file" | "reference";
  /**
   * Default
   */
  default?: string | number | number | boolean | null;
  /**
   * References
   */
  references?: string | null;
  /**
   * On Delete
   */
  on_delete?: "restrict" | "cascade" | "set_null";
};

/**
 * DataType
 */
export const DataType = {
  DICT: "dict",
  STR: "str",
  LIST: "list",
  IMAGE: "Image",
  AUDIO: "Audio",
  FILE: "File",
  QUOTE: "Quote",
  CUSTOM: "Custom",
  CHAT: "Chat",
  NUMBER: "number",
  BOOL: "bool",
  DATETIME: "datetime",
  BASEMODEL: "basemodel",
  OBJECT: "object",
} as const;

/**
 * DataType
 */
export type DataType = (typeof DataType)[keyof typeof DataType];

/**
 * DataTypeInformation
 */
export type DataTypeInformation = {
  /**
   * Data Types
   */
  data_types: Array<string>;
  /**
   * Supported Conversions
   */
  supported_conversions: {
    [key: string]: Array<string>;
  };
};

/**
 * DataUIPart
 *
 * Data part with dynamic type based on data name.
 */
export type DataUiPart = {
  /**
   * Type
   */
  type: string;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Data
   */
  data: unknown;
};

/**
 * DatabaseBackendActivity
 */
export type DatabaseBackendActivity = {
  /**
   * Pid
   */
  pid: number;
  /**
   * Database
   */
  database: "postgres" | "vector" | "procrastinate";
  /**
   * Database Name
   */
  database_name: string;
  /**
   * User
   */
  user?: string | null;
  /**
   * Application Name
   */
  application_name?: string | null;
  /**
   * Client Addr
   */
  client_addr?: string | null;
  /**
   * Backend Type
   */
  backend_type?: string | null;
  /**
   * State
   */
  state?: string | null;
  /**
   * Wait Event Type
   */
  wait_event_type?: string | null;
  /**
   * Wait Event
   */
  wait_event?: string | null;
  /**
   * Backend Start
   */
  backend_start?: string | null;
  /**
   * Xact Start
   */
  xact_start?: string | null;
  /**
   * Query Start
   */
  query_start?: string | null;
  /**
   * State Change
   */
  state_change?: string | null;
  /**
   * Xact Age Seconds
   */
  xact_age_seconds?: number | null;
  /**
   * Query Age Seconds
   */
  query_age_seconds?: number | null;
  /**
   * State Age Seconds
   */
  state_age_seconds?: number | null;
  /**
   * Idle In Transaction Seconds
   */
  idle_in_transaction_seconds?: number | null;
  /**
   * Lock Count
   */
  lock_count?: number;
  /**
   * Granted Lock Count
   */
  granted_lock_count?: number;
  /**
   * Waiting Lock Count
   */
  waiting_lock_count?: number;
  /**
   * Lock Modes
   */
  lock_modes?: Array<string>;
  /**
   * Lock Targets
   */
  lock_targets?: Array<string>;
  /**
   * Blocked By
   */
  blocked_by?: Array<number>;
  /**
   * Blocking Pids
   */
  blocking_pids?: Array<number>;
  /**
   * Query
   */
  query?: string;
};

/**
 * DatabaseLocksResponse
 */
export type DatabaseLocksResponse = {
  /**
   * Timestamp
   */
  timestamp: string;
  /**
   * Database
   */
  database: "postgres" | "vector" | "procrastinate";
  /**
   * Database Name
   */
  database_name: string;
  /**
   * Backends
   */
  backends: Array<DatabaseBackendActivity>;
};

/**
 * DatasourceSummary
 */
export type DatasourceSummary = {
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Image
   */
  image?: string | null;
};

/**
 * DateTimeContainer
 */
export type DateTimeContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "datetime";
  /**
   * Value
   */
  value: unknown;
};

/**
 * DebugRecomputeResponse
 */
export type DebugRecomputeResponse = {
  /**
   * Analyzed
   */
  analyzed: number;
  /**
   * Aggregates Emitted
   */
  aggregates_emitted: number;
  /**
   * Queued
   */
  queued?: boolean;
};

/**
 * DecisionModelConfigurationResponse
 */
export type DecisionModelConfigurationResponse = {
  /**
   * Decision Model Id
   */
  decision_model_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Creator
   */
  creator: string;
  /**
   * Capabilities
   */
  capabilities: Array<string>;
  /**
   * Compatible Providers
   */
  compatible_providers: Array<string>;
  /**
   * Is Deprecated
   */
  is_deprecated: boolean;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Image
   */
  image: string | null;
  /**
   * Provider Links
   */
  provider_links: Array<ProviderLinkResponse>;
};

/**
 * DecisionModelsDefinition
 */
export type DecisionModelsDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Slug
   */
  slug?: string | null;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Model Type
   */
  model_type: string;
  /**
   * Model Category
   */
  model_category?: string | null;
  /**
   * Properties
   */
  properties: LlmModelProperties | ModelProperties;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider
   */
  provider?: string | null;
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Provider Image
   */
  provider_image?: string | null;
  /**
   * Providers
   */
  providers?: Array<ProviderRef>;
  /**
   * Host
   */
  host?: string | null;
  /**
   * Location
   */
  location?: string | null;
  /**
   * Price
   */
  price?: string | null;
  /**
   * Release Date
   */
  release_date?: string | null;
  /**
   * Data Cutoff Date
   */
  data_cutoff_date?: string | null;
  /**
   * Retirement Date
   */
  retirement_date?: string | null;
  /**
   * Replaced By
   */
  replaced_by?: string | null;
  /**
   * Privacy Level
   */
  privacy_level?: string | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Is Available
   */
  is_available?: boolean;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Provider Is Active
   */
  provider_is_active?: boolean;
  /**
   * Has Link
   */
  has_link?: boolean;
  /**
   * Is Valid
   */
  is_valid?: boolean;
  /**
   * Is Healthy
   */
  is_healthy?: boolean;
  /**
   * Lifecycle State
   */
  lifecycle_state?: string;
  /**
   * Last Error
   */
  last_error?: string | null;
  /**
   * Consecutive Failures
   */
  consecutive_failures?: number;
  /**
   * Last Checked At
   */
  last_checked_at?: string | null;
  /**
   * Max Choice Options
   */
  max_choice_options?: number | null;
  /**
   * Max Score Levels
   */
  max_score_levels?: number | null;
};

/**
 * DeepSeekProviderData
 */
export type DeepSeekProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "deepseek";
  /**
   * Deepseek Api Key
   */
  DEEPSEEK_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * DeleteWorkflowRequest
 */
export type DeleteWorkflowRequest = {
  /**
   * Force
   */
  force?: boolean;
};

/**
 * DeploymentCreate
 */
export type DeploymentCreate = {
  /**
   * Channel Type
   */
  channel_type: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Alias
   */
  alias?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  };
  /**
   * Assistant Version Id
   */
  assistant_version_id?: string | null;
};

/**
 * DeploymentEvent
 *
 * Event entry for the deployment events view.
 */
export type DeploymentEvent = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * State
   */
  state: string;
  /**
   * Event Type
   */
  event_type: string;
  /**
   * Started Conversation
   */
  started_conversation?: boolean;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Ignore Reason
   */
  ignore_reason?: string | null;
  /**
   * Event Data
   */
  event_data?: {
    [key: string]: unknown;
  };
};

/**
 * DeploymentResponse
 */
export type DeploymentResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string | null;
  /**
   * Alias
   */
  alias?: string | null;
  /**
   * Assistant Id
   */
  assistant_id: string;
  /**
   * Channel Type
   */
  channel_type: string;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  };
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Published At
   */
  published_at: string | null;
  /**
   * Trigger Id
   */
  trigger_id: string | null;
  /**
   * Assistant Version Id
   */
  assistant_version_id: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Effective Active
   */
  effective_active: boolean;
  /**
   * Toggle On Action
   */
  toggle_on_action: "activate" | "resume";
  /**
   * Toggle Off Action
   */
  toggle_off_action: "deactivate" | "pause";
  /**
   * Config Read Only
   */
  config_read_only: boolean;
  native_teams_app?: NativeTeamsAppState | null;
};

/**
 * DeploymentSummary
 *
 * Lightweight deployment info for assistant list views.
 */
export type DeploymentSummary = {
  /**
   * Id
   */
  id: string;
  /**
   * Channel Type
   */
  channel_type: string;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Name
   */
  name?: string | null;
};

/**
 * DeploymentUpdate
 */
export type DeploymentUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Alias
   */
  alias?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Assistant Version Id
   */
  assistant_version_id?: string | null;
};

/**
 * DeploymentVersionUsage
 */
export type DeploymentVersionUsage = {
  /**
   * Deployment Id
   */
  deployment_id: string;
  /**
   * Name
   */
  name: string | null;
  /**
   * Channel Type
   */
  channel_type: string;
  /**
   * Is Active
   */
  is_active: boolean;
};

/**
 * DeviceAuthPollResponse
 */
export type DeviceAuthPollResponse = {
  /**
   * Status
   */
  status: string;
  /**
   * Credential Id
   */
  credential_id?: string | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * DeviceAuthStartResponse
 */
export type DeviceAuthStartResponse = {
  /**
   * Session Id
   */
  session_id: string;
  /**
   * Verification Url
   */
  verification_url: string;
  /**
   * User Code
   */
  user_code: string;
  /**
   * Expires In Seconds
   */
  expires_in_seconds: number;
  /**
   * Poll Interval Seconds
   */
  poll_interval_seconds: number;
};

/**
 * DictContainer
 */
export type DictContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "dict";
  /**
   * Value
   */
  value: unknown;
};

/**
 * DictationResponse
 */
export type DictationResponse = {
  /**
   * Text
   */
  text: string;
};

/**
 * Direction
 */
export const Direction = {
  DB_TO_GIT: "db_to_git",
  GIT_TO_DB: "git_to_db",
  BIDIRECTIONAL: "bidirectional",
} as const;

/**
 * Direction
 */
export type Direction = (typeof Direction)[keyof typeof Direction];

/**
 * DisabledAgentToolsResponse
 *
 * Tool ``type`` discriminators the config picker should treat specially.
 */
export type DisabledAgentToolsResponse = {
  /**
   * Disabled
   */
  disabled: Array<string>;
  /**
   * Beta
   */
  beta: Array<string>;
};

/**
 * DocInformation
 */
export type DocInformation = {
  /**
   * Title
   */
  title: string;
  /**
   * Content Type
   */
  content_type?: string | null;
  /**
   * Url
   */
  url?: string | null;
  /**
   * Drive Id
   */
  drive_id?: string | null;
  drive_metadata?: DriveItemMetadata | null;
  /**
   * Modified At
   */
  modified_at?: string | null;
};

/**
 * DocRef
 */
export type DocRef = {
  /**
   * Key
   */
  key: string;
  /**
   * Page
   */
  page: string;
  /**
   * Title
   */
  title: string;
  /**
   * Url
   */
  url: string;
};

/**
 * DocumentExtraction
 */
export type DocumentExtraction = {
  /**
   * Ocr Provider
   */
  ocr_provider:
    | "google_document_ai"
    | "aws_textract"
    | "azure_document_intelligence";
  /**
   * Ocr Model Version
   */
  ocr_model_version: string;
  /**
   * Pages
   */
  pages: number;
  /**
   * Fields
   */
  fields: {
    [key: string]: ExtractedValue | null;
  };
  /**
   * Tables
   */
  tables: {
    [key: string]: Array<{
      [key: string]: ExtractedValue | null;
    }>;
  };
};

/**
 * DocumentExtractionRequest
 */
export type DocumentExtractionRequest = {
  /**
   * File Id
   */
  file_id: string;
  /**
   * Extraction Schema
   */
  extraction_schema: Array<ExtractionSchemaEntry>;
  /**
   * Model
   *
   * Model ids or a preset reference such as preset:multi-modal-cost; the workspace's own preset is used, and only models that read images are tried
   */
  model?: Array<string>;
};

/**
 * DocumentMetadata
 *
 * Structural metadata for a word-processing document (Word).
 *
 * ``page_count`` is the authoring app's cached count from
 * ``docProps/app.xml`` — present only for Word-authored files, ``None`` when
 * the document came from another tool (Google Docs, LibreOffice, python-docx)
 * that does not write the cache. ``word_count`` / ``paragraph_count`` are
 * derived from the body text and always populated.
 */
export type DocumentMetadata = {
  /**
   * Kind
   */
  kind?: "document";
  /**
   * Word Count
   */
  word_count: number;
  /**
   * Paragraph Count
   */
  paragraph_count: number;
  /**
   * Page Count
   */
  page_count?: number | null;
};

/**
 * DocumentReferenceMetadata
 */
export type DocumentReferenceMetadata = {
  /**
   * Type
   */
  type?: "document";
  /**
   * Source
   */
  source: string;
  /**
   * Score
   */
  score: number;
  /**
   * Document Id
   */
  document_id: string;
  /**
   * Document Metadata
   */
  document_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Quote
   */
  quote?: string | null;
  /**
   * Page Number
   */
  page_number?: string | null;
  /**
   * Url
   */
  url?: string | null;
  /**
   * File Id
   */
  file_id?: string | null;
  /**
   * Ref Num
   */
  ref_num?: number | null;
};

/**
 * DocumentResult
 */
export type DocumentResult = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Kb Id
   */
  kb_id: string;
  /**
   * File Id
   */
  file_id: string | null;
  /**
   * Name
   */
  name: string;
  status: DocumentStatus;
  /**
   * Short Summary
   */
  short_summary: string | null;
  /**
   * Summary
   */
  summary: string | null;
  /**
   * Doc Type
   */
  doc_type: string | null;
  /**
   * Doc Metadata
   */
  doc_metadata: {
    [key: string]: unknown;
  };
  /**
   * Prefix
   */
  prefix: string;
  /**
   * M Content Type
   */
  m_content_type: string | null;
  /**
   * M Source Type
   */
  m_source_type: string | null;
  /**
   * M Source Metadata
   */
  m_source_metadata: {
    [key: string]: unknown;
  } | null;
};

/**
 * DocumentStatus
 */
export const DocumentStatus = {
  TRAINED: "trained",
  TRAINING: "training",
  ERROR: "error",
  UPLOADED: "uploaded",
  FOLDER: "folder",
  OUTDATED: "outdated",
} as const;

/**
 * DocumentStatus
 */
export type DocumentStatus =
  (typeof DocumentStatus)[keyof typeof DocumentStatus];

/**
 * DocumentUrl
 *
 * The URL of the document.
 */
export type DocumentUrl = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "document-url";
  /**
   * Media Type
   *
   * Return the media type of the file, based on the URL or the provided `media_type`.
   */
  readonly media_type: string;
  /**
   * Identifier
   *
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `FileUrl`.
   *
   * This identifier is only automatically passed to the model when the `FileUrl` is returned by a tool.
   * If you're passing the `FileUrl` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `FileUrl`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  readonly identifier: string;
};

/**
 * DocumentationSettings
 */
export type DocumentationSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "documentation";
};

/**
 * DocumentationSettings
 */
export type DocumentationSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "documentation";
};

/**
 * DocxSettings
 */
export type DocxSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "docx";
  /**
   * Author Name
   *
   * Author shown on track-change revisions and comments.
   */
  author_name?: string;
};

/**
 * DocxSettings
 */
export type DocxSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "docx";
  /**
   * Author Name
   *
   * Author shown on track-change revisions and comments.
   */
  author_name?: string;
};

/**
 * DriveItemMetadata
 */
export type DriveItemMetadata = {
  /**
   * Createddatetime
   */
  createdDateTime: string;
  /**
   * Etag
   */
  eTag?: string | null;
  /**
   * Id
   */
  id: string;
  /**
   * Lastmodifieddatetime
   */
  lastModifiedDateTime: string;
  /**
   * Name
   */
  name: string;
  /**
   * Weburl
   */
  webUrl: string;
  /**
   * Ctag
   */
  cTag?: string | null;
  /**
   * Size
   */
  size: number;
  /**
   * Createdby
   */
  createdBy?: {
    [key: string]: unknown;
  } | null;
  /**
   * Lastmodifiedby
   */
  lastModifiedBy?: {
    [key: string]: unknown;
  } | null;
  /**
   * Parentreference
   */
  parentReference?: {
    [key: string]: unknown;
  } | null;
  /**
   * Filesysteminfo
   */
  fileSystemInfo?: {
    [key: string]: unknown;
  } | null;
  folder?: Folder | null;
  file?: SpotflowBackendSchemasIntegrationsFile | null;
};

/**
 * DryRunChange
 */
export type DryRunChange = {
  /**
   * Kind
   */
  kind: string;
  /**
   * Slug
   */
  slug: string;
  /**
   * Name
   */
  name: string | null;
  /**
   * Action
   */
  action: string;
};

/**
 * DynamicToolApprovalRequestedPart
 *
 * Dynamic tool part in approval-requested state (awaiting user decision).
 */
export type DynamicToolApprovalRequestedPart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "approval-requested";
  /**
   * Input
   */
  input: unknown;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * DynamicToolApprovalRespondedPart
 *
 * Dynamic tool part in approval-responded state (user approved/denied, execution pending).
 */
export type DynamicToolApprovalRespondedPart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "approval-responded";
  /**
   * Input
   */
  input: unknown;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * DynamicToolInputAvailablePart
 *
 * Dynamic tool part in input-available state.
 */
export type DynamicToolInputAvailablePart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "input-available";
  /**
   * Input
   */
  input: unknown;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * DynamicToolInputStreamingPart
 *
 * Dynamic tool part in input-streaming state.
 */
export type DynamicToolInputStreamingPart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "input-streaming";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * DynamicToolOutputAvailablePart
 *
 * Dynamic tool part in output-available state.
 */
export type DynamicToolOutputAvailablePart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "output-available";
  /**
   * Input
   */
  input: unknown;
  /**
   * Output
   */
  output: unknown;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Preliminary
   */
  preliminary?: boolean | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * DynamicToolOutputDeniedPart
 *
 * Dynamic tool part in output-denied state (tool was denied, terminal state).
 */
export type DynamicToolOutputDeniedPart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "output-denied";
  /**
   * Input
   */
  input: unknown;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * DynamicToolOutputErrorPart
 *
 * Dynamic tool part in output-error state.
 */
export type DynamicToolOutputErrorPart = {
  /**
   * Type
   */
  type?: "dynamic-tool";
  /**
   * Toolname
   */
  toolName: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "output-error";
  /**
   * Input
   */
  input: unknown;
  /**
   * Errortext
   */
  errorText: string;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * EdgePreview
 */
export type EdgePreview = {
  from_id: ConnectorAddress;
  to_id: ConnectorAddress;
  /**
   * Id
   */
  id: string;
};

/**
 * EdgeProgress
 */
export type EdgeProgress = {
  status?: EdgeStatus;
};

/**
 * EdgeStatus
 */
export const EdgeStatus = {
  PENDING: "pending",
  ACTIVE: "active",
  SKIPPED: "skipped",
  COMPLETED: "completed",
} as const;

/**
 * EdgeStatus
 */
export type EdgeStatus = (typeof EdgeStatus)[keyof typeof EdgeStatus];

/**
 * EditGroupRequest
 */
export type EditGroupRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description: string | null;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
};

/**
 * EditUserForGroupRequest
 */
export type EditUserForGroupRequest = {
  /**
   * Role Id
   */
  role_id?: string | null;
  /**
   * Primary
   */
  primary?: boolean;
};

/**
 * EffectivePermissionsResponse
 *
 * Schema for effective permissions response.
 */
export type EffectivePermissionsResponse = {
  /**
   * User Id
   */
  user_id: string;
  /**
   * Tenant Permissions
   *
   * Effective tenant-level permissions
   */
  tenant_permissions: {
    [key: string]: boolean;
  };
  /**
   * Group Permissions
   *
   * Effective group-level permissions (if in a group context)
   */
  group_permissions?: {
    [key: string]: boolean;
  } | null;
  /**
   * Role Name
   *
   * Assigned role name
   */
  role_name?: string | null;
  /**
   * Has Custom Overrides
   *
   * Whether user has custom permission overrides
   */
  has_custom_overrides: boolean;
};

/**
 * ElevenLabsProviderData
 */
export type ElevenLabsProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "elevenlabs";
  /**
   * Elevenlabs Api Key
   */
  ELEVENLABS_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Elevenlabs Base Url
   */
  ELEVENLABS_BASE_URL?: string | null;
};

/**
 * EmailDeliverySettings
 */
export type EmailDeliverySettings = {
  /**
   * Mode
   */
  mode?: "environment" | "noxus" | "server" | "disabled";
  /**
   * Credential Id
   */
  credential_id?: string | null;
  /**
   * Sender
   */
  sender?: string | null;
};

/**
 * EmailVerificationRequest
 */
export type EmailVerificationRequest = {
  /**
   * User Id
   */
  user_id: string;
};

/**
 * EmailVerificationResponse
 */
export type EmailVerificationResponse = {
  /**
   * Email Verified
   */
  email_verified: boolean;
  /**
   * Email
   */
  email?: string | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * EmbedAuthorizeRequest
 */
export type EmbedAuthorizeRequest = {
  /**
   * Deployment Id
   */
  deployment_id: string;
  /**
   * Origin
   */
  origin: string;
};

/**
 * EmbeddingConfigurationResponse
 *
 * List endpoint response for embeddings.
 */
export type EmbeddingConfigurationResponse = {
  /**
   * Embedding Id
   */
  embedding_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Creator
   */
  creator: string;
  /**
   * Embedding Type
   */
  embedding_type: string;
  /**
   * Capabilities
   */
  capabilities: Array<string>;
  /**
   * Max Input Length
   */
  max_input_length: number;
  /**
   * Embedding Dimension
   */
  embedding_dimension: number;
  /**
   * Compatible Providers
   */
  compatible_providers: Array<string>;
  /**
   * Is Deprecated
   */
  is_deprecated: boolean;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Image
   */
  image: string | null;
  /**
   * Provider Links
   */
  provider_links: Array<ProviderLinkResponse>;
};

/**
 * EmbeddingHealth
 */
export type EmbeddingHealth = {
  /**
   * Healthy
   */
  healthy: boolean;
  /**
   * Message
   */
  message?: string;
  /**
   * Embedding Model
   */
  embedding_model?: string;
};

/**
 * EmbeddingsDefinition
 */
export type EmbeddingsDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Slug
   */
  slug?: string | null;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Model Type
   */
  model_type: string;
  /**
   * Model Category
   */
  model_category?: string | null;
  /**
   * Properties
   */
  properties: LlmModelProperties | ModelProperties;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider
   */
  provider?: string | null;
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Provider Image
   */
  provider_image?: string | null;
  /**
   * Providers
   */
  providers?: Array<ProviderRef>;
  /**
   * Host
   */
  host?: string | null;
  /**
   * Location
   */
  location?: string | null;
  /**
   * Price
   */
  price?: string | null;
  /**
   * Release Date
   */
  release_date?: string | null;
  /**
   * Data Cutoff Date
   */
  data_cutoff_date?: string | null;
  /**
   * Retirement Date
   */
  retirement_date?: string | null;
  /**
   * Replaced By
   */
  replaced_by?: string | null;
  /**
   * Privacy Level
   */
  privacy_level?: string | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Is Available
   */
  is_available?: boolean;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Provider Is Active
   */
  provider_is_active?: boolean;
  /**
   * Has Link
   */
  has_link?: boolean;
  /**
   * Is Valid
   */
  is_valid?: boolean;
  /**
   * Is Healthy
   */
  is_healthy?: boolean;
  /**
   * Lifecycle State
   */
  lifecycle_state?: string;
  /**
   * Last Error
   */
  last_error?: string | null;
  /**
   * Consecutive Failures
   */
  consecutive_failures?: number;
  /**
   * Last Checked At
   */
  last_checked_at?: string | null;
  /**
   * Embedding Type
   */
  embedding_type: "text" | "multimodal";
  /**
   * Embedding Dimension
   */
  embedding_dimension: number;
  /**
   * Supported Dimensions
   */
  supported_dimensions?: Array<number> | null;
};

/**
 * EnableSyncRequest
 */
export type EnableSyncRequest = {
  source_type: SourceType;
  /**
   * Watched Items
   */
  watched_items: Array<WatchedItemSchema>;
  schedule?: KbSyncScheduleInput;
  /**
   * Delete On Source Removal
   */
  delete_on_source_removal?: boolean;
  /**
   * Resource Label
   */
  resource_label?: string | null;
};

/**
 * EncryptProviderKeysResponse
 */
export type EncryptProviderKeysResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Encrypted
   */
  encrypted: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Message
   */
  message: string;
};

/**
 * EndpointRateLimit
 */
export type EndpointRateLimit = {
  /**
   * Runs
   */
  runs?: number;
  /**
   * Delta Seconds
   */
  delta_seconds?: number;
};

/**
 * EntityGrantDelete
 *
 * Schema for removing grants from an entity for a principal.
 */
export type EntityGrantDelete = {
  /**
   * Principal Type
   *
   * Type of principal: 'user', 'role', or 'api_key'
   */
  principal_type: string;
  /**
   * Principal Id
   *
   * ID of the principal
   */
  principal_id: string;
};

/**
 * EntityGrantResponse
 *
 * Schema for a single entity grant row.
 */
export type EntityGrantResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Scope Type
   */
  scope_type: string;
  /**
   * Scope Id
   */
  scope_id: string;
  /**
   * Principal Type
   */
  principal_type: string;
  /**
   * Principal Id
   */
  principal_id: string;
  /**
   * Permission
   */
  permission: string;
  /**
   * Created By
   */
  created_by: string | null;
  /**
   * Created At
   */
  created_at: string;
};

/**
 * EntityGrantsBulkUpdate
 *
 * Schema for bulk-setting grants on an entity for a principal.
 */
export type EntityGrantsBulkUpdate = {
  /**
   * Principal Type
   *
   * Type of principal: 'user', 'role', or 'api_key'
   */
  principal_type: string;
  /**
   * Principal Id
   *
   * ID of the principal
   */
  principal_id: string;
  /**
   * Permissions
   *
   * Permissions to set (replaces existing grants for this principal)
   */
  permissions: Array<string>;
};

/**
 * EntityGrantsListResponse
 *
 * Schema for listing grants on an entity.
 */
export type EntityGrantsListResponse = {
  /**
   * Entity Type
   */
  entity_type: string;
  /**
   * Entity Id
   */
  entity_id: string;
  /**
   * Grants
   */
  grants: Array<EntityGrantResponse>;
};

/**
 * EntitySource
 *
 * Who authored an entity (or version). Absent/``user`` means a person made
 * it; ``genie`` marks something the platform assistant created on their behalf,
 * so the frontend can badge it.
 */
export const EntitySource = { GENIE: "genie", USER: "user" } as const;

/**
 * EntitySource
 *
 * Who authored an entity (or version). Absent/``user`` means a person made
 * it; ``genie`` marks something the platform assistant created on their behalf,
 * so the frontend can badge it.
 */
export type EntitySource = (typeof EntitySource)[keyof typeof EntitySource];

/**
 * EntityTagAssociationResponse
 */
export type EntityTagAssociationResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Tag Id
   */
  tag_id: string;
  /**
   * Entity Id
   */
  entity_id: string;
  entity_type: EntityType;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Created By
   */
  created_by: string;
};

/**
 * EntityTagsResponse
 */
export type EntityTagsResponse = {
  /**
   * Entity Id
   */
  entity_id: string;
  entity_type: EntityType;
  /**
   * Tags
   */
  tags: Array<TagResponse>;
};

/**
 * EntityType
 */
export const EntityType = {
  WORKFLOW: "workflow",
  ASSISTANT: "assistant",
  KNOWLEDGE_BASE: "knowledge_base",
  TABLE: "table",
} as const;

/**
 * EntityType
 */
export type EntityType = (typeof EntityType)[keyof typeof EntityType];

/**
 * EntryChange
 */
export type EntryChange = {
  /**
   * Op
   */
  op: "added" | "removed" | "changed";
  /**
   * Label
   */
  label: string;
  /**
   * Entity
   */
  entity?:
    | "knowledge_base"
    | "table"
    | "workflow"
    | "agent"
    | "credential"
    | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Fields
   */
  fields?: Array<EntryFieldChange>;
};

/**
 * EntryFieldChange
 */
export type EntryFieldChange = {
  /**
   * Field
   */
  field: string;
  /**
   * Label
   */
  label: string;
  before?: PydanticTypesJsonValue;
  after?: PydanticTypesJsonValue;
};

/**
 * EnvironmentSourceAvailability
 */
export type EnvironmentSourceAvailability = {
  /**
   * Available
   */
  available: boolean;
  reason?: EnvironmentSourceReason | null;
};

/**
 * EnvironmentSourceReason
 */
export const EnvironmentSourceReason = {
  ON_PREM_ONLY: "on_prem_only",
  DISABLED_BY_ADMIN: "disabled_by_admin",
} as const;

/**
 * EnvironmentSourceReason
 */
export type EnvironmentSourceReason =
  (typeof EnvironmentSourceReason)[keyof typeof EnvironmentSourceReason];

/**
 * ErrorContainer
 */
export type ErrorContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "_error";
  value: SpotError;
};

/**
 * ErrorDetails
 */
export type ErrorDetails = {
  /**
   * Type
   */
  type: string;
  /**
   * Loc
   */
  loc: Array<number | string>;
  /**
   * Msg
   */
  msg: string;
  /**
   * Input
   */
  input: unknown;
  /**
   * Ctx
   */
  ctx?: {
    [key: string]: unknown;
  };
  /**
   * Url
   */
  url?: string;
};

/**
 * ErrorEvent
 *
 * Any error the server couldn't recover from — the client surfaces
 * these in a toast / the ``startError`` state.  ``code`` is the stable
 * machine-readable identifier the FE branches on.
 */
export type ErrorEvent = {
  /**
   * Type
   */
  type: "error";
  /**
   * Message
   */
  message: string;
  /**
   * Code
   */
  code?: string | null;
};

/**
 * EvaluationResultDetailResponse
 *
 * Complete evaluation result with all related data joined.
 */
export type EvaluationResultDetailResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Evaluator Id
   */
  evaluator_id: string;
  /**
   * Test Case Id
   */
  test_case_id: string;
  /**
   * Score
   */
  score: number | null;
  /**
   * Feedback
   */
  feedback: string | null;
  /**
   * Status
   */
  status: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Evaluation Type
   */
  evaluation_type: "workflow" | "agent";
  /**
   * Workflow Run Id
   */
  workflow_run_id: string | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id: string | null;
  /**
   * Agent Version Id
   */
  agent_version_id: string | null;
  /**
   * Conversation Id
   */
  conversation_id: string | null;
  evaluator: EvaluatorResponse;
  /**
   * Expected Output
   */
  expected_output: {
    [key: string]: unknown;
  } | null;
  /**
   * Actual Output
   */
  actual_output: {
    [key: string]: unknown;
  } | null;
  run_metadata: RunMetadata;
};

/**
 * EvaluationRunListItem
 */
export type EvaluationRunListItem = {
  /**
   * Id
   */
  id: string;
  /**
   * Status
   */
  status:
    | "pending"
    | "running"
    | "completed"
    | "failed"
    | "invalid"
    | "cancelled";
  /**
   * Created At
   */
  created_at: string;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Agent Version Id
   */
  agent_version_id?: string | null;
  /**
   * Outcomes
   */
  outcomes?: {
    [key: string]: number;
  };
  /**
   * Tests
   */
  tests?: {
    [key: string]:
      | "running"
      | "passed"
      | "failed"
      | "error"
      | "cancelled"
      | "not_run";
  };
  /**
   * Avg Latency Ms
   */
  avg_latency_ms?: number | null;
  /**
   * Avg Cost
   */
  avg_cost?: number | null;
};

/**
 * EvaluationRunReport
 */
export type EvaluationRunReport = {
  /**
   * Evaluation Run Id
   */
  evaluation_run_id: string;
  /**
   * Test Suite Id
   */
  test_suite_id: string;
  /**
   * Status
   */
  status: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Agent Version Id
   */
  agent_version_id: string | null;
  /**
   * Agent Version Name
   */
  agent_version_name: string | null;
  /**
   * Outcomes
   */
  outcomes: {
    [key: string]: number;
  };
  /**
   * Tests
   */
  tests: Array<TestCaseReport>;
};

/**
 * EvaluatorDefinition
 */
export type EvaluatorDefinition = {
  /**
   * Type
   */
  type: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Config Schema
   */
  config_schema: {
    [key: string]: unknown;
  };
  /**
   * Expected Schema
   */
  expected_schema?: {
    [key: string]: unknown;
  } | null;
  /**
   * Category
   */
  category?: "workflow" | "agent" | "knowledge_base";
  /**
   * Group
   */
  group?: string;
  /**
   * Docs Url
   */
  docs_url?: string;
};

/**
 * EvaluatorResponse
 *
 * Response model for evaluator.
 *
 * Note:
 * - `name` is the user-provided name for this evaluator instance (stored in DB)
 * - `display_name` is the evaluator type's display name (derived from registry)
 * - `description` is the evaluator type's description (derived from registry)
 */
export type EvaluatorResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Test Suite Id
   */
  test_suite_id: string;
  /**
   * Type
   */
  type: string;
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Test Case Id
   */
  test_case_id?: string | null;
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
  /**
   * Expected Output
   */
  expected_output?: {
    [key: string]: PydanticTypesJsonValue;
  } | null;
  /**
   * Created At
   */
  created_at: string;
};

/**
 * EvaluatorResultResponse
 *
 * Response model for evaluation result.
 */
export type EvaluatorResultResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Evaluator Id
   */
  evaluator_id: string;
  /**
   * Test Case Id
   */
  test_case_id: string;
  /**
   * Workflow Run Id
   */
  workflow_run_id: string | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id: string | null;
  /**
   * Agent Version Id
   */
  agent_version_id: string | null;
  /**
   * Score
   */
  score: number | null;
  /**
   * Passed
   */
  passed?: boolean | null;
  /**
   * Feedback
   */
  feedback: string | null;
  /**
   * Definition Hash
   */
  definition_hash?: string | null;
  /**
   * Evaluator Config Hash
   */
  evaluator_config_hash?: string | null;
  /**
   * Test Case Hash
   */
  test_case_hash?: string | null;
  /**
   * Evaluator Set Hash
   */
  evaluator_set_hash?: string | null;
  /**
   * Is Outdated
   */
  is_outdated?: boolean | null;
  /**
   * Outdated Reasons
   */
  outdated_reasons?: Array<string> | null;
  /**
   * Status
   */
  status: string;
  /**
   * Created At
   */
  created_at: string;
};

/**
 * EvaluatorSummary
 */
export type EvaluatorSummary = {
  /**
   * Evaluator Id
   */
  evaluator_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type: string;
  /**
   * Mean Score
   */
  mean_score: number;
  /**
   * Pass Rate
   */
  pass_rate: number;
  /**
   * Completed
   */
  completed: number;
  /**
   * Invalid
   */
  invalid: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Baseline Mean Score
   */
  baseline_mean_score: number | null;
  /**
   * Delta
   */
  delta: number | null;
  /**
   * Regressed
   */
  regressed: boolean | null;
};

/**
 * ExcelSettings
 */
export type ExcelSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "excel";
  /**
   * Enable Python Sandbox
   */
  enable_python_sandbox?: boolean;
  /**
   * Enable Formula Eval
   */
  enable_formula_eval?: boolean;
  /**
   * Experimental
   */
  experimental?: boolean;
};

/**
 * ExcelSettings
 */
export type ExcelSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "excel";
  /**
   * Enable Python Sandbox
   */
  enable_python_sandbox?: boolean;
  /**
   * Enable Formula Eval
   */
  enable_formula_eval?: boolean;
  /**
   * Experimental
   */
  experimental?: boolean;
};

/**
 * ExchangeRequest
 */
export type ExchangeRequest = {
  /**
   * Deployment Id
   */
  deployment_id: string;
  /**
   * Origin
   */
  origin?: string | null;
};

/**
 * ExecutionLogEntry
 *
 * A single log entry from worker execution.
 */
export type ExecutionLogEntry = {
  /**
   * Id
   */
  id?: string;
  /**
   * Ts
   */
  ts: string;
  /**
   * Level
   */
  level: string;
  /**
   * Msg
   */
  msg: string;
  /**
   * Name
   */
  name?: string;
  /**
   * Func
   */
  func?: string;
  /**
   * Line
   */
  line?: string;
  /**
   * Exc Type
   */
  exc_type?: string | null;
  /**
   * Exc Msg
   */
  exc_msg?: string | null;
  /**
   * Exc Tb
   */
  exc_tb?: string | null;
};

/**
 * ExecutionLogs
 *
 * Full execution logs for a run.
 */
export type ExecutionLogs = {
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Source
   */
  source: string;
  /**
   * Log Count
   */
  log_count: number;
  /**
   * Logs
   */
  logs: Array<ExecutionLogEntry>;
  /**
   * Archived At
   */
  archived_at?: string | null;
};

/**
 * ExportFormat
 *
 * Wire format for an export/import payload.
 *
 * - ``auto`` (default both directions): export emits the default format
 * (currently ``v3`` base64, for back-compat with existing pipelines); import
 * detects the format from the payload content.
 * - ``v4``: plaintext multi-doc YAML (`.nx`) — opt-in, requested on purpose.
 * - ``v3``: legacy base64-encoded bundle.
 */
export const ExportFormat = {
  AUTO: "auto",
  V3: "v3",
  V4: "v4",
} as const;

/**
 * ExportFormat
 *
 * Wire format for an export/import payload.
 *
 * - ``auto`` (default both directions): export emits the default format
 * (currently ``v3`` base64, for back-compat with existing pipelines); import
 * detects the format from the payload content.
 * - ``v4``: plaintext multi-doc YAML (`.nx`) — opt-in, requested on purpose.
 * - ``v3``: legacy base64-encoded bundle.
 */
export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

/**
 * ExportPreviewResponse
 */
export type ExportPreviewResponse = {
  /**
   * Entities
   */
  entities: Array<ExportableReference>;
  /**
   * Warnings
   */
  warnings: Array<ExportWarning>;
};

/**
 * ExportWarning
 */
export type ExportWarning = {
  /**
   * Identifier
   */
  identifier: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Icon
   */
  icon?: string | null;
  type: ExportWarningType;
  /**
   * Message
   */
  message: string;
};

/**
 * ExportWarningType
 */
export const ExportWarningType = {
  KB_TOO_LARGE: "kb_too_large",
  TABLE_TOO_LARGE: "table_too_large",
  FILE_TOO_LARGE: "file_too_large",
  ENTITY_NOT_FOUND: "entity_not_found",
  ENTITY_ACCESS_ERROR: "entity_access_error",
  EXPORT_ERROR: "export_error",
  TRIGGERS_NOT_EXPORTED: "triggers_not_exported",
  APP_DEPLOYMENT_NOT_EXPORTED: "app_deployment_not_exported",
  SECRET_FOUND: "secret_found",
} as const;

/**
 * ExportWarningType
 */
export type ExportWarningType =
  (typeof ExportWarningType)[keyof typeof ExportWarningType];

/**
 * ExportableReference
 */
export type ExportableReference = {
  /**
   * Reference
   */
  reference: string;
  /**
   * Type
   */
  type:
    | "kb"
    | "workflow"
    | "assistant"
    | "file"
    | "inbox"
    | "connection"
    | "secret"
    | "deployment"
    | "table"
    | "trigger";
  /**
   * Version
   */
  version?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Icon
   */
  icon?: string | null;
};

/**
 * ExtractedValue
 */
export type ExtractedValue = {
  /**
   * Value
   */
  value: string;
  /**
   * Confidence
   */
  confidence: number;
  /**
   * Page
   */
  page: number | null;
  box: PageBox | null;
  /**
   * Line Ids
   */
  line_ids: Array<string>;
  /**
   * Ocr Confidence
   */
  ocr_confidence: number | null;
};

/**
 * ExtractionSchemaEntry
 */
export type ExtractionSchemaEntry = {
  /**
   * Label
   */
  Label: string;
  /**
   * Type
   */
  type?:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "boolean"
    | "select"
    | "email"
    | "phone"
    | "url"
    | "file"
    | "table";
  /**
   * Description
   */
  description?: string | null;
  options?: JsonValueInput;
};

/**
 * FailureProbeSettings
 *
 * Makes an agent fail on demand, so tool-failure handling can be driven
 * in a real conversation instead of only in a unit test.
 *
 * Noxus-internal, behind two independent gates. ``internal_only`` +
 * ``settings.shows_internal_test_tools()`` decides whether the tool exists on
 * this deployment at all — never on-prem, never production, and not
 * configurable. ``AGENT_FAILURE_PROBE_ENABLED`` then decides which of our
 * tenants see it, off by default like every other beta tool.
 *
 * The flag is the weaker of the two on purpose: it is a convenience, and an
 * admin flipping it can never reach a customer deployment, because the
 * deployment gate is checked independently at build time.
 */
export type FailureProbeSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "failure_probe";
};

/**
 * FailureProbeSettings
 *
 * Makes an agent fail on demand, so tool-failure handling can be driven
 * in a real conversation instead of only in a unit test.
 *
 * Noxus-internal, behind two independent gates. ``internal_only`` +
 * ``settings.shows_internal_test_tools()`` decides whether the tool exists on
 * this deployment at all — never on-prem, never production, and not
 * configurable. ``AGENT_FAILURE_PROBE_ENABLED`` then decides which of our
 * tenants see it, off by default like every other beta tool.
 *
 * The flag is the weaker of the two on purpose: it is a convenience, and an
 * admin flipping it can never reach a customer deployment, because the
 * deployment gate is checked independently at build time.
 */
export type FailureProbeSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "failure_probe";
};

/**
 * FeatureFlags
 */
export type FeatureFlags = {
  /**
   * Experimental Nodes Enabled
   */
  EXPERIMENTAL_NODES_ENABLED?: boolean;
  /**
   * Noxus Confidence Nodes Enabled
   */
  NOXUS_CONFIDENCE_NODES_ENABLED?: boolean;
  /**
   * Voice Agent Enabled
   */
  VOICE_AGENT_ENABLED?: boolean;
  /**
   * Drive Triggers Enabled
   */
  DRIVE_TRIGGERS_ENABLED?: boolean;
  /**
   * Trigger Debug Page Enabled
   */
  TRIGGER_DEBUG_PAGE_ENABLED?: boolean;
  /**
   * Editor Disable Autosave
   */
  EDITOR_DISABLE_AUTOSAVE?: boolean;
  /**
   * Run Allow Profile
   */
  RUN_ALLOW_PROFILE?: boolean;
  /**
   * Trigger Always Profile
   */
  TRIGGER_ALWAYS_PROFILE?: boolean;
  /**
   * Paybyrd Enabled
   */
  PAYBYRD_ENABLED?: boolean;
  /**
   * Cuf Mode Enabled
   */
  CUF_MODE_ENABLED?: boolean;
  /**
   * Noxus Browser Tools Enabled
   */
  NOXUS_BROWSER_TOOLS_ENABLED?: boolean;
  /**
   * Storage Nodes Enabled
   */
  STORAGE_NODES_ENABLED?: boolean;
  /**
   * Debugger Enabled
   */
  DEBUGGER_ENABLED?: boolean;
  /**
   * Frulact Enabled
   */
  FRULACT_ENABLED?: boolean;
  /**
   * Coworker Memory Tool Enabled
   */
  COWORKER_MEMORY_TOOL_ENABLED?: boolean;
  /**
   * Coworker Action Tools Enabled
   */
  COWORKER_ACTION_TOOLS_ENABLED?: boolean;
  /**
   * Pandas Agent Enabled
   */
  PANDAS_AGENT_ENABLED?: boolean;
  /**
   * Playwright Agent Enabled
   */
  PLAYWRIGHT_AGENT_ENABLED?: boolean;
  /**
   * Dynamics365 Nodes
   */
  DYNAMICS365_NODES?: boolean;
  /**
   * Dataverse Nodes
   */
  DATAVERSE_NODES?: boolean;
  /**
   * New Web Search Agent Enabled
   */
  NEW_WEB_SEARCH_AGENT_ENABLED?: boolean;
  /**
   * Agent Guidelines Enabled
   */
  AGENT_GUIDELINES_ENABLED?: boolean;
  /**
   * Agent Subagents Enabled
   */
  AGENT_SUBAGENTS_ENABLED?: boolean;
  /**
   * Agent Sandbox Enabled
   */
  AGENT_SANDBOX_ENABLED?: boolean;
  /**
   * Agent Todos Enabled
   */
  AGENT_TODOS_ENABLED?: boolean;
  /**
   * Agent Failure Probe Enabled
   */
  AGENT_FAILURE_PROBE_ENABLED?: boolean;
  /**
   * Agent Schedule Tool Enabled
   */
  AGENT_SCHEDULE_TOOL_ENABLED?: boolean;
  /**
   * Agent Tool Approvals Enabled
   */
  AGENT_TOOL_APPROVALS_ENABLED?: boolean;
  /**
   * Agent Doc Tools Enabled
   */
  AGENT_DOC_TOOLS_ENABLED?: boolean;
  /**
   * Browser Playbook Enabled
   */
  BROWSER_PLAYBOOK_ENABLED?: boolean;
  /**
   * Agent Memory Enabled
   */
  AGENT_MEMORY_ENABLED?: boolean;
  /**
   * Agent Variables Enabled
   */
  AGENT_VARIABLES_ENABLED?: boolean;
  /**
   * Agent Insights Enabled
   */
  AGENT_INSIGHTS_ENABLED?: boolean;
  /**
   * Genie Knowledge Bases Enabled
   */
  GENIE_KNOWLEDGE_BASES_ENABLED?: boolean;
  /**
   * Genie Analytics Enabled
   */
  GENIE_ANALYTICS_ENABLED?: boolean;
  /**
   * Genie Sandboxes Enabled
   */
  GENIE_SANDBOXES_ENABLED?: boolean;
  /**
   * Genie Workspace Admin Enabled
   */
  GENIE_WORKSPACE_ADMIN_ENABLED?: boolean;
  /**
   * Genie Cases Enabled
   */
  GENIE_CASES_ENABLED?: boolean;
  /**
   * Gitops Enabled
   */
  GITOPS_ENABLED?: boolean;
  /**
   * Agent Tests Enabled
   */
  AGENT_TESTS_ENABLED?: boolean;
  /**
   * Support Log Export Enabled
   */
  SUPPORT_LOG_EXPORT_ENABLED?: boolean;
  /**
   * Better Auth Migration Enabled
   */
  BETTER_AUTH_MIGRATION_ENABLED?: boolean;
};

/**
 * FeedbackMeta
 */
export type FeedbackMeta = {
  /**
   * Feedback Reason
   */
  feedback_reason?: string | null;
  /**
   * Comment
   */
  comment?: string | null;
};

/**
 * FieldChange
 */
export type FieldChange = {
  /**
   * Field
   */
  field: "title" | "status" | "gating" | "note" | "linear" | "parent";
  /**
   * Before
   */
  before: string | null;
  /**
   * After
   */
  after: string | null;
};

/**
 * FieldValidation
 */
export type FieldValidation = {
  /**
   * Strict
   */
  strict?: boolean;
  /**
   * Trim
   */
  trim?: boolean;
  /**
   * Text Case
   */
  text_case?: "upper" | "lower" | null;
  /**
   * Pattern
   */
  pattern?: string | null;
  /**
   * Pattern Message
   */
  pattern_message?: string | null;
  /**
   * Min Length
   */
  min_length?: number | null;
  /**
   * Max Length
   */
  max_length?: number | null;
  /**
   * Min Value
   */
  min_value?: number | null;
  /**
   * Max Value
   */
  max_value?: number | null;
  /**
   * Integer Only
   */
  integer_only?: boolean;
  /**
   * Max Decimals
   */
  max_decimals?: number | null;
  /**
   * Check Format
   */
  check_format?: boolean;
  /**
   * Format
   */
  format?: "iban" | "luhn" | "ean" | "nif_pt" | null;
  /**
   * Min Date
   */
  min_date?: string | null;
  /**
   * Max Date
   */
  max_date?: string | null;
  /**
   * Min Date Offset Days
   */
  min_date_offset_days?: number | null;
  /**
   * Max Date Offset Days
   */
  max_date_offset_days?: number | null;
  /**
   * Min Rows
   */
  min_rows?: number | null;
  /**
   * Max Rows
   */
  max_rows?: number | null;
  /**
   * Unique In Table
   */
  unique_in_table?: boolean;
  /**
   * Unique
   */
  unique?: boolean;
  /**
   * Unique Scope
   */
  unique_scope?: "all" | "open";
  /**
   * Unique Case Insensitive
   */
  unique_case_insensitive?: boolean;
  /**
   * Allowed Extensions
   */
  allowed_extensions?: Array<string> | null;
  /**
   * Max File Size Mb
   */
  max_file_size_mb?: number | null;
};

/**
 * FieldWarning
 */
export type FieldWarning = {
  /**
   * Field Id
   */
  field_id: string;
  /**
   * Field Name
   */
  field_name: string;
  /**
   * Reason
   */
  reason: string;
  /**
   * Value
   */
  value?: string | null;
  /**
   * Duplicate Case Id
   */
  duplicate_case_id?: string | null;
  /**
   * Duplicate Display Id
   */
  duplicate_display_id?: string | null;
};

/**
 * File
 */
export type FileInput = {
  /**
   * Id
   */
  id?: string | null;
  /**
   * Uri
   */
  uri: string;
  /**
   * Name
   */
  name: string;
  /**
   * Content Type
   */
  content_type?: string;
  /**
   * Size
   */
  size?: number | null;
  /**
   * Content Metadata
   */
  content_metadata?:
    | ({
        kind: "pdf";
      } & PdfMetadata)
    | ({
        kind: "spreadsheet";
      } & SpreadsheetMetadata)
    | ({
        kind: "tabular";
      } & TabularMetadata)
    | ({
        kind: "image";
      } & ImageMetadata)
    | ({
        kind: "presentation";
      } & PresentationMetadata)
    | ({
        kind: "document";
      } & DocumentMetadata)
    | null;
  source_type?: SourceType;
  /**
   * Source Metadata
   */
  source_metadata?:
    | SourceMetadata
    | {
        [key: string]: unknown;
      }
    | null;
  [key: string]:
    | unknown
    | string
    | null
    | string
    | number
    | null
    | ({
        kind: "pdf";
      } & PdfMetadata)
    | ({
        kind: "spreadsheet";
      } & SpreadsheetMetadata)
    | ({
        kind: "tabular";
      } & TabularMetadata)
    | ({
        kind: "image";
      } & ImageMetadata)
    | ({
        kind: "presentation";
      } & PresentationMetadata)
    | ({
        kind: "document";
      } & DocumentMetadata)
    | null
    | SourceType
    | SourceMetadata
    | {
        [key: string]: unknown;
      }
    | null
    | undefined;
};

/**
 * FileDataContainer
 */
export type FileDataContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "File";
  /**
   * Value
   */
  value: FileType | Array<FileType> | unknown | null;
};

/**
 * FilePart
 *
 * A file response from a model.
 */
export type FilePart = {
  content: BinaryContent;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "file";
};

/**
 * FileSettings
 *
 * Unified file capability for the agent.
 *
 * One master toggle that, when on, lets the user attach files and lets the
 * agent generate + present them (``create_file``, ``present_file``,
 * ``sandbox_publish_file``). Word (.docx) and Excel editing are offered
 * on-demand whenever a matching file is in the session — no separate toggles.
 *
 * The always-on session-file reader (``list_files``/``read_file``) is a
 * separate, hidden capability (``FileSystemSettings``) and is unaffected.
 */
export type FileSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "files";
  /**
   * Allow Html Graphs
   *
   * Instruct the agent to use self-contained HTML (Plotly, Chart.js, ...) when generating charts and visualizations.
   */
  allow_html_graphs?: boolean;
  /**
   * Show Inline
   *
   * Render HTML and images directly in the chat instead of as a card.
   */
  show_inline?: boolean;
  /**
   * Custom Author Name
   *
   * Use a custom author for track-change revisions and comments. When off, the agent's name is used.
   */
  custom_author_name?: boolean;
  /**
   * Author Name
   *
   * Author shown on track-change revisions and comments. Defaults to the agent's name when left unset.
   */
  author_name?: string;
  /**
   * Enable Python Sandbox
   *
   * Run pandas/Python over the workbook in the sandbox.
   */
  enable_python_sandbox?: boolean;
  /**
   * Enable Formula Eval
   *
   * Evaluate spreadsheet formulas via the formulas library.
   */
  enable_formula_eval?: boolean;
  /**
   * Experimental
   *
   * Enable every experimental spreadsheet tool above.
   */
  experimental?: boolean;
};

/**
 * FileSettings
 *
 * Unified file capability for the agent.
 *
 * One master toggle that, when on, lets the user attach files and lets the
 * agent generate + present them (``create_file``, ``present_file``,
 * ``sandbox_publish_file``). Word (.docx) and Excel editing are offered
 * on-demand whenever a matching file is in the session — no separate toggles.
 *
 * The always-on session-file reader (``list_files``/``read_file``) is a
 * separate, hidden capability (``FileSystemSettings``) and is unaffected.
 */
export type FileSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "files";
  /**
   * Allow Html Graphs
   *
   * Instruct the agent to use self-contained HTML (Plotly, Chart.js, ...) when generating charts and visualizations.
   */
  allow_html_graphs?: boolean;
  /**
   * Show Inline
   *
   * Render HTML and images directly in the chat instead of as a card.
   */
  show_inline?: boolean;
  /**
   * Custom Author Name
   *
   * Use a custom author for track-change revisions and comments. When off, the agent's name is used.
   */
  custom_author_name?: boolean;
  /**
   * Author Name
   *
   * Author shown on track-change revisions and comments. Defaults to the agent's name when left unset.
   */
  author_name?: string;
  /**
   * Enable Python Sandbox
   *
   * Run pandas/Python over the workbook in the sandbox.
   */
  enable_python_sandbox?: boolean;
  /**
   * Enable Formula Eval
   *
   * Evaluate spreadsheet formulas via the formulas library.
   */
  enable_formula_eval?: boolean;
  /**
   * Experimental
   *
   * Enable every experimental spreadsheet tool above.
   */
  experimental?: boolean;
};

/**
 * FileSystemSettings
 */
export type FileSystemSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "filesystem";
};

/**
 * FileSystemSettings
 */
export type FileSystemSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "filesystem";
};

/**
 * FileType
 */
export type FileType = {
  /**
   * Uri
   */
  uri: string;
  /**
   * Name
   */
  name: string;
  /**
   * Content Type
   */
  content_type: string;
};

/**
 * FileUIPart
 *
 * A file part of a message.
 */
export type FileUiPart = {
  /**
   * Type
   */
  type?: "file";
  /**
   * Mediatype
   */
  mediaType: string;
  /**
   * Filename
   */
  filename?: string | null;
  /**
   * Url
   */
  url: string;
  /**
   * Providermetadata
   */
  providerMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
};

/**
 * FireworksProviderData
 *
 * Deprecated. Kept only so existing JSONB rows deserialize.
 */
export type FireworksProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "fireworks";
  /**
   * Fireworks Api Key
   */
  FIREWORKS_API_KEY?: string;
};

/**
 * FlatNode
 *
 * A node with its position in the tree resolved.
 */
export type FlatNode = {
  /**
   * Key
   */
  key: string;
  /**
   * Title
   */
  title: string;
  /**
   * Kind
   */
  kind: "module" | "feature" | "capability";
  /**
   * Parent
   */
  parent: string | null;
  /**
   * Number
   */
  number: string;
  /**
   * Depth
   */
  depth: number;
  /**
   * Status
   */
  status: "live" | "coming-soon" | "needs-review" | "deprecated";
  /**
   * Gating
   */
  gating: Array<string>;
  /**
   * Note
   */
  note: string | null;
  /**
   * Linear
   */
  linear: string | null;
  /**
   * Reach
   */
  reach?: Array<string>;
  /**
   * Gotchas
   */
  gotchas?: Array<string>;
  /**
   * File
   */
  file: string;
};

/**
 * FlowDependencies
 */
export type FlowDependencies = {
  /**
   * Inputs
   */
  inputs: Array<FlowInput>;
  /**
   * Outputs
   */
  outputs: Array<FlowOutput>;
  /**
   * Dependencies
   */
  dependencies: {
    [key: string]: Array<FlowDependency>;
  };
  /**
   * Types
   */
  types: {
    [key: string]: TypeDefinition;
  };
};

/**
 * FlowDependency
 */
export type FlowDependency = {
  target: ConnectorAddress;
  source: ConnectorAddress | null;
};

/**
 * FlowInput
 */
export type FlowInput = {
  conn: ConnectorAddress;
  type: TypeDefinition;
  /**
   * Label
   */
  label: string;
  /**
   * Fixed Value
   */
  fixed_value?: unknown | null;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Modifiable On Run
   */
  modifiable_on_run?: boolean;
};

/**
 * FlowInputConnector
 */
export type FlowInputConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "input";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
};

/**
 * FlowOutput
 */
export type FlowOutput = {
  conn: ConnectorAddress;
  type: TypeDefinition;
  /**
   * Label
   */
  label: string;
};

/**
 * FlowOutputConnector
 */
export type FlowOutputConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "output";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
};

/**
 * FlowTypes
 */
export type FlowTypes = {
  /**
   * Input Types
   */
  input_types: {
    [key: string]: TypeDefinition;
  };
  /**
   * Output Types
   */
  output_types: {
    [key: string]: TypeDefinition;
  };
  /**
   * Workflow Hash
   */
  workflow_hash: string;
};

/**
 * FlowValidation
 */
export type FlowValidation = {
  /**
   * Valid
   */
  valid: boolean;
  /**
   * Nodes
   */
  nodes?: {
    [key: string]: NodeValidation;
  };
  /**
   * Name Conflicts
   */
  name_conflicts?: Array<NameConflict>;
};

/**
 * Folder
 */
export type Folder = {
  /**
   * Childcount
   */
  childCount: number;
  view?: FolderView | null;
};

/**
 * FolderView
 */
export type FolderView = {
  /**
   * Sortby
   */
  sortBy: string;
  /**
   * Sortorder
   */
  sortOrder: string;
  /**
   * Viewtype
   */
  viewType: string;
};

/**
 * ForgotPasswordRequest
 */
export type ForgotPasswordRequest = {
  /**
   * Email
   */
  email: string;
  /**
   * Connection
   */
  connection?: string | null;
};

/**
 * ForgotPasswordResponse
 */
export type ForgotPasswordResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Error Code
   */
  error_code?: string | null;
};

/**
 * FunnelOutcome
 */
export type FunnelOutcome = {
  /**
   * Name
   */
  name: string;
  /**
   * Count
   */
  count: number;
  /**
   * Pct
   */
  pct: number;
};

/**
 * GeminiProviderData
 */
export type GeminiProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "gemini";
  /**
   * Gemini Api Key
   */
  GEMINI_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * GenerateKBSummaryResponse
 */
export type GenerateKbSummaryResponse = {
  /**
   * Job Id
   */
  job_id?: number | null;
  /**
   * Status
   */
  status?: "queued";
};

/**
 * GenericTrainBody
 *
 * Body for ``POST /v2/.../knowledge-bases/{kb}/generic_train``.
 *
 * ``source`` stays loose (``dict``) because the per-datasource shapes
 * are validated downstream in ``Source.model_validate``, and the
 * endpoint also extracts ``_credential_id_*`` keys from
 * ``source.config`` before that validation runs (the underlying
 * SourceConfig model uses ``extra="ignore"`` so the credentials
 * would otherwise be lost).
 *
 * ``sync`` is optional and fully typed: when present, the endpoint
 * creates the sync trigger row carrying the watched_items and runs
 * ingestion in the same call. The engine self-heals
 * ``watched_items[].kb_doc_id`` on the first poll by matching source
 * ids against the freshly-ingested docs.
 */
export type GenericTrainBody = {
  /**
   * Source
   */
  source?: {
    [key: string]: unknown;
  };
  sync?: EnableSyncRequest | null;
};

/**
 * GenieMode
 */
export const GenieMode = {
  DEFAULT: "default",
  AUTO: "auto",
  YOLO: "yolo",
} as const;

/**
 * GenieMode
 */
export type GenieMode = (typeof GenieMode)[keyof typeof GenieMode];

/**
 * GetWorkflowDependenciesRequest
 */
export type GetWorkflowDependenciesRequest = {
  /**
   * Node Ids
   */
  node_ids?: Array<string> | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
};

/**
 * GetWorkflowNodeDependenciesRequest
 */
export type GetWorkflowNodeDependenciesRequest = {
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
};

/**
 * GitPluginSource
 *
 * Git plugin source with GitHub API optimization and sparse checkout.
 */
export type GitPluginSource = {
  /**
   * Type
   */
  type?: "git";
  /**
   * Repo Url
   *
   * Git repository URL (HTTPS or SSH)
   */
  repo_url: string;
  /**
   * Branch
   *
   * Git branch to checkout
   */
  branch?: string;
  /**
   * Commit
   *
   * Specific commit hash (optional)
   */
  commit?: string | null;
  /**
   * Path
   *
   * Subdirectory path within the repository
   */
  path?: string | null;
  /**
   * Token
   *
   * Access token for private repositories
   */
  token?: string | null;
  /**
   * Username
   *
   * Git username for authentication
   */
  username?: string | null;
  /**
   * Password
   *
   * Git password or personal access token
   */
  password?: string | null;
};

/**
 * GitSyncChangeOut
 */
export type GitSyncChangeOut = {
  /**
   * Kind
   */
  kind: string;
  /**
   * Slug
   */
  slug: string;
  /**
   * Name
   */
  name: string | null;
  /**
   * Action
   */
  action: string;
  /**
   * Direction
   */
  direction: string;
  /**
   * Error
   */
  error: string | null;
};

/**
 * GitSyncConfig
 *
 * Per-workspace git-sync config, stored on ``UserGroup.settings``.
 */
export type GitSyncConfig = {
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Provider
   */
  provider?: "github" | "gitlab" | "generic_git";
  /**
   * Credential Id
   */
  credential_id?: string | null;
  /**
   * Repo
   */
  repo?: string;
  /**
   * Branch
   */
  branch?: string;
  /**
   * Base Path
   */
  base_path?: string;
  direction?: Direction;
  /**
   * Authoritative
   */
  authoritative?: "db" | "git";
  /**
   * Kinds
   */
  kinds?: Array<ArtifactKind>;
  /**
   * Include Slugs
   */
  include_slugs?: Array<string> | null;
  /**
   * Exclude Slugs
   */
  exclude_slugs?: Array<string>;
  /**
   * Sync Interval Minutes
   */
  sync_interval_minutes?: number;
  /**
   * Allow Same Deployment
   */
  allow_same_deployment?: boolean;
  /**
   * Secret Policy
   */
  secret_policy?: "block" | "warn" | "off";
  match_strategy?: MatchStrategy;
  write_mode?: WriteMode;
  on_missing_dependency?: MissingDependency;
  on_upstream_delete?: UpstreamDelete;
  /**
   * Remove Untracked
   */
  remove_untracked?: boolean;
};

/**
 * GitSyncHealth
 */
export type GitSyncHealth = {
  /**
   * Status
   */
  status:
    | "healthy"
    | "connection_missing"
    | "connection_invalid"
    | "repo_unreachable";
  /**
   * Credential Present
   */
  credential_present?: boolean;
  /**
   * Credential Status
   */
  credential_status?: string | null;
  repo?: RepoState | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * GitSyncRunDetail
 */
export type GitSyncRunDetail = {
  /**
   * Id
   */
  id: string;
  /**
   * Trigger
   */
  trigger: string;
  /**
   * Direction
   */
  direction: string;
  /**
   * Status
   */
  status: "running" | "success" | "partial" | "failed";
  /**
   * Started At
   */
  started_at: string;
  /**
   * Finished At
   */
  finished_at: string | null;
  /**
   * Commit Before
   */
  commit_before: string | null;
  /**
   * Commit After
   */
  commit_after: string | null;
  /**
   * Added
   */
  added: number;
  /**
   * Updated
   */
  updated: number;
  /**
   * Deleted
   */
  deleted: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Error
   */
  error: string | null;
  /**
   * Error Code
   */
  error_code?: string | null;
  /**
   * Changes
   */
  changes: Array<GitSyncChangeOut>;
};

/**
 * GitSyncRunOut
 */
export type GitSyncRunOut = {
  /**
   * Id
   */
  id: string;
  /**
   * Trigger
   */
  trigger: string;
  /**
   * Direction
   */
  direction: string;
  /**
   * Status
   */
  status: "running" | "success" | "partial" | "failed";
  /**
   * Started At
   */
  started_at: string;
  /**
   * Finished At
   */
  finished_at: string | null;
  /**
   * Commit Before
   */
  commit_before: string | null;
  /**
   * Commit After
   */
  commit_after: string | null;
  /**
   * Added
   */
  added: number;
  /**
   * Updated
   */
  updated: number;
  /**
   * Deleted
   */
  deleted: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Error
   */
  error: string | null;
  /**
   * Error Code
   */
  error_code?: string | null;
};

/**
 * GitSyncTestResult
 */
export type GitSyncTestResult = {
  /**
   * Ok
   */
  ok: boolean;
  /**
   * Exists
   */
  exists?: boolean;
  /**
   * Can Read
   */
  can_read?: boolean;
  /**
   * Can Write
   */
  can_write?: boolean;
  /**
   * Default Branch
   */
  default_branch?: string | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * GlossaryTermConfig
 *
 * A glossary term definition stored in agent settings.
 */
export type GlossaryTermConfig = {
  /**
   * Term
   */
  term: string;
  /**
   * Definition
   */
  definition: string;
  /**
   * Synonyms
   */
  synonyms?: Array<string>;
};

/**
 * GoToLinkRequest
 */
export type GoToLinkRequest = {
  /**
   * Url
   */
  url: string;
};

/**
 * GoToLinkResponse
 */
export type GoToLinkResponse = {
  /**
   * Resource Type
   */
  resource_type: "workflow" | "kb" | "agent";
  /**
   * Resource Id
   */
  resource_id: string;
  /**
   * Resource Name
   */
  resource_name: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Group Name
   */
  group_name: string;
  /**
   * Tenant Id
   */
  tenant_id: string;
  /**
   * Tenant Name
   */
  tenant_name: string;
};

/**
 * GoogleDriveSources
 *
 * Available drive sources the picker can list files from.
 */
export type GoogleDriveSources = {
  /**
   * Shared Drives
   */
  shared_drives: Array<GoogleSharedDrive>;
};

/**
 * GoogleSharedDrive
 */
export type GoogleSharedDrive = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * GrokProviderData
 */
export type GrokProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "grok";
  /**
   * Xai Api Key
   */
  XAI_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * GroqProviderData
 */
export type GroqProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "groq";
  /**
   * Groq Api Key
   */
  GROQ_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * Group
 */
export type Group = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
};

/**
 * GroupAddRequest
 */
export type GroupAddRequest = {
  /**
   * Id
   */
  id?: string | null;
  /**
   * Role Id
   */
  role_id?: string | null;
  /**
   * Name
   */
  name?: string;
  /**
   * Default
   */
  default?: boolean;
};

/**
 * GroupOrder
 */
export const GroupOrder = {
  CREATED_AT: "created_at",
  GROUP_NAME: "group_name",
  GROUP_ROLE: "group_role",
  MEMBERS: "members",
} as const;

/**
 * GroupOrder
 */
export type GroupOrder = (typeof GroupOrder)[keyof typeof GroupOrder];

/**
 * GroupTriggerEvent
 *
 * Event entry for the group-level events view (all triggers).
 */
export type GroupTriggerEvent = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  state: TriggerQueueStatus;
  /**
   * Event Type
   */
  event_type: string;
  /**
   * Event Data
   */
  event_data: {
    [key: string]: unknown;
  };
  /**
   * Trigger Id
   */
  trigger_id?: string | null;
  /**
   * Trigger Type
   */
  trigger_type?: string | null;
  /**
   * Workflow Id
   */
  workflow_id?: string | null;
  /**
   * Workflow Name
   */
  workflow_name?: string | null;
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Started Run
   */
  started_run?: boolean;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Ignore Reason
   */
  ignore_reason?: string | null;
};

/**
 * GroupWithRole
 */
export type GroupWithRole = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
  /**
   * Role
   */
  role: string;
};

/**
 * GroupWithUsers
 */
export type GroupWithUsers = {
  group: Group;
  /**
   * Users
   */
  users: Array<UserWithGroupRole>;
};

/**
 * GuardrailValue
 */
export type GuardrailValue = {
  /**
   * Value
   */
  value?: string;
  /**
   * Context
   */
  context?: string;
};

/**
 * GuardrailsSettings
 *
 * Every safety option of an agent on guardrails v2, owned by its quick action.
 *
 * The fields are ``PIIConfig``'s, so v1 agents, which keep them on the
 * agent's own settings, and v2 agents read the same shape. The toggle is the
 * master switch: off builds no shields at all.
 */
export type GuardrailsSettingsInput = {
  /**
   * Pii Banner
   *
   * Information about PII anonymization
   */
  pii_banner?: string;
  /**
   * Pii Info
   *
   * Information about PII anonymization
   */
  pii_info?: string;
  /**
   * Pii Lock
   *
   * Information about the guardrails
   */
  pii_lock?: boolean;
  /**
   * Pii Anonimization
   *
   * Preferred anonymization method for PII values.
   */
  pii_anonimization?: Array<{
    [key: string]: string;
  }>;
  /**
   * Divider1
   */
  divider1?: string;
  /**
   * Guardrail Info
   *
   * Information about the guardrails
   */
  guardrail_info?: string;
  /**
   * Guardrail Lock
   *
   * Locks the guardrails settings.
   */
  guardrail_lock?: boolean;
  /**
   * Guardrail Latency Banner
   */
  guardrail_latency_banner?: string;
  violence_and_hate?: GuardrailValue;
  sexual_content?: GuardrailValue;
  guns_and_weapons?: GuardrailValue;
  suicide?: GuardrailValue;
  crime?: GuardrailValue;
  toxicity?: GuardrailValue;
  bias?: GuardrailValue;
  profanity?: GuardrailValue;
  /**
   * Guardrails Scan Output
   *
   * Scan the model's response with the same semantic guardrails applied to input. Catches sensitive content sourced from Knowledge Base documents or tools that the input scan cannot see.
   */
  guardrails_scan_output?: boolean;
  /**
   * Guardrails Output Mode
   *
   * How output guardrails are enforced. 'post_stream' streams the response in real-time and warns/clears after the fact. 'pre_stream' buffers the full response, validates it against guardrails, and only emits it if it passes.
   */
  guardrails_output_mode?: "post_stream" | "pre_stream";
  /**
   * Divider2
   */
  divider2?: string;
  /**
   * Guardrail Action
   *
   * Choose the action to take when a guardrail is triggered.
   */
  guardrail_action?:
    | {
        [key: string]: string;
      }
    | string;
  /**
   * Guardrail Action Replace
   */
  guardrail_action_replace?: string;
  /**
   * Guardrail Action Human
   */
  guardrail_action_human?: string;
  /**
   * Block After Violations
   *
   * Block the chat after this many turns with guardrail violations. Set to 0 to never block (per-turn warnings still surface).
   */
  block_after_violations?: number;
  /**
   * Block Message
   *
   * Message shown to the user when the chat is locked.
   */
  block_message?: string;
  /**
   * Divider3
   */
  divider3?: string;
  /**
   * Prompt Defense
   */
  prompt_defense?: string;
  /**
   * Prompt Lock
   *
   * Locks the prompt defense settings
   */
  prompt_lock?: boolean;
  /**
   * Jailbreak
   *
   * Prevents attempts to manipulate or "jailbreak" the model into ignoring safety rules or system instructions (e.g., prompts like "ignore previous instructions").
   */
  jailbreak?: boolean;
  /**
   * Prompt Injection
   *
   * Detects and blocks strategies where users attempt to get around restrictions indirectly (e.g., asking the model to 'pretend' or speak as a fictional character to evade policies).
   */
  prompt_injection?: boolean;
  /**
   * Context Leak
   *
   * Prevents the model from unintentionally revealing internal context, instructions, or system messages that are not meant to be shared with the user.
   */
  context_leak?: boolean;
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   *
   * The message to display to the user when the tool is called.
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   *
   * The message to display to the user when the tool completes.
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "guardrails";
  /**
   * Settings Display
   */
  settings_display?: "dialog";
  /**
   * Guardrail Engine
   *
   * How the semantic categories are scanned. The language model makes one call per enabled category. A decision model answers every category in a single call, faster and far cheaper, and needs a decision model enabled under Settings > Models; without one the language model is used.
   */
  guardrail_engine?: "llm" | "system_one";
};

/**
 * GuardrailsSettings
 *
 * Every safety option of an agent on guardrails v2, owned by its quick action.
 *
 * The fields are ``PIIConfig``'s, so v1 agents, which keep them on the
 * agent's own settings, and v2 agents read the same shape. The toggle is the
 * master switch: off builds no shields at all.
 */
export type GuardrailsSettingsOutput = {
  /**
   * Pii Banner
   *
   * Information about PII anonymization
   */
  pii_banner?: string;
  /**
   * Pii Info
   *
   * Information about PII anonymization
   */
  pii_info?: string;
  /**
   * Pii Lock
   *
   * Information about the guardrails
   */
  pii_lock?: boolean;
  /**
   * Pii Anonimization
   *
   * Preferred anonymization method for PII values.
   */
  pii_anonimization?: Array<{
    [key: string]: string;
  }>;
  /**
   * Divider1
   */
  divider1?: string;
  /**
   * Guardrail Info
   *
   * Information about the guardrails
   */
  guardrail_info?: string;
  /**
   * Guardrail Lock
   *
   * Locks the guardrails settings.
   */
  guardrail_lock?: boolean;
  /**
   * Guardrail Latency Banner
   */
  guardrail_latency_banner?: string;
  violence_and_hate?: GuardrailValue;
  sexual_content?: GuardrailValue;
  guns_and_weapons?: GuardrailValue;
  suicide?: GuardrailValue;
  crime?: GuardrailValue;
  toxicity?: GuardrailValue;
  bias?: GuardrailValue;
  profanity?: GuardrailValue;
  /**
   * Guardrails Scan Output
   *
   * Scan the model's response with the same semantic guardrails applied to input. Catches sensitive content sourced from Knowledge Base documents or tools that the input scan cannot see.
   */
  guardrails_scan_output?: boolean;
  /**
   * Guardrails Output Mode
   *
   * How output guardrails are enforced. 'post_stream' streams the response in real-time and warns/clears after the fact. 'pre_stream' buffers the full response, validates it against guardrails, and only emits it if it passes.
   */
  guardrails_output_mode?: "post_stream" | "pre_stream";
  /**
   * Divider2
   */
  divider2?: string;
  /**
   * Guardrail Action
   *
   * Choose the action to take when a guardrail is triggered.
   */
  guardrail_action?:
    | {
        [key: string]: string;
      }
    | string;
  /**
   * Guardrail Action Replace
   */
  guardrail_action_replace?: string;
  /**
   * Guardrail Action Human
   */
  guardrail_action_human?: string;
  /**
   * Block After Violations
   *
   * Block the chat after this many turns with guardrail violations. Set to 0 to never block (per-turn warnings still surface).
   */
  block_after_violations?: number;
  /**
   * Block Message
   *
   * Message shown to the user when the chat is locked.
   */
  block_message?: string;
  /**
   * Divider3
   */
  divider3?: string;
  /**
   * Prompt Defense
   */
  prompt_defense?: string;
  /**
   * Prompt Lock
   *
   * Locks the prompt defense settings
   */
  prompt_lock?: boolean;
  /**
   * Jailbreak
   *
   * Prevents attempts to manipulate or "jailbreak" the model into ignoring safety rules or system instructions (e.g., prompts like "ignore previous instructions").
   */
  jailbreak?: boolean;
  /**
   * Prompt Injection
   *
   * Detects and blocks strategies where users attempt to get around restrictions indirectly (e.g., asking the model to 'pretend' or speak as a fictional character to evade policies).
   */
  prompt_injection?: boolean;
  /**
   * Context Leak
   *
   * Prevents the model from unintentionally revealing internal context, instructions, or system messages that are not meant to be shared with the user.
   */
  context_leak?: boolean;
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   *
   * The message to display to the user when the tool is called.
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   *
   * The message to display to the user when the tool completes.
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "guardrails";
  /**
   * Settings Display
   */
  settings_display?: "dialog";
  /**
   * Guardrail Engine
   *
   * How the semantic categories are scanned. The language model makes one call per enabled category. A decision model answers every category in a single call, faster and far cheaper, and needs a decision model enabled under Settings > Models; without one the language model is used.
   */
  guardrail_engine?: "llm" | "system_one";
};

/**
 * GuidelineConfig
 *
 * A single When/Then behavioral guideline stored in agent settings.
 */
export type GuidelineConfig = {
  /**
   * Id
   */
  id: string;
  /**
   * Condition
   */
  condition: string;
  /**
   * Action
   */
  action: string;
  /**
   * Priority
   */
  priority?: string;
  /**
   * Tags
   */
  tags?: Array<string>;
  /**
   * Enabled
   */
  enabled?: boolean;
  tool_association?: ToolAssociationConfig | null;
  /**
   * Tool Associations
   */
  tool_associations?: Array<ToolAssociationConfig>;
};

/**
 * HTTPHealthOverride
 */
export type HttpHealthOverride = {
  /**
   * Enabled
   */
  enabled?: boolean | null;
  /**
   * Url
   *
   * Complete health endpoint URL; replaces the runtime service URL.
   */
  url?: string | null;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
  /**
   * Detail
   */
  detail?: Array<ValidationError>;
};

/**
 * HealthCheckOverrides
 */
export type HealthCheckOverrides = {
  backend?: HttpHealthOverride;
  frontend?: HttpHealthOverride;
  relay?: HttpHealthOverride;
  sandbox?: HttpHealthOverride;
  workflow_worker?: WorkerHealthOverride;
  chat_worker?: WorkerHealthOverride;
};

/**
 * HeuristicEvaluationRequest
 *
 * Request to evaluate prompt quality using heuristics.
 */
export type HeuristicEvaluationRequest = {
  /**
   * Prompt
   */
  prompt:
    | Array<{
        [key: string]: unknown;
      }>
    | string;
  /**
   * Context
   */
  context?: string | null;
  /**
   * Heuristic Types
   */
  heuristic_types?: Array<string> | null;
};

/**
 * HeuristicEvaluationResponse
 *
 * Complete heuristic evaluation result.
 */
export type HeuristicEvaluationResponse = {
  clarity?: HeuristicScoreResponse | null;
  ambiguity?: HeuristicScoreResponse | null;
  specificity?: HeuristicScoreResponse | null;
  actionability?: HeuristicScoreResponse | null;
  task_breakdown?: HeuristicScoreResponse | null;
  roleplaying?: HeuristicScoreResponse | null;
  /**
   * Weighted Score
   */
  weighted_score: number;
  /**
   * Evaluated Types
   */
  evaluated_types: Array<string>;
};

/**
 * HeuristicImprovementRequest
 *
 * Request to improve a prompt using heuristic-driven optimization.
 */
export type HeuristicImprovementRequest = {
  /**
   * Prompt
   */
  prompt?:
    | Array<{
        [key: string]: unknown;
      }>
    | string;
  /**
   * Feedback
   */
  feedback?: string | null;
  heuristic_results?: HeuristicEvaluationResponse | null;
  /**
   * Context
   */
  context?: string | null;
  /**
   * Inputs
   */
  inputs?: Array<InputSpec> | Array<string> | null;
  /**
   * Variable Syntax
   */
  variable_syntax?: "double_paren" | "var_directive";
  /**
   * Strategy
   */
  strategy?: "simple" | "gepa";
  /**
   * Max Iterations
   */
  max_iterations?: number;
  /**
   * Max Metric Calls
   */
  max_metric_calls?: number;
};

/**
 * HeuristicImprovementResponse
 *
 * Response from heuristic-driven prompt improvement.
 */
export type HeuristicImprovementResponse = {
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Reasoning
   */
  reasoning: string;
  /**
   * Strategy Used
   */
  strategy_used: string;
  /**
   * Original Score
   */
  original_score?: number | null;
  /**
   * Improved Score
   */
  improved_score?: number | null;
};

/**
 * HeuristicScoreResponse
 *
 * Individual heuristic score.
 */
export type HeuristicScoreResponse = {
  /**
   * Score
   */
  score: number;
  /**
   * Reasoning
   */
  reasoning: string;
  /**
   * Suggestions
   */
  suggestions: Array<string>;
};

/**
 * HistoryEvent
 */
export type HistoryEvent = {
  /**
   * Kind
   */
  kind:
    | "added"
    | "changed"
    | "moved"
    | "removed"
    | "case-added"
    | "case-changed"
    | "case-removed";
  commit: CommitRef | null;
  /**
   * Title
   */
  title: string;
  /**
   * Changes
   */
  changes?: Array<FieldChange>;
  /**
   * Case Key
   */
  case_key?: string | null;
  /**
   * Case Title
   */
  case_title?: string | null;
};

/**
 * HumanInTheLoopSettings
 */
export type HumanInTheLoopSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "human_in_the_loop";
  /**
   * Has Timeout
   */
  has_timeout?: boolean;
  /**
   * Timeout
   */
  timeout?: number | null;
  /**
   * Handover Message
   */
  handover_message?: string;
};

/**
 * HumanInTheLoopSettings
 */
export type HumanInTheLoopSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "human_in_the_loop";
  /**
   * Has Timeout
   */
  has_timeout?: boolean;
  /**
   * Timeout
   */
  timeout?: number | null;
  /**
   * Handover Message
   */
  handover_message?: string;
};

/**
 * ImageDataContainer
 */
export type ImageDataContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "Image";
  /**
   * Value
   */
  value: FileType | Array<FileType> | unknown | null;
};

/**
 * ImageGenerationSettings
 *
 * Image generation quick action.
 *
 * When on, the agent can generate images from text prompts with any
 * image-generation-capable model in the registry; results are surfaced
 * inline in the chat.
 */
export type ImageGenerationSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "image_generation";
};

/**
 * ImageGenerationSettings
 *
 * Image generation quick action.
 *
 * When on, the agent can generate images from text prompts with any
 * image-generation-capable model in the registry; results are surfaced
 * inline in the chat.
 */
export type ImageGenerationSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "image_generation";
};

/**
 * ImageMetadata
 *
 * Structural metadata for an image file.
 */
export type ImageMetadata = {
  /**
   * Kind
   */
  kind?: "image";
  /**
   * Width
   */
  width: number;
  /**
   * Height
   */
  height: number;
  /**
   * Format
   */
  format?: string | null;
};

/**
 * ImageUrl
 *
 * A URL to an image.
 */
export type ImageUrl = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "image-url";
  /**
   * Media Type
   *
   * Return the media type of the file, based on the URL or the provided `media_type`.
   */
  readonly media_type: string;
  /**
   * Identifier
   *
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `FileUrl`.
   *
   * This identifier is only automatically passed to the model when the `FileUrl` is returned by a tool.
   * If you're passing the `FileUrl` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `FileUrl`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  readonly identifier: string;
};

/**
 * ImportInboxRequest
 */
export type ImportInboxRequest = {
  /**
   * Definition
   */
  definition: string;
  /**
   * Payload format. `auto` (default) detects it from the content; `v3` / `v4` force the base64 / YAML parse path.
   */
  version?: ExportFormat;
};

/**
 * ImportMode
 *
 * Operator control over how a bundle's artifacts land in the target.
 *
 * - ``version``: keep the bundle's IDs. If the artifact already exists in
 * the target workspace, append a new version row (Workflow/Assistant)
 * or refuse with NOT_VERSIONABLE (KB/Inbox — use ``replace``). If not,
 * create with the bundle's id. Lineage history is appended on every
 * successful artifact write.
 * - ``clone``: today's default — fresh UUIDs everywhere, no history.
 * - ``replace``: keep IDs, overwrite the active definition in place. No
 * version row. Lineage history is appended.
 */
export const ImportMode = {
  VERSION: "version",
  CLONE: "clone",
  REPLACE: "replace",
} as const;

/**
 * ImportMode
 *
 * Operator control over how a bundle's artifacts land in the target.
 *
 * - ``version``: keep the bundle's IDs. If the artifact already exists in
 * the target workspace, append a new version row (Workflow/Assistant)
 * or refuse with NOT_VERSIONABLE (KB/Inbox — use ``replace``). If not,
 * create with the bundle's id. Lineage history is appended on every
 * successful artifact write.
 * - ``clone``: today's default — fresh UUIDs everywhere, no history.
 * - ``replace``: keep IDs, overwrite the active definition in place. No
 * version row. Lineage history is appended.
 */
export type ImportMode = (typeof ImportMode)[keyof typeof ImportMode];

/**
 * ImportPlanColumn
 */
export type ImportPlanColumn = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Type
   */
  type: string;
  /**
   * Type Label
   */
  type_label: string;
  /**
   * Filled From
   */
  filled_from?: string | null;
};

/**
 * ImportPlanRequest
 *
 * A file import as Genie's tables_import_csv call names it.
 */
export type ImportPlanRequest = {
  /**
   * File Id
   */
  file_id: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Table Id
   */
  table_id?: string | null;
  /**
   * Mapping
   */
  mapping?: {
    [key: string]: string | null;
  } | null;
  /**
   * Skip Rows
   */
  skip_rows?: number | null;
};

/**
 * ImportPlanResponse
 *
 * What the import would create, with nothing written: the columns (a new
 * table's, or the existing table's that get values) and the row count.
 */
export type ImportPlanResponse = {
  /**
   * New Table
   */
  new_table: boolean;
  /**
   * Columns
   */
  columns: Array<ImportPlanColumn>;
  /**
   * Rows
   */
  rows: number;
};

/**
 * ImportPreviewResponse
 */
export type ImportPreviewResponse = {
  /**
   * Rows
   */
  rows: Array<Array<string>>;
  /**
   * Sheets
   */
  sheets?: Array<string>;
  /**
   * Total Rows
   */
  total_rows?: number | null;
};

/**
 * ImportRowsResponse
 */
export type ImportRowsResponse = {
  /**
   * Inserted
   */
  inserted: number;
};

/**
 * ImportWorkflowRequest
 */
export type ImportWorkflowRequest = {
  /**
   * Definition
   */
  definition: string;
  /**
   * Payload format. `auto` (default) detects it from the content; `v3` / `v4` force the base64 / YAML parse path.
   */
  version?: ExportFormat;
};

/**
 * ImprovementRequest
 */
export type ImprovementRequest = {
  /**
   * Improver Prompt
   */
  improver_prompt?: "PromptImproverPromptV2" | "JsonSchemaGeneratorPrompt";
  /**
   * Response
   */
  response: string;
  /**
   * Prompt
   */
  prompt: string;
};

/**
 * ImprovementResponse
 */
export type ImprovementResponse = {
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Reasoning
   */
  reasoning: string;
};

/**
 * InboxCreate
 */
export type InboxCreate = {
  /**
   * Name
   *
   * Name of the inbox
   */
  name: string;
  /**
   * Description
   *
   * Description of the inbox
   */
  description?: string | null;
  /**
   * Meta
   *
   * Inbox metadata (icon, etc.)
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Order
   *
   * Display order
   */
  order?: number | null;
  /**
   * Filtering Enabled
   *
   * Whether filtering is enabled
   */
  filtering_enabled?: boolean;
  /**
   * Filtering Rules
   *
   * AI filtering rules
   */
  filtering_rules?: string | null;
  /**
   * Filtering Mode
   *
   * Filtering mode (ai_rules or custom)
   */
  filtering_mode?: "ai_rules" | "custom";
  /**
   * Extraction Enabled
   *
   * Whether extraction is enabled
   */
  extraction_enabled?: boolean;
  /**
   * Extraction Mode
   *
   * Extraction mode (auto or custom)
   */
  extraction_mode?: "auto" | "custom";
  /**
   * Process Enabled
   *
   * Whether process flow is enabled
   */
  process_enabled?: boolean;
  /**
   * Case Prefix
   *
   * Case ID prefix (e.g. TAP for TAP-001)
   */
  case_prefix?: string | null;
};

/**
 * InboxCreateResponse
 */
export type InboxCreateResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Filtering Workflow Id
   */
  filtering_workflow_id?: string | null;
  /**
   * Extraction Workflow Id
   */
  extraction_workflow_id?: string | null;
  /**
   * Default Filtering Workflow Id
   */
  default_filtering_workflow_id?: string | null;
  /**
   * Default Extraction Workflow Id
   */
  default_extraction_workflow_id?: string | null;
  /**
   * Case Prefix
   */
  case_prefix?: string | null;
  /**
   * Meta
   */
  meta: {
    [key: string]: unknown;
  } | null;
  /**
   * Version
   */
  version: number;
  /**
   * Order
   */
  order: number;
  /**
   * Filtering Enabled
   */
  filtering_enabled: boolean;
  /**
   * Filtering Rules
   */
  filtering_rules?: string | null;
  /**
   * Filtering Mode
   */
  filtering_mode: string;
  /**
   * Extraction Enabled
   */
  extraction_enabled: boolean;
  /**
   * Extraction Mode
   */
  extraction_mode: string;
  /**
   * Process Enabled
   */
  process_enabled: boolean;
  /**
   * Created By
   */
  created_by: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * InboxFieldCreate
 */
export type InboxFieldCreate = {
  /**
   * Name
   *
   * Field name
   */
  name: string;
  /**
   * Type
   *
   * Field type (text, textarea, number, date, boolean, select, email, phone, url, file, table)
   */
  type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "boolean"
    | "select"
    | "email"
    | "phone"
    | "url"
    | "file"
    | "table";
  /**
   * Required
   *
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Options
   *
   * Per-type options: list[str] for select, a table-lookup source for lookup selects, list[TableColumn] for table
   */
  options?: Array<string> | Array<TableColumn> | TableSelectSource | null;
  /**
   * Group
   *
   * Field group name
   */
  group?: string | null;
  /**
   * Order
   *
   * Display order; auto-assigned when omitted
   */
  order?: number | null;
  /**
   * Description
   *
   * Optional description for the field
   */
  description?: string | null;
  /**
   * Case View
   *
   * How the field appears in the case view: 'hidden' (not shown), 'read_only' (shown locked), 'editable' (shown editable)
   */
  case_view?: "hidden" | "read_only" | "editable";
  /**
   * Flow Editable
   *
   * Whether workflows (nodes, extraction) can write this field. Reads are always allowed.
   */
  flow_editable?: boolean;
  /**
   * Render Markdown
   *
   * Long-text fields only: render the case-view editor as markdown instead of plain text.
   */
  render_markdown?: boolean;
  /**
   * Shape-validation rules for this field's values
   */
  validation?: FieldValidation | null;
};

/**
 * InboxFieldResponse
 */
export type InboxFieldResponse = {
  /**
   * Name
   *
   * Field name
   */
  name: string;
  /**
   * Type
   *
   * Field type (text, textarea, number, date, boolean, select, email, phone, url, file, table)
   */
  type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "boolean"
    | "select"
    | "email"
    | "phone"
    | "url"
    | "file"
    | "table";
  /**
   * Required
   *
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Options
   *
   * Per-type options: list[str] for select, a table-lookup source for lookup selects, list[TableColumn] for table
   */
  options?: Array<string> | Array<TableColumn> | TableSelectSource | null;
  /**
   * Group
   *
   * Field group name
   */
  group?: string | null;
  /**
   * Order
   *
   * Display order
   */
  order?: number;
  /**
   * Description
   *
   * Optional description for the field
   */
  description?: string | null;
  /**
   * Case View
   *
   * How the field appears in the case view: 'hidden' (not shown), 'read_only' (shown locked), 'editable' (shown editable)
   */
  case_view?: "hidden" | "read_only" | "editable";
  /**
   * Flow Editable
   *
   * Whether workflows (nodes, extraction) can write this field. Reads are always allowed.
   */
  flow_editable?: boolean;
  /**
   * Render Markdown
   *
   * Long-text fields only: render the case-view editor as markdown instead of plain text.
   */
  render_markdown?: boolean;
  /**
   * Shape-validation rules for this field's values
   */
  validation?: FieldValidation | null;
  /**
   * Id
   */
  id: string;
  /**
   * Inbox Id
   */
  inbox_id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * InboxFieldUpdate
 */
export type InboxFieldUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Type
   */
  type?: string | null;
  /**
   * Required
   */
  required?: boolean | null;
  /**
   * Options
   */
  options?: Array<string> | Array<TableColumn> | TableSelectSource | null;
  /**
   * Group
   */
  group?: string | null;
  /**
   * Order
   */
  order?: number | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Case View
   */
  case_view?: "hidden" | "read_only" | "editable" | null;
  /**
   * Flow Editable
   */
  flow_editable?: boolean | null;
  /**
   * Render Markdown
   */
  render_markdown?: boolean | null;
  validation?: FieldValidation | null;
};

/**
 * InboxGroupRenameRequest
 */
export type InboxGroupRenameRequest = {
  /**
   * Old Name
   */
  old_name: string;
  /**
   * New Name
   */
  new_name: string;
};

/**
 * InboxListResponse
 */
export type InboxListResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Meta
   */
  meta: {
    [key: string]: unknown;
  } | null;
  /**
   * Version
   */
  version: number;
  /**
   * Order
   */
  order: number;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  stats?: InboxStats | null;
};

/**
 * InboxResponse
 */
export type InboxResponse = {
  /**
   * Name
   *
   * Name of the inbox
   */
  name: string;
  /**
   * Description
   *
   * Description of the inbox
   */
  description?: string | null;
  /**
   * Meta
   *
   * Inbox metadata (icon, etc.)
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Order
   *
   * Display order
   */
  order?: number | null;
  /**
   * Filtering Enabled
   *
   * Whether filtering is enabled
   */
  filtering_enabled?: boolean;
  /**
   * Filtering Rules
   *
   * AI filtering rules
   */
  filtering_rules?: string | null;
  /**
   * Filtering Mode
   *
   * Filtering mode (ai_rules or custom)
   */
  filtering_mode?: "ai_rules" | "custom";
  /**
   * Extraction Enabled
   *
   * Whether extraction is enabled
   */
  extraction_enabled?: boolean;
  /**
   * Extraction Mode
   *
   * Extraction mode (auto or custom)
   */
  extraction_mode?: "auto" | "custom";
  /**
   * Process Enabled
   *
   * Whether process flow is enabled
   */
  process_enabled?: boolean;
  /**
   * Id
   */
  id: string;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Filtering Workflow Id
   */
  filtering_workflow_id?: string | null;
  /**
   * Extraction Workflow Id
   */
  extraction_workflow_id?: string | null;
  /**
   * Default Filtering Workflow Id
   */
  default_filtering_workflow_id?: string | null;
  /**
   * Default Extraction Workflow Id
   */
  default_extraction_workflow_id?: string | null;
  /**
   * Case Prefix
   */
  case_prefix?: string | null;
  /**
   * Validation Rules
   */
  validation_rules?: Array<
    | ({
        kind: "compare";
      } & CompareRule)
    | ({
        kind: "require_when";
      } & RequireWhenRule)
    | ({
        kind: "any_of";
      } & AnyOfRule)
  > | null;
  case_layout?: CaseLayout | null;
  /**
   * Version
   */
  version: number;
  /**
   * Created By
   */
  created_by: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Inbox Fields
   */
  inbox_fields?: Array<InboxFieldResponse> | null;
};

/**
 * InboxStats
 */
export type InboxStats = {
  /**
   * Open Cases Count
   */
  open_cases_count?: number;
};

/**
 * InboxTemplateDeploy
 */
export type InboxTemplateDeploy = {
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
  /**
   * Source
   */
  source?: string | null;
  /**
   * Source Config
   */
  source_config?: {
    [key: string]: unknown;
  };
};

/**
 * InboxUpdate
 */
export type InboxUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Order
   */
  order?: number | null;
  /**
   * Filtering Enabled
   */
  filtering_enabled?: boolean | null;
  /**
   * Filtering Rules
   */
  filtering_rules?: string | null;
  /**
   * Filtering Mode
   */
  filtering_mode?: "ai_rules" | "custom" | null;
  /**
   * Extraction Enabled
   */
  extraction_enabled?: boolean | null;
  /**
   * Extraction Mode
   */
  extraction_mode?: "auto" | "custom" | null;
  /**
   * Process Enabled
   */
  process_enabled?: boolean | null;
  /**
   * Case Prefix
   *
   * Case ID prefix (e.g. TAP for TAP-001)
   */
  case_prefix?: string | null;
  /**
   * Validation Rules
   *
   * Cross-field validation rules for this inbox
   */
  validation_rules?: Array<
    | ({
        kind: "compare";
      } & CompareRule)
    | ({
        kind: "require_when";
      } & RequireWhenRule)
    | ({
        kind: "any_of";
      } & AnyOfRule)
  > | null;
};

/**
 * InputOverride
 *
 * Override for a single workflow input.
 */
export type InputOverride = {
  /**
   * Mode
   */
  mode?: "fixed" | "variable" | "conversation_property";
  /**
   * Value
   */
  value?: string;
};

/**
 * InputSpec
 *
 * Typed description of an available input variable.
 */
export type InputSpec = {
  /**
   * Name
   */
  name: string;
  /**
   * Kind
   */
  kind?: "value" | "reference";
  /**
   * Label
   */
  label?: string | null;
};

/**
 * InsightConversation
 *
 * A conversation behind an insight fact (the drill-down list rows).
 */
export type InsightConversation = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Preview
   */
  preview: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Score
   */
  score?: number | null;
};

/**
 * InsightConversations
 */
export type InsightConversations = {
  /**
   * Conversations
   */
  conversations: Array<InsightConversation>;
};

/**
 * InsightsBootstrapResponse
 */
export type InsightsBootstrapResponse = {
  /**
   * Queued
   */
  queued: boolean;
};

/**
 * InsightsCalculating
 */
export type InsightsCalculating = {
  /**
   * Targets
   */
  targets: Array<string>;
};

/**
 * InsightsConfigResponse
 */
export type InsightsConfigResponse = {
  /**
   * Paused
   */
  paused: boolean;
  /**
   * Analysis Interval Seconds
   */
  analysis_interval_seconds: number;
  /**
   * Aggregate Interval Seconds
   */
  aggregate_interval_seconds: number;
  /**
   * Analysis Concurrency
   */
  analysis_concurrency: number;
  /**
   * Job Priority
   */
  job_priority: number;
  /**
   * Analysis Last Run At
   */
  analysis_last_run_at: number;
  /**
   * Aggregate Last Run At
   */
  aggregate_last_run_at: number;
  /**
   * Analysis Interval Is Default
   */
  analysis_interval_is_default: boolean;
  /**
   * Aggregate Interval Is Default
   */
  aggregate_interval_is_default: boolean;
  /**
   * Tuning
   */
  tuning: {
    [key: string]: number;
  };
};

/**
 * InsightsConfigUpdateRequest
 *
 * Pass 0 to reset an interval/concurrency to its default. ``job_priority``
 * is signed (negative = low) and has no reset sentinel. ``tuning`` updates the
 * pipeline knobs by ``InsightsTuning`` field name (0 resets one to default).
 */
export type InsightsConfigUpdateRequest = {
  /**
   * Paused
   */
  paused?: boolean | null;
  /**
   * Analysis Interval Seconds
   */
  analysis_interval_seconds?: number | null;
  /**
   * Aggregate Interval Seconds
   */
  aggregate_interval_seconds?: number | null;
  /**
   * Analysis Concurrency
   */
  analysis_concurrency?: number | null;
  /**
   * Job Priority
   */
  job_priority?: number | null;
  /**
   * Tuning
   */
  tuning?: {
    [key: string]: number;
  } | null;
};

/**
 * InsightsRunResponse
 */
export type InsightsRunResponse = {
  /**
   * Job Id
   */
  job_id: number;
  /**
   * Message
   */
  message: string;
};

/**
 * InstalledPluginResponse
 *
 * Response schema for installed plugins (runtime instances)
 */
export type InstalledPluginResponse = {
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Version
   */
  version: string;
  /**
   * Author
   */
  author: string;
  category: PluginCategory;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Dependencies
   */
  dependencies?: Array<string>;
  components: PluginComponents;
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  source_type: PluginSourceType;
  status: PluginStatus;
  /**
   * Last Error
   */
  last_error?: string | null;
  /**
   * Last Error Code
   */
  last_error_code?: string | null;
  /**
   * Config Schema
   */
  config_schema?: {
    [key: string]: unknown;
  } | null;
  /**
   * Config Data
   */
  config_data?: {
    [key: string]: unknown;
  } | null;
};

/**
 * IntegrationAdminStatus
 */
export type IntegrationAdminStatus = {
  /**
   * Type
   */
  type: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Image
   */
  image: string;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Enabled
   */
  enabled: boolean;
  /**
   * Bundled
   */
  bundled: boolean;
  /**
   * Has Secret Auth
   */
  has_secret_auth: boolean;
  /**
   * Providers
   */
  providers: Array<ProviderStatus>;
  /**
   * Scopes
   */
  scopes: Array<ScopeInfo>;
  /**
   * Nodes
   */
  nodes: Array<AdminIntegrationNodeInfo>;
  /**
   * Triggers
   */
  triggers: Array<AdminIntegrationTriggerInfo>;
  settings: IntegrationEntitySettings;
};

/**
 * IntegrationEnabledRequest
 */
export type IntegrationEnabledRequest = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * IntegrationEntitySettings
 *
 * Per-integration override settings.  Same shape at every layer.
 */
export type IntegrationEntitySettings = {
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Disabled Nodes
   */
  disabled_nodes?: Array<string>;
  /**
   * Disabled Triggers
   */
  disabled_triggers?: Array<string>;
  /**
   * Active Scope Keys
   */
  active_scope_keys?: Array<string> | null;
};

/**
 * IntegrationInfo
 */
export type IntegrationInfo = {
  /**
   * Type
   */
  type: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Image
   */
  image: string;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Enabled
   */
  enabled: boolean;
  /**
   * Category
   */
  category: string;
  /**
   * Mcp Category
   */
  mcp_category?: string | null;
  /**
   * Providers
   */
  providers: Array<ProviderInfo>;
  /**
   * Scopes
   */
  scopes: Array<ScopeInfo>;
  /**
   * Nodes
   */
  nodes: Array<IntegrationNodeInfo>;
  /**
   * Triggers
   */
  triggers: Array<IntegrationTriggerInfo>;
};

/**
 * IntegrationNodeInfo
 *
 * A node associated with an integration.
 */
export type IntegrationNodeInfo = {
  /**
   * Node Name
   */
  node_name: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Category
   */
  category: string;
  /**
   * Image
   */
  image: string | null;
  /**
   * Admin Enabled
   */
  admin_enabled: boolean;
  /**
   * Visible
   */
  visible: boolean;
};

/**
 * IntegrationProviderData
 *
 * Generic container for integration OAuth admin configs.
 */
export type IntegrationProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Type
   */
  type?: "integration_provider";
  /**
   * Provider Key
   */
  provider_key: string;
  /**
   * Config
   */
  config: {
    [key: string]: string;
  };
};

/**
 * IntegrationScopesResponse
 */
export type IntegrationScopesResponse = {
  /**
   * Integration Type
   */
  integration_type: string;
  /**
   * Scopes
   */
  scopes: Array<ScopeInfo>;
  /**
   * All Enabled
   */
  all_enabled: boolean;
};

/**
 * IntegrationSummary
 */
export type IntegrationSummary = {
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Image
   */
  image: string;
  /**
   * Description
   */
  description?: string;
};

/**
 * IntegrationTriggerInfo
 *
 * A trigger associated with an integration.
 */
export type IntegrationTriggerInfo = {
  /**
   * Trigger Name
   */
  trigger_name: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Image
   */
  image: string | null;
  /**
   * Admin Enabled
   */
  admin_enabled: boolean;
  /**
   * Visible
   */
  visible: boolean;
};

/**
 * IntegrationsInfoResponse
 */
export type IntegrationsInfoResponse = {
  /**
   * Integrations
   */
  integrations: Array<IntegrationInfo>;
};

/**
 * IntegrationsStatus
 */
export type IntegrationsStatus = {
  /**
   * Gdrive
   */
  gdrive: boolean;
  /**
   * Gmail
   */
  gmail: boolean;
  /**
   * Gspreadsheets
   */
  gspreadsheets: boolean;
  /**
   * Gcalendar
   */
  gcalendar: boolean;
  /**
   * Telegram
   */
  telegram: boolean;
  /**
   * Slack
   */
  slack: boolean;
  /**
   * Airtable
   */
  airtable: boolean;
  /**
   * Calendly
   */
  calendly: boolean;
  /**
   * Notion
   */
  notion: boolean;
  /**
   * Typeform
   */
  typeform: boolean;
  /**
   * Outlook
   */
  outlook: boolean;
  /**
   * Clipone
   */
  clipone: boolean;
  /**
   * Thom
   */
  thom: boolean;
  /**
   * Confluence
   */
  confluence: boolean;
  /**
   * Onedrive
   */
  onedrive: boolean;
  /**
   * Sharepoint
   */
  sharepoint: boolean;
  /**
   * Teams
   */
  teams: boolean;
  /**
   * Odoo
   */
  odoo: boolean;
  /**
   * Paypay
   */
  paypay: boolean;
  /**
   * Rapyd
   */
  rapyd: boolean;
  /**
   * Linear
   */
  linear: boolean;
  /**
   * Persona
   */
  persona: boolean;
  [key: string]: unknown | boolean;
};

/**
 * InterpretFiltersRequest
 */
export type InterpretFiltersRequest = {
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Schema Json
   */
  schema_json: string;
};

/**
 * InterpretFiltersResponse
 */
export type InterpretFiltersResponse = {
  /**
   * Filters
   */
  filters: Array<{
    [key: string]: unknown;
  }>;
};

/**
 * IsValidRequest
 */
export type IsValidRequest = {
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
};

/**
 * IsValidResponse
 */
export type IsValidResponse = {
  /**
   * Is Valid
   */
  is_valid: boolean;
  /**
   * Exceptions
   */
  exceptions: {
    [key: string]: Array<{
      [key: string]: unknown;
    }>;
  };
  /**
   * Warnings
   */
  warnings: {
    [key: string]: Array<{
      [key: string]: unknown;
    }>;
  };
};

/**
 * IterationError
 */
export type IterationError = {
  /**
   * Iteration
   */
  iteration: number;
  error: SpotError;
};

/**
 * IterationOutcomes
 *
 * Per-item record of a fan-out that failed under the `fail` policy.
 * Iterations are 1-based; items absent from every list finished.
 */
export type IterationOutcomes = {
  /**
   * Failed
   */
  failed: Array<IterationError>;
  /**
   * Not Run
   */
  not_run: Array<number>;
  /**
   * Skipped
   */
  skipped: Array<number>;
};

/**
 * IterationOutcomesContainer
 */
export type IterationOutcomesContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "_iterations";
  value: IterationOutcomes;
};

/**
 * JWKSet
 */
export type JwkSet = {
  /**
   * Keys
   */
  keys: Array<{
    [key: string]: string;
  }>;
};

/**
 * JWTScope
 */
export const JwtScope = {
  METRICS: "Metrics",
  SHELL_ACCESS: "Shell Access",
  USER_ADMINISTRATION: "User Administration",
  LOGS: "Logs",
  UPGRADE: "Upgrade",
} as const;

/**
 * JWTScope
 */
export type JwtScope = (typeof JwtScope)[keyof typeof JwtScope];

export type JsonValueInput = unknown;

/**
 * KBChaosInfo
 *
 * User-facing copy for a check's chaos operation. Local-dev only.
 */
export type KbChaosInfo = {
  /**
   * Label
   */
  label: string;
  /**
   * Description
   */
  description: string;
  /**
   * Icon
   */
  icon: string;
};

/**
 * KBChaosResult
 *
 * Returned by ``KBHealthCheck.chaos`` after a chaos action runs.
 */
export type KbChaosResult = {
  /**
   * Action
   */
  action:
    | "vector_db_connectivity"
    | "vectors_database"
    | "vector_table"
    | "embedding_dim"
    | "index_struct"
    | "embedding_provider"
    | "reranker"
    | "chunk_orphans"
    | "missing_chunks"
    | "metadata_drift"
    | "hnsw_index";
  /**
   * Note
   */
  note: string;
  /**
   * Affected
   */
  affected?: number;
};

/**
 * KBConfig
 */
export type KbConfig = {
  /**
   * Retrieval Info
   *
   * Information about retrieval settings
   */
  retrieval_info?: string;
  /**
   * Retrieval Method
   */
  retrieval_method?: string;
  /**
   * Reranking Enabled
   *
   * Retrieve a wider candidate pool, then reorder it with a reranker before returning results.
   */
  reranking_enabled?: boolean;
  /**
   * Rerankers
   *
   * Tried in order. If one is unavailable the next takes over. Leave empty to use the rerankers enabled for your workspace.
   */
  rerankers?: Array<string>;
  /**
   * Retrieval Top K
   *
   * Maximum number of results to return
   */
  retrieval_top_k?: number;
  /**
   * Retrieval Top K Window
   *
   * Searches against this Knowledge Base always return between min and max results. Requested top_k values outside this window are clamped.
   */
  retrieval_top_k_window?: Array<number>;
  /**
   * Fts Weight
   *
   * Weight for keyword search in hybrid methods
   */
  fts_weight?: number;
  /**
   * Semantic Weight
   *
   * Weight for semantic search in hybrid methods
   */
  semantic_weight?: number;
  /**
   * Rrf K
   *
   * RRF constant k - higher values reduce the impact of rank differences
   */
  rrf_k?: number;
  /**
   * Threshold Enabled
   *
   * Semantic retrieval only: exclude results below the minimum similarity score. Ignored by every other retrieval method.
   */
  threshold_enabled?: boolean;
  /**
   * Threshold
   *
   * Minimum cosine similarity for semantic retrieval results. Ignored by every other retrieval method.
   */
  threshold?: number;
  /**
   * Reranker Type
   *
   * The reranking method to use after initial retrieval
   */
  reranker_type?: string;
  /**
   * Reranker Top K
   *
   * Number of results to return from reranker
   */
  reranker_top_k?: number;
  /**
   * Reranker Threshold Enabled
   *
   * Enable minimum score threshold for reranker
   */
  reranker_threshold_enabled?: boolean;
  /**
   * Reranker Threshold
   *
   * Minimum score threshold for reranker results
   */
  reranker_threshold?: number;
  /**
   * Vertex Model
   *
   * Vertex AI ranking model to use
   */
  vertex_model?: string;
  /**
   * Ingestion Info
   *
   * Information about ingestion settings
   */
  ingestion_info?: string;
  /**
   * Batch Size
   *
   * Number of documents to process in each batch
   */
  batch_size?: number;
  /**
   * Default Chunk Size
   *
   * Default size for text chunks in characters
   */
  default_chunk_size?: number;
  /**
   * Default Chunk Overlap
   *
   * Default overlap between chunks in characters
   */
  default_chunk_overlap?: number;
  /**
   * Enrich Chunks Mode
   *
   * How to enrich chunks with additional context
   */
  enrich_chunks_mode?: string;
  /**
   * Enrich Pre Made Qa
   *
   * Generate Q&A pairs from the document content
   */
  enrich_pre_made_qa?: boolean;
  /**
   * Pdf Info
   *
   * PDF-specific processing settings
   */
  pdf_info?: string;
  /**
   * Pdf Extract Images
   *
   * Extract and process images from PDF files
   */
  pdf_extract_images?: boolean;
  /**
   * Pdf Chunk Size
   *
   * Chunk size for PDF documents
   */
  pdf_chunk_size?: number;
  /**
   * Pdf Chunk Overlap
   *
   * Chunk overlap for PDF documents
   */
  pdf_chunk_overlap?: number;
  /**
   * Pdf Ocr Enabled
   *
   * Enable OCR processing for scanned PDFs
   */
  pdf_ocr_enabled?: boolean;
  /**
   * Pdf Ocr Confidence
   *
   * Minimum confidence threshold for OCR text recognition
   */
  pdf_ocr_confidence?: number;
  /**
   * Pdf Ocr Language
   *
   * Language hint for OCR processing
   */
  pdf_ocr_language?: string;
  /**
   * Text Info
   *
   * Text-specific processing settings
   */
  text_info?: string;
  /**
   * Text Chunking Enabled
   *
   * Enable automatic chunking for text files
   */
  text_chunking_enabled?: boolean;
  /**
   * Text Chunk Size
   *
   * Chunk size for text documents
   */
  text_chunk_size?: number;
  /**
   * Text Chunk Overlap
   *
   * Chunk overlap for text documents
   */
  text_chunk_overlap?: number;
  /**
   * Text Preserve Formatting
   *
   * Preserve original text formatting and structure
   */
  text_preserve_formatting?: boolean;
  /**
   * Html Info
   *
   * HTML-specific processing settings
   */
  html_info?: string;
  /**
   * Html Extract Text Only
   *
   * Extract only text content from HTML
   */
  html_extract_text_only?: boolean;
  /**
   * Html Include Tables
   *
   * Include table content from HTML
   */
  html_include_tables?: boolean;
  /**
   * Html Include Images
   *
   * Include image references from HTML
   */
  html_include_images?: boolean;
  /**
   * Html Ingest As Markdown
   *
   * Convert HTML to Markdown before chunking. Preserves headings, lists, links, and tables while dropping tag noise, producing smaller, more semantic chunks. Recommended for crawled web pages; turn off only to reproduce legacy raw-HTML behaviour.
   */
  html_ingest_as_markdown?: boolean;
  /**
   * Image Info
   *
   * Image-specific processing settings
   */
  image_info?: string;
  /**
   * Image Ocr Enabled
   *
   * Enable OCR processing for images
   */
  image_ocr_enabled?: boolean;
  /**
   * Image Ocr Confidence
   *
   * Minimum confidence threshold for image OCR
   */
  image_ocr_confidence?: number;
  /**
   * Csv Row As Document
   *
   * Treat each row as a separate chunk.
   */
  csv_row_as_document?: boolean;
  /**
   * Csv Include Headers
   *
   * Include headers in the text representation
   */
  csv_include_headers?: boolean;
  /**
   * Csv Auto Detect Delimiter
   *
   * Automatically detect CSV delimiter
   */
  csv_auto_detect_delimiter?: boolean;
  /**
   * Csv Delimiter
   *
   * Custom delimiter for CSV parsing
   */
  csv_delimiter?: string;
  /**
   * Csv Encoding
   *
   * File encoding for CSV files
   */
  csv_encoding?: string;
  /**
   * Xlsx Row As Document
   *
   * Excel: emit one chunk per row (with cell coordinates and detected headers). Off → render the whole sheet as a markdown table and chunk by token budget, closer to the legacy text-based behavior. Default on; toggle off if a workbook has unusual structure that confuses row mode.
   */
  xlsx_row_as_document?: boolean;
  /**
   * Xlsx Include Headers
   *
   * Excel: include detected column headers alongside cell coordinates in row chunks.
   */
  xlsx_include_headers?: boolean;
  /**
   * Tabular Ingest All Rows
   *
   * Ingest every row regardless of size. Turn on only for small-to-medium workbooks; very large files will slow retrieval and may exhaust worker memory.
   */
  tabular_ingest_all_rows?: boolean;
  /**
   * Tabular Max Rows
   *
   * Excel & CSV: max rows ingested per sheet when the 'Ingest all rows' toggle is off. Rows beyond this are dropped and the document is flagged as truncated.
   */
  tabular_max_rows?: number;
  /**
   * Embedding Model
   *
   * The model to use for embedding documents
   */
  embedding_model?: Array<string>;
  /**
   * Embedding Dimension
   *
   * Leave unset to use the model's default dimension.
   */
  embedding_dimension?: number | null;
  /**
   * Mm Embedding Model
   *
   * The model to use for embedding documents
   */
  mm_embedding_model?: Array<string>;
  /**
   * Auto Summary
   *
   * Keep an AI-generated summary of this Knowledge Base up to date
   */
  auto_summary?: boolean;
  /**
   * Citation Mode
   *
   * Where a citation of a document from this Knowledge Base points.
   */
  citation_mode?: "source_url" | "noxus_url" | "metadata_key";
  /**
   * Citation Url Key
   *
   * Metadata field holding the URL to cite. Use a dot for nested fields. Documents without it fall back to the Noxus copy.
   */
  citation_url_key?: string;
  /**
   * Citation Title Mode
   *
   * What a citation of a document from this Knowledge Base reads as.
   */
  citation_title_mode?: "document_name" | "source_title" | "metadata_key";
  /**
   * Citation Title Key
   *
   * Metadata field holding the title to show. Use a dot for nested fields. Documents without it are cited by their name.
   */
  citation_title_key?: string;
};

/**
 * KBEvaluationRunRequest
 *
 * Request to launch a retrieval config-sweep run for a KB test suite.
 */
export type KbEvaluationRunRequest = {
  /**
   * Configs
   */
  configs: Array<KbRetrievalConfig>;
};

/**
 * KBEvaluationRunResponse
 *
 * Acknowledgement for a launched KB retrieval evaluation run.
 */
export type KbEvaluationRunResponse = {
  /**
   * Evaluation Run Id
   */
  evaluation_run_id: string;
  /**
   * Status
   */
  status: string;
};

/**
 * KBHealth
 *
 * Structural health of a v3 knowledge base, computed at request time.
 *
 * Returned on every ``GET /kbs/{id}`` so the FE can surface an inline
 * banner + CTA when the KB needs repair. Four concrete issues can be
 * detected by ``KBManagementService.compute_health``:
 *
 * - ``vector_table_missing`` — pgvector table was never created or was
 * dropped out from under us. Repair rebuilds the table at the
 * declared dim and queues every document for re-ingestion.
 * - ``index_struct_missing`` — the pgvector table is present but the
 * kirby ``index_struct`` row is absent. Repair rebuilds the struct
 * anchored to the existing column; no documents need reprocessing.
 * - ``dim_drift`` — declared dim no longer matches the pgvector column
 * width. Retrieval still works against the actual dim, but new
 * INSERTs fail. Repair recreates the table at the declared dim and
 * queues every document for re-ingestion.
 * - ``resolver_error`` — the resolver itself failed (DB outage,
 * unexpected column type). Not a repair-able state; the user is
 * asked to retry later. HTTP stays 200 so the FE can render the
 * apology inline instead of a blank error page.
 */
export type KbHealth = {
  /**
   * Status
   */
  status: "ok" | "needs_repair" | "error";
  /**
   * Issue
   */
  issue?:
    | "vector_table_missing"
    | "index_struct_missing"
    | "dim_drift"
    | "resolver_error"
    | "embedding_config_error"
    | null;
  /**
   * Detected Dim
   */
  detected_dim?: number | null;
  /**
   * Declared Dim
   */
  declared_dim?: number | null;
  /**
   * Message
   */
  message?: string;
  /**
   * Action
   */
  action?: string;
};

/**
 * KBHealthCheckMetadata
 *
 * Static metadata for a health check. Lives as a ClassVar on the check.
 *
 * ``repair`` is non-None when the check has a ``.repair()`` method.
 * ``chaos`` is non-None when the check has a ``.chaos()`` method.
 */
export type KbHealthCheckMetadata = {
  /**
   * Id
   */
  id:
    | "vector_db_connectivity"
    | "vectors_database"
    | "vector_table"
    | "embedding_dim"
    | "index_struct"
    | "embedding_provider"
    | "reranker"
    | "chunk_orphans"
    | "missing_chunks"
    | "metadata_drift"
    | "hnsw_index";
  /**
   * Severity
   */
  severity: "critical" | "warning";
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  repair?: KbRepairInfo | null;
  chaos?: KbChaosInfo | null;
};

/**
 * KBHealthManifest
 *
 * Full manifest of all checks; ordered same as ``HEALTH_CHECKS``.
 */
export type KbHealthManifest = {
  /**
   * Checks
   */
  checks: Array<KbHealthCheckMetadata>;
};

/**
 * KBHealthPipeline
 *
 * Result of running the full health pipeline against one KB.
 *
 * ``is_broken`` is ``True`` when any critical step status is ``"fail"``.
 * ``cluster_ok`` is ``False`` when either cluster-level check failed —
 * in that case all per-KB checks return ``"skip"``.
 */
export type KbHealthPipeline = {
  /**
   * Is Broken
   */
  is_broken: boolean;
  /**
   * Cluster Ok
   */
  cluster_ok: boolean;
  /**
   * Steps
   */
  steps: Array<KbHealthStep>;
  /**
   * Computed At
   */
  computed_at: string;
};

/**
 * KBHealthStep
 *
 * Result of one ``KBHealthCheck.detect`` call.
 */
export type KbHealthStep = {
  /**
   * Id
   */
  id:
    | "vector_db_connectivity"
    | "vectors_database"
    | "vector_table"
    | "embedding_dim"
    | "index_struct"
    | "embedding_provider"
    | "reranker"
    | "chunk_orphans"
    | "missing_chunks"
    | "metadata_drift"
    | "hnsw_index";
  /**
   * Severity
   */
  severity: "critical" | "warning";
  /**
   * Status
   */
  status: "ok" | "fail" | "skip";
  /**
   * Latency Ms
   */
  latency_ms?: number | null;
  /**
   * Note
   */
  note?: string | null;
  /**
   * Detected Dim
   */
  detected_dim?: number | null;
  /**
   * Declared Dim
   */
  declared_dim?: number | null;
  /**
   * Settings Target
   */
  settings_target?: "kb_embedding" | "workspace_rerankers" | null;
  /**
   * Repairable
   */
  repairable?: boolean;
};

/**
 * KBRepairInfo
 *
 * User-facing copy for a check's repair operation.
 */
export type KbRepairInfo = {
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Impact
   */
  impact: string;
};

/**
 * KBRepairQueued
 *
 * Returned when a repair is handed to the worker. The job id is what
 * the watch stream follows.
 */
export type KbRepairQueued = {
  /**
   * Job Id
   */
  job_id: number;
  /**
   * Message
   */
  message: string;
};

/**
 * KBRetrievalConfig
 */
export type KbRetrievalConfig = {
  /**
   * Retrieval Info
   *
   * Information about retrieval settings
   */
  retrieval_info?: string;
  /**
   * Retrieval Method
   */
  retrieval_method?: string;
  /**
   * Reranking Enabled
   *
   * Retrieve a wider candidate pool, then reorder it with a reranker before returning results.
   */
  reranking_enabled?: boolean;
  /**
   * Rerankers
   *
   * Tried in order. If one is unavailable the next takes over. Leave empty to use the rerankers enabled for your workspace.
   */
  rerankers?: Array<string>;
  /**
   * Retrieval Top K
   *
   * Maximum number of results to return
   */
  retrieval_top_k?: number;
  /**
   * Retrieval Top K Window
   *
   * Searches against this Knowledge Base always return between min and max results. Requested top_k values outside this window are clamped.
   */
  retrieval_top_k_window?: Array<number>;
  /**
   * Fts Weight
   *
   * Weight for keyword search in hybrid methods
   */
  fts_weight?: number;
  /**
   * Semantic Weight
   *
   * Weight for semantic search in hybrid methods
   */
  semantic_weight?: number;
  /**
   * Rrf K
   *
   * RRF constant k - higher values reduce the impact of rank differences
   */
  rrf_k?: number;
  /**
   * Threshold Enabled
   *
   * Semantic retrieval only: exclude results below the minimum similarity score. Ignored by every other retrieval method.
   */
  threshold_enabled?: boolean;
  /**
   * Threshold
   *
   * Minimum cosine similarity for semantic retrieval results. Ignored by every other retrieval method.
   */
  threshold?: number;
  /**
   * Reranker Type
   *
   * The reranking method to use after initial retrieval
   */
  reranker_type?: string;
  /**
   * Reranker Top K
   *
   * Number of results to return from reranker
   */
  reranker_top_k?: number;
  /**
   * Reranker Threshold Enabled
   *
   * Enable minimum score threshold for reranker
   */
  reranker_threshold_enabled?: boolean;
  /**
   * Reranker Threshold
   *
   * Minimum score threshold for reranker results
   */
  reranker_threshold?: number;
  /**
   * Vertex Model
   *
   * Vertex AI ranking model to use
   */
  vertex_model?: string;
};

/**
 * KBSyncSchedule
 *
 * Structured schedule the frontend round-trips; compiled to ``cron`` by
 * delegating to the matching ``schedule_v2`` config class.
 */
export type KbSyncSchedule = {
  action?: ScheduleV2Type;
  /**
   * Times
   */
  times?: Array<string>;
  /**
   * Days Of Week
   */
  days_of_week?: Array<string>;
  /**
   * Day Of Month
   */
  day_of_month?: number;
  /**
   * Interval Value
   */
  interval_value?: number;
  /**
   * Interval Unit
   */
  interval_unit?: "minutes" | "hours";
  /**
   * Cron Expression
   */
  cron_expression?: string;
};

/**
 * KBSyncScheduleInput
 */
export type KbSyncScheduleInput = {
  action?: ScheduleV2Type;
  /**
   * Times
   */
  times?: Array<string>;
  /**
   * Days Of Week
   */
  days_of_week?: Array<string>;
  /**
   * Day Of Month
   */
  day_of_month?: number;
  /**
   * Interval Value
   */
  interval_value?: number;
  /**
   * Interval Unit
   */
  interval_unit?: "minutes" | "hours";
  /**
   * Cron Expression
   */
  cron_expression?: string;
};

/**
 * KBSyntheticDatasetRequest
 *
 * Request to generate a synthetic retrieval-eval dataset from a KB.
 */
export type KbSyntheticDatasetRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Questions Per Chunk
   */
  questions_per_chunk?: number;
  /**
   * Sampling
   */
  sampling?: "random" | "stratified_by_doc";
  /**
   * Max Chunks
   */
  max_chunks?: number;
};

/**
 * KBSyntheticDatasetResponse
 *
 * Acknowledgement for a queued synthetic dataset generation job.
 */
export type KbSyntheticDatasetResponse = {
  /**
   * Knowledge Base Id
   */
  knowledge_base_id: string;
  /**
   * Status
   */
  status: string;
};

/**
 * KBTestSuiteCreate
 *
 * Request to create a knowledge_base test suite (a retrieval dataset).
 */
export type KbTestSuiteCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Kb Dataset Kind
   */
  kb_dataset_kind?: "synthetic" | "golden";
};

/**
 * KBToolMetadata
 */
export type KbToolMetadata = {
  /**
   * Type
   */
  type?: "kb";
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Display Message Start
   */
  display_message_start?: string | null;
  /**
   * Display Message End
   */
  display_message_end?: string | null;
  /**
   * Kb Id
   */
  kb_id: string;
  /**
   * Kb Name
   */
  kb_name: string;
  /**
   * Query
   */
  query?: string | null;
};

/**
 * KBType
 */
export const KbType = { TEMPORARY: "temporary", ENTITY: "entity" } as const;

/**
 * KBType
 */
export type KbType = (typeof KbType)[keyof typeof KbType];

/**
 * KeyHistory
 */
export type KeyHistory = {
  /**
   * Key
   */
  key: string;
  /**
   * Current
   */
  current: boolean;
  /**
   * Last Title
   */
  last_title: string | null;
  first_seen: CommitRef | null;
  last_changed: CommitRef | null;
  /**
   * Events
   */
  events: Array<HistoryEvent>;
};

/**
 * KnowledgeBaseBulkDeleteRequest
 */
export type KnowledgeBaseBulkDeleteRequest = {
  /**
   * Document Ids
   */
  document_ids: Array<string>;
};

/**
 * KnowledgeBaseBulkDeleteResult
 */
export type KnowledgeBaseBulkDeleteResult = {
  /**
   * Deleted Ids
   */
  deleted_ids: Array<string>;
  /**
   * Not Found Ids
   */
  not_found_ids: Array<string>;
};

/**
 * KnowledgeBaseDocument
 */
export type KnowledgeBaseDocument = {
  /**
   * Id
   */
  id: string;
  /**
   * Summary
   */
  summary: string | null;
  /**
   * Short Summary
   */
  short_summary?: string | null;
  /**
   * Doc Type
   */
  doc_type?: string | null;
  /**
   * Source Type
   */
  source_type?: string | null;
  /**
   * Source External Id
   */
  source_external_id?: string | null;
  /**
   * Source Metadata
   */
  source_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Content Type
   */
  content_type?: string | null;
  /**
   * Has Conversion
   */
  has_conversion?: boolean | null;
  status: DocumentStatus;
  /**
   * Doc Metadata
   */
  doc_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Error
   */
  error: {
    [key: string]: unknown;
  } | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Prefix
   */
  prefix?: string | null;
  /**
   * Uri
   */
  uri?: string | null;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Size
   */
  size: number;
  /**
   * File Id
   */
  file_id?: string | null;
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Dismissed
   */
  dismissed?: boolean | null;
  /**
   * Warnings
   */
  warnings?: Array<unknown> | null;
  /**
   * Needs Reingestion
   */
  needs_reingestion?: boolean | null;
};

/**
 * KnowledgeBaseDocumentWithCitation
 */
export type KnowledgeBaseDocumentWithCitation = {
  /**
   * Id
   */
  id: string;
  /**
   * Summary
   */
  summary: string | null;
  /**
   * Short Summary
   */
  short_summary?: string | null;
  /**
   * Doc Type
   */
  doc_type?: string | null;
  /**
   * Source Type
   */
  source_type?: string | null;
  /**
   * Source External Id
   */
  source_external_id?: string | null;
  /**
   * Source Metadata
   */
  source_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Content Type
   */
  content_type?: string | null;
  /**
   * Has Conversion
   */
  has_conversion?: boolean | null;
  status: DocumentStatus;
  /**
   * Doc Metadata
   */
  doc_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Error
   */
  error: {
    [key: string]: unknown;
  } | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Prefix
   */
  prefix?: string | null;
  /**
   * Uri
   */
  uri?: string | null;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Size
   */
  size: number;
  /**
   * File Id
   */
  file_id?: string | null;
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Dismissed
   */
  dismissed?: boolean | null;
  /**
   * Warnings
   */
  warnings?: Array<unknown> | null;
  /**
   * Needs Reingestion
   */
  needs_reingestion?: boolean | null;
  /**
   * Citation Url
   */
  citation_url?: string | null;
};

/**
 * KnowledgeBaseQaSettings
 *
 * Tool that allows the user to answer questions about a specific pre-selected knowledge base
 */
export type KnowledgeBaseQaSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "kb_qa";
  /**
   * Kb Id
   */
  kb_id: string;
  /**
   * Advanced Search
   */
  advanced_search?: boolean;
  /**
   * Clamp Top K
   */
  clamp_top_k?: boolean;
  /**
   * Top K Window
   */
  top_k_window?: Array<number>;
  /**
   * Private
   */
  private?: boolean;
  /**
   * Enable Browse Tools
   */
  enable_browse_tools?: boolean;
  /**
   * Include Tree In Instructions
   */
  include_tree_in_instructions?: boolean;
  /**
   * Tree Depth In Instructions
   */
  tree_depth_in_instructions?: number;
  /**
   * Link References To Noxus File
   */
  link_references_to_noxus_file?: boolean;
};

/**
 * KnowledgeBaseQaSettings
 *
 * Tool that allows the user to answer questions about a specific pre-selected knowledge base
 */
export type KnowledgeBaseQaSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "kb_qa";
  /**
   * Kb Id
   */
  kb_id: string;
  /**
   * Advanced Search
   */
  advanced_search?: boolean;
  /**
   * Clamp Top K
   */
  clamp_top_k?: boolean;
  /**
   * Top K Window
   */
  top_k_window?: Array<number>;
  /**
   * Private
   */
  private?: boolean;
  /**
   * Enable Browse Tools
   */
  enable_browse_tools?: boolean;
  /**
   * Include Tree In Instructions
   */
  include_tree_in_instructions?: boolean;
  /**
   * Tree Depth In Instructions
   */
  tree_depth_in_instructions?: number;
  /**
   * Link References To Noxus File
   */
  link_references_to_noxus_file?: boolean;
};

/**
 * KnowledgeBaseSelectorSettings
 *
 * Tool that allows the user to select a knowledge base to answer questions about
 */
export type KnowledgeBaseSelectorSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "kb_selector";
};

/**
 * KnowledgeBaseSelectorSettings
 *
 * Tool that allows the user to select a knowledge base to answer questions about
 */
export type KnowledgeBaseSelectorSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "kb_selector";
};

/**
 * KnowledgeBaseType
 */
export type KnowledgeBaseType = {
  /**
   * Source Type
   */
  source_type: string;
  /**
   * Name
   */
  name: string;
  /**
   * Image
   */
  image: string;
  /**
   * Integration
   */
  integration?: string | null;
  /**
   * Subtype
   */
  subtype?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Exposed
   */
  exposed?: boolean;
};

/**
 * KnowledgeBaseV2
 */
export type KnowledgeBaseV2 = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  status: TrainStatus;
  /**
   * Description
   */
  description: string;
  /**
   * Document Types
   */
  document_types: Array<string>;
  kb_type: KbType;
  /**
   * Size
   */
  size: number;
  /**
   * Num Docs
   */
  num_docs: number;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Error
   */
  error: {
    [key: string]: unknown;
  } | null;
  /**
   * Version
   */
  version: "v2" | "v3";
  /**
   * Summary
   */
  summary?: string | null;
  /**
   * Needs Summary Update
   */
  needs_summary_update?: boolean;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  embedding_health?: EmbeddingHealth | null;
  health?: KbHealth | null;
  /**
   * Total Documents
   */
  total_documents: number;
  /**
   * Training Documents
   */
  training_documents: number;
  /**
   * Trained Documents
   */
  trained_documents: number;
  /**
   * Error Documents
   */
  error_documents: number;
  /**
   * Uploaded Documents
   */
  uploaded_documents: number;
  /**
   * Source Types
   */
  source_types: {
    [key: string]: number;
  };
  /**
   * Training Source Types
   */
  training_source_types: Array<string>;
  settings_?: KbConfig | null;
};

/**
 * KnowledgeBaseV2WithTags
 */
export type KnowledgeBaseV2WithTags = {
  /**
   * Tags
   */
  tags?: Array<Tag> | null;
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  status: TrainStatus;
  /**
   * Description
   */
  description: string;
  /**
   * Document Types
   */
  document_types: Array<string>;
  kb_type: KbType;
  /**
   * Size
   */
  size: number;
  /**
   * Num Docs
   */
  num_docs: number;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Error
   */
  error: {
    [key: string]: unknown;
  } | null;
  /**
   * Version
   */
  version: "v2" | "v3";
  /**
   * Summary
   */
  summary?: string | null;
  /**
   * Needs Summary Update
   */
  needs_summary_update?: boolean;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  embedding_health?: EmbeddingHealth | null;
  health?: KbHealth | null;
  /**
   * Total Documents
   */
  total_documents: number;
  /**
   * Training Documents
   */
  training_documents: number;
  /**
   * Trained Documents
   */
  trained_documents: number;
  /**
   * Error Documents
   */
  error_documents: number;
  /**
   * Uploaded Documents
   */
  uploaded_documents: number;
  /**
   * Source Types
   */
  source_types: {
    [key: string]: number;
  };
  /**
   * Training Source Types
   */
  training_source_types: Array<string>;
  settings_?: KbConfig | null;
};

/**
 * KnowledgeBaseWithDocuments
 */
export type KnowledgeBaseWithDocuments = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  status: TrainStatus;
  /**
   * Description
   */
  description: string;
  /**
   * Document Types
   */
  document_types: Array<string>;
  kb_type: KbType;
  /**
   * Size
   */
  size: number;
  /**
   * Num Docs
   */
  num_docs: number;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Error
   */
  error: {
    [key: string]: unknown;
  } | null;
  /**
   * Version
   */
  version: "v2" | "v3";
  /**
   * Summary
   */
  summary?: string | null;
  /**
   * Needs Summary Update
   */
  needs_summary_update?: boolean;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  embedding_health?: EmbeddingHealth | null;
  health?: KbHealth | null;
  /**
   * Total Documents
   */
  total_documents: number;
  /**
   * Training Documents
   */
  training_documents: number;
  /**
   * Trained Documents
   */
  trained_documents: number;
  /**
   * Error Documents
   */
  error_documents: number;
  /**
   * Uploaded Documents
   */
  uploaded_documents: number;
  /**
   * Source Types
   */
  source_types: {
    [key: string]: number;
  };
  /**
   * Training Source Types
   */
  training_source_types: Array<string>;
  settings_?: KbConfig | null;
  /**
   * Documents
   */
  documents?: Array<KnowledgeBaseDocumentWithCitation>;
};

/**
 * LLMModelProperties
 */
export type LlmModelProperties = {
  /**
   * Speed
   */
  speed: number;
  /**
   * Quality
   */
  quality: number;
  /**
   * Max Input Tokens
   */
  max_input_tokens: number;
  /**
   * Max Output Tokens
   */
  max_output_tokens: number;
  /**
   * Is Multimodal
   */
  is_multimodal?: boolean;
};

/**
 * LabellingStatus
 */
export const LabellingStatus = {
  PENDING: "pending",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
} as const;

/**
 * LabellingStatus
 */
export type LabellingStatus =
  (typeof LabellingStatus)[keyof typeof LabellingStatus];

/**
 * LabellingTask
 */
export type LabellingTask = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string | null;
  /**
   * Timeout At
   */
  timeout_at: string | null;
  /**
   * Labelled By
   */
  labelled_by: string | null;
  status: LabellingStatus;
  /**
   * Inputs
   */
  inputs: {
    [key: string]: unknown;
  };
  /**
   * Outputs
   */
  outputs?: {
    [key: string]: unknown;
  } | null;
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
  /**
   * Workflow Name
   */
  workflow_name: string;
  /**
   * Workflow Icon
   */
  workflow_icon: string | null;
  /**
   * Workflow Id
   */
  workflow_id: string | null;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Node Id
   */
  node_id: string;
  /**
   * It
   */
  it: number;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Priority
   */
  priority?: string | null;
  /**
   * Assignee Id
   */
  assignee_id?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Assistant Id
   */
  assistant_id?: string | null;
  /**
   * Assistant Name
   */
  assistant_name?: string | null;
  /**
   * Assistant Icon
   */
  assistant_icon?: string | null;
};

/**
 * LangfuseProviderData
 *
 * Langfuse — open-source LLM observability with native OTLP support.
 */
export type LangfuseProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Endpoint
   */
  endpoint?: string;
  /**
   * Protocol
   */
  protocol?: "http/protobuf";
  /**
   * Project Name
   */
  project_name?: string;
  /**
   * Type
   */
  type?: "langfuse";
  /**
   * Public Key
   */
  public_key?: string;
  /**
   * Secret Key
   */
  secret_key?: string;
  connect_button?: ApiToggleState;
};

/**
 * LayoutCell
 */
export type LayoutCell = {
  /**
   * Field Id
   */
  field_id: string;
  /**
   * X
   */
  x: number;
  /**
   * Y
   */
  y: number;
  /**
   * W
   */
  w: number;
};

/**
 * LayoutSection
 */
export type LayoutSection = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Cells
   */
  cells: Array<LayoutCell>;
};

/**
 * LegacyAuthUser
 */
export type LegacyAuthUser = {
  /**
   * Id
   */
  id: string;
  /**
   * Auth Id
   */
  auth_id: string;
  /**
   * Email
   */
  email: string;
  /**
   * Name
   */
  name: string;
  /**
   * Tenant Id
   */
  tenant_id: string;
  /**
   * Tenant Name
   */
  tenant_name: string;
  /**
   * Is Active
   */
  is_active: boolean;
};

/**
 * LegacyCredentialMigrationResponse
 */
export type LegacyCredentialMigrationResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Secret Auth Migrated
   */
  secret_auth_migrated: number;
  /**
   * Secret Auth Failed
   */
  secret_auth_failed: number;
  /**
   * Oauth Migrated
   */
  oauth_migrated: number;
  /**
   * Oauth Skipped
   */
  oauth_skipped: number;
  /**
   * Oauth Failed
   */
  oauth_failed: number;
  /**
   * Message
   */
  message: string;
};

/**
 * LicenseConstraint
 */
export type LicenseConstraint = {
  /**
   * Id
   */
  id: string;
  /**
   * Cap
   */
  cap: number | null;
  constraint: ConstraintType;
  /**
   * Value
   */
  value?: number;
  /**
   * Payg Cap
   */
  payg_cap?: number | null;
  /**
   * Tenant Id
   */
  tenant_id?: string | null;
  /**
   * Group Id
   */
  group_id?: string | null;
  /**
   * Filter
   */
  filter_?: {
    [key: string]: unknown;
  } | null;
  period: BillingPeriod;
  /**
   * Changes Allowed
   */
  changes_allowed?: boolean;
  /**
   * Strict Limit
   */
  strict_limit?: boolean;
};

/**
 * LicenseResult
 */
export type LicenseResult = {
  /**
   * Metrics
   */
  metrics: {
    [key: string]: number;
  };
  /**
   * Constraints
   */
  constraints: Array<LicenseConstraint>;
};

/**
 * LifecycleConfigResponse
 */
export type LifecycleConfigResponse = {
  /**
   * Paused
   */
  paused: boolean;
  /**
   * Last Run At
   */
  last_run_at: number;
  /**
   * Interval Seconds
   */
  interval_seconds: number;
  /**
   * Deactivation Threshold
   */
  deactivation_threshold: number;
  /**
   * Recovery Probe Interval Seconds
   */
  recovery_probe_interval_seconds: number;
  /**
   * Check Timeout Seconds
   */
  check_timeout_seconds: number;
  /**
   * Max Concurrent Checks
   */
  max_concurrent_checks: number;
  /**
   * Deprecation Age Days
   */
  deprecation_age_days: number;
  /**
   * Interval Seconds Is Default
   */
  interval_seconds_is_default: boolean;
  /**
   * Deactivation Threshold Is Default
   */
  deactivation_threshold_is_default: boolean;
  /**
   * Recovery Probe Interval Seconds Is Default
   */
  recovery_probe_interval_seconds_is_default: boolean;
  /**
   * Check Timeout Seconds Is Default
   */
  check_timeout_seconds_is_default: boolean;
  /**
   * Max Concurrent Checks Is Default
   */
  max_concurrent_checks_is_default: boolean;
  /**
   * Deprecation Age Days Is Default
   */
  deprecation_age_days_is_default: boolean;
};

/**
 * LifecycleConfigUpdateRequest
 *
 * Pass 0 / 0.0 to reset a field to its env default.
 */
export type LifecycleConfigUpdateRequest = {
  /**
   * Paused
   */
  paused?: boolean | null;
  /**
   * Interval Seconds
   */
  interval_seconds?: number | null;
  /**
   * Deactivation Threshold
   */
  deactivation_threshold?: number | null;
  /**
   * Recovery Probe Interval Seconds
   */
  recovery_probe_interval_seconds?: number | null;
  /**
   * Check Timeout Seconds
   */
  check_timeout_seconds?: number | null;
  /**
   * Max Concurrent Checks
   */
  max_concurrent_checks?: number | null;
  /**
   * Deprecation Age Days
   */
  deprecation_age_days?: number | null;
};

/**
 * LifecycleRunHealthCheckResponse
 */
export type LifecycleRunHealthCheckResponse = {
  /**
   * Job Id
   */
  job_id: number;
  /**
   * Message
   */
  message: string;
};

/**
 * LimitUsageResponse
 */
export type LimitUsageResponse = {
  /**
   * Usage
   */
  usage: number;
};

/**
 * LimitsResponse
 *
 * Response containing all limits.
 */
export type LimitsResponse = {
  /**
   * Limits
   */
  limits: Array<OperationsLimit | ProviderCostLimit>;
};

/**
 * LinkConversationRequest
 */
export type LinkConversationRequest = {
  /**
   * Conversation Id
   */
  conversation_id: string;
};

/**
 * LinkProviderRequest
 */
export type LinkProviderRequest = {
  /**
   * Provider Id
   */
  provider_id: string;
  /**
   * Model Id
   */
  model_id: string;
  model_type: ConfigLinkType;
  /**
   * Priority
   */
  priority?: number;
};

/**
 * ListChange
 */
export type ListChange = {
  /**
   * Kind
   */
  kind?: "list";
  /**
   * Key
   */
  key: string;
  /**
   * Label
   */
  label: string;
  /**
   * Entries
   */
  entries: Array<EntryChange>;
};

/**
 * ListContainer
 */
export type ListContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "list";
  /**
   * Value
   */
  value: unknown;
};

/**
 * LiteLLMProviderData
 *
 * A LiteLLM proxy standing in front of whatever the customer runs.
 *
 * Unlike every other provider here, this one is not a vendor — it is a
 * gateway that can serve any model. That is why ``base_url`` is required and
 * has no default: there is no such thing as "the" LiteLLM endpoint.
 *
 * Models reach it by their ``integration_aliases.litellm`` name, which is the
 * ``model_name`` a LiteLLM config declares. Every model we ship carries one,
 * so the whole catalogue is routable through a proxy without any per-model
 * work — that is the point of having this provider at all rather than
 * pointing an OpenAI provider at the proxy.
 */
export type LiteLlmProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "litellm";
  /**
   * Litellm Api Key
   */
  LITELLM_API_KEY: string;
  /**
   * Base Url
   */
  base_url: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
  /**
   * Model Aliases
   */
  model_aliases?: Array<{
    [key: string]: unknown;
  }>;
};

/**
 * LoadCapabilityArgs
 *
 * Typed arguments for a `load_capability` tool call.
 */
export type LoadCapabilityArgs = {
  /**
   * Id
   *
   * The id of the capability to load.
   */
  id: string;
};

/**
 * LoadCapabilityCallPart
 *
 * Typed `ToolCallPart` for the `load_capability` tool.
 */
export type LoadCapabilityCallPart = {
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Name
   */
  tool_name?: "load_capability";
  /**
   * Args
   */
  args?: string | LoadCapabilityArgs | null;
  /**
   * Tool Kind
   */
  tool_kind?: "capability-load";
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "tool-call";
};

/**
 * LoadCapabilityReturn
 *
 * Typed return value for the `load_capability` tool.
 */
export type LoadCapabilityReturn = {
  /**
   * Instructions
   */
  instructions?: string;
};

/**
 * LoadCapabilityReturnPart
 *
 * Typed `ToolReturnPart` for the `load_capability` tool.
 */
export type LoadCapabilityReturnPart = {
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Name
   */
  tool_name?: "load_capability";
  content: LoadCapabilityReturn;
  /**
   * Tool Kind
   */
  tool_kind?: "capability-load";
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Part Kind
   */
  part_kind?: "tool-return";
};

/**
 * LocalPluginSource
 *
 * Local plugin source
 */
export type LocalPluginSource = {
  /**
   * Type
   */
  type?: "local";
  /**
   * Path
   *
   * Local path to the plugin directory
   */
  path: string;
};

/**
 * LogDetails
 */
export type LogDetails = {
  /**
   * Columns
   */
  columns: Array<WorkflowLogColumn>;
  /**
   * Number Of Rows
   */
  number_of_rows: number;
};

/**
 * LogEntry
 *
 * A single log entry from a plugin worker.
 */
export type LogEntry = {
  /**
   * Timestamp
   */
  timestamp: string;
  /**
   * Level
   */
  level: "info" | "warning" | "error" | "debug";
  /**
   * Message
   */
  message: string;
  /**
   * Source
   */
  source?: "stdout" | "stderr" | "application" | "install" | null;
};

/**
 * LoginRequest
 */
export type LoginRequest = {
  /**
   * Email
   */
  email: string;
  /**
   * Password
   */
  password: string;
  /**
   * Connection
   */
  connection?: string | null;
};

/**
 * LoginResponse
 */
export type LoginResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Access Token
   */
  access_token?: string | null;
  /**
   * Id Token
   */
  id_token?: string | null;
  /**
   * Refresh Token
   */
  refresh_token?: string | null;
  /**
   * Expires In
   */
  expires_in?: number | null;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Error Code
   */
  error_code?: string | null;
};

/**
 * LookupFillMapping
 */
export type LookupFillMapping = {
  /**
   * From
   */
  from: string;
  /**
   * To
   */
  to: string;
};

/**
 * MCPIdentityInfo
 */
export type McpIdentityInfo = {
  /**
   * Enabled
   */
  enabled: boolean;
  /**
   * Header Name
   */
  header_name: string;
  /**
   * Issuer
   */
  issuer: string;
  /**
   * Jwks Url
   */
  jwks_url: string;
  /**
   * Audience
   */
  audience: string;
  /**
   * Algorithm
   */
  algorithm: string;
  /**
   * Token Use
   */
  token_use: string;
  /**
   * Lifetime Seconds
   */
  lifetime_seconds: number;
};

/**
 * MCPServerBinding
 *
 * Per-agent binding to a workspace MCP credential.
 *
 * ``selected_tools=None`` means "every tool the server exposes minus the
 * credential's ``disabled_tools``" — new server tools flow through without a
 * re-probe. An explicit list is a per-agent allowlist narrowed further by the
 * workspace denials.
 */
export type McpServerBinding = {
  /**
   * Credential Id
   */
  credential_id: string;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Selected Tools
   */
  selected_tools?: Array<string> | null;
};

/**
 * MCPToolsResponse
 */
export type McpToolsResponse = {
  /**
   * Credential Id
   */
  credential_id: string;
  /**
   * Enabled Tools
   */
  enabled_tools: Array<string>;
  /**
   * Discovered Tools
   */
  discovered_tools: Array<string>;
  /**
   * Tool List Hash
   */
  tool_list_hash: string;
};

/**
 * ManagerUnreachable
 */
export type ManagerUnreachable = {
  /**
   * Url
   */
  url: string;
  /**
   * Error
   */
  error: string;
};

/**
 * Mark
 */
export type Mark = {
  /**
   * Type
   */
  type: string;
  /**
   * Attrs
   */
  attrs?: {
    [key: string]: unknown;
  } | null;
};

/**
 * MarketplacePluginResponse
 *
 * Response schema for marketplace plugins (catalog entries)
 */
export type MarketplacePluginResponse = {
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Version
   */
  version: string;
  /**
   * Author
   */
  author: string;
  category: PluginCategory;
  /**
   * Image
   */
  image?: string | null;
  components: PluginComponents;
  source_type?: PluginSourceType;
  /**
   * Config Schema
   */
  config_schema?: {
    [key: string]: unknown;
  } | null;
  /**
   * Added
   */
  added?: boolean;
};

/**
 * MarketplacePluginSource
 *
 * Marketplace plugin source that downloads plugins from the official marketplace repository
 */
export type MarketplacePluginSource = {
  /**
   * Type
   */
  type?: "marketplace";
  /**
   * Name
   *
   * Plugin name in the marketplace
   */
  name: string;
  /**
   * Version
   *
   * Plugin version to download
   */
  version: string;
};

/**
 * MatchStrategy
 *
 * ``by_id`` is the safe default — it never name-matches, so a credential
 * can't silently bind to the wrong connection.
 */
export const MatchStrategy = {
  BY_ID: "by_id",
  ID_THEN_SLUG: "id_then_slug",
  BY_SLUG: "by_slug",
  CLONE: "clone",
} as const;

/**
 * MatchStrategy
 *
 * ``by_id`` is the safe default — it never name-matches, so a credential
 * can't silently bind to the wrong connection.
 */
export type MatchStrategy = (typeof MatchStrategy)[keyof typeof MatchStrategy];

/**
 * MemorySettings
 */
export type MemorySettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "memory";
};

/**
 * MemorySettings
 */
export type MemorySettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "memory";
};

/**
 * MergeTenant
 */
export type MergeTenant = {
  /**
   * Other
   */
  other: string;
};

/**
 * Message
 */
export type Message = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Pydantic Message Parts
   */
  pydantic_message_parts: Array<
    | ({
        kind: "request";
      } & ModelRequest)
    | ({
        kind: "response";
      } & ModelResponse)
  >;
  /**
   * Is Positive
   */
  is_positive: boolean | null;
  feedback_meta?: FeedbackMeta | null;
  sender_ref?: SenderRefSchema | null;
};

/**
 * MessageCostBreakdown
 *
 * Additive spend reconciles to total_cost:
 * models[] (direct model calls) + workflow/agent tool lines (exact sub-run
 * spend, cost_is_estimate=False) + embedding_cost == total_cost.
 *
 * models[] excludes sub-run model spend — that lives on the workflow/agent
 * tool lines so it totals as a separate item. The remaining tool lines
 * (cost_is_estimate=True) are marginal token estimates already inside the
 * direct model spend, shown for transparency and never added to the total.
 */
export type MessageCostBreakdown = {
  /**
   * Message Id
   */
  message_id: string;
  /**
   * Conversation Total Cost
   */
  conversation_total_cost: number;
  /**
   * Total Cost
   */
  total_cost: number;
  /**
   * Input Cost
   */
  input_cost: number;
  /**
   * Output Cost
   */
  output_cost: number;
  /**
   * Embedding Cost
   */
  embedding_cost: number;
  /**
   * Input Tokens
   */
  input_tokens: number;
  /**
   * Cached Input Tokens
   */
  cached_input_tokens: number;
  /**
   * Output Tokens
   */
  output_tokens: number;
  /**
   * Tool Calls
   */
  tool_calls: number;
  /**
   * Tools
   */
  tools: Array<ToolUsageLine>;
  /**
   * Models
   */
  models: Array<ModelCostLine>;
};

/**
 * MessageCostRow
 */
export type MessageCostRow = {
  /**
   * Message Id
   */
  message_id: string;
  /**
   * Preview
   */
  preview: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Cost
   */
  cost: number;
  /**
   * Llm Calls
   */
  llm_calls: number;
  /**
   * Tool Calls
   */
  tool_calls: number;
};

/**
 * MessageFeedback
 */
export type MessageFeedback = {
  /**
   * Feedback Type
   */
  feedback_type: string;
  /**
   * Comment
   */
  comment?: string | null;
};

/**
 * MessageResponse
 *
 * Standard response for operations that return a status message.
 */
export type MessageResponse = {
  /**
   * Message
   */
  message: string;
};

/**
 * MigrateGoogleResponse
 */
export type MigrateGoogleResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Created
   */
  created: number;
  /**
   * Skipped
   */
  skipped: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Message
   */
  message: string;
};

/**
 * MigrationConnections
 */
export type MigrationConnections = {
  /**
   * Configured
   */
  configured: boolean;
  /**
   * Oidc Connections
   */
  oidc_connections: Array<string>;
};

/**
 * MimeTypeInfo
 *
 * Information about a supported MIME type
 */
export type MimeTypeInfo = {
  /**
   * Type
   */
  type: string;
  /**
   * Extension
   */
  extension: string;
  /**
   * Image
   */
  image?: string | null;
};

/**
 * MissingDependency
 */
export const MissingDependency = { FAIL: "fail", WARN: "warn" } as const;

/**
 * MissingDependency
 */
export type MissingDependency =
  (typeof MissingDependency)[keyof typeof MissingDependency];

/**
 * MissingDependencyPolicy
 */
export const MissingDependencyPolicy = { FAIL: "fail", WARN: "warn" } as const;

/**
 * MissingDependencyPolicy
 */
export type MissingDependencyPolicy =
  (typeof MissingDependencyPolicy)[keyof typeof MissingDependencyPolicy];

/**
 * MistralProviderData
 */
export type MistralProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "mistral";
  /**
   * Mistral Api Key
   */
  MISTRAL_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * ModelCapability
 */
export const ModelCapability = {
  STREAMING: "streaming",
  FUNCTION_CALLING: "function_calling",
  PARALLEL_FUNCTION_CALLING: "parallel_function_calling",
  VISION: "vision",
  SYSTEM_PROMPT: "system_prompt",
  TOOLS: "tools",
  JSON_MODE: "json_mode",
  STRUCTURED_OUTPUT: "structured_output",
  REASONING: "reasoning",
  WEB_SEARCH: "web_search",
  AUDIO_IN: "audio_in",
  AUDIO_OUT: "audio_out",
  VIDEO_IN: "video_in",
  VIDEO_OUT: "video_out",
  IMAGE_GENERATION: "image_generation",
  DOCUMENT_INPUT: "document_input",
  SPREADSHEET_INPUT: "spreadsheet_input",
} as const;

/**
 * ModelCapability
 */
export type ModelCapability =
  (typeof ModelCapability)[keyof typeof ModelCapability];

/**
 * ModelCapabilityEnum
 *
 * PostgreSQL enum mirroring no_llm.core.models.enums.ModelCapability.
 */
export const ModelCapabilityEnum = {
  STREAMING: "streaming",
  FUNCTION_CALLING: "function_calling",
  PARALLEL_FUNCTION_CALLING: "parallel_function_calling",
  VISION: "vision",
  SYSTEM_PROMPT: "system_prompt",
  TOOLS: "tools",
  JSON_MODE: "json_mode",
  STRUCTURED_OUTPUT: "structured_output",
  REASONING: "reasoning",
  WEB_SEARCH: "web_search",
  AUDIO_IN: "audio_in",
  AUDIO_OUT: "audio_out",
  VIDEO_IN: "video_in",
  VIDEO_OUT: "video_out",
  IMAGE_GENERATION: "image_generation",
} as const;

/**
 * ModelCapabilityEnum
 *
 * PostgreSQL enum mirroring no_llm.core.models.enums.ModelCapability.
 */
export type ModelCapabilityEnum =
  (typeof ModelCapabilityEnum)[keyof typeof ModelCapabilityEnum];

/**
 * ModelConfigurationResponse
 *
 * List endpoint response — omits full details blob for performance.
 */
export type ModelConfigurationResponse = {
  /**
   * Model Id
   */
  model_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Creator
   */
  creator: string;
  /**
   * Description
   */
  description: string;
  /**
   * Speed Score
   */
  speed_score: number;
  /**
   * Quality Score
   */
  quality_score: number;
  /**
   * Max Input Tokens
   */
  max_input_tokens: number;
  /**
   * Max Output Tokens
   */
  max_output_tokens: number;
  /**
   * Capabilities
   */
  capabilities: Array<string>;
  /**
   * Compatible Providers
   */
  compatible_providers: Array<string>;
  /**
   * Is Deprecated
   */
  is_deprecated: boolean;
  /**
   * Retirement Date
   */
  retirement_date: string | null;
  /**
   * Replaced By
   */
  replaced_by: string | null;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Image
   */
  image: string | null;
  custom: CustomModelSummary | null;
  /**
   * Provider Links
   */
  provider_links: Array<ProviderLinkResponse>;
  /**
   * Reprice Queued
   */
  reprice_queued?: boolean;
};

/**
 * ModelCostLine
 */
export type ModelCostLine = {
  /**
   * Model
   */
  model: string;
  /**
   * Provider
   */
  provider?: string | null;
  /**
   * Purpose
   */
  purpose?: string | null;
  /**
   * Calls
   */
  calls: number;
  /**
   * Input Tokens
   */
  input_tokens: number;
  /**
   * Cached Input Tokens
   */
  cached_input_tokens: number;
  /**
   * Output Tokens
   */
  output_tokens: number;
  /**
   * Input Cost
   */
  input_cost: number;
  /**
   * Output Cost
   */
  output_cost: number;
  /**
   * Cost
   */
  cost: number;
};

/**
 * ModelDefinition
 */
export type ModelDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Slug
   */
  slug?: string | null;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Model Type
   */
  model_type: string;
  /**
   * Model Category
   */
  model_category?: string | null;
  /**
   * Properties
   */
  properties: LlmModelProperties | ModelProperties;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider
   */
  provider?: string | null;
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Provider Image
   */
  provider_image?: string | null;
  /**
   * Providers
   */
  providers?: Array<ProviderRef>;
  /**
   * Host
   */
  host?: string | null;
  /**
   * Location
   */
  location?: string | null;
  /**
   * Price
   */
  price?: string | null;
  /**
   * Release Date
   */
  release_date?: string | null;
  /**
   * Data Cutoff Date
   */
  data_cutoff_date?: string | null;
  /**
   * Retirement Date
   */
  retirement_date?: string | null;
  /**
   * Replaced By
   */
  replaced_by?: string | null;
  /**
   * Privacy Level
   */
  privacy_level?: string | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Is Available
   */
  is_available?: boolean;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Provider Is Active
   */
  provider_is_active?: boolean;
  /**
   * Has Link
   */
  has_link?: boolean;
  /**
   * Is Valid
   */
  is_valid?: boolean;
  /**
   * Is Healthy
   */
  is_healthy?: boolean;
  /**
   * Lifecycle State
   */
  lifecycle_state?: string;
  /**
   * Last Error
   */
  last_error?: string | null;
  /**
   * Consecutive Failures
   */
  consecutive_failures?: number;
  /**
   * Last Checked At
   */
  last_checked_at?: string | null;
};

/**
 * ModelProperties
 */
export type ModelProperties = {
  /**
   * Speed
   */
  speed: number;
  /**
   * Quality
   */
  quality: number;
};

/**
 * ModelRequest
 *
 * A request generated by Pydantic AI and sent to a model, e.g. a message from the Pydantic AI app to the model.
 */
export type ModelRequest = {
  /**
   * Parts
   */
  parts: Array<
    | SystemPromptPart
    | UserPromptPart
    | SpeechPart
    | ToolSearchReturnPart
    | LoadCapabilityReturnPart
    | ToolReturnPart
    | RetryPromptPart
    | ToolAvailabilityDeltaPart
  >;
  /**
   * Timestamp
   */
  timestamp?: string | null;
  /**
   * Instructions
   */
  instructions?: string | null;
  /**
   * Kind
   */
  kind?: "request";
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Metadata
   */
  metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * State
   */
  state?: "complete" | "interrupted";
};

/**
 * ModelRequestAttempt
 *
 * An attempt at a model request that did not produce the response it is recorded on.
 *
 * A [`FallbackModel`][pydantic_ai.models.fallback.FallbackModel] records one for each model it moved
 * on from, on the response of the model that answered
 * ([`ModelResponse.failed_attempts`][pydantic_ai.messages.ModelResponse.failed_attempts]), or on the
 * [`FallbackExceptionGroup`][pydantic_ai.exceptions.FallbackExceptionGroup] when every model failed.
 */
export type ModelRequestAttempt = {
  /**
   * Model Name
   */
  model_name: string;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Outcome
   */
  outcome: "error" | "rejected";
  /**
   * Error
   */
  error?: string | null;
  /**
   * Timestamp
   */
  timestamp: string;
  /**
   * Duration
   */
  duration: string;
  usage?: RequestUsage | null;
};

/**
 * ModelResponse
 *
 * A response from a model, e.g. a message from the model to the Pydantic AI app.
 */
export type ModelResponse = {
  /**
   * Parts
   */
  parts: Array<
    | TextPart
    | ToolSearchCallPart
    | LoadCapabilityCallPart
    | ToolCallPart
    | NativeToolSearchCallPart
    | NativeToolCallPart
    | NativeToolSearchReturnPart
    | NativeToolReturnPart
    | ThinkingPart
    | CompactionPart
    | FilePart
    | SpeechPart
  >;
  usage?: RequestUsage;
  /**
   * Model Name
   */
  model_name?: string | null;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Kind
   */
  kind?: "response";
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Url
   */
  provider_url?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider Response Id
   */
  provider_response_id?: string | null;
  /**
   * Finish Reason
   */
  finish_reason?:
    | "stop"
    | "length"
    | "content_filter"
    | "tool_call"
    | "error"
    | null;
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Metadata
   */
  metadata?: {
    [key: string]: unknown;
  } | null;
  workspace_ref?: WorkspaceRef | null;
  /**
   * Failed Attempts
   */
  failed_attempts?: Array<ModelRequestAttempt> | null;
  /**
   * State
   */
  state?: "complete" | "incomplete" | "suspended" | "interrupted";
};

/**
 * MoveBody
 */
export type MoveBody = {
  /**
   * Direction
   */
  direction?: "up" | "down" | null;
  /**
   * Parent
   */
  parent?: string | null;
  /**
   * Position
   */
  position?: number | null;
};

/**
 * NameConflict
 */
export type NameConflict = {
  /**
   * Node Type
   */
  node_type: string;
  /**
   * Name
   */
  name: string;
  /**
   * Node Ids
   */
  node_ids: Array<string>;
};

/**
 * NativeTeamsAppState
 */
export type NativeTeamsAppState = {
  /**
   * Id
   */
  id: string;
  status: NativeTeamsAppStatus;
  /**
   * Is Paused
   */
  is_paused: boolean;
  /**
   * Bound Tenant Id
   */
  bound_tenant_id: string;
  /**
   * Tenant Admin Credential Id
   */
  tenant_admin_credential_id: string;
  /**
   * Manifest App Id
   */
  manifest_app_id: string | null;
  /**
   * Provisioning Step
   */
  provisioning_step:
    | "application_retired"
    | "bot_credential"
    | "bot_credential_cleanup_pending"
    | "bot_credential_rotated"
    | "bot_endpoint"
    | "bot_resource"
    | "bot_retired"
    | "catalog_retired"
    | "entra_application"
    | "entra_sign_in_audience"
    | "metadata_republished"
    | "old_bot_credential_removed"
    | "package_publish"
    | "publishing_observed"
    | "retirement_completed"
    | "service_principal"
    | "teams_channel"
    | "update_approved"
    | null;
  /**
   * Observed Publishing State
   */
  observed_publishing_state: "submitted" | "published" | "rejected" | null;
  /**
   * Package Version
   */
  package_version: string | null;
  /**
   * Error Code
   */
  error_code: string | null;
  /**
   * Error Message
   */
  error_message: string | null;
  /**
   * Attempt Count
   */
  attempt_count: number;
  /**
   * Next Retry At
   */
  next_retry_at: string | null;
  /**
   * Last Reconciled At
   */
  last_reconciled_at: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Retryable
   */
  readonly retryable: boolean;
};

/**
 * NativeTeamsAppStatus
 */
export const NativeTeamsAppStatus = {
  PUBLISHING: "publishing",
  AWAITING_APPROVAL: "awaiting_approval",
  ACTIVE: "active",
  FAILED: "failed",
  RETIRING: "retiring",
  RETIREMENT_FAILED: "retirement_failed",
  RETIRED: "retired",
} as const;

/**
 * NativeTeamsAppStatus
 */
export type NativeTeamsAppStatus =
  (typeof NativeTeamsAppStatus)[keyof typeof NativeTeamsAppStatus];

/**
 * NativeToolCallPart
 *
 * A tool call to a native tool.
 *
 * For native tools with a stable cross-provider shape (currently `tool_search`), this base
 * class can be promoted to a typed subclass with a narrowed `args` `TypedDict`. See
 * [`NativeToolSearchCallPart`][pydantic_ai.messages.NativeToolSearchCallPart] for the
 * canonical example.
 *
 * Adding a typed subclass for a future native tool (see `pydantic_ai._tool_search` for
 * a worked example):
 *
 * 1. Add a sibling `pydantic_ai/_<name>.py` module that defines the cross-provider
 * `TypedDict`s, the `NativeToolCallPart` / `NativeToolReturnPart` subclasses,
 * and registers their narrowers into `_NATIVE_CALL_NARROWERS` /
 * `_NATIVE_RETURN_NARROWERS` keyed by `tool_kind`. Subclass overrides
 * `tool_kind: Literal['<emitter>']` to match the emitting
 * [`AbstractNativeTool.kind`][pydantic_ai.native_tools.AbstractNativeTool.kind],
 * and shadows `args` / `content` with a narrower type.
 * 2. Late-import the new module from this file (alongside the existing tool-search
 * import) so registration runs whenever `pydantic_ai.messages` is imported.
 * 3. Add the subclass to `ModelResponsePart`'s discriminated union and to
 * `_model_response_part_discriminator` so Pydantic deserialization auto-promotes
 * on `model_validate` / `model_validate_json`.
 *
 * Dispatch is by `tool_kind`, not `tool_name`. This protects users whose tools happen to
 * share a name with one of ours from accidentally getting their parts promoted (and
 * failing shape validation against the typed `args`/`content`).
 *
 * The `provider_details` field carries genuinely non-portable provider extras
 * (e.g. Anthropic's `strategy: 'bm25' | 'regex'` for tool search). Promote a field
 * to a typed slot in `args` / `content` only when at least two of OpenAI, Anthropic,
 * and Google support it (cf. [issue #3885](https://github.com/pydantic/pydantic-ai/issues/3885)).
 *
 * MCP server tools land here with `tool_kind='mcp_server'` (label stays in
 * `tool_name='mcp_server:<label>'`); typed-subclass work for MCP is tracked by
 * [issue #3561](https://github.com/pydantic/pydantic-ai/issues/3561).
 */
export type NativeToolCallPart = {
  /**
   * Tool Name
   */
  tool_name: string;
  /**
   * Args
   */
  args?:
    | string
    | {
        [key: string]: unknown;
      }
    | null;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search" | "capability-load" | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "builtin-tool-call";
};

/**
 * NativeToolReturnPart
 *
 * A tool return message from a native tool.
 *
 * For native tools with a stable cross-provider shape (currently `tool_search`), a
 * `NativeToolReturnPart` may be promoted to a typed subclass like
 * [`NativeToolSearchReturnPart`][pydantic_ai.messages.NativeToolSearchReturnPart]
 * with a narrowed `content` `TypedDict`. See `NativeToolCallPart` for the pattern.
 */
export type NativeToolReturnPart = {
  /**
   * Tool Name
   */
  tool_name: string;
  content: ToolReturnContent;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search" | "capability-load" | null;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "builtin-tool-return";
};

/**
 * NativeToolSearchCallPart
 *
 * Typed view of a [`NativeToolCallPart`][pydantic_ai.messages.NativeToolCallPart] for tool search.
 *
 * Used on the native server-side tool-search path (Anthropic BM25/regex, OpenAI
 * Responses) where the provider executes the search and emits a native result.
 * The local-fallback path uses
 * [`ToolSearchCallPart`][pydantic_ai.messages.ToolSearchCallPart] instead.
 *
 * To detect a tool-search part regardless of execution path (native server-side
 * vs. local fallback), check `part.tool_kind == 'tool-search'` — this works
 * across both call/return and both server/local variants.
 *
 * Shadows `args` with a narrower type. The `str` variant covers the
 * streaming / partial-args case before parsing completes; once parsed,
 * `args` is a [`ToolSearchArgs`][pydantic_ai.messages.ToolSearchArgs]
 * `TypedDict`.
 */
export type NativeToolSearchCallPart = {
  /**
   * Tool Name
   */
  tool_name?: "tool_search";
  /**
   * Args
   */
  args?: string | ToolSearchArgs | null;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search";
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "builtin-tool-call";
};

/**
 * NativeToolSearchReturnPart
 *
 * Typed view of a [`NativeToolReturnPart`][pydantic_ai.messages.NativeToolReturnPart] for tool search.
 *
 * Used on the native server-side tool-search path (Anthropic BM25/regex, OpenAI
 * Responses) where the provider executes the search and emits a native result.
 * The local-fallback path uses
 * [`ToolSearchReturnPart`][pydantic_ai.messages.ToolSearchReturnPart] instead.
 *
 * To detect a tool-search part regardless of execution path (native server-side
 * vs. local fallback), check `part.tool_kind == 'tool-search'` — this works
 * across both call/return and both server/local variants.
 *
 * Shadows `content` with a narrower
 * [`ToolSearchReturnContent`][pydantic_ai.messages.ToolSearchReturnContent]
 * `TypedDict`.
 */
export type NativeToolSearchReturnPart = {
  /**
   * Tool Name
   */
  tool_name?: "tool_search";
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search";
  content: ToolSearchReturnContent;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "builtin-tool-return";
};

/**
 * NeuralTrustProviderData
 *
 * An application on a NeuralTrust TrustGate LLM gateway.
 */
export type NeuralTrustProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "neuraltrust";
  /**
   * Neuraltrust Api Key
   */
  NEURALTRUST_API_KEY: string;
  /**
   * Gateway
   */
  gateway: string;
  /**
   * Application
   */
  application: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * NewCheck
 */
export type NewCheck = {
  /**
   * Type
   */
  type: string;
  /**
   * Name
   */
  name: string;
  /**
   * Config
   */
  config?: {
    [key: string]: JsonValueInput;
  };
  /**
   * Expected Output
   */
  expected_output?: {
    [key: string]: JsonValueInput;
  } | null;
};

/**
 * Node
 */
export type NodeInput = {
  /**
   * Type
   */
  type: string;
  /**
   * Attrs
   */
  attrs?: {
    [key: string]: unknown;
  } | null;
  /**
   * Content
   */
  content?: Array<NodeInput> | null;
  /**
   * Marks
   */
  marks?: Array<Mark> | null;
  /**
   * Text
   */
  text?: string | null;
};

/**
 * Node
 */
export type NodeOutput = {
  /**
   * Type
   */
  type: string;
  /**
   * Attrs
   */
  attrs?: {
    [key: string]: unknown;
  } | null;
  /**
   * Content
   */
  content?: Array<NodeOutput> | null;
  /**
   * Marks
   */
  marks?: Array<Mark> | null;
  /**
   * Text
   */
  text?: string | null;
};

/**
 * NodeCreate
 */
export type NodeCreate = {
  /**
   * Parent
   */
  parent?: string | null;
  /**
   * Kind
   */
  kind?: "module" | "feature" | "capability";
  /**
   * Title
   */
  title: string;
  /**
   * Status
   */
  status?: "live" | "coming-soon" | "needs-review" | "deprecated";
  /**
   * Gating
   */
  gating?: Array<string>;
  /**
   * Note
   */
  note?: string | null;
  /**
   * After
   */
  after?: string | null;
};

/**
 * NodeDefinition
 */
export type NodeDefinition = {
  /**
   * Inputs
   */
  inputs: Array<unknown>;
  /**
   * Outputs
   */
  outputs: Array<unknown>;
  /**
   * Details
   */
  details: Array<unknown>;
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
  /**
   * Type
   */
  type: string;
  /**
   * Color
   */
  color: string;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Small Description
   */
  small_description?: string | null;
  /**
   * Documentation Url
   */
  documentation_url?: string | null;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Sub Category
   */
  sub_category?: string | null;
  /**
   * Example
   */
  example?: string | null;
  /**
   * Ai Instructions
   */
  ai_instructions?: string | null;
  /**
   * Integrations
   */
  integrations?: Array<string | Array<string>> | null;
  /**
   * Providers
   */
  providers?: Array<string> | null;
  /**
   * Config Endpoint
   */
  config_endpoint?: string | null;
  subflow_config?: SubflowConfig | null;
  /**
   * Is Valid
   */
  is_valid?: boolean | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Superseded By
   */
  superseded_by?: string | null;
  /**
   * Is Tool
   */
  is_tool?: boolean;
  /**
   * Is Available
   */
  is_available?: boolean;
  /**
   * Show To User
   */
  show_to_user?: boolean;
  /**
   * Paywalled
   */
  paywalled?: boolean;
  /**
   * Plugin Id
   */
  plugin_id?: string | null;
  /**
   * Plugin Name
   */
  plugin_name?: string | null;
  /**
   * Supports Mocking
   */
  supports_mocking?: boolean;
  /**
   * Deletable
   */
  deletable?: boolean;
  /**
   * Unique
   */
  unique?: boolean;
  /**
   * Supports Error Handling
   */
  supports_error_handling?: boolean;
  /**
   * Supports Iteration
   */
  supports_iteration?: boolean;
  /**
   * No Repeated Names
   */
  no_repeated_names?: boolean;
  /**
   * Allowed In Flow Types
   */
  allowed_in_flow_types?: Array<string>;
  /**
   * Pinned Fields
   */
  pinned_fields?: Array<string>;
  /**
   * Picker Priority
   */
  picker_priority?: number;
  /**
   * Output Paths
   */
  output_paths?: Array<string>;
  /**
   * Input Paths
   */
  input_paths?: Array<string>;
  /**
   * V2 Ready
   */
  v2_ready?: boolean;
  /**
   * Accepts Inline Files
   */
  accepts_inline_files?: boolean;
  /**
   * Default Output Type
   */
  default_output_type?: [string, number] | null;
  /**
   * Gathers List
   */
  gathers_list?: boolean;
  /**
   * Gather Fields
   */
  gather_fields?: Array<string>;
  /**
   * Is Loop Scope
   */
  is_loop_scope?: boolean;
  /**
   * Scope Only Outputs
   */
  scope_only_outputs?: Array<string>;
  /**
   * Referenced Assets
   */
  referenced_assets?: Array<ReferencedAssetField>;
};

/**
 * NodeDisplay
 */
export type NodeDisplay = {
  /**
   * Type
   */
  type: string;
  /**
   * Title
   */
  title: string | null;
  /**
   * Category
   */
  category: string | null;
  /**
   * Image
   */
  image: string | null;
};

/**
 * NodeOutputV2
 *
 * A V2 node output — a named, typed value the node produces (a bindable
 * variable). No connector vocabulary; V2 has no connectors.
 */
export type NodeOutputV2 = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Data Type
   */
  data_type?: string;
  /**
   * Object Fields
   */
  object_fields?: Array<ObjectFieldSpec> | null;
  /**
   * Is List
   */
  is_list?: boolean;
  /**
   * List Depth
   */
  list_depth?: number;
};

/**
 * NodePatch
 */
export type NodePatch = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Status
   */
  status?: "live" | "coming-soon" | "needs-review" | "deprecated" | null;
  /**
   * Gating
   */
  gating?: Array<string> | null;
  /**
   * Note
   */
  note?: string | null;
  /**
   * Clear Note
   */
  clear_note?: boolean;
};

/**
 * NodePreview
 */
export type NodePreview = {
  /**
   * Node Config
   */
  node_config: {
    [key: string]: unknown;
  };
  /**
   * Connector Config
   */
  connector_config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Credential Ids
   */
  credential_ids?: {
    [key: string]: string;
  };
  /**
   * Subflow Id
   */
  subflow_id?: string | null;
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Type
   */
  type: string;
  subflow_config?: SubflowConfig | null;
  /**
   * Plugin Name
   */
  plugin_name?: string | null;
};

/**
 * NodeProgress
 */
export type NodeProgress = {
  /**
   * Current It
   */
  current_it: number;
  /**
   * Total It
   */
  total_it: number;
  status: NodeStatus;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Warnings
   */
  warnings?: Array<SpotWarning>;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * NodeStatus
 */
export const NodeStatus = {
  RUNNING: "running",
  ERRORED: "errored",
  SUCCEEDED: "succeeded",
  SKIPPED: "skipped",
  PAYMENT_REQUIRED: "payment_required",
  AWAITING_HUMAN_FEEDBACK: "awaiting_human_feedback",
  PAUSED: "paused",
} as const;

/**
 * NodeStatus
 */
export type NodeStatus = (typeof NodeStatus)[keyof typeof NodeStatus];

/**
 * NodeSummary
 */
export type NodeSummary = {
  /**
   * Name
   */
  name: string;
  /**
   * Title
   */
  title: string;
  /**
   * Color
   */
  color: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Small Description
   */
  small_description?: string | null;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Category
   */
  category?: string | null;
  /**
   * Sub Category
   */
  sub_category?: string | null;
  /**
   * Documentation Url
   */
  documentation_url?: string | null;
};

/**
 * NodeValidation
 */
export type NodeValidation = {
  /**
   * Node Id
   */
  node_id: string;
  /**
   * Field Errors
   */
  field_errors?: {
    [key: string]: string;
  };
  /**
   * Node Errors
   */
  node_errors?: Array<string>;
  /**
   * Field Warnings
   */
  field_warnings?: {
    [key: string]: string;
  };
  /**
   * Node Warnings
   */
  node_warnings?: Array<string>;
};

/**
 * NoticedFeed
 */
export type NoticedFeed = {
  /**
   * Items
   */
  items: Array<NoticedItem>;
};

/**
 * NoticedItem
 */
export type NoticedItem = {
  /**
   * Sub Topic
   */
  sub_topic: string;
  /**
   * Parent Topic
   */
  parent_topic: string;
  /**
   * Badge
   */
  badge: "emerging" | "rising" | "needs_attention";
  /**
   * Stat
   */
  stat: string;
  /**
   * Blurb
   */
  blurb: string;
  /**
   * Share
   */
  share: number;
  /**
   * Conversation Id
   */
  conversation_id: string;
};

/**
 * NoxusProviderConfigResponse
 */
export type NoxusProviderConfigResponse = {
  /**
   * Tenant Id
   */
  tenant_id: string | null;
  /**
   * Tenant Name
   */
  tenant_name: string | null;
};

/**
 * NoxusProviderConfigUpdate
 */
export type NoxusProviderConfigUpdate = {
  /**
   * Tenant Id
   */
  tenant_id: string | null;
};

/**
 * NoxusProviderData
 *
 * Marker provider type that delegates to the Noxus platform tenant.
 *
 * Carries no credentials — when a client tenant adds this provider and
 * activates it, ``ModelService`` falls back to the active model-provider
 * links in the configured ``noxus_tenant_id`` tenant (see
 * ``spotflow/services/platform_config.py``). The actual dispatch uses
 * whichever real OpenAI / Anthropic / Vertex provider the Noxus tenant
 * has linked for the requested model.
 *
 * SaaS-only. Not exposed on on-prem deployments (gated via the platform
 * setting).
 */
export type NoxusProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "noxus";
};

/**
 * NoxusQaSettings
 */
export type NoxusQaSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "noxus_qa";
};

/**
 * NoxusQaSettings
 */
export type NoxusQaSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "noxus_qa";
};

/**
 * NoxusRepairResponse
 */
export type NoxusRepairResponse = {
  /**
   * Links Reactivated
   */
  links_reactivated: number;
  /**
   * Tenants Affected
   */
  tenants_affected: number;
  /**
   * Health Check Job Id
   */
  health_check_job_id: number | null;
};

/**
 * NoxusTipTapDocument
 */
export type NoxusTipTapDocumentInput = {
  /**
   * Text
   */
  text: string;
  definition?: TiptapDocumentInput;
};

/**
 * NoxusTipTapDocument
 */
export type NoxusTipTapDocumentOutput = {
  /**
   * Text
   */
  text: string;
  definition?: TiptapDocumentOutput;
};

/**
 * NoxusValidationMessage
 */
export type NoxusValidationMessage = {
  /**
   * Detail
   */
  detail: string;
};

/**
 * NoxusVercelApprovalSupersededMessage
 *
 * The approval this tab answered no longer waits: another tab or a new
 * message settled it. The tab reloads the conversation instead of the model
 * running again. Not transient: only a part written into the message moves
 * the answered card out of its last step, which stops the SDK sending the
 * same answer again.
 */
export type NoxusVercelApprovalSupersededMessage = {
  /**
   * Type
   */
  type?: "data-approval_superseded";
};

/**
 * NoxusVercelCSATRequestMessage
 *
 * Asks the chat UI to render the CSAT feedback form.
 *
 * Pushed straight onto the stream outside a run, so it never rides a message
 * and nothing can rebuild it on reload.
 */
export type NoxusVercelCsatRequestMessage = {
  /**
   * Type
   */
  type?: "data-csat_request";
};

/**
 * NoxusVercelClearConversationMessage
 */
export type NoxusVercelClearConversationMessage = {
  /**
   * Type
   */
  type?: "data-clear_conversation";
};

/**
 * NoxusVercelCompactionMessage
 */
export type NoxusVercelCompactionMessage = {
  /**
   * Type
   */
  type?: "data-compaction";
  /**
   * Status
   */
  status: "completed" | "failed";
};

/**
 * NoxusVercelConversationLockedMessage
 *
 * Emitted once a conversation crosses ``block_after_violations``.
 *
 * The frontend disables the prompt input and renders ``message`` as a
 * lock notice. Re-emitted on every subsequent turn of an already-locked
 * conversation so a refresh/reconnect re-locks the UI without needing
 * to re-fetch the GET endpoint.
 */
export type NoxusVercelConversationLockedMessage = {
  /**
   * Type
   */
  type?: "data-conversation_locked";
  /**
   * Message
   */
  message: string;
  /**
   * Violation Count
   */
  violation_count: number;
  /**
   * Threshold
   */
  threshold: number;
};

/**
 * NoxusVercelErrorMessage
 */
export type NoxusVercelErrorMessage = {
  /**
   * Type
   */
  type?: "data-error";
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Code
   */
  code?: string | null;
  /**
   * Params
   */
  params?: {
    [key: string]: PydanticTypesJsonValue;
  } | null;
};

/**
 * NoxusVercelFileAttachmentMessage
 */
export type NoxusVercelFileAttachmentMessage = {
  /**
   * Type
   */
  type?: "data-file_attachment";
  /**
   * File Id
   */
  file_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Content Type
   */
  content_type: string;
  /**
   * Url
   */
  url: string;
  /**
   * Size
   */
  size?: number | null;
  /**
   * Caption
   */
  caption?: string | null;
  /**
   * Show Inline
   */
  show_inline?: boolean;
  /**
   * Source Image
   */
  source_image?: boolean;
};

/**
 * NoxusVercelFinalAnswerReplayMessage
 *
 * The run ended without a ``<final_answer>`` marker, so the whole reply
 * streamed as folded narration. Replay it as the answer and drop it from the
 * trailing reasoning part, so the same words don't sit in the body and the
 * chain of thought at once.
 */
export type NoxusVercelFinalAnswerReplayMessage = {
  /**
   * Type
   */
  type?: "data-final_answer_replay";
  /**
   * Text
   */
  text: string;
};

/**
 * NoxusVercelGuardrailWarningMessage
 */
export type NoxusVercelGuardrailWarningMessage = {
  /**
   * Type
   */
  type?: "data-guardrail_warning";
  /**
   * Message
   */
  message: string;
  /**
   * Severity
   */
  severity?: "low" | "medium" | "high";
};

/**
 * NoxusVercelHistoryCompactedMessage
 *
 * The turn compacted the history, so the chat's newest page now starts at
 * it. Sent on every compaction; ``data-compaction`` only marks the first.
 */
export type NoxusVercelHistoryCompactedMessage = {
  /**
   * Type
   */
  type?: "data-history_compacted";
};

/**
 * NoxusVercelHumanInTheLoopMessage
 */
export type NoxusVercelHumanInTheLoopMessage = {
  /**
   * Type
   */
  type?: "data-human_in_the_loop";
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Reason
   */
  reason: string;
  /**
   * Priority
   */
  priority: "low" | "medium" | "high" | "critical";
};

/**
 * NoxusVercelInfoMessage
 */
export type NoxusVercelInfoMessage = {
  /**
   * Type
   */
  type?: "data-info";
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
};

/**
 * NoxusVercelSetConversationTitleMessage
 */
export type NoxusVercelSetConversationTitleMessage = {
  /**
   * Type
   */
  type?: "data-set_conversation_title";
  /**
   * Title
   */
  title: string;
};

/**
 * NoxusVercelSuccessMessage
 */
export type NoxusVercelSuccessMessage = {
  /**
   * Type
   */
  type?: "data-success";
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
};

/**
 * NoxusVercelWarningMessage
 */
export type NoxusVercelWarningMessage = {
  /**
   * Type
   */
  type?: "data-warning";
  /**
   * Message
   */
  message: string;
  /**
   * Title
   */
  title?: string | null;
};

/**
 * NumberContainer
 */
export type NumberContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "number";
  /**
   * Value
   */
  value: unknown;
};

/**
 * OAuthStartRequest
 */
export type OAuthStartRequest = {
  /**
   * Integration Type
   */
  integration_type: string;
  /**
   * Provider Key
   */
  provider_key: string;
  /**
   * Redirect Uri
   */
  redirect_uri: string;
  /**
   * Allowed Scope Keys
   */
  allowed_scope_keys?: Array<string>;
  /**
   * Credential Id
   */
  credential_id?: string | null;
  /**
   * Connection Config
   */
  connection_config?: {
    [key: string]: JsonValueInput;
  } | null;
};

/**
 * OAuthStartResponse
 */
export type OAuthStartResponse = {
  /**
   * Authorize Url
   */
  authorize_url: string;
  /**
   * State
   */
  state: string;
};

/**
 * ObjectContainer
 */
export type ObjectContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "object";
  /**
   * Value
   */
  value: unknown;
};

/**
 * ObjectFieldSpec
 *
 * One field of a typed object output (of each element, for lists).
 * Display-only — never affects compatibility. Object values are JSON values;
 * producers must not leak driver types (see `json_safe_rows`).
 */
export type ObjectFieldSpec = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label?: string | null;
  /**
   * Data Type
   */
  data_type?: string;
  /**
   * Is List
   */
  is_list?: boolean;
};

/**
 * OidcSyncStatusResponse
 */
export type OidcSyncStatusResponse = {
  /**
   * Status
   */
  status: string;
  /**
   * Connection Name
   */
  connection_name: string;
  /**
   * Connection Id
   */
  connection_id?: string | null;
  /**
   * Detail
   */
  detail?: string | null;
};

/**
 * OpenAIProviderData
 */
export type OpenAiProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "openai";
  /**
   * Openai Api Key
   */
  OPENAI_API_KEY: string;
  /**
   * Base Url
   */
  base_url?: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * OpenRouterProviderData
 */
export type OpenRouterProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "openrouter";
  /**
   * Openrouter Api Key
   */
  OPENROUTER_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * OpenTelemetryProviderData
 *
 * Generic OTLP provider, HTTP or gRPC, for any standard OTel collector.
 */
export type OpenTelemetryProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Endpoint
   */
  endpoint?: string;
  /**
   * Protocol
   */
  protocol?: "http/protobuf" | "grpc";
  /**
   * Project Name
   */
  project_name?: string;
  /**
   * Type
   */
  type?: "opentelemetry";
  /**
   * Api Key
   */
  api_key?: string;
  connect_button?: ApiToggleState;
};

/**
 * OperationsLimit
 */
export type OperationsLimit = {
  /**
   * Metric
   */
  metric: "operations";
  /**
   * Id
   */
  id: string | null;
  /**
   * Group Id
   */
  group_id: string | null;
  group: Group | null;
  /**
   * Usage
   */
  usage: number;
  /**
   * Reserved
   *
   * Held by work still running
   */
  reserved?: number;
  /**
   * Limit
   */
  limit: number | null;
  /**
   * Warnings
   */
  warnings: Array<number>;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Emails
   */
  emails: Array<string>;
};

/**
 * OverflowSettings
 *
 * Where an oversized tool output is stored, owned by the quick action.
 *
 * It lives here rather than on ``AgentSettings`` so the capability that
 * reads it carries it: a quick action is only present on agents that
 * added it, so an agent without overflow is never shown knobs for it, and no
 * settings version has to exist to say so.
 */
export type OverflowSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "overflow";
  /**
   * Storage Backend
   *
   * Where a stored tool output lives until it is read back.
   */
  storage_backend?: "blob" | "redis";
};

/**
 * OverflowSettings
 *
 * Where an oversized tool output is stored, owned by the quick action.
 *
 * It lives here rather than on ``AgentSettings`` so the capability that
 * reads it carries it: a quick action is only present on agents that
 * added it, so an agent without overflow is never shown knobs for it, and no
 * settings version has to exist to say so.
 */
export type OverflowSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "overflow";
  /**
   * Storage Backend
   *
   * Where a stored tool output lives until it is read back.
   */
  storage_backend?: "blob" | "redis";
};

/**
 * OwnedFile
 */
export type OwnedFile = {
  /**
   * Id
   */
  id: string;
  /**
   * Uri
   */
  uri: string;
  /**
   * Size
   */
  size: number;
  /**
   * Group Id
   */
  group_id?: string | null;
  /**
   * Filename
   */
  filename: string;
  /**
   * Content Type
   */
  content_type: string;
  source_type: SourceType;
  /**
   * Source Metadata
   */
  source_metadata:
    | SourceMetadata
    | {
        [key: string]: unknown;
      }
    | null;
  /**
   * Created At
   */
  created_at?: string | null;
};

/**
 * PageBox
 */
export type PageBox = {
  /**
   * Left
   */
  left: number;
  /**
   * Top
   */
  top: number;
  /**
   * Right
   */
  right: number;
  /**
   * Bottom
   */
  bottom: number;
};

/**
 * Page[ApiKey]
 */
export type PageApiKey = {
  /**
   * Items
   */
  items: Array<ApiKey>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[App]
 */
export type PageApp = {
  /**
   * Items
   */
  items: Array<App>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[AssistantVersion]
 */
export type PageAssistantVersion = {
  /**
   * Items
   */
  items: Array<AssistantVersion>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[AssistantWithTags]
 */
export type PageAssistantWithTags = {
  /**
   * Items
   */
  items: Array<AssistantWithTags>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[AuditLogEntry]
 */
export type PageAuditLogEntry = {
  /**
   * Items
   */
  items: Array<AuditLogEntry>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[CaseListResponse]
 */
export type PageCaseListResponse = {
  /**
   * Items
   */
  items: Array<CaseListResponse>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[Conversation]
 */
export type PageConversation = {
  /**
   * Items
   */
  items: Array<Conversation>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[DeploymentEvent]
 */
export type PageDeploymentEvent = {
  /**
   * Items
   */
  items: Array<DeploymentEvent>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[EvaluatorResultResponse]
 */
export type PageEvaluatorResultResponse = {
  /**
   * Items
   */
  items: Array<EvaluatorResultResponse>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[GroupTriggerEvent]
 */
export type PageGroupTriggerEvent = {
  /**
   * Items
   */
  items: Array<GroupTriggerEvent>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[GroupWithUsers]
 */
export type PageGroupWithUsers = {
  /**
   * Items
   */
  items: Array<GroupWithUsers>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[InboxListResponse]
 */
export type PageInboxListResponse = {
  /**
   * Items
   */
  items: Array<InboxListResponse>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[InstalledPluginResponse]
 */
export type PageInstalledPluginResponse = {
  /**
   * Items
   */
  items: Array<InstalledPluginResponse>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[KnowledgeBaseDocument]
 */
export type PageKnowledgeBaseDocument = {
  /**
   * Items
   */
  items: Array<KnowledgeBaseDocument>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[KnowledgeBaseV2WithTags]
 */
export type PageKnowledgeBaseV2WithTags = {
  /**
   * Items
   */
  items: Array<KnowledgeBaseV2WithTags>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[LabellingTask]
 */
export type PageLabellingTask = {
  /**
   * Items
   */
  items: Array<LabellingTask>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[RunIngestion]
 */
export type PageRunIngestion = {
  /**
   * Items
   */
  items: Array<RunIngestion>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[RunNoIO]
 */
export type PageRunNoIo = {
  /**
   * Items
   */
  items: Array<RunNoIo>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[RunWithSearchData]
 */
export type PageRunWithSearchData = {
  /**
   * Items
   */
  items: Array<RunWithSearchData>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[TaskListResponse]
 */
export type PageTaskListResponse = {
  /**
   * Items
   */
  items: Array<TaskListResponse>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[Template]
 */
export type PageTemplate = {
  /**
   * Items
   */
  items: Array<Template>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[TenantWithUsers]
 */
export type PageTenantWithUsers = {
  /**
   * Items
   */
  items: Array<TenantWithUsers>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[TestCaseWEvals]
 */
export type PageTestCaseWEvals = {
  /**
   * Items
   */
  items: Array<TestCaseWEvals>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[TriggerEvent]
 */
export type PageTriggerEvent = {
  /**
   * Items
   */
  items: Array<TriggerEvent>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[Trigger]
 */
export type PageTrigger = {
  /**
   * Items
   */
  items: Array<Trigger>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[UserWithGroupRole]
 */
export type PageUserWithGroupRole = {
  /**
   * Items
   */
  items: Array<UserWithGroupRole>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[UserWithGroup]
 */
export type PageUserWithGroup = {
  /**
   * Items
   */
  items: Array<UserWithGroup>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[User]
 */
export type PageUser = {
  /**
   * Items
   */
  items: Array<User>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[WorkflowVersion]
 */
export type PageWorkflowVersion = {
  /**
   * Items
   */
  items: Array<WorkflowVersion>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * Page[WorkflowWithTriggersAndTags]
 */
export type PageWorkflowWithTriggersAndTags = {
  /**
   * Items
   */
  items: Array<WorkflowWithTriggersAndTags>;
  /**
   * Total
   */
  total: number;
  /**
   * Page
   */
  page: number;
  /**
   * Size
   */
  size: number;
  /**
   * Pages
   */
  pages: number;
};

/**
 * PaginatedDocInformation
 */
export type PaginatedDocInformation = {
  /**
   * Items
   */
  items: Array<DocInformation>;
  /**
   * Next Page Token
   */
  next_page_token?: string | null;
};

/**
 * PayGSettings
 *
 * Pay-as-you-go settings.
 */
export type PayGSettings = {
  /**
   * Enabled
   *
   * Whether PayG is enabled
   */
  enabled: boolean;
  /**
   * Package
   *
   * Selected PayG package
   */
  package?: number | null;
  /**
   * Max Spend In Cycle
   *
   * Maximum spend allowed in current billing cycle
   */
  max_spend_in_cycle?: number | null;
};

/**
 * Payment
 */
export type Payment = {
  /**
   * Id
   */
  id: string;
  /**
   * Amount
   */
  amount: number;
  /**
   * Currency
   */
  currency: string;
  /**
   * Status
   */
  status:
    | "canceled"
    | "processing"
    | "requires_action"
    | "requires_capture"
    | "requires_confirmation"
    | "requires_payment_method"
    | "succeeded";
  /**
   * Created At
   */
  created_at: string;
};

/**
 * PdfMetadata
 *
 * Structural metadata for a PDF file.
 *
 * ``page_labels`` is only set when the document's labels differ from plain
 * 1..N numbering (cover pages, roman numerals), mapping physical page index
 * to label.
 */
export type PdfMetadata = {
  /**
   * Kind
   */
  kind?: "pdf";
  /**
   * Page Count
   */
  page_count: number;
  /**
   * Page Labels
   */
  page_labels?: Array<string> | null;
};

/**
 * PendingInvite
 */
export type PendingInvite = {
  tenant: Tenant;
  user: User;
  /**
   * Groups
   */
  groups: Array<GroupWithRole>;
};

/**
 * PeriodicTaskResponse
 */
export type PeriodicTaskResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Message
   */
  message: string;
  /**
   * Frequency
   */
  frequency: string;
  /**
   * Cron
   */
  cron: string;
  /**
   * New Conversation
   */
  new_conversation: boolean;
  /**
   * One Shot
   */
  one_shot: boolean;
  /**
   * Time Of Day
   */
  time_of_day?: string | null;
  /**
   * Day Of Week
   */
  day_of_week?: string | null;
  /**
   * Day Of Month
   */
  day_of_month?: number | null;
  /**
   * Offset
   */
  offset?: number | null;
  /**
   * Created At
   */
  created_at?: string | null;
};

/**
 * PerplexityProviderData
 */
export type PerplexityProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "perplexity";
  /**
   * Perplexity Api Key
   */
  PERPLEXITY_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * PhoenixProviderData
 *
 * Arize Phoenix — OTel-compatible, self-hosted or Arize Cloud.
 */
export type PhoenixProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Endpoint
   */
  endpoint?: string;
  /**
   * Protocol
   */
  protocol?: "http/protobuf" | "grpc";
  /**
   * Project Name
   */
  project_name?: string;
  /**
   * Type
   */
  type?: "phoenix";
  /**
   * Api Key
   */
  api_key?: string;
  connect_button?: ApiToggleState;
};

/**
 * PickEvent
 *
 * Reply to a ``pick_at`` command — selector and bounding box for
 * the element under the given viewport point.
 */
export type PickEvent = {
  /**
   * Type
   */
  type: "pick";
  /**
   * Request Id
   */
  request_id: string;
  /**
   * Selector
   */
  selector: {
    [key: string]: unknown;
  } | null;
  bbox: BBoxRect | null;
};

/**
 * Plan
 */
export const Plan = {
  PROFESSIONAL: "professional",
  STARTER: "starter",
  PAY_AS_YOU_GO: "pay-as-you-go",
  NONE: "none",
} as const;

/**
 * Plan
 */
export type Plan = (typeof Plan)[keyof typeof Plan];

/**
 * PlatformStatus
 */
export type PlatformStatus = {
  /**
   * Timestamp
   */
  timestamp: string;
  overall: CheckStatus;
  /**
   * Checks
   */
  checks: {
    [key: string]: ServiceCheck;
  };
  workers?: WorkerPoolSummary | null;
  relay?: RelayStatus | null;
};

/**
 * PluginCatalogEntry
 *
 * A running plugin as any workspace member sees it: its identity and the
 * components it contributes, without install state, source or config.
 */
export type PluginCatalogEntry = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  category: PluginCategory;
  /**
   * Image
   */
  image?: string | null;
  components: PluginComponents;
};

/**
 * PluginCategory
 */
export const PluginCategory = {
  GENERAL: "general",
  DOCUMENT: "document",
  OTHER: "other",
} as const;

/**
 * PluginCategory
 */
export type PluginCategory =
  (typeof PluginCategory)[keyof typeof PluginCategory];

/**
 * PluginComponents
 */
export type PluginComponents = {
  /**
   * Nodes
   */
  nodes: Array<NodeSummary>;
  /**
   * Nodes V2
   */
  nodes_v2?: Array<NodeSummary>;
  /**
   * Integrations
   */
  integrations: Array<IntegrationSummary>;
  /**
   * Triggers
   */
  triggers?: Array<TriggerSummary>;
  /**
   * Datasources
   */
  datasources?: Array<DatasourceSummary>;
};

/**
 * PluginConfigRequest
 */
export type PluginConfigRequest = {
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
};

/**
 * PluginCreateRequest
 */
export type PluginCreateRequest = {
  /**
   * Source
   */
  source:
    | ({
        type: "git";
      } & GitPluginSource)
    | ({
        type: "local";
      } & LocalPluginSource)
    | ({
        type: "upload";
      } & UploadPluginSource)
    | ({
        type: "marketplace";
      } & MarketplacePluginSource);
  /**
   * On Conflict
   */
  on_conflict?: "error" | "replace" | "separate";
};

/**
 * PluginSourceType
 */
export const PluginSourceType = {
  GIT: "git",
  LOCAL: "local",
  UPLOAD: "upload",
  MARKETPLACE: "marketplace",
} as const;

/**
 * PluginSourceType
 */
export type PluginSourceType =
  (typeof PluginSourceType)[keyof typeof PluginSourceType];

/**
 * PluginStatus
 */
export const PluginStatus = {
  INSTALLING: "installing",
  MISSING_CONFIG: "missing_config",
  RUNNING: "running",
  RESTARTING: "restarting",
  UNINSTALLING: "uninstalling",
  UNINSTALLED: "uninstalled",
  ERROR: "error",
} as const;

/**
 * PluginStatus
 */
export type PluginStatus = (typeof PluginStatus)[keyof typeof PluginStatus];

/**
 * PongEvent
 *
 * Keepalive — not emitted in practice today but reserved so the
 * discriminator stays stable if we add one.
 */
export type PongEvent = {
  /**
   * Type
   */
  type: "pong";
};

/**
 * PresentationMetadata
 *
 * Structural metadata for a presentation (PowerPoint) file.
 */
export type PresentationMetadata = {
  /**
   * Kind
   */
  kind?: "presentation";
  /**
   * Slide Count
   */
  slide_count: number;
};

/**
 * PresetCreateRequest
 */
export type PresetCreateRequest = {
  /**
   * Handle
   */
  handle: string;
  /**
   * Title
   */
  title: string;
  /**
   * Subtitle
   */
  subtitle?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Models
   */
  models: Array<string>;
  /**
   * Required Capabilities
   */
  required_capabilities?: Array<ModelCapabilityEnum>;
  /**
   * Exhaustive
   */
  exhaustive?: boolean;
  /**
   * Data Center Fallback
   */
  data_center_fallback?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Sort Order
   */
  sort_order?: number;
};

/**
 * PresetResponse
 */
export type PresetResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Handle
   */
  handle: string;
  /**
   * Title
   */
  title: string;
  /**
   * Subtitle
   */
  subtitle: string;
  /**
   * Description
   */
  description: string;
  /**
   * Icon
   */
  icon: string;
  /**
   * Models
   */
  models: Array<string>;
  /**
   * Required Capabilities
   */
  required_capabilities: Array<ModelCapabilityEnum>;
  /**
   * Exhaustive
   */
  exhaustive: boolean;
  /**
   * Data Center Fallback
   */
  data_center_fallback: boolean;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Deprecated
   */
  is_deprecated: boolean;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Sort Order
   */
  sort_order: number;
  /**
   * Tenant Id
   */
  tenant_id: string;
  /**
   * Preset Type
   */
  preset_type: "custom" | "default" | "internal" | "deprecated";
  /**
   * Is Modified
   */
  is_modified: boolean;
};

/**
 * PresetUpdateRequest
 */
export type PresetUpdateRequest = {
  /**
   * Title
   */
  title?: string | null;
  /**
   * Subtitle
   */
  subtitle?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Models
   */
  models?: Array<string> | null;
  /**
   * Required Capabilities
   */
  required_capabilities?: Array<ModelCapabilityEnum> | null;
  /**
   * Exhaustive
   */
  exhaustive?: boolean | null;
  /**
   * Data Center Fallback
   */
  data_center_fallback?: boolean | null;
  /**
   * Visible
   */
  visible?: boolean | null;
  /**
   * Sort Order
   */
  sort_order?: number | null;
  /**
   * Is Active
   */
  is_active?: boolean | null;
};

/**
 * PriceReferenceResponse
 *
 * A published-price entry a custom model can be billed against.
 *
 * ``(provider_id, model_ref)`` together are the reference: the same model id
 * is listed by several providers at different rates. Prices are USD per
 * million tokens — the unit price pages use — not the per-1k the runtime
 * stores.
 */
export type PriceReferenceResponse = {
  /**
   * Model Ref
   */
  model_ref: string;
  /**
   * Name
   */
  name: string;
  /**
   * Provider Id
   */
  provider_id: string;
  /**
   * Input Price Per Mtok
   */
  input_price_per_mtok: number;
  /**
   * Output Price Per Mtok
   */
  output_price_per_mtok: number;
  /**
   * Context Window
   */
  context_window: number | null;
  /**
   * Deprecated
   */
  deprecated: boolean;
  /**
   * Tiered
   */
  tiered?: boolean;
};

/**
 * ProviderCategory
 */
export const ProviderCategory = {
  MODEL: "model",
  SCRAPING: "scraping",
  SEARCH: "search",
  OTHER: "other",
  OBSERVABILITY: "observability",
  INTEGRATION: "integration",
} as const;

/**
 * ProviderCategory
 */
export type ProviderCategory =
  (typeof ProviderCategory)[keyof typeof ProviderCategory];

/**
 * ProviderCostLimit
 */
export type ProviderCostLimit = {
  /**
   * Metric
   */
  metric: "provider";
  /**
   * Id
   */
  id: string | null;
  provider?: ProviderCostPreview | null;
  /**
   * Group Id
   */
  group_id: string | null;
  group: Group | null;
  /**
   * Usage
   */
  usage: number;
  /**
   * Reserved
   *
   * Held by work still running
   */
  reserved?: number;
  /**
   * Limit
   */
  limit: number | null;
  /**
   * Warnings
   */
  warnings: Array<number>;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Emails
   */
  emails: Array<string>;
};

/**
 * ProviderCostPreview
 */
export type ProviderCostPreview = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Logo
   */
  logo: string | null;
  /**
   * Provider Type
   */
  provider_type?: string | null;
};

/**
 * ProviderCreateRequest
 */
export type ProviderCreateRequest = {
  /**
   * Data
   */
  data:
    | ({
        type: "test";
      } & ChaosProviderData)
    | ({
        type: "vertex";
      } & VertexProviderData)
    | ({
        type: "openai";
      } & OpenAiProviderData)
    | ({
        type: "anthropic";
      } & AnthropicProviderData)
    | ({
        type: "azure";
      } & AzureProviderData)
    | ({
        type: "azure-foundry";
      } & AzureFoundryProviderData)
    | ({
        type: "bedrock";
      } & BedrockProviderData)
    | ({
        type: "deepseek";
      } & DeepSeekProviderData)
    | ({
        type: "elevenlabs";
      } & ElevenLabsProviderData)
    | ({
        type: "fireworks";
      } & FireworksProviderData)
    | ({
        type: "grok";
      } & GrokProviderData)
    | ({
        type: "groq";
      } & GroqProviderData)
    | ({
        type: "gemini";
      } & GeminiProviderData)
    | ({
        type: "litellm";
      } & LiteLlmProviderData)
    | ({
        type: "neuraltrust";
      } & NeuralTrustProviderData)
    | ({
        type: "mistral";
      } & MistralProviderData)
    | ({
        type: "noxus";
      } & NoxusProviderData)
    | ({
        type: "openrouter";
      } & OpenRouterProviderData)
    | ({
        type: "perplexity";
      } & PerplexityProviderData)
    | ({
        type: "together";
      } & TogetherProviderData)
    | ({
        type: "typesafe";
      } & TypeSafeProviderData)
    | ({
        type: "scrapingbee";
      } & ScrapingBeeProviderData)
    | ({
        type: "opentelemetry";
      } & OpenTelemetryProviderData)
    | ({
        type: "phoenix";
      } & PhoenixProviderData)
    | ({
        type: "langfuse";
      } & LangfuseProviderData)
    | ({
        type: "integration_provider";
      } & IntegrationProviderData);
  /**
   * Name
   */
  name?: string | null;
  /**
   * Slug
   */
  slug?: string | null;
};

/**
 * ProviderDefinitionPreview
 */
export type ProviderDefinitionPreview = {
  provider_category: ProviderCategory;
  /**
   * Provider Name
   */
  provider_name: string;
  /**
   * Provider Logo
   */
  provider_logo: string;
  /**
   * Provider Config
   */
  provider_config: {
    [key: string]: unknown;
  };
  /**
   * Disabled
   */
  disabled?: boolean;
  /**
   * Supports Custom Models
   */
  supports_custom_models?: boolean;
};

/**
 * ProviderHealthCheckStep
 *
 * One pipeline step in the provider-test response.
 *
 * Mirrors ``no_llm.core.providers.HealthCheckStepResult`` but lives on
 * the BE API surface so it gets a stable name in the OpenAPI schema
 * (the no-llm internal type was generic enough to slip past the SDK
 * generator). The FE imports this directly from the regenerated SDK
 * instead of hand-mirroring the shape in TypeScript.
 */
export type ProviderHealthCheckStep = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  /**
   * Status
   */
  status: "pass" | "fail" | "skip";
  /**
   * Message
   */
  message?: string | null;
  /**
   * Hint
   */
  hint?: string | null;
  /**
   * Latency Seconds
   */
  latency_seconds?: number;
  /**
   * Order
   */
  order?: number;
};

/**
 * ProviderInfo
 */
export type ProviderInfo = {
  /**
   * Key
   */
  key: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Image
   */
  image: string;
  /**
   * Auth Type
   */
  auth_type: string;
  /**
   * Available
   */
  available: boolean;
  /**
   * Ncs Managed
   */
  ncs_managed: boolean;
  /**
   * Connected
   */
  connected: boolean;
  /**
   * Status
   */
  status: string | null;
  /**
   * Credentials
   */
  credentials: Array<CredentialEntry>;
  /**
   * Config Url
   */
  config_url?: string | null;
  /**
   * Save Url
   */
  save_url?: string | null;
  /**
   * Device Auth Url
   */
  device_auth_url?: string | null;
  /**
   * Oauth Redirect Uri
   */
  oauth_redirect_uri?: string | null;
};

/**
 * ProviderLinkHealth
 */
export type ProviderLinkHealth = {
  /**
   * Total Links
   */
  total_links?: number;
  /**
   * Healthy Links
   */
  healthy_links?: number;
  /**
   * Suspended Links
   */
  suspended_links?: number;
  /**
   * Deactivated Links
   */
  deactivated_links?: number;
  /**
   * Last Checked At
   */
  last_checked_at?: string | null;
};

/**
 * ProviderLinkResponse
 */
export type ProviderLinkResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Provider Id
   */
  provider_id: string;
  /**
   * Provider Name
   */
  provider_name: string;
  /**
   * Provider Type
   */
  provider_type: string;
  /**
   * Priority
   */
  priority: number;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Last Checked At
   */
  last_checked_at: string | null;
  /**
   * Last Error
   */
  last_error: string | null;
  /**
   * Consecutive Failures
   */
  consecutive_failures: number;
  /**
   * Lifecycle State
   */
  lifecycle_state: string;
  /**
   * Regions To Add
   */
  regions_to_add?: Array<string>;
};

/**
 * ProviderRef
 *
 * One entry in :pyattr:`ModelDefinition.providers` — describes a single
 * no-llm provider that can serve the parent model.
 *
 * ``qualified_slug`` is precomputed so the frontend never has to do its own
 * ``f"{id}:{name}"`` formatting (and so the grammar lives in one place).
 */
export type ProviderRef = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Host
   */
  host?: string | null;
  /**
   * Location
   */
  location?: string | null;
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Provider Is Active
   */
  provider_is_active?: boolean;
  /**
   * Qualified Slug
   */
  qualified_slug: string;
};

/**
 * ProviderResponse
 */
export type ProviderResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Slug
   */
  slug: string;
  /**
   * Data
   */
  data: unknown;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Provider Type
   */
  provider_type?: string | null;
  /**
   * Is Healthy
   */
  is_healthy?: boolean;
  /**
   * Consecutive Failures
   */
  consecutive_failures?: number;
  /**
   * Lifecycle State
   */
  lifecycle_state?: string;
  /**
   * Last Health Check At
   */
  last_health_check_at?: string | null;
  /**
   * Last Health Error
   */
  last_health_error?: string | null;
  /**
   * Health Checks Enabled
   */
  health_checks_enabled?: boolean;
  link_health?: ProviderLinkHealth | null;
};

/**
 * ProviderStatus
 */
export type ProviderStatus = {
  /**
   * Key
   */
  key: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Auth Type
   */
  auth_type: string;
  /**
   * Enabled
   */
  enabled: boolean;
  /**
   * Configured
   */
  configured: boolean;
  /**
   * Ncs Managed
   */
  ncs_managed: boolean;
  /**
   * Settings Managed
   */
  settings_managed: boolean;
  /**
   * Admin Configurable
   */
  admin_configurable: boolean;
  /**
   * Configured At
   */
  configured_at?: string | null;
};

/**
 * ProviderTestResponse
 *
 * Response shape for ``POST /groups/{group_id}/providers/{id}/test``.
 *
 * Each step is rendered live by the FE timeline; the top-level
 * ``is_healthy`` / ``error`` / ``total_latency`` fields are the
 * summary the test dialog binds to.
 */
export type ProviderTestResponse = {
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Steps
   */
  steps: Array<ProviderHealthCheckStep>;
  /**
   * Total Latency
   */
  total_latency: number;
};

/**
 * ProviderUpdateRequest
 */
export type ProviderUpdateRequest = {
  /**
   * Data
   */
  data?: unknown | null;
  /**
   * Is Active
   */
  is_active?: boolean | null;
  /**
   * Dead At
   */
  dead_at?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Slug
   */
  slug?: string | null;
  /**
   * Health Checks Enabled
   */
  health_checks_enabled?: boolean | null;
};

/**
 * PublicAssistant
 */
export type PublicAssistant = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Image Url
   */
  image_url?: string | null;
  /**
   * App Settings V2
   */
  app_settings_v2?: {
    [key: string]: unknown;
  } | null;
  /**
   * File Upload Enabled
   */
  file_upload_enabled?: boolean;
  /**
   * Chat Input Placeholder
   */
  chat_input_placeholder?: string | null;
  /**
   * Tool Display Preset
   */
  tool_display_preset?: string;
  /**
   * Hide Reasoning
   */
  hide_reasoning?: boolean;
  /**
   * Hide Workflow Progress
   */
  hide_workflow_progress?: boolean;
  /**
   * Hide Error Banners
   */
  hide_error_banners?: boolean;
  /**
   * Group Into Chain Of Thought
   */
  group_into_chain_of_thought?: boolean;
  /**
   * Chain Of Thought Final Answer Only
   */
  chain_of_thought_final_answer_only?: boolean;
  /**
   * Citation Style
   */
  citation_style?: string;
  /**
   * Deduplicate Sources By Url
   */
  deduplicate_sources_by_url?: boolean;
  /**
   * Omit Citation Quotes
   */
  omit_citation_quotes?: boolean;
  /**
   * Show Source Images
   */
  show_source_images?: boolean;
  /**
   * Minimal Caret Only
   */
  minimal_caret_only?: boolean;
  /**
   * Minimal Tool Message
   */
  minimal_tool_message?: string;
  /**
   * Message Footer
   */
  message_footer?: string;
};

/**
 * PublicAuthConfigJSON
 *
 * Login-screen-safe subset of auth configuration. Returned by the
 * unauthenticated welcome-config endpoint so the login/signup/forgot-password
 * UI can render without leaking operational fields (autojoin_tenant_id,
 * saml_attribute_mapping, redirect_map, session policy, etc).
 */
export type PublicAuthConfigJson = {
  /**
   * Whitelabel Logo
   */
  whitelabel_logo?: string | null;
  /**
   * Whitelabel Logo Size
   */
  whitelabel_logo_size?: number;
  /**
   * Whitelabel Name
   */
  whitelabel_name?: string;
  /**
   * Whitelabel Primary Color
   */
  whitelabel_primary_color?: string | null;
  /**
   * Allow User Password
   */
  allow_user_password?: boolean;
  /**
   * Allow Signup
   */
  allow_signup?: boolean;
  /**
   * Allow Password Reset
   */
  allow_password_reset?: boolean;
  /**
   * Enabled Connections
   */
  enabled_connections?: Array<AuthConnection>;
  /**
   * Allowed Domains
   */
  allowed_domains?: Array<string>;
  /**
   * Custom Error Messages
   */
  custom_error_messages?: {
    [key: string]: string;
  };
  /**
   * Skip Onboarding
   */
  skip_onboarding?: boolean;
  /**
   * Force Email Verification
   */
  force_email_verification?: boolean;
  /**
   * Allow Personal Workspace
   */
  allow_personal_workspace?: boolean;
};

/**
 * QuadrantCounts
 */
export type QuadrantCounts = {
  /**
   * Correct Sufficient
   */
  correct_sufficient: number;
  /**
   * Correct Insufficient
   */
  correct_insufficient: number;
  /**
   * Incorrect Sufficient
   */
  incorrect_sufficient: number;
  /**
   * Incorrect Insufficient
   */
  incorrect_insufficient: number;
};

/**
 * QueryExportRequest
 */
export type QueryExportRequest = {
  /**
   * Sql
   */
  sql: string;
};

/**
 * QueryRequest
 */
export type QueryRequest = {
  /**
   * Sql
   */
  sql: string;
  /**
   * Limit
   */
  limit?: number | null;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Include Total
   */
  include_total?: boolean;
};

/**
 * QueryResponse
 */
export type QueryResponse = {
  /**
   * Columns
   */
  columns: Array<string>;
  /**
   * Rows
   */
  rows: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Total
   */
  total?: number | null;
};

/**
 * QueuedEvent
 *
 * The session is waiting for sandbox capacity. Sent while the
 * manager's admission queue holds the browser's sandbox request, on
 * every position change and heartbeat, so the client can say where in
 * the line the user is.
 */
export type QueuedEvent = {
  /**
   * Type
   */
  type: "queued";
  /**
   * Queue Position
   */
  queue_position?: number | null;
  /**
   * Queue Depth
   */
  queue_depth?: number | null;
  /**
   * Wait Seconds
   */
  wait_seconds?: number;
};

/**
 * QuoteContainer
 */
export type QuoteContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "Quote";
  /**
   * Value
   */
  value: unknown;
};

/**
 * RateLimitSettings
 */
export type RateLimitSettings = {
  create_run_async?: EndpointRateLimit;
  create_run_sync?: EndpointRateLimit;
  get_run?: EndpointRateLimit;
  create_conversation?: EndpointRateLimit;
  chat?: EndpointRateLimit;
  create_agent?: EndpointRateLimit;
  create_kb?: EndpointRateLimit;
  kb_search?: EndpointRateLimit;
  upload_document?: EndpointRateLimit;
  file_upload?: EndpointRateLimit;
  public_assistant?: EndpointRateLimit;
  sandbox_create?: EndpointRateLimit;
  sandbox_exec?: EndpointRateLimit;
  tables_query?: EndpointRateLimit;
  tables_write?: EndpointRateLimit;
  triggers?: EndpointRateLimit;
  triggers_write?: EndpointRateLimit;
  insights?: EndpointRateLimit;
  deployments?: EndpointRateLimit;
  deployments_write?: EndpointRateLimit;
  users?: EndpointRateLimit;
  default?: EndpointRateLimit;
};

/**
 * RatedMessage
 */
export type RatedMessage = {
  /**
   * Id
   */
  id: string;
  /**
   * Is Positive
   */
  is_positive: boolean;
  feedback_meta?: FeedbackMeta | null;
};

/**
 * RatingDriver
 */
export type RatingDriver = {
  /**
   * Theme
   */
  theme: string;
  /**
   * Count
   */
  count: number;
};

/**
 * RatingDrivers
 */
export type RatingDrivers = {
  /**
   * Positive
   */
  positive: Array<RatingDriver>;
  /**
   * Negative
   */
  negative: Array<RatingDriver>;
};

/**
 * ReadyInfo
 */
export type ReadyInfo = {
  /**
   * Ready
   */
  ready: boolean;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Error Fields
   */
  error_fields?: Array<string> | null;
};

/**
 * ReasoningUIPart
 *
 * A reasoning part of a message.
 */
export type ReasoningUiPart = {
  /**
   * Type
   */
  type?: "reasoning";
  /**
   * Id
   */
  id?: string | null;
  /**
   * Text
   */
  text: string;
  /**
   * State
   */
  state?: "streaming" | "done" | null;
  /**
   * Providermetadata
   */
  providerMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
};

/**
 * RecentActivity
 */
export type RecentActivity = {
  /**
   * Type
   */
  type: string;
  /**
   * Description
   */
  description: string;
  /**
   * Time
   */
  time: string;
  /**
   * Tenant Name
   */
  tenant_name?: string | null;
};

/**
 * RecoveryStats
 */
export type RecoveryStats = {
  /**
   * Queued Count
   */
  queued_count: number;
  /**
   * Running Count
   */
  running_count: number;
  /**
   * Recent Running Count
   */
  recent_running_count: number;
};

/**
 * ReferencedAssetField
 */
export type ReferencedAssetField = {
  /**
   * Config Field
   */
  config_field: string;
  /**
   * Kind
   */
  kind: "workflow" | "assistant";
  /**
   * Label
   */
  label?: string | null;
};

/**
 * RefreshRequest
 */
export type RefreshRequest = {
  /**
   * Refresh Token
   */
  refresh_token: string;
};

/**
 * RelayStatus
 */
export type RelayStatus = {
  status: CheckStatus;
  /**
   * Latency Ms
   */
  latency_ms: number;
  /**
   * Queue Pending
   */
  queue_pending?: number;
  /**
   * Queue Processing
   */
  queue_processing?: number;
  /**
   * Queue Failed
   */
  queue_failed?: number;
};

/**
 * RemigrateSAResponse
 */
export type RemigrateSaResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Updated
   */
  updated: number;
  /**
   * Skipped
   */
  skipped: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Message
   */
  message: string;
};

/**
 * RenameColumnRequest
 */
export type RenameColumnRequest = {
  /**
   * New Name
   */
  new_name: string;
};

/**
 * RenameCredentialRequest
 */
export type RenameCredentialRequest = {
  /**
   * Display Name
   */
  display_name: string;
};

/**
 * RenameTenant
 */
export type RenameTenant = {
  /**
   * Name
   */
  name: string;
};

/**
 * ReorderColumnsRequest
 */
export type ReorderColumnsRequest = {
  /**
   * Column Names
   */
  column_names: Array<string>;
};

/**
 * ReplayEvent
 *
 * Reply to a ``replay_step`` command.
 */
export type ReplayEvent = {
  /**
   * Type
   */
  type: "replay";
  /**
   * Request Id
   */
  request_id: string;
  /**
   * Success
   */
  success: boolean;
  /**
   * Error
   */
  error: string | null;
};

/**
 * RepoOut
 */
export type RepoOut = {
  /**
   * Full Name
   */
  full_name: string;
  /**
   * Default Branch
   */
  default_branch?: string | null;
  /**
   * Private
   */
  private?: boolean;
};

/**
 * RepoState
 */
export type RepoState = {
  /**
   * Exists
   */
  exists?: boolean;
  /**
   * Can Read
   */
  can_read?: boolean;
  /**
   * Can Write
   */
  can_write?: boolean;
};

/**
 * RequestUsage
 *
 * LLM usage associated with a single request.
 *
 * This is an implementation of `genai_prices.types.AbstractUsage` so it can be used to calculate the price of the
 * request using [genai-prices](https://github.com/pydantic/genai-prices).
 */
export type RequestUsage = {
  /**
   * Input Tokens
   */
  input_tokens?: number;
  /**
   * Cache Write Tokens
   */
  cache_write_tokens?: number;
  /**
   * Cache Read Tokens
   */
  cache_read_tokens?: number;
  /**
   * Output Tokens
   */
  output_tokens?: number;
  /**
   * Input Audio Tokens
   */
  input_audio_tokens?: number;
  /**
   * Cache Audio Read Tokens
   */
  cache_audio_read_tokens?: number;
  /**
   * Output Audio Tokens
   */
  output_audio_tokens?: number;
  /**
   * Audio Seconds
   */
  audio_seconds?: number;
  /**
   * Details
   */
  details?: {
    [key: string]: number;
  };
  /**
   * Cost
   */
  cost?: string | null;
};

/**
 * RequireWhenRule
 */
export type RequireWhenRule = {
  /**
   * Kind
   */
  kind: "require_when";
  when: RuleCondition;
  /**
   * Require
   */
  require?: Array<string>;
  /**
   * Message
   */
  message?: string | null;
  /**
   * Strict
   */
  strict?: boolean;
};

/**
 * RerankerConfigurationResponse
 *
 * List endpoint response for rerankers.
 */
export type RerankerConfigurationResponse = {
  /**
   * Reranker Id
   */
  reranker_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Creator
   */
  creator: string;
  /**
   * Capabilities
   */
  capabilities: Array<string>;
  /**
   * Compatible Providers
   */
  compatible_providers: Array<string>;
  /**
   * Is Deprecated
   */
  is_deprecated: boolean;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Image
   */
  image: string | null;
  /**
   * Provider Links
   */
  provider_links: Array<ProviderLinkResponse>;
};

/**
 * RerankersDefinition
 */
export type RerankersDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Slug
   */
  slug?: string | null;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Model Type
   */
  model_type: string;
  /**
   * Model Category
   */
  model_category?: string | null;
  /**
   * Properties
   */
  properties: LlmModelProperties | ModelProperties;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider
   */
  provider?: string | null;
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Provider Image
   */
  provider_image?: string | null;
  /**
   * Providers
   */
  providers?: Array<ProviderRef>;
  /**
   * Host
   */
  host?: string | null;
  /**
   * Location
   */
  location?: string | null;
  /**
   * Price
   */
  price?: string | null;
  /**
   * Release Date
   */
  release_date?: string | null;
  /**
   * Data Cutoff Date
   */
  data_cutoff_date?: string | null;
  /**
   * Retirement Date
   */
  retirement_date?: string | null;
  /**
   * Replaced By
   */
  replaced_by?: string | null;
  /**
   * Privacy Level
   */
  privacy_level?: string | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Is Available
   */
  is_available?: boolean;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Provider Is Active
   */
  provider_is_active?: boolean;
  /**
   * Has Link
   */
  has_link?: boolean;
  /**
   * Is Valid
   */
  is_valid?: boolean;
  /**
   * Is Healthy
   */
  is_healthy?: boolean;
  /**
   * Lifecycle State
   */
  lifecycle_state?: string;
  /**
   * Last Error
   */
  last_error?: string | null;
  /**
   * Consecutive Failures
   */
  consecutive_failures?: number;
  /**
   * Last Checked At
   */
  last_checked_at?: string | null;
  /**
   * Max Documents
   */
  max_documents?: number | null;
};

/**
 * RetryPromptPart
 *
 * A message back to a model asking it to try again.
 *
 * This can be sent for a number of reasons:
 *
 * * Pydantic validation of tool arguments failed, here content is derived from a Pydantic
 * [`ValidationError`][pydantic_core.ValidationError]
 * * a tool raised a [`ModelRetry`][pydantic_ai.exceptions.ModelRetry] exception
 * * no tool was found for the tool name
 * * the model returned plain text when a structured response was expected
 * * Pydantic validation of a structured response failed, here content is derived from a Pydantic
 * [`ValidationError`][pydantic_core.ValidationError]
 * * an output validator raised a [`ModelRetry`][pydantic_ai.exceptions.ModelRetry] exception
 */
export type RetryPromptPart = {
  /**
   * Content
   */
  content: Array<ErrorDetails> | string;
  /**
   * Tool Name
   */
  tool_name?: string | null;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Part Kind
   */
  part_kind?: "retry-prompt";
};

/**
 * RevokeRequest
 */
export type RevokeRequest = {
  /**
   * Deployment Id
   */
  deployment_id: string;
};

/**
 * RevokeResponse
 */
export type RevokeResponse = {
  /**
   * Revoked
   */
  revoked: number;
};

/**
 * RoleInfo
 *
 * Role information with metadata for UI
 */
export type RoleInfo = {
  /**
   * Name
   */
  name: string;
  /**
   * Is System
   */
  is_system: boolean;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
};

/**
 * RowFilterOp
 */
export const RowFilterOp = {
  CONTAINS: "contains",
  IS: "is",
  IS_NOT: "is_not",
  GT: "gt",
  LT: "lt",
  GTE: "gte",
  LTE: "lte",
  EQ: "eq",
  BETWEEN: "between",
  IN: "in",
  NOT_IN: "not_in",
} as const;

/**
 * RowFilterOp
 */
export type RowFilterOp = (typeof RowFilterOp)[keyof typeof RowFilterOp];

/**
 * RowFilterSpec
 */
export type RowFilterSpec = {
  /**
   * Field
   */
  field: string;
  op: RowFilterOp;
  /**
   * Value
   */
  value: string | number | Array<string>;
  /**
   * Value2
   */
  value2?: number | null;
};

/**
 * RowRequest
 */
export type RowRequest = {
  /**
   * Values
   */
  values: {
    [key: string]: unknown;
  };
};

/**
 * RowResponse
 */
export type RowResponse = {
  /**
   * Row
   */
  row: {
    [key: string]: unknown;
  };
};

/**
 * RowsResponse
 */
export type RowsResponse = {
  /**
   * Rows
   */
  rows: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Limit
   */
  limit: number;
  /**
   * Offset
   */
  offset: number;
  /**
   * Total
   */
  total: number;
};

/**
 * RuleCondition
 */
export type RuleCondition = {
  /**
   * Field
   */
  field: string;
  /**
   * Op
   */
  op: "eq" | "neq" | "in" | "empty" | "not_empty";
  /**
   * Value
   */
  value?: string | Array<string> | null;
};

/**
 * Run
 */
export type Run = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Input
   */
  input?: {
    [key: string]: unknown;
  } | null;
  /**
   * Output
   */
  output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Node Ids
   */
  node_ids?:
    | Array<string>
    | {
        [key: string]: unknown;
      }
    | null;
  /**
   * Single Node Id
   */
  single_node_id?: string | null;
  /**
   * Workflow Definition
   */
  workflow_definition?: {
    [key: string]: unknown;
  } | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
  status: RunStatus;
  /**
   * Progress
   */
  progress?: number;
  progress_details?: WorkflowProgress | null;
  /**
   * Definitions
   */
  definitions?: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Source
   */
  source?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
};

/**
 * RunComparison
 */
export type RunComparison = {
  /**
   * Evaluation Run Id
   */
  evaluation_run_id: string;
  /**
   * Status
   */
  status: string;
  /**
   * Total Cases
   */
  total_cases: number;
  /**
   * Configs
   */
  configs: Array<ConfigResult>;
  /**
   * Best Config Per Metric
   */
  best_config_per_metric: {
    [key: string]: string;
  };
};

/**
 * RunDataIndexStats
 */
export type RunDataIndexStats = {
  /**
   * Size Bytes
   */
  size_bytes: number;
  /**
   * Size Pretty
   */
  size_pretty: string;
  /**
   * Runs In Window
   */
  runs_in_window: number;
  /**
   * Runs Indexed
   */
  runs_indexed: number;
  /**
   * Backfill Running
   */
  backfill_running: boolean;
};

/**
 * RunFilters
 */
export type RunFilters = {
  /**
   * From Date
   */
  from_date?: string | null;
  /**
   * To Date
   */
  to_date?: string | null;
  /**
   * Status
   */
  status?: string | null;
  /**
   * Source
   */
  source?: string | null;
  /**
   * User Id
   */
  user_id?: string | null;
  /**
   * Workflow Id
   */
  workflow_id?: string | null;
  /**
   * Tags
   */
  tags?: string | null;
  /**
   * Kb Id
   */
  kb_id?: string | null;
};

/**
 * RunIO
 */
export type RunIo = {
  /**
   * Inputs
   */
  inputs: {
    [key: string]:
      | ({
          type: "File";
        } & FileDataContainer)
      | ({
          type: "Image";
        } & ImageDataContainer)
      | ({
          type: "str";
        } & TextDataContainer)
      | ({
          type: "dict";
        } & DictContainer)
      | ({
          type: "list";
        } & ListContainer)
      | ({
          type: "Audio";
        } & AudioContainer)
      | ({
          type: "Quote";
        } & QuoteContainer)
      | ({
          type: "Custom";
        } & CustomContainer)
      | ({
          type: "number";
        } & NumberContainer)
      | ({
          type: "bool";
        } & BoolContainer)
      | ({
          type: "datetime";
        } & DateTimeContainer)
      | ({
          type: "object";
        } & ObjectContainer)
      | ({
          type: "basemodel";
        } & BaseModelContainer)
      | ({
          type: "_error";
        } & ErrorContainer)
      | ({
          type: "_iterations";
        } & IterationOutcomesContainer);
  };
  /**
   * Outputs
   */
  outputs: {
    [key: string]:
      | ({
          type: "File";
        } & FileDataContainer)
      | ({
          type: "Image";
        } & ImageDataContainer)
      | ({
          type: "str";
        } & TextDataContainer)
      | ({
          type: "dict";
        } & DictContainer)
      | ({
          type: "list";
        } & ListContainer)
      | ({
          type: "Audio";
        } & AudioContainer)
      | ({
          type: "Quote";
        } & QuoteContainer)
      | ({
          type: "Custom";
        } & CustomContainer)
      | ({
          type: "number";
        } & NumberContainer)
      | ({
          type: "bool";
        } & BoolContainer)
      | ({
          type: "datetime";
        } & DateTimeContainer)
      | ({
          type: "object";
        } & ObjectContainer)
      | ({
          type: "basemodel";
        } & BaseModelContainer)
      | ({
          type: "_error";
        } & ErrorContainer)
      | ({
          type: "_iterations";
        } & IterationOutcomesContainer);
  };
  /**
   * Source
   */
  source?: "hot" | "cold" | "memory";
};

/**
 * RunIngestion
 */
export type RunIngestion = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id?: string | null;
  status: RunStatus;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Source
   */
  source?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Documents
   */
  documents?: Array<KnowledgeBaseDocument> | null;
  /**
   * Output
   */
  output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * RunLogs
 */
export type RunLogs = {
  /**
   * Logs
   */
  logs: Array<TraceSpan>;
};

/**
 * RunMetadata
 *
 * Metadata for the evaluated run or conversation.
 */
export type RunMetadata = {
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Status
   */
  status: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Workflow Id
   */
  workflow_id?: string | null;
  /**
   * Workflow Name
   */
  workflow_name?: string | null;
  /**
   * Agent Id
   */
  agent_id?: string | null;
  /**
   * Agent Name
   */
  agent_name?: string | null;
};

/**
 * RunNoIO
 */
export type RunNoIo = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
  /**
   * Input
   */
  input?: {
    [key: string]: unknown;
  } | null;
  /**
   * Node Ids
   */
  node_ids?:
    | Array<string>
    | {
        [key: string]: unknown;
      }
    | null;
  /**
   * Single Node Id
   */
  single_node_id?: string | null;
  status: RunStatus;
  /**
   * Progress
   */
  progress?: number;
  /**
   * Definitions
   */
  definitions?: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Source
   */
  source?: string | null;
  /**
   * User Id
   */
  user_id?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Api Key Id
   */
  api_key_id?: string | null;
};

/**
 * RunState
 */
export type RunState = {
  /**
   * Progress Details
   */
  progress_details: {
    [key: string]: unknown;
  };
  /**
   * Status
   */
  status: string;
  /**
   * Error
   */
  error?: {
    [key: string]: unknown;
  } | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
};

/**
 * RunStatus
 */
export const RunStatus = {
  QUEUED: "queued",
  RUNNING: "running",
  FAILED: "failed",
  COMPLETED: "completed",
  STOPPED: "stopped",
  AWAITING_HUMAN_FEEDBACK: "awaiting_human_feedback",
} as const;

/**
 * RunStatus
 */
export type RunStatus = (typeof RunStatus)[keyof typeof RunStatus];

/**
 * RunWithSearchData
 */
export type RunWithSearchData = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Input
   */
  input?: {
    [key: string]: unknown;
  } | null;
  /**
   * Output
   */
  output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Node Ids
   */
  node_ids?:
    | Array<string>
    | {
        [key: string]: unknown;
      }
    | null;
  /**
   * Single Node Id
   */
  single_node_id?: string | null;
  /**
   * Workflow Definition
   */
  workflow_definition?: {
    [key: string]: unknown;
  } | null;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
  status: RunStatus;
  /**
   * Progress
   */
  progress?: number;
  progress_details?: WorkflowProgress | null;
  /**
   * Definitions
   */
  definitions?: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
  /**
   * Source
   */
  source?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Similarity
   */
  similarity?: number;
  /**
   * Matches
   */
  matches?: Array<{
    [key: string]: string;
  }>;
};

/**
 * SAMLAttributeMappingConfig
 *
 * Two-phase mapping for IdP-driven user provisioning.
 *
 * Phase 1 — ``claim_mappings`` translate raw SAML/OIDC claims into
 * ``user.metadata_`` entries. The same metadata is editable from the Users
 * table by org admins, so admins can audit / override what the IdP set.
 *
 * Phase 2 — ``rules`` match against ``user.metadata_`` (NOT raw claims) and
 * apply workspace/role assignments accordingly.
 *
 * This is applied during autojoin when users authenticate via SAML/OIDC.
 */
export type SamlAttributeMappingConfig = {
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Claim Mappings
   */
  claim_mappings?: Array<ClaimToMetadataMapping>;
  /**
   * Rules
   */
  rules?: Array<SamlAttributeRule>;
  /**
   * Default Workspaces
   */
  default_workspaces?: Array<SamlWorkspaceMapping>;
  /**
   * Default Tenant Role Id
   */
  default_tenant_role_id?: string | null;
  /**
   * Default Tenant Role Name
   */
  default_tenant_role_name?: string | null;
  /**
   * Sync On Login
   */
  sync_on_login?: boolean;
};

/**
 * SAMLAttributeRule
 *
 * A single rule matching a user metadata key/value.
 *
 * Rules are evaluated against ``user.metadata_`` after claim mappings
 * populate it from raw SAML/OIDC claims. When matched, applies the
 * specified workspace memberships and/or tenant role.
 */
export type SamlAttributeRule = {
  /**
   * Metadata Key
   */
  metadata_key: string;
  /**
   * Match Value
   */
  match_value: string;
  /**
   * Match Mode
   */
  match_mode?: string;
  /**
   * Workspaces
   */
  workspaces?: Array<SamlWorkspaceMapping>;
  tenant_role?: SamlTenantRoleMapping | null;
  /**
   * Priority
   */
  priority?: number;
};

/**
 * SAMLTenantRoleMapping
 *
 * Maps a SAML attribute/group value to a tenant role.
 *
 * Either `role_id` (UUID, authoritative) or `role_name` (resolved against the
 * autojoin tenant's TenantRole table case-insensitively) must be set.
 */
export type SamlTenantRoleMapping = {
  /**
   * Role Id
   */
  role_id?: string | null;
  /**
   * Role Name
   */
  role_name?: string | null;
};

/**
 * SAMLWorkspaceMapping
 *
 * Maps a SAML attribute/group value to a workspace (group) with a specific role.
 *
 * The workspace can be identified by ID or name. If the workspace doesn't exist
 * and create_if_missing is True, it will be created.
 *
 * Either `role_id` (UUID, authoritative) or `role_name` (resolved against the
 * autojoin tenant's UserGroupRole table case-insensitively) must be set for
 * real-workspace entries — see ``_require_workspace_role``. Personal-workspace
 * entries (``is_personal=True``) are exempt; the per-user creation path
 * assigns Owner.
 *
 * When ``is_personal`` is True, the entry represents "create a fresh personal
 * workspace for each user at sign-up time" — ``workspace_id`` /
 * ``workspace_name`` are not used, the workspace is materialised per-user
 * from the user's display_name. Requires ``allow_personal_workspace`` to be
 * True on the parent auth config.
 */
export type SamlWorkspaceMapping = {
  /**
   * Workspace Id
   */
  workspace_id?: string | null;
  /**
   * Workspace Name
   */
  workspace_name?: string | null;
  /**
   * Role Id
   */
  role_id?: string | null;
  /**
   * Role Name
   */
  role_name?: string | null;
  /**
   * Create If Missing
   */
  create_if_missing?: boolean;
  /**
   * Set As Default
   */
  set_as_default?: boolean;
  /**
   * Is Personal
   */
  is_personal?: boolean;
};

/**
 * SandboxAllocation
 */
export type SandboxAllocation = {
  /**
   * Manager Url
   */
  manager_url: string;
  /**
   * Sandbox Id
   */
  sandbox_id: string;
  source: SandboxAllocationSource;
  /**
   * Execution Slots
   */
  execution_slots: number;
  /**
   * Persistent
   */
  persistent: boolean;
  /**
   * Ephemeral
   */
  ephemeral: boolean;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Memory Current Bytes
   */
  memory_current_bytes?: number | null;
  /**
   * Memory Max Bytes
   */
  memory_max_bytes?: number | null;
  /**
   * Cpu Usage Usec
   */
  cpu_usage_usec?: number | null;
  /**
   * Cpu Usage Percent
   */
  cpu_usage_percent?: number | null;
};

/**
 * SandboxAllocationSource
 */
export type SandboxAllocationSource = {
  /**
   * Kind
   */
  kind:
    | "plugin"
    | "assistant"
    | "run"
    | "lease"
    | "browser"
    | "recording"
    | "unknown";
  /**
   * Raw
   */
  raw?: string | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Group Id
   */
  group_id?: string | null;
  /**
   * Node Id
   */
  node_id?: string | null;
  /**
   * Href
   */
  href?: string | null;
  /**
   * User Id
   */
  user_id?: string | null;
  /**
   * Group Name
   */
  group_name?: string | null;
  /**
   * User Name
   */
  user_name?: string | null;
  /**
   * User Email
   */
  user_email?: string | null;
};

/**
 * SandboxAllocationsStatus
 */
export type SandboxAllocationsStatus = {
  /**
   * Slots Total
   *
   * Sum of slots_total over the managers that answered.
   */
  slots_total: number;
  /**
   * Manager Reserved Slots
   *
   * Sum of the slots each manager keeps for its own process; already excluded from slots_total.
   */
  manager_reserved_slots: number;
  /**
   * Slots Used
   *
   * Sum of execution_slots over running sandboxes; reservations still being provisioned are not counted.
   */
  slots_used: number;
  /**
   * Slots Free
   */
  slots_free: number;
  /**
   * Persistent Slots
   */
  persistent_slots: number;
  /**
   * Reserved Slots
   */
  reserved_slots: number;
  /**
   * Queue Depth
   */
  queue_depth: number;
  /**
   * Managers
   */
  managers: Array<SandboxManagerStatus>;
  /**
   * Unreachable
   */
  unreachable: Array<ManagerUnreachable>;
  /**
   * Allocations
   */
  allocations: Array<SandboxAllocation>;
  /**
   * Queued
   */
  queued: Array<SandboxQueueEntry>;
};

/**
 * SandboxManagerStatus
 */
export type SandboxManagerStatus = {
  /**
   * Url
   */
  url: string;
  /**
   * Slots Total
   */
  slots_total: number;
  /**
   * Manager Reserved Slots
   */
  manager_reserved_slots: number;
  /**
   * Slots In Use
   */
  slots_in_use: number;
  /**
   * Slots Free
   */
  slots_free: number;
  /**
   * Persistent Slots
   */
  persistent_slots: number;
  /**
   * Max Admissible Slots
   */
  max_admissible_slots: number;
  /**
   * Queue Depth
   */
  queue_depth: number;
  /**
   * Running
   */
  running: number;
};

/**
 * SandboxQueueEntry
 */
export type SandboxQueueEntry = {
  /**
   * Manager Url
   */
  manager_url: string;
  /**
   * Operation Id
   */
  operation_id: string;
  source: SandboxAllocationSource;
  /**
   * Execution Slots
   */
  execution_slots: number;
  /**
   * Persistent
   */
  persistent: boolean;
  /**
   * Ephemeral
   */
  ephemeral: boolean;
  /**
   * Queue Position
   *
   * Admission order on its manager; 1 is admitted next.
   */
  queue_position: number | null;
  /**
   * Queued At
   */
  queued_at: string;
};

/**
 * SandboxSettings
 *
 * Settings shared by every version of the sandbox capability: file
 * operations and optional shell execution in an isolated sandbox.
 */
export type SandboxSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "sandbox";
  /**
   * Persistent
   *
   * Keep sandbox running permanently (no idle timeout)
   */
  persistent?: boolean;
  /**
   * Include Execute
   *
   * Include shell command execution tool
   */
  include_execute?: boolean;
  /**
   * Require Write Approval
   *
   * Require user approval for file write/edit operations
   */
  require_write_approval?: boolean;
  /**
   * Require Execute Approval
   *
   * Require user approval for shell command execution
   */
  require_execute_approval?: boolean;
  /**
   * Execution Slots
   *
   * CPU/memory budget of the sandbox in manager slots (1/8 CPU + 256 MiB each)
   */
  execution_slots?: number;
};

/**
 * SandboxSettings
 *
 * Settings shared by every version of the sandbox capability: file
 * operations and optional shell execution in an isolated sandbox.
 */
export type SandboxSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "sandbox";
  /**
   * Persistent
   *
   * Keep sandbox running permanently (no idle timeout)
   */
  persistent?: boolean;
  /**
   * Include Execute
   *
   * Include shell command execution tool
   */
  include_execute?: boolean;
  /**
   * Require Write Approval
   *
   * Require user approval for file write/edit operations
   */
  require_write_approval?: boolean;
  /**
   * Require Execute Approval
   *
   * Require user approval for shell command execution
   */
  require_execute_approval?: boolean;
  /**
   * Execution Slots
   *
   * CPU/memory budget of the sandbox in manager slots (1/8 CPU + 256 MiB each)
   */
  execution_slots?: number;
};

/**
 * SaveProviderConfigRequest
 */
export type SaveProviderConfigRequest = {
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
  /**
   * Enabled
   */
  enabled?: boolean;
};

/**
 * ScheduleToolSettings
 */
export type ScheduleToolSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "schedule_tool";
};

/**
 * ScheduleToolSettings
 */
export type ScheduleToolSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "schedule_tool";
};

/**
 * ScheduleV2Type
 */
export const ScheduleV2Type = {
  INTERVAL: "interval",
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  CRON_EXPRESSION: "cron_expression",
} as const;

/**
 * ScheduleV2Type
 */
export type ScheduleV2Type =
  (typeof ScheduleV2Type)[keyof typeof ScheduleV2Type];

/**
 * ScopeInfo
 */
export type ScopeInfo = {
  /**
   * Key
   */
  key: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * ScrapingBeeProviderData
 */
export type ScrapingBeeProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Type
   */
  type?: "scrapingbee";
  /**
   * Scrapingbee Api Key
   */
  SCRAPINGBEE_API_KEY: string;
};

/**
 * SearchResult
 */
export type SearchResult = {
  /**
   * Score
   */
  score: number;
  /**
   * Content
   */
  content: string;
  /**
   * Source
   */
  source: string | null;
  document_source: DocumentResult;
  /**
   * Node Id
   */
  node_id?: string | null;
  /**
   * Page Label
   */
  page_label?: string | null;
};

/**
 * SearchRunRequest
 */
export type SearchRunRequest = {
  /**
   * Query
   */
  query: string;
  /**
   * Similarity Threshold
   */
  similarity_threshold?: number;
  /**
   * Limit
   */
  limit?: number;
  /**
   * Offset
   */
  offset?: number;
  /**
   * Context Size
   */
  context_size?: number;
  /**
   * Max Matches Per Run
   */
  max_matches_per_run?: number;
  /**
   * Exact
   */
  exact?: boolean;
  /**
   * Search In
   */
  search_in?: Array<"input" | "output" | "other" | "run_id"> | null;
  filters?: RunFilters | null;
};

/**
 * SecuritySettingsJSON
 *
 * Deployment-level security toggles, stored as the ServerSettings row
 * with ``key="security"``.
 *
 * Lives alongside the ``"global"`` and ``"auth"`` rows so it can be edited
 * from the System → Security admin tab without disturbing other settings.
 */
export type SecuritySettingsJson = {
  /**
   * Mcp Allow Private Ips
   */
  mcp_allow_private_ips?: boolean;
  /**
   * Allow Environment Secrets
   */
  allow_environment_secrets?: boolean;
};

/**
 * SenderOptions
 */
export type SenderOptions = {
  /**
   * Senders
   */
  senders: Array<string>;
  /**
   * Domains
   */
  domains: Array<string>;
  /**
   * Error
   */
  error?: string | null;
};

/**
 * SenderRefSchema
 *
 * External participant identity on a message.
 */
export type SenderRefSchema = {
  /**
   * Type
   */
  type?: string;
  /**
   * User Id
   */
  user_id?: string | null;
  /**
   * Agent Id
   */
  agent_id?: string | null;
  /**
   * External Participant
   */
  external_participant?: {
    [key: string]: unknown;
  } | null;
};

/**
 * SentimentDay
 */
export type SentimentDay = {
  /**
   * Date
   */
  date: string;
  /**
   * Positive
   */
  positive?: number;
  /**
   * Neutral
   */
  neutral?: number;
  /**
   * Negative
   */
  negative?: number;
};

/**
 * SentimentOverTime
 */
export type SentimentOverTime = {
  /**
   * Days
   */
  days: Array<SentimentDay>;
  /**
   * Positive Pct
   */
  positive_pct?: number;
  /**
   * Neutral Pct
   */
  neutral_pct?: number;
  /**
   * Negative Pct
   */
  negative_pct?: number;
};

/**
 * ServerSettingsJSON
 */
export type ServerSettingsJson = {
  health_checks?: HealthCheckOverrides | null;
  /**
   * Support Pub Pem
   */
  support_pub_pem?: string;
  /**
   * Remote Support Mode
   */
  remote_support_mode?: boolean;
  /**
   * Record Shell
   */
  record_shell?: boolean;
  /**
   * License Key
   */
  license_key?: string;
  /**
   * Disabled Users
   */
  disabled_users?: boolean;
  /**
   * Use Ncs
   */
  use_ncs?: boolean;
  /**
   * Ncs Checkin Enabled
   */
  ncs_checkin_enabled?: boolean;
  /**
   * Enable Metrics Sending
   */
  enable_metrics_sending?: boolean;
  /**
   * Noxus Tenant Id
   */
  noxus_tenant_id?: string;
  /**
   * Chaos Testing Enabled
   */
  chaos_testing_enabled?: boolean;
  /**
   * Audit Log Retention Days
   */
  audit_log_retention_days?: 7 | 30 | 90 | 180 | 365 | 730 | 1825 | null;
  /**
   * Run Data Retention Months
   */
  run_data_retention_months?: 1 | 3 | 6 | 12 | 24 | null;
  /**
   * Beta Features
   */
  beta_features?: {
    [key: string]: BetaOptIn;
  };
};

/**
 * ServiceCheck
 */
export type ServiceCheck = {
  status: CheckStatus;
  /**
   * Latency Ms
   */
  latency_ms: number;
  /**
   * Message
   */
  message?: string;
  /**
   * Details
   */
  details?: {
    [key: string]: string | number | number | boolean | null;
  };
};

/**
 * ServiceMetricPoint
 */
export type ServiceMetricPoint = {
  /**
   * Service
   */
  service: string;
  /**
   * Instance
   */
  instance: string;
  /**
   * Timestamp
   */
  timestamp: string;
  /**
   * Rss Mb
   */
  rss_mb?: number | null;
  /**
   * Load 1M
   */
  load_1m?: number | null;
  /**
   * Host Available Mb
   */
  host_available_mb?: number | null;
  /**
   * Disk Free Gb
   */
  disk_free_gb?: number | null;
};

/**
 * ServiceMetricsResponse
 */
export type ServiceMetricsResponse = {
  /**
   * Points
   */
  points: Array<ServiceMetricPoint>;
};

/**
 * SessionCreatedEvent
 *
 * First server event after the recording WS comes up.  Carries the
 * session_id for log correlation and the post-fallback environment
 * flags the FE uses for the banner.
 */
export type SessionCreatedEvent = {
  /**
   * Type
   */
  type: "session_created";
  /**
   * Session Id
   */
  session_id: string;
  /**
   * Environment
   */
  environment: {
    [key: string]: unknown;
  };
};

/**
 * SessionResponse
 */
export type SessionResponse = {
  /**
   * Access Token
   */
  access_token: string;
  /**
   * Refresh Token
   */
  refresh_token: string;
  /**
   * Token Type
   */
  token_type?: string;
  /**
   * Expires In
   */
  expires_in: number;
};

/**
 * SetActiveScopesRequest
 */
export type SetActiveScopesRequest = {
  /**
   * Active Scope Keys
   */
  active_scope_keys: Array<string> | null;
};

/**
 * SetAdminStatusRequest
 */
export type SetAdminStatusRequest = {
  /**
   * Is Admin
   */
  is_admin: boolean;
};

/**
 * SetApiKeyRequest
 */
export type SetApiKeyRequest = {
  /**
   * Api Key
   */
  api_key: string;
};

/**
 * SetBaselineRequest
 */
export type SetBaselineRequest = {
  /**
   * Evaluation Run Id
   */
  evaluation_run_id: string;
};

/**
 * SetComponentEnabledRequest
 */
export type SetComponentEnabledRequest = {
  /**
   * Enabled
   */
  enabled: boolean;
};

/**
 * SetCustomPermissionsRequest
 *
 * Schema for setting custom permission overrides.
 *
 * Permission keys are validated at the service layer (EntityGrantService)
 * to ensure only recognized keys are accepted.
 */
export type SetCustomPermissionsRequest = {
  /**
   * Permissions
   *
   * Permission overrides (null removes override)
   */
  permissions: {
    [key: string]: boolean | null;
  };
};

/**
 * SharepointDrive
 */
export type SharepointDrive = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * SharepointSite
 */
export type SharepointSite = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Displayname
   */
  displayName: string;
  /**
   * Weburl
   */
  webUrl: string;
};

/**
 * SheetInfo
 *
 * Per-worksheet metadata for a spreadsheet file.
 */
export type SheetInfo = {
  /**
   * Name
   */
  name: string;
  /**
   * Dimensions
   */
  dimensions: string;
};

/**
 * SignupRequest
 */
export type SignupRequest = {
  /**
   * Email
   */
  email: string;
  /**
   * Password
   */
  password: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Connection
   */
  connection?: string | null;
};

/**
 * SignupResponse
 */
export type SignupResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Message
   */
  message?: string | null;
  /**
   * User Id
   */
  user_id?: string | null;
  /**
   * Requires Verification
   */
  requires_verification?: boolean | null;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Error Code
   */
  error_code?: string | null;
};

/**
 * SkillConfig
 *
 * Full skill definition stored in agent settings.
 *
 * Skills are markdown-based instruction packages that extend agent
 * capabilities.  Stored directly in the agent's ``definition`` JSONB
 * column — no separate table or migration needed.
 */
export type SkillConfig = {
  /**
   * Name
   */
  name: string;
  /**
   * Usage Conditions
   */
  usage_conditions?: string;
  /**
   * Tags
   */
  tags?: Array<string>;
  /**
   * Version
   */
  version?: string;
  /**
   * Author
   */
  author?: string;
  /**
   * Instructions
   */
  instructions?: string;
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Resources
   */
  resources?: {
    [key: string]: string;
  };
};

/**
 * SlackChannel
 */
export type SlackChannel = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * SlackInformation
 */
export type SlackInformation = {
  /**
   * Channels
   */
  channels: Array<SlackChannel>;
  /**
   * Users
   */
  users: Array<SlackUser>;
};

/**
 * SlackUser
 */
export type SlackUser = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Image
   */
  image: string | null;
};

/**
 * SourceDocumentUIPart
 *
 * A document source part of a message.
 */
export type SourceDocumentUiPart = {
  /**
   * Type
   */
  type?: "source-document";
  /**
   * Sourceid
   */
  sourceId: string;
  /**
   * Mediatype
   */
  mediaType: string;
  /**
   * Title
   */
  title: string;
  /**
   * Filename
   */
  filename?: string | null;
  /**
   * Providermetadata
   */
  providerMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
};

/**
 * SourceMetadata
 *
 * Base class for source metadata across different source types.
 *
 * ``external_id`` is the source's stable id for this item (Drive
 * file id, OneDrive item id, URL for websites, etc.). Datasource
 * ``download_file`` implementations stamp it explicitly, and the
 * DB materialisation trigger projects it onto
 * ``KnowledgeBaseDocument.m_source_external_id`` — which is the
 * join key the KB sync engine uses to find existing docs by their
 * source id.
 */
export type SourceMetadata = {
  /**
   * Rel Path
   */
  rel_path?: string | null;
  /**
   * External Id
   */
  external_id?: string | null;
  /**
   * File Created Date
   */
  file_created_date?: string | null;
  /**
   * File Modified Date
   */
  file_modified_date?: string | null;
  /**
   * Original Url
   */
  original_url?: string | null;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Source Label
   */
  source_label?: string | null;
  /**
   * Source Image
   */
  source_image?: string | null;
  [key: string]:
    | unknown
    | string
    | null
    | string
    | null
    | string
    | null
    | string
    | null
    | string
    | null
    | string
    | null
    | string
    | null
    | string
    | null
    | undefined;
};

/**
 * SourceType
 */
export const SourceType = {
  DOCUMENT: "Document",
  GOOGLE_DRIVE: "Google Drive",
  NOTION: "Notion",
  WEBSITE: "Website",
  ONE_DRIVE: "OneDrive",
  SLACK: "Slack",
  LINEAR: "Linear",
  GITHUB: "Github",
  TEAMS: "Teams",
  SHAREPOINT: "Sharepoint",
  SERVICE_NOW: "ServiceNow",
  CUSTOM: "Custom",
} as const;

/**
 * SourceType
 */
export type SourceType = (typeof SourceType)[keyof typeof SourceType];

/**
 * SourceUrlUIPart
 *
 * A source part of a message.
 */
export type SourceUrlUiPart = {
  /**
   * Type
   */
  type?: "source-url";
  /**
   * Sourceid
   */
  sourceId: string;
  /**
   * Url
   */
  url: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * Providermetadata
   */
  providerMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
};

/**
 * SpecNodeOut
 */
export type SpecNodeOut = {
  /**
   * Key
   */
  key: string;
  /**
   * Title
   */
  title: string;
};

/**
 * SpecPayload
 */
export type SpecPayload = {
  /**
   * Build Version
   */
  build_version: string;
  /**
   * Editable
   */
  editable: boolean;
  /**
   * Nodes
   */
  nodes: Array<FlatNode>;
  /**
   * Cases
   */
  cases: Array<TestCase>;
  /**
   * Automation
   */
  automation: Array<AutomationRef>;
  /**
   * Docs
   */
  docs?: Array<DocRef>;
};

/**
 * SpeechPart
 *
 * Spoken audio exchanged during a realtime session, paired with its transcript.
 *
 * This part is a member of both [`ModelRequestPart`][pydantic_ai.messages.ModelRequestPart] and
 * [`ModelResponsePart`][pydantic_ai.messages.ModelResponsePart], distinguished by `speaker`:
 * in `ModelRequest.parts` the speaker is always `'user'`; in `ModelResponse.parts` it is always
 * `'assistant'`. This invariant is enforced at runtime when a message is constructed.
 *
 * Standard (non-realtime) models can't consume this part directly; when history containing it is
 * used in an agent run, [`Model.prepare_messages`][pydantic_ai.models.Model.prepare_messages]
 * converts user-speaker parts to [`UserPromptPart`][pydantic_ai.messages.UserPromptPart]s and
 * assistant-speaker parts to [`TextPart`][pydantic_ai.messages.TextPart]s.
 */
export type SpeechPart = {
  /**
   * Speaker
   */
  speaker: "user" | "assistant";
  /**
   * Transcript
   */
  transcript?: string | null;
  audio?: BinaryContent | null;
  /**
   * Interrupted At Ms
   */
  interrupted_at_ms?: number | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "speech";
};

/**
 * SpotError
 */
export type SpotError = {
  /**
   * Status Code
   */
  status_code: number;
  /**
   * Message
   */
  message: string;
  /**
   * Snippet
   */
  snippet?: string | null;
  /**
   * Markdown
   */
  markdown?: string | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Identifier
   */
  identifier: string;
  /**
   * Params
   */
  params?: {
    [key: string]: unknown;
  };
  /**
   * Node Id
   */
  node_id?: string | null;
};

/**
 * SpotWarning
 */
export type SpotWarning = {
  /**
   * Detail
   */
  detail: string;
  identifier: WarningIdentifier;
  /**
   * Node Id
   */
  node_id?: string | null;
  /**
   * Params
   */
  params?: {
    [key: string]: unknown;
  };
  /**
   * Markdown
   */
  markdown?: string | null;
  /**
   * Snippet
   */
  snippet?: string | null;
};

/**
 * SpreadsheetMetadata
 *
 * Structural metadata for a spreadsheet (Excel) file.
 */
export type SpreadsheetMetadata = {
  /**
   * Kind
   */
  kind?: "spreadsheet";
  /**
   * Sheets
   */
  sheets: Array<SheetInfo>;
};

/**
 * SpreadsheetSheet
 */
export type SpreadsheetSheet = {
  /**
   * Sheet Id
   */
  sheet_id: string | number;
  /**
   * Title
   */
  title: string;
  /**
   * Columns
   */
  columns: Array<string>;
  /**
   * Max Rows
   */
  max_rows: number;
};

/**
 * SpreadsheetsInformation
 */
export type SpreadsheetsInformation = {
  /**
   * Title
   */
  title: string;
  /**
   * Sheets
   */
  sheets: Array<SpreadsheetSheet>;
};

/**
 * SqlGenerationRequest
 */
export type SqlGenerationRequest = {
  /**
   * Prompt
   */
  prompt: string;
  /**
   * Table Schema
   */
  table_schema?: string;
  /**
   * Mode
   */
  mode?: "select" | "write";
};

/**
 * SqlGenerationResponse
 */
export type SqlGenerationResponse = {
  /**
   * Sql
   */
  sql: string;
};

/**
 * StartEvaluationRunRequest
 *
 * Request to start an evaluation run.
 *
 * The test suite determines whether this is a workflow or agent evaluation.
 * - For workflows: version_id refers to workflow_version_id (required)
 * - For agents: version_id refers to agent_version_id (None = current draft)
 */
export type StartEvaluationRunRequest = {
  /**
   * Version Id
   */
  version_id?: string | null;
  /**
   * Test Case Ids
   */
  test_case_ids?: Array<string> | null;
  /**
   * Wait For Completion
   */
  wait_for_completion?: boolean;
};

/**
 * StartedEvaluation
 */
export type StartedEvaluation = {
  /**
   * Run Ids
   */
  run_ids: Array<string>;
  /**
   * Evaluation Run Id
   */
  evaluation_run_id: string | null;
};

/**
 * StatSummary
 */
export type StatSummary = {
  /**
   * Mean
   */
  mean: number;
  /**
   * P50
   */
  p50: number;
  /**
   * P95
   */
  p95: number;
};

/**
 * StaticSelectSource
 */
export type StaticSelectSource = {
  /**
   * Kind
   */
  kind: "static";
  /**
   * Options
   */
  options?: Array<string>;
};

/**
 * Status
 */
export type Status = {
  /**
   * Success
   */
  success: boolean;
};

/**
 * StatusEvent
 *
 * Session status — recording on/off, current URL + page title.
 */
export type StatusEvent = {
  /**
   * Type
   */
  type: "status";
  /**
   * Recording
   */
  recording: boolean;
  /**
   * Url
   */
  url: string;
  /**
   * Title
   */
  title: string;
};

/**
 * StepEvent
 *
 * A freshly-recorded playbook step appended to the session.
 */
export type StepEvent = {
  /**
   * Type
   */
  type: "step";
  /**
   * Step
   */
  step: {
    [key: string]: unknown;
  };
  /**
   * Index
   */
  index: number;
};

/**
 * StepStartUIPart
 *
 * A step boundary part of a message.
 */
export type StepStartUiPart = {
  /**
   * Type
   */
  type?: "step-start";
};

/**
 * StepUpdateEvent
 *
 * An edit to an existing step (e.g. coalescing fills into one step).
 */
export type StepUpdateEvent = {
  /**
   * Type
   */
  type: "step_update";
  /**
   * Step
   */
  step: {
    [key: string]: unknown;
  };
  /**
   * Index
   */
  index: number;
};

/**
 * StorageStatus
 */
export type StorageStatus = {
  /**
   * Level
   */
  level: "healthy" | "warning" | "critical";
  /**
   * Used Bytes
   */
  used_bytes: number;
  /**
   * Total Bytes
   */
  total_bytes: number;
  /**
   * Free Bytes
   */
  free_bytes: number;
  /**
   * Used Percentage
   */
  used_percentage: number;
  /**
   * Warning Free Bytes
   */
  warning_free_bytes: number;
  /**
   * Critical Free Bytes
   */
  critical_free_bytes: number;
};

/**
 * StripePortal
 */
export type StripePortal = {
  /**
   * Url
   */
  url: string;
};

/**
 * SubAgentDefinition
 *
 * One delegate: a stored roster entry, or the built-in general-purpose one.
 *
 * The roster editor came out in DEV-2279. Agents that built one keep it,
 * hidden, and it keeps running; nothing offers to write a new entry.
 */
export type SubAgentDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Usage Conditions
   */
  usage_conditions: string;
  /**
   * Instructions
   */
  instructions?: string;
  /**
   * Model
   */
  model?: Array<string>;
  /**
   * Enabled
   */
  enabled?: boolean;
};

/**
 * SubAgentSettings
 *
 * Settings for the subagent task delegation toolset.
 *
 * The roster lives on the agent settings (``subagents``); this variant only
 * carries the tool's chat display.
 */
export type SubAgentSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "subagent";
  /**
   * Subagents
   */
  subagents?: Array<SubAgentDefinition>;
};

/**
 * SubAgentSettings
 *
 * Settings for the subagent task delegation toolset.
 *
 * The roster lives on the agent settings (``subagents``); this variant only
 * carries the tool's chat display.
 */
export type SubAgentSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "subagent";
  /**
   * Subagents
   */
  subagents?: Array<SubAgentDefinition>;
};

/**
 * SubTopic
 */
export type SubTopic = {
  /**
   * Label
   */
  label: string;
  /**
   * Parent Topic
   */
  parent_topic: string;
  /**
   * Count
   */
  count: number;
};

/**
 * SubTopics
 */
export type SubTopics = {
  /**
   * Subtopics
   */
  subtopics: Array<SubTopic>;
};

/**
 * SubflowConfig
 */
export type SubflowConfig = {
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Workflow Name
   */
  workflow_name: string;
  /**
   * Workflow Nodes
   */
  workflow_nodes?: Array<string>;
};

/**
 * SubflowDefinitions
 *
 * The definitions for one subflow node, held once per run.
 *
 * The raw_definition is stored for resume purposes - when resuming a paused
 * subflow (e.g., after HITL), we must use the exact same workflow definition
 * that was used originally, not the potentially-modified current version.
 */
export type SubflowDefinitions = {
  workflow_definition?: WorkflowDefinition | null;
  /**
   * Raw Definition
   */
  raw_definition?: {
    [key: string]: unknown;
  } | null;
};

/**
 * SubflowExecution
 *
 * Some important notes:
 * - This stores metadata needed to display subflow results in the frontend.
 * - Each iteration's progress is stored separately in the iterations dict.
 *
 * The definitions live in ``WorkflowProgress.subflow_definitions`` on the run's
 * ROOT progress, keyed by ``node_id`` — NOT inline here. A nested subflow is
 * re-registered once per parent iteration, and every progress snapshot deep
 * copies the subtree it sits in, so an inline definition is duplicated
 * O(parent_iterations x depth) times. At CTT that was 625 copies of a 117KB
 * definition: 174MB of a 190MB progress blob, and 14GB of worker RSS.
 *
 * ``workflow_definition``/``raw_definition`` stay on the model, unset, so runs
 * persisted before the registry existed still deserialize and render.
 */
export type SubflowExecution = {
  /**
   * Node Id
   */
  node_id: string;
  /**
   * Workflow Name
   */
  workflow_name: string;
  /**
   * Workflow Id
   */
  workflow_id: string;
  workflow_definition?: WorkflowDefinition | null;
  /**
   * Raw Definition
   */
  raw_definition?: {
    [key: string]: unknown;
  } | null;
  /**
   * Definition Key
   */
  definition_key?: string | null;
  /**
   * Total Iterations
   */
  total_iterations: number;
  /**
   * Iterations
   */
  iterations?: {
    [key: string]: WorkflowProgress;
  };
  /**
   * Iteration Summaries
   */
  iteration_summaries?: {
    [key: string]: SubflowIterationSummary;
  };
};

/**
 * SubflowIterationSummary
 *
 * The always-resident description of one subflow iteration.
 *
 * Bodies are windowed and spilled; these are not. The scrubber's iteration
 * list and its errored-iteration markers read from here, so they stay correct
 * for every iteration whether or not its body is still in memory.
 */
export type SubflowIterationSummary = {
  /**
   * Has Error
   */
  has_error?: boolean;
  /**
   * Is Complete
   */
  is_complete?: boolean;
  /**
   * Errored Nodes
   */
  errored_nodes?: Array<string>;
  /**
   * Executed
   */
  executed?: number;
  /**
   * Total
   */
  total?: number;
  /**
   * Started At
   */
  started_at?: string | null;
  /**
   * Finished At
   */
  finished_at?: string | null;
};

/**
 * SubflowVersionUsage
 */
export type SubflowVersionUsage = {
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Workflow Name
   */
  workflow_name: string;
  /**
   * Workflow Icon
   */
  workflow_icon: string | null;
  /**
   * Version Id
   */
  version_id: string | null;
  /**
   * Version Name
   */
  version_name: string | null;
};

/**
 * SubscriptionUpdate
 */
export type SubscriptionUpdate = {
  subscription_plan: Plan;
};

/**
 * SupportLogUploadResult
 */
export type SupportLogUploadResult = {
  /**
   * Password
   */
  password: string;
  /**
   * Filename
   */
  filename: string;
};

/**
 * SyncSpreadsheetLogsRequest
 */
export type SyncSpreadsheetLogsRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Spreadsheet Id
   */
  spreadsheet_id: string;
};

/**
 * SyncStatusResponse
 */
export type SyncStatusResponse = {
  /**
   * Trigger Id
   */
  trigger_id: string;
  source_type: SourceType;
  /**
   * Cron
   */
  cron: string;
  schedule: KbSyncSchedule | null;
  /**
   * Next Runs
   */
  next_runs: Array<string>;
  /**
   * Watched Items
   */
  watched_items: Array<WatchedItemSchema>;
  /**
   * Resource Label
   */
  resource_label?: string | null;
  /**
   * Delete On Source Removal
   */
  delete_on_source_removal?: boolean;
  /**
   * Last Sync At
   */
  last_sync_at: string | null;
  /**
   * Error Count
   */
  error_count: number;
  /**
   * Last Error
   */
  last_error: string | null;
  /**
   * Is Circuit Open
   */
  is_circuit_open: boolean;
};

/**
 * SystemPromptPart
 *
 * A system prompt, generally written by the application developer.
 *
 * This gives the model context and guidance on how to respond.
 */
export type SystemPromptPart = {
  /**
   * Content
   */
  content: string;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Dynamic Ref
   */
  dynamic_ref?: string | null;
  /**
   * Part Kind
   */
  part_kind?: "system-prompt";
};

/**
 * TableColumn
 */
export type TableColumn = {
  /**
   * Name
   *
   * Column identifier
   */
  name: string;
  /**
   * Label
   *
   * Column display label
   */
  label: string;
  /**
   * Type
   *
   * Column scalar type
   */
  type:
    | "text"
    | "number"
    | "date"
    | "boolean"
    | "select"
    | "email"
    | "phone"
    | "url";
  /**
   * Required
   *
   * Whether the cell must be filled
   */
  required?: boolean;
  /**
   * Options
   *
   * Options for select-type columns
   */
  options?: Array<string> | null;
  /**
   * Read Only
   *
   * Whether humans can edit cells in this column
   */
  read_only?: boolean;
  /**
   * Source
   *
   * Option source for select columns: static list or table lookup
   */
  source?:
    | ({
        kind: "static";
      } & StaticSelectSource)
    | ({
        kind: "table";
      } & TableSelectSource)
    | null;
  /**
   * Shape-validation rules for cells in this column
   */
  validation?: FieldValidation | null;
};

/**
 * TableResponse
 */
export type TableResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Sql Name
   */
  sql_name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Id Type
   */
  id_type: "uuid" | "autoincrement";
  /**
   * Columns
   */
  columns: Array<DataTableColumn>;
  /**
   * Meta
   */
  meta: {
    [key: string]: unknown;
  };
  /**
   * Source
   */
  source?: "custom" | "platform";
  /**
   * Created At
   */
  created_at: string | null;
  /**
   * Updated At
   */
  updated_at: string | null;
};

/**
 * TableSelectSource
 */
export type TableSelectSource = {
  /**
   * Kind
   */
  kind: "table";
  /**
   * Table Id
   */
  table_id: string;
  /**
   * Value Column
   */
  value_column: string;
  /**
   * Fill
   */
  fill?: Array<LookupFillMapping>;
};

/**
 * TableStats
 */
export type TableStats = {
  /**
   * Row Count
   */
  row_count: number;
  /**
   * Size Bytes
   */
  size_bytes: number;
  /**
   * Column Count
   */
  column_count: number;
};

/**
 * TableWithCount
 */
export type TableWithCount = {
  /**
   * Tags
   */
  tags?: Array<Tag> | null;
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Sql Name
   */
  sql_name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Id Type
   */
  id_type: "uuid" | "autoincrement";
  /**
   * Columns
   */
  columns: Array<DataTableColumn>;
  /**
   * Meta
   */
  meta: {
    [key: string]: unknown;
  };
  /**
   * Source
   */
  source?: "custom" | "platform";
  /**
   * Created At
   */
  created_at: string | null;
  /**
   * Updated At
   */
  updated_at: string | null;
  /**
   * Row Count
   */
  row_count: number;
};

/**
 * TabularMetadata
 *
 * Structural metadata for a delimited-text (CSV/TSV) file.
 */
export type TabularMetadata = {
  /**
   * Kind
   */
  kind?: "tabular";
  /**
   * Row Count
   */
  row_count: number;
  /**
   * Column Count
   */
  column_count: number;
};

/**
 * Tag
 */
export type Tag = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Color
   */
  color: string;
};

/**
 * TagEntityRequest
 */
export type TagEntityRequest = {
  /**
   * Entity Id
   *
   * ID of the entity to tag
   */
  entity_id: string;
  /**
   * Type of entity (workflow, assistant, knowledge_base)
   */
  entity_type: EntityType;
};

/**
 * TagResponse
 */
export type TagResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Color
   */
  color: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Created By
   */
  created_by: string;
};

/**
 * TaggedEntitiesResponse
 */
export type TaggedEntitiesResponse = {
  tag: TagResponse;
  /**
   * Entities
   */
  entities: Array<EntityTagAssociationResponse>;
};

/**
 * TaskDetailResponse
 */
export type TaskDetailResponse = {
  /**
   * Title
   *
   * Task title
   */
  title: string;
  /**
   * Description
   *
   * Task description
   */
  description?: string | null;
  /**
   * Task Type
   *
   * Task type
   */
  task_type: string;
  /**
   * Status
   *
   * Task status
   */
  status: "pending" | "in_progress" | "completed" | "failed";
  /**
   * Assigned To
   *
   * User assigned to this task
   */
  assigned_to?: string | null;
  /**
   * Priority
   *
   * Task priority
   */
  priority?: "low" | "medium" | "high" | "urgent";
  /**
   * Input
   *
   * Task input data
   */
  input?: {
    [key: string]: unknown;
  } | null;
  /**
   * Output
   *
   * Task output data
   */
  output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Suggested Output
   *
   * Agent's draft answer (frozen at automation finish-time). Form pre-populates from this when output is null; never overwritten by human edits.
   */
  suggested_output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Prefilled At
   *
   * Timestamp the automation finished prefilling the task. Discriminates 'no agent ran' (null) from 'agent ran and produced a draft' (set).
   */
  prefilled_at?: string | null;
  /**
   * Task Config
   *
   * Task configuration
   */
  task_config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Automation Run Id
   */
  automation_run_id?: string | null;
  /**
   * Automation Workflow Id
   */
  automation_workflow_id?: string | null;
  /**
   * Automation Run Status
   */
  automation_run_status?: string | null;
  /**
   * Id
   */
  id: string;
  /**
   * Case Id
   */
  case_id: string;
  /**
   * Completed By
   */
  completed_by?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  case?: CaseResponse | null;
};

/**
 * TaskListResponse
 */
export type TaskListResponse = {
  /**
   * Automation Run Id
   */
  automation_run_id?: string | null;
  /**
   * Automation Workflow Id
   */
  automation_workflow_id?: string | null;
  /**
   * Automation Run Status
   */
  automation_run_status?: string | null;
  /**
   * Id
   */
  id: string;
  /**
   * Case Id
   */
  case_id: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Task Type
   */
  task_type: string;
  /**
   * Status
   */
  status: "pending" | "in_progress" | "completed" | "failed";
  /**
   * Assigned To
   */
  assigned_to: string | null;
  /**
   * Priority
   */
  priority: "low" | "medium" | "high" | "urgent";
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Case Subject
   */
  case_subject?: string | null;
  /**
   * Case Display Id
   */
  case_display_id?: string | null;
};

/**
 * TaskResponse
 */
export type TaskResponse = {
  /**
   * Title
   *
   * Task title
   */
  title: string;
  /**
   * Description
   *
   * Task description
   */
  description?: string | null;
  /**
   * Task Type
   *
   * Task type
   */
  task_type: string;
  /**
   * Status
   *
   * Task status
   */
  status: "pending" | "in_progress" | "completed" | "failed";
  /**
   * Assigned To
   *
   * User assigned to this task
   */
  assigned_to?: string | null;
  /**
   * Priority
   *
   * Task priority
   */
  priority?: "low" | "medium" | "high" | "urgent";
  /**
   * Input
   *
   * Task input data
   */
  input?: {
    [key: string]: unknown;
  } | null;
  /**
   * Output
   *
   * Task output data
   */
  output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Suggested Output
   *
   * Agent's draft answer (frozen at automation finish-time). Form pre-populates from this when output is null; never overwritten by human edits.
   */
  suggested_output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Prefilled At
   *
   * Timestamp the automation finished prefilling the task. Discriminates 'no agent ran' (null) from 'agent ran and produced a draft' (set).
   */
  prefilled_at?: string | null;
  /**
   * Task Config
   *
   * Task configuration
   */
  task_config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Automation Run Id
   */
  automation_run_id?: string | null;
  /**
   * Automation Workflow Id
   */
  automation_workflow_id?: string | null;
  /**
   * Automation Run Status
   */
  automation_run_status?: string | null;
  /**
   * Id
   */
  id: string;
  /**
   * Case Id
   */
  case_id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * TaskUpdate
 */
export type TaskUpdate = {
  /**
   * Status
   */
  status?: "pending" | "in_progress" | "completed" | "failed" | null;
  /**
   * Assigned To
   */
  assigned_to?: string | null;
  /**
   * Output
   */
  output?: {
    [key: string]: unknown;
  } | null;
  /**
   * Priority
   */
  priority?: "low" | "medium" | "high" | "urgent" | null;
  /**
   * Complete Case
   */
  complete_case?: boolean | null;
};

/**
 * Template
 */
export type Template = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Category
   */
  category: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Used Count
   */
  used_count: number;
  /**
   * Color
   */
  color?: string | null;
  /**
   * Color2
   */
  color2?: string | null;
};

/**
 * TemplateComponent
 *
 * A component in the template (e.g., Triage main)
 */
export type TemplateComponent = {
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type: string;
  /**
   * Description
   */
  description: string;
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Is Main
   */
  is_main?: boolean;
  /**
   * Sidenav Text
   */
  sidenav_text?: string | null;
  /**
   * Sub Components
   */
  sub_components?: Array<TemplateSubComponent>;
  /**
   * Meta Icon
   */
  meta_icon?: string | null;
};

/**
 * TemplateConfigResponse
 */
export type TemplateConfigResponse = {
  /**
   * Config
   */
  config: {
    [key: string]: {
      [key: string]: unknown;
    };
  };
  /**
   * Ready
   */
  ready: boolean;
  /**
   * Integrations
   */
  integrations?: Array<string>;
  /**
   * Errors
   */
  errors?: {
    [key: string]: string;
  };
};

/**
 * TemplateSubComponent
 *
 * A sub-component within a template component (e.g., Subflow, Knowledge base)
 */
export type TemplateSubComponent = {
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Sidenav Text
   */
  sidenav_text?: string | null;
  /**
   * Meta Icon
   */
  meta_icon?: string | null;
};

/**
 * TemplateVisual
 */
export type TemplateVisual = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string;
  /**
   * Long Description
   */
  long_description?: string | null;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Category
   */
  category: string;
  /**
   * Color
   */
  color: string;
  /**
   * Featured
   */
  featured: boolean;
  /**
   * Integrations
   */
  integrations: Array<string>;
  /**
   * Time To Production
   */
  time_to_production?: string | null;
  /**
   * Video Url
   */
  video_url?: string | null;
  /**
   * Sidenav Runner Text
   */
  sidenav_runner_text?: string | null;
  /**
   * Components
   */
  components?: Array<TemplateComponent>;
  /**
   * Tag Name
   */
  tag_name?: string | null;
  /**
   * Tag Color
   */
  tag_color?: string | null;
  /**
   * Marketing Text
   */
  marketing_text?: string | null;
  /**
   * Improvement Text
   */
  improvement_text?: string | null;
  /**
   * Is Configurable
   */
  is_configurable?: boolean;
};

/**
 * Tenant
 */
export type Tenant = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Domain
   */
  domain: string | null;
  /**
   * Reserved Domain
   */
  reserved_domain?: string | null;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Stripe Id
   */
  stripe_id?: string | null;
  /**
   * Subscription Plan
   */
  subscription_plan?: string | null;
  /**
   * Automatic Recharge Amount
   */
  automatic_recharge_amount?: number | null;
  /**
   * Current Credits
   */
  current_credits?: number | null;
  /**
   * Logo
   */
  logo?: string | null;
  /**
   * Metadata
   */
  metadata_?: {
    [key: string]: unknown;
  } | null;
};

/**
 * TenantGroupSummary
 */
export type TenantGroupSummary = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
};

/**
 * TenantRoleCreate
 *
 * Schema for creating a tenant role.
 */
export type TenantRoleCreate = {
  /**
   * Name
   *
   * Role name
   */
  name: string;
  /**
   * Description
   *
   * Role description
   */
  description?: string | null;
  /**
   * Permissions
   *
   * Permission settings
   */
  permissions: {
    [key: string]: boolean;
  };
  /**
   * Meta
   *
   * UI meta (color, icon, etc.)
   */
  meta?: {
    [key: string]: string;
  } | null;
};

/**
 * TenantRoleResponse
 *
 * Schema for tenant role response.
 */
export type TenantRoleResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Tenant Id
   */
  tenant_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Is Default
   */
  is_default: boolean;
  /**
   * Is System
   */
  is_system: boolean;
  /**
   * Permissions
   */
  permissions: {
    [key: string]: boolean;
  };
  /**
   * Meta
   */
  meta: {
    [key: string]: string;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * TenantRoleUpdate
 *
 * Schema for updating a tenant role.
 */
export type TenantRoleUpdate = {
  /**
   * Name
   *
   * Role name
   */
  name?: string | null;
  /**
   * Description
   *
   * Role description
   */
  description?: string | null;
  /**
   * Permissions
   *
   * Permission settings
   */
  permissions?: {
    [key: string]: boolean;
  } | null;
  /**
   * Meta
   *
   * UI meta (color, icon, etc.)
   */
  meta?: {
    [key: string]: string;
  } | null;
};

/**
 * TenantUpdateRequest
 */
export type TenantUpdateRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Automatic Recharge Amount
   */
  automatic_recharge_amount?: number | null;
  /**
   * Metadata
   */
  metadata_?: {
    [key: string]: unknown;
  } | null;
  /**
   * Logo
   */
  logo?: string | null;
};

/**
 * TenantWithBilling
 *
 * Tenant schema with embedded billing information.
 */
export type TenantWithBilling = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Domain
   */
  domain: string | null;
  /**
   * Reserved Domain
   */
  reserved_domain?: string | null;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Stripe Id
   */
  stripe_id?: string | null;
  /**
   * Subscription Plan
   */
  subscription_plan?: string | null;
  /**
   * Automatic Recharge Amount
   */
  automatic_recharge_amount?: number | null;
  /**
   * Current Credits
   */
  current_credits?: number | null;
  /**
   * Logo
   */
  logo?: string | null;
  /**
   * Metadata
   */
  metadata_?: {
    [key: string]: unknown;
  } | null;
  billing?: BillingInfo | null;
};

/**
 * TenantWithUsers
 */
export type TenantWithUsers = {
  tenant: Tenant;
  /**
   * Users
   */
  users: Array<User>;
};

/**
 * TerminateBackendRequest
 */
export type TerminateBackendRequest = {
  /**
   * Database
   */
  database?: "postgres" | "vector" | "procrastinate";
  /**
   * Pid
   */
  pid: number;
  /**
   * Reason
   */
  reason?: string | null;
};

/**
 * TerminateBackendResponse
 */
export type TerminateBackendResponse = {
  /**
   * Timestamp
   */
  timestamp: string;
  /**
   * Database
   */
  database: "postgres" | "vector" | "procrastinate";
  /**
   * Pid
   */
  pid: number;
  /**
   * Terminated
   */
  terminated: boolean;
  /**
   * Previous State
   */
  previous_state?: string | null;
  /**
   * Previous Query
   */
  previous_query?: string;
};

/**
 * TestCase
 */
export type TestCase = {
  /**
   * Key
   */
  key?: string | null;
  /**
   * Capability
   */
  capability: string;
  /**
   * Title
   */
  title: string;
  /**
   * Priority
   */
  priority?: "low" | "medium" | "high" | "critical";
  /**
   * Preconditions
   */
  preconditions?: string | null;
  /**
   * Steps
   */
  steps?: Array<TestStep>;
  /**
   * Tags
   */
  tags?: Array<string>;
  /**
   * Linear
   */
  linear?: string | null;
};

/**
 * TestCaseReport
 */
export type TestCaseReport = {
  /**
   * Test Case Id
   */
  test_case_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Outcome
   */
  outcome: "running" | "passed" | "failed" | "error" | "cancelled" | "not_run";
  /**
   * Conversation Id
   */
  conversation_id: string | null;
  /**
   * Workflow Run Id
   */
  workflow_run_id: string | null;
  /**
   * Started At
   */
  started_at: string | null;
  /**
   * Finished At
   */
  finished_at: string | null;
  /**
   * Checks
   */
  checks: Array<CheckResult>;
};

/**
 * TestCaseResponse
 *
 * Response model for test case.
 *
 * Returns data exactly as stored in DB (canonical format):
 * - inputs: {"connector_name": "plain string"}
 * - expected_outputs: {"connector_name": "plain string"} (only active fields)
 */
export type TestCaseResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Test Suite Id
   */
  test_suite_id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Inputs
   */
  inputs: {
    [key: string]: unknown;
  };
  /**
   * Expected Outputs
   */
  expected_outputs: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * TestCaseRunResponse
 *
 * A previous run associated with a test case.
 */
export type TestCaseRunResponse = {
  /**
   * Run Id
   */
  run_id: string;
  /**
   * Status
   */
  status: string;
  /**
   * Created At
   */
  created_at: string;
};

/**
 * TestCaseWEvals
 *
 * Test case with its evaluation results.
 */
export type TestCaseWEvals = {
  /**
   * Id
   */
  id: string;
  /**
   * Test Suite Id
   */
  test_suite_id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Inputs
   */
  inputs: {
    [key: string]: unknown;
  };
  /**
   * Expected Outputs
   */
  expected_outputs: {
    [key: string]: unknown;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Evaluation Results
   */
  evaluation_results: Array<EvaluatorResultResponse>;
  /**
   * Workflow Run Io
   */
  workflow_run_io?: {
    [key: string]: unknown;
  } | null;
  /**
   * Latency Ms
   */
  latency_ms?: number | null;
  /**
   * Cost
   */
  cost?: number | null;
  /**
   * Passed
   */
  passed?: boolean | null;
  /**
   * Is Outdated
   */
  is_outdated?: boolean | null;
  /**
   * Outdated Reasons
   */
  outdated_reasons?: Array<string> | null;
  /**
   * Last Run At
   */
  last_run_at?: string | null;
};

/**
 * TestStep
 */
export type TestStep = {
  /**
   * Do
   */
  do: string;
  /**
   * Expect
   */
  expect?: string;
  /**
   * Run
   */
  run?: Array<string>;
  /**
   * Check
   */
  check?: Array<string>;
};

/**
 * TestSuiteCreate
 *
 * Request to create a test suite.
 */
export type TestSuiteCreate = {
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * TestSuiteResponse
 *
 * Response model for test suite.
 */
export type TestSuiteResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id: string | null;
  /**
   * Agent Id
   */
  agent_id: string | null;
  /**
   * Knowledge Base Id
   */
  knowledge_base_id?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * TestSuiteStats
 *
 * Statistics for a test suite.
 */
export type TestSuiteStats = {
  /**
   * Test Count
   */
  test_count: number;
  /**
   * Evaluator Count
   */
  evaluator_count: number;
  /**
   * Pass Rate
   */
  pass_rate: number;
  /**
   * Last Run At
   */
  last_run_at: string | null;
  /**
   * Last Run Agent Version Id
   */
  last_run_agent_version_id?: string | null;
  /**
   * Avg Latency Ms
   */
  avg_latency_ms?: number | null;
  /**
   * Avg Cost
   */
  avg_cost?: number | null;
  /**
   * Passed Count
   */
  passed_count?: number;
  /**
   * Failed Count
   */
  failed_count?: number;
  /**
   * Not Run Count
   */
  not_run_count?: number;
  /**
   * Outdated Count
   */
  outdated_count?: number;
  /**
   * Running Count
   */
  running_count?: number;
  /**
   * Cancelled Count
   */
  cancelled_count?: number;
  /**
   * Error Count
   */
  error_count?: number;
};

/**
 * TestSuiteUpdate
 *
 * Request to update a test suite.
 */
export type TestSuiteUpdate = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
};

/**
 * TestSuiteWithStats
 *
 * Test suite response with stats.
 */
export type TestSuiteWithStats = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id: string | null;
  /**
   * Agent Id
   */
  agent_id: string | null;
  /**
   * Knowledge Base Id
   */
  knowledge_base_id?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  stats: TestSuiteStats;
};

/**
 * TextChange
 */
export type TextChange = {
  /**
   * Kind
   */
  kind?: "text";
  /**
   * Key
   */
  key: string;
  /**
   * Label
   */
  label: string;
  /**
   * Lines
   */
  lines: Array<TextLine>;
};

/**
 * TextContent
 *
 * String content that is tagged with additional metadata.
 *
 * This is useful for including metadata that can be accessed programmatically by the application, but is not sent to the LLM.
 */
export type TextContent = {
  /**
   * Content
   */
  content: string;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Kind
   */
  kind?: "text-content";
};

/**
 * TextDataContainer
 */
export type TextDataContainer = {
  /**
   * Total Size
   */
  total_size?: number | null;
  /**
   * Taken
   */
  taken?: number | null;
  /**
   * Truncated
   */
  truncated?: boolean;
  preview_file?: FileType | null;
  /**
   * List Offset
   */
  list_offset?: number;
  /**
   * Type
   */
  type?: "str";
  /**
   * Value
   */
  value: unknown;
};

/**
 * TextLine
 */
export type TextLine = {
  /**
   * Op
   */
  op: "same" | "removed" | "added" | "gap";
  /**
   * Text
   */
  text?: string;
};

/**
 * TextPart
 *
 * A plain text response from a model.
 */
export type TextPart = {
  /**
   * Content
   */
  content: string;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "text";
};

/**
 * TextRefinementRequest
 */
export type TextRefinementRequest = {
  /**
   * Text
   */
  text: string;
  /**
   * Instruction
   */
  instruction: string;
  /**
   * Context
   */
  context?: string | null;
};

/**
 * TextRefinementResponse
 */
export type TextRefinementResponse = {
  /**
   * Text
   */
  text: string;
};

/**
 * TextTranslationRequest
 */
export type TextTranslationRequest = {
  /**
   * Text
   */
  text: string;
  /**
   * Target Language
   */
  target_language: string;
};

/**
 * TextTranslationResponse
 */
export type TextTranslationResponse = {
  /**
   * Text
   */
  text: string;
};

/**
 * TextUIPart
 *
 * A text part of a message.
 */
export type TextUiPart = {
  /**
   * Type
   */
  type?: "text";
  /**
   * Text
   */
  text: string;
  /**
   * State
   */
  state?: "streaming" | "done" | null;
  /**
   * Providermetadata
   */
  providerMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
};

/**
 * ThinkingPart
 *
 * A thinking response from a model.
 */
export type ThinkingPart = {
  /**
   * Content
   */
  content: string;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Signature
   */
  signature?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "thinking";
};

/**
 * TicketRef
 */
export type TicketRef = {
  /**
   * Id
   */
  id: string;
  /**
   * Url
   */
  url: string;
};

/**
 * TiptapDocument
 */
export type TiptapDocumentInput = {
  /**
   * Type
   */
  type?: string;
  /**
   * Content
   */
  content?: Array<NodeInput>;
};

/**
 * TiptapDocument
 */
export type TiptapDocumentOutput = {
  /**
   * Type
   */
  type?: string;
  /**
   * Content
   */
  content?: Array<NodeOutput>;
};

/**
 * TodoSettings
 *
 * Settings for the task tracking toolset.
 *
 * Wraps pydantic-ai-todo's create_todo_toolset() with Noxus settings.
 * Todos are persisted in conversation.memory["todos"].
 */
export type TodoSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "todos";
  /**
   * Enable Subtasks
   *
   * Enable subtask hierarchy and dependency features
   */
  enable_subtasks?: boolean;
};

/**
 * TodoSettings
 *
 * Settings for the task tracking toolset.
 *
 * Wraps pydantic-ai-todo's create_todo_toolset() with Noxus settings.
 * Todos are persisted in conversation.memory["todos"].
 */
export type TodoSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "todos";
  /**
   * Enable Subtasks
   *
   * Enable subtask hierarchy and dependency features
   */
  enable_subtasks?: boolean;
};

/**
 * TogetherProviderData
 *
 * Deprecated. Kept only so existing JSONB rows deserialize.
 * See the comment above ``FireworksProviderData``.
 */
export type TogetherProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "together";
  /**
   * Together Api Key
   */
  TOGETHER_API_KEY?: string;
};

/**
 * TokenRequest
 */
export type TokenRequest = {
  /**
   * Grant Type
   */
  grant_type: "authorization_code" | "refresh_token";
  /**
   * Code
   */
  code?: string | null;
  /**
   * Code Verifier
   */
  code_verifier?: string | null;
  /**
   * Refresh Token
   */
  refresh_token?: string | null;
};

/**
 * ToolApprovalRequested
 *
 * Tool approval in requested state (awaiting user response).
 */
export type ToolApprovalRequested = {
  /**
   * Id
   */
  id: string;
};

/**
 * ToolApprovalRequestedPart
 *
 * Tool part in approval-requested state (awaiting user decision).
 */
export type ToolApprovalRequestedPart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "approval-requested";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * ToolApprovalResponded
 *
 * Tool approval in responded state (user has approved or denied).
 */
export type ToolApprovalResponded = {
  /**
   * Id
   */
  id: string;
  /**
   * Approved
   */
  approved: boolean;
  /**
   * Reason
   */
  reason?: string | null;
};

/**
 * ToolApprovalRespondedPart
 *
 * Tool part in approval-responded state (user approved/denied, execution pending).
 */
export type ToolApprovalRespondedPart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "approval-responded";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * ToolAssociationConfig
 *
 * Tool enforcement configuration for a guideline.
 */
export type ToolAssociationConfig = {
  /**
   * Tool Names
   */
  tool_names?: Array<string>;
  /**
   * Mode
   */
  mode?: string;
};

/**
 * ToolAvailabilityDeltaPart
 *
 * Records that the set of tools available to the model changed at this point.
 *
 * Additions only. Withdrawing a tool is not supported yet, because no provider can be told about one
 * without also invalidating the prompt cache this part exists to protect: Anthropic rejects a
 * reference to a tool the request doesn't declare, so a withdrawn tool has to leave the `tools`
 * array, and that is itself the invalidation. The name says *availability* rather than *addition* so
 * removals can join once they can be done cache-safely — see
 * https://github.com/pydantic/pydantic-ai/issues/6985.
 */
export type ToolAvailabilityDeltaPart = {
  /**
   * Tools Added
   */
  tools_added?: Array<string>;
  /**
   * Tool Call Id
   */
  tool_call_id?: string | null;
  /**
   * Part Kind
   */
  part_kind?: "tool-availability-delta";
};

/**
 * ToolCallPart
 *
 * A tool call from a model.
 */
export type ToolCallPart = {
  /**
   * Tool Name
   */
  tool_name: string;
  /**
   * Args
   */
  args?:
    | string
    | {
        [key: string]: unknown;
      }
    | null;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search" | "capability-load" | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "tool-call";
};

/**
 * ToolDisplayEntry
 */
export type ToolDisplayEntry = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  /**
   * Icon
   */
  icon: string;
};

/**
 * ToolInputAvailablePart
 *
 * Tool part in input-available state.
 */
export type ToolInputAvailablePart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "input-available";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * ToolInputStreamingPart
 *
 * Tool part in input-streaming state.
 */
export type ToolInputStreamingPart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "input-streaming";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * ToolOutputAvailablePart
 *
 * Tool part in output-available state.
 */
export type ToolOutputAvailablePart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "output-available";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Output
   */
  output?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Preliminary
   */
  preliminary?: boolean | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * ToolOutputDeniedPart
 *
 * Tool part in output-denied state (tool was denied, terminal state).
 */
export type ToolOutputDeniedPart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "output-denied";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

/**
 * ToolOutputErrorPart
 *
 * Tool part in output-error state.
 */
export type ToolOutputErrorPart = {
  /**
   * Type
   */
  type: string;
  /**
   * Toolcallid
   */
  toolCallId: string;
  /**
   * Title
   */
  title?: string | null;
  /**
   * State
   */
  state?: "output-error";
  /**
   * Input
   */
  input?: unknown | null;
  /**
   * Rawinput
   */
  rawInput?: unknown | null;
  /**
   * Errortext
   */
  errorText: string;
  /**
   * Providerexecuted
   */
  providerExecuted?: boolean | null;
  /**
   * Callprovidermetadata
   */
  callProviderMetadata?: {
    [key: string]: {
      [key: string]: unknown;
    };
  } | null;
  /**
   * Approval
   */
  approval?: ToolApprovalRequested | ToolApprovalResponded | null;
};

export type ToolReturnContent =
  | string
  | ({
      kind: "image-url";
    } & ImageUrl)
  | ({
      kind: "audio-url";
    } & AudioUrl)
  | ({
      kind: "document-url";
    } & DocumentUrl)
  | ({
      kind: "video-url";
    } & VideoUrl)
  | ({
      kind: "binary";
    } & BinaryContent)
  | ({
      kind: "uploaded-file";
    } & UploadedFile)
  | {
      [key: string]: ToolReturnContent;
    }
  | Array<ToolReturnContent>
  | unknown;

/**
 * ToolReturnPart
 *
 * A tool return message, this encodes the result of running a tool.
 */
export type ToolReturnPart = {
  /**
   * Tool Name
   */
  tool_name: string;
  content: ToolReturnContent;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search" | "capability-load" | null;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Part Kind
   */
  part_kind?: "tool-return";
};

/**
 * ToolSearchArgs
 *
 * Typed arguments for a tool-search call.
 *
 * Carried on
 * [`NativeToolSearchCallPart.args`][pydantic_ai.messages.NativeToolSearchCallPart.args]
 * (native server-side path) and
 * [`ToolSearchCallPart.args`][pydantic_ai.messages.ToolSearchCallPart.args]
 * (local-fallback path) as the canonical cross-provider shape. Each adapter
 * normalizes its provider's wire format into this shape on parse, and rebuilds the
 * wire format from this shape on emit.
 */
export type ToolSearchArgs = {
  /**
   * Queries
   */
  queries: Array<string>;
};

/**
 * ToolSearchCallPart
 *
 * Typed view of a [`ToolCallPart`][pydantic_ai.messages.ToolCallPart] for the local `search_tools` function call.
 *
 * Used on the local-fallback path (and as the synthetic-injection target on
 * non-native providers receiving cross-provider history). The native server-side
 * path uses
 * [`NativeToolSearchCallPart`][pydantic_ai.messages.NativeToolSearchCallPart]
 * instead.
 *
 * To detect a tool-search part regardless of execution path (native server-side
 * vs. local fallback), check `part.tool_kind == 'tool-search'` — this works
 * across both call/return and both server/local variants.
 *
 * Shadows `args` with the canonical typed shape. The `str` variant covers the
 * streaming / partial-args case before parsing completes; once parsed,
 * `args` is a [`ToolSearchArgs`][pydantic_ai.messages.ToolSearchArgs]
 * `TypedDict`.
 */
export type ToolSearchCallPart = {
  /**
   * Tool Name
   */
  tool_name?: "search_tools";
  /**
   * Args
   */
  args?: string | ToolSearchArgs | null;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search";
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "tool-call";
};

/**
 * ToolSearchMatch
 *
 * A single match in a tool-search result.
 */
export type ToolSearchMatch = {
  /**
   * Name
   */
  name: string;
};

/**
 * ToolSearchReturnContent
 *
 * Typed return value of the framework-managed tool-search builtin.
 *
 * Carried on
 * [`NativeToolSearchReturnPart.content`][pydantic_ai.messages.NativeToolSearchReturnPart.content]
 * (native server-side path) and
 * [`ToolSearchReturnPart.content`][pydantic_ai.messages.ToolSearchReturnPart.content]
 * (local-fallback path) as the canonical cross-provider shape.
 */
export type ToolSearchReturnContent = {
  /**
   * Discovered Tools
   */
  discovered_tools: Array<ToolSearchMatch>;
  /**
   * Message
   */
  message?: string;
};

/**
 * ToolSearchReturnPart
 *
 * Typed view of a [`ToolReturnPart`][pydantic_ai.messages.ToolReturnPart] for the local `search_tools` function return.
 *
 * Used on the local-fallback path (and as the synthetic-injection target on
 * non-native providers receiving cross-provider history). The native server-side
 * path uses
 * [`NativeToolSearchReturnPart`][pydantic_ai.messages.NativeToolSearchReturnPart]
 * instead.
 *
 * To detect a tool-search part regardless of execution path (native server-side
 * vs. local fallback), check `part.tool_kind == 'tool-search'` — this works
 * across both call/return and both server/local variants.
 *
 * Shadows `content` with a narrower
 * [`ToolSearchReturnContent`][pydantic_ai.messages.ToolSearchReturnContent]
 * `TypedDict`.
 */
export type ToolSearchReturnPart = {
  /**
   * Tool Name
   */
  tool_name?: "search_tools";
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search";
  content: ToolSearchReturnContent;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Part Kind
   */
  part_kind?: "tool-return";
};

/**
 * ToolUsageLine
 */
export type ToolUsageLine = {
  /**
   * Name
   */
  name: string;
  /**
   * Detail
   */
  detail?: string | null;
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Calls
   */
  calls: number;
  /**
   * Kind
   */
  kind?: "tool" | "workflow" | "agent";
  /**
   * Input Tokens
   */
  input_tokens?: number;
  /**
   * Output Tokens
   */
  output_tokens?: number;
  /**
   * Cost
   */
  cost?: number;
  /**
   * Cost Is Estimate
   */
  cost_is_estimate?: boolean;
  /**
   * Is Cached
   */
  is_cached?: boolean;
};

/**
 * TopTopic
 */
export type TopTopic = {
  /**
   * Label
   */
  label: string;
  /**
   * Count
   */
  count: number;
};

/**
 * TopTopics
 */
export type TopTopics = {
  /**
   * Topics
   */
  topics: Array<TopTopic>;
};

/**
 * TopupCredits
 */
export type TopupCredits = {
  /**
   * Credits
   */
  credits: number;
};

/**
 * TraceSpan
 */
export type TraceSpan = {
  /**
   * Start Time
   */
  start_time: string;
  /**
   * End Time
   */
  end_time: string | null;
  type: TraceType;
  /**
   * Data
   */
  data: {
    [key: string]: unknown;
  };
  /**
   * Grouping Key
   */
  grouping_key?: string | null;
};

/**
 * TraceType
 */
export const TraceType = {
  SUCCESS_NODE: "success_node",
  FAILED_NODE: "failed_node",
  TIMEOUT_NODE: "timeout_node",
  LOG: "log",
  MODEL: "model",
} as const;

/**
 * TraceType
 */
export type TraceType = (typeof TraceType)[keyof typeof TraceType];

/**
 * TrainStatus
 */
export const TrainStatus = {
  CREATED: "created",
  TRAINED: "trained",
  TRAINING: "training",
  ERROR: "error",
} as const;

/**
 * TrainStatus
 */
export type TrainStatus = (typeof TrainStatus)[keyof typeof TrainStatus];

/**
 * Trigger
 */
export type Trigger = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Name
   */
  name?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Routing Key
   */
  routing_key: string;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
  /**
   * Last Run At
   */
  last_run_at?: string | null;
  /**
   * Last Fired At
   */
  last_fired_at?: string | null;
  /**
   * Last Error
   */
  last_error?: {
    [key: string]: unknown;
  } | null;
};

/**
 * TriggerCompatibility
 */
export type TriggerCompatibility = {
  /**
   * Valid
   */
  valid: boolean;
  /**
   * Problems
   */
  problems: Array<string>;
  /**
   * Warnings
   */
  warnings: Array<string>;
};

/**
 * TriggerDefinition
 */
export type TriggerDefinition = {
  /**
   * Type
   */
  type: string;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description: string;
  /**
   * Image
   */
  image: string;
  /**
   * Color
   */
  color?: string | null;
  /**
   * Config
   */
  config: {
    [key: string]: unknown;
  };
  /**
   * Input Mapping
   */
  input_mapping: Array<TriggerInput> | null;
  /**
   * Integration
   */
  integration?: string | null;
  ready_info?: ReadyInfo | null;
};

/**
 * TriggerEvent
 */
export type TriggerEvent = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  state: TriggerQueueStatus;
  /**
   * Matched
   */
  matched: boolean;
  /**
   * Event Type
   */
  event_type: string;
  /**
   * Event Data
   */
  event_data: {
    [key: string]: unknown;
  };
  /**
   * Retries
   */
  retries: number;
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Error
   */
  error?: string | null;
  /**
   * Ignore Reason
   */
  ignore_reason?: string | null;
};

/**
 * TriggerInput
 */
export type TriggerInput = {
  /**
   * Value
   */
  value: string;
  /**
   * Label
   */
  label: string;
  type: DataType;
};

/**
 * TriggerList
 */
export type TriggerList = {
  /**
   * Triggers
   */
  triggers: Array<TriggerDefinition>;
};

/**
 * TriggerQueueStatus
 */
export const TriggerQueueStatus = {
  PENDING: "Pending",
  PROCESSING: "Processing",
  COMPLETE: "Complete",
  FAILED: "Failed",
} as const;

/**
 * TriggerQueueStatus
 */
export type TriggerQueueStatus =
  (typeof TriggerQueueStatus)[keyof typeof TriggerQueueStatus];

/**
 * TriggerSummary
 */
export type TriggerSummary = {
  /**
   * Name
   */
  name: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Polling Interval
   */
  polling_interval?: number;
};

/**
 * TriggerVersionUsage
 */
export type TriggerVersionUsage = {
  /**
   * Trigger Id
   */
  trigger_id: string;
  /**
   * Name
   */
  name: string | null;
  /**
   * Trigger Type
   */
  trigger_type: string;
};

/**
 * TypeAndKeysSetBy
 */
export type TypeAndKeysSetBy = {
  /**
   * Type
   */
  type?: "type_and_keys_set_by";
  /**
   * Config Source
   */
  config_source: string;
};

/**
 * TypeDefinition
 */
export type TypeDefinition = {
  data_type: DataType;
  /**
   * Is List
   */
  is_list?: boolean;
  /**
   * Schema
   */
  schema?: {
    [key: string]: unknown;
  } | null;
  /**
   * List Depth
   */
  list_depth?: number | null;
};

/**
 * TypeSafeProviderData
 */
export type TypeSafeProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "typesafe";
  /**
   * Typesafe Api Key
   */
  TYPESAFE_API_KEY: string;
  connect_button?: ApiToggleState;
  /**
   * Typesafe Base Url
   */
  TYPESAFE_BASE_URL?: string | null;
};

/**
 * TypeSetBy
 */
export type TypeSetBy = {
  /**
   * Type
   */
  type?: "type_set_by";
  /**
   * Config Source
   */
  config_source: string;
};

/**
 * UIMessage
 *
 * A message as displayed in the UI by Vercel AI Elements.
 */
export type UiMessage = {
  /**
   * Id
   */
  id: string;
  /**
   * Role
   */
  role: "system" | "user" | "assistant";
  /**
   * Metadata
   */
  metadata?: unknown | null;
  /**
   * Parts
   */
  parts: Array<
    | TextUiPart
    | ReasoningUiPart
    | ToolInputStreamingPart
    | ToolInputAvailablePart
    | ToolOutputAvailablePart
    | ToolOutputErrorPart
    | ToolApprovalRequestedPart
    | ToolApprovalRespondedPart
    | ToolOutputDeniedPart
    | DynamicToolInputStreamingPart
    | DynamicToolInputAvailablePart
    | DynamicToolOutputAvailablePart
    | DynamicToolOutputErrorPart
    | DynamicToolApprovalRequestedPart
    | DynamicToolApprovalRespondedPart
    | DynamicToolOutputDeniedPart
    | SourceUrlUiPart
    | SourceDocumentUiPart
    | FileUiPart
    | DataUiPart
    | StepStartUiPart
  >;
};

/**
 * Unset
 */
export type Unset = {
  [key: string]: unknown;
};

/**
 * UpdateAssistant
 */
export type UpdateAssistant = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  definition?: CoWorkerSettingsInput | null;
  /**
   * Visible
   */
  visible?: boolean | null;
  /**
   * Image Url
   */
  image_url?: string | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Is App Published
   */
  is_app_published?: boolean | null;
};

/**
 * UpdateColumnLabelRequest
 */
export type UpdateColumnLabelRequest = {
  /**
   * Label
   */
  label: string | null;
};

/**
 * UpdateConnection
 */
export type UpdateConnection = {
  /**
   * Payload
   */
  payload: {
    [key: string]: JsonValueInput;
  };
};

/**
 * UpdateConversation
 */
export type UpdateConversation = {
  /**
   * Name
   */
  name?: string | null;
  settings?: CoWorkerSettingsInput | null;
  /**
   * Is Deleted
   */
  is_deleted?: boolean;
};

/**
 * UpdateCredentialRequest
 */
export type UpdateCredentialRequest = {
  /**
   * Payload
   */
  payload: {
    [key: string]: unknown;
  };
  /**
   * Display Name
   */
  display_name?: string | null;
};

/**
 * UpdateCredentialSettingsRequest
 *
 * Update per-credential disabled nodes/triggers.
 */
export type UpdateCredentialSettingsRequest = {
  /**
   * Disabled Nodes
   */
  disabled_nodes?: Array<string> | null;
  /**
   * Disabled Triggers
   */
  disabled_triggers?: Array<string> | null;
};

/**
 * UpdateDocument
 */
export type UpdateDocument = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Summary
   */
  summary?: string | null;
  /**
   * Short Summary
   */
  short_summary?: string | null;
  status?: DocumentStatus | null;
  /**
   * Doc Type
   */
  doc_type?: string | null;
  /**
   * Error
   */
  error?: {
    [key: string]: unknown;
  } | null;
  /**
   * Warnings
   */
  warnings?: Array<SpotWarning> | null;
  /**
   * Prefix
   */
  prefix?: string | null;
  /**
   * File Id
   */
  file_id?: string | null;
  /**
   * Dismissed
   */
  dismissed?: boolean | null;
  /**
   * Needs Reingestion
   */
  needs_reingestion?: boolean | null;
  /**
   * Doc Metadata
   */
  doc_metadata?: {
    [key: string]: unknown;
  } | null;
};

/**
 * UpdateEnabledToolsRequest
 */
export type UpdateEnabledToolsRequest = {
  /**
   * Enabled Tools
   */
  enabled_tools: Array<string>;
};

/**
 * UpdateEvaluatorRequest
 *
 * Request to update an evaluator.
 */
export type UpdateEvaluatorRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Expected Output
   */
  expected_output?: {
    [key: string]: JsonValueInput;
  } | null;
};

/**
 * UpdateIntegrationSettingsRequest
 *
 * Update platform-level settings for one integration.
 */
export type UpdateIntegrationSettingsRequest = {
  /**
   * Enabled
   */
  enabled?: boolean | null;
  /**
   * Disabled Nodes
   */
  disabled_nodes?: Array<string> | null;
  /**
   * Disabled Triggers
   */
  disabled_triggers?: Array<string> | null;
};

/**
 * UpdateKnowledgeBase
 */
export type UpdateKnowledgeBase = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  status?: TrainStatus | null;
  /**
   * Document Types
   */
  document_types?: Array<string> | null;
  kb_type?: KbType | null;
  /**
   * Error
   */
  error?: {
    [key: string]: unknown;
  } | null;
  /**
   * Summary
   */
  summary?: string | null;
  /**
   * Summary Embedding
   */
  summary_embedding?: Array<number> | null;
  /**
   * Needs Summary Update
   */
  needs_summary_update?: boolean | null;
  settings_?: KbConfig | null;
  /**
   * Version
   */
  version?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
};

/**
 * UpdateLabellingTask
 */
export type UpdateLabellingTask = {
  /**
   * Outputs
   */
  outputs: {
    [key: string]: unknown;
  };
};

/**
 * UpdateLinkRequest
 */
export type UpdateLinkRequest = {
  /**
   * Priority
   */
  priority?: number | null;
  /**
   * Is Active
   */
  is_active?: boolean | null;
};

/**
 * UpdatePeriodicTaskRequest
 */
export type UpdatePeriodicTaskRequest = {
  /**
   * Message
   */
  message?: string | null;
  /**
   * Frequency
   */
  frequency?: "hourly" | "daily" | "weekly" | "monthly" | null;
  /**
   * New Conversation
   */
  new_conversation?: boolean | null;
  /**
   * One Shot
   */
  one_shot?: boolean | null;
  /**
   * Time Of Day
   */
  time_of_day?: string | null;
  /**
   * Day Of Week
   */
  day_of_week?: string | null;
  /**
   * Day Of Month
   */
  day_of_month?: number | null;
  /**
   * Offset
   */
  offset?: number | null;
};

/**
 * UpdateSyncRequest
 */
export type UpdateSyncRequest = {
  /**
   * Watched Items
   */
  watched_items?: Array<WatchedItemSchema> | null;
  schedule?: KbSyncScheduleInput | null;
  /**
   * Delete On Source Removal
   */
  delete_on_source_removal?: boolean | null;
  /**
   * Resource Label
   */
  resource_label?: string | null;
};

/**
 * UpdateTableRequest
 *
 * ``meta`` is merged key-by-key into the table's existing meta, so a caller
 * only sends the keys it owns.
 */
export type UpdateTableRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Meta
   */
  meta?: {
    [key: string]: unknown;
  } | null;
};

/**
 * UpdateTagRequest
 */
export type UpdateTagRequest = {
  /**
   * Name
   *
   * Tag name
   */
  name?: string | null;
  /**
   * Color
   *
   * Hex color code
   */
  color?: string | null;
  /**
   * Description
   *
   * Tag description
   */
  description?: string | null;
};

/**
 * UpdateTestCaseRequest
 *
 * Request to update a test case.
 *
 * Accepts any format — normalization is handled by the service layer.
 */
export type UpdateTestCaseRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Inputs
   */
  inputs?: {
    [key: string]: unknown;
  } | null;
  /**
   * Expected Outputs
   */
  expected_outputs?: {
    [key: string]: unknown;
  } | null;
};

/**
 * UpdateTriggerRequest
 */
export type UpdateTriggerRequest = {
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Name
   */
  name?: string | null | Unset;
  /**
   * Workflow Version Id
   */
  workflow_version_id?: string | null;
};

/**
 * UpdateWorkflowRequest
 */
export type UpdateWorkflowRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  type?: WorkflowType | null;
  /**
   * Saved
   */
  saved?: boolean | null;
  /**
   * Definition
   */
  definition?: {
    [key: string]: unknown;
  } | null;
  /**
   * Error Handler
   */
  error_handler?: string | Unset | null;
  meta?: WorkflowMeta | null;
  /**
   * Workflow Hash
   */
  workflow_hash?: string | null;
  /**
   * Is App Published
   */
  is_app_published?: boolean | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Expected Workflow Id
   */
  expected_workflow_id?: string | null;
  /**
   * Base Revision
   */
  base_revision?: number | null;
};

/**
 * UpgradeRequest
 *
 * Request to upgrade a tenant's subscription (backoffice only).
 */
export type UpgradeRequest = {
  /**
   * Subscription Plan
   *
   * Target subscription plan
   */
  subscription_plan: string;
  /**
   * Custom Price
   *
   * Custom price ID
   */
  custom_price?: string | null;
  /**
   * Force
   */
  force?: boolean;
};

/**
 * UpgradeResponse
 *
 * Response containing checkout link for upgrade.
 */
export type UpgradeResponse = {
  /**
   * Checkout Url
   */
  checkout_url: string;
  /**
   * Subscription Plan
   */
  subscription_plan: string;
  /**
   * Estimated Price
   */
  estimated_price?: number | null;
};

/**
 * UpgradeToNCSResponse
 */
export type UpgradeToNcsResponse = {
  /**
   * Success
   */
  success: boolean;
  /**
   * Upgraded
   */
  upgraded: number;
  /**
   * Skipped
   */
  skipped: number;
  /**
   * Failed
   */
  failed: number;
  /**
   * Message
   */
  message: string;
};

/**
 * UploadPluginSource
 *
 * Upload plugin source for uploaded files
 */
export type UploadPluginSource = {
  /**
   * Type
   */
  type?: "upload";
  /**
   * File Id
   *
   * File ID to download from platform
   */
  file_id: string;
  /**
   * Filename
   *
   * Original filename of the uploaded plugin
   */
  filename: string;
};

/**
 * UploadedFile
 *
 * A reference to a file uploaded to a provider's file storage by ID.
 *
 * This allows referencing files that have been uploaded via provider-specific file APIs
 * rather than providing the file content directly.
 *
 * Supported by:
 *
 * - [`AnthropicModel`][pydantic_ai.models.anthropic.AnthropicModel]
 * - [`OpenAIChatModel`][pydantic_ai.models.openai.OpenAIChatModel]
 * - [`OpenAIResponsesModel`][pydantic_ai.models.openai.OpenAIResponsesModel]
 * - [`BedrockConverseModel`][pydantic_ai.models.bedrock.BedrockConverseModel]
 * - [`GoogleModel`][pydantic_ai.models.google.GoogleModel] (Gemini API: [Files API](https://ai.google.dev/gemini-api/docs/files) URIs, Google Cloud: GCS `gs://` URIs)
 * - [`XaiModel`][pydantic_ai.models.xai.XaiModel]
 */
export type UploadedFile = {
  /**
   * File Id
   */
  file_id: string;
  /**
   * Provider Name
   */
  provider_name:
    | "anthropic"
    | "openai"
    | "google"
    | "google-cloud"
    | "google-gla"
    | "google-vertex"
    | "bedrock"
    | "xai";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "uploaded-file";
  /**
   * Media Type
   *
   * Return the media type of the file, inferred from `file_id` if not explicitly provided.
   *
   * Note: Inference relies on the file extension in `file_id`.
   * For opaque file IDs (e.g., `'file-abc123'`), the media type will default to `'application/octet-stream'`.
   * Inference relies on Python's `mimetypes` module, whose results may vary across platforms.
   *
   * Required by some providers (e.g., Bedrock) for certain file types.
   */
  readonly media_type: string;
  /**
   * Identifier
   *
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `UploadedFile`.
   *
   * This identifier is only automatically passed to the model when the `UploadedFile` is returned by a tool.
   * If you're passing the `UploadedFile` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `UploadedFile`.
   */
  readonly identifier: string;
};

/**
 * UpstreamDelete
 */
export const UpstreamDelete = {
  IGNORE_WARN: "ignore_warn",
  SOFT_DELETE: "soft_delete",
} as const;

/**
 * UpstreamDelete
 */
export type UpstreamDelete =
  (typeof UpstreamDelete)[keyof typeof UpstreamDelete];

/**
 * User
 */
export type User = {
  /**
   * Auth Id
   */
  auth_id: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Domain
   */
  domain?: string | null;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Color Scheme
   */
  color_scheme?: string | null;
  genie_mode?: GenieMode;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Categories
   */
  categories?: Array<string> | unknown | null;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
  /**
   * Metadata
   */
  metadata_?: {
    [key: string]: string;
  } | null;
  /**
   * Id
   */
  id: string;
  /**
   * Tenant Id
   */
  tenant_id: string;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Admin
   */
  is_admin: boolean;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Last Login
   */
  last_login: string | null;
  /**
   * Role
   */
  role: string;
  role_info?: RoleInfo | null;
  /**
   * Is Pending
   */
  is_pending?: boolean;
};

/**
 * UserAddRequest
 */
export type UserAddRequest = {
  /**
   * Email
   */
  email: string;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Role
   */
  role: string;
  /**
   * Groups
   */
  groups: Array<GroupAddRequest>;
  /**
   * Create Personal Workspace
   */
  create_personal_workspace?: boolean;
};

/**
 * UserCreateRequest
 */
export type UserCreateRequest = {
  /**
   * Auth Id
   */
  auth_id: string | null;
  /**
   * Email
   */
  email: string;
  /**
   * Domain
   */
  domain?: string | null;
  /**
   * Display Name
   */
  display_name: string;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Color Scheme
   */
  color_scheme?: string | null;
  genie_mode?: GenieMode;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Categories
   */
  categories?: Array<string> | unknown | null;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
  /**
   * Metadata
   */
  metadata_?: {
    [key: string]: string;
  } | null;
  /**
   * Organization
   */
  organization: string;
  /**
   * Reserve Domain
   */
  reserve_domain?: boolean;
};

/**
 * UserGroupRoleCreate
 *
 * Schema for creating a user group role.
 */
export type UserGroupRoleCreate = {
  /**
   * Name
   *
   * Role name
   */
  name: string;
  /**
   * Description
   *
   * Role description
   */
  description?: string | null;
  /**
   * Permissions
   *
   * Permission settings
   */
  permissions: {
    [key: string]: boolean;
  };
  /**
   * Meta
   *
   * UI meta (color, icon, etc.)
   */
  meta?: {
    [key: string]: string;
  } | null;
  /**
   * Is Global
   *
   * Whether the role is visible in all workspaces
   */
  is_global?: boolean;
};

/**
 * UserGroupRoleResponse
 *
 * Schema for user group role response.
 */
export type UserGroupRoleResponse = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string | null;
  /**
   * Tenant Id
   */
  tenant_id?: string | null;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description: string | null;
  /**
   * Is Default
   */
  is_default: boolean;
  /**
   * Is System
   */
  is_system: boolean;
  /**
   * Is Global
   */
  is_global: boolean;
  /**
   * Permissions
   */
  permissions: {
    [key: string]: boolean;
  };
  /**
   * Meta
   */
  meta: {
    [key: string]: string;
  } | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * UserGroupRoleUpdate
 *
 * Schema for updating a user group role.
 */
export type UserGroupRoleUpdate = {
  /**
   * Name
   *
   * Role name
   */
  name?: string | null;
  /**
   * Description
   *
   * Role description
   */
  description?: string | null;
  /**
   * Permissions
   *
   * Permission settings
   */
  permissions?: {
    [key: string]: boolean;
  } | null;
  /**
   * Meta
   *
   * UI meta (color, icon, etc.)
   */
  meta?: {
    [key: string]: string;
  } | null;
  /**
   * Is Global
   *
   * Whether the role is visible in all workspaces
   */
  is_global?: boolean | null;
};

/**
 * UserMetadataUpdateRequest
 */
export type UserMetadataUpdateRequest = {
  /**
   * Metadata
   */
  metadata_: {
    [key: string]: string;
  };
};

/**
 * UserOrderColumn
 */
export const UserOrderColumn = {
  CREATED_AT: "created_at",
  LAST_LOGIN: "last_login",
  DISPLAY_NAME: "display_name",
  EMAIL: "email",
  ROLE: "role",
} as const;

/**
 * UserOrderColumn
 */
export type UserOrderColumn =
  (typeof UserOrderColumn)[keyof typeof UserOrderColumn];

/**
 * UserPermissionsResponse
 *
 * Schema for current user's permissions.
 */
export type UserPermissionsResponse = {
  /**
   * Tenant Permissions
   *
   * Tenant-level permissions
   */
  tenant_permissions: {
    [key: string]: boolean;
  };
  /**
   * Group Permissions
   *
   * Group-level permissions (if group_id provided)
   */
  group_permissions?: {
    [key: string]: boolean;
  } | null;
  /**
   * Role Name
   *
   * Assigned role name
   */
  role_name?: string | null;
  /**
   * Has Custom Overrides
   *
   * Whether user has custom permission overrides
   */
  has_custom_overrides?: boolean;
};

/**
 * UserPromptPart
 *
 * A user prompt, generally written by the end user.
 *
 * Content comes from the `user_prompt` parameter of [`Agent.run`][pydantic_ai.agent.AbstractAgent.run],
 * [`Agent.run_sync`][pydantic_ai.agent.AbstractAgent.run_sync], and [`Agent.run_stream`][pydantic_ai.agent.AbstractAgent.run_stream].
 */
export type UserPromptPart = {
  /**
   * Content
   */
  content:
    | string
    | Array<
        | string
        | TextContent
        | ({
            kind: "image-url";
          } & ImageUrl)
        | ({
            kind: "audio-url";
          } & AudioUrl)
        | ({
            kind: "document-url";
          } & DocumentUrl)
        | ({
            kind: "video-url";
          } & VideoUrl)
        | ({
            kind: "binary";
          } & BinaryContent)
        | ({
            kind: "uploaded-file";
          } & UploadedFile)
        | CachePoint
      >;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Part Kind
   */
  part_kind?: "user-prompt";
};

/**
 * UserUpdateProfileRequest
 */
export type UserUpdateProfileRequest = {
  /**
   * Display Name
   */
  display_name?: string | null;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Settings
   */
  settings?: {
    [key: string]: unknown;
  } | null;
  genie_mode?: GenieMode | null;
};

/**
 * UserUpdateRequest
 */
export type UserUpdateRequest = {
  /**
   * Role
   */
  role: string;
};

/**
 * UserWithGroup
 */
export type UserWithGroup = {
  user: User;
  /**
   * Groups
   */
  groups: Array<GroupWithRole>;
};

/**
 * UserWithGroupRole
 */
export type UserWithGroupRole = {
  user: User;
  /**
   * Role
   */
  role: string;
};

/**
 * ValidateRecaptchaRequest
 */
export type ValidateRecaptchaRequest = {
  /**
   * Token
   */
  token: string;
};

/**
 * ValidateWorkflowRequest
 */
export type ValidateWorkflowRequest = {
  /**
   * Definition
   */
  definition?: {
    [key: string]: unknown;
  } | null;
};

/**
 * ValidationError
 */
export type ValidationError = {
  /**
   * Location
   */
  loc: Array<string | number>;
  /**
   * Message
   */
  msg: string;
  /**
   * Error Type
   */
  type: string;
  /**
   * Input
   */
  input?: unknown;
  /**
   * Context
   */
  ctx?: {
    [key: string]: unknown;
  };
};

/**
 * ValidationResponse
 */
export type ValidationResponse = {
  /**
   * Errors
   */
  errors?: {
    [key: string]: NoxusValidationMessage;
  } | null;
  /**
   * Warnings
   */
  warnings?: {
    [key: string]: NoxusValidationMessage;
  } | null;
  /**
   * Infos
   */
  infos?: {
    [key: string]: NoxusValidationMessage;
  } | null;
};

/**
 * ValidationResult
 */
export type ValidationResult = {
  /**
   * Valid
   */
  valid: boolean;
  /**
   * Errors
   */
  errors?: Array<string>;
  /**
   * Warnings
   */
  warnings?: Array<string>;
};

/**
 * ValidityDuration
 */
export const ValidityDuration = {
  "1H": "1h",
  "1D": "1d",
  "1W": "1w",
  "1M": "1m",
  "1Y": "1y",
  "10Y": "10y",
} as const;

/**
 * ValidityDuration
 */
export type ValidityDuration =
  (typeof ValidityDuration)[keyof typeof ValidityDuration];

/**
 * ValueChange
 */
export type ValueChange = {
  /**
   * Kind
   */
  kind?: "value";
  /**
   * Key
   */
  key: string;
  /**
   * Label
   */
  label: string;
  before?: PydanticTypesJsonValue;
  after?: PydanticTypesJsonValue;
};

/**
 * VariableConnector
 */
export type VariableConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "variable_connector";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
  /**
   * Keys
   */
  keys?: Array<string>;
  /**
   * Manual
   */
  manual?: boolean;
  /**
   * Allow Empty
   */
  allow_empty?: boolean;
};

/**
 * VariableDefinition
 *
 * A user-defined variable available to the agent and its tools.
 *
 * Values are supplied at runtime (e.g. by the embed host page), so a variable
 * is just a name.
 */
export type VariableDefinition = {
  /**
   * Name
   */
  name: string;
};

/**
 * VariablePatchRequest
 *
 * Partial-update payload — same shape, all fields optional.
 */
export type VariablePatchRequest = {
  /**
   * Name
   */
  name?: string | null;
  /**
   * Value Type
   */
  value_type?:
    | "string"
    | "number"
    | "boolean"
    | "object"
    | "datetime"
    | "file"
    | null;
  /**
   * Source
   */
  source?: "inline" | "vault" | "environment" | null;
  /**
   * Inline Value
   */
  inline_value?: string | null;
  /**
   * Vault Credential Id
   */
  vault_credential_id?: string | null;
  /**
   * Vault Path
   */
  vault_path?: string | null;
  /**
   * Vault Key
   */
  vault_key?: string | null;
  /**
   * Env Var Name
   */
  env_var_name?: string | null;
};

/**
 * VariableSourcesResponse
 *
 * What the deployment lets a workspace variable be backed by, so the
 * form can grey out the environment option with the right explanation.
 */
export type VariableSourcesResponse = {
  environment: EnvironmentSourceAvailability;
};

/**
 * VariableTypeConnector
 */
export type VariableTypeConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "variable_type_connector";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
  /**
   * Choices
   */
  choices?: Array<TypeDefinition>;
};

/**
 * VariableTypeFlowInputConnector
 */
export type VariableTypeFlowInputConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "variable_type_input";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
  /**
   * Choices
   */
  choices?: Array<TypeDefinition>;
  /**
   * Configured By
   */
  configured_by?: string | null;
};

/**
 * VariableTypeFlowOutputConnector
 */
export type VariableTypeFlowOutputConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "variable_type_output";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
  /**
   * Choices
   */
  choices?: Array<TypeDefinition>;
  /**
   * Configured By
   */
  configured_by?: string | null;
};

/**
 * VariableTypeSizeConnector
 */
export type VariableTypeSizeConnector = {
  /**
   * Name
   */
  name: string;
  /**
   * Label
   */
  label: string;
  definition: TypeDefinition;
  /**
   * Type
   */
  type?: "variable_type_size_connector";
  /**
   * Propagates List
   */
  propagates_list?: boolean;
  /**
   * Optional
   */
  optional?: boolean;
  /**
   * Rules
   */
  rules?: Array<TypeAndKeysSetBy | TypeSetBy>;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Tool Mode
   */
  tool_mode?: "llm" | "user" | "hidden";
  /**
   * Tool Argument
   */
  tool_argument?: boolean;
  /**
   * Tool Argument Description
   */
  tool_argument_description?: string | null;
  /**
   * Tool Argument Optional
   */
  tool_argument_optional?: boolean;
  /**
   * Type Definitions
   */
  type_definitions?: {
    [key: string]: TypeDefinition;
  };
  /**
   * Choices
   */
  choices?: Array<TypeDefinition>;
  /**
   * Keys
   */
  keys?: Array<string>;
  /**
   * Manual
   */
  manual?: boolean;
  /**
   * Gathered Keys
   */
  gathered_keys?: Array<string>;
};

/**
 * VariableUpsertRequest
 *
 * Create payload — used by both group and workflow CRUD.
 */
export type VariableUpsertRequest = {
  /**
   * Name
   */
  name: string;
  /**
   * Kind
   */
  kind?: "variable" | "secret";
  /**
   * Value Type
   */
  value_type?: "string" | "number" | "boolean" | "object" | "datetime" | "file";
  /**
   * Source
   */
  source?: "inline" | "vault" | "environment";
  /**
   * Inline Value
   */
  inline_value?: string | null;
  /**
   * Vault Credential Id
   */
  vault_credential_id?: string | null;
  /**
   * Vault Path
   */
  vault_path?: string | null;
  /**
   * Vault Key
   */
  vault_key?: string | null;
  /**
   * Env Var Name
   */
  env_var_name?: string | null;
};

/**
 * VaultTestRequest
 *
 * Common test-vault probe shape.
 */
export type VaultTestRequest = {
  /**
   * Vault Credential Id
   */
  vault_credential_id: string;
  /**
   * Vault Path
   */
  vault_path: string;
  /**
   * Vault Key
   */
  vault_key?: string | null;
};

/**
 * VersionMeta
 */
export type VersionMeta = {
  source?: EntitySource | null;
};

/**
 * VertexProviderData
 */
export type VertexProviderData = {
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Enabled Models
   */
  enabled_models?: Array<string>;
  /**
   * Enabled Embeddings
   */
  enabled_embeddings?: Array<string>;
  /**
   * Enabled Rerankers
   */
  enabled_rerankers?: Array<string>;
  /**
   * Enabled Decision Models
   */
  enabled_decision_models?: Array<string>;
  /**
   * Type
   */
  type?: "vertex";
  /**
   * Auth Mode
   */
  auth_mode?: string;
  /**
   * Project Id
   */
  project_id?: string;
  /**
   * Locations
   */
  locations?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Api Key
   */
  api_key?: string;
  /**
   * Service Account File
   */
  service_account_file?: string | null;
  /**
   * Base Url
   */
  base_url?: string;
  connect_button?: ApiToggleState;
  /**
   * Divider
   */
  divider?: string;
};

/**
 * VideoUrl
 *
 * A URL to a video.
 */
export type VideoUrl = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "video-url";
  /**
   * Media Type
   *
   * Return the media type of the file, based on the URL or the provided `media_type`.
   */
  readonly media_type: string;
  /**
   * Identifier
   *
   * The identifier of the file, such as a unique ID.
   *
   * This identifier can be provided to the model in a message to allow it to refer to this file in a tool call argument,
   * and the tool can look up the file in question by iterating over the message history and finding the matching `FileUrl`.
   *
   * This identifier is only automatically passed to the model when the `FileUrl` is returned by a tool.
   * If you're passing the `FileUrl` as a user message, it's up to you to include a separate text part with the identifier,
   * e.g. "This is file <identifier>:" preceding the `FileUrl`.
   *
   * It's also included in inline-text delimiters for providers that require inlining text documents, so the model can
   * distinguish multiple files.
   */
  readonly identifier: string;
};

/**
 * VisibilityUpdate
 */
export type VisibilityUpdate = {
  /**
   * Visible
   */
  visible: boolean;
};

/**
 * VoiceMode
 */
export const VoiceMode = {
  SPEECH_TO_TEXT: "speech_to_text",
  TEXT_TO_SPEECH: "text_to_speech",
} as const;

/**
 * VoiceMode
 */
export type VoiceMode = (typeof VoiceMode)[keyof typeof VoiceMode];

/**
 * VoiceModelConfigurationResponse
 */
export type VoiceModelConfigurationResponse = {
  /**
   * Voice Model Id
   */
  voice_model_id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Creator
   */
  creator: string;
  mode: VoiceMode;
  /**
   * Capabilities
   */
  capabilities: Array<string>;
  /**
   * Compatible Providers
   */
  compatible_providers: Array<string>;
  /**
   * Is Deprecated
   */
  is_deprecated: boolean;
  /**
   * Visible
   */
  visible: boolean;
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Is Healthy
   */
  is_healthy: boolean;
  /**
   * Image
   */
  image: string | null;
  /**
   * Provider Links
   */
  provider_links: Array<ProviderLinkResponse>;
  /**
   * Voices
   */
  voices: Array<VoiceOptionRef>;
  /**
   * Output Formats
   */
  output_formats: Array<string>;
  /**
   * Input Media Types
   */
  input_media_types: Array<string>;
};

/**
 * VoiceModelsDefinition
 */
export type VoiceModelsDefinition = {
  /**
   * Name
   */
  name: string;
  /**
   * Slug
   */
  slug?: string | null;
  /**
   * Title
   */
  title: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Model Type
   */
  model_type: string;
  /**
   * Model Category
   */
  model_category?: string | null;
  /**
   * Properties
   */
  properties: LlmModelProperties | ModelProperties;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider
   */
  provider?: string | null;
  /**
   * Provider Id
   */
  provider_id?: string | null;
  /**
   * Provider Image
   */
  provider_image?: string | null;
  /**
   * Providers
   */
  providers?: Array<ProviderRef>;
  /**
   * Host
   */
  host?: string | null;
  /**
   * Location
   */
  location?: string | null;
  /**
   * Price
   */
  price?: string | null;
  /**
   * Release Date
   */
  release_date?: string | null;
  /**
   * Data Cutoff Date
   */
  data_cutoff_date?: string | null;
  /**
   * Retirement Date
   */
  retirement_date?: string | null;
  /**
   * Replaced By
   */
  replaced_by?: string | null;
  /**
   * Privacy Level
   */
  privacy_level?: string | null;
  /**
   * Visible
   */
  visible?: boolean;
  /**
   * Is Available
   */
  is_available?: boolean;
  /**
   * Capabilities
   */
  capabilities?: Array<ModelCapability>;
  /**
   * Is Active
   */
  is_active?: boolean;
  /**
   * Provider Is Active
   */
  provider_is_active?: boolean;
  /**
   * Has Link
   */
  has_link?: boolean;
  /**
   * Is Valid
   */
  is_valid?: boolean;
  /**
   * Is Healthy
   */
  is_healthy?: boolean;
  /**
   * Lifecycle State
   */
  lifecycle_state?: string;
  /**
   * Last Error
   */
  last_error?: string | null;
  /**
   * Consecutive Failures
   */
  consecutive_failures?: number;
  /**
   * Last Checked At
   */
  last_checked_at?: string | null;
  mode?: VoiceMode;
  /**
   * Voices
   */
  voices?: Array<VoiceOptionRef>;
  /**
   * Default Voice
   */
  default_voice?: string | null;
  /**
   * Output Formats
   */
  output_formats?: Array<string>;
  /**
   * Output Sample Rate
   */
  output_sample_rate?: number | null;
  /**
   * Max Characters
   */
  max_characters?: number | null;
  /**
   * Max Input Seconds
   */
  max_input_seconds?: number | null;
};

/**
 * VoiceOptionRef
 */
export type VoiceOptionRef = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Language
   */
  language?: string | null;
};

/**
 * WarningIdentifier
 */
export const WarningIdentifier = {
  GENERAL: "general",
  MODEL_FALLBACK: "model_fallback",
  MODEL_TIMEOUT: "model_timeout",
  READING_SPREADSHEET: "reading_spreadsheet",
  INVALID_SEARCH: "invalid_search",
  NO_RESULTS_RETURNED: "no_results_returned",
  INTEGRATION_WARNING: "integration_warning",
  FAILED_SCRAPING: "failed_scraping",
  INVALID_VALUE: "invalid_value",
  CONTINUE_ON_ERROR: "continue_on_error",
  DATA_INGESTION: "data_ingestion",
  DOCUMENT_ALREADY_EXISTS: "document_already_exists",
  INGESTION_TIMEOUT: "ingestion_timeout",
  LIBREOFFICE_CONVERSION: "libreoffice_conversion",
  LOW_CONFIDENCE: "low_confidence",
  GUARDRAIL_VALIDATION: "guardrail_validation",
  KB_CREATION: "kb_creation",
  KB_EMPTY: "kb_empty",
  UNSUPPORTED_QA_FILE_TYPE: "unsupported_qa_file_type",
  UNSUPPORTED_FILE_TYPE: "unsupported_file_type",
  NO_FILES_TO_ADD_TO_KB: "no_files_to_add_to_kb",
  NO_FILES_ADDED_TO_KB: "no_files_added_to_kb",
  NO_GOOGLE_DRIVE_FILES: "no_google_drive_files",
  NO_ONE_DRIVE_FILES: "no_one_drive_files",
  NO_SHAREPOINT_FILES: "no_sharepoint_files",
  NO_WEBSITES_SCRAPED: "no_websites_scraped",
  WEBSITES_FAILED_SCRAPING: "websites_failed_scraping",
  EMPTY_FILE: "empty_file",
  DOCUMENT_OVERWRITE: "document_overwrite",
  NO_CONTENT_PROVIDED: "no_content_provided",
  INVALID_FOLDER: "invalid_folder",
  DOCUMENT_NAME_TOO_LONG: "document_name_too_long",
  RETRY_WARNING: "retry_warning",
  TABULAR_TRUNCATED: "tabular_truncated",
  RESULTS_TRUNCATED: "results_truncated",
  NESTED_LIST_FLATTENED: "nested_list_flattened",
  ITERATION_FAILURES: "iteration_failures",
  PASSWORD_PROTECTED: "password_protected",
  MALFORMED_DOCUMENT: "malformed_document",
  UNREADABLE_ENCODING: "unreadable_encoding",
} as const;

/**
 * WarningIdentifier
 */
export type WarningIdentifier =
  (typeof WarningIdentifier)[keyof typeof WarningIdentifier];

/**
 * WatchedItemSchema
 *
 * One pinned item the sync is watching.
 *
 * ``kb_doc_id`` is the live link to the KB document this watched item
 * materialised as. Starts ``None`` when sync is enabled (docs don't
 * exist yet); the engine stamps it on the first poll and onward by
 * matching the source id against ``KnowledgeBaseDocument.m_source_external_id``.
 * The FE uses this id to highlight which docs in the explorer are
 * governed by which sync (no walking parent prefix chains required).
 */
export type WatchedItemSchema = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Type
   */
  type: string;
  /**
   * Kb Doc Id
   */
  kb_doc_id?: string | null;
  /**
   * Drive Id
   */
  drive_id?: string | null;
};

/**
 * WebReferenceMetadata
 */
export type WebReferenceMetadata = {
  /**
   * Type
   */
  type?: "web";
  /**
   * Url
   */
  url?: string | null;
  /**
   * Image Url
   */
  image_url?: string | null;
  /**
   * Quote
   */
  quote?: string | null;
  /**
   * Query
   */
  query?: string | null;
  /**
   * Ref Num
   */
  ref_num?: number | null;
};

/**
 * WebResearchSettings
 */
export type WebResearchSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "web_research";
};

/**
 * WebResearchSettings
 */
export type WebResearchSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   *
   * The category of the tool
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Icon
   */
  icon?: string;
  /**
   * Bg Color
   */
  bg_color?: string;
  /**
   * Icon Color
   */
  icon_color?: string;
  /**
   * Deprecated
   *
   * Whether this tool type is deprecated and should be hidden from the UI
   */
  deprecated?: boolean;
  /**
   * Type
   */
  type?: "web_research";
};

/**
 * WorkerHealthOverride
 */
export type WorkerHealthOverride = {
  /**
   * Enabled
   */
  enabled?: boolean | null;
  /**
   * Queue
   */
  queue?: string | null;
  /**
   * Workspace Id
   *
   * Workspace group_id for fair-scheduling filters; null uses runtime subscription settings.
   */
  workspace_id?: string | null;
};

/**
 * WorkerInfo
 */
export type WorkerInfo = {
  /**
   * Id
   */
  id: number;
  /**
   * Last Heartbeat
   */
  last_heartbeat: string;
};

/**
 * WorkerPoolSummary
 */
export type WorkerPoolSummary = {
  /**
   * Total Workers
   */
  total_workers: number;
  /**
   * Total Active Tasks
   */
  total_active_tasks: number;
  /**
   * Workers
   */
  workers: Array<WorkerInfo>;
};

/**
 * Workflow
 */
export type Workflow = {
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  type: WorkflowType;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Workflow Hash
   */
  workflow_hash?: string | null;
  /**
   * Revision
   */
  revision?: number;
  meta?: WorkflowMeta | null;
  /**
   * Last Updated At
   */
  last_updated_at?: string | null;
  /**
   * Last Run At
   */
  last_run_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Deleted At
   */
  deleted_at?: string | null;
  /**
   * Runs Count
   */
  runs_count?: number | null;
  /**
   * Author Name
   */
  author_name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Saved
   */
  saved?: boolean | null;
  /**
   * Error Handler
   */
  error_handler?: string | null;
  /**
   * Is App Published
   */
  is_app_published?: boolean | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Flow Version
   */
  flow_version?: string;
};

/**
 * WorkflowDefinition
 */
export type WorkflowDefinition = {
  /**
   * Nodes
   */
  nodes: Array<NodePreview>;
  /**
   * Edges
   */
  edges: Array<EdgePreview>;
};

/**
 * WorkflowLogColumn
 */
export type WorkflowLogColumn = {
  /**
   * Name
   */
  name: string;
  /**
   * Visible
   */
  visible?: boolean;
};

/**
 * WorkflowLogSheet
 */
export type WorkflowLogSheet = {
  /**
   * Name
   */
  name: string;
  /**
   * Id
   */
  id: string;
  /**
   * Time Created
   */
  time_created: string;
};

/**
 * WorkflowLogSpreadsheet
 */
export type WorkflowLogSpreadsheet = {
  /**
   * Name
   */
  name: string;
  /**
   * Id
   */
  id: string;
  /**
   * Timestamp
   */
  timestamp: string;
  /**
   * Url
   */
  url?: string | null;
};

/**
 * WorkflowMeta
 */
export type WorkflowMeta = {
  /**
   * Tags
   */
  tags?: Array<WorkflowTag>;
  /**
   * Icon
   */
  icon?: string | null;
  log_sync_sheet?: WorkflowLogSheet | null;
  /**
   * Columns
   */
  columns?: Array<WorkflowLogColumn> | null;
  spreadsheet?: WorkflowLogSpreadsheet | null;
  /**
   * Template Id
   */
  template_id?: string | null;
  /**
   * Variables
   */
  variables?: Array<{
    [key: string]: unknown;
  }> | null;
  source?: EntitySource | null;
};

/**
 * WorkflowProgress
 */
export type WorkflowProgress = {
  /**
   * Executed Nodes
   */
  executed_nodes: Array<string>;
  /**
   * Running Nodes
   */
  running_nodes?: Array<string> | null;
  /**
   * Remaining Nodes
   */
  remaining_nodes: Array<string>;
  /**
   * Skipped Nodes
   */
  skipped_nodes?: Array<string>;
  /**
   * Error Handler Nodes
   */
  error_handler_nodes?: Array<string>;
  /**
   * Per Node Progress
   */
  per_node_progress: {
    [key: string]: NodeProgress;
  };
  /**
   * Terminal Error
   */
  terminal_error?: boolean;
  /**
   * Subflow Executions
   */
  subflow_executions?: {
    [key: string]: SubflowExecution;
  };
  /**
   * Subflow Definitions
   */
  subflow_definitions?: {
    [key: string]: SubflowDefinitions;
  };
  /**
   * Per Edge Progress
   */
  per_edge_progress?: {
    [key: string]: EdgeProgress;
  };
  /**
   * Run Logs
   */
  run_logs?: {
    [key: string]:
      | string
      | {
          [key: string]: unknown;
        }
      | number
      | number;
  };
};

/**
 * WorkflowSettings
 */
export type WorkflowSettingsInput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   *
   * The lucide icon to display for this tool
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentInput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "workflow";
  /**
   * Workflow Id
   *
   * The ID of the workflow to run. Comes unselected, and should be selected by the user.
   */
  workflow_id: string;
  /**
   * Inputs Descriptions
   *
   * A dictionary of input names to descriptions.
   */
  inputs_descriptions?: {
    [key: string]: string;
  };
  /**
   * Input Overrides
   */
  input_overrides?: {
    [key: string]: InputOverride;
  };
  /**
   * Version Id
   *
   * The ID of the version of the workflow to run. Comes unselected, and should be selected by the user.
   */
  version_id?: string | null;
};

/**
 * WorkflowSettings
 */
export type WorkflowSettingsOutput = {
  /**
   * Title
   */
  title?: string;
  /**
   * Description
   */
  description?: string;
  /**
   * Category
   */
  category?:
    | "QuickAction"
    | "Flows"
    | "Knowledge"
    | "Actions"
    | "MCP"
    | "Agents";
  /**
   * Enabled
   *
   * Whether the tool is enabled
   */
  enabled?: boolean;
  /**
   * Tool Display Icon
   *
   * The lucide icon to display for this tool
   */
  tool_display_icon?: string;
  /**
   * Tool Display Message Start
   */
  tool_display_message_start?: string;
  /**
   * Tool Display Message End
   */
  tool_display_message_end?: string;
  /**
   * Any extra instructions for the tool
   */
  extra_instructions?: NoxusTipTapDocumentOutput | null;
  /**
   * Requires Approval
   *
   * Whether tool calls require user approval before execution
   */
  requires_approval?: boolean;
  /**
   * Visible
   *
   * Whether the tool is visible
   */
  visible?: boolean;
  /**
   * Type
   */
  type?: "workflow";
  /**
   * Workflow Id
   *
   * The ID of the workflow to run. Comes unselected, and should be selected by the user.
   */
  workflow_id: string;
  /**
   * Inputs Descriptions
   *
   * A dictionary of input names to descriptions.
   */
  inputs_descriptions?: {
    [key: string]: string;
  };
  /**
   * Input Overrides
   */
  input_overrides?: {
    [key: string]: InputOverride;
  };
  /**
   * Version Id
   *
   * The ID of the version of the workflow to run. Comes unselected, and should be selected by the user.
   */
  version_id?: string | null;
};

/**
 * WorkflowTag
 */
export type WorkflowTag = {
  /**
   * Value
   */
  value: string;
  /**
   * Color
   */
  color: string;
};

/**
 * WorkflowToolMetadata
 */
export type WorkflowToolMetadata = {
  /**
   * Type
   */
  type?: "workflow";
  /**
   * Icon
   */
  icon?: string | null;
  /**
   * Display Message Start
   */
  display_message_start?: string | null;
  /**
   * Display Message End
   */
  display_message_end?: string | null;
  /**
   * Workflow Id
   */
  workflow_id: string;
  /**
   * Workflow Name
   */
  workflow_name: string;
  /**
   * Run Id
   */
  run_id: string;
};

/**
 * WorkflowType
 */
export const WorkflowType = {
  FLOW: "flow",
  HANDLER: "handler",
  INGESTION: "ingestion",
  INBOX_PROCESS: "inbox_process",
  INBOX_FILTERING: "inbox_filtering",
  INBOX_EXTRACTION: "inbox_extraction",
  TASK_AUTOMATION: "task_automation",
} as const;

/**
 * WorkflowType
 */
export type WorkflowType = (typeof WorkflowType)[keyof typeof WorkflowType];

/**
 * WorkflowVersion
 */
export type WorkflowVersion = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Created By
   */
  created_by?: string | null;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  meta?: VersionMeta | null;
  /**
   * Is Valid
   */
  is_valid?: boolean;
};

/**
 * WorkflowVersionUsages
 */
export type WorkflowVersionUsages = {
  /**
   * Triggers
   */
  triggers: Array<TriggerVersionUsage>;
  /**
   * Subflows
   */
  subflows: Array<SubflowVersionUsage>;
  /**
   * Agent Tools
   */
  agent_tools: Array<AgentToolVersionUsage>;
  /**
   * Apps
   */
  apps: Array<AppVersionUsage>;
};

/**
 * WorkflowWithTriggersAndTags
 */
export type WorkflowWithTriggersAndTags = {
  /**
   * Tags
   */
  tags?: Array<Tag> | null;
  /**
   * Id
   */
  id: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Name
   */
  name: string;
  type: WorkflowType;
  /**
   * Definition
   */
  definition: {
    [key: string]: unknown;
  };
  /**
   * Workflow Hash
   */
  workflow_hash?: string | null;
  /**
   * Revision
   */
  revision?: number;
  meta?: WorkflowMeta | null;
  /**
   * Last Updated At
   */
  last_updated_at?: string | null;
  /**
   * Last Run At
   */
  last_run_at?: string | null;
  /**
   * Created At
   */
  created_at?: string | null;
  /**
   * Deleted At
   */
  deleted_at?: string | null;
  /**
   * Runs Count
   */
  runs_count?: number | null;
  /**
   * Author Name
   */
  author_name?: string | null;
  /**
   * Description
   */
  description?: string | null;
  /**
   * Saved
   */
  saved?: boolean | null;
  /**
   * Error Handler
   */
  error_handler?: string | null;
  /**
   * Is App Published
   */
  is_app_published?: boolean | null;
  app_settings_v2?: AppSettingsV2 | null;
  /**
   * Flow Version
   */
  flow_version?: string;
  /**
   * Triggers
   */
  triggers?: Array<Trigger>;
};

/**
 * WorkshopRecent
 */
export type WorkshopRecent = {
  /**
   * Name
   */
  name: string;
  /**
   * Image
   */
  image?: string | null;
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Group Id
   */
  group_id: string;
  /**
   * Type
   */
  type: "flow" | "assistant" | "knowledge_base" | "table" | "inbox";
};

/**
 * WorkspaceRef
 *
 * Serializable identity of a [workspace](../workspace.md) environment, without credentials.
 *
 * Pass it to a later run as `workspace=` to continue in that environment.
 */
export type WorkspaceRef = {
  /**
   * Provider
   */
  provider: string;
  /**
   * Id
   */
  id: string;
};

/**
 * WriteMode
 *
 * Uppercase members avoid shadowing ``str`` methods like ``replace``.
 */
export const WriteMode = {
  CREATE: "create",
  REPLACE: "replace",
  VERSION: "version",
} as const;

/**
 * WriteMode
 *
 * Uppercase members avoid shadowing ``str`` methods like ``replace``.
 */
export type WriteMode = (typeof WriteMode)[keyof typeof WriteMode];

/**
 * WriteSummaryRequest
 */
export type WriteSummaryRequest = {
  /**
   * Sql
   */
  sql: string;
};

/**
 * WriteSummaryResponse
 */
export type WriteSummaryResponse = {
  /**
   * Kind
   */
  kind: "insert" | "update";
  /**
   * Sql Name
   */
  sql_name: string;
  /**
   * Table Id
   */
  table_id: string | null;
  /**
   * Affected
   */
  affected: number | null;
};

/**
 * WsAuthResponse
 */
export type WsAuthResponse = {
  /**
   * Token
   */
  token: string;
};

/**
 * YoutubeReferenceMetadata
 */
export type YoutubeReferenceMetadata = {
  /**
   * Type
   */
  type?: "youtube";
  /**
   * Timestamp
   */
  timestamp?: string | null;
  /**
   * Ref Num
   */
  ref_num?: number | null;
};

/**
 * File
 */
export type NoxusTypesFile = {
  /**
   * Id
   */
  id?: string | null;
  /**
   * Uri
   */
  uri: string;
  /**
   * Name
   */
  name: string;
  /**
   * Content Type
   */
  content_type?: string;
  /**
   * Size
   */
  size?: number | null;
  /**
   * Content Metadata
   */
  content_metadata?:
    | ({
        kind: "pdf";
      } & PdfMetadata)
    | ({
        kind: "spreadsheet";
      } & SpreadsheetMetadata)
    | ({
        kind: "tabular";
      } & TabularMetadata)
    | ({
        kind: "image";
      } & ImageMetadata)
    | ({
        kind: "presentation";
      } & PresentationMetadata)
    | ({
        kind: "document";
      } & DocumentMetadata)
    | null;
  source_type?: SourceType;
  /**
   * Source Metadata
   */
  source_metadata?:
    | SourceMetadata
    | {
        [key: string]: unknown;
      }
    | null;
  [key: string]:
    | unknown
    | string
    | null
    | string
    | number
    | null
    | ({
        kind: "pdf";
      } & PdfMetadata)
    | ({
        kind: "spreadsheet";
      } & SpreadsheetMetadata)
    | ({
        kind: "tabular";
      } & TabularMetadata)
    | ({
        kind: "image";
      } & ImageMetadata)
    | ({
        kind: "presentation";
      } & PresentationMetadata)
    | ({
        kind: "document";
      } & DocumentMetadata)
    | null
    | SourceType
    | SourceMetadata
    | {
        [key: string]: unknown;
      }
    | null
    | undefined;
};

export type PydanticTypesJsonValue = unknown;

/**
 * TokenResponse
 */
export type SpotflowBackendApiAdminTokenResponse = {
  /**
   * Token
   */
  token: string;
};

/**
 * TokenResponse
 */
export type SpotflowBackendApiClientsTokenResponse = {
  /**
   * Access Token
   */
  access_token: string;
  /**
   * Refresh Token
   */
  refresh_token: string;
  /**
   * Token Type
   */
  token_type?: string;
  /**
   * Expires In
   */
  expires_in: number;
};

export type SpotflowBackendApiModelConfigsJsonValue =
  | string
  | number
  | number
  | boolean
  | Array<SpotflowBackendApiModelConfigsJsonValue>
  | {
      [key: string]: SpotflowBackendApiModelConfigsJsonValue;
    }
  | null;

export type SpotflowBackendApiProvidersJsonValue =
  | string
  | number
  | number
  | boolean
  | Array<SpotflowBackendApiProvidersJsonValue>
  | {
      [key: string]: SpotflowBackendApiProvidersJsonValue;
    }
  | null;

/**
 * CaseCreate
 */
export type SpotflowBackendApiSpecCaseCreate = {
  /**
   * Capability
   */
  capability: string;
  /**
   * Title
   */
  title: string;
  /**
   * Priority
   */
  priority?: "low" | "medium" | "high" | "critical";
  /**
   * Preconditions
   */
  preconditions?: string | null;
  /**
   * Steps
   */
  steps: Array<TestStep>;
};

/**
 * CaseCreate
 */
export type SpotflowBackendSchemasCaseManagementCaseCreate = {
  /**
   * Inbox Id
   *
   * ID of the inbox this case belongs to
   */
  inbox_id: string;
  /**
   * Subject
   *
   * Case subject/title
   */
  subject: string;
  /**
   * Priority
   *
   * Case priority (low, medium, high, urgent)
   */
  priority?: "low" | "medium" | "high" | "urgent";
  /**
   * Data
   *
   * Case data
   */
  data?: {
    [key: string]: unknown;
  } | null;
  /**
   * Conversation Id
   *
   * Optional conversation ID to link to this case
   */
  conversation_id?: string | null;
};

/**
 * File
 */
export type SpotflowBackendSchemasIntegrationsFile = {
  /**
   * Mimetype
   */
  mimeType: string;
};

/**
 * AudioUrl
 *
 * A URL to an audio file.
 */
export type AudioUrlWritable = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "audio-url";
};

/**
 * BinaryContent
 *
 * Binary content, e.g. an audio or image file.
 */
export type BinaryContentWritable = {
  /**
   * Data
   */
  data: Blob | File;
  /**
   * Media Type
   */
  media_type:
    | "audio/wav"
    | "audio/mpeg"
    | "audio/ogg"
    | "audio/flac"
    | "audio/aiff"
    | "audio/aac"
    | "image/jpeg"
    | "image/png"
    | "image/gif"
    | "image/webp"
    | "application/pdf"
    | "text/plain"
    | "text/csv"
    | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    | "text/html"
    | "text/markdown"
    | "application/msword"
    | "application/vnd.ms-excel"
    | string;
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "binary";
};

/**
 * CaseTimelineEvent
 *
 * Generic fallback for backwards compat — allows any shape to serialize.
 */
export type CaseTimelineEventWritable = {
  [key: string]: unknown;
};

/**
 * ConversationWithMessages
 */
export type ConversationWithMessagesWritable = {
  /**
   * Id
   */
  id: string;
  /**
   * User Id
   */
  user_id: string | null;
  /**
   * Api Key Id
   */
  api_key_id: string | null;
  /**
   * Name
   */
  name: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Last Updated At
   */
  last_updated_at: string;
  /**
   * Etag
   */
  etag: string | null;
  settings: CoWorkerSettingsOutput;
  /**
   * Assistant Id
   */
  assistant_id?: string | null;
  status: RunStatus;
  /**
   * Manual
   */
  manual?: boolean;
  /**
   * Preview
   */
  preview?: boolean;
  /**
   * Assistant Version Id
   */
  assistant_version_id?: string | null;
  /**
   * Chat Transport
   */
  chat_transport?: string | null;
  /**
   * Channel Ref
   */
  channel_ref?: {
    [key: string]: unknown;
  } | null;
  /**
   * Deployment Id
   */
  deployment_id?: string | null;
  /**
   * Public Access Token
   */
  public_access_token?: string | null;
  /**
   * Todos
   */
  todos?: Array<{
    [key: string]: unknown;
  }>;
  /**
   * Is Locked
   */
  is_locked?: boolean;
  /**
   * Lock Message
   */
  lock_message?: string | null;
  /**
   * Messages
   */
  messages: Array<MessageWritable>;
  history?: ConversationHistoryPage | null;
  /**
   * Vercel Messages
   */
  vercel_messages?: Array<UiMessage>;
};

/**
 * DeploymentResponse
 */
export type DeploymentResponseWritable = {
  /**
   * Id
   */
  id: string;
  /**
   * Name
   */
  name: string | null;
  /**
   * Alias
   */
  alias?: string | null;
  /**
   * Assistant Id
   */
  assistant_id: string;
  /**
   * Channel Type
   */
  channel_type: string;
  /**
   * Config
   */
  config?: {
    [key: string]: unknown;
  };
  /**
   * Is Active
   */
  is_active: boolean;
  /**
   * Published At
   */
  published_at: string | null;
  /**
   * Trigger Id
   */
  trigger_id: string | null;
  /**
   * Assistant Version Id
   */
  assistant_version_id: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
  /**
   * Effective Active
   */
  effective_active: boolean;
  /**
   * Toggle On Action
   */
  toggle_on_action: "activate" | "resume";
  /**
   * Toggle Off Action
   */
  toggle_off_action: "deactivate" | "pause";
  /**
   * Config Read Only
   */
  config_read_only: boolean;
  native_teams_app?: NativeTeamsAppStateWritable | null;
};

/**
 * DocumentUrl
 *
 * The URL of the document.
 */
export type DocumentUrlWritable = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "document-url";
};

/**
 * FilePart
 *
 * A file response from a model.
 */
export type FilePartWritable = {
  content: BinaryContentWritable;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "file";
};

/**
 * ImageUrl
 *
 * A URL to an image.
 */
export type ImageUrlWritable = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "image-url";
};

/**
 * Message
 */
export type MessageWritable = {
  /**
   * Id
   */
  id: string;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Pydantic Message Parts
   */
  pydantic_message_parts: Array<
    | ({
        kind: "ModelRequestWritable";
      } & ModelRequestWritable)
    | ({
        kind: "ModelResponseWritable";
      } & ModelResponseWritable)
  >;
  /**
   * Is Positive
   */
  is_positive: boolean | null;
  feedback_meta?: FeedbackMeta | null;
  sender_ref?: SenderRefSchema | null;
};

/**
 * ModelRequest
 *
 * A request generated by Pydantic AI and sent to a model, e.g. a message from the Pydantic AI app to the model.
 */
export type ModelRequestWritable = {
  /**
   * Parts
   */
  parts: Array<
    | SystemPromptPart
    | UserPromptPartWritable
    | SpeechPartWritable
    | ToolSearchReturnPart
    | LoadCapabilityReturnPart
    | ToolReturnPartWritable
    | RetryPromptPart
    | ToolAvailabilityDeltaPart
  >;
  /**
   * Timestamp
   */
  timestamp?: string | null;
  /**
   * Instructions
   */
  instructions?: string | null;
  /**
   * Kind
   */
  kind?: "request";
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Metadata
   */
  metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * State
   */
  state?: "complete" | "interrupted";
};

/**
 * ModelResponse
 *
 * A response from a model, e.g. a message from the model to the Pydantic AI app.
 */
export type ModelResponseWritable = {
  /**
   * Parts
   */
  parts: Array<
    | TextPart
    | ToolSearchCallPart
    | LoadCapabilityCallPart
    | ToolCallPart
    | NativeToolSearchCallPart
    | NativeToolCallPart
    | NativeToolSearchReturnPart
    | NativeToolReturnPartWritable
    | ThinkingPart
    | CompactionPart
    | FilePartWritable
    | SpeechPartWritable
  >;
  usage?: RequestUsage;
  /**
   * Model Name
   */
  model_name?: string | null;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Kind
   */
  kind?: "response";
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Url
   */
  provider_url?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Provider Response Id
   */
  provider_response_id?: string | null;
  /**
   * Finish Reason
   */
  finish_reason?:
    | "stop"
    | "length"
    | "content_filter"
    | "tool_call"
    | "error"
    | null;
  /**
   * Run Id
   */
  run_id?: string | null;
  /**
   * Conversation Id
   */
  conversation_id?: string | null;
  /**
   * Metadata
   */
  metadata?: {
    [key: string]: unknown;
  } | null;
  workspace_ref?: WorkspaceRef | null;
  /**
   * Failed Attempts
   */
  failed_attempts?: Array<ModelRequestAttempt> | null;
  /**
   * State
   */
  state?: "complete" | "incomplete" | "suspended" | "interrupted";
};

/**
 * NativeTeamsAppState
 */
export type NativeTeamsAppStateWritable = {
  /**
   * Id
   */
  id: string;
  status: NativeTeamsAppStatus;
  /**
   * Is Paused
   */
  is_paused: boolean;
  /**
   * Bound Tenant Id
   */
  bound_tenant_id: string;
  /**
   * Tenant Admin Credential Id
   */
  tenant_admin_credential_id: string;
  /**
   * Manifest App Id
   */
  manifest_app_id: string | null;
  /**
   * Provisioning Step
   */
  provisioning_step:
    | "application_retired"
    | "bot_credential"
    | "bot_credential_cleanup_pending"
    | "bot_credential_rotated"
    | "bot_endpoint"
    | "bot_resource"
    | "bot_retired"
    | "catalog_retired"
    | "entra_application"
    | "entra_sign_in_audience"
    | "metadata_republished"
    | "old_bot_credential_removed"
    | "package_publish"
    | "publishing_observed"
    | "retirement_completed"
    | "service_principal"
    | "teams_channel"
    | "update_approved"
    | null;
  /**
   * Observed Publishing State
   */
  observed_publishing_state: "submitted" | "published" | "rejected" | null;
  /**
   * Package Version
   */
  package_version: string | null;
  /**
   * Error Code
   */
  error_code: string | null;
  /**
   * Error Message
   */
  error_message: string | null;
  /**
   * Attempt Count
   */
  attempt_count: number;
  /**
   * Next Retry At
   */
  next_retry_at: string | null;
  /**
   * Last Reconciled At
   */
  last_reconciled_at: string | null;
  /**
   * Created At
   */
  created_at: string;
  /**
   * Updated At
   */
  updated_at: string;
};

/**
 * NativeToolReturnPart
 *
 * A tool return message from a native tool.
 *
 * For native tools with a stable cross-provider shape (currently `tool_search`), a
 * `NativeToolReturnPart` may be promoted to a typed subclass like
 * [`NativeToolSearchReturnPart`][pydantic_ai.messages.NativeToolSearchReturnPart]
 * with a narrowed `content` `TypedDict`. See `NativeToolCallPart` for the pattern.
 */
export type NativeToolReturnPartWritable = {
  /**
   * Tool Name
   */
  tool_name: string;
  content: ToolReturnContentWritable;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search" | "capability-load" | null;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "builtin-tool-return";
};

/**
 * ServerSettingsJSON
 */
export type ServerSettingsJsonWritable = {
  health_checks?: HealthCheckOverrides | null;
  /**
   * Support Priv Pem
   */
  support_priv_pem?: string;
  /**
   * Support Pub Pem
   */
  support_pub_pem?: string;
  /**
   * Remote Support Mode
   */
  remote_support_mode?: boolean;
  /**
   * Record Shell
   */
  record_shell?: boolean;
  /**
   * License Key
   */
  license_key?: string;
  /**
   * Disabled Users
   */
  disabled_users?: boolean;
  /**
   * Use Ncs
   */
  use_ncs?: boolean;
  /**
   * Ncs Checkin Enabled
   */
  ncs_checkin_enabled?: boolean;
  /**
   * Enable Metrics Sending
   */
  enable_metrics_sending?: boolean;
  /**
   * Noxus Tenant Id
   */
  noxus_tenant_id?: string;
  /**
   * Chaos Testing Enabled
   */
  chaos_testing_enabled?: boolean;
  /**
   * Audit Log Retention Days
   */
  audit_log_retention_days?: 7 | 30 | 90 | 180 | 365 | 730 | 1825 | null;
  /**
   * Run Data Retention Months
   */
  run_data_retention_months?: 1 | 3 | 6 | 12 | 24 | null;
  /**
   * Beta Features
   */
  beta_features?: {
    [key: string]: BetaOptIn;
  };
};

/**
 * SpeechPart
 *
 * Spoken audio exchanged during a realtime session, paired with its transcript.
 *
 * This part is a member of both [`ModelRequestPart`][pydantic_ai.messages.ModelRequestPart] and
 * [`ModelResponsePart`][pydantic_ai.messages.ModelResponsePart], distinguished by `speaker`:
 * in `ModelRequest.parts` the speaker is always `'user'`; in `ModelResponse.parts` it is always
 * `'assistant'`. This invariant is enforced at runtime when a message is constructed.
 *
 * Standard (non-realtime) models can't consume this part directly; when history containing it is
 * used in an agent run, [`Model.prepare_messages`][pydantic_ai.models.Model.prepare_messages]
 * converts user-speaker parts to [`UserPromptPart`][pydantic_ai.messages.UserPromptPart]s and
 * assistant-speaker parts to [`TextPart`][pydantic_ai.messages.TextPart]s.
 */
export type SpeechPartWritable = {
  /**
   * Speaker
   */
  speaker: "user" | "assistant";
  /**
   * Transcript
   */
  transcript?: string | null;
  audio?: BinaryContentWritable | null;
  /**
   * Interrupted At Ms
   */
  interrupted_at_ms?: number | null;
  /**
   * Id
   */
  id?: string | null;
  /**
   * Provider Name
   */
  provider_name?: string | null;
  /**
   * Provider Details
   */
  provider_details?: {
    [key: string]: unknown;
  } | null;
  /**
   * Part Kind
   */
  part_kind?: "speech";
};

export type ToolReturnContentWritable =
  | string
  | ({
      kind: "ImageUrlWritable";
    } & ImageUrlWritable)
  | ({
      kind: "AudioUrlWritable";
    } & AudioUrlWritable)
  | ({
      kind: "DocumentUrlWritable";
    } & DocumentUrlWritable)
  | ({
      kind: "VideoUrlWritable";
    } & VideoUrlWritable)
  | ({
      kind: "BinaryContentWritable";
    } & BinaryContentWritable)
  | ({
      kind: "UploadedFileWritable";
    } & UploadedFileWritable)
  | {
      [key: string]: ToolReturnContentWritable;
    }
  | Array<ToolReturnContentWritable>
  | unknown;

/**
 * ToolReturnPart
 *
 * A tool return message, this encodes the result of running a tool.
 */
export type ToolReturnPartWritable = {
  /**
   * Tool Name
   */
  tool_name: string;
  content: ToolReturnContentWritable;
  /**
   * Tool Call Id
   */
  tool_call_id?: string;
  /**
   * Tool Kind
   */
  tool_kind?: "tool-search" | "capability-load" | null;
  /**
   * Metadata
   */
  metadata?: unknown;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Outcome
   */
  outcome?: "success" | "failed" | "denied" | "interrupted";
  /**
   * Part Kind
   */
  part_kind?: "tool-return";
};

/**
 * Unset
 */
export type UnsetWritable = {
  [key: string]: unknown;
};

/**
 * UploadedFile
 *
 * A reference to a file uploaded to a provider's file storage by ID.
 *
 * This allows referencing files that have been uploaded via provider-specific file APIs
 * rather than providing the file content directly.
 *
 * Supported by:
 *
 * - [`AnthropicModel`][pydantic_ai.models.anthropic.AnthropicModel]
 * - [`OpenAIChatModel`][pydantic_ai.models.openai.OpenAIChatModel]
 * - [`OpenAIResponsesModel`][pydantic_ai.models.openai.OpenAIResponsesModel]
 * - [`BedrockConverseModel`][pydantic_ai.models.bedrock.BedrockConverseModel]
 * - [`GoogleModel`][pydantic_ai.models.google.GoogleModel] (Gemini API: [Files API](https://ai.google.dev/gemini-api/docs/files) URIs, Google Cloud: GCS `gs://` URIs)
 * - [`XaiModel`][pydantic_ai.models.xai.XaiModel]
 */
export type UploadedFileWritable = {
  /**
   * File Id
   */
  file_id: string;
  /**
   * Provider Name
   */
  provider_name:
    | "anthropic"
    | "openai"
    | "google"
    | "google-cloud"
    | "google-gla"
    | "google-vertex"
    | "bedrock"
    | "xai";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "uploaded-file";
};

/**
 * UserPromptPart
 *
 * A user prompt, generally written by the end user.
 *
 * Content comes from the `user_prompt` parameter of [`Agent.run`][pydantic_ai.agent.AbstractAgent.run],
 * [`Agent.run_sync`][pydantic_ai.agent.AbstractAgent.run_sync], and [`Agent.run_stream`][pydantic_ai.agent.AbstractAgent.run_stream].
 */
export type UserPromptPartWritable = {
  /**
   * Content
   */
  content:
    | string
    | Array<
        | string
        | TextContent
        | ({
            kind: "ImageUrlWritable";
          } & ImageUrlWritable)
        | ({
            kind: "AudioUrlWritable";
          } & AudioUrlWritable)
        | ({
            kind: "DocumentUrlWritable";
          } & DocumentUrlWritable)
        | ({
            kind: "VideoUrlWritable";
          } & VideoUrlWritable)
        | ({
            kind: "BinaryContentWritable";
          } & BinaryContentWritable)
        | ({
            kind: "UploadedFileWritable";
          } & UploadedFileWritable)
        | CachePoint
      >;
  /**
   * Timestamp
   */
  timestamp?: string;
  /**
   * Part Kind
   */
  part_kind?: "user-prompt";
};

/**
 * VideoUrl
 *
 * A URL to a video.
 */
export type VideoUrlWritable = {
  /**
   * Url
   */
  url: string;
  /**
   * Force Download
   */
  force_download?: boolean | "allow-local";
  /**
   * Vendor Metadata
   */
  vendor_metadata?: {
    [key: string]: unknown;
  } | null;
  /**
   * Kind
   */
  kind?: "video-url";
};

export type SpotflowBackendApiModelConfigsJsonValueWritable =
  | string
  | number
  | number
  | boolean
  | Array<SpotflowBackendApiModelConfigsJsonValueWritable>
  | {
      [key: string]: SpotflowBackendApiModelConfigsJsonValueWritable;
    }
  | null;

export type SpotflowBackendApiProvidersJsonValueWritable =
  | string
  | number
  | number
  | boolean
  | Array<SpotflowBackendApiProvidersJsonValueWritable>
  | {
      [key: string]: SpotflowBackendApiProvidersJsonValueWritable;
    }
  | null;

export type GetWorkflowsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Query
     */
    query?: string | null;
    /**
     * Type
     */
    type?: string;
    /**
     * Status
     */
    status?: string | null;
    /**
     * Tags
     */
    tags?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Ignore Ids
     */
    ignore_ids?: string | null;
    /**
     * Ids
     */
    ids?: string | null;
    /**
     * Assistant Support
     */
    assistant_support?: boolean;
    /**
     * Allow Error Handler
     */
    allow_error_handler?: boolean;
    /**
     * Allow Inbox Flows
     */
    allow_inbox_flows?: boolean;
    /**
     * Flow Version
     */
    flow_version?: string | null;
    /**
     * Include Deleted
     */
    include_deleted?: boolean;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/workflows";
};

export type GetWorkflowsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowsError = GetWorkflowsErrors[keyof GetWorkflowsErrors];

export type GetWorkflowsResponses = {
  /**
   * Successful Response
   */
  200: PageWorkflowWithTriggersAndTags;
};

export type GetWorkflowsResponse =
  GetWorkflowsResponses[keyof GetWorkflowsResponses];

export type AddWorkflowData = {
  body: CreateWorkflowRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows";
};

export type AddWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddWorkflowError = AddWorkflowErrors[keyof AddWorkflowErrors];

export type AddWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type AddWorkflowResponse =
  AddWorkflowResponses[keyof AddWorkflowResponses];

export type ImportWorkflowData = {
  body: ImportWorkflowRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    mode?: ImportMode;
    /**
     * Activate
     */
    activate?: boolean;
    on_missing_dependency?: MissingDependencyPolicy;
    /**
     * Dry Run
     */
    dry_run?: boolean;
  };
  url: "/groups/{group_id}/workflows/import";
};

export type ImportWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportWorkflowError =
  ImportWorkflowErrors[keyof ImportWorkflowErrors];

export type ImportWorkflowResponses = {
  /**
   * Response Import Workflow
   *
   * Successful Response
   */
  200: Array<ExportableReference>;
};

export type ImportWorkflowResponse =
  ImportWorkflowResponses[keyof ImportWorkflowResponses];

export type DeleteWorkflowData = {
  body: DeleteWorkflowRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}";
};

export type DeleteWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteWorkflowError =
  DeleteWorkflowErrors[keyof DeleteWorkflowErrors];

export type DeleteWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteWorkflowResponse =
  DeleteWorkflowResponses[keyof DeleteWorkflowResponses];

export type GetWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Workflow Version Id
     */
    workflow_version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}";
};

export type GetWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowError = GetWorkflowErrors[keyof GetWorkflowErrors];

export type GetWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type GetWorkflowResponse =
  GetWorkflowResponses[keyof GetWorkflowResponses];

export type UpdateWorkflowData = {
  body: UpdateWorkflowRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Force
     */
    force?: boolean;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}";
};

export type UpdateWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateWorkflowError =
  UpdateWorkflowErrors[keyof UpdateWorkflowErrors];

export type UpdateWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type UpdateWorkflowResponse =
  UpdateWorkflowResponses[keyof UpdateWorkflowResponses];

export type RemoveErrorHandlerForWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/error_handler";
};

export type RemoveErrorHandlerForWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveErrorHandlerForWorkflowError =
  RemoveErrorHandlerForWorkflowErrors[keyof RemoveErrorHandlerForWorkflowErrors];

export type RemoveErrorHandlerForWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type RemoveErrorHandlerForWorkflowResponse =
  RemoveErrorHandlerForWorkflowResponses[keyof RemoveErrorHandlerForWorkflowResponses];

export type CreateErrorHandlerForWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/error_handler";
};

export type CreateErrorHandlerForWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateErrorHandlerForWorkflowError =
  CreateErrorHandlerForWorkflowErrors[keyof CreateErrorHandlerForWorkflowErrors];

export type CreateErrorHandlerForWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type CreateErrorHandlerForWorkflowResponse =
  CreateErrorHandlerForWorkflowResponses[keyof CreateErrorHandlerForWorkflowResponses];

export type ValidateWorkflowV2EndpointData = {
  body: ValidateWorkflowRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/validate";
};

export type ValidateWorkflowV2EndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ValidateWorkflowV2EndpointError =
  ValidateWorkflowV2EndpointErrors[keyof ValidateWorkflowV2EndpointErrors];

export type ValidateWorkflowV2EndpointResponses = {
  /**
   * Successful Response
   */
  200: FlowValidation;
};

export type ValidateWorkflowV2EndpointResponse =
  ValidateWorkflowV2EndpointResponses[keyof ValidateWorkflowV2EndpointResponses];

export type GetWorkflowDependenciesData = {
  body: GetWorkflowDependenciesRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/dependencies";
};

export type GetWorkflowDependenciesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowDependenciesError =
  GetWorkflowDependenciesErrors[keyof GetWorkflowDependenciesErrors];

export type GetWorkflowDependenciesResponses = {
  /**
   * Successful Response
   */
  200: FlowDependencies;
};

export type GetWorkflowDependenciesResponse =
  GetWorkflowDependenciesResponses[keyof GetWorkflowDependenciesResponses];

export type GetWorkflowInputsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/inputs";
};

export type GetWorkflowInputsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowInputsError =
  GetWorkflowInputsErrors[keyof GetWorkflowInputsErrors];

export type GetWorkflowInputsResponses = {
  /**
   * Response Get Workflow Inputs
   *
   * Successful Response
   */
  200: Array<FlowInput>;
};

export type GetWorkflowInputsResponse =
  GetWorkflowInputsResponses[keyof GetWorkflowInputsResponses];

export type GetWorkflowNodeDependenciesData = {
  body: GetWorkflowNodeDependenciesRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Node Id
     */
    node_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/nodes/{node_id}/dependencies";
};

export type GetWorkflowNodeDependenciesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowNodeDependenciesError =
  GetWorkflowNodeDependenciesErrors[keyof GetWorkflowNodeDependenciesErrors];

export type GetWorkflowNodeDependenciesResponses = {
  /**
   * Successful Response
   */
  200: FlowDependencies;
};

export type GetWorkflowNodeDependenciesResponse =
  GetWorkflowNodeDependenciesResponses[keyof GetWorkflowNodeDependenciesResponses];

export type GetWorkflowNodeInputsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Node Id
     */
    node_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/nodes/{node_id}/inputs";
};

export type GetWorkflowNodeInputsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowNodeInputsError =
  GetWorkflowNodeInputsErrors[keyof GetWorkflowNodeInputsErrors];

export type GetWorkflowNodeInputsResponses = {
  /**
   * Response Get Workflow Node Inputs
   *
   * Successful Response
   */
  200: Array<FlowInput>;
};

export type GetWorkflowNodeInputsResponse =
  GetWorkflowNodeInputsResponses[keyof GetWorkflowNodeInputsResponses];

export type GetNodeLastInputsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Node Id
     */
    node_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/nodes/{node_id}/last-inputs";
};

export type GetNodeLastInputsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetNodeLastInputsError =
  GetNodeLastInputsErrors[keyof GetNodeLastInputsErrors];

export type GetNodeLastInputsResponses = {
  /**
   * Response Get Node Last Inputs
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  } | null;
};

export type GetNodeLastInputsResponse =
  GetNodeLastInputsResponses[keyof GetNodeLastInputsResponses];

export type SaveWorkflowData = {
  body: UpdateWorkflowRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Force
     */
    force?: boolean;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/save";
};

export type SaveWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SaveWorkflowError = SaveWorkflowErrors[keyof SaveWorkflowErrors];

export type SaveWorkflowResponses = {
  /**
   * Successful Response
   */
  200: FlowTypes;
};

export type SaveWorkflowResponse =
  SaveWorkflowResponses[keyof SaveWorkflowResponses];

export type GetIsValidWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/valid";
};

export type GetIsValidWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIsValidWorkflowError =
  GetIsValidWorkflowErrors[keyof GetIsValidWorkflowErrors];

export type GetIsValidWorkflowResponses = {
  /**
   * Successful Response
   */
  200: IsValidResponse;
};

export type GetIsValidWorkflowResponse =
  GetIsValidWorkflowResponses[keyof GetIsValidWorkflowResponses];

export type GetIsValidWorkflowFromDefinitionData = {
  body: IsValidRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Process Subflows
     */
    process_subflows?: boolean;
  };
  url: "/groups/{group_id}/workflows/valid";
};

export type GetIsValidWorkflowFromDefinitionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIsValidWorkflowFromDefinitionError =
  GetIsValidWorkflowFromDefinitionErrors[keyof GetIsValidWorkflowFromDefinitionErrors];

export type GetIsValidWorkflowFromDefinitionResponses = {
  /**
   * Successful Response
   */
  200: IsValidResponse;
};

export type GetIsValidWorkflowFromDefinitionResponse =
  GetIsValidWorkflowFromDefinitionResponses[keyof GetIsValidWorkflowFromDefinitionResponses];

export type GetIsValidWorkflowV2FromDefinitionData = {
  body: IsValidRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/valid/v2";
};

export type GetIsValidWorkflowV2FromDefinitionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIsValidWorkflowV2FromDefinitionError =
  GetIsValidWorkflowV2FromDefinitionErrors[keyof GetIsValidWorkflowV2FromDefinitionErrors];

export type GetIsValidWorkflowV2FromDefinitionResponses = {
  /**
   * Successful Response
   */
  200: IsValidResponse;
};

export type GetIsValidWorkflowV2FromDefinitionResponse =
  GetIsValidWorkflowV2FromDefinitionResponses[keyof GetIsValidWorkflowV2FromDefinitionResponses];

export type DuplicateWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/duplicate";
};

export type DuplicateWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DuplicateWorkflowError =
  DuplicateWorkflowErrors[keyof DuplicateWorkflowErrors];

export type DuplicateWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type DuplicateWorkflowResponse =
  DuplicateWorkflowResponses[keyof DuplicateWorkflowResponses];

export type ExportWorkflowPreviewData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/export/preview";
};

export type ExportWorkflowPreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportWorkflowPreviewError =
  ExportWorkflowPreviewErrors[keyof ExportWorkflowPreviewErrors];

export type ExportWorkflowPreviewResponses = {
  /**
   * Successful Response
   */
  200: ExportPreviewResponse;
};

export type ExportWorkflowPreviewResponse =
  ExportWorkflowPreviewResponses[keyof ExportWorkflowPreviewResponses];

export type ExportWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
    /**
     * Export wire format. `auto` (default) emits the legacy base64-encoded bundle for back-compat; pass `v4` for plaintext multi-doc YAML (`.nx`), or `v3` for base64 explicitly.
     */
    version?: ExportFormat;
    /**
     * Set Active On Import
     */
    set_active_on_import?: boolean;
    /**
     * Include Dependencies
     *
     * Bundle referenced sub-flows / KBs / files into the .nx. False exports only this workflow.
     */
    include_dependencies?: boolean;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/export";
};

export type ExportWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportWorkflowError =
  ExportWorkflowErrors[keyof ExportWorkflowErrors];

export type ExportWorkflowResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetWorkflowHandledByData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/handled-workflow";
};

export type GetWorkflowHandledByErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowHandledByError =
  GetWorkflowHandledByErrors[keyof GetWorkflowHandledByErrors];

export type GetWorkflowHandledByResponses = {
  /**
   * Response Get Workflow Handled By
   *
   * Successful Response
   */
  200: Workflow | null;
};

export type GetWorkflowHandledByResponse =
  GetWorkflowHandledByResponses[keyof GetWorkflowHandledByResponses];

export type RestoreWorkflowData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/restore";
};

export type RestoreWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RestoreWorkflowError =
  RestoreWorkflowErrors[keyof RestoreWorkflowErrors];

export type RestoreWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type RestoreWorkflowResponse =
  RestoreWorkflowResponses[keyof RestoreWorkflowResponses];

export type GetWorkflowLogColumnsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Time Start
     */
    time_start?: string | null;
    /**
     * Time End
     */
    time_end?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/logs/details";
};

export type GetWorkflowLogColumnsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowLogColumnsError =
  GetWorkflowLogColumnsErrors[keyof GetWorkflowLogColumnsErrors];

export type GetWorkflowLogColumnsResponses = {
  /**
   * Successful Response
   */
  200: LogDetails;
};

export type GetWorkflowLogColumnsResponse =
  GetWorkflowLogColumnsResponses[keyof GetWorkflowLogColumnsResponses];

export type GetWorkflowCsvFileData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Columns
     */
    columns?: Array<string>;
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/logs/csv";
};

export type GetWorkflowCsvFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowCsvFileError =
  GetWorkflowCsvFileErrors[keyof GetWorkflowCsvFileErrors];

export type GetWorkflowCsvFileResponses = {
  /**
   * Response Get Workflow Csv File
   *
   * Successful Response
   */
  200: Blob | File;
};

export type GetWorkflowCsvFileResponse =
  GetWorkflowCsvFileResponses[keyof GetWorkflowCsvFileResponses];

export type GetWorkflowJsonFileData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Columns
     */
    columns?: Array<string>;
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/logs/json";
};

export type GetWorkflowJsonFileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowJsonFileError =
  GetWorkflowJsonFileErrors[keyof GetWorkflowJsonFileErrors];

export type GetWorkflowJsonFileResponses = {
  /**
   * Response Get Workflow Json File
   *
   * Successful Response
   */
  200: Blob | File;
};

export type GetWorkflowJsonFileResponse =
  GetWorkflowJsonFileResponses[keyof GetWorkflowJsonFileResponses];

export type RetrieveWorkflowLogsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Time Start
     */
    time_start?: string | null;
    /**
     * Time End
     */
    time_end?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/logs";
};

export type RetrieveWorkflowLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RetrieveWorkflowLogsError =
  RetrieveWorkflowLogsErrors[keyof RetrieveWorkflowLogsErrors];

export type RetrieveWorkflowLogsResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type RetrieveWorkflowLogsResponse =
  RetrieveWorkflowLogsResponses[keyof RetrieveWorkflowLogsResponses];

export type SyncSpreadsheetLogsData = {
  body: SyncSpreadsheetLogsRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/logs/spreadsheet/sync";
};

export type SyncSpreadsheetLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SyncSpreadsheetLogsError =
  SyncSpreadsheetLogsErrors[keyof SyncSpreadsheetLogsErrors];

export type SyncSpreadsheetLogsResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type SyncSpreadsheetLogsResponse =
  SyncSpreadsheetLogsResponses[keyof SyncSpreadsheetLogsResponses];

export type UnsyncSpreadsheetLogsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/logs/spreadsheet/unsync";
};

export type UnsyncSpreadsheetLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UnsyncSpreadsheetLogsError =
  UnsyncSpreadsheetLogsErrors[keyof UnsyncSpreadsheetLogsErrors];

export type UnsyncSpreadsheetLogsResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type UnsyncSpreadsheetLogsResponse =
  UnsyncSpreadsheetLogsResponses[keyof UnsyncSpreadsheetLogsResponses];

export type GetWorkshopRecentData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Query
     */
    query?: string | null;
  };
  url: "/groups/{group_id}/workshop";
};

export type GetWorkshopRecentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkshopRecentError =
  GetWorkshopRecentErrors[keyof GetWorkshopRecentErrors];

export type GetWorkshopRecentResponses = {
  /**
   * Response Get Workshop Recent
   *
   * Successful Response
   */
  200: Array<WorkshopRecent>;
};

export type GetWorkshopRecentResponse =
  GetWorkshopRecentResponses[keyof GetWorkshopRecentResponses];

export type GetWorkflowVersionsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Support
     */
    assistant_support?: boolean;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/versions";
};

export type GetWorkflowVersionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowVersionsError =
  GetWorkflowVersionsErrors[keyof GetWorkflowVersionsErrors];

export type GetWorkflowVersionsResponses = {
  /**
   * Successful Response
   */
  200: PageWorkflowVersion;
};

export type GetWorkflowVersionsResponse =
  GetWorkflowVersionsResponses[keyof GetWorkflowVersionsResponses];

export type CreateWorkflowVersionData = {
  body: CreateWorkflowVersionRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/versions";
};

export type CreateWorkflowVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateWorkflowVersionError =
  CreateWorkflowVersionErrors[keyof CreateWorkflowVersionErrors];

export type CreateWorkflowVersionResponses = {
  /**
   * Successful Response
   */
  200: WorkflowVersion;
};

export type CreateWorkflowVersionResponse =
  CreateWorkflowVersionResponses[keyof CreateWorkflowVersionResponses];

export type GetWorkflowVersionData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/versions/{version_id}";
};

export type GetWorkflowVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowVersionError =
  GetWorkflowVersionErrors[keyof GetWorkflowVersionErrors];

export type GetWorkflowVersionResponses = {
  /**
   * Successful Response
   */
  200: WorkflowVersion;
};

export type GetWorkflowVersionResponse =
  GetWorkflowVersionResponses[keyof GetWorkflowVersionResponses];

export type UpdateWorkflowVersionData = {
  body: CreateWorkflowVersionRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/versions/{version_id}";
};

export type UpdateWorkflowVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateWorkflowVersionError =
  UpdateWorkflowVersionErrors[keyof UpdateWorkflowVersionErrors];

export type UpdateWorkflowVersionResponses = {
  /**
   * Successful Response
   */
  200: WorkflowVersion;
};

export type UpdateWorkflowVersionResponse =
  UpdateWorkflowVersionResponses[keyof UpdateWorkflowVersionResponses];

export type GetWorkflowVersionUsagesData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/versions/{version_id}/usages";
};

export type GetWorkflowVersionUsagesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowVersionUsagesError =
  GetWorkflowVersionUsagesErrors[keyof GetWorkflowVersionUsagesErrors];

export type GetWorkflowVersionUsagesResponses = {
  /**
   * Successful Response
   */
  200: WorkflowVersionUsages;
};

export type GetWorkflowVersionUsagesResponse =
  GetWorkflowVersionUsagesResponses[keyof GetWorkflowVersionUsagesResponses];

export type RestoreWorkflowVersionData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/versions/{version_id}/restore";
};

export type RestoreWorkflowVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RestoreWorkflowVersionError =
  RestoreWorkflowVersionErrors[keyof RestoreWorkflowVersionErrors];

export type RestoreWorkflowVersionResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type RestoreWorkflowVersionResponse =
  RestoreWorkflowVersionResponses[keyof RestoreWorkflowVersionResponses];

export type RestoreWorkflowFromRunData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/runs/{run_id}/restore";
};

export type RestoreWorkflowFromRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RestoreWorkflowFromRunError =
  RestoreWorkflowFromRunErrors[keyof RestoreWorkflowFromRunErrors];

export type RestoreWorkflowFromRunResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type RestoreWorkflowFromRunResponse =
  RestoreWorkflowFromRunResponses[keyof RestoreWorkflowFromRunResponses];

export type ListWorkflowVariablesData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/variables";
};

export type ListWorkflowVariablesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListWorkflowVariablesError =
  ListWorkflowVariablesErrors[keyof ListWorkflowVariablesErrors];

export type ListWorkflowVariablesResponses = {
  /**
   * Response List Workflow Variables
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type ListWorkflowVariablesResponse =
  ListWorkflowVariablesResponses[keyof ListWorkflowVariablesResponses];

export type CreateWorkflowVariableData = {
  body: VariableUpsertRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/variables";
};

export type CreateWorkflowVariableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateWorkflowVariableError =
  CreateWorkflowVariableErrors[keyof CreateWorkflowVariableErrors];

export type CreateWorkflowVariableResponses = {
  /**
   * Response Create Workflow Variable
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type CreateWorkflowVariableResponse =
  CreateWorkflowVariableResponses[keyof CreateWorkflowVariableResponses];

export type DeleteWorkflowVariableData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Variable Id
     */
    variable_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/variables/{variable_id}";
};

export type DeleteWorkflowVariableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteWorkflowVariableError =
  DeleteWorkflowVariableErrors[keyof DeleteWorkflowVariableErrors];

export type DeleteWorkflowVariableResponses = {
  /**
   * Response Delete Workflow Variable
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type DeleteWorkflowVariableResponse =
  DeleteWorkflowVariableResponses[keyof DeleteWorkflowVariableResponses];

export type UpdateWorkflowVariableData = {
  body: VariablePatchRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Variable Id
     */
    variable_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/variables/{variable_id}";
};

export type UpdateWorkflowVariableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateWorkflowVariableError =
  UpdateWorkflowVariableErrors[keyof UpdateWorkflowVariableErrors];

export type UpdateWorkflowVariableResponses = {
  /**
   * Response Update Workflow Variable
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type UpdateWorkflowVariableResponse =
  UpdateWorkflowVariableResponses[keyof UpdateWorkflowVariableResponses];

export type TestVaultReferenceData = {
  body: VaultTestRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/variables/test-vault";
};

export type TestVaultReferenceErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TestVaultReferenceError =
  TestVaultReferenceErrors[keyof TestVaultReferenceErrors];

export type TestVaultReferenceResponses = {
  /**
   * Response Test Vault Reference
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type TestVaultReferenceResponse =
  TestVaultReferenceResponses[keyof TestVaultReferenceResponses];

export type ListGroupVariablesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/variables";
};

export type ListGroupVariablesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListGroupVariablesError =
  ListGroupVariablesErrors[keyof ListGroupVariablesErrors];

export type ListGroupVariablesResponses = {
  /**
   * Response List Group Variables
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type ListGroupVariablesResponse =
  ListGroupVariablesResponses[keyof ListGroupVariablesResponses];

export type CreateGroupVariableData = {
  body: VariableUpsertRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/variables";
};

export type CreateGroupVariableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateGroupVariableError =
  CreateGroupVariableErrors[keyof CreateGroupVariableErrors];

export type CreateGroupVariableResponses = {
  /**
   * Response Create Group Variable
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type CreateGroupVariableResponse =
  CreateGroupVariableResponses[keyof CreateGroupVariableResponses];

export type GetGroupVariableSourcesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/variables/sources";
};

export type GetGroupVariableSourcesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGroupVariableSourcesError =
  GetGroupVariableSourcesErrors[keyof GetGroupVariableSourcesErrors];

export type GetGroupVariableSourcesResponses = {
  /**
   * Successful Response
   */
  200: VariableSourcesResponse;
};

export type GetGroupVariableSourcesResponse =
  GetGroupVariableSourcesResponses[keyof GetGroupVariableSourcesResponses];

export type DeleteGroupVariableData = {
  body?: never;
  path: {
    /**
     * Variable Id
     */
    variable_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/variables/{variable_id}";
};

export type DeleteGroupVariableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteGroupVariableError =
  DeleteGroupVariableErrors[keyof DeleteGroupVariableErrors];

export type DeleteGroupVariableResponses = {
  /**
   * Response Delete Group Variable
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type DeleteGroupVariableResponse =
  DeleteGroupVariableResponses[keyof DeleteGroupVariableResponses];

export type UpdateGroupVariableData = {
  body: VariablePatchRequest;
  path: {
    /**
     * Variable Id
     */
    variable_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/variables/{variable_id}";
};

export type UpdateGroupVariableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateGroupVariableError =
  UpdateGroupVariableErrors[keyof UpdateGroupVariableErrors];

export type UpdateGroupVariableResponses = {
  /**
   * Response Update Group Variable
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type UpdateGroupVariableResponse =
  UpdateGroupVariableResponses[keyof UpdateGroupVariableResponses];

export type TestVaultReference2Data = {
  body: VaultTestRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/variables/test-vault";
};

export type TestVaultReference2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TestVaultReference2Error =
  TestVaultReference2Errors[keyof TestVaultReference2Errors];

export type TestVaultReference2Responses = {
  /**
   * Response Test Vault Reference
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type TestVaultReference2Response =
  TestVaultReference2Responses[keyof TestVaultReference2Responses];

export type CreateUserData = {
  body: UserCreateRequest;
  path?: never;
  query?: never;
  url: "/users";
};

export type CreateUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateUserError = CreateUserErrors[keyof CreateUserErrors];

export type CreateUserResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type CreateUserResponse = CreateUserResponses[keyof CreateUserResponses];

export type CheckDomainReservationData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Email
     */
    email: string;
  };
  url: "/users/check-domain-reservation";
};

export type CheckDomainReservationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CheckDomainReservationError =
  CheckDomainReservationErrors[keyof CheckDomainReservationErrors];

export type CheckDomainReservationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CheckAuthorizedData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/users/me/is-authorized";
};

export type CheckAuthorizedResponses = {
  /**
   * Response Check Authorized
   *
   * Successful Response
   */
  200: boolean;
};

export type CheckAuthorizedResponse =
  CheckAuthorizedResponses[keyof CheckAuthorizedResponses];

export type DeleteMeData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Connection Id
     */
    connection_id: string;
  };
  url: "/users/me";
};

export type DeleteMeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteMeError = DeleteMeErrors[keyof DeleteMeErrors];

export type DeleteMeResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteMeResponse = DeleteMeResponses[keyof DeleteMeResponses];

export type GetMeData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/users/me";
};

export type GetMeResponses = {
  /**
   * Response Get Me
   *
   * Successful Response
   */
  200: User | null;
};

export type GetMeResponse = GetMeResponses[keyof GetMeResponses];

export type UpdateMeData = {
  body: UserUpdateProfileRequest;
  path?: never;
  query?: never;
  url: "/users/me";
};

export type UpdateMeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateMeError = UpdateMeErrors[keyof UpdateMeErrors];

export type UpdateMeResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type UpdateMeResponse = UpdateMeResponses[keyof UpdateMeResponses];

export type GetAnonymousUserData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/users/anonymous";
};

export type GetAnonymousUserResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type GetAnonymousUserResponse =
  GetAnonymousUserResponses[keyof GetAnonymousUserResponses];

export type GetPendingInviteData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tenant/invitation";
};

export type GetPendingInviteResponses = {
  /**
   * Response Get Pending Invite
   *
   * Successful Response
   */
  200: PendingInvite | null;
};

export type GetPendingInviteResponse =
  GetPendingInviteResponses[keyof GetPendingInviteResponses];

export type AcceptTenantInvitationData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tenant/invitation/accept";
};

export type AcceptTenantInvitationResponses = {
  /**
   * Response Accept Tenant Invitation
   *
   * Successful Response
   */
  200: Tenant | null;
};

export type AcceptTenantInvitationResponse =
  AcceptTenantInvitationResponses[keyof AcceptTenantInvitationResponses];

export type RejectTenantInvitationData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tenant/invitation/reject";
};

export type RejectTenantInvitationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetTenantData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tenant/me";
};

export type GetTenantResponses = {
  /**
   * Response Get Tenant
   *
   * Successful Response
   */
  200: TenantWithBilling | null;
};

export type GetTenantResponse = GetTenantResponses[keyof GetTenantResponses];

export type UpdateTenantData = {
  body: TenantUpdateRequest;
  path?: never;
  query?: never;
  url: "/tenant/me";
};

export type UpdateTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTenantError = UpdateTenantErrors[keyof UpdateTenantErrors];

export type UpdateTenantResponses = {
  /**
   * Successful Response
   */
  200: Tenant;
};

export type UpdateTenantResponse =
  UpdateTenantResponses[keyof UpdateTenantResponses];

export type SubmitQuestionnaireData = {
  body: TenantUpdateRequest;
  path?: never;
  query?: never;
  url: "/tenant/questionnaire";
};

export type SubmitQuestionnaireErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SubmitQuestionnaireError =
  SubmitQuestionnaireErrors[keyof SubmitQuestionnaireErrors];

export type SubmitQuestionnaireResponses = {
  /**
   * Successful Response
   */
  200: Tenant;
};

export type SubmitQuestionnaireResponse =
  SubmitQuestionnaireResponses[keyof SubmitQuestionnaireResponses];

export type DeleteUserData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: {
    /**
     * Transfer To
     */
    transfer_to?: string | null;
  };
  url: "/tenant/users/{user_id}";
};

export type DeleteUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserError = DeleteUserErrors[keyof DeleteUserErrors];

export type DeleteUserResponses = {
  /**
   * Response Delete User
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteUserResponse = DeleteUserResponses[keyof DeleteUserResponses];

export type GetUserDetailsData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/tenant/users/{user_id}";
};

export type GetUserDetailsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserDetailsError =
  GetUserDetailsErrors[keyof GetUserDetailsErrors];

export type GetUserDetailsResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type GetUserDetailsResponse =
  GetUserDetailsResponses[keyof GetUserDetailsResponses];

export type EditUserData = {
  body: UserUpdateRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/tenant/users/{user_id}";
};

export type EditUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EditUserError = EditUserErrors[keyof EditUserErrors];

export type EditUserResponses = {
  /**
   * Response Edit User
   *
   * Successful Response
   */
  200: boolean;
};

export type EditUserResponse = EditUserResponses[keyof EditUserResponses];

export type ListUsersData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Display Name
     */
    display_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    /**
     * Group Not In
     */
    group_not_in?: string | null;
    /**
     * Group Id
     */
    group_id?: string | null;
    /**
     * Is Admin
     */
    is_admin?: boolean | null;
    order_by?: UserOrderColumn;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/tenant/users";
};

export type ListUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListUsersError = ListUsersErrors[keyof ListUsersErrors];

export type ListUsersResponses = {
  /**
   * Successful Response
   */
  200: PageUserWithGroup;
};

export type ListUsersResponse = ListUsersResponses[keyof ListUsersResponses];

export type AddUserData = {
  body: UserAddRequest;
  path?: never;
  query?: never;
  url: "/tenant/users";
};

export type AddUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddUserError = AddUserErrors[keyof AddUserErrors];

export type AddUserResponses = {
  /**
   * Response Add User
   *
   * Successful Response
   */
  200: boolean;
};

export type AddUserResponse = AddUserResponses[keyof AddUserResponses];

export type UpdateUserMetadataData = {
  body: UserMetadataUpdateRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/tenant/users/{user_id}/metadata";
};

export type UpdateUserMetadataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateUserMetadataError =
  UpdateUserMetadataErrors[keyof UpdateUserMetadataErrors];

export type UpdateUserMetadataResponses = {
  /**
   * Response Update User Metadata
   *
   * Successful Response
   */
  200: boolean;
};

export type UpdateUserMetadataResponse =
  UpdateUserMetadataResponses[keyof UpdateUserMetadataResponses];

export type ResendEmailData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/tenant/users/{user_id}/email-resend";
};

export type ResendEmailErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResendEmailError = ResendEmailErrors[keyof ResendEmailErrors];

export type ResendEmailResponses = {
  /**
   * Response Resend Email
   *
   * Successful Response
   */
  200: boolean;
};

export type ResendEmailResponse =
  ResendEmailResponses[keyof ResendEmailResponses];

export type SendVerificationEmailData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/users/auth0/verification-email";
};

export type SendVerificationEmailResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type SendVerificationEmailResponse =
  SendVerificationEmailResponses[keyof SendVerificationEmailResponses];

export type ValidateRecaptchaTokenData = {
  body: ValidateRecaptchaRequest;
  path?: never;
  query?: never;
  url: "/users/recaptcha";
};

export type ValidateRecaptchaTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ValidateRecaptchaTokenError =
  ValidateRecaptchaTokenErrors[keyof ValidateRecaptchaTokenErrors];

export type ValidateRecaptchaTokenResponses = {
  /**
   * Response Validate Recaptcha Token
   *
   * Successful Response
   */
  200: boolean;
};

export type ValidateRecaptchaTokenResponse =
  ValidateRecaptchaTokenResponses[keyof ValidateRecaptchaTokenResponses];

export type GetAllGroupsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Name
     */
    name?: string | null;
    order_by?: GroupOrder;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups";
};

export type GetAllGroupsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAllGroupsError = GetAllGroupsErrors[keyof GetAllGroupsErrors];

export type GetAllGroupsResponses = {
  /**
   * Successful Response
   */
  200: PageGroupWithUsers;
};

export type GetAllGroupsResponse =
  GetAllGroupsResponses[keyof GetAllGroupsResponses];

export type CreateGroupData = {
  body: CreateGroupRequest;
  path?: never;
  query?: never;
  url: "/groups";
};

export type CreateGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateGroupError = CreateGroupErrors[keyof CreateGroupErrors];

export type CreateGroupResponses = {
  /**
   * Successful Response
   */
  200: GroupWithUsers;
};

export type CreateGroupResponse =
  CreateGroupResponses[keyof CreateGroupResponses];

export type GetMyGroupsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Name
     */
    name?: string | null;
    order_by?: GroupOrder;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Required Permission
     */
    required_permission?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/me";
};

export type GetMyGroupsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMyGroupsError = GetMyGroupsErrors[keyof GetMyGroupsErrors];

export type GetMyGroupsResponses = {
  /**
   * Successful Response
   */
  200: PageGroupWithUsers;
};

export type GetMyGroupsResponse =
  GetMyGroupsResponses[keyof GetMyGroupsResponses];

export type DeleteGroupData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}";
};

export type DeleteGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteGroupError = DeleteGroupErrors[keyof DeleteGroupErrors];

export type DeleteGroupResponses = {
  /**
   * Response Delete Group
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteGroupResponse =
  DeleteGroupResponses[keyof DeleteGroupResponses];

export type GetGroupData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}";
};

export type GetGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGroupError = GetGroupErrors[keyof GetGroupErrors];

export type GetGroupResponses = {
  /**
   * Successful Response
   */
  200: GroupWithUsers;
};

export type GetGroupResponse = GetGroupResponses[keyof GetGroupResponses];

export type EditGroupData = {
  body: EditGroupRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}";
};

export type EditGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EditGroupError = EditGroupErrors[keyof EditGroupErrors];

export type EditGroupResponses = {
  /**
   * Successful Response
   */
  200: GroupWithUsers;
};

export type EditGroupResponse = EditGroupResponses[keyof EditGroupResponses];

export type ListGroupUsersData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Display Name
     */
    display_name?: string | null;
    /**
     * Email
     */
    email?: string | null;
    order_by?: UserOrderColumn;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/users";
};

export type ListGroupUsersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListGroupUsersError =
  ListGroupUsersErrors[keyof ListGroupUsersErrors];

export type ListGroupUsersResponses = {
  /**
   * Successful Response
   */
  200: PageUserWithGroupRole;
};

export type ListGroupUsersResponse =
  ListGroupUsersResponses[keyof ListGroupUsersResponses];

export type AddUserToGroupData = {
  body: AddUserToGroupRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users";
};

export type AddUserToGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddUserToGroupError =
  AddUserToGroupErrors[keyof AddUserToGroupErrors];

export type AddUserToGroupResponses = {
  /**
   * Successful Response
   */
  200: GroupWithUsers;
};

export type AddUserToGroupResponse =
  AddUserToGroupResponses[keyof AddUserToGroupResponses];

export type RemoveUserFromGroupData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/{user_id}";
};

export type RemoveUserFromGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveUserFromGroupError =
  RemoveUserFromGroupErrors[keyof RemoveUserFromGroupErrors];

export type RemoveUserFromGroupResponses = {
  /**
   * Successful Response
   */
  200: GroupWithUsers;
};

export type RemoveUserFromGroupResponse =
  RemoveUserFromGroupResponses[keyof RemoveUserFromGroupResponses];

export type EditUserRoleForGroupData = {
  body: EditUserForGroupRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/{user_id}";
};

export type EditUserRoleForGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EditUserRoleForGroupError =
  EditUserRoleForGroupErrors[keyof EditUserRoleForGroupErrors];

export type EditUserRoleForGroupResponses = {
  /**
   * Successful Response
   */
  200: GroupWithUsers;
};

export type EditUserRoleForGroupResponse =
  EditUserRoleForGroupResponses[keyof EditUserRoleForGroupResponses];

export type GetApiKeysData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/users/api-keys";
};

export type GetApiKeysErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetApiKeysError = GetApiKeysErrors[keyof GetApiKeysErrors];

export type GetApiKeysResponses = {
  /**
   * Successful Response
   */
  200: PageApiKey;
};

export type GetApiKeysResponse = GetApiKeysResponses[keyof GetApiKeysResponses];

export type CreateApiKeyData = {
  body: CreateApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/api-keys";
};

export type CreateApiKeyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateApiKeyError = CreateApiKeyErrors[keyof CreateApiKeyErrors];

export type CreateApiKeyResponses = {
  /**
   * Successful Response
   */
  200: ApiKey;
};

export type CreateApiKeyResponse =
  CreateApiKeyResponses[keyof CreateApiKeyResponses];

export type DeleteApiKeyData = {
  body?: never;
  path: {
    /**
     * Api Key Id
     */
    api_key_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/api-keys/{api_key_id}";
};

export type DeleteApiKeyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteApiKeyError = DeleteApiKeyErrors[keyof DeleteApiKeyErrors];

export type DeleteApiKeyResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteApiKeyResponse =
  DeleteApiKeyResponses[keyof DeleteApiKeyResponses];

export type GetApiKeyData = {
  body?: never;
  path: {
    /**
     * Api Key Id
     */
    api_key_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/api-keys/{api_key_id}";
};

export type GetApiKeyErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetApiKeyError = GetApiKeyErrors[keyof GetApiKeyErrors];

export type GetApiKeyResponses = {
  /**
   * Successful Response
   */
  200: ApiKey;
};

export type GetApiKeyResponse = GetApiKeyResponses[keyof GetApiKeyResponses];

export type DownloadAuditData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Days
     *
     * How many days back to include. Omit (null) to export every row the tenant can see.
     */
    days?: number | null;
  };
  url: "/audit/download";
};

export type DownloadAuditErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DownloadAuditError = DownloadAuditErrors[keyof DownloadAuditErrors];

export type DownloadAuditResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetAuthorizationContextData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Group Id
     *
     * The active workspace, when the client knows it
     */
    group_id?: string | null;
  };
  url: "/authorization-context";
};

export type GetAuthorizationContextErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAuthorizationContextError =
  GetAuthorizationContextErrors[keyof GetAuthorizationContextErrors];

export type GetAuthorizationContextResponses = {
  /**
   * Successful Response
   */
  200: AuthorizationContext;
};

export type GetAuthorizationContextResponse =
  GetAuthorizationContextResponses[keyof GetAuthorizationContextResponses];

export type ListTenantRolesData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/roles";
};

export type ListTenantRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTenantRolesError =
  ListTenantRolesErrors[keyof ListTenantRolesErrors];

export type ListTenantRolesResponses = {
  /**
   * Response List Tenant Roles
   *
   * Successful Response
   */
  200: Array<TenantRoleResponse>;
};

export type ListTenantRolesResponse =
  ListTenantRolesResponses[keyof ListTenantRolesResponses];

export type CreateTenantRoleData = {
  body: TenantRoleCreate;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/roles";
};

export type CreateTenantRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTenantRoleError =
  CreateTenantRoleErrors[keyof CreateTenantRoleErrors];

export type CreateTenantRoleResponses = {
  /**
   * Successful Response
   */
  200: TenantRoleResponse;
};

export type CreateTenantRoleResponse =
  CreateTenantRoleResponses[keyof CreateTenantRoleResponses];

export type ListTenantRolesAsPlatformAdminData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/platform/tenants/{tenant_id}/roles";
};

export type ListTenantRolesAsPlatformAdminErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTenantRolesAsPlatformAdminError =
  ListTenantRolesAsPlatformAdminErrors[keyof ListTenantRolesAsPlatformAdminErrors];

export type ListTenantRolesAsPlatformAdminResponses = {
  /**
   * Response List Tenant Roles As Platform Admin
   *
   * Successful Response
   */
  200: Array<TenantRoleResponse>;
};

export type ListTenantRolesAsPlatformAdminResponse =
  ListTenantRolesAsPlatformAdminResponses[keyof ListTenantRolesAsPlatformAdminResponses];

export type DeleteTenantRoleData = {
  body?: never;
  path: {
    /**
     * Role Id
     */
    role_id: string;
  };
  query?: never;
  url: "/admin/roles/{role_id}";
};

export type DeleteTenantRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTenantRoleError =
  DeleteTenantRoleErrors[keyof DeleteTenantRoleErrors];

export type DeleteTenantRoleResponses = {
  /**
   * Successful Response
   */
  200: MessageResponse;
};

export type DeleteTenantRoleResponse =
  DeleteTenantRoleResponses[keyof DeleteTenantRoleResponses];

export type GetTenantRoleData = {
  body?: never;
  path: {
    /**
     * Role Id
     */
    role_id: string;
  };
  query?: never;
  url: "/admin/roles/{role_id}";
};

export type GetTenantRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTenantRoleError = GetTenantRoleErrors[keyof GetTenantRoleErrors];

export type GetTenantRoleResponses = {
  /**
   * Successful Response
   */
  200: TenantRoleResponse;
};

export type GetTenantRoleResponse =
  GetTenantRoleResponses[keyof GetTenantRoleResponses];

export type UpdateTenantRoleData = {
  body: TenantRoleUpdate;
  path: {
    /**
     * Role Id
     */
    role_id: string;
  };
  query?: never;
  url: "/admin/roles/{role_id}";
};

export type UpdateTenantRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTenantRoleError =
  UpdateTenantRoleErrors[keyof UpdateTenantRoleErrors];

export type UpdateTenantRoleResponses = {
  /**
   * Successful Response
   */
  200: TenantRoleResponse;
};

export type UpdateTenantRoleResponse =
  UpdateTenantRoleResponses[keyof UpdateTenantRoleResponses];

export type ListGroupRolesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/roles";
};

export type ListGroupRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListGroupRolesError =
  ListGroupRolesErrors[keyof ListGroupRolesErrors];

export type ListGroupRolesResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateGroupRoleData = {
  body: UserGroupRoleCreate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/roles";
};

export type CreateGroupRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateGroupRoleError =
  CreateGroupRoleErrors[keyof CreateGroupRoleErrors];

export type CreateGroupRoleResponses = {
  /**
   * Successful Response
   */
  200: UserGroupRoleResponse;
};

export type CreateGroupRoleResponse =
  CreateGroupRoleResponses[keyof CreateGroupRoleResponses];

export type ListTenantGroupsAsPlatformAdminData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/platform/tenants/{tenant_id}/groups";
};

export type ListTenantGroupsAsPlatformAdminErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTenantGroupsAsPlatformAdminError =
  ListTenantGroupsAsPlatformAdminErrors[keyof ListTenantGroupsAsPlatformAdminErrors];

export type ListTenantGroupsAsPlatformAdminResponses = {
  /**
   * Response List Tenant Groups As Platform Admin
   *
   * Successful Response
   */
  200: Array<TenantGroupSummary>;
};

export type ListTenantGroupsAsPlatformAdminResponse =
  ListTenantGroupsAsPlatformAdminResponses[keyof ListTenantGroupsAsPlatformAdminResponses];

export type ListGroupRolesAsPlatformAdminData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/admin/platform/groups/{group_id}/roles";
};

export type ListGroupRolesAsPlatformAdminErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListGroupRolesAsPlatformAdminError =
  ListGroupRolesAsPlatformAdminErrors[keyof ListGroupRolesAsPlatformAdminErrors];

export type ListGroupRolesAsPlatformAdminResponses = {
  /**
   * Response List Group Roles As Platform Admin
   *
   * Successful Response
   */
  200: Array<UserGroupRoleResponse>;
};

export type ListGroupRolesAsPlatformAdminResponse =
  ListGroupRolesAsPlatformAdminResponses[keyof ListGroupRolesAsPlatformAdminResponses];

export type ListAllWorkspaceRolesData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/workspace-roles";
};

export type ListAllWorkspaceRolesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListAllWorkspaceRolesError =
  ListAllWorkspaceRolesErrors[keyof ListAllWorkspaceRolesErrors];

export type ListAllWorkspaceRolesResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DeleteGroupRoleData = {
  body?: never;
  path: {
    /**
     * Role Id
     */
    role_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/roles/{role_id}";
};

export type DeleteGroupRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteGroupRoleError =
  DeleteGroupRoleErrors[keyof DeleteGroupRoleErrors];

export type DeleteGroupRoleResponses = {
  /**
   * Successful Response
   */
  200: MessageResponse;
};

export type DeleteGroupRoleResponse =
  DeleteGroupRoleResponses[keyof DeleteGroupRoleResponses];

export type GetGroupRoleData = {
  body?: never;
  path: {
    /**
     * Role Id
     */
    role_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/roles/{role_id}";
};

export type GetGroupRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGroupRoleError = GetGroupRoleErrors[keyof GetGroupRoleErrors];

export type GetGroupRoleResponses = {
  /**
   * Successful Response
   */
  200: UserGroupRoleResponse;
};

export type GetGroupRoleResponse =
  GetGroupRoleResponses[keyof GetGroupRoleResponses];

export type UpdateGroupRoleData = {
  body: UserGroupRoleUpdate;
  path: {
    /**
     * Role Id
     */
    role_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/roles/{role_id}";
};

export type UpdateGroupRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateGroupRoleError =
  UpdateGroupRoleErrors[keyof UpdateGroupRoleErrors];

export type UpdateGroupRoleResponses = {
  /**
   * Successful Response
   */
  200: UserGroupRoleResponse;
};

export type UpdateGroupRoleResponse =
  UpdateGroupRoleResponses[keyof UpdateGroupRoleResponses];

export type AssignUserRoleData = {
  body: AssignRoleRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/{user_id}/role";
};

export type AssignUserRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AssignUserRoleError =
  AssignUserRoleErrors[keyof AssignUserRoleErrors];

export type AssignUserRoleResponses = {
  /**
   * Successful Response
   */
  200: MessageResponse;
};

export type AssignUserRoleResponse =
  AssignUserRoleResponses[keyof AssignUserRoleResponses];

export type SetUserPermissionsData = {
  body: SetCustomPermissionsRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/{user_id}/permissions";
};

export type SetUserPermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetUserPermissionsError =
  SetUserPermissionsErrors[keyof SetUserPermissionsErrors];

export type SetUserPermissionsResponses = {
  /**
   * Successful Response
   */
  200: MessageResponse;
};

export type SetUserPermissionsResponse =
  SetUserPermissionsResponses[keyof SetUserPermissionsResponses];

export type GetUserEffectivePermissionsData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/{user_id}/effective-permissions";
};

export type GetUserEffectivePermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserEffectivePermissionsError =
  GetUserEffectivePermissionsErrors[keyof GetUserEffectivePermissionsErrors];

export type GetUserEffectivePermissionsResponses = {
  /**
   * Successful Response
   */
  200: EffectivePermissionsResponse;
};

export type GetUserEffectivePermissionsResponse =
  GetUserEffectivePermissionsResponses[keyof GetUserEffectivePermissionsResponses];

export type AssignGroupUserRoleData = {
  body: AssignRoleRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/{user_id}/role";
};

export type AssignGroupUserRoleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AssignGroupUserRoleError =
  AssignGroupUserRoleErrors[keyof AssignGroupUserRoleErrors];

export type AssignGroupUserRoleResponses = {
  /**
   * Successful Response
   */
  200: MessageResponse;
};

export type AssignGroupUserRoleResponse =
  AssignGroupUserRoleResponses[keyof AssignGroupUserRoleResponses];

export type SetGroupUserPermissionsData = {
  body: SetCustomPermissionsRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/users/{user_id}/permissions";
};

export type SetGroupUserPermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetGroupUserPermissionsError =
  SetGroupUserPermissionsErrors[keyof SetGroupUserPermissionsErrors];

export type SetGroupUserPermissionsResponses = {
  /**
   * Successful Response
   */
  200: MessageResponse;
};

export type SetGroupUserPermissionsResponse =
  SetGroupUserPermissionsResponses[keyof SetGroupUserPermissionsResponses];

export type DeleteEntityGrantsData = {
  body: EntityGrantDelete;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/entities/{entity_type}/{entity_id}/grants";
};

export type DeleteEntityGrantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteEntityGrantsError =
  DeleteEntityGrantsErrors[keyof DeleteEntityGrantsErrors];

export type DeleteEntityGrantsResponses = {
  /**
   * Response Delete Entity Grants
   *
   * Successful Response
   */
  200: {
    [key: string]: number;
  };
};

export type DeleteEntityGrantsResponse =
  DeleteEntityGrantsResponses[keyof DeleteEntityGrantsResponses];

export type ListEntityGrantsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/entities/{entity_type}/{entity_id}/grants";
};

export type ListEntityGrantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListEntityGrantsError =
  ListEntityGrantsErrors[keyof ListEntityGrantsErrors];

export type ListEntityGrantsResponses = {
  /**
   * Successful Response
   */
  200: EntityGrantsListResponse;
};

export type ListEntityGrantsResponse =
  ListEntityGrantsResponses[keyof ListEntityGrantsResponses];

export type SetEntityGrantsData = {
  body: EntityGrantsBulkUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Entity Type
     */
    entity_type: string;
    /**
     * Entity Id
     */
    entity_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/entities/{entity_type}/{entity_id}/grants";
};

export type SetEntityGrantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetEntityGrantsError =
  SetEntityGrantsErrors[keyof SetEntityGrantsErrors];

export type SetEntityGrantsResponses = {
  /**
   * Successful Response
   */
  200: EntityGrantsListResponse;
};

export type SetEntityGrantsResponse =
  SetEntityGrantsResponses[keyof SetEntityGrantsResponses];

export type GetMyPermissionsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Group Id
     *
     * Optional group ID for group-level permissions
     */
    group_id?: string | null;
  };
  url: "/me/permissions";
};

export type GetMyPermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMyPermissionsError =
  GetMyPermissionsErrors[keyof GetMyPermissionsErrors];

export type GetMyPermissionsResponses = {
  /**
   * Successful Response
   */
  200: UserPermissionsResponse;
};

export type GetMyPermissionsResponse =
  GetMyPermissionsResponses[keyof GetMyPermissionsResponses];

export type GetMyGroupPermissionsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/me/permissions";
};

export type GetMyGroupPermissionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMyGroupPermissionsError =
  GetMyGroupPermissionsErrors[keyof GetMyGroupPermissionsErrors];

export type GetMyGroupPermissionsResponses = {
  /**
   * Response Get My Group Permissions
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type GetMyGroupPermissionsResponse =
  GetMyGroupPermissionsResponses[keyof GetMyGroupPermissionsResponses];

export type GetTemplatesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Query
     */
    query?: string | null;
    /**
     * Category
     */
    category?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/templates";
};

export type GetTemplatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplatesError = GetTemplatesErrors[keyof GetTemplatesErrors];

export type GetTemplatesResponses = {
  /**
   * Successful Response
   */
  200: PageTemplate;
};

export type GetTemplatesResponse =
  GetTemplatesResponses[keyof GetTemplatesResponses];

export type GetTemplateCategoriesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Count
     */
    count?: number;
  };
  url: "/templates/categories";
};

export type GetTemplateCategoriesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateCategoriesError =
  GetTemplateCategoriesErrors[keyof GetTemplateCategoriesErrors];

export type GetTemplateCategoriesResponses = {
  /**
   * Response Get Template Categories
   *
   * Successful Response
   */
  200: Array<string>;
};

export type GetTemplateCategoriesResponse =
  GetTemplateCategoriesResponses[keyof GetTemplateCategoriesResponses];

export type GetTrendingTemplatesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Count
     */
    count?: number;
  };
  url: "/templates/trending";
};

export type GetTrendingTemplatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTrendingTemplatesError =
  GetTrendingTemplatesErrors[keyof GetTrendingTemplatesErrors];

export type GetTrendingTemplatesResponses = {
  /**
   * Response Get Trending Templates
   *
   * Successful Response
   */
  200: Array<Template>;
};

export type GetTrendingTemplatesResponse =
  GetTrendingTemplatesResponses[keyof GetTrendingTemplatesResponses];

export type DeleteTemplateData = {
  body?: never;
  path: {
    /**
     * Template Id
     */
    template_id: string;
  };
  query?: {
    /**
     * Category
     */
    category?: string | null;
  };
  url: "/templates/{template_id}";
};

export type DeleteTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTemplateError =
  DeleteTemplateErrors[keyof DeleteTemplateErrors];

export type DeleteTemplateResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteTemplateResponse =
  DeleteTemplateResponses[keyof DeleteTemplateResponses];

export type GetTemplateData = {
  body?: never;
  path: {
    /**
     * Template Id
     */
    template_id: string;
  };
  query?: never;
  url: "/templates/{template_id}";
};

export type GetTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateError = GetTemplateErrors[keyof GetTemplateErrors];

export type GetTemplateResponses = {
  /**
   * Successful Response
   */
  200: Template;
};

export type GetTemplateResponse =
  GetTemplateResponses[keyof GetTemplateResponses];

export type GetTemplateAsWorkflowData = {
  body?: never;
  path: {
    /**
     * Template Id
     */
    template_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/templates/{template_id}/as-workflow";
};

export type GetTemplateAsWorkflowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateAsWorkflowError =
  GetTemplateAsWorkflowErrors[keyof GetTemplateAsWorkflowErrors];

export type GetTemplateAsWorkflowResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type GetTemplateAsWorkflowResponse =
  GetTemplateAsWorkflowResponses[keyof GetTemplateAsWorkflowResponses];

export type CreateWorkflowFromTemplateData = {
  body?: never;
  path: {
    /**
     * Template Id
     */
    template_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/templates/{template_id}/workflow";
};

export type CreateWorkflowFromTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateWorkflowFromTemplateError =
  CreateWorkflowFromTemplateErrors[keyof CreateWorkflowFromTemplateErrors];

export type CreateWorkflowFromTemplateResponses = {
  /**
   * Successful Response
   */
  200: Workflow;
};

export type CreateWorkflowFromTemplateResponse =
  CreateWorkflowFromTemplateResponses[keyof CreateWorkflowFromTemplateResponses];

export type GetTemplatesV2Data = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/v2/templates";
};

export type GetTemplatesV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplatesV2Error =
  GetTemplatesV2Errors[keyof GetTemplatesV2Errors];

export type GetTemplatesV2Responses = {
  /**
   * Response Get Templates V2
   *
   * Successful Response
   */
  200: Array<TemplateVisual>;
};

export type GetTemplatesV2Response =
  GetTemplatesV2Responses[keyof GetTemplatesV2Responses];

export type GetTemplateV2Data = {
  body?: never;
  path: {
    /**
     * Template Id
     */
    template_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/v2/templates/{template_id}";
};

export type GetTemplateV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateV2Error = GetTemplateV2Errors[keyof GetTemplateV2Errors];

export type GetTemplateV2Responses = {
  /**
   * Successful Response
   */
  200: TemplateVisual;
};

export type GetTemplateV2Response =
  GetTemplateV2Responses[keyof GetTemplateV2Responses];

export type GetTemplateV2ConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Template Id
     */
    template_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/v2/templates/{template_id}/config";
};

export type GetTemplateV2ConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTemplateV2ConfigError =
  GetTemplateV2ConfigErrors[keyof GetTemplateV2ConfigErrors];

export type GetTemplateV2ConfigResponses = {
  /**
   * Successful Response
   */
  200: TemplateConfigResponse;
};

export type GetTemplateV2ConfigResponse =
  GetTemplateV2ConfigResponses[keyof GetTemplateV2ConfigResponses];

export type DeployTemplateV2Data = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Template Id
     */
    template_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/v2/templates/{template_id}/deploy";
};

export type DeployTemplateV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeployTemplateV2Error =
  DeployTemplateV2Errors[keyof DeployTemplateV2Errors];

export type DeployTemplateV2Responses = {
  /**
   * Response Deploy Template V2
   *
   * Successful Response
   */
  200: Array<ExportableReference>;
};

export type DeployTemplateV2Response =
  DeployTemplateV2Responses[keyof DeployTemplateV2Responses];

export type GetTriggersData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers";
};

export type GetTriggersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTriggersError = GetTriggersErrors[keyof GetTriggersErrors];

export type GetTriggersResponses = {
  /**
   * Successful Response
   */
  200: PageTrigger;
};

export type GetTriggersResponse =
  GetTriggersResponses[keyof GetTriggersResponses];

export type CreateTriggerData = {
  body: CreateTriggerRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers";
};

export type CreateTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTriggerError = CreateTriggerErrors[keyof CreateTriggerErrors];

export type CreateTriggerResponses = {
  /**
   * Successful Response
   */
  200: Trigger;
};

export type CreateTriggerResponse =
  CreateTriggerResponses[keyof CreateTriggerResponses];

export type GetTriggersCompatibilityData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers/compatibility";
};

export type GetTriggersCompatibilityErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTriggersCompatibilityError =
  GetTriggersCompatibilityErrors[keyof GetTriggersCompatibilityErrors];

export type GetTriggersCompatibilityResponses = {
  /**
   * Response Get Triggers Compatibility
   *
   * Successful Response
   */
  200: {
    [key: string]: TriggerCompatibility;
  };
};

export type GetTriggersCompatibilityResponse =
  GetTriggersCompatibilityResponses[keyof GetTriggersCompatibilityResponses];

export type DeleteTriggersData = {
  body?: never;
  path: {
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers/{trigger_id}";
};

export type DeleteTriggersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTriggersError =
  DeleteTriggersErrors[keyof DeleteTriggersErrors];

export type DeleteTriggersResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteTriggersResponse =
  DeleteTriggersResponses[keyof DeleteTriggersResponses];

export type GetTriggerData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers/{trigger_id}";
};

export type GetTriggerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTriggerError = GetTriggerErrors[keyof GetTriggerErrors];

export type GetTriggerResponses = {
  /**
   * Successful Response
   */
  200: Trigger;
};

export type GetTriggerResponse = GetTriggerResponses[keyof GetTriggerResponses];

export type UpdateTriggersData = {
  body: UpdateTriggerRequest;
  path: {
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers/{trigger_id}";
};

export type UpdateTriggersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTriggersError =
  UpdateTriggersErrors[keyof UpdateTriggersErrors];

export type UpdateTriggersResponses = {
  /**
   * Successful Response
   */
  200: Trigger;
};

export type UpdateTriggersResponse =
  UpdateTriggersResponses[keyof UpdateTriggersResponses];

export type GetTriggerEventsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Search
     */
    search?: string | null;
    /**
     * Errored
     */
    errored?: boolean | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers/{trigger_id}/events";
};

export type GetTriggerEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTriggerEventsError =
  GetTriggerEventsErrors[keyof GetTriggerEventsErrors];

export type GetTriggerEventsResponses = {
  /**
   * Successful Response
   */
  200: PageTriggerEvent;
};

export type GetTriggerEventsResponse =
  GetTriggerEventsResponses[keyof GetTriggerEventsResponses];

export type ReplayTriggerEventData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Event Id
     */
    event_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/triggers/{trigger_id}/events/{event_id}/replay";
};

export type ReplayTriggerEventErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReplayTriggerEventError =
  ReplayTriggerEventErrors[keyof ReplayTriggerEventErrors];

export type ReplayTriggerEventResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type ReplayTriggerEventResponse =
  ReplayTriggerEventResponses[keyof ReplayTriggerEventResponses];

export type GetGroupTriggerEventsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Search
     */
    search?: string | null;
    /**
     * Event Type
     */
    event_type?: string | null;
    /**
     * Workflow Id
     */
    workflow_id?: string | null;
    /**
     * Started Run
     */
    started_run?: boolean | null;
    /**
     * Errored
     */
    errored?: boolean | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/triggers/events";
};

export type GetGroupTriggerEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGroupTriggerEventsError =
  GetGroupTriggerEventsErrors[keyof GetGroupTriggerEventsErrors];

export type GetGroupTriggerEventsResponses = {
  /**
   * Successful Response
   */
  200: PageGroupTriggerEvent;
};

export type GetGroupTriggerEventsResponse =
  GetGroupTriggerEventsResponses[keyof GetGroupTriggerEventsResponses];

export type ConvertPlaywrightCodeData = {
  body: ConvertPlaywrightRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/playbook/convert-playwright";
};

export type ConvertPlaywrightCodeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConvertPlaywrightCodeError =
  ConvertPlaywrightCodeErrors[keyof ConvertPlaywrightCodeErrors];

export type ConvertPlaywrightCodeResponses = {
  /**
   * Successful Response
   */
  200: ConvertPlaywrightResponse;
};

export type ConvertPlaywrightCodeResponse =
  ConvertPlaywrightCodeResponses[keyof ConvertPlaywrightCodeResponses];

export type IssueRecordWsAuthData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/playbook/record/auth";
};

export type IssueRecordWsAuthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type IssueRecordWsAuthError =
  IssueRecordWsAuthErrors[keyof IssueRecordWsAuthErrors];

export type IssueRecordWsAuthResponses = {
  /**
   * Successful Response
   */
  200: WsAuthResponse;
};

export type IssueRecordWsAuthResponse =
  IssueRecordWsAuthResponses[keyof IssueRecordWsAuthResponses];

export type EventsSchemaStubData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/playbook/record/events/_schema";
};

export type EventsSchemaStubResponses = {
  /**
   * Response  Events Schema Stub
   *
   * Successful Response
   */
  200:
    | ({
        type: "step";
      } & StepEvent)
    | ({
        type: "step_update";
      } & StepUpdateEvent)
    | ({
        type: "status";
      } & StatusEvent)
    | ({
        type: "bbox";
      } & BBoxEvent)
    | ({
        type: "pick";
      } & PickEvent)
    | ({
        type: "replay";
      } & ReplayEvent)
    | ({
        type: "error";
      } & ErrorEvent)
    | ({
        type: "queued";
      } & QueuedEvent)
    | ({
        type: "pong";
      } & PongEvent)
    | ({
        type: "session_created";
      } & SessionCreatedEvent)
    | ({
        type: "clipboard_data";
      } & ClipboardDataEvent);
};

export type EventsSchemaStubResponse =
  EventsSchemaStubResponses[keyof EventsSchemaStubResponses];

export type GetChangelogData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/changelog";
};

export type GetChangelogResponses = {
  /**
   * Response Get Changelog
   *
   * Successful Response
   */
  200: Array<ChangelogEntry>;
};

export type GetChangelogResponse =
  GetChangelogResponses[keyof GetChangelogResponses];

export type GetNodesData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Is Tool
     *
     * Filter nodes by is_tool flag
     */
    is_tool?: boolean | null;
  };
  url: "/nodes";
};

export type GetNodesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetNodesError = GetNodesErrors[keyof GetNodesErrors];

export type GetNodesResponses = {
  /**
   * Response Get Nodes
   *
   * Successful Response
   */
  200: Array<NodeDefinition>;
};

export type GetNodesResponse = GetNodesResponses[keyof GetNodesResponses];

export type GetNodeDisplayData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/nodes/display";
};

export type GetNodeDisplayResponses = {
  /**
   * Response Get Node Display
   *
   * Successful Response
   */
  200: Array<NodeDisplay>;
};

export type GetNodeDisplayResponse =
  GetNodeDisplayResponses[keyof GetNodeDisplayResponses];

export type GetDataMessageSchemasData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/data-message-schemas";
};

export type GetDataMessageSchemasResponses = {
  /**
   * Response Get Data Message Schemas
   *
   * Successful Response
   */
  200: Array<
    | ({
        type: "data-guardrail_warning";
      } & NoxusVercelGuardrailWarningMessage)
    | ({
        type: "data-conversation_locked";
      } & NoxusVercelConversationLockedMessage)
    | ({
        type: "data-info";
      } & NoxusVercelInfoMessage)
    | ({
        type: "data-warning";
      } & NoxusVercelWarningMessage)
    | ({
        type: "data-success";
      } & NoxusVercelSuccessMessage)
    | ({
        type: "data-error";
      } & NoxusVercelErrorMessage)
    | ({
        type: "data-clear_conversation";
      } & NoxusVercelClearConversationMessage)
    | ({
        type: "data-approval_superseded";
      } & NoxusVercelApprovalSupersededMessage)
    | ({
        type: "data-set_conversation_title";
      } & NoxusVercelSetConversationTitleMessage)
    | ({
        type: "data-human_in_the_loop";
      } & NoxusVercelHumanInTheLoopMessage)
    | ({
        type: "data-compaction";
      } & NoxusVercelCompactionMessage)
    | ({
        type: "data-history_compacted";
      } & NoxusVercelHistoryCompactedMessage)
    | ({
        type: "data-file_attachment";
      } & NoxusVercelFileAttachmentMessage)
    | ({
        type: "data-final_answer_replay";
      } & NoxusVercelFinalAnswerReplayMessage)
    | ({
        type: "data-csat_request";
      } & NoxusVercelCsatRequestMessage)
  >;
};

export type GetDataMessageSchemasResponse =
  GetDataMessageSchemasResponses[keyof GetDataMessageSchemasResponses];

export type GetToolMetadataSchemasData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tool-metadata";
};

export type GetToolMetadataSchemasResponses = {
  /**
   * Response Get Tool Metadata Schemas
   *
   * Successful Response
   */
  200: Array<
    | ({
        type: "workflow";
      } & WorkflowToolMetadata)
    | ({
        type: "kb";
      } & KbToolMetadata)
    | ({
        type: "agent";
      } & AgentToolMetadata)
    | ({
        type: "document";
      } & DocumentReferenceMetadata)
    | ({
        type: "youtube";
      } & YoutubeReferenceMetadata)
    | ({
        type: "web";
      } & WebReferenceMetadata)
  >;
};

export type GetToolMetadataSchemasResponse =
  GetToolMetadataSchemasResponses[keyof GetToolMetadataSchemasResponses];

export type GetToolDisplaysData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tool-displays";
};

export type GetToolDisplaysResponses = {
  /**
   * Response Get Tool Displays
   *
   * Successful Response
   */
  200: Array<ToolDisplayEntry>;
};

export type GetToolDisplaysResponse =
  GetToolDisplaysResponses[keyof GetToolDisplaysResponses];

export type GetLlmsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Multimodal
     *
     * Whether to include multimodal models
     */
    multimodal?: boolean;
    /**
     * Structured Outputs
     *
     * Whether to include structured outputs models
     */
    structured_outputs?: boolean;
    /**
     * Provider
     *
     * The provider to get compatible models for
     */
    provider?: string | null;
  };
  url: "/groups/{group_id}/models/llms";
};

export type GetLlmsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetLlmsError = GetLlmsErrors[keyof GetLlmsErrors];

export type GetLlmsResponses = {
  /**
   * Response Get Llms
   *
   * Successful Response
   */
  200: Array<ModelDefinition>;
};

export type GetLlmsResponse = GetLlmsResponses[keyof GetLlmsResponses];

export type GetRerankersData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/rerankers";
};

export type GetRerankersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRerankersError = GetRerankersErrors[keyof GetRerankersErrors];

export type GetRerankersResponses = {
  /**
   * Response Get Rerankers
   *
   * Successful Response
   */
  200: Array<RerankersDefinition>;
};

export type GetRerankersResponse =
  GetRerankersResponses[keyof GetRerankersResponses];

export type GetDecisionModelsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/decision-models";
};

export type GetDecisionModelsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDecisionModelsError =
  GetDecisionModelsErrors[keyof GetDecisionModelsErrors];

export type GetDecisionModelsResponses = {
  /**
   * Response Get Decision Models
   *
   * Successful Response
   */
  200: Array<DecisionModelsDefinition>;
};

export type GetDecisionModelsResponse =
  GetDecisionModelsResponses[keyof GetDecisionModelsResponses];

export type GetVoiceModelsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Mode
     *
     * Only models of this voice mode; both when unset
     */
    mode?: VoiceMode | null;
  };
  url: "/groups/{group_id}/models/voice-models";
};

export type GetVoiceModelsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVoiceModelsError =
  GetVoiceModelsErrors[keyof GetVoiceModelsErrors];

export type GetVoiceModelsResponses = {
  /**
   * Response Get Voice Models
   *
   * Successful Response
   */
  200: Array<VoiceModelsDefinition>;
};

export type GetVoiceModelsResponse =
  GetVoiceModelsResponses[keyof GetVoiceModelsResponses];

export type GetVoiceModelVoicesData = {
  body?: never;
  path: {
    /**
     * Slug
     */
    slug: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/voice-models/{slug}/voices";
};

export type GetVoiceModelVoicesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetVoiceModelVoicesError =
  GetVoiceModelVoicesErrors[keyof GetVoiceModelVoicesErrors];

export type GetVoiceModelVoicesResponses = {
  /**
   * Response Get Voice Model Voices
   *
   * Successful Response
   */
  200: Array<VoiceOptionRef>;
};

export type GetVoiceModelVoicesResponse =
  GetVoiceModelVoicesResponses[keyof GetVoiceModelVoicesResponses];

export type DictateAudioData = {
  body: BodyDictateAudio;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/voice/dictation";
};

export type DictateAudioErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DictateAudioError = DictateAudioErrors[keyof DictateAudioErrors];

export type DictateAudioResponses = {
  /**
   * Successful Response
   */
  200: DictationResponse;
};

export type DictateAudioResponse =
  DictateAudioResponses[keyof DictateAudioResponses];

export type GetEmbeddingsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Multimodal
     *
     * Whether to include multimodal models
     */
    multimodal?: boolean;
    /**
     * Provider
     *
     * The provider to get compatible models for
     */
    provider?: string | null;
  };
  url: "/groups/{group_id}/models/embeddings";
};

export type GetEmbeddingsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEmbeddingsError = GetEmbeddingsErrors[keyof GetEmbeddingsErrors];

export type GetEmbeddingsResponses = {
  /**
   * Response Get Embeddings
   *
   * Successful Response
   */
  200: Array<EmbeddingsDefinition>;
};

export type GetEmbeddingsResponse =
  GetEmbeddingsResponses[keyof GetEmbeddingsResponses];

export type GetImageModelsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Model Type
     *
     * Type of image model (text_to_image or image_to_image)
     */
    model_type?: string;
  };
  url: "/models/image";
};

export type GetImageModelsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetImageModelsError =
  GetImageModelsErrors[keyof GetImageModelsErrors];

export type GetImageModelsResponses = {
  /**
   * Response Get Image Models
   *
   * Successful Response
   */
  200: Array<ModelDefinition>;
};

export type GetImageModelsResponse =
  GetImageModelsResponses[keyof GetImageModelsResponses];

export type GetTypesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/types";
};

export type GetTypesResponses = {
  /**
   * Successful Response
   */
  200: DataTypeInformation;
};

export type GetTypesResponse = GetTypesResponses[keyof GetTypesResponses];

export type GetLogoData = {
  body?: never;
  path: {
    /**
     * Url
     */
    url: string;
  };
  query?: never;
  url: "/logo/{url}";
};

export type GetLogoErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetLogoError = GetLogoErrors[keyof GetLogoErrors];

export type GetLogoResponses = {
  /**
   * Response Get Logo
   *
   * Successful Response
   */
  200: string;
};

export type GetLogoResponse = GetLogoResponses[keyof GetLogoResponses];

export type ImprovePromptData = {
  body: ImprovementRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/prompts/improvement";
};

export type ImprovePromptErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImprovePromptError = ImprovePromptErrors[keyof ImprovePromptErrors];

export type ImprovePromptResponses = {
  /**
   * Successful Response
   */
  200: ImprovementResponse;
};

export type ImprovePromptResponse =
  ImprovePromptResponses[keyof ImprovePromptResponses];

export type EvaluatePromptHeuristicsData = {
  body: HeuristicEvaluationRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/prompts/heuristics";
};

export type EvaluatePromptHeuristicsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EvaluatePromptHeuristicsError =
  EvaluatePromptHeuristicsErrors[keyof EvaluatePromptHeuristicsErrors];

export type EvaluatePromptHeuristicsResponses = {
  /**
   * Successful Response
   */
  200: HeuristicEvaluationResponse;
};

export type EvaluatePromptHeuristicsResponse =
  EvaluatePromptHeuristicsResponses[keyof EvaluatePromptHeuristicsResponses];

export type ImprovePromptWithHeuristicsData = {
  body: HeuristicImprovementRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/prompts/heuristic-improvement";
};

export type ImprovePromptWithHeuristicsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImprovePromptWithHeuristicsError =
  ImprovePromptWithHeuristicsErrors[keyof ImprovePromptWithHeuristicsErrors];

export type ImprovePromptWithHeuristicsResponses = {
  /**
   * Successful Response
   */
  200: HeuristicImprovementResponse;
};

export type ImprovePromptWithHeuristicsResponse =
  ImprovePromptWithHeuristicsResponses[keyof ImprovePromptWithHeuristicsResponses];

export type EvaluatePromptHeuristicsJobData = {
  body: HeuristicEvaluationRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/prompts/heuristics/jobs";
};

export type EvaluatePromptHeuristicsJobErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EvaluatePromptHeuristicsJobError =
  EvaluatePromptHeuristicsJobErrors[keyof EvaluatePromptHeuristicsJobErrors];

export type EvaluatePromptHeuristicsJobResponses = {
  /**
   * Successful Response
   */
  200: AiJobHandle;
};

export type EvaluatePromptHeuristicsJobResponse =
  EvaluatePromptHeuristicsJobResponses[keyof EvaluatePromptHeuristicsJobResponses];

export type ImprovePromptWithHeuristicsJobData = {
  body: HeuristicImprovementRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/prompts/heuristic-improvement/jobs";
};

export type ImprovePromptWithHeuristicsJobErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImprovePromptWithHeuristicsJobError =
  ImprovePromptWithHeuristicsJobErrors[keyof ImprovePromptWithHeuristicsJobErrors];

export type ImprovePromptWithHeuristicsJobResponses = {
  /**
   * Successful Response
   */
  200: AiJobHandle;
};

export type ImprovePromptWithHeuristicsJobResponse =
  ImprovePromptWithHeuristicsJobResponses[keyof ImprovePromptWithHeuristicsJobResponses];

export type GetPromptJobStatusData = {
  body?: never;
  path: {
    /**
     * Job Id
     */
    job_id: number;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/prompts/jobs/{job_id}";
};

export type GetPromptJobStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPromptJobStatusError =
  GetPromptJobStatusErrors[keyof GetPromptJobStatusErrors];

export type GetPromptJobStatusResponses = {
  /**
   * Successful Response
   */
  200: AiJobStatusResponse;
};

export type GetPromptJobStatusResponse =
  GetPromptJobStatusResponses[keyof GetPromptJobStatusResponses];

export type GenerateCodeData = {
  body: CodeGenerationRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/code/generation";
};

export type GenerateCodeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateCodeError = GenerateCodeErrors[keyof GenerateCodeErrors];

export type GenerateCodeResponses = {
  /**
   * Successful Response
   */
  200: CodeGenerationResponse;
};

export type GenerateCodeResponse =
  GenerateCodeResponses[keyof GenerateCodeResponses];

export type RefineTextData = {
  body: TextRefinementRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/text/refine";
};

export type RefineTextErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RefineTextError = RefineTextErrors[keyof RefineTextErrors];

export type RefineTextResponses = {
  /**
   * Successful Response
   */
  200: TextRefinementResponse;
};

export type RefineTextResponse = RefineTextResponses[keyof RefineTextResponses];

export type TranslateTextData = {
  body: TextTranslationRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/text/translate";
};

export type TranslateTextErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TranslateTextError = TranslateTextErrors[keyof TranslateTextErrors];

export type TranslateTextResponses = {
  /**
   * Successful Response
   */
  200: TextTranslationResponse;
};

export type TranslateTextResponse =
  TranslateTextResponses[keyof TranslateTextResponses];

export type GenerateTableSqlData = {
  body: SqlGenerationRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tables/sql/generate";
};

export type GenerateTableSqlErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateTableSqlError =
  GenerateTableSqlErrors[keyof GenerateTableSqlErrors];

export type GenerateTableSqlResponses = {
  /**
   * Successful Response
   */
  200: SqlGenerationResponse;
};

export type GenerateTableSqlResponse =
  GenerateTableSqlResponses[keyof GenerateTableSqlResponses];

export type InterpretTableFiltersData = {
  body: InterpretFiltersRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tables/filters/interpret";
};

export type InterpretTableFiltersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type InterpretTableFiltersError =
  InterpretTableFiltersErrors[keyof InterpretTableFiltersErrors];

export type InterpretTableFiltersResponses = {
  /**
   * Successful Response
   */
  200: InterpretFiltersResponse;
};

export type InterpretTableFiltersResponse =
  InterpretTableFiltersResponses[keyof InterpretTableFiltersResponses];

export type UpdateEnabledToolsData = {
  body: UpdateEnabledToolsRequest;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/credentials/{credential_id}/mcp/tools";
};

export type UpdateEnabledToolsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateEnabledToolsError =
  UpdateEnabledToolsErrors[keyof UpdateEnabledToolsErrors];

export type UpdateEnabledToolsResponses = {
  /**
   * Successful Response
   */
  200: McpToolsResponse;
};

export type UpdateEnabledToolsResponse =
  UpdateEnabledToolsResponses[keyof UpdateEnabledToolsResponses];

export type ReprobeToolsData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/credentials/{credential_id}/mcp/reprobe";
};

export type ReprobeToolsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReprobeToolsError = ReprobeToolsErrors[keyof ReprobeToolsErrors];

export type ReprobeToolsResponses = {
  /**
   * Successful Response
   */
  200: McpToolsResponse;
};

export type ReprobeToolsResponse =
  ReprobeToolsResponses[keyof ReprobeToolsResponses];

export type CustomMcpStartData = {
  body: CustomMcpStartRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/mcp/custom/start";
};

export type CustomMcpStartErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CustomMcpStartError =
  CustomMcpStartErrors[keyof CustomMcpStartErrors];

export type CustomMcpStartResponses = {
  /**
   * Successful Response
   */
  200: CustomMcpStartResponse;
};

export type CustomMcpStartResponse2 =
  CustomMcpStartResponses[keyof CustomMcpStartResponses];

export type CustomMcpConnectWithHeadersData = {
  body: CustomMcpHeadersConnectRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/mcp/custom/connect";
};

export type CustomMcpConnectWithHeadersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CustomMcpConnectWithHeadersError =
  CustomMcpConnectWithHeadersErrors[keyof CustomMcpConnectWithHeadersErrors];

export type CustomMcpConnectWithHeadersResponses = {
  /**
   * Successful Response
   */
  200: CustomMcpHeadersConnectResponse;
};

export type CustomMcpConnectWithHeadersResponse =
  CustomMcpConnectWithHeadersResponses[keyof CustomMcpConnectWithHeadersResponses];

export type GetJwksData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/.well-known/jwks.json";
};

export type GetJwksResponses = {
  /**
   * Successful Response
   */
  200: JwkSet;
};

export type GetJwksResponse = GetJwksResponses[keyof GetJwksResponses];

export type GetMcpIdentityData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/credentials/{credential_id}/mcp/identity";
};

export type GetMcpIdentityErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMcpIdentityError =
  GetMcpIdentityErrors[keyof GetMcpIdentityErrors];

export type GetMcpIdentityResponses = {
  /**
   * Successful Response
   */
  200: McpIdentityInfo;
};

export type GetMcpIdentityResponse =
  GetMcpIdentityResponses[keyof GetMcpIdentityResponses];

export type GetConfigData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/config";
};

export type GetConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConfigError = GetConfigErrors[keyof GetConfigErrors];

export type GetConfigResponses = {
  /**
   * Successful Response
   */
  200: GitSyncConfig;
};

export type GetConfigResponse = GetConfigResponses[keyof GetConfigResponses];

export type PutConfigData = {
  body: GitSyncConfig;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/config";
};

export type PutConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PutConfigError = PutConfigErrors[keyof PutConfigErrors];

export type PutConfigResponses = {
  /**
   * Successful Response
   */
  200: GitSyncConfig;
};

export type PutConfigResponse = PutConfigResponses[keyof PutConfigResponses];

export type TestConnectionData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/test";
};

export type TestConnectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TestConnectionError =
  TestConnectionErrors[keyof TestConnectionErrors];

export type TestConnectionResponses = {
  /**
   * Successful Response
   */
  200: GitSyncTestResult;
};

export type TestConnectionResponse =
  TestConnectionResponses[keyof TestConnectionResponses];

export type GetHealthData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/health";
};

export type GetHealthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetHealthError = GetHealthErrors[keyof GetHealthErrors];

export type GetHealthResponses = {
  /**
   * Successful Response
   */
  200: GitSyncHealth;
};

export type GetHealthResponse = GetHealthResponses[keyof GetHealthResponses];

export type DryRunData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/dry-run";
};

export type DryRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DryRunError = DryRunErrors[keyof DryRunErrors];

export type DryRunResponses = {
  /**
   * Response Dry Run
   *
   * Successful Response
   */
  200: Array<DryRunChange>;
};

export type DryRunResponse = DryRunResponses[keyof DryRunResponses];

export type ListReposData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Credential Id
     */
    credential_id?: string | null;
    /**
     * Provider
     */
    provider?: "github" | "gitlab" | "generic_git" | null;
  };
  url: "/groups/{group_id}/git-sync/repos";
};

export type ListReposErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListReposError = ListReposErrors[keyof ListReposErrors];

export type ListReposResponses = {
  /**
   * Response List Repos
   *
   * Successful Response
   */
  200: Array<RepoOut>;
};

export type ListReposResponse = ListReposResponses[keyof ListReposResponses];

export type TriggerRunData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/run";
};

export type TriggerRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TriggerRunError = TriggerRunErrors[keyof TriggerRunErrors];

export type TriggerRunResponses = {
  /**
   * Response Trigger Run
   *
   * Successful Response
   */
  200: Array<GitSyncRunOut>;
};

export type TriggerRunResponse = TriggerRunResponses[keyof TriggerRunResponses];

export type ListRunsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/runs";
};

export type ListRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListRunsError = ListRunsErrors[keyof ListRunsErrors];

export type ListRunsResponses = {
  /**
   * Response List Runs
   *
   * Successful Response
   */
  200: Array<GitSyncRunOut>;
};

export type ListRunsResponse = ListRunsResponses[keyof ListRunsResponses];

export type GetRunData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/runs/{run_id}";
};

export type GetRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunError = GetRunErrors[keyof GetRunErrors];

export type GetRunResponses = {
  /**
   * Successful Response
   */
  200: GitSyncRunDetail;
};

export type GetRunResponse = GetRunResponses[keyof GetRunResponses];

export type GetArtifactStatusData = {
  body?: never;
  path: {
    kind: ArtifactKind;
    /**
     * Artifact Id
     */
    artifact_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/git-sync/artifacts/{kind}/{artifact_id}";
};

export type GetArtifactStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetArtifactStatusError =
  GetArtifactStatusErrors[keyof GetArtifactStatusErrors];

export type GetArtifactStatusResponses = {
  /**
   * Successful Response
   */
  200: ArtifactSyncStatus;
};

export type GetArtifactStatusResponse =
  GetArtifactStatusResponses[keyof GetArtifactStatusResponses];

export type PushOneArtifactData = {
  body?: never;
  path: {
    kind: ArtifactKind;
    /**
     * Artifact Id
     */
    artifact_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * With Dependencies
     */
    with_dependencies?: boolean;
  };
  url: "/groups/{group_id}/git-sync/artifacts/{kind}/{artifact_id}/push";
};

export type PushOneArtifactErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PushOneArtifactError =
  PushOneArtifactErrors[keyof PushOneArtifactErrors];

export type PushOneArtifactResponses = {
  /**
   * Successful Response
   */
  200: GitSyncRunOut;
};

export type PushOneArtifactResponse =
  PushOneArtifactResponses[keyof PushOneArtifactResponses];

export type PullOneArtifactData = {
  body?: never;
  path: {
    kind: ArtifactKind;
    /**
     * Artifact Id
     */
    artifact_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * With Dependencies
     */
    with_dependencies?: boolean;
  };
  url: "/groups/{group_id}/git-sync/artifacts/{kind}/{artifact_id}/pull";
};

export type PullOneArtifactErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PullOneArtifactError =
  PullOneArtifactErrors[keyof PullOneArtifactErrors];

export type PullOneArtifactResponses = {
  /**
   * Successful Response
   */
  200: GitSyncRunOut;
};

export type PullOneArtifactResponse =
  PullOneArtifactResponses[keyof PullOneArtifactResponses];

export type VerifyRunOwnerData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/verify_user";
};

export type VerifyRunOwnerErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type VerifyRunOwnerError =
  VerifyRunOwnerErrors[keyof VerifyRunOwnerErrors];

export type VerifyRunOwnerResponses = {
  /**
   * Response Verify Run Owner
   *
   * Successful Response
   */
  200: boolean;
};

export type VerifyRunOwnerResponse =
  VerifyRunOwnerResponses[keyof VerifyRunOwnerResponses];

export type GetWorkflowRunsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * User Id
     */
    user_id?: string | null;
    /**
     * Source
     */
    source?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Workflow Type
     */
    workflow_type?: WorkflowType | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/runs";
};

export type GetWorkflowRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowRunsError =
  GetWorkflowRunsErrors[keyof GetWorkflowRunsErrors];

export type GetWorkflowRunsResponses = {
  /**
   * Successful Response
   */
  200: PageRunNoIo;
};

export type GetWorkflowRunsResponse =
  GetWorkflowRunsResponses[keyof GetWorkflowRunsResponses];

export type V0CreateRunData = {
  body: CreateRunRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Profile
     */
    profile?: boolean;
    /**
     * Source
     */
    source?: string;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/runs";
};

export type V0CreateRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type V0CreateRunError = V0CreateRunErrors[keyof V0CreateRunErrors];

export type V0CreateRunResponses = {
  /**
   * Successful Response
   */
  200: Run;
};

export type V0CreateRunResponse =
  V0CreateRunResponses[keyof V0CreateRunResponses];

export type V0GetRunData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/runs/{run_id}";
};

export type V0GetRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type V0GetRunError = V0GetRunErrors[keyof V0GetRunErrors];

export type V0GetRunResponses = {
  /**
   * Successful Response
   */
  200: Run;
};

export type V0GetRunResponse = V0GetRunResponses[keyof V0GetRunResponses];

export type GetRunByIdData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}";
};

export type GetRunByIdErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunByIdError = GetRunByIdErrors[keyof GetRunByIdErrors];

export type GetRunByIdResponses = {
  /**
   * Successful Response
   */
  200: RunNoIo;
};

export type GetRunByIdResponse = GetRunByIdResponses[keyof GetRunByIdResponses];

export type GetWorkflowForRunData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Workflow Id
     */
    workflow_id?: string | null;
  };
  url: "/groups/{group_id}/runs/{run_id}/workflow";
};

export type GetWorkflowForRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowForRunError =
  GetWorkflowForRunErrors[keyof GetWorkflowForRunErrors];

export type GetWorkflowForRunResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetRunStateData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/state";
};

export type GetRunStateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunStateError = GetRunStateErrors[keyof GetRunStateErrors];

export type GetRunStateResponses = {
  /**
   * Response Get Run State
   *
   * Successful Response
   */
  200: RunState | null;
};

export type GetRunStateResponse =
  GetRunStateResponses[keyof GetRunStateResponses];

export type GetRunSubflowIterationData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Path
     */
    path: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/subflow-iterations/{path}";
};

export type GetRunSubflowIterationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunSubflowIterationError =
  GetRunSubflowIterationErrors[keyof GetRunSubflowIterationErrors];

export type GetRunSubflowIterationResponses = {
  /**
   * Response Get Run Subflow Iteration
   *
   * Successful Response
   */
  200: WorkflowProgress | null;
};

export type GetRunSubflowIterationResponse =
  GetRunSubflowIterationResponses[keyof GetRunSubflowIterationResponses];

export type GetRunIoData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Node Id
     */
    node_id: string;
    /**
     * It
     */
    it: number;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Skip Memory Cache
     *
     * Skip memory cache lookup (for testing cold storage fallback)
     */
    skip_memory_cache?: boolean;
    /**
     * List Offset
     *
     * Index of the first list element to return. List values are capped at 100 items per response; page through longer outputs by stepping this offset. `total_size` always reports the full length.
     */
    list_offset?: number;
  };
  url: "/groups/{group_id}/runs/{run_id}/io/{node_id}/{it}";
};

export type GetRunIoErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunIoError = GetRunIoErrors[keyof GetRunIoErrors];

export type GetRunIoResponses = {
  /**
   * Successful Response
   */
  200: RunIo;
};

export type GetRunIoResponse = GetRunIoResponses[keyof GetRunIoResponses];

export type GetRunLogsData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Node Id
     */
    node_id: string;
    /**
     * It
     */
    it: number;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/logs/{node_id}/{it}";
};

export type GetRunLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunLogsError = GetRunLogsErrors[keyof GetRunLogsErrors];

export type GetRunLogsResponses = {
  /**
   * Successful Response
   */
  200: RunLogs;
};

export type GetRunLogsResponse = GetRunLogsResponses[keyof GetRunLogsResponses];

export type GetRunExecutionLogsData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Force Source
     *
     * Force reading from a specific source: 'hot' (Redis) or 'cold' (S3)
     */
    force_source?: string | null;
  };
  url: "/groups/{group_id}/runs/{run_id}/execution-logs";
};

export type GetRunExecutionLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunExecutionLogsError =
  GetRunExecutionLogsErrors[keyof GetRunExecutionLogsErrors];

export type GetRunExecutionLogsResponses = {
  /**
   * Successful Response
   */
  200: ExecutionLogs;
};

export type GetRunExecutionLogsResponse =
  GetRunExecutionLogsResponses[keyof GetRunExecutionLogsResponses];

export type GetRunSpansData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/spans";
};

export type GetRunSpansErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunSpansError = GetRunSpansErrors[keyof GetRunSpansErrors];

export type GetRunSpansResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type StopRunData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/runs/{run_id}/stop";
};

export type StopRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StopRunError = StopRunErrors[keyof StopRunErrors];

export type StopRunResponses = {
  /**
   * Successful Response
   */
  200: Run;
};

export type StopRunResponse = StopRunResponses[keyof StopRunResponses];

export type ResumeRunData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/runs/{run_id}/resume";
};

export type ResumeRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResumeRunError = ResumeRunErrors[keyof ResumeRunErrors];

export type ResumeRunResponses = {
  /**
   * Successful Response
   */
  200: Run;
};

export type ResumeRunResponse = ResumeRunResponses[keyof ResumeRunResponses];

export type GetRunsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * From Date
     */
    from_date?: string | null;
    /**
     * To Date
     */
    to_date?: string | null;
    /**
     * Status
     */
    status?: string | RunStatus | null;
    /**
     * Source
     */
    source?: string | null;
    /**
     * User Id
     */
    user_id?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Workflow Id
     */
    workflow_id?: string | null;
    /**
     * Kb Id
     */
    kb_id?: string | null;
    /**
     * Workflow Type
     */
    workflow_type?: WorkflowType | null;
    /**
     * Tags
     */
    tags?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/runs";
};

export type GetRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRunsError = GetRunsErrors[keyof GetRunsErrors];

export type GetRunsResponses = {
  /**
   * Successful Response
   */
  200: PageRunNoIo;
};

export type GetRunsResponse = GetRunsResponses[keyof GetRunsResponses];

export type SearchWorkflowRunsData = {
  body: SearchRunRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/workflow/{workflow_id}/runs/search";
};

export type SearchWorkflowRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchWorkflowRunsError =
  SearchWorkflowRunsErrors[keyof SearchWorkflowRunsErrors];

export type SearchWorkflowRunsResponses = {
  /**
   * Successful Response
   */
  200: PageRunWithSearchData;
};

export type SearchWorkflowRunsResponse =
  SearchWorkflowRunsResponses[keyof SearchWorkflowRunsResponses];

export type SearchGroupRunsData = {
  body: SearchRunRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/runs/search";
};

export type SearchGroupRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchGroupRunsError =
  SearchGroupRunsErrors[keyof SearchGroupRunsErrors];

export type SearchGroupRunsResponses = {
  /**
   * Successful Response
   */
  200: PageRunWithSearchData;
};

export type SearchGroupRunsResponse =
  SearchGroupRunsResponses[keyof SearchGroupRunsResponses];

export type RunEventsData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Etag
     */
    etag?: string;
  };
  url: "/groups/{group_id}/runs/{run_id}/events";
};

export type RunEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RunEventsError = RunEventsErrors[keyof RunEventsErrors];

export type RunEventsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BulkRerunData = {
  body: BulkRunActionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/bulk/rerun";
};

export type BulkRerunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkRerunError = BulkRerunErrors[keyof BulkRerunErrors];

export type BulkRerunResponses = {
  /**
   * Successful Response
   */
  200: BulkRunActionResult;
};

export type BulkRerunResponse = BulkRerunResponses[keyof BulkRerunResponses];

export type BulkRetryData = {
  body: BulkRunActionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/bulk/retry";
};

export type BulkRetryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkRetryError = BulkRetryErrors[keyof BulkRetryErrors];

export type BulkRetryResponses = {
  /**
   * Successful Response
   */
  200: BulkRunActionResult;
};

export type BulkRetryResponse = BulkRetryResponses[keyof BulkRetryResponses];

export type BulkStopData = {
  body: BulkRunActionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/bulk/stop";
};

export type BulkStopErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkStopError = BulkStopErrors[keyof BulkStopErrors];

export type BulkStopResponses = {
  /**
   * Successful Response
   */
  200: BulkRunActionResult;
};

export type BulkStopResponse = BulkStopResponses[keyof BulkStopResponses];

export type BulkResumeData = {
  body: BulkRunActionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/bulk/resume";
};

export type BulkResumeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkResumeError = BulkResumeErrors[keyof BulkResumeErrors];

export type BulkResumeResponses = {
  /**
   * Successful Response
   */
  200: BulkRunActionResult;
};

export type BulkResumeResponse = BulkResumeResponses[keyof BulkResumeResponses];

export type BulkDeleteData = {
  body: BulkRunActionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/bulk/delete";
};

export type BulkDeleteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkDeleteError = BulkDeleteErrors[keyof BulkDeleteErrors];

export type BulkDeleteResponses = {
  /**
   * Successful Response
   */
  200: BulkRunActionResult;
};

export type BulkDeleteResponse = BulkDeleteResponses[keyof BulkDeleteResponses];

export type BulkExportRunsCsvData = {
  body: BulkRunActionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/bulk/export";
};

export type BulkExportRunsCsvErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkExportRunsCsvError =
  BulkExportRunsCsvErrors[keyof BulkExportRunsCsvErrors];

export type BulkExportRunsCsvResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetCompatibleMimeTypesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-base/mime-types";
};

export type GetCompatibleMimeTypesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCompatibleMimeTypesError =
  GetCompatibleMimeTypesErrors[keyof GetCompatibleMimeTypesErrors];

export type GetCompatibleMimeTypesResponses = {
  /**
   * Response Get Compatible Mime Types
   *
   * Successful Response
   */
  200: Array<MimeTypeInfo>;
};

export type GetCompatibleMimeTypesResponse =
  GetCompatibleMimeTypesResponses[keyof GetCompatibleMimeTypesResponses];

export type GetKnowledgeBasesV2Data = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Query
     */
    query?: string | null;
    /**
     * Tags
     */
    tags?: string | null;
    /**
     * Ignore Ids
     */
    ignore_ids?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Ids
     */
    ids?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/v2/groups/{group_id}/knowledge-bases";
};

export type GetKnowledgeBasesV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBasesV2Error =
  GetKnowledgeBasesV2Errors[keyof GetKnowledgeBasesV2Errors];

export type GetKnowledgeBasesV2Responses = {
  /**
   * Successful Response
   */
  200: PageKnowledgeBaseV2WithTags;
};

export type GetKnowledgeBasesV2Response =
  GetKnowledgeBasesV2Responses[keyof GetKnowledgeBasesV2Responses];

export type AddKnowledgeBaseV2Data = {
  body: CreateKnowledgeBase;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases";
};

export type AddKnowledgeBaseV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddKnowledgeBaseV2Error =
  AddKnowledgeBaseV2Errors[keyof AddKnowledgeBaseV2Errors];

export type AddKnowledgeBaseV2Responses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseV2;
};

export type AddKnowledgeBaseV2Response =
  AddKnowledgeBaseV2Responses[keyof AddKnowledgeBaseV2Responses];

export type DeleteKnowledgeBaseV2Data = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}";
};

export type DeleteKnowledgeBaseV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteKnowledgeBaseV2Error =
  DeleteKnowledgeBaseV2Errors[keyof DeleteKnowledgeBaseV2Errors];

export type DeleteKnowledgeBaseV2Responses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteKnowledgeBaseV2Response =
  DeleteKnowledgeBaseV2Responses[keyof DeleteKnowledgeBaseV2Responses];

export type GetKnowledgeBaseV2Data = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}";
};

export type GetKnowledgeBaseV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBaseV2Error =
  GetKnowledgeBaseV2Errors[keyof GetKnowledgeBaseV2Errors];

export type GetKnowledgeBaseV2Responses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseWithDocuments;
};

export type GetKnowledgeBaseV2Response =
  GetKnowledgeBaseV2Responses[keyof GetKnowledgeBaseV2Responses];

export type UpdateKnowledgeBaseV2Data = {
  body: UpdateKnowledgeBase;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Needs Reingestion
     *
     * Whether to signal reingestion for all documents
     */
    needs_reingestion?: boolean;
    /**
     * Reingestion Mimetype
     *
     * Whether to signal reingestion for all documents
     */
    reingestion_mimetype?: string | null;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}";
};

export type UpdateKnowledgeBaseV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateKnowledgeBaseV2Error =
  UpdateKnowledgeBaseV2Errors[keyof UpdateKnowledgeBaseV2Errors];

export type UpdateKnowledgeBaseV2Responses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseV2;
};

export type UpdateKnowledgeBaseV2Response =
  UpdateKnowledgeBaseV2Responses[keyof UpdateKnowledgeBaseV2Responses];

export type RepairKnowledgeBaseV2Data = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/repair";
};

export type RepairKnowledgeBaseV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RepairKnowledgeBaseV2Error =
  RepairKnowledgeBaseV2Errors[keyof RepairKnowledgeBaseV2Errors];

export type RepairKnowledgeBaseV2Responses = {
  /**
   * Successful Response
   */
  200: KbHealth;
};

export type RepairKnowledgeBaseV2Response =
  RepairKnowledgeBaseV2Responses[keyof RepairKnowledgeBaseV2Responses];

export type ExportKnowledgeBaseV2Data = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Export wire format. `auto` (default) emits the legacy base64-encoded bundle for back-compat; pass `v4` for plaintext multi-doc YAML (`.nx`), or `v3` for base64 explicitly.
     */
    version?: ExportFormat;
    /**
     * Set Active On Import
     */
    set_active_on_import?: boolean;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/export";
};

export type ExportKnowledgeBaseV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportKnowledgeBaseV2Error =
  ExportKnowledgeBaseV2Errors[keyof ExportKnowledgeBaseV2Errors];

export type ExportKnowledgeBaseV2Responses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SearchKnowledgeBaseData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Query
     */
    query: string;
    /**
     * Prefix
     */
    prefix?: string | null;
    /**
     * Date From
     */
    date_from?: string | null;
    /**
     * Date To
     */
    date_to?: string | null;
    /**
     * Date Field
     */
    date_field?: string;
    /**
     * Recency Boost
     */
    recency_boost?: number | null;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/search";
};

export type SearchKnowledgeBaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SearchKnowledgeBaseError =
  SearchKnowledgeBaseErrors[keyof SearchKnowledgeBaseErrors];

export type SearchKnowledgeBaseResponses = {
  /**
   * Response Search Knowledge Base
   *
   * Successful Response
   */
  200: Array<SearchResult>;
};

export type SearchKnowledgeBaseResponse =
  SearchKnowledgeBaseResponses[keyof SearchKnowledgeBaseResponses];

export type AddKnowledgeBaseDocumentData = {
  body: CreateDocument;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/document";
};

export type AddKnowledgeBaseDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddKnowledgeBaseDocumentError =
  AddKnowledgeBaseDocumentErrors[keyof AddKnowledgeBaseDocumentErrors];

export type AddKnowledgeBaseDocumentResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseDocument;
};

export type AddKnowledgeBaseDocumentResponse =
  AddKnowledgeBaseDocumentResponses[keyof AddKnowledgeBaseDocumentResponses];

export type DeleteKnowledgeBaseDocumentData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/document/{document_id}";
};

export type DeleteKnowledgeBaseDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteKnowledgeBaseDocumentError =
  DeleteKnowledgeBaseDocumentErrors[keyof DeleteKnowledgeBaseDocumentErrors];

export type DeleteKnowledgeBaseDocumentResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseDocument;
};

export type DeleteKnowledgeBaseDocumentResponse =
  DeleteKnowledgeBaseDocumentResponses[keyof DeleteKnowledgeBaseDocumentResponses];

export type GetKnowledgeBaseDocumentData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/document/{document_id}";
};

export type GetKnowledgeBaseDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBaseDocumentError =
  GetKnowledgeBaseDocumentErrors[keyof GetKnowledgeBaseDocumentErrors];

export type GetKnowledgeBaseDocumentResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseDocument;
};

export type GetKnowledgeBaseDocumentResponse =
  GetKnowledgeBaseDocumentResponses[keyof GetKnowledgeBaseDocumentResponses];

export type UpdateKnowledgeBaseDocumentData = {
  body: UpdateDocument;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/document/{document_id}";
};

export type UpdateKnowledgeBaseDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateKnowledgeBaseDocumentError =
  UpdateKnowledgeBaseDocumentErrors[keyof UpdateKnowledgeBaseDocumentErrors];

export type UpdateKnowledgeBaseDocumentResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseDocument;
};

export type UpdateKnowledgeBaseDocumentResponse =
  UpdateKnowledgeBaseDocumentResponses[keyof UpdateKnowledgeBaseDocumentResponses];

export type GetKnowledgeBaseDocumentsData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    status: DocumentStatus;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Query
     */
    query?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/documents/{status}";
};

export type GetKnowledgeBaseDocumentsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBaseDocumentsError =
  GetKnowledgeBaseDocumentsErrors[keyof GetKnowledgeBaseDocumentsErrors];

export type GetKnowledgeBaseDocumentsResponses = {
  /**
   * Successful Response
   */
  200: PageKnowledgeBaseDocument;
};

export type GetKnowledgeBaseDocumentsResponse =
  GetKnowledgeBaseDocumentsResponses[keyof GetKnowledgeBaseDocumentsResponses];

export type BulkDeleteKnowledgeBaseDocumentsData = {
  body: KnowledgeBaseBulkDeleteRequest;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/documents/bulk/delete";
};

export type BulkDeleteKnowledgeBaseDocumentsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkDeleteKnowledgeBaseDocumentsError =
  BulkDeleteKnowledgeBaseDocumentsErrors[keyof BulkDeleteKnowledgeBaseDocumentsErrors];

export type BulkDeleteKnowledgeBaseDocumentsResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseBulkDeleteResult;
};

export type BulkDeleteKnowledgeBaseDocumentsResponse =
  BulkDeleteKnowledgeBaseDocumentsResponses[keyof BulkDeleteKnowledgeBaseDocumentsResponses];

export type DismissKnowledgeBaseDocumentData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/document/{document_id}/dismiss";
};

export type DismissKnowledgeBaseDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DismissKnowledgeBaseDocumentError =
  DismissKnowledgeBaseDocumentErrors[keyof DismissKnowledgeBaseDocumentErrors];

export type DismissKnowledgeBaseDocumentResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseDocument;
};

export type DismissKnowledgeBaseDocumentResponse =
  DismissKnowledgeBaseDocumentResponses[keyof DismissKnowledgeBaseDocumentResponses];

export type UploadTrainKnowledgeBaseData = {
  body: BodyUploadTrainKnowledgeBase;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Prefix
     *
     * The path prefix where to upload
     */
    prefix?: string;
    /**
     * Duplicate File Mode
     *
     * The mode to handle duplicate files
     */
    duplicate_file_mode?: "overwrite" | "skip" | "append";
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/upload_train";
};

export type UploadTrainKnowledgeBaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UploadTrainKnowledgeBaseError =
  UploadTrainKnowledgeBaseErrors[keyof UploadTrainKnowledgeBaseErrors];

export type UploadTrainKnowledgeBaseResponses = {
  /**
   * Response Upload Train Knowledge Base
   *
   * Successful Response
   */
  200: Array<string>;
};

export type UploadTrainKnowledgeBaseResponse =
  UploadTrainKnowledgeBaseResponses[keyof UploadTrainKnowledgeBaseResponses];

export type FetchKnowledgeBaseCustomTypesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/v2/knowledge-bases/types";
};

export type FetchKnowledgeBaseCustomTypesResponses = {
  /**
   * Response Fetch Knowledge Base Custom Types
   *
   * Successful Response
   */
  200: Array<KnowledgeBaseType>;
};

export type FetchKnowledgeBaseCustomTypesResponse =
  FetchKnowledgeBaseCustomTypesResponses[keyof FetchKnowledgeBaseCustomTypesResponses];

export type GenericTrainKnowledgeBaseData = {
  body: GenericTrainBody;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Prefix
     *
     * The path prefix where to upload
     */
    prefix?: string;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/generic_train";
};

export type GenericTrainKnowledgeBaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenericTrainKnowledgeBaseError =
  GenericTrainKnowledgeBaseErrors[keyof GenericTrainKnowledgeBaseErrors];

export type GenericTrainKnowledgeBaseResponses = {
  /**
   * Response Generic Train Knowledge Base
   *
   * Successful Response
   */
  200: Array<string>;
};

export type GenericTrainKnowledgeBaseResponse =
  GenericTrainKnowledgeBaseResponses[keyof GenericTrainKnowledgeBaseResponses];

export type RetryTrainKnowledgeBaseData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Force Reingestion
     *
     * If True, reingest ALL documents (except folders), not just the errored and outdated ones
     */
    force_reingestion?: boolean;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/retry_all";
};

export type RetryTrainKnowledgeBaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RetryTrainKnowledgeBaseError =
  RetryTrainKnowledgeBaseErrors[keyof RetryTrainKnowledgeBaseErrors];

export type RetryTrainKnowledgeBaseResponses = {
  /**
   * Response Retry Train Knowledge Base
   *
   * Successful Response
   */
  200: Array<string>;
};

export type RetryTrainKnowledgeBaseResponse =
  RetryTrainKnowledgeBaseResponses[keyof RetryTrainKnowledgeBaseResponses];

export type GenerateKnowledgeBaseSummaryData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/generate-summary";
};

export type GenerateKnowledgeBaseSummaryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateKnowledgeBaseSummaryError =
  GenerateKnowledgeBaseSummaryErrors[keyof GenerateKnowledgeBaseSummaryErrors];

export type GenerateKnowledgeBaseSummaryResponses = {
  /**
   * Successful Response
   */
  200: GenerateKbSummaryResponse;
};

export type GenerateKnowledgeBaseSummaryResponse =
  GenerateKnowledgeBaseSummaryResponses[keyof GenerateKnowledgeBaseSummaryResponses];

export type RetryDocumentIngestionData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/document/{document_id}/retry";
};

export type RetryDocumentIngestionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RetryDocumentIngestionError =
  RetryDocumentIngestionErrors[keyof RetryDocumentIngestionErrors];

export type RetryDocumentIngestionResponses = {
  /**
   * Response Retry Document Ingestion
   *
   * Successful Response
   */
  200: string;
};

export type RetryDocumentIngestionResponse =
  RetryDocumentIngestionResponses[keyof RetryDocumentIngestionResponses];

export type GetKnowledgeBaseRunningJobsData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Status
     */
    status?: RunStatus | null;
    /**
     * Run Ids
     */
    run_ids?: string | null;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/runs";
};

export type GetKnowledgeBaseRunningJobsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBaseRunningJobsError =
  GetKnowledgeBaseRunningJobsErrors[keyof GetKnowledgeBaseRunningJobsErrors];

export type GetKnowledgeBaseRunningJobsResponses = {
  /**
   * Response Get Knowledge Base Running Jobs
   *
   * Successful Response
   */
  200: Array<Run>;
};

export type GetKnowledgeBaseRunningJobsResponse =
  GetKnowledgeBaseRunningJobsResponses[keyof GetKnowledgeBaseRunningJobsResponses];

export type GetKnowledgeBaseIngestionJobsData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/ingestion-jobs";
};

export type GetKnowledgeBaseIngestionJobsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBaseIngestionJobsError =
  GetKnowledgeBaseIngestionJobsErrors[keyof GetKnowledgeBaseIngestionJobsErrors];

export type GetKnowledgeBaseIngestionJobsResponses = {
  /**
   * Response Get Knowledge Base Ingestion Jobs
   *
   * Successful Response
   */
  200: Array<RunIngestion>;
};

export type GetKnowledgeBaseIngestionJobsResponse =
  GetKnowledgeBaseIngestionJobsResponses[keyof GetKnowledgeBaseIngestionJobsResponses];

export type GetKnowledgeBaseIngestionRunsData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Status
     */
    status?: RunStatus | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/ingestion-runs";
};

export type GetKnowledgeBaseIngestionRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKnowledgeBaseIngestionRunsError =
  GetKnowledgeBaseIngestionRunsErrors[keyof GetKnowledgeBaseIngestionRunsErrors];

export type GetKnowledgeBaseIngestionRunsResponses = {
  /**
   * Successful Response
   */
  200: PageRunIngestion;
};

export type GetKnowledgeBaseIngestionRunsResponse =
  GetKnowledgeBaseIngestionRunsResponses[keyof GetKnowledgeBaseIngestionRunsResponses];

export type DismissKnowledgeBaseRunData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/runs/{run_id}/dismiss";
};

export type DismissKnowledgeBaseRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DismissKnowledgeBaseRunError =
  DismissKnowledgeBaseRunErrors[keyof DismissKnowledgeBaseRunErrors];

export type DismissKnowledgeBaseRunResponses = {
  /**
   * Successful Response
   */
  200: Run;
};

export type DismissKnowledgeBaseRunResponse =
  DismissKnowledgeBaseRunResponses[keyof DismissKnowledgeBaseRunResponses];

export type ListKbSyncsData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/sync";
};

export type ListKbSyncsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListKbSyncsError = ListKbSyncsErrors[keyof ListKbSyncsErrors];

export type ListKbSyncsResponses = {
  /**
   * Response List Kb Syncs
   *
   * Successful Response
   */
  200: Array<SyncStatusResponse>;
};

export type ListKbSyncsResponse =
  ListKbSyncsResponses[keyof ListKbSyncsResponses];

export type EnableKbSyncData = {
  body: EnableSyncRequest;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/sync";
};

export type EnableKbSyncErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EnableKbSyncError = EnableKbSyncErrors[keyof EnableKbSyncErrors];

export type EnableKbSyncResponses = {
  /**
   * Successful Response
   */
  201: SyncStatusResponse;
};

export type EnableKbSyncResponse =
  EnableKbSyncResponses[keyof EnableKbSyncResponses];

export type DisableKbSyncData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/sync/{trigger_id}";
};

export type DisableKbSyncErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisableKbSyncError = DisableKbSyncErrors[keyof DisableKbSyncErrors];

export type DisableKbSyncResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DisableKbSyncResponse =
  DisableKbSyncResponses[keyof DisableKbSyncResponses];

export type UpdateKbSyncData = {
  body: UpdateSyncRequest;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/sync/{trigger_id}";
};

export type UpdateKbSyncErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateKbSyncError = UpdateKbSyncErrors[keyof UpdateKbSyncErrors];

export type UpdateKbSyncResponses = {
  /**
   * Successful Response
   */
  200: SyncStatusResponse;
};

export type UpdateKbSyncResponse =
  UpdateKbSyncResponses[keyof UpdateKbSyncResponses];

export type UnsyncKbDocumentData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/sync/{trigger_id}/unsync/{document_id}";
};

export type UnsyncKbDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UnsyncKbDocumentError =
  UnsyncKbDocumentErrors[keyof UnsyncKbDocumentErrors];

export type UnsyncKbDocumentResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseDocument;
};

export type UnsyncKbDocumentResponse =
  UnsyncKbDocumentResponses[keyof UnsyncKbDocumentResponses];

export type ForceKbSyncData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/sync/{trigger_id}/trigger";
};

export type ForceKbSyncErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ForceKbSyncError = ForceKbSyncErrors[keyof ForceKbSyncErrors];

export type ForceKbSyncResponses = {
  /**
   * Successful Response
   */
  202: unknown;
};

export type GetTmpKnowledgeBaseData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Node Id
     */
    node_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{workflow_id}/{node_id}";
};

export type GetTmpKnowledgeBaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTmpKnowledgeBaseError =
  GetTmpKnowledgeBaseErrors[keyof GetTmpKnowledgeBaseErrors];

export type GetTmpKnowledgeBaseResponses = {
  /**
   * Successful Response
   */
  200: KnowledgeBaseWithDocuments;
};

export type GetTmpKnowledgeBaseResponse =
  GetTmpKnowledgeBaseResponses[keyof GetTmpKnowledgeBaseResponses];

export type StreamKbHealthData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/health/stream";
};

export type StreamKbHealthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StreamKbHealthError =
  StreamKbHealthErrors[keyof StreamKbHealthErrors];

export type StreamKbHealthResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type RunKbHealthData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/health/run";
};

export type RunKbHealthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RunKbHealthError = RunKbHealthErrors[keyof RunKbHealthErrors];

export type RunKbHealthResponses = {
  /**
   * Successful Response
   */
  200: KbHealthPipeline;
};

export type RunKbHealthResponse =
  RunKbHealthResponses[keyof RunKbHealthResponses];

export type KbHealthManifestData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-base/health-manifest";
};

export type KbHealthManifestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type KbHealthManifestError =
  KbHealthManifestErrors[keyof KbHealthManifestErrors];

export type KbHealthManifestResponses = {
  /**
   * Successful Response
   */
  200: KbHealthManifest;
};

export type KbHealthManifestResponse =
  KbHealthManifestResponses[keyof KbHealthManifestResponses];

export type RepairKbHealthData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Check Id
     */
    check_id:
      | "vector_db_connectivity"
      | "vectors_database"
      | "vector_table"
      | "embedding_dim"
      | "index_struct"
      | "embedding_provider"
      | "reranker"
      | "chunk_orphans"
      | "missing_chunks"
      | "metadata_drift"
      | "hnsw_index";
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/health/repair/{check_id}";
};

export type RepairKbHealthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RepairKbHealthError =
  RepairKbHealthErrors[keyof RepairKbHealthErrors];

export type RepairKbHealthResponses = {
  /**
   * Successful Response
   */
  200: KbRepairQueued;
};

export type RepairKbHealthResponse =
  RepairKbHealthResponses[keyof RepairKbHealthResponses];

export type WatchKbHealthRepairData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Check Id
     */
    check_id:
      | "vector_db_connectivity"
      | "vectors_database"
      | "vector_table"
      | "embedding_dim"
      | "index_struct"
      | "embedding_provider"
      | "reranker"
      | "chunk_orphans"
      | "missing_chunks"
      | "metadata_drift"
      | "hnsw_index";
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Job Id
     */
    job_id: number;
  };
  url: "/v2/groups/{group_id}/knowledge-bases/{knowledge_base_id}/health/repair/{check_id}/watch";
};

export type WatchKbHealthRepairErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type WatchKbHealthRepairError =
  WatchKbHealthRepairErrors[keyof WatchKbHealthRepairErrors];

export type WatchKbHealthRepairResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListTablesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables";
};

export type ListTablesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTablesError = ListTablesErrors[keyof ListTablesErrors];

export type ListTablesResponses = {
  /**
   * Response List Tables
   *
   * Successful Response
   */
  200: Array<TableWithCount>;
};

export type ListTablesResponse = ListTablesResponses[keyof ListTablesResponses];

export type CreateTableData = {
  body: CreateTableRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables";
};

export type CreateTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTableError = CreateTableErrors[keyof CreateTableErrors];

export type CreateTableResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type CreateTableResponse =
  CreateTableResponses[keyof CreateTableResponses];

export type ImportCsvData = {
  body: BodyImportCsv;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/import";
};

export type ImportCsvErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportCsvError = ImportCsvErrors[keyof ImportCsvErrors];

export type ImportCsvResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type ImportCsvResponse = ImportCsvResponses[keyof ImportCsvResponses];

export type ImportCsvIntoTableData = {
  body: BodyImportCsvIntoTable;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/import";
};

export type ImportCsvIntoTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportCsvIntoTableError =
  ImportCsvIntoTableErrors[keyof ImportCsvIntoTableErrors];

export type ImportCsvIntoTableResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type ImportCsvIntoTableResponse =
  ImportCsvIntoTableResponses[keyof ImportCsvIntoTableResponses];

export type ImportCsvRowsData = {
  body: BodyImportCsvRows;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/import-rows";
};

export type ImportCsvRowsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportCsvRowsError = ImportCsvRowsErrors[keyof ImportCsvRowsErrors];

export type ImportCsvRowsResponses = {
  /**
   * Successful Response
   */
  200: ImportRowsResponse;
};

export type ImportCsvRowsResponse =
  ImportCsvRowsResponses[keyof ImportCsvRowsResponses];

export type ImportPreviewData = {
  body: BodyImportPreview;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Sheet
     *
     * xlsx only: worksheet to preview
     */
    sheet?: string | null;
  };
  url: "/v2/groups/{group_id}/tables/import/preview";
};

export type ImportPreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportPreviewError = ImportPreviewErrors[keyof ImportPreviewErrors];

export type ImportPreviewResponses = {
  /**
   * Successful Response
   */
  200: ImportPreviewResponse;
};

export type ImportPreviewResponse2 =
  ImportPreviewResponses[keyof ImportPreviewResponses];

export type ImportPlanData = {
  body: ImportPlanRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/import/plan";
};

export type ImportPlanErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportPlanError = ImportPlanErrors[keyof ImportPlanErrors];

export type ImportPlanResponses = {
  /**
   * Successful Response
   */
  200: ImportPlanResponse;
};

export type ImportPlanResponse2 =
  ImportPlanResponses[keyof ImportPlanResponses];

export type GetTableStatsData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/stats";
};

export type GetTableStatsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTableStatsError = GetTableStatsErrors[keyof GetTableStatsErrors];

export type GetTableStatsResponses = {
  /**
   * Successful Response
   */
  200: TableStats;
};

export type GetTableStatsResponse =
  GetTableStatsResponses[keyof GetTableStatsResponses];

export type DeleteTableData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}";
};

export type DeleteTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTableError = DeleteTableErrors[keyof DeleteTableErrors];

export type DeleteTableResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetTableData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}";
};

export type GetTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTableError = GetTableErrors[keyof GetTableErrors];

export type GetTableResponses = {
  /**
   * Successful Response
   */
  200: TableWithCount;
};

export type GetTableResponse = GetTableResponses[keyof GetTableResponses];

export type UpdateTableData = {
  body: UpdateTableRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}";
};

export type UpdateTableErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTableError = UpdateTableErrors[keyof UpdateTableErrors];

export type UpdateTableResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type UpdateTableResponse =
  UpdateTableResponses[keyof UpdateTableResponses];

export type AddColumnData = {
  body: AddColumnRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/columns";
};

export type AddColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddColumnError = AddColumnErrors[keyof AddColumnErrors];

export type AddColumnResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type AddColumnResponse = AddColumnResponses[keyof AddColumnResponses];

export type RenameColumnData = {
  body: RenameColumnRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/columns/{name}/rename";
};

export type RenameColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RenameColumnError = RenameColumnErrors[keyof RenameColumnErrors];

export type RenameColumnResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type RenameColumnResponse =
  RenameColumnResponses[keyof RenameColumnResponses];

export type ChangeColumnTypeData = {
  body: ChangeColumnTypeRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/columns/{name}/type";
};

export type ChangeColumnTypeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ChangeColumnTypeError =
  ChangeColumnTypeErrors[keyof ChangeColumnTypeErrors];

export type ChangeColumnTypeResponses = {
  /**
   * Successful Response
   */
  200: ChangeColumnTypeResponse;
};

export type ChangeColumnTypeResponse2 =
  ChangeColumnTypeResponses[keyof ChangeColumnTypeResponses];

export type DropColumnData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/columns/{name}";
};

export type DropColumnErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DropColumnError = DropColumnErrors[keyof DropColumnErrors];

export type DropColumnResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type DropColumnResponse = DropColumnResponses[keyof DropColumnResponses];

export type UpdateColumnLabelData = {
  body: UpdateColumnLabelRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/columns/{name}/label";
};

export type UpdateColumnLabelErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateColumnLabelError =
  UpdateColumnLabelErrors[keyof UpdateColumnLabelErrors];

export type UpdateColumnLabelResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type UpdateColumnLabelResponse =
  UpdateColumnLabelResponses[keyof UpdateColumnLabelResponses];

export type ReorderColumnsData = {
  body: ReorderColumnsRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/columns/reorder";
};

export type ReorderColumnsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReorderColumnsError =
  ReorderColumnsErrors[keyof ReorderColumnsErrors];

export type ReorderColumnsResponses = {
  /**
   * Successful Response
   */
  200: TableResponse;
};

export type ReorderColumnsResponse =
  ReorderColumnsResponses[keyof ReorderColumnsResponses];

export type ClearRowsData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/rows";
};

export type ClearRowsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ClearRowsError = ClearRowsErrors[keyof ClearRowsErrors];

export type ClearRowsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListRowsData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Limit
     */
    limit?: number;
    /**
     * Offset
     */
    offset?: number;
    /**
     * Order By
     */
    order_by?: string | null;
    /**
     * Descending
     */
    descending?: boolean;
    /**
     * Search
     */
    search?: string | null;
    /**
     * Filters
     *
     * JSON-encoded array of row filters
     */
    filters?: string | null;
  };
  url: "/v2/groups/{group_id}/tables/{table_id}/rows";
};

export type ListRowsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListRowsError = ListRowsErrors[keyof ListRowsErrors];

export type ListRowsResponses = {
  /**
   * Successful Response
   */
  200: RowsResponse;
};

export type ListRowsResponse = ListRowsResponses[keyof ListRowsResponses];

export type InsertRowData = {
  body: RowRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/rows";
};

export type InsertRowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type InsertRowError = InsertRowErrors[keyof InsertRowErrors];

export type InsertRowResponses = {
  /**
   * Successful Response
   */
  200: RowResponse;
};

export type InsertRowResponse = InsertRowResponses[keyof InsertRowResponses];

export type ExportTableCsvData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Search
     */
    search?: string | null;
    /**
     * Filters
     *
     * JSON-encoded array of row filters
     */
    filters?: string | null;
  };
  url: "/v2/groups/{group_id}/tables/{table_id}/export";
};

export type ExportTableCsvErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportTableCsvError =
  ExportTableCsvErrors[keyof ExportTableCsvErrors];

export type ExportTableCsvResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DeleteRowData = {
  body?: never;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Row Id
     */
    row_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/rows/{row_id}";
};

export type DeleteRowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteRowError = DeleteRowErrors[keyof DeleteRowErrors];

export type DeleteRowResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type UpdateRowData = {
  body: RowRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Row Id
     */
    row_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/rows/{row_id}";
};

export type UpdateRowErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateRowError = UpdateRowErrors[keyof UpdateRowErrors];

export type UpdateRowResponses = {
  /**
   * Successful Response
   */
  200: RowResponse;
};

export type UpdateRowResponse = UpdateRowResponses[keyof UpdateRowResponses];

export type BulkDeleteRowsData = {
  body: BulkDeleteRowsRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/rows/bulk-delete";
};

export type BulkDeleteRowsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkDeleteRowsError =
  BulkDeleteRowsErrors[keyof BulkDeleteRowsErrors];

export type BulkDeleteRowsResponses = {
  /**
   * Successful Response
   */
  200: BulkDeleteRowsResponse;
};

export type BulkDeleteRowsResponse2 =
  BulkDeleteRowsResponses[keyof BulkDeleteRowsResponses];

export type BulkDuplicateRowsData = {
  body: BulkDuplicateRowsRequest;
  path: {
    /**
     * Table Id
     */
    table_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/{table_id}/rows/bulk-duplicate";
};

export type BulkDuplicateRowsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkDuplicateRowsError =
  BulkDuplicateRowsErrors[keyof BulkDuplicateRowsErrors];

export type BulkDuplicateRowsResponses = {
  /**
   * Successful Response
   */
  200: BulkDuplicateRowsResponse;
};

export type BulkDuplicateRowsResponse2 =
  BulkDuplicateRowsResponses[keyof BulkDuplicateRowsResponses];

export type QueryTablesData = {
  body: QueryRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/query";
};

export type QueryTablesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type QueryTablesError = QueryTablesErrors[keyof QueryTablesErrors];

export type QueryTablesResponses = {
  /**
   * Successful Response
   */
  200: QueryResponse;
};

export type QueryTablesResponse =
  QueryTablesResponses[keyof QueryTablesResponses];

export type SummarizeTableWriteData = {
  body: WriteSummaryRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/write-summary";
};

export type SummarizeTableWriteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SummarizeTableWriteError =
  SummarizeTableWriteErrors[keyof SummarizeTableWriteErrors];

export type SummarizeTableWriteResponses = {
  /**
   * Response Summarize Table Write
   *
   * Successful Response
   */
  200: WriteSummaryResponse | null;
};

export type SummarizeTableWriteResponse =
  SummarizeTableWriteResponses[keyof SummarizeTableWriteResponses];

export type ExportQueryCsvData = {
  body: QueryExportRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/v2/groups/{group_id}/tables/query/export";
};

export type ExportQueryCsvErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportQueryCsvError =
  ExportQueryCsvErrors[keyof ExportQueryCsvErrors];

export type ExportQueryCsvResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SendRunLogsToSupportData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/send-logs-to-support";
};

export type SendRunLogsToSupportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SendRunLogsToSupportError =
  SendRunLogsToSupportErrors[keyof SendRunLogsToSupportErrors];

export type SendRunLogsToSupportResponses = {
  /**
   * Successful Response
   */
  200: SupportLogUploadResult;
};

export type SendRunLogsToSupportResponse =
  SendRunLogsToSupportResponses[keyof SendRunLogsToSupportResponses];

export type DownloadRunDebugExportData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/runs/{run_id}/debug-export";
};

export type DownloadRunDebugExportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DownloadRunDebugExportError =
  DownloadRunDebugExportErrors[keyof DownloadRunDebugExportErrors];

export type DownloadRunDebugExportResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetAvailableTriggersData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/triggers";
};

export type GetAvailableTriggersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAvailableTriggersError =
  GetAvailableTriggersErrors[keyof GetAvailableTriggersErrors];

export type GetAvailableTriggersResponses = {
  /**
   * Successful Response
   */
  200: TriggerList;
};

export type GetAvailableTriggersResponse =
  GetAvailableTriggersResponses[keyof GetAvailableTriggersResponses];

export type GetTriggerConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Trigger Id
     */
    trigger_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/triggers/{trigger_id}/config";
};

export type GetTriggerConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTriggerConfigError =
  GetTriggerConfigErrors[keyof GetTriggerConfigErrors];

export type GetTriggerConfigResponses = {
  /**
   * Successful Response
   */
  200: TriggerDefinition;
};

export type GetTriggerConfigResponse =
  GetTriggerConfigResponses[keyof GetTriggerConfigResponses];

export type GetBaseIntegrationConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Skip Cache
     */
    skip_cache?: boolean;
    /**
     * Provider Key
     */
    provider_key?: string | null;
  };
  url: "/groups/{group_id}/integrations/base/{integration_type}/config";
};

export type GetBaseIntegrationConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetBaseIntegrationConfigError =
  GetBaseIntegrationConfigErrors[keyof GetBaseIntegrationConfigErrors];

export type GetBaseIntegrationConfigResponses = {
  /**
   * Successful Response
   */
  200: ConfigResponse;
};

export type GetBaseIntegrationConfigResponse =
  GetBaseIntegrationConfigResponses[keyof GetBaseIntegrationConfigResponses];

export type SetupIntegrationData = {
  /**
   * Req
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Integration Name
     */
    integration_name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Provider Key
     */
    provider_key?: string | null;
  };
  url: "/groups/{group_id}/integrations/base/{integration_name}";
};

export type SetupIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetupIntegrationError =
  SetupIntegrationErrors[keyof SetupIntegrationErrors];

export type SetupIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type SetupIntegrationResponse =
  SetupIntegrationResponses[keyof SetupIntegrationResponses];

export type StartDeviceAuthData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/base/{integration_type}/device-auth";
};

export type StartDeviceAuthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartDeviceAuthError =
  StartDeviceAuthErrors[keyof StartDeviceAuthErrors];

export type StartDeviceAuthResponses = {
  /**
   * Successful Response
   */
  200: DeviceAuthStartResponse;
};

export type StartDeviceAuthResponse =
  StartDeviceAuthResponses[keyof StartDeviceAuthResponses];

export type PollDeviceAuthData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Session Id
     */
    session_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Credential Id
     */
    credential_id?: string | null;
  };
  url: "/groups/{group_id}/integrations/base/{integration_type}/device-auth/{session_id}";
};

export type PollDeviceAuthErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PollDeviceAuthError =
  PollDeviceAuthErrors[keyof PollDeviceAuthErrors];

export type PollDeviceAuthResponses = {
  /**
   * Successful Response
   */
  200: DeviceAuthPollResponse;
};

export type PollDeviceAuthResponse =
  PollDeviceAuthResponses[keyof PollDeviceAuthResponses];

export type DisconnectIntegrationData = {
  body?: never;
  path: {
    /**
     * Integration Name
     */
    integration_name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/base/{integration_name}/disconnect";
};

export type DisconnectIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectIntegrationError =
  DisconnectIntegrationErrors[keyof DisconnectIntegrationErrors];

export type DisconnectIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DisconnectIntegrationResponse =
  DisconnectIntegrationResponses[keyof DisconnectIntegrationResponses];

export type DeleteCredentialEndpointData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/credentials/{credential_id}";
};

export type DeleteCredentialEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteCredentialEndpointError =
  DeleteCredentialEndpointErrors[keyof DeleteCredentialEndpointErrors];

export type DeleteCredentialEndpointResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteCredentialEndpointResponse =
  DeleteCredentialEndpointResponses[keyof DeleteCredentialEndpointResponses];

export type GetCredentialPayloadData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/credentials/{credential_id}";
};

export type GetCredentialPayloadErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCredentialPayloadError =
  GetCredentialPayloadErrors[keyof GetCredentialPayloadErrors];

export type GetCredentialPayloadResponses = {
  /**
   * Successful Response
   */
  200: CredentialPayloadResponse;
};

export type GetCredentialPayloadResponse =
  GetCredentialPayloadResponses[keyof GetCredentialPayloadResponses];

export type UpdateCredentialData = {
  body: UpdateCredentialRequest;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/credentials/{credential_id}";
};

export type UpdateCredentialErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCredentialError =
  UpdateCredentialErrors[keyof UpdateCredentialErrors];

export type UpdateCredentialResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type UpdateCredentialResponse =
  UpdateCredentialResponses[keyof UpdateCredentialResponses];

export type RenameCredentialData = {
  body: RenameCredentialRequest;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/credentials/{credential_id}/label";
};

export type RenameCredentialErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RenameCredentialError =
  RenameCredentialErrors[keyof RenameCredentialErrors];

export type RenameCredentialResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type RenameCredentialResponse =
  RenameCredentialResponses[keyof RenameCredentialResponses];

export type ForceRefreshCredentialData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/credentials/{credential_id}/refresh";
};

export type ForceRefreshCredentialErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ForceRefreshCredentialError =
  ForceRefreshCredentialErrors[keyof ForceRefreshCredentialErrors];

export type ForceRefreshCredentialResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type ForceRefreshCredentialResponse =
  ForceRefreshCredentialResponses[keyof ForceRefreshCredentialResponses];

export type UpdateCredentialSettingsData = {
  body: UpdateCredentialSettingsRequest;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/credentials/{credential_id}/settings";
};

export type UpdateCredentialSettingsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCredentialSettingsError =
  UpdateCredentialSettingsErrors[keyof UpdateCredentialSettingsErrors];

export type UpdateCredentialSettingsResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type UpdateCredentialSettingsResponse =
  UpdateCredentialSettingsResponses[keyof UpdateCredentialSettingsResponses];

export type OauthStartData = {
  body: OAuthStartRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/oauth/start";
};

export type OauthStartErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type OauthStartError = OauthStartErrors[keyof OauthStartErrors];

export type OauthStartResponses = {
  /**
   * Successful Response
   */
  200: OAuthStartResponse;
};

export type OauthStartResponse = OauthStartResponses[keyof OauthStartResponses];

export type OauthCallbackData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/integrations/oauth/callback";
};

export type OauthCallbackResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListConnectionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/integrations/server";
};

export type ListConnectionsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ConnectionConfigData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/server/{integration_type}/config";
};

export type ConnectionConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConnectionConfigError =
  ConnectionConfigErrors[keyof ConnectionConfigErrors];

export type ConnectionConfigResponses = {
  /**
   * Successful Response
   */
  200: ConfigResponse;
};

export type ConnectionConfigResponse =
  ConnectionConfigResponses[keyof ConnectionConfigResponses];

export type CreateConnectionData = {
  /**
   * Payload
   */
  body: {
    [key: string]: JsonValueInput;
  };
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/server/{integration_type}";
};

export type CreateConnectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateConnectionError =
  CreateConnectionErrors[keyof CreateConnectionErrors];

export type CreateConnectionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ConnectionSendersData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
  };
  query?: never;
  url: "/admin/integrations/server/credentials/{credential_id}/senders";
};

export type ConnectionSendersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConnectionSendersError =
  ConnectionSendersErrors[keyof ConnectionSendersErrors];

export type ConnectionSendersResponses = {
  /**
   * Successful Response
   */
  200: SenderOptions;
};

export type ConnectionSendersResponse =
  ConnectionSendersResponses[keyof ConnectionSendersResponses];

export type DeleteConnectionData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
  };
  query?: never;
  url: "/admin/integrations/server/credentials/{credential_id}";
};

export type DeleteConnectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteConnectionError =
  DeleteConnectionErrors[keyof DeleteConnectionErrors];

export type DeleteConnectionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetConnectionData = {
  body?: never;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
  };
  query?: never;
  url: "/admin/integrations/server/credentials/{credential_id}";
};

export type GetConnectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConnectionError = GetConnectionErrors[keyof GetConnectionErrors];

export type GetConnectionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type UpdateConnectionData = {
  body: UpdateConnection;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
  };
  query?: never;
  url: "/admin/integrations/server/credentials/{credential_id}";
};

export type UpdateConnectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateConnectionError =
  UpdateConnectionErrors[keyof UpdateConnectionErrors];

export type UpdateConnectionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SetEmailDeliveryData = {
  body: EmailDeliverySettings;
  path?: never;
  query?: never;
  url: "/admin/integrations/server/email-delivery";
};

export type SetEmailDeliveryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetEmailDeliveryError =
  SetEmailDeliveryErrors[keyof SetEmailDeliveryErrors];

export type SetEmailDeliveryResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SetConnectionEnabledData = {
  body: ConnectionState;
  path: {
    /**
     * Credential Id
     */
    credential_id: string;
  };
  query?: never;
  url: "/admin/integrations/server/credentials/{credential_id}/enabled";
};

export type SetConnectionEnabledErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetConnectionEnabledError =
  SetConnectionEnabledErrors[keyof SetConnectionEnabledErrors];

export type SetConnectionEnabledResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListIntegrationsAdminData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/integrations";
};

export type ListIntegrationsAdminResponses = {
  /**
   * Response List Integrations Admin
   *
   * Successful Response
   */
  200: Array<IntegrationAdminStatus>;
};

export type ListIntegrationsAdminResponse =
  ListIntegrationsAdminResponses[keyof ListIntegrationsAdminResponses];

export type GetProviderConfigSchemaData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Provider Key
     */
    provider_key: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/providers/{provider_key}/config";
};

export type GetProviderConfigSchemaErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetProviderConfigSchemaError =
  GetProviderConfigSchemaErrors[keyof GetProviderConfigSchemaErrors];

export type GetProviderConfigSchemaResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DeleteProviderConfigEndpointData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Provider Key
     */
    provider_key: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/providers/{provider_key}";
};

export type DeleteProviderConfigEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteProviderConfigEndpointError =
  DeleteProviderConfigEndpointErrors[keyof DeleteProviderConfigEndpointErrors];

export type DeleteProviderConfigEndpointResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type SaveProviderConfigEndpointData = {
  body: SaveProviderConfigRequest;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Provider Key
     */
    provider_key: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/providers/{provider_key}";
};

export type SaveProviderConfigEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SaveProviderConfigEndpointError =
  SaveProviderConfigEndpointErrors[keyof SaveProviderConfigEndpointErrors];

export type SaveProviderConfigEndpointResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetIntegrationScopesEndpointData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/scopes";
};

export type GetIntegrationScopesEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIntegrationScopesEndpointError =
  GetIntegrationScopesEndpointErrors[keyof GetIntegrationScopesEndpointErrors];

export type GetIntegrationScopesEndpointResponses = {
  /**
   * Successful Response
   */
  200: IntegrationScopesResponse;
};

export type GetIntegrationScopesEndpointResponse =
  GetIntegrationScopesEndpointResponses[keyof GetIntegrationScopesEndpointResponses];

export type SetIntegrationScopesEndpointData = {
  body: SetActiveScopesRequest;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/scopes";
};

export type SetIntegrationScopesEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetIntegrationScopesEndpointError =
  SetIntegrationScopesEndpointErrors[keyof SetIntegrationScopesEndpointErrors];

export type SetIntegrationScopesEndpointResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetIntegrationEnabledData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/enabled";
};

export type GetIntegrationEnabledErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIntegrationEnabledError =
  GetIntegrationEnabledErrors[keyof GetIntegrationEnabledErrors];

export type GetIntegrationEnabledResponses = {
  /**
   * Response Get Integration Enabled
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetIntegrationEnabledResponse =
  GetIntegrationEnabledResponses[keyof GetIntegrationEnabledResponses];

export type SetIntegrationEnabledData = {
  body: IntegrationEnabledRequest;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/enabled";
};

export type SetIntegrationEnabledErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetIntegrationEnabledError =
  SetIntegrationEnabledErrors[keyof SetIntegrationEnabledErrors];

export type SetIntegrationEnabledResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetIntegrationSettingsEndpointData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/settings";
};

export type GetIntegrationSettingsEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIntegrationSettingsEndpointError =
  GetIntegrationSettingsEndpointErrors[keyof GetIntegrationSettingsEndpointErrors];

export type GetIntegrationSettingsEndpointResponses = {
  /**
   * Response Get Integration Settings Endpoint
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetIntegrationSettingsEndpointResponse =
  GetIntegrationSettingsEndpointResponses[keyof GetIntegrationSettingsEndpointResponses];

export type UpdateIntegrationSettingsEndpointData = {
  body: UpdateIntegrationSettingsRequest;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
  };
  query?: never;
  url: "/admin/integrations/{integration_type}/settings";
};

export type UpdateIntegrationSettingsEndpointErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateIntegrationSettingsEndpointError =
  UpdateIntegrationSettingsEndpointErrors[keyof UpdateIntegrationSettingsEndpointErrors];

export type UpdateIntegrationSettingsEndpointResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DisconnectOauthCredentialData = {
  body?: never;
  path: {
    /**
     * Integration Type
     */
    integration_type: string;
    /**
     * Provider Key
     */
    provider_key: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/oauth/{integration_type}/{provider_key}";
};

export type DisconnectOauthCredentialErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectOauthCredentialError =
  DisconnectOauthCredentialErrors[keyof DisconnectOauthCredentialErrors];

export type DisconnectOauthCredentialResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetGdriveFilesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Folder Id
     */
    folder_id?: string | null;
    /**
     * Query
     */
    query?: string | null;
    /**
     * Mime Type Filter
     */
    mime_type_filter?: string | null;
    /**
     * Credential Id
     */
    credential_id?: string | null;
    /**
     * Page Token
     */
    page_token?: string | null;
    /**
     * Source
     */
    source?: string;
  };
  url: "/groups/{group_id}/integrations/gdrive/files";
};

export type GetGdriveFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGdriveFilesError =
  GetGdriveFilesErrors[keyof GetGdriveFilesErrors];

export type GetGdriveFilesResponses = {
  /**
   * Successful Response
   */
  200: PaginatedDocInformation;
};

export type GetGdriveFilesResponse =
  GetGdriveFilesResponses[keyof GetGdriveFilesResponses];

export type GetGdriveSourcesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Credential Id
     */
    credential_id?: string | null;
  };
  url: "/groups/{group_id}/integrations/gdrive/sources";
};

export type GetGdriveSourcesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetGdriveSourcesError =
  GetGdriveSourcesErrors[keyof GetGdriveSourcesErrors];

export type GetGdriveSourcesResponses = {
  /**
   * Successful Response
   */
  200: GoogleDriveSources;
};

export type GetGdriveSourcesResponse =
  GetGdriveSourcesResponses[keyof GetGdriveSourcesResponses];

export type GetDriveTokenData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/google/token";
};

export type GetDriveTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDriveTokenError = GetDriveTokenErrors[keyof GetDriveTokenErrors];

export type GetDriveTokenResponses = {
  /**
   * Response Get Drive Token
   *
   * Successful Response
   */
  200: string | null;
};

export type GetDriveTokenResponse =
  GetDriveTokenResponses[keyof GetDriveTokenResponses];

export type GetSpreadsheetInformationData = {
  body?: never;
  path: {
    /**
     * Spreadsheet Id
     */
    spreadsheet_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Max Column
     */
    max_column?: number;
  };
  url: "/groups/{group_id}/integrations/gspreadsheets/{spreadsheet_id}";
};

export type GetSpreadsheetInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSpreadsheetInformationError =
  GetSpreadsheetInformationErrors[keyof GetSpreadsheetInformationErrors];

export type GetSpreadsheetInformationResponses = {
  /**
   * Successful Response
   */
  200: SpreadsheetsInformation;
};

export type GetSpreadsheetInformationResponse =
  GetSpreadsheetInformationResponses[keyof GetSpreadsheetInformationResponses];

export type GetDocsInformationData = {
  body?: never;
  path: {
    /**
     * Doc Id
     */
    doc_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/gdocs/{doc_id}";
};

export type GetDocsInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDocsInformationError =
  GetDocsInformationErrors[keyof GetDocsInformationErrors];

export type GetDocsInformationResponses = {
  /**
   * Successful Response
   */
  200: DocInformation;
};

export type GetDocsInformationResponse =
  GetDocsInformationResponses[keyof GetDocsInformationResponses];

export type GetSlackInformationData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/slack/information";
};

export type GetSlackInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSlackInformationError =
  GetSlackInformationErrors[keyof GetSlackInformationErrors];

export type GetSlackInformationResponses = {
  /**
   * Successful Response
   */
  200: SlackInformation;
};

export type GetSlackInformationResponse =
  GetSlackInformationResponses[keyof GetSlackInformationResponses];

export type CompleteTelegramIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/telegram";
};

export type CompleteTelegramIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteTelegramIntegrationError =
  CompleteTelegramIntegrationErrors[keyof CompleteTelegramIntegrationErrors];

export type CompleteTelegramIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompleteTelegramIntegrationResponse =
  CompleteTelegramIntegrationResponses[keyof CompleteTelegramIntegrationResponses];

export type DisconnectTelegramData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/telegram/disconnect";
};

export type DisconnectTelegramErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectTelegramError =
  DisconnectTelegramErrors[keyof DisconnectTelegramErrors];

export type DisconnectTelegramResponses = {
  /**
   * Response Disconnect Telegram
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectTelegramResponse =
  DisconnectTelegramResponses[keyof DisconnectTelegramResponses];

export type GetOnedriveUserInformationData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/onedrive/me";
};

export type GetOnedriveUserInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOnedriveUserInformationError =
  GetOnedriveUserInformationErrors[keyof GetOnedriveUserInformationErrors];

export type GetOnedriveUserInformationResponses = {
  /**
   * Response Get Onedrive User Information
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetOnedriveUserInformationResponse =
  GetOnedriveUserInformationResponses[keyof GetOnedriveUserInformationResponses];

export type GetOnedriveDocsInformationData = {
  body?: never;
  path: {
    /**
     * Doc Id
     */
    doc_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/onedrive/{doc_id}";
};

export type GetOnedriveDocsInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOnedriveDocsInformationError =
  GetOnedriveDocsInformationErrors[keyof GetOnedriveDocsInformationErrors];

export type GetOnedriveDocsInformationResponses = {
  /**
   * Successful Response
   */
  200: DocInformation;
};

export type GetOnedriveDocsInformationResponse =
  GetOnedriveDocsInformationResponses[keyof GetOnedriveDocsInformationResponses];

export type GetSharepointDocsInformationData = {
  body?: never;
  path: {
    /**
     * Drive Id
     */
    drive_id: string;
    /**
     * Doc Id
     */
    doc_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/sharepoint/{drive_id}/files/{doc_id}";
};

export type GetSharepointDocsInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSharepointDocsInformationError =
  GetSharepointDocsInformationErrors[keyof GetSharepointDocsInformationErrors];

export type GetSharepointDocsInformationResponses = {
  /**
   * Successful Response
   */
  200: DocInformation;
};

export type GetSharepointDocsInformationResponse =
  GetSharepointDocsInformationResponses[keyof GetSharepointDocsInformationResponses];

export type GetOnedriveDriveFilesData = {
  body?: never;
  path: {
    /**
     * Drive Id
     */
    drive_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Folder Id
     */
    folder_id?: string | null;
    /**
     * Query
     */
    query?: string | null;
    /**
     * Credential Id
     */
    credential_id?: string | null;
  };
  url: "/groups/{group_id}/integrations/onedrive/{drive_id}/files";
};

export type GetOnedriveDriveFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOnedriveDriveFilesError =
  GetOnedriveDriveFilesErrors[keyof GetOnedriveDriveFilesErrors];

export type GetOnedriveDriveFilesResponses = {
  /**
   * Response Get Onedrive Drive Files
   *
   * Successful Response
   */
  200: Array<DocInformation>;
};

export type GetOnedriveDriveFilesResponse =
  GetOnedriveDriveFilesResponses[keyof GetOnedriveDriveFilesResponses];

export type GetSharepointDriveFilesData = {
  body?: never;
  path: {
    /**
     * Site Id
     */
    site_id: string;
    /**
     * Drive Id
     */
    drive_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Folder Id
     */
    folder_id?: string | null;
    /**
     * Query
     */
    query?: string | null;
    /**
     * Credential Id
     */
    credential_id?: string | null;
  };
  url: "/groups/{group_id}/integrations/sharepoint/{site_id}/drives/{drive_id}/files";
};

export type GetSharepointDriveFilesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSharepointDriveFilesError =
  GetSharepointDriveFilesErrors[keyof GetSharepointDriveFilesErrors];

export type GetSharepointDriveFilesResponses = {
  /**
   * Response Get Sharepoint Drive Files
   *
   * Successful Response
   */
  200: Array<DocInformation>;
};

export type GetSharepointDriveFilesResponse =
  GetSharepointDriveFilesResponses[keyof GetSharepointDriveFilesResponses];

export type GetSharepointSitesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/sharepoint/sites";
};

export type GetSharepointSitesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSharepointSitesError =
  GetSharepointSitesErrors[keyof GetSharepointSitesErrors];

export type GetSharepointSitesResponses = {
  /**
   * Response Get Sharepoint Sites
   *
   * Successful Response
   */
  200: Array<SharepointSite>;
};

export type GetSharepointSitesResponse =
  GetSharepointSitesResponses[keyof GetSharepointSitesResponses];

export type GetSharepointSubsitesData = {
  body?: never;
  path: {
    /**
     * Site Id
     */
    site_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/sharepoint/sites/{site_id}/subsites";
};

export type GetSharepointSubsitesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSharepointSubsitesError =
  GetSharepointSubsitesErrors[keyof GetSharepointSubsitesErrors];

export type GetSharepointSubsitesResponses = {
  /**
   * Response Get Sharepoint Subsites
   *
   * Successful Response
   */
  200: Array<SharepointSite>;
};

export type GetSharepointSubsitesResponse =
  GetSharepointSubsitesResponses[keyof GetSharepointSubsitesResponses];

export type GetSharepointSiteDrivesData = {
  body?: never;
  path: {
    /**
     * Site Id
     */
    site_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/sharepoint/sites/{site_id}/drives";
};

export type GetSharepointSiteDrivesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSharepointSiteDrivesError =
  GetSharepointSiteDrivesErrors[keyof GetSharepointSiteDrivesErrors];

export type GetSharepointSiteDrivesResponses = {
  /**
   * Response Get Sharepoint Site Drives
   *
   * Successful Response
   */
  200: Array<SharepointDrive>;
};

export type GetSharepointSiteDrivesResponse =
  GetSharepointSiteDrivesResponses[keyof GetSharepointSiteDrivesResponses];

export type CompleteConfluenceIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/confluence";
};

export type CompleteConfluenceIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteConfluenceIntegrationError =
  CompleteConfluenceIntegrationErrors[keyof CompleteConfluenceIntegrationErrors];

export type CompleteConfluenceIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompleteConfluenceIntegrationResponse =
  CompleteConfluenceIntegrationResponses[keyof CompleteConfluenceIntegrationResponses];

export type DisconnectConfluenceData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/confluence/disconnect";
};

export type DisconnectConfluenceErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectConfluenceError =
  DisconnectConfluenceErrors[keyof DisconnectConfluenceErrors];

export type DisconnectConfluenceResponses = {
  /**
   * Response Disconnect Confluence
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectConfluenceResponse =
  DisconnectConfluenceResponses[keyof DisconnectConfluenceResponses];

export type CompleteOdooIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/odoo";
};

export type CompleteOdooIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteOdooIntegrationError =
  CompleteOdooIntegrationErrors[keyof CompleteOdooIntegrationErrors];

export type CompleteOdooIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompleteOdooIntegrationResponse =
  CompleteOdooIntegrationResponses[keyof CompleteOdooIntegrationResponses];

export type DisconnectOdooData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/odoo/disconnect";
};

export type DisconnectOdooErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectOdooError =
  DisconnectOdooErrors[keyof DisconnectOdooErrors];

export type DisconnectOdooResponses = {
  /**
   * Response Disconnect Odoo
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectOdooResponse =
  DisconnectOdooResponses[keyof DisconnectOdooResponses];

export type CompletePaypayIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/paypay";
};

export type CompletePaypayIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompletePaypayIntegrationError =
  CompletePaypayIntegrationErrors[keyof CompletePaypayIntegrationErrors];

export type CompletePaypayIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompletePaypayIntegrationResponse =
  CompletePaypayIntegrationResponses[keyof CompletePaypayIntegrationResponses];

export type DisconnectPaypayData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/paypay/disconnect";
};

export type DisconnectPaypayErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectPaypayError =
  DisconnectPaypayErrors[keyof DisconnectPaypayErrors];

export type DisconnectPaypayResponses = {
  /**
   * Response Disconnect Paypay
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectPaypayResponse =
  DisconnectPaypayResponses[keyof DisconnectPaypayResponses];

export type CompleteRapydIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/rapyd";
};

export type CompleteRapydIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteRapydIntegrationError =
  CompleteRapydIntegrationErrors[keyof CompleteRapydIntegrationErrors];

export type CompleteRapydIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompleteRapydIntegrationResponse =
  CompleteRapydIntegrationResponses[keyof CompleteRapydIntegrationResponses];

export type DisconnectRapydData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/rapyd/disconnect";
};

export type DisconnectRapydErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectRapydError =
  DisconnectRapydErrors[keyof DisconnectRapydErrors];

export type DisconnectRapydResponses = {
  /**
   * Response Disconnect Rapyd
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectRapydResponse =
  DisconnectRapydResponses[keyof DisconnectRapydResponses];

export type CompletePersonaIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/persona";
};

export type CompletePersonaIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompletePersonaIntegrationError =
  CompletePersonaIntegrationErrors[keyof CompletePersonaIntegrationErrors];

export type CompletePersonaIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompletePersonaIntegrationResponse =
  CompletePersonaIntegrationResponses[keyof CompletePersonaIntegrationResponses];

export type DisconnectPersonaData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/persona/disconnect";
};

export type DisconnectPersonaErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectPersonaError =
  DisconnectPersonaErrors[keyof DisconnectPersonaErrors];

export type DisconnectPersonaResponses = {
  /**
   * Response Disconnect Persona
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectPersonaResponse =
  DisconnectPersonaResponses[keyof DisconnectPersonaResponses];

export type CompleteCliponeIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/clipone";
};

export type CompleteCliponeIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteCliponeIntegrationError =
  CompleteCliponeIntegrationErrors[keyof CompleteCliponeIntegrationErrors];

export type CompleteCliponeIntegrationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DisconnectCliponeData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/clipone/disconnect";
};

export type DisconnectCliponeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectCliponeError =
  DisconnectCliponeErrors[keyof DisconnectCliponeErrors];

export type DisconnectCliponeResponses = {
  /**
   * Response Disconnect Clipone
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectCliponeResponse =
  DisconnectCliponeResponses[keyof DisconnectCliponeResponses];

export type CompleteThomIntegrationData = {
  body: SetApiKeyRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/thom";
};

export type CompleteThomIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteThomIntegrationError =
  CompleteThomIntegrationErrors[keyof CompleteThomIntegrationErrors];

export type CompleteThomIntegrationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DisconnectThomData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/thom/disconnect";
};

export type DisconnectThomErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectThomError =
  DisconnectThomErrors[keyof DisconnectThomErrors];

export type DisconnectThomResponses = {
  /**
   * Response Disconnect Thom
   *
   * Successful Response
   */
  200: boolean;
};

export type DisconnectThomResponse =
  DisconnectThomResponses[keyof DisconnectThomResponses];

export type GetIntegrationConfigData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/config";
};

export type GetIntegrationConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIntegrationConfigError =
  GetIntegrationConfigErrors[keyof GetIntegrationConfigErrors];

export type GetIntegrationConfigResponses = {
  /**
   * Successful Response
   */
  200: IntegrationsInfoResponse;
};

export type GetIntegrationConfigResponse =
  GetIntegrationConfigResponses[keyof GetIntegrationConfigResponses];

export type GetIntegrationStatusData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/status";
};

export type GetIntegrationStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIntegrationStatusError =
  GetIntegrationStatusErrors[keyof GetIntegrationStatusErrors];

export type GetIntegrationStatusResponses = {
  /**
   * Successful Response
   */
  200: IntegrationsStatus;
};

export type GetIntegrationStatusResponse =
  GetIntegrationStatusResponses[keyof GetIntegrationStatusResponses];

export type GetAvailableIntegrationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/integrations/available";
};

export type GetAvailableIntegrationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAvailableIntegrationsError =
  GetAvailableIntegrationsErrors[keyof GetAvailableIntegrationsErrors];

export type GetAvailableIntegrationsResponses = {
  /**
   * Successful Response
   */
  200: IntegrationsStatus;
};

export type GetAvailableIntegrationsResponse =
  GetAvailableIntegrationsResponses[keyof GetAvailableIntegrationsResponses];

export type RecordChatTimingData = {
  body: ChatTiming;
  path?: never;
  query?: never;
  url: "/ui-timing/chat";
};

export type RecordChatTimingErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RecordChatTimingError =
  RecordChatTimingErrors[keyof RecordChatTimingErrors];

export type RecordChatTimingResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type RecordChatTimingResponse =
  RecordChatTimingResponses[keyof RecordChatTimingResponses];

export type StorageStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/storage";
};

export type StorageStatusResponses = {
  /**
   * Successful Response
   */
  200: StorageStatus;
};

export type StorageStatusResponse =
  StorageStatusResponses[keyof StorageStatusResponses];

export type SandboxAllocationsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/sandbox/allocations";
};

export type SandboxAllocationsResponses = {
  /**
   * Successful Response
   */
  200: SandboxAllocationsStatus;
};

export type SandboxAllocationsResponse =
  SandboxAllocationsResponses[keyof SandboxAllocationsResponses];

export type EvictSandboxAllocationData = {
  body?: never;
  path: {
    /**
     * Sandbox Id
     */
    sandbox_id: string;
  };
  query: {
    /**
     * Manager Url
     */
    manager_url: string;
  };
  url: "/status/sandbox/allocations/{sandbox_id}";
};

export type EvictSandboxAllocationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type EvictSandboxAllocationError =
  EvictSandboxAllocationErrors[keyof EvictSandboxAllocationErrors];

export type EvictSandboxAllocationResponses = {
  /**
   * Successful Response
   */
  200: SandboxAllocation;
};

export type EvictSandboxAllocationResponse =
  EvictSandboxAllocationResponses[keyof EvictSandboxAllocationResponses];

export type RemoveQueuedSandboxRequestData = {
  body?: never;
  path: {
    /**
     * Operation Id
     */
    operation_id: string;
  };
  query: {
    /**
     * Manager Url
     */
    manager_url: string;
  };
  url: "/status/sandbox/queue/{operation_id}";
};

export type RemoveQueuedSandboxRequestErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveQueuedSandboxRequestError =
  RemoveQueuedSandboxRequestErrors[keyof RemoveQueuedSandboxRequestErrors];

export type RemoveQueuedSandboxRequestResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type RemoveQueuedSandboxRequestResponse =
  RemoveQueuedSandboxRequestResponses[keyof RemoveQueuedSandboxRequestResponses];

export type HealthData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/health";
};

export type HealthResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ReadItemData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/psql-conn";
};

export type ReadItemResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type TestRedisConnData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/redis-conn";
};

export type TestRedisConnResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type RedisConnData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/redis-reset";
};

export type RedisConnResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DatabaseLocksData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Database
     */
    database?: "postgres" | "vector" | "procrastinate";
    /**
     * Include Idle
     */
    include_idle?: boolean;
    /**
     * Only Problematic
     */
    only_problematic?: boolean;
    /**
     * Limit
     */
    limit?: number;
  };
  url: "/status/db-locks";
};

export type DatabaseLocksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DatabaseLocksError = DatabaseLocksErrors[keyof DatabaseLocksErrors];

export type DatabaseLocksResponses = {
  /**
   * Successful Response
   */
  200: DatabaseLocksResponse;
};

export type DatabaseLocksResponse2 =
  DatabaseLocksResponses[keyof DatabaseLocksResponses];

export type TerminateDatabaseBackendData = {
  body: TerminateBackendRequest;
  path?: never;
  query?: never;
  url: "/status/db-locks/terminate";
};

export type TerminateDatabaseBackendErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TerminateDatabaseBackendError =
  TerminateDatabaseBackendErrors[keyof TerminateDatabaseBackendErrors];

export type TerminateDatabaseBackendResponses = {
  /**
   * Successful Response
   */
  200: TerminateBackendResponse;
};

export type TerminateDatabaseBackendResponse =
  TerminateDatabaseBackendResponses[keyof TerminateDatabaseBackendResponses];

export type PlatformStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/status/platform";
};

export type PlatformStatusResponses = {
  /**
   * Successful Response
   */
  200: PlatformStatus;
};

export type PlatformStatusResponse =
  PlatformStatusResponses[keyof PlatformStatusResponses];

export type AddImageData = {
  body: BodyAddImage;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Is Public
     *
     * Whether the file should be public
     */
    is_public?: boolean;
  };
  url: "/groups/{group_id}/file";
};

export type AddImageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddImageError = AddImageErrors[keyof AddImageErrors];

export type AddImageResponses = {
  /**
   * Successful Response
   */
  200: OwnedFile;
};

export type AddImageResponse = AddImageResponses[keyof AddImageResponses];

export type GetImageData = {
  body?: never;
  path: {
    /**
     * Name
     */
    name: string | "empty";
  };
  query?: {
    /**
     * Preview
     *
     * Whether the file should be downloaded, or opened on another tab.
     */
    preview?: boolean;
  };
  url: "/file/{name}";
};

export type GetImageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetImageError = GetImageErrors[keyof GetImageErrors];

export type GetImageResponses = {
  /**
   * Response Get Image
   *
   * Successful Response
   */
  200: Blob | File;
};

export type GetImageResponse = GetImageResponses[keyof GetImageResponses];

export type GetPublicImageData = {
  body?: never;
  path: {
    /**
     * Name
     */
    name: string;
  };
  query?: {
    /**
     * Preview
     *
     * Whether the file should be downloaded, or opened on another tab.
     */
    preview?: boolean;
  };
  url: "/file/public/{name}";
};

export type GetPublicImageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPublicImageError =
  GetPublicImageErrors[keyof GetPublicImageErrors];

export type GetPublicImageResponses = {
  /**
   * Response Get Public Image
   *
   * Successful Response
   */
  200: Blob | File;
};

export type GetPublicImageResponse =
  GetPublicImageResponses[keyof GetPublicImageResponses];

export type GetImageMetadataData = {
  body?: never;
  path: {
    /**
     * Name
     */
    name: string;
  };
  query?: never;
  url: "/file/{name}/metadata";
};

export type GetImageMetadataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetImageMetadataError =
  GetImageMetadataErrors[keyof GetImageMetadataErrors];

export type GetImageMetadataResponses = {
  /**
   * Successful Response
   */
  200: OwnedFile;
};

export type GetImageMetadataResponse =
  GetImageMetadataResponses[keyof GetImageMetadataResponses];

export type AddPublicImageData = {
  body: BodyAddPublicImage;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/file/public";
};

export type AddPublicImageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddPublicImageError =
  AddPublicImageErrors[keyof AddPublicImageErrors];

export type AddPublicImageResponses = {
  /**
   * Successful Response
   */
  200: OwnedFile;
};

export type AddPublicImageResponse =
  AddPublicImageResponses[keyof AddPublicImageResponses];

export type GetAdminStatsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/stats";
};

export type GetAdminStatsResponses = {
  /**
   * Successful Response
   */
  200: AdminDashboardStats;
};

export type GetAdminStatsResponse =
  GetAdminStatsResponses[keyof GetAdminStatsResponses];

export type ListTenantsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Name
     */
    name?: string;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/admin/tenants";
};

export type ListTenantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTenantsError = ListTenantsErrors[keyof ListTenantsErrors];

export type ListTenantsResponses = {
  /**
   * Successful Response
   */
  200: PageTenantWithUsers;
};

export type ListTenantsResponse =
  ListTenantsResponses[keyof ListTenantsResponses];

export type AdminCreateTenantData = {
  body: AdminCreateTenantRequest;
  path?: never;
  query?: never;
  url: "/admin/tenants";
};

export type AdminCreateTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminCreateTenantError =
  AdminCreateTenantErrors[keyof AdminCreateTenantErrors];

export type AdminCreateTenantResponses = {
  /**
   * Successful Response
   */
  200: Tenant;
};

export type AdminCreateTenantResponse =
  AdminCreateTenantResponses[keyof AdminCreateTenantResponses];

export type DeleteTenantData = {
  body: AdminDeleteTenantRequest;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}";
};

export type DeleteTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTenantError = DeleteTenantErrors[keyof DeleteTenantErrors];

export type DeleteTenantResponses = {
  /**
   * Successful Response
   */
  202: AdminDeleteTenantResponse;
};

export type DeleteTenantResponse =
  DeleteTenantResponses[keyof DeleteTenantResponses];

export type GetAdminTenantData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}";
};

export type GetAdminTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAdminTenantError =
  GetAdminTenantErrors[keyof GetAdminTenantErrors];

export type GetAdminTenantResponses = {
  /**
   * Successful Response
   */
  200: TenantWithUsers;
};

export type GetAdminTenantResponse =
  GetAdminTenantResponses[keyof GetAdminTenantResponses];

export type AdminInviteUserData = {
  body: AdminInviteUserRequest;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/invite";
};

export type AdminInviteUserErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminInviteUserError =
  AdminInviteUserErrors[keyof AdminInviteUserErrors];

export type AdminInviteUserResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type AdminInviteUserResponse =
  AdminInviteUserResponses[keyof AdminInviteUserResponses];

export type UpdateSubscriptionPlanData = {
  body: SubscriptionUpdate;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/plan";
};

export type UpdateSubscriptionPlanErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateSubscriptionPlanError =
  UpdateSubscriptionPlanErrors[keyof UpdateSubscriptionPlanErrors];

export type UpdateSubscriptionPlanResponses = {
  /**
   * Response Update Subscription Plan
   *
   * Successful Response
   */
  200: boolean;
};

export type UpdateSubscriptionPlanResponse =
  UpdateSubscriptionPlanResponses[keyof UpdateSubscriptionPlanResponses];

export type RenameTenantData = {
  body: RenameTenant;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/rename";
};

export type RenameTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RenameTenantError = RenameTenantErrors[keyof RenameTenantErrors];

export type RenameTenantResponses = {
  /**
   * Response Rename Tenant
   *
   * Successful Response
   */
  200: boolean;
};

export type RenameTenantResponse =
  RenameTenantResponses[keyof RenameTenantResponses];

export type SuspendTenantData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/suspend";
};

export type SuspendTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SuspendTenantError = SuspendTenantErrors[keyof SuspendTenantErrors];

export type SuspendTenantResponses = {
  /**
   * Response Suspend Tenant
   *
   * Successful Response
   */
  200: boolean;
};

export type SuspendTenantResponse =
  SuspendTenantResponses[keyof SuspendTenantResponses];

export type ActivateTenantData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/activate";
};

export type ActivateTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ActivateTenantError =
  ActivateTenantErrors[keyof ActivateTenantErrors];

export type ActivateTenantResponses = {
  /**
   * Response Activate Tenant
   *
   * Successful Response
   */
  200: boolean;
};

export type ActivateTenantResponse =
  ActivateTenantResponses[keyof ActivateTenantResponses];

export type GetTenantAuditLogsData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: {
    /**
     * Q
     */
    q?: string;
    /**
     * Method
     */
    method?: Array<string> | null;
    /**
     * Resource Type
     */
    resource_type?: Array<string> | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/admin/tenants/{tenant_id}/audit-logs";
};

export type GetTenantAuditLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTenantAuditLogsError =
  GetTenantAuditLogsErrors[keyof GetTenantAuditLogsErrors];

export type GetTenantAuditLogsResponses = {
  /**
   * Successful Response
   */
  200: PageAuditLogEntry;
};

export type GetTenantAuditLogsResponse =
  GetTenantAuditLogsResponses[keyof GetTenantAuditLogsResponses];

export type GetTenantAuditLogFacetsData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/audit-log-facets";
};

export type GetTenantAuditLogFacetsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTenantAuditLogFacetsError =
  GetTenantAuditLogFacetsErrors[keyof GetTenantAuditLogFacetsErrors];

export type GetTenantAuditLogFacetsResponses = {
  /**
   * Successful Response
   */
  200: AuditLogFacets;
};

export type GetTenantAuditLogFacetsResponse =
  GetTenantAuditLogFacetsResponses[keyof GetTenantAuditLogFacetsResponses];

export type MergeTenantData = {
  body: MergeTenant;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/merge";
};

export type MergeTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MergeTenantError = MergeTenantErrors[keyof MergeTenantErrors];

export type MergeTenantResponses = {
  /**
   * Response Merge Tenant
   *
   * Successful Response
   */
  200: boolean;
};

export type MergeTenantResponse =
  MergeTenantResponses[keyof MergeTenantResponses];

export type TopupCreditsData = {
  body: TopupCredits;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/topup";
};

export type TopupCreditsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TopupCreditsError = TopupCreditsErrors[keyof TopupCreditsErrors];

export type TopupCreditsResponses = {
  /**
   * Response Topup Credits
   *
   * Successful Response
   */
  200: boolean;
};

export type TopupCreditsResponse =
  TopupCreditsResponses[keyof TopupCreditsResponses];

export type AdminUnjoinTenantData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/tenants/unjoin";
};

export type AdminUnjoinTenantResponses = {
  /**
   * Response Admin Unjoin Tenant
   *
   * Successful Response
   */
  200: boolean;
};

export type AdminUnjoinTenantResponse =
  AdminUnjoinTenantResponses[keyof AdminUnjoinTenantResponses];

export type AdminJoinTenantData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/join";
};

export type AdminJoinTenantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminJoinTenantError =
  AdminJoinTenantErrors[keyof AdminJoinTenantErrors];

export type AdminJoinTenantResponses = {
  /**
   * Response Admin Join Tenant
   *
   * Successful Response
   */
  200: boolean;
};

export type AdminJoinTenantResponse =
  AdminJoinTenantResponses[keyof AdminJoinTenantResponses];

export type GetTenantFeatureFlagsData = {
  body?: never;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/ffs";
};

export type GetTenantFeatureFlagsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTenantFeatureFlagsError =
  GetTenantFeatureFlagsErrors[keyof GetTenantFeatureFlagsErrors];

export type GetTenantFeatureFlagsResponses = {
  /**
   * Successful Response
   */
  200: FeatureFlags;
};

export type GetTenantFeatureFlagsResponse =
  GetTenantFeatureFlagsResponses[keyof GetTenantFeatureFlagsResponses];

export type UpdateFeatureFlagsData = {
  body: FeatureFlags;
  path: {
    /**
     * Tenant Id
     */
    tenant_id: string;
  };
  query?: never;
  url: "/admin/tenants/{tenant_id}/ffs";
};

export type UpdateFeatureFlagsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateFeatureFlagsError =
  UpdateFeatureFlagsErrors[keyof UpdateFeatureFlagsErrors];

export type UpdateFeatureFlagsResponses = {
  /**
   * Successful Response
   */
  200: FeatureFlags;
};

export type UpdateFeatureFlagsResponse =
  UpdateFeatureFlagsResponses[keyof UpdateFeatureFlagsResponses];

export type GetUserProfileData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Search
     */
    search?: string;
    /**
     * Order By
     */
    order_by?: "created_at" | "email" | "display_name" | "last_login";
    /**
     * Sort
     */
    sort?: "asc" | "desc";
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/admin/users/spot";
};

export type GetUserProfileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserProfileError =
  GetUserProfileErrors[keyof GetUserProfileErrors];

export type GetUserProfileResponses = {
  /**
   * Successful Response
   */
  200: PageUser;
};

export type GetUserProfileResponse =
  GetUserProfileResponses[keyof GetUserProfileResponses];

export type DeleteUserProfileData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/spot/{user_id}";
};

export type DeleteUserProfileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteUserProfileError =
  DeleteUserProfileErrors[keyof DeleteUserProfileErrors];

export type DeleteUserProfileResponses = {
  /**
   * Response Delete User Profile
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteUserProfileResponse =
  DeleteUserProfileResponses[keyof DeleteUserProfileResponses];

export type GetUserProfileByIdData = {
  body?: never;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/spot/{user_id}";
};

export type GetUserProfileByIdErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetUserProfileByIdError =
  GetUserProfileByIdErrors[keyof GetUserProfileByIdErrors];

export type GetUserProfileByIdResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type GetUserProfileByIdResponse =
  GetUserProfileByIdResponses[keyof GetUserProfileByIdResponses];

export type PatchUserProfileData = {
  /**
   * Req
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/spot/{user_id}";
};

export type PatchUserProfileErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchUserProfileError =
  PatchUserProfileErrors[keyof PatchUserProfileErrors];

export type PatchUserProfileResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type PatchUserProfileResponse =
  PatchUserProfileResponses[keyof PatchUserProfileResponses];

export type SetUserAdminStatusData = {
  body: SetAdminStatusRequest;
  path: {
    /**
     * User Id
     */
    user_id: string;
  };
  query?: never;
  url: "/admin/users/{user_id}/set-admin";
};

export type SetUserAdminStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetUserAdminStatusError =
  SetUserAdminStatusErrors[keyof SetUserAdminStatusErrors];

export type SetUserAdminStatusResponses = {
  /**
   * Successful Response
   */
  200: User;
};

export type SetUserAdminStatusResponse =
  SetUserAdminStatusResponses[keyof SetUserAdminStatusResponses];

export type GetAuth0UsersData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/users/auth0";
};

export type GetAuth0UsersResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type RefreshAuth0UsersData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/users/auth0/refresh";
};

export type RefreshAuth0UsersResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type AdminResumeRunData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
  };
  query?: never;
  url: "/admin/runs/{run_id}/resume";
};

export type AdminResumeRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminResumeRunError =
  AdminResumeRunErrors[keyof AdminResumeRunErrors];

export type AdminResumeRunResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type AdminStopRunData = {
  body?: never;
  path: {
    /**
     * Run Id
     */
    run_id: string;
  };
  query?: never;
  url: "/admin/runs/{run_id}/stop";
};

export type AdminStopRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminStopRunError = AdminStopRunErrors[keyof AdminStopRunErrors];

export type AdminStopRunResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type MakeTemplateData = {
  body: CreateTemplateRequest;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
  };
  query?: never;
  url: "/admin/workflows/{workflow_id}/template";
};

export type MakeTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MakeTemplateError = MakeTemplateErrors[keyof MakeTemplateErrors];

export type MakeTemplateResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetRunningWorkersData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/workers";
};

export type GetRunningWorkersResponses = {
  /**
   * Response Get Running Workers
   *
   * Successful Response
   */
  200: Array<string>;
};

export type GetRunningWorkersResponse =
  GetRunningWorkersResponses[keyof GetRunningWorkersResponses];

export type GetWorkerLogsData = {
  body?: never;
  path: {
    /**
     * Worker Name
     */
    worker_name: string;
  };
  query?: never;
  url: "/admin/workers/{worker_name}/logs";
};

export type GetWorkerLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkerLogsError = GetWorkerLogsErrors[keyof GetWorkerLogsErrors];

export type GetWorkerLogsResponses = {
  /**
   * Response Get Worker Logs
   *
   * Successful Response
   */
  200: Array<[string, string]>;
};

export type GetWorkerLogsResponse =
  GetWorkerLogsResponses[keyof GetWorkerLogsResponses];

export type GetApiKeysSetData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/secrets";
};

export type GetApiKeysSetResponses = {
  /**
   * Response Get Api Keys Set
   *
   * Successful Response
   */
  200: Array<{
    [key: string]: unknown;
  }>;
};

export type GetApiKeysSetResponse =
  GetApiKeysSetResponses[keyof GetApiKeysSetResponses];

export type SetApiKeysData = {
  body: AddSecretRequest;
  path: {
    /**
     * Secret Name
     */
    secret_name: string;
  };
  query?: never;
  url: "/admin/secrets/{secret_name}";
};

export type SetApiKeysErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetApiKeysError = SetApiKeysErrors[keyof SetApiKeysErrors];

export type SetApiKeysResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetRecoveryStatsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Lookback Queued
     */
    lookback_queued?: number;
    /**
     * Lookback Recent
     */
    lookback_recent?: number;
  };
  url: "/admin/runs/recovery-stats";
};

export type GetRecoveryStatsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRecoveryStatsError =
  GetRecoveryStatsErrors[keyof GetRecoveryStatsErrors];

export type GetRecoveryStatsResponses = {
  /**
   * Successful Response
   */
  200: RecoveryStats;
};

export type GetRecoveryStatsResponse =
  GetRecoveryStatsResponses[keyof GetRecoveryStatsResponses];

export type ResumeRecentData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Lookback
     */
    lookback?: number;
  };
  url: "/admin/runs/resume-recent";
};

export type ResumeRecentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResumeRecentError = ResumeRecentErrors[keyof ResumeRecentErrors];

export type ResumeRecentResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ResumeQueuedData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Lookback
     */
    lookback?: number;
  };
  url: "/admin/runs/resume-queued";
};

export type ResumeQueuedErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResumeQueuedError = ResumeQueuedErrors[keyof ResumeQueuedErrors];

export type ResumeQueuedResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetServerSettingsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/server/settings";
};

export type GetServerSettingsResponses = {
  /**
   * Successful Response
   */
  200: ServerSettingsJson;
};

export type GetServerSettingsResponse =
  GetServerSettingsResponses[keyof GetServerSettingsResponses];

export type UpdateServerSettingsData = {
  body: ServerSettingsJsonWritable;
  path?: never;
  query?: never;
  url: "/admin/server/settings";
};

export type UpdateServerSettingsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateServerSettingsError =
  UpdateServerSettingsErrors[keyof UpdateServerSettingsErrors];

export type UpdateServerSettingsResponses = {
  /**
   * Successful Response
   */
  200: ServerSettingsJson;
};

export type UpdateServerSettingsResponse =
  UpdateServerSettingsResponses[keyof UpdateServerSettingsResponses];

export type GetAuditLogsStorageData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/server/audit-logs/storage";
};

export type GetAuditLogsStorageResponses = {
  /**
   * Successful Response
   */
  200: AuditLogStorageStats;
};

export type GetAuditLogsStorageResponse =
  GetAuditLogsStorageResponses[keyof GetAuditLogsStorageResponses];

export type GetRunDataIndexStatsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/server/run-data/index";
};

export type GetRunDataIndexStatsResponses = {
  /**
   * Successful Response
   */
  200: RunDataIndexStats;
};

export type GetRunDataIndexStatsResponse =
  GetRunDataIndexStatsResponses[keyof GetRunDataIndexStatsResponses];

export type TriggerRunDataBackfillData = {
  body: BackfillRunDataRequest;
  path?: never;
  query?: never;
  url: "/admin/server/run-data/backfill";
};

export type TriggerRunDataBackfillErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TriggerRunDataBackfillError =
  TriggerRunDataBackfillErrors[keyof TriggerRunDataBackfillErrors];

export type TriggerRunDataBackfillResponses = {
  /**
   * Successful Response
   */
  202: unknown;
};

export type GetServiceMetricsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Hours
     */
    hours?: number;
  };
  url: "/admin/server/service-metrics";
};

export type GetServiceMetricsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetServiceMetricsError =
  GetServiceMetricsErrors[keyof GetServiceMetricsErrors];

export type GetServiceMetricsResponses = {
  /**
   * Successful Response
   */
  200: ServiceMetricsResponse;
};

export type GetServiceMetricsResponse =
  GetServiceMetricsResponses[keyof GetServiceMetricsResponses];

export type GetBuildVersionData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/server/version";
};

export type GetBuildVersionResponses = {
  /**
   * Successful Response
   */
  200: BuildVersionResponse;
};

export type GetBuildVersionResponse =
  GetBuildVersionResponses[keyof GetBuildVersionResponses];

export type GetSecuritySettingsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/server/security";
};

export type GetSecuritySettingsResponses = {
  /**
   * Successful Response
   */
  200: SecuritySettingsJson;
};

export type GetSecuritySettingsResponse =
  GetSecuritySettingsResponses[keyof GetSecuritySettingsResponses];

export type UpdateSecuritySettingsData = {
  body: SecuritySettingsJson;
  path?: never;
  query?: never;
  url: "/admin/server/security";
};

export type UpdateSecuritySettingsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateSecuritySettingsError =
  UpdateSecuritySettingsErrors[keyof UpdateSecuritySettingsErrors];

export type UpdateSecuritySettingsResponses = {
  /**
   * Successful Response
   */
  200: SecuritySettingsJson;
};

export type UpdateSecuritySettingsResponse =
  UpdateSecuritySettingsResponses[keyof UpdateSecuritySettingsResponses];

export type GetRateLimitsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/rate-limits";
};

export type GetRateLimitsResponses = {
  /**
   * Successful Response
   */
  200: RateLimitSettings;
};

export type GetRateLimitsResponse =
  GetRateLimitsResponses[keyof GetRateLimitsResponses];

export type UpdateRateLimitsData = {
  body: RateLimitSettings;
  path?: never;
  query?: never;
  url: "/admin/rate-limits";
};

export type UpdateRateLimitsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateRateLimitsError =
  UpdateRateLimitsErrors[keyof UpdateRateLimitsErrors];

export type UpdateRateLimitsResponses = {
  /**
   * Successful Response
   */
  200: RateLimitSettings;
};

export type UpdateRateLimitsResponse =
  UpdateRateLimitsResponses[keyof UpdateRateLimitsResponses];

export type ActivateDevLicenseData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/server/activate-dev-license";
};

export type ActivateDevLicenseResponses = {
  /**
   * Response Activate Dev License
   *
   * Successful Response
   */
  200: boolean;
};

export type ActivateDevLicenseResponse =
  ActivateDevLicenseResponses[keyof ActivateDevLicenseResponses];

export type GenerateJwtData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Scopes
     */
    scopes?: Array<JwtScope>;
    validity?: ValidityDuration;
  };
  url: "/admin/support/jwt";
};

export type GenerateJwtErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateJwtError = GenerateJwtErrors[keyof GenerateJwtErrors];

export type GenerateJwtResponses = {
  /**
   * Successful Response
   */
  200: SpotflowBackendApiAdminTokenResponse;
};

export type GenerateJwtResponse =
  GenerateJwtResponses[keyof GenerateJwtResponses];

export type InvalidateCacheData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/cache/invalidate";
};

export type InvalidateCacheResponses = {
  /**
   * Response Invalidate Cache
   *
   * Successful Response
   */
  200: boolean;
};

export type InvalidateCacheResponse =
  InvalidateCacheResponses[keyof InvalidateCacheResponses];

export type GetCacheStatsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/cache/stats";
};

export type GetCacheStatsResponses = {
  /**
   * Successful Response
   */
  200: CacheStats;
};

export type GetCacheStatsResponse =
  GetCacheStatsResponses[keyof GetCacheStatsResponses];

export type InvalidateCacheTagData = {
  body: CacheInvalidateTagRequest;
  path?: never;
  query?: never;
  url: "/admin/cache/invalidate-tag";
};

export type InvalidateCacheTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type InvalidateCacheTagError =
  InvalidateCacheTagErrors[keyof InvalidateCacheTagErrors];

export type InvalidateCacheTagResponses = {
  /**
   * Response Invalidate Cache Tag
   *
   * Successful Response
   */
  200: boolean;
};

export type InvalidateCacheTagResponse =
  InvalidateCacheTagResponses[keyof InvalidateCacheTagResponses];

export type DeleteCachePatternData = {
  body: CacheDeletePatternRequest;
  path?: never;
  query?: never;
  url: "/admin/cache/delete-pattern";
};

export type DeleteCachePatternErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteCachePatternError =
  DeleteCachePatternErrors[keyof DeleteCachePatternErrors];

export type DeleteCachePatternResponses = {
  /**
   * Successful Response
   */
  200: CacheDeletePatternResponse;
};

export type DeleteCachePatternResponse =
  DeleteCachePatternResponses[keyof DeleteCachePatternResponses];

export type GoToLinkData = {
  body: GoToLinkRequest;
  path?: never;
  query?: never;
  url: "/admin/go-to-link";
};

export type GoToLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GoToLinkError = GoToLinkErrors[keyof GoToLinkErrors];

export type GoToLinkResponses = {
  /**
   * Successful Response
   */
  200: GoToLinkResponse;
};

export type GoToLinkResponse2 = GoToLinkResponses[keyof GoToLinkResponses];

export type EncryptProviderKeysData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/providers/encrypt";
};

export type EncryptProviderKeysResponses = {
  /**
   * Successful Response
   */
  200: EncryptProviderKeysResponse;
};

export type EncryptProviderKeysResponse2 =
  EncryptProviderKeysResponses[keyof EncryptProviderKeysResponses];

export type MigrateLegacyCredentialsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/credentials/migrate-legacy";
};

export type MigrateLegacyCredentialsResponses = {
  /**
   * Successful Response
   */
  200: LegacyCredentialMigrationResponse;
};

export type MigrateLegacyCredentialsResponse =
  MigrateLegacyCredentialsResponses[keyof MigrateLegacyCredentialsResponses];

export type UpgradeCredentialsToNcsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/credentials/upgrade-to-ncs";
};

export type UpgradeCredentialsToNcsResponses = {
  /**
   * Successful Response
   */
  200: UpgradeToNcsResponse;
};

export type UpgradeCredentialsToNcsResponse =
  UpgradeCredentialsToNcsResponses[keyof UpgradeCredentialsToNcsResponses];

export type RemigrateSecretAuthCredentialsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/credentials/remigrate-secret-auth";
};

export type RemigrateSecretAuthCredentialsResponses = {
  /**
   * Successful Response
   */
  200: RemigrateSaResponse;
};

export type RemigrateSecretAuthCredentialsResponse =
  RemigrateSecretAuthCredentialsResponses[keyof RemigrateSecretAuthCredentialsResponses];

export type MigrateGoogleCredentialsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/credentials/migrate-google";
};

export type MigrateGoogleCredentialsResponses = {
  /**
   * Successful Response
   */
  200: MigrateGoogleResponse;
};

export type MigrateGoogleCredentialsResponse =
  MigrateGoogleCredentialsResponses[keyof MigrateGoogleCredentialsResponses];

export type BillingRectificationSummaryData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Model
     */
    model?: string | null;
    /**
     * Tenant Id
     */
    tenant_id?: string | null;
    /**
     * Date From
     */
    date_from?: string | null;
    /**
     * Date To
     */
    date_to?: string | null;
  };
  url: "/admin/billing-rectification/summary";
};

export type BillingRectificationSummaryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BillingRectificationSummaryError =
  BillingRectificationSummaryErrors[keyof BillingRectificationSummaryErrors];

export type BillingRectificationSummaryResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BillingRectificationEventsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Model
     */
    model?: string | null;
    /**
     * Tenant Id
     */
    tenant_id?: string | null;
    /**
     * Date From
     */
    date_from?: string | null;
    /**
     * Date To
     */
    date_to?: string | null;
    /**
     * Page
     */
    page?: number;
    /**
     * Size
     */
    size?: number;
  };
  url: "/admin/billing-rectification/events";
};

export type BillingRectificationEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BillingRectificationEventsError =
  BillingRectificationEventsErrors[keyof BillingRectificationEventsErrors];

export type BillingRectificationEventsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BillingRectificationDeduplicateData = {
  body: BillingRectificationFilterRequest;
  path?: never;
  query?: never;
  url: "/admin/billing-rectification/deduplicate";
};

export type BillingRectificationDeduplicateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BillingRectificationDeduplicateError =
  BillingRectificationDeduplicateErrors[keyof BillingRectificationDeduplicateErrors];

export type BillingRectificationDeduplicateResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BillingRectificationCorrectPricesData = {
  body: BillingRectificationFilterRequest;
  path?: never;
  query?: never;
  url: "/admin/billing-rectification/correct-prices";
};

export type BillingRectificationCorrectPricesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BillingRectificationCorrectPricesError =
  BillingRectificationCorrectPricesErrors[keyof BillingRectificationCorrectPricesErrors];

export type BillingRectificationCorrectPricesResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetLifecycleConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/lifecycle/config";
};

export type GetLifecycleConfigResponses = {
  /**
   * Successful Response
   */
  200: LifecycleConfigResponse;
};

export type GetLifecycleConfigResponse =
  GetLifecycleConfigResponses[keyof GetLifecycleConfigResponses];

export type UpdateLifecycleConfigData = {
  body: LifecycleConfigUpdateRequest;
  path?: never;
  query?: never;
  url: "/admin/lifecycle/config";
};

export type UpdateLifecycleConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateLifecycleConfigError =
  UpdateLifecycleConfigErrors[keyof UpdateLifecycleConfigErrors];

export type UpdateLifecycleConfigResponses = {
  /**
   * Successful Response
   */
  200: LifecycleConfigResponse;
};

export type UpdateLifecycleConfigResponse =
  UpdateLifecycleConfigResponses[keyof UpdateLifecycleConfigResponses];

export type ApplyLocalTestPresetData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/lifecycle/config/local-preset";
};

export type ApplyLocalTestPresetResponses = {
  /**
   * Successful Response
   */
  200: LifecycleConfigResponse;
};

export type ApplyLocalTestPresetResponse =
  ApplyLocalTestPresetResponses[keyof ApplyLocalTestPresetResponses];

export type ToggleLifecyclePauseData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/lifecycle/pause";
};

export type ToggleLifecyclePauseResponses = {
  /**
   * Response Toggle Lifecycle Pause
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type ToggleLifecyclePauseResponse =
  ToggleLifecyclePauseResponses[keyof ToggleLifecyclePauseResponses];

export type GetLifecycleStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/lifecycle/status";
};

export type GetLifecycleStatusResponses = {
  /**
   * Response Get Lifecycle Status
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type GetLifecycleStatusResponse =
  GetLifecycleStatusResponses[keyof GetLifecycleStatusResponses];

export type RunLifecycleHealthChecksData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/lifecycle/run-health-checks";
};

export type RunLifecycleHealthChecksResponses = {
  /**
   * Successful Response
   */
  200: LifecycleRunHealthCheckResponse;
};

export type RunLifecycleHealthChecksResponse =
  RunLifecycleHealthChecksResponses[keyof RunLifecycleHealthChecksResponses];

export type GetInsightsConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/insights/config";
};

export type GetInsightsConfigResponses = {
  /**
   * Successful Response
   */
  200: InsightsConfigResponse;
};

export type GetInsightsConfigResponse =
  GetInsightsConfigResponses[keyof GetInsightsConfigResponses];

export type UpdateInsightsConfigData = {
  body: InsightsConfigUpdateRequest;
  path?: never;
  query?: never;
  url: "/admin/insights/config";
};

export type UpdateInsightsConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateInsightsConfigError =
  UpdateInsightsConfigErrors[keyof UpdateInsightsConfigErrors];

export type UpdateInsightsConfigResponses = {
  /**
   * Successful Response
   */
  200: InsightsConfigResponse;
};

export type UpdateInsightsConfigResponse =
  UpdateInsightsConfigResponses[keyof UpdateInsightsConfigResponses];

export type RunInsightsAnalysisData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/insights/run-analysis";
};

export type RunInsightsAnalysisResponses = {
  /**
   * Successful Response
   */
  200: InsightsRunResponse;
};

export type RunInsightsAnalysisResponse =
  RunInsightsAnalysisResponses[keyof RunInsightsAnalysisResponses];

export type RunInsightsAggregatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/insights/run-aggregates";
};

export type RunInsightsAggregatesResponses = {
  /**
   * Successful Response
   */
  200: InsightsRunResponse;
};

export type RunInsightsAggregatesResponse =
  RunInsightsAggregatesResponses[keyof RunInsightsAggregatesResponses];

export type GetNoxusProviderConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/noxus-provider";
};

export type GetNoxusProviderConfigResponses = {
  /**
   * Successful Response
   */
  200: NoxusProviderConfigResponse;
};

export type GetNoxusProviderConfigResponse =
  GetNoxusProviderConfigResponses[keyof GetNoxusProviderConfigResponses];

export type SetNoxusProviderConfigData = {
  body: NoxusProviderConfigUpdate;
  path?: never;
  query?: never;
  url: "/admin/noxus-provider";
};

export type SetNoxusProviderConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetNoxusProviderConfigError =
  SetNoxusProviderConfigErrors[keyof SetNoxusProviderConfigErrors];

export type SetNoxusProviderConfigResponses = {
  /**
   * Successful Response
   */
  200: NoxusProviderConfigResponse;
};

export type SetNoxusProviderConfigResponse =
  SetNoxusProviderConfigResponses[keyof SetNoxusProviderConfigResponses];

export type RepairNoxusLinksData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/noxus-provider/repair";
};

export type RepairNoxusLinksResponses = {
  /**
   * Successful Response
   */
  200: NoxusRepairResponse;
};

export type RepairNoxusLinksResponse =
  RepairNoxusLinksResponses[keyof RepairNoxusLinksResponses];

export type GetChaosTestingConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/chaos-testing";
};

export type GetChaosTestingConfigResponses = {
  /**
   * Successful Response
   */
  200: ChaosTestingConfigResponse;
};

export type GetChaosTestingConfigResponse =
  GetChaosTestingConfigResponses[keyof GetChaosTestingConfigResponses];

export type SetChaosTestingConfigData = {
  body: ChaosTestingConfigUpdate;
  path?: never;
  query?: never;
  url: "/admin/chaos-testing";
};

export type SetChaosTestingConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetChaosTestingConfigError =
  SetChaosTestingConfigErrors[keyof SetChaosTestingConfigErrors];

export type SetChaosTestingConfigResponses = {
  /**
   * Successful Response
   */
  200: ChaosTestingConfigResponse;
};

export type SetChaosTestingConfigResponse =
  SetChaosTestingConfigResponses[keyof SetChaosTestingConfigResponses];

export type AdminKbChaosData = {
  body?: never;
  path: {
    /**
     * Kb Id
     */
    kb_id: string;
    /**
     * Check Id
     */
    check_id:
      | "vector_db_connectivity"
      | "vectors_database"
      | "vector_table"
      | "embedding_dim"
      | "index_struct"
      | "embedding_provider"
      | "reranker"
      | "chunk_orphans"
      | "missing_chunks"
      | "metadata_drift"
      | "hnsw_index";
  };
  query?: never;
  url: "/admin/kbs/{kb_id}/health/chaos/{check_id}";
};

export type AdminKbChaosErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AdminKbChaosError = AdminKbChaosErrors[keyof AdminKbChaosErrors];

export type AdminKbChaosResponses = {
  /**
   * Successful Response
   */
  200: KbChaosResult;
};

export type AdminKbChaosResponse =
  AdminKbChaosResponses[keyof AdminKbChaosResponses];

export type ResumeRecent2Data = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Lookback
     */
    lookback?: number;
  };
  url: "/admin/runs/resume-recent-api";
};

export type ResumeRecent2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResumeRecent2Error = ResumeRecent2Errors[keyof ResumeRecent2Errors];

export type ResumeRecent2Responses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BatchGetNodeConfigData = {
  body: BatchNodeConfigRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/nodes/batch-config";
};

export type BatchGetNodeConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BatchGetNodeConfigError =
  BatchGetNodeConfigErrors[keyof BatchGetNodeConfigErrors];

export type BatchGetNodeConfigResponses = {
  /**
   * Successful Response
   */
  200: BatchNodeConfigResponse;
};

export type BatchGetNodeConfigResponse =
  BatchGetNodeConfigResponses[keyof BatchGetNodeConfigResponses];

export type GetNodeConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Node Id
     */
    node_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Skip Cache
     */
    skip_cache?: boolean;
    /**
     * Workflow Id
     */
    workflow_id?: string | null;
  };
  url: "/groups/{group_id}/nodes/{node_id}/config";
};

export type GetNodeConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetNodeConfigError = GetNodeConfigErrors[keyof GetNodeConfigErrors];

export type GetNodeConfigResponses = {
  /**
   * Successful Response
   */
  200: ConfigResponse;
};

export type GetNodeConfigResponse =
  GetNodeConfigResponses[keyof GetNodeConfigResponses];

export type GetNodeConfigV2Data = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Node Id
     */
    node_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Skip Cache
     */
    skip_cache?: boolean;
    /**
     * Workflow Id
     */
    workflow_id?: string | null;
  };
  url: "/groups/{group_id}/nodes-v2/{node_id}/config";
};

export type GetNodeConfigV2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetNodeConfigV2Error =
  GetNodeConfigV2Errors[keyof GetNodeConfigV2Errors];

export type GetNodeConfigV2Responses = {
  /**
   * Successful Response
   */
  200: ConfigResponseV2;
};

export type GetNodeConfigV2Response =
  GetNodeConfigV2Responses[keyof GetNodeConfigV2Responses];

export type ListComponentsAdminData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/components";
};

export type ListComponentsAdminResponses = {
  /**
   * Successful Response
   */
  200: ComponentListResponse;
};

export type ListComponentsAdminResponse =
  ListComponentsAdminResponses[keyof ListComponentsAdminResponses];

export type SetComponentEnabledData = {
  body: SetComponentEnabledRequest;
  path: {
    kind: ComponentKind;
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/components/{kind}/{key}/enabled";
};

export type SetComponentEnabledErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetComponentEnabledError =
  SetComponentEnabledErrors[keyof SetComponentEnabledErrors];

export type SetComponentEnabledResponses = {
  /**
   * Response Set Component Enabled
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type SetComponentEnabledResponse =
  SetComponentEnabledResponses[keyof SetComponentEnabledResponses];

export type BulkSetDisabledData = {
  body: BulkSetDisabledRequest;
  path: {
    kind: ComponentKind;
  };
  query?: never;
  url: "/admin/components/{kind}/bulk";
};

export type BulkSetDisabledErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkSetDisabledError =
  BulkSetDisabledErrors[keyof BulkSetDisabledErrors];

export type BulkSetDisabledResponses = {
  /**
   * Response Bulk Set Disabled
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type BulkSetDisabledResponse =
  BulkSetDisabledResponses[keyof BulkSetDisabledResponses];

export type GetProductSpecData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/spec";
};

export type GetProductSpecResponses = {
  /**
   * Successful Response
   */
  200: SpecPayload;
};

export type GetProductSpecResponse =
  GetProductSpecResponses[keyof GetProductSpecResponses];

export type GetSpecNodeHistoryData = {
  body?: never;
  path: {
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/spec/nodes/{key}/history";
};

export type GetSpecNodeHistoryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSpecNodeHistoryError =
  GetSpecNodeHistoryErrors[keyof GetSpecNodeHistoryErrors];

export type GetSpecNodeHistoryResponses = {
  /**
   * Successful Response
   */
  200: KeyHistory;
};

export type GetSpecNodeHistoryResponse =
  GetSpecNodeHistoryResponses[keyof GetSpecNodeHistoryResponses];

export type CreateSpecNodeData = {
  body: NodeCreate;
  path?: never;
  query?: never;
  url: "/admin/spec/nodes";
};

export type CreateSpecNodeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateSpecNodeError =
  CreateSpecNodeErrors[keyof CreateSpecNodeErrors];

export type CreateSpecNodeResponses = {
  /**
   * Successful Response
   */
  200: SpecNodeOut;
};

export type CreateSpecNodeResponse =
  CreateSpecNodeResponses[keyof CreateSpecNodeResponses];

export type DeleteSpecNodeData = {
  body?: never;
  path: {
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/spec/nodes/{key}";
};

export type DeleteSpecNodeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteSpecNodeError =
  DeleteSpecNodeErrors[keyof DeleteSpecNodeErrors];

export type DeleteSpecNodeResponses = {
  /**
   * Response Delete Spec Node
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteSpecNodeResponse =
  DeleteSpecNodeResponses[keyof DeleteSpecNodeResponses];

export type PatchSpecNodeData = {
  body: NodePatch;
  path: {
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/spec/nodes/{key}";
};

export type PatchSpecNodeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchSpecNodeError = PatchSpecNodeErrors[keyof PatchSpecNodeErrors];

export type PatchSpecNodeResponses = {
  /**
   * Successful Response
   */
  200: SpecNodeOut;
};

export type PatchSpecNodeResponse =
  PatchSpecNodeResponses[keyof PatchSpecNodeResponses];

export type MoveSpecNodeData = {
  body: MoveBody;
  path: {
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/spec/nodes/{key}/move";
};

export type MoveSpecNodeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MoveSpecNodeError = MoveSpecNodeErrors[keyof MoveSpecNodeErrors];

export type MoveSpecNodeResponses = {
  /**
   * Response Move Spec Node
   *
   * Successful Response
   */
  200: boolean;
};

export type MoveSpecNodeResponse =
  MoveSpecNodeResponses[keyof MoveSpecNodeResponses];

export type CreateSpecCaseData = {
  body: SpotflowBackendApiSpecCaseCreate;
  path?: never;
  query?: never;
  url: "/admin/spec/cases";
};

export type CreateSpecCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateSpecCaseError =
  CreateSpecCaseErrors[keyof CreateSpecCaseErrors];

export type CreateSpecCaseResponses = {
  /**
   * Successful Response
   */
  200: TestCase;
};

export type CreateSpecCaseResponse =
  CreateSpecCaseResponses[keyof CreateSpecCaseResponses];

export type DeleteSpecCaseData = {
  body?: never;
  path: {
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/spec/cases/{key}";
};

export type DeleteSpecCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteSpecCaseError =
  DeleteSpecCaseErrors[keyof DeleteSpecCaseErrors];

export type DeleteSpecCaseResponses = {
  /**
   * Response Delete Spec Case
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteSpecCaseResponse =
  DeleteSpecCaseResponses[keyof DeleteSpecCaseResponses];

export type PatchSpecCaseData = {
  body: CasePatch;
  path: {
    /**
     * Key
     */
    key: string;
  };
  query?: never;
  url: "/admin/spec/cases/{key}";
};

export type PatchSpecCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PatchSpecCaseError = PatchSpecCaseErrors[keyof PatchSpecCaseErrors];

export type PatchSpecCaseResponses = {
  /**
   * Successful Response
   */
  200: TestCase;
};

export type PatchSpecCaseResponse =
  PatchSpecCaseResponses[keyof PatchSpecCaseResponses];

export type GetLicenseDataData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/license";
};

export type GetLicenseDataResponses = {
  /**
   * Successful Response
   */
  200: LicenseResult;
};

export type GetLicenseDataResponse =
  GetLicenseDataResponses[keyof GetLicenseDataResponses];

export type GetAllInvoicesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/payments/invoices";
};

export type GetAllInvoicesResponses = {
  /**
   * Response Get All Invoices
   *
   * Successful Response
   */
  200: Array<BillingInvoiceInformation>;
};

export type GetAllInvoicesResponse =
  GetAllInvoicesResponses[keyof GetAllInvoicesResponses];

export type CreateStripeCustomerData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/payment/stripe/customer";
};

export type CreateStripeCustomerResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CancelSubscriptionCancelationData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/payment/stripe/subscription/uncancel";
};

export type CancelSubscriptionCancelationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateCheckoutSessionData = {
  body: CreateCheckoutSessionRequest;
  path?: never;
  query?: never;
  url: "/payment/stripe/create-checkout-session";
};

export type CreateCheckoutSessionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateCheckoutSessionError =
  CreateCheckoutSessionErrors[keyof CreateCheckoutSessionErrors];

export type CreateCheckoutSessionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreateBillingPortalSessionData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/payment/stripe/create-billing-portal-session";
};

export type CreateBillingPortalSessionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CreatePayasyougoCheckoutSessionData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/payment/stripe/create-pay-as-you-go-checkout-session";
};

export type CreatePayasyougoCheckoutSessionResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type WebhookCheckoutCompletedData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/payment/stripe/webhook-checkout-completed";
};

export type WebhookCheckoutCompletedResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type WebhookSubscriptionUpdatedData = {
  /**
   * Req
   */
  body: {
    [key: string]: unknown;
  };
  path?: never;
  query?: never;
  url: "/payment/stripe/webhook-subscription-updated";
};

export type WebhookSubscriptionUpdatedErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type WebhookSubscriptionUpdatedError =
  WebhookSubscriptionUpdatedErrors[keyof WebhookSubscriptionUpdatedErrors];

export type WebhookSubscriptionUpdatedResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type WebhookSubscriptionUpdatedResponse =
  WebhookSubscriptionUpdatedResponses[keyof WebhookSubscriptionUpdatedResponses];

export type GetBillingInformationData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Refresh billing
     */
    refresh?: boolean;
  };
  url: "/billing";
};

export type GetBillingInformationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetBillingInformationError =
  GetBillingInformationErrors[keyof GetBillingInformationErrors];

export type GetBillingInformationResponses = {
  /**
   * Response Get Billing Information
   *
   * Successful Response
   */
  200: BillingInformationResponse | null;
};

export type GetBillingInformationResponse =
  GetBillingInformationResponses[keyof GetBillingInformationResponses];

export type GetBillingCurrencyData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/billing/currency";
};

export type GetBillingCurrencyResponses = {
  /**
   * Successful Response
   */
  200: BillingCurrencyInformation;
};

export type GetBillingCurrencyResponse =
  GetBillingCurrencyResponses[keyof GetBillingCurrencyResponses];

export type OpenStripeBillingPortalData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/billing/stripe";
};

export type OpenStripeBillingPortalResponses = {
  /**
   * Successful Response
   */
  200: StripePortal;
};

export type OpenStripeBillingPortalResponse =
  OpenStripeBillingPortalResponses[keyof OpenStripeBillingPortalResponses];

export type UpgradeTenantSubscriptionData = {
  body: UpgradeRequest;
  path?: never;
  query?: never;
  url: "/billing/upgrade";
};

export type UpgradeTenantSubscriptionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpgradeTenantSubscriptionError =
  UpgradeTenantSubscriptionErrors[keyof UpgradeTenantSubscriptionErrors];

export type UpgradeTenantSubscriptionResponses = {
  /**
   * Successful Response
   */
  200: UpgradeResponse;
};

export type UpgradeTenantSubscriptionResponse =
  UpgradeTenantSubscriptionResponses[keyof UpgradeTenantSubscriptionResponses];

export type GetLimitsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/billing/limits";
};

export type GetLimitsResponses = {
  /**
   * Successful Response
   */
  200: LimitsResponse;
};

export type GetLimitsResponse = GetLimitsResponses[keyof GetLimitsResponses];

export type AddLimitData = {
  body: AddLimitRequest;
  path?: never;
  query?: never;
  url: "/billing/limits";
};

export type AddLimitErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddLimitError = AddLimitErrors[keyof AddLimitErrors];

export type AddLimitResponses = {
  /**
   * Successful Response
   */
  200: LimitsResponse;
};

export type AddLimitResponse = AddLimitResponses[keyof AddLimitResponses];

export type GetLimitProvidersData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/billing/limits/providers";
};

export type GetLimitProvidersResponses = {
  /**
   * Response Get Limit Providers
   *
   * Successful Response
   */
  200: Array<ProviderCostPreview>;
};

export type GetLimitProvidersResponse =
  GetLimitProvidersResponses[keyof GetLimitProvidersResponses];

export type GetLimitUsageData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Provider
     */
    provider?: string | null;
    /**
     * Group Id
     */
    group_id?: string | null;
  };
  url: "/billing/limits/usage";
};

export type GetLimitUsageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetLimitUsageError = GetLimitUsageErrors[keyof GetLimitUsageErrors];

export type GetLimitUsageResponses = {
  /**
   * Successful Response
   */
  200: LimitUsageResponse;
};

export type GetLimitUsageResponse =
  GetLimitUsageResponses[keyof GetLimitUsageResponses];

export type DeleteLimitData = {
  body?: never;
  path: {
    /**
     * Limit Id
     */
    limit_id: string;
  };
  query?: never;
  url: "/billing/limits/{limit_id}";
};

export type DeleteLimitErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteLimitError = DeleteLimitErrors[keyof DeleteLimitErrors];

export type DeleteLimitResponses = {
  /**
   * Response Delete Limit
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteLimitResponse =
  DeleteLimitResponses[keyof DeleteLimitResponses];

export type UpdateLimitData = {
  body: AddLimitRequest;
  path: {
    /**
     * Limit Id
     */
    limit_id: string;
  };
  query?: never;
  url: "/billing/limits/{limit_id}";
};

export type UpdateLimitErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateLimitError = UpdateLimitErrors[keyof UpdateLimitErrors];

export type UpdateLimitResponses = {
  /**
   * Successful Response
   */
  200: LimitsResponse;
};

export type UpdateLimitResponse =
  UpdateLimitResponses[keyof UpdateLimitResponses];

export type GetPaymentsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/billing/payments";
};

export type GetPaymentsResponses = {
  /**
   * Response Get Payments
   *
   * Successful Response
   */
  200: Array<Payment>;
};

export type GetPaymentsResponse =
  GetPaymentsResponses[keyof GetPaymentsResponses];

export type GetConnectionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/custom/connections";
};

export type GetConnectionsResponses = {
  /**
   * Successful Response
   */
  200: ConnectionsResponse;
};

export type GetConnectionsResponse =
  GetConnectionsResponses[keyof GetConnectionsResponses];

export type LoginData = {
  body: LoginRequest;
  path?: never;
  query?: never;
  url: "/auth/custom/login";
};

export type LoginErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type LoginError = LoginErrors[keyof LoginErrors];

export type LoginResponses = {
  /**
   * Successful Response
   */
  200: LoginResponse;
};

export type LoginResponse2 = LoginResponses[keyof LoginResponses];

export type SignupData = {
  body: SignupRequest;
  path?: never;
  query?: never;
  url: "/auth/custom/signup";
};

export type SignupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SignupError = SignupErrors[keyof SignupErrors];

export type SignupResponses = {
  /**
   * Successful Response
   */
  200: SignupResponse;
};

export type SignupResponse2 = SignupResponses[keyof SignupResponses];

export type ForgotPasswordData = {
  body: ForgotPasswordRequest;
  path?: never;
  query?: never;
  url: "/auth/custom/forgot-password";
};

export type ForgotPasswordErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ForgotPasswordError =
  ForgotPasswordErrors[keyof ForgotPasswordErrors];

export type ForgotPasswordResponses = {
  /**
   * Successful Response
   */
  200: ForgotPasswordResponse;
};

export type ForgotPasswordResponse2 =
  ForgotPasswordResponses[keyof ForgotPasswordResponses];

export type CheckEmailVerificationData = {
  body: EmailVerificationRequest;
  path?: never;
  query?: never;
  url: "/auth/custom/email-verification-status";
};

export type CheckEmailVerificationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CheckEmailVerificationError =
  CheckEmailVerificationErrors[keyof CheckEmailVerificationErrors];

export type CheckEmailVerificationResponses = {
  /**
   * Successful Response
   */
  200: EmailVerificationResponse;
};

export type CheckEmailVerificationResponse =
  CheckEmailVerificationResponses[keyof CheckEmailVerificationResponses];

export type EmailConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/email/config";
};

export type EmailConfigResponses = {
  /**
   * Response Email Config
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type EmailConfigResponse =
  EmailConfigResponses[keyof EmailConfigResponses];

export type SendAuthEmailData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/email/send";
};

export type SendAuthEmailResponses = {
  /**
   * Successful Response
   */
  202: unknown;
};

export type AuthBootstrapStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/bootstrap";
};

export type AuthBootstrapStatusResponses = {
  /**
   * Successful Response
   */
  200: AuthBootstrapStatus;
};

export type AuthBootstrapStatusResponse =
  AuthBootstrapStatusResponses[keyof AuthBootstrapStatusResponses];

export type GetWelcomeAuthConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/welcome-config";
};

export type GetWelcomeAuthConfigResponses = {
  /**
   * Successful Response
   */
  200: PublicAuthConfigJson;
};

export type GetWelcomeAuthConfigResponse =
  GetWelcomeAuthConfigResponses[keyof GetWelcomeAuthConfigResponses];

export type GetAuthConfigData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/config";
};

export type GetAuthConfigResponses = {
  /**
   * Successful Response
   */
  200: AuthConfigResponse;
};

export type GetAuthConfigResponse =
  GetAuthConfigResponses[keyof GetAuthConfigResponses];

export type UpdateAuthConfigData = {
  body: AuthConfigJson;
  path?: never;
  query?: never;
  url: "/auth/config";
};

export type UpdateAuthConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateAuthConfigError =
  UpdateAuthConfigErrors[keyof UpdateAuthConfigErrors];

export type UpdateAuthConfigResponses = {
  /**
   * Successful Response
   */
  200: AuthConfigJson;
};

export type UpdateAuthConfigResponse =
  UpdateAuthConfigResponses[keyof UpdateAuthConfigResponses];

export type LegacyAuthUsersData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/migration/users";
};

export type LegacyAuthUsersResponses = {
  /**
   * Response Legacy Auth Users
   *
   * Successful Response
   */
  200: Array<LegacyAuthUser>;
};

export type LegacyAuthUsersResponse =
  LegacyAuthUsersResponses[keyof LegacyAuthUsersResponses];

export type GetOidcSyncStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/config/oidc/sync";
};

export type GetOidcSyncStatusResponses = {
  /**
   * Successful Response
   */
  200: OidcSyncStatusResponse;
};

export type GetOidcSyncStatusResponse =
  GetOidcSyncStatusResponses[keyof GetOidcSyncStatusResponses];

export type SyncOidcConnectionData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/config/oidc/sync";
};

export type SyncOidcConnectionResponses = {
  /**
   * Successful Response
   */
  200: OidcSyncStatusResponse;
};

export type SyncOidcConnectionResponse =
  SyncOidcConnectionResponses[keyof SyncOidcConnectionResponses];

export type GetRedirectUrlData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/config/redirect";
};

export type GetRedirectUrlResponses = {
  /**
   * Response Get Redirect Url
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetRedirectUrlResponse =
  GetRedirectUrlResponses[keyof GetRedirectUrlResponses];

export type AuthRuntimeData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/runtime";
};

export type AuthRuntimeResponses = {
  /**
   * Successful Response
   */
  200: AuthPolicy;
};

export type AuthRuntimeResponse =
  AuthRuntimeResponses[keyof AuthRuntimeResponses];

export type ChangeAuthRuntimeData = {
  body: AuthTransitionInput;
  path?: never;
  query?: never;
  url: "/auth/migration/state";
};

export type ChangeAuthRuntimeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ChangeAuthRuntimeError =
  ChangeAuthRuntimeErrors[keyof ChangeAuthRuntimeErrors];

export type ChangeAuthRuntimeResponses = {
  /**
   * Successful Response
   */
  200: AuthPolicy;
};

export type ChangeAuthRuntimeResponse =
  ChangeAuthRuntimeResponses[keyof ChangeAuthRuntimeResponses];

export type MigrationIdentityData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/migration/identity";
};

export type MigrationIdentityResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type MigrationConnectionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/migration/connections";
};

export type MigrationConnectionsResponses = {
  /**
   * Successful Response
   */
  200: MigrationConnections;
};

export type MigrationConnectionsResponse =
  MigrationConnectionsResponses[keyof MigrationConnectionsResponses];

export type ReviewAuth0EmailsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/auth/migration/emails";
};

export type ReviewAuth0EmailsResponses = {
  /**
   * Successful Response
   */
  200: Auth0EmailReview;
};

export type ReviewAuth0EmailsResponse =
  ReviewAuth0EmailsResponses[keyof ReviewAuth0EmailsResponses];

export type UpdateAuth0EmailsData = {
  body: Auth0EmailUpdateInput;
  path?: never;
  query?: never;
  url: "/auth/migration/emails";
};

export type UpdateAuth0EmailsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateAuth0EmailsError =
  UpdateAuth0EmailsErrors[keyof UpdateAuth0EmailsErrors];

export type UpdateAuth0EmailsResponses = {
  /**
   * Successful Response
   */
  200: Auth0EmailUpdateResult;
};

export type UpdateAuth0EmailsResponse =
  UpdateAuth0EmailsResponses[keyof UpdateAuth0EmailsResponses];

export type ListProviderDefinitionsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Category
     */
    category?: ProviderCategory | null;
  };
  url: "/groups/{group_id}/providers/definitions";
};

export type ListProviderDefinitionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListProviderDefinitionsError =
  ListProviderDefinitionsErrors[keyof ListProviderDefinitionsErrors];

export type ListProviderDefinitionsResponses = {
  /**
   * Response List Provider Definitions
   *
   * Successful Response
   */
  200: Array<ProviderDefinitionPreview>;
};

export type ListProviderDefinitionsResponse =
  ListProviderDefinitionsResponses[keyof ListProviderDefinitionsResponses];

export type ListProvidersData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Category
     */
    category?: ProviderCategory | null;
  };
  url: "/groups/{group_id}/providers";
};

export type ListProvidersErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListProvidersError = ListProvidersErrors[keyof ListProvidersErrors];

export type ListProvidersResponses = {
  /**
   * Response List Providers
   *
   * Successful Response
   */
  200: Array<ProviderResponse>;
};

export type ListProvidersResponse =
  ListProvidersResponses[keyof ListProvidersResponses];

export type CreateProviderData = {
  body: ProviderCreateRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers";
};

export type CreateProviderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateProviderError =
  CreateProviderErrors[keyof CreateProviderErrors];

export type CreateProviderResponses = {
  /**
   * Successful Response
   */
  200: ProviderResponse;
};

export type CreateProviderResponse =
  CreateProviderResponses[keyof CreateProviderResponses];

export type DeleteProviderData = {
  body?: never;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}";
};

export type DeleteProviderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteProviderError =
  DeleteProviderErrors[keyof DeleteProviderErrors];

export type DeleteProviderResponses = {
  /**
   * Response Delete Provider
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteProviderResponse =
  DeleteProviderResponses[keyof DeleteProviderResponses];

export type GetProviderData = {
  body?: never;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}";
};

export type GetProviderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetProviderError = GetProviderErrors[keyof GetProviderErrors];

export type GetProviderResponses = {
  /**
   * Successful Response
   */
  200: ProviderResponse;
};

export type GetProviderResponse =
  GetProviderResponses[keyof GetProviderResponses];

export type UpdateProviderData = {
  body: ProviderUpdateRequest;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}";
};

export type UpdateProviderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateProviderError =
  UpdateProviderErrors[keyof UpdateProviderErrors];

export type UpdateProviderResponses = {
  /**
   * Successful Response
   */
  200: ProviderResponse;
};

export type UpdateProviderResponse =
  UpdateProviderResponses[keyof UpdateProviderResponses];

export type DeactivateProviderData = {
  body?: never;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}/deactivate";
};

export type DeactivateProviderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeactivateProviderError =
  DeactivateProviderErrors[keyof DeactivateProviderErrors];

export type DeactivateProviderResponses = {
  /**
   * Successful Response
   */
  200: ProviderResponse;
};

export type DeactivateProviderResponse =
  DeactivateProviderResponses[keyof DeactivateProviderResponses];

export type ActivateProviderData = {
  body?: never;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}/activate";
};

export type ActivateProviderErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ActivateProviderError =
  ActivateProviderErrors[keyof ActivateProviderErrors];

export type ActivateProviderResponses = {
  /**
   * Successful Response
   */
  200: ProviderResponse;
};

export type ActivateProviderResponse =
  ActivateProviderResponses[keyof ActivateProviderResponses];

export type AddProviderRegionData = {
  body: AddProviderRegionRequest;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}/regions";
};

export type AddProviderRegionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddProviderRegionError =
  AddProviderRegionErrors[keyof AddProviderRegionErrors];

export type AddProviderRegionResponses = {
  /**
   * Successful Response
   */
  200: ProviderResponse;
};

export type AddProviderRegionResponse =
  AddProviderRegionResponses[keyof AddProviderRegionResponses];

export type TestProviderConnectionData = {
  body?: never;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}/test";
};

export type TestProviderConnectionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TestProviderConnectionError =
  TestProviderConnectionErrors[keyof TestProviderConnectionErrors];

export type TestProviderConnectionResponses = {
  /**
   * Successful Response
   */
  200: ProviderTestResponse;
};

export type TestProviderConnectionResponse =
  TestProviderConnectionResponses[keyof TestProviderConnectionResponses];

export type TestAllModelsData = {
  body?: never;
  path: {
    /**
     * Provider Id
     */
    provider_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/providers/{provider_id}/test-all";
};

export type TestAllModelsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TestAllModelsError = TestAllModelsErrors[keyof TestAllModelsErrors];

export type TestAllModelsResponses = {
  /**
   * Response Test All Models
   *
   * Successful Response
   */
  200: Array<{
    [key: string]: SpotflowBackendApiProvidersJsonValue;
  }>;
};

export type TestAllModelsResponse =
  TestAllModelsResponses[keyof TestAllModelsResponses];

export type ListModelConfigurationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/configurations";
};

export type ListModelConfigurationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListModelConfigurationsError =
  ListModelConfigurationsErrors[keyof ListModelConfigurationsErrors];

export type ListModelConfigurationsResponses = {
  /**
   * Response List Model Configurations
   *
   * Successful Response
   */
  200: Array<ModelConfigurationResponse>;
};

export type ListModelConfigurationsResponse =
  ListModelConfigurationsResponses[keyof ListModelConfigurationsResponses];

export type CreateCustomModelConfigurationData = {
  body: CustomModelDraft;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/configurations";
};

export type CreateCustomModelConfigurationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateCustomModelConfigurationError =
  CreateCustomModelConfigurationErrors[keyof CreateCustomModelConfigurationErrors];

export type CreateCustomModelConfigurationResponses = {
  /**
   * Successful Response
   */
  201: ModelConfigurationResponse;
};

export type CreateCustomModelConfigurationResponse =
  CreateCustomModelConfigurationResponses[keyof CreateCustomModelConfigurationResponses];

export type DeleteCustomModelConfigurationData = {
  body?: never;
  path: {
    /**
     * Model Id
     */
    model_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/configurations/{model_id}";
};

export type DeleteCustomModelConfigurationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteCustomModelConfigurationError =
  DeleteCustomModelConfigurationErrors[keyof DeleteCustomModelConfigurationErrors];

export type DeleteCustomModelConfigurationResponses = {
  /**
   * Successful Response
   */
  204: void;
};

export type DeleteCustomModelConfigurationResponse =
  DeleteCustomModelConfigurationResponses[keyof DeleteCustomModelConfigurationResponses];

export type UpdateModelConfigurationData = {
  body: VisibilityUpdate;
  path: {
    /**
     * Model Id
     */
    model_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/configurations/{model_id}";
};

export type UpdateModelConfigurationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateModelConfigurationError =
  UpdateModelConfigurationErrors[keyof UpdateModelConfigurationErrors];

export type UpdateModelConfigurationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListPriceReferencesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Q
     */
    q: string;
    /**
     * Limit
     */
    limit?: number;
  };
  url: "/groups/{group_id}/models/price-references";
};

export type ListPriceReferencesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPriceReferencesError =
  ListPriceReferencesErrors[keyof ListPriceReferencesErrors];

export type ListPriceReferencesResponses = {
  /**
   * Response List Price References
   *
   * Successful Response
   */
  200: Array<PriceReferenceResponse>;
};

export type ListPriceReferencesResponse =
  ListPriceReferencesResponses[keyof ListPriceReferencesResponses];

export type UpdateCustomModelConfigurationData = {
  body: CustomModelEdit;
  path: {
    /**
     * Model Id
     */
    model_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/configurations/{model_id}/custom";
};

export type UpdateCustomModelConfigurationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCustomModelConfigurationError =
  UpdateCustomModelConfigurationErrors[keyof UpdateCustomModelConfigurationErrors];

export type UpdateCustomModelConfigurationResponses = {
  /**
   * Successful Response
   */
  200: ModelConfigurationResponse;
};

export type UpdateCustomModelConfigurationResponse =
  UpdateCustomModelConfigurationResponses[keyof UpdateCustomModelConfigurationResponses];

export type RepriceCustomModelUsageData = {
  body: CustomModelRepriceRequest;
  path: {
    /**
     * Model Id
     */
    model_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/configurations/{model_id}/custom/reprice";
};

export type RepriceCustomModelUsageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RepriceCustomModelUsageError =
  RepriceCustomModelUsageErrors[keyof RepriceCustomModelUsageErrors];

export type RepriceCustomModelUsageResponses = {
  /**
   * Successful Response
   */
  202: CustomModelRepriceQueued;
};

export type RepriceCustomModelUsageResponse =
  RepriceCustomModelUsageResponses[keyof RepriceCustomModelUsageResponses];

export type ListEmbeddingConfigurationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/embeddings/configurations";
};

export type ListEmbeddingConfigurationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListEmbeddingConfigurationsError =
  ListEmbeddingConfigurationsErrors[keyof ListEmbeddingConfigurationsErrors];

export type ListEmbeddingConfigurationsResponses = {
  /**
   * Response List Embedding Configurations
   *
   * Successful Response
   */
  200: Array<EmbeddingConfigurationResponse>;
};

export type ListEmbeddingConfigurationsResponse =
  ListEmbeddingConfigurationsResponses[keyof ListEmbeddingConfigurationsResponses];

export type UpdateEmbeddingConfigurationData = {
  body: VisibilityUpdate;
  path: {
    /**
     * Embedding Id
     */
    embedding_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/embeddings/configurations/{embedding_id}";
};

export type UpdateEmbeddingConfigurationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateEmbeddingConfigurationError =
  UpdateEmbeddingConfigurationErrors[keyof UpdateEmbeddingConfigurationErrors];

export type UpdateEmbeddingConfigurationResponses = {
  /**
   * Response Update Embedding Configuration
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type UpdateEmbeddingConfigurationResponse =
  UpdateEmbeddingConfigurationResponses[keyof UpdateEmbeddingConfigurationResponses];

export type ListRerankerConfigurationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/rerankers/configurations";
};

export type ListRerankerConfigurationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListRerankerConfigurationsError =
  ListRerankerConfigurationsErrors[keyof ListRerankerConfigurationsErrors];

export type ListRerankerConfigurationsResponses = {
  /**
   * Response List Reranker Configurations
   *
   * Successful Response
   */
  200: Array<RerankerConfigurationResponse>;
};

export type ListRerankerConfigurationsResponse =
  ListRerankerConfigurationsResponses[keyof ListRerankerConfigurationsResponses];

export type ListDecisionModelConfigurationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/decision-models/configurations";
};

export type ListDecisionModelConfigurationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListDecisionModelConfigurationsError =
  ListDecisionModelConfigurationsErrors[keyof ListDecisionModelConfigurationsErrors];

export type ListDecisionModelConfigurationsResponses = {
  /**
   * Response List Decision Model Configurations
   *
   * Successful Response
   */
  200: Array<DecisionModelConfigurationResponse>;
};

export type ListDecisionModelConfigurationsResponse =
  ListDecisionModelConfigurationsResponses[keyof ListDecisionModelConfigurationsResponses];

export type ListVoiceModelConfigurationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Mode
     *
     * Only rows of this voice mode; both when unset
     */
    mode?: VoiceMode | null;
  };
  url: "/groups/{group_id}/voice-models/configurations";
};

export type ListVoiceModelConfigurationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListVoiceModelConfigurationsError =
  ListVoiceModelConfigurationsErrors[keyof ListVoiceModelConfigurationsErrors];

export type ListVoiceModelConfigurationsResponses = {
  /**
   * Response List Voice Model Configurations
   *
   * Successful Response
   */
  200: Array<VoiceModelConfigurationResponse>;
};

export type ListVoiceModelConfigurationsResponse =
  ListVoiceModelConfigurationsResponses[keyof ListVoiceModelConfigurationsResponses];

export type CreateProviderLinkData = {
  body: LinkProviderRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links";
};

export type CreateProviderLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateProviderLinkError =
  CreateProviderLinkErrors[keyof CreateProviderLinkErrors];

export type CreateProviderLinkResponses = {
  /**
   * Successful Response
   */
  200: ProviderLinkResponse;
};

export type CreateProviderLinkResponse =
  CreateProviderLinkResponses[keyof CreateProviderLinkResponses];

export type RunHealthChecksData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links/health-check";
};

export type RunHealthChecksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RunHealthChecksError =
  RunHealthChecksErrors[keyof RunHealthChecksErrors];

export type RunHealthChecksResponses = {
  /**
   * Response Run Health Checks
   *
   * Successful Response
   */
  200: {
    [key: string]: number;
  };
};

export type RunHealthChecksResponse =
  RunHealthChecksResponses[keyof RunHealthChecksResponses];

export type ChaosTestEnabledData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links/chaos-test/enabled";
};

export type ChaosTestEnabledErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ChaosTestEnabledError =
  ChaosTestEnabledErrors[keyof ChaosTestEnabledErrors];

export type ChaosTestEnabledResponses = {
  /**
   * Successful Response
   */
  200: ChaosTestingFlag;
};

export type ChaosTestEnabledResponse =
  ChaosTestEnabledResponses[keyof ChaosTestEnabledResponses];

export type ChaosTestLinksData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links/chaos-test";
};

export type ChaosTestLinksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ChaosTestLinksError =
  ChaosTestLinksErrors[keyof ChaosTestLinksErrors];

export type ChaosTestLinksResponses = {
  /**
   * Successful Response
   */
  200: ChaosTestResponse;
};

export type ChaosTestLinksResponse =
  ChaosTestLinksResponses[keyof ChaosTestLinksResponses];

export type DeleteProviderLinkData = {
  body?: never;
  path: {
    /**
     * Link Id
     */
    link_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links/{link_id}";
};

export type DeleteProviderLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteProviderLinkError =
  DeleteProviderLinkErrors[keyof DeleteProviderLinkErrors];

export type DeleteProviderLinkResponses = {
  /**
   * Response Delete Provider Link
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type DeleteProviderLinkResponse =
  DeleteProviderLinkResponses[keyof DeleteProviderLinkResponses];

export type UpdateProviderLinkData = {
  body: UpdateLinkRequest;
  path: {
    /**
     * Link Id
     */
    link_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links/{link_id}";
};

export type UpdateProviderLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateProviderLinkError =
  UpdateProviderLinkErrors[keyof UpdateProviderLinkErrors];

export type UpdateProviderLinkResponses = {
  /**
   * Response Update Provider Link
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type UpdateProviderLinkResponse =
  UpdateProviderLinkResponses[keyof UpdateProviderLinkResponses];

export type TestProviderLinkData = {
  body?: never;
  path: {
    /**
     * Link Id
     */
    link_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/provider-links/{link_id}/test";
};

export type TestProviderLinkErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TestProviderLinkError =
  TestProviderLinkErrors[keyof TestProviderLinkErrors];

export type TestProviderLinkResponses = {
  /**
   * Response Test Provider Link
   *
   * Successful Response
   */
  200: {
    [key: string]: SpotflowBackendApiModelConfigsJsonValue;
  };
};

export type TestProviderLinkResponse =
  TestProviderLinkResponses[keyof TestProviderLinkResponses];

export type StreamTestBatchData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Link Ids
     */
    link_ids?: Array<string>;
    /**
     * Provider Ids
     */
    provider_ids?: Array<string>;
  };
  url: "/groups/{group_id}/provider-links/test/stream";
};

export type StreamTestBatchErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StreamTestBatchError =
  StreamTestBatchErrors[keyof StreamTestBatchErrors];

export type StreamTestBatchResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListInboxesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Sort By
     *
     * Field to sort by (e.g., name, created_at, updated_at, order)
     */
    sort_by?: string | null;
    /**
     * Sort Order
     *
     * Sort order (asc or desc)
     */
    sort_order?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/inboxes";
};

export type ListInboxesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListInboxesError = ListInboxesErrors[keyof ListInboxesErrors];

export type ListInboxesResponses = {
  /**
   * Successful Response
   */
  200: PageInboxListResponse;
};

export type ListInboxesResponse =
  ListInboxesResponses[keyof ListInboxesResponses];

export type CreateInboxData = {
  body: InboxCreate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes";
};

export type CreateInboxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateInboxError = CreateInboxErrors[keyof CreateInboxErrors];

export type CreateInboxResponses = {
  /**
   * Successful Response
   */
  200: InboxCreateResponse;
};

export type CreateInboxResponse =
  CreateInboxResponses[keyof CreateInboxResponses];

export type CheckSlugData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Slug
     */
    slug: string;
    /**
     * Exclude Inbox Id
     */
    exclude_inbox_id?: string | null;
  };
  url: "/groups/{group_id}/inboxes/check-slug";
};

export type CheckSlugErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CheckSlugError = CheckSlugErrors[keyof CheckSlugErrors];

export type CheckSlugResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type CheckPrefixData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Prefix
     */
    prefix: string;
    /**
     * Exclude Inbox Id
     */
    exclude_inbox_id?: string | null;
  };
  url: "/groups/{group_id}/inboxes/check-prefix";
};

export type CheckPrefixErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CheckPrefixError = CheckPrefixErrors[keyof CheckPrefixErrors];

export type CheckPrefixResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DeleteInboxData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}";
};

export type DeleteInboxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteInboxError = DeleteInboxErrors[keyof DeleteInboxErrors];

export type DeleteInboxResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteInboxResponse =
  DeleteInboxResponses[keyof DeleteInboxResponses];

export type GetInboxData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}";
};

export type GetInboxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInboxError = GetInboxErrors[keyof GetInboxErrors];

export type GetInboxResponses = {
  /**
   * Successful Response
   */
  200: InboxResponse;
};

export type GetInboxResponse = GetInboxResponses[keyof GetInboxResponses];

export type UpdateInboxData = {
  body: InboxUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}";
};

export type UpdateInboxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateInboxError = UpdateInboxErrors[keyof UpdateInboxErrors];

export type UpdateInboxResponses = {
  /**
   * Successful Response
   */
  200: InboxResponse;
};

export type UpdateInboxResponse =
  UpdateInboxResponses[keyof UpdateInboxResponses];

export type UpdateInboxCaseLayoutData = {
  body: CaseLayoutUpdateRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}/case-layout";
};

export type UpdateInboxCaseLayoutErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateInboxCaseLayoutError =
  UpdateInboxCaseLayoutErrors[keyof UpdateInboxCaseLayoutErrors];

export type UpdateInboxCaseLayoutResponses = {
  /**
   * Successful Response
   */
  200: InboxResponse;
};

export type UpdateInboxCaseLayoutResponse =
  UpdateInboxCaseLayoutResponses[keyof UpdateInboxCaseLayoutResponses];

export type RenameInboxGroupData = {
  body: InboxGroupRenameRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}/groups/rename";
};

export type RenameInboxGroupErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RenameInboxGroupError =
  RenameInboxGroupErrors[keyof RenameInboxGroupErrors];

export type RenameInboxGroupResponses = {
  /**
   * Successful Response
   */
  200: InboxResponse;
};

export type RenameInboxGroupResponse =
  RenameInboxGroupResponses[keyof RenameInboxGroupResponses];

export type ExportInboxData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: {
    /**
     * Export wire format. `auto` (default) emits the legacy base64-encoded bundle for back-compat; pass `v4` for plaintext multi-doc YAML (`.nx`), or `v3` for base64 explicitly.
     */
    version?: ExportFormat;
    /**
     * Set Active On Import
     */
    set_active_on_import?: boolean;
    /**
     * Include Dependencies
     *
     * Bundle referenced sub-flows / KBs / files into the .nx.
     */
    include_dependencies?: boolean;
  };
  url: "/groups/{group_id}/inboxes/{inbox_id}/export";
};

export type ExportInboxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportInboxError = ExportInboxErrors[keyof ExportInboxErrors];

export type ExportInboxResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ExportInboxPreviewData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}/export/preview";
};

export type ExportInboxPreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportInboxPreviewError =
  ExportInboxPreviewErrors[keyof ExportInboxPreviewErrors];

export type ExportInboxPreviewResponses = {
  /**
   * Response Export Inbox Preview
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type ExportInboxPreviewResponse =
  ExportInboxPreviewResponses[keyof ExportInboxPreviewResponses];

export type ImportInboxData = {
  body: ImportInboxRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    mode?: ImportMode;
    /**
     * Activate
     */
    activate?: boolean;
    on_missing_dependency?: MissingDependencyPolicy;
    /**
     * Dry Run
     */
    dry_run?: boolean;
  };
  url: "/groups/{group_id}/inboxes/import";
};

export type ImportInboxErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ImportInboxError = ImportInboxErrors[keyof ImportInboxErrors];

export type ImportInboxResponses = {
  /**
   * Response Import Inbox
   *
   * Successful Response
   */
  200: Array<ExportableReference>;
};

export type ImportInboxResponse =
  ImportInboxResponses[keyof ImportInboxResponses];

export type ListInboxTemplatesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inbox-templates";
};

export type ListInboxTemplatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListInboxTemplatesError =
  ListInboxTemplatesErrors[keyof ListInboxTemplatesErrors];

export type ListInboxTemplatesResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetInboxTemplateConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Template Id
     */
    template_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inbox-templates/{template_id}/config";
};

export type GetInboxTemplateConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInboxTemplateConfigError =
  GetInboxTemplateConfigErrors[keyof GetInboxTemplateConfigErrors];

export type GetInboxTemplateConfigResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type DeployInboxTemplateData = {
  body: InboxTemplateDeploy;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Template Id
     */
    template_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inbox-templates/{template_id}/deploy";
};

export type DeployInboxTemplateErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeployInboxTemplateError =
  DeployInboxTemplateErrors[keyof DeployInboxTemplateErrors];

export type DeployInboxTemplateResponses = {
  /**
   * Response Deploy Inbox Template
   *
   * Successful Response
   */
  200: Array<ExportableReference>;
};

export type DeployInboxTemplateResponse =
  DeployInboxTemplateResponses[keyof DeployInboxTemplateResponses];

export type CreateInboxFieldData = {
  body: InboxFieldCreate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}/inbox-fields";
};

export type CreateInboxFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateInboxFieldError =
  CreateInboxFieldErrors[keyof CreateInboxFieldErrors];

export type CreateInboxFieldResponses = {
  /**
   * Successful Response
   */
  200: InboxFieldResponse;
};

export type CreateInboxFieldResponse =
  CreateInboxFieldResponses[keyof CreateInboxFieldResponses];

export type DeleteInboxFieldData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Field Id
     */
    field_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inbox-fields/{field_id}";
};

export type DeleteInboxFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteInboxFieldError =
  DeleteInboxFieldErrors[keyof DeleteInboxFieldErrors];

export type DeleteInboxFieldResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteInboxFieldResponse =
  DeleteInboxFieldResponses[keyof DeleteInboxFieldResponses];

export type UpdateInboxFieldData = {
  body: InboxFieldUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Field Id
     */
    field_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inbox-fields/{field_id}";
};

export type UpdateInboxFieldErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateInboxFieldError =
  UpdateInboxFieldErrors[keyof UpdateInboxFieldErrors];

export type UpdateInboxFieldResponses = {
  /**
   * Successful Response
   */
  200: InboxFieldResponse;
};

export type UpdateInboxFieldResponse =
  UpdateInboxFieldResponses[keyof UpdateInboxFieldResponses];

export type ListCasesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Inbox Id
     *
     * Filter by inbox ID
     */
    inbox_id?: string | null;
    /**
     * Statuses
     *
     * Filter by statuses
     */
    statuses?: Array<string> | null;
    /**
     * Priorities
     *
     * Filter by priorities
     */
    priorities?: Array<string> | null;
    /**
     * Search
     *
     * Search cases by ID, subject, or status
     */
    search?: string | null;
    /**
     * Sort By
     *
     * Field to sort by (e.g., created_at, updated_at, status)
     */
    sort_by?: string | null;
    /**
     * Sort Order
     *
     * Sort order (asc or desc)
     */
    sort_order?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/cases";
};

export type ListCasesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListCasesError = ListCasesErrors[keyof ListCasesErrors];

export type ListCasesResponses = {
  /**
   * Successful Response
   */
  200: PageCaseListResponse;
};

export type ListCasesResponse = ListCasesResponses[keyof ListCasesResponses];

export type CreateCaseData = {
  body: SpotflowBackendSchemasCaseManagementCaseCreate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases";
};

export type CreateCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateCaseError = CreateCaseErrors[keyof CreateCaseErrors];

export type CreateCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseCreateResponse;
};

export type CreateCaseResponse = CreateCaseResponses[keyof CreateCaseResponses];

export type DeleteCaseData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}";
};

export type DeleteCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteCaseError = DeleteCaseErrors[keyof DeleteCaseErrors];

export type DeleteCaseResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteCaseResponse = DeleteCaseResponses[keyof DeleteCaseResponses];

export type GetCaseData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}";
};

export type GetCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCaseError = GetCaseErrors[keyof GetCaseErrors];

export type GetCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseResponse;
};

export type GetCaseResponse = GetCaseResponses[keyof GetCaseResponses];

export type UpdateCaseData = {
  body: CaseUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}";
};

export type UpdateCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateCaseError = UpdateCaseErrors[keyof UpdateCaseErrors];

export type UpdateCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseResponse;
};

export type UpdateCaseResponse = UpdateCaseResponses[keyof UpdateCaseResponses];

export type SimulateEmailData = {
  body: BodySimulateEmail;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}/simulate-email";
};

export type SimulateEmailErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SimulateEmailError = SimulateEmailErrors[keyof SimulateEmailErrors];

export type SimulateEmailResponses = {
  /**
   * Response Simulate Email
   *
   * Successful Response
   */
  200: CaseCreateResponse | null;
};

export type SimulateEmailResponse =
  SimulateEmailResponses[keyof SimulateEmailResponses];

export type DeleteAllCasesData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Inbox Id
     */
    inbox_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/inboxes/{inbox_id}/cases";
};

export type DeleteAllCasesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteAllCasesError =
  DeleteAllCasesErrors[keyof DeleteAllCasesErrors];

export type DeleteAllCasesResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteAllCasesResponse =
  DeleteAllCasesResponses[keyof DeleteAllCasesResponses];

export type CloneCaseData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}/clone";
};

export type CloneCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CloneCaseError = CloneCaseErrors[keyof CloneCaseErrors];

export type CloneCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseCreateResponse;
};

export type CloneCaseResponse = CloneCaseResponses[keyof CloneCaseResponses];

export type MoveCaseData = {
  body: CaseMove;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}/move";
};

export type MoveCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type MoveCaseError = MoveCaseErrors[keyof MoveCaseErrors];

export type MoveCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseResponse;
};

export type MoveCaseResponse = MoveCaseResponses[keyof MoveCaseResponses];

export type ReprocessCaseData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}/reprocess";
};

export type ReprocessCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReprocessCaseError = ReprocessCaseErrors[keyof ReprocessCaseErrors];

export type ReprocessCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseResponse;
};

export type ReprocessCaseResponse =
  ReprocessCaseResponses[keyof ReprocessCaseResponses];

export type LinkConversationToCaseData = {
  body: LinkConversationRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Case Id
     */
    case_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/cases/{case_id}/link-conversation";
};

export type LinkConversationToCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type LinkConversationToCaseError =
  LinkConversationToCaseErrors[keyof LinkConversationToCaseErrors];

export type LinkConversationToCaseResponses = {
  /**
   * Successful Response
   */
  200: CaseResponse;
};

export type LinkConversationToCaseResponse =
  LinkConversationToCaseResponses[keyof LinkConversationToCaseResponses];

export type ListTasksData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Statuses
     *
     * Filter by statuses
     */
    statuses?: Array<string> | null;
    /**
     * Assigned To
     *
     * Filter by assigned user
     */
    assigned_to?: string | null;
    /**
     * Priority
     *
     * Filter by priority
     */
    priority?: string | null;
    /**
     * Inbox Id
     *
     * Filter by inbox ID
     */
    inbox_id?: string | null;
    /**
     * Case Id
     *
     * Filter by case ID
     */
    case_id?: string | null;
    /**
     * Search
     *
     * Search tasks by ID, title, or case subject
     */
    search?: string | null;
    /**
     * Sort By
     *
     * Field to sort by (e.g., created_at, updated_at, priority)
     */
    sort_by?: string | null;
    /**
     * Sort Order
     *
     * Sort order (asc or desc)
     */
    sort_order?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/tasks";
};

export type ListTasksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTasksError = ListTasksErrors[keyof ListTasksErrors];

export type ListTasksResponses = {
  /**
   * Successful Response
   */
  200: PageTaskListResponse;
};

export type ListTasksResponse = ListTasksResponses[keyof ListTasksResponses];

export type GetTaskData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Task Id
     */
    task_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tasks/{task_id}";
};

export type GetTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTaskError = GetTaskErrors[keyof GetTaskErrors];

export type GetTaskResponses = {
  /**
   * Successful Response
   */
  200: TaskDetailResponse;
};

export type GetTaskResponse = GetTaskResponses[keyof GetTaskResponses];

export type UpdateTaskData = {
  body: TaskUpdate;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Task Id
     */
    task_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tasks/{task_id}";
};

export type UpdateTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTaskError = UpdateTaskErrors[keyof UpdateTaskErrors];

export type UpdateTaskResponses = {
  /**
   * Successful Response
   */
  200: TaskDetailResponse;
};

export type UpdateTaskResponse = UpdateTaskResponses[keyof UpdateTaskResponses];

export type ListPluginsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Search
     */
    search?: string | null;
    /**
     * Status
     */
    status?: PluginStatus | null;
    /**
     * Category
     */
    category?: PluginCategory | null;
    /**
     * Source Type
     */
    source_type?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/plugins";
};

export type ListPluginsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPluginsError = ListPluginsErrors[keyof ListPluginsErrors];

export type ListPluginsResponses = {
  /**
   * Successful Response
   */
  200: PageInstalledPluginResponse;
};

export type ListPluginsResponse =
  ListPluginsResponses[keyof ListPluginsResponses];

export type CreatePluginData = {
  body: PluginCreateRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins";
};

export type CreatePluginErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePluginError = CreatePluginErrors[keyof CreatePluginErrors];

export type CreatePluginResponses = {
  /**
   * Successful Response
   */
  200: InstalledPluginResponse;
};

export type CreatePluginResponse =
  CreatePluginResponses[keyof CreatePluginResponses];

export type ListMarketplacePluginsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Search
     */
    search?: string | null;
    /**
     * Category
     */
    category?: PluginCategory | null;
  };
  url: "/groups/{group_id}/plugins/marketplace";
};

export type ListMarketplacePluginsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListMarketplacePluginsError =
  ListMarketplacePluginsErrors[keyof ListMarketplacePluginsErrors];

export type ListMarketplacePluginsResponses = {
  /**
   * Response List Marketplace Plugins
   *
   * Successful Response
   */
  200: Array<MarketplacePluginResponse>;
};

export type ListMarketplacePluginsResponse =
  ListMarketplacePluginsResponses[keyof ListMarketplacePluginsResponses];

export type ListPluginCatalogData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/catalog";
};

export type ListPluginCatalogErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPluginCatalogError =
  ListPluginCatalogErrors[keyof ListPluginCatalogErrors];

export type ListPluginCatalogResponses = {
  /**
   * Response List Plugin Catalog
   *
   * Successful Response
   */
  200: Array<PluginCatalogEntry>;
};

export type ListPluginCatalogResponse =
  ListPluginCatalogResponses[keyof ListPluginCatalogResponses];

export type DeletePluginData = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}";
};

export type DeletePluginErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePluginError = DeletePluginErrors[keyof DeletePluginErrors];

export type DeletePluginResponses = {
  /**
   * Response Delete Plugin
   *
   * Successful Response
   */
  200: boolean;
};

export type DeletePluginResponse =
  DeletePluginResponses[keyof DeletePluginResponses];

export type GetPluginData = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}";
};

export type GetPluginErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPluginError = GetPluginErrors[keyof GetPluginErrors];

export type GetPluginResponses = {
  /**
   * Successful Response
   */
  200: InstalledPluginResponse;
};

export type GetPluginResponse = GetPluginResponses[keyof GetPluginResponses];

export type ReloadPluginData = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}/reload";
};

export type ReloadPluginErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReloadPluginError = ReloadPluginErrors[keyof ReloadPluginErrors];

export type ReloadPluginResponses = {
  /**
   * Successful Response
   */
  200: InstalledPluginResponse;
};

export type ReloadPluginResponse =
  ReloadPluginResponses[keyof ReloadPluginResponses];

export type ValidatePluginConfigData = {
  body: PluginConfigRequest;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}/config/validate";
};

export type ValidatePluginConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ValidatePluginConfigError =
  ValidatePluginConfigErrors[keyof ValidatePluginConfigErrors];

export type ValidatePluginConfigResponses = {
  /**
   * Successful Response
   */
  200: ValidationResult;
};

export type ValidatePluginConfigResponse =
  ValidatePluginConfigResponses[keyof ValidatePluginConfigResponses];

export type UpdatePluginConfigData = {
  body: PluginConfigRequest;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}/config";
};

export type UpdatePluginConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePluginConfigError =
  UpdatePluginConfigErrors[keyof UpdatePluginConfigErrors];

export type UpdatePluginConfigResponses = {
  /**
   * Successful Response
   */
  200: InstalledPluginResponse;
};

export type UpdatePluginConfigResponse =
  UpdatePluginConfigResponses[keyof UpdatePluginConfigResponses];

export type CompleteIntegrationData = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}/integrations/{name}/complete";
};

export type CompleteIntegrationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CompleteIntegrationError =
  CompleteIntegrationErrors[keyof CompleteIntegrationErrors];

export type CompleteIntegrationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type CompleteIntegrationResponse =
  CompleteIntegrationResponses[keyof CompleteIntegrationResponses];

export type DisconnectIntegration2Data = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Name
     */
    name: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/plugins/{plugin_id}/integrations/{name}/disconnect";
};

export type DisconnectIntegration2Errors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DisconnectIntegration2Error =
  DisconnectIntegration2Errors[keyof DisconnectIntegration2Errors];

export type DisconnectIntegration2Responses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DisconnectIntegration2Response =
  DisconnectIntegration2Responses[keyof DisconnectIntegration2Responses];

export type GetPluginLogsData = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Limit
     */
    limit?: number | null;
    /**
     * Level
     */
    level?: string | null;
    /**
     * Since
     */
    since?: string | null;
    /**
     * Search Query
     */
    search_query?: string | null;
  };
  url: "/groups/{group_id}/plugins/{plugin_id}/logs";
};

export type GetPluginLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPluginLogsError = GetPluginLogsErrors[keyof GetPluginLogsErrors];

export type GetPluginLogsResponses = {
  /**
   * Response Get Plugin Logs
   *
   * Successful Response
   */
  200: Array<LogEntry>;
};

export type GetPluginLogsResponse =
  GetPluginLogsResponses[keyof GetPluginLogsResponses];

export type GetPluginLogStatsData = {
  body?: never;
  path: {
    /**
     * Plugin Id
     */
    plugin_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Since
     */
    since?: string | null;
  };
  url: "/groups/{group_id}/plugins/{plugin_id}/logs/stats";
};

export type GetPluginLogStatsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPluginLogStatsError =
  GetPluginLogStatsErrors[keyof GetPluginLogStatsErrors];

export type GetPluginLogStatsResponses = {
  /**
   * Response Get Plugin Log Stats
   *
   * Successful Response
   */
  200: {
    [key: string]: number;
  };
};

export type GetPluginLogStatsResponse =
  GetPluginLogStatsResponses[keyof GetPluginLogStatsResponses];

export type ListConversationsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Preview
     *
     * Preview changes without saving
     */
    preview?: boolean | null;
    /**
     * Query
     *
     * Search query
     */
    query?: string | null;
    /**
     * User Id
     */
    user_id?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * From Date
     */
    from_date?: string | null;
    /**
     * To Date
     */
    to_date?: string | null;
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Type
     */
    type?: "assistant" | null;
    /**
     * Has Feedback
     *
     * Only conversations with at least one rated message
     */
    has_feedback?: boolean;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
  };
  url: "/groups/{group_id}/conversation";
};

export type ListConversationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListConversationsError =
  ListConversationsErrors[keyof ListConversationsErrors];

export type ListConversationsResponses = {
  /**
   * Successful Response
   */
  200: PageConversation;
};

export type ListConversationsResponse =
  ListConversationsResponses[keyof ListConversationsResponses];

export type CreateConversationData = {
  body: CreateConversation;
  headers?: {
    /**
     * Accept-Language
     */
    "accept-language"?: string | null;
  };
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
  };
  url: "/groups/{group_id}/conversation";
};

export type CreateConversationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateConversationError =
  CreateConversationErrors[keyof CreateConversationErrors];

export type CreateConversationResponses = {
  /**
   * Successful Response
   */
  200: ConversationWithMessages;
};

export type CreateConversationResponse =
  CreateConversationResponses[keyof CreateConversationResponses];

export type ListConversationCostsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Conversation Ids
     */
    conversation_ids: Array<string>;
  };
  url: "/groups/{group_id}/conversation/costs";
};

export type ListConversationCostsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListConversationCostsError =
  ListConversationCostsErrors[keyof ListConversationCostsErrors];

export type ListConversationCostsResponses = {
  /**
   * Response List Conversation Costs
   *
   * Successful Response
   */
  200: Array<ConversationCostItem>;
};

export type ListConversationCostsResponse =
  ListConversationCostsResponses[keyof ListConversationCostsResponses];

export type GetConversationCostSummaryData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/{conversation_id}/cost";
};

export type GetConversationCostSummaryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConversationCostSummaryError =
  GetConversationCostSummaryErrors[keyof GetConversationCostSummaryErrors];

export type GetConversationCostSummaryResponses = {
  /**
   * Successful Response
   */
  200: ConversationCostSummary;
};

export type GetConversationCostSummaryResponse =
  GetConversationCostSummaryResponses[keyof GetConversationCostSummaryResponses];

export type BulkExportFeedbackCsvData = {
  body: ConversationBulkRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/bulk/export-feedback";
};

export type BulkExportFeedbackCsvErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkExportFeedbackCsvError =
  BulkExportFeedbackCsvErrors[keyof BulkExportFeedbackCsvErrors];

export type BulkExportFeedbackCsvResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BulkExportConversationsCsvData = {
  body: ConversationBulkRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/bulk/export";
};

export type BulkExportConversationsCsvErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkExportConversationsCsvError =
  BulkExportConversationsCsvErrors[keyof BulkExportConversationsCsvErrors];

export type BulkExportConversationsCsvResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type BulkDeleteConversationsData = {
  body: ConversationBulkRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/bulk/delete";
};

export type BulkDeleteConversationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkDeleteConversationsError =
  BulkDeleteConversationsErrors[keyof BulkDeleteConversationsErrors];

export type BulkDeleteConversationsResponses = {
  /**
   * Successful Response
   */
  200: ConversationBulkResult;
};

export type BulkDeleteConversationsResponse =
  BulkDeleteConversationsResponses[keyof BulkDeleteConversationsResponses];

export type DeleteConversationData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}";
};

export type DeleteConversationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteConversationError =
  DeleteConversationErrors[keyof DeleteConversationErrors];

export type DeleteConversationResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteConversationResponse =
  DeleteConversationResponses[keyof DeleteConversationResponses];

export type GetConversationData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Paged
     *
     * Return only the newest page of history: the turns since the latest compaction, at most 30.
     */
    paged?: boolean;
    /**
     * Before
     *
     * Return the page of history ending just before this message. Implies paged.
     */
    before?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}";
};

export type GetConversationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConversationError =
  GetConversationErrors[keyof GetConversationErrors];

export type GetConversationResponses = {
  /**
   * Successful Response
   */
  200: ConversationWithMessages;
};

export type GetConversationResponse =
  GetConversationResponses[keyof GetConversationResponses];

export type UpdateConversationData = {
  body: UpdateConversation;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}";
};

export type UpdateConversationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateConversationError =
  UpdateConversationErrors[keyof UpdateConversationErrors];

export type UpdateConversationResponses = {
  /**
   * Successful Response
   */
  200: ConversationWithMessages;
};

export type UpdateConversationResponse =
  UpdateConversationResponses[keyof UpdateConversationResponses];

export type AddMessageData = {
  body: AddMessageRequest;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Message Id
     */
    message_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}";
};

export type AddMessageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddMessageError = AddMessageErrors[keyof AddMessageErrors];

export type AddMessageResponses = {
  /**
   * Successful Response
   */
  200: ConversationWithMessages;
};

export type AddMessageResponse = AddMessageResponses[keyof AddMessageResponses];

export type ListRatedMessagesData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/{conversation_id}/rated-messages";
};

export type ListRatedMessagesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListRatedMessagesError =
  ListRatedMessagesErrors[keyof ListRatedMessagesErrors];

export type ListRatedMessagesResponses = {
  /**
   * Response List Rated Messages
   *
   * Successful Response
   */
  200: Array<RatedMessage>;
};

export type ListRatedMessagesResponse =
  ListRatedMessagesResponses[keyof ListRatedMessagesResponses];

export type AddAdminMessageData = {
  body: AddMessageRequest;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Is Final
     */
    is_final?: boolean;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/adminmessage";
};

export type AddAdminMessageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddAdminMessageError =
  AddAdminMessageErrors[keyof AddAdminMessageErrors];

export type AddAdminMessageResponses = {
  /**
   * Successful Response
   */
  200: ConversationWithMessages;
};

export type AddAdminMessageResponse =
  AddAdminMessageResponses[keyof AddAdminMessageResponses];

export type RefreshMessageData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Message Id
     */
    message_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/messages/{message_id}/refresh";
};

export type RefreshMessageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RefreshMessageError =
  RefreshMessageErrors[keyof RefreshMessageErrors];

export type RefreshMessageResponses = {
  /**
   * Successful Response
   */
  200: ConversationWithMessages;
};

export type RefreshMessageResponse =
  RefreshMessageResponses[keyof RefreshMessageResponses];

export type GetMessageDebugInfoData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Message Id
     *
     * Message ID
     */
    message_id: string;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/messages/debug";
};

export type GetMessageDebugInfoErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMessageDebugInfoError =
  GetMessageDebugInfoErrors[keyof GetMessageDebugInfoErrors];

export type GetMessageDebugInfoResponses = {
  /**
   * Successful Response
   */
  200: Message;
};

export type GetMessageDebugInfoResponse =
  GetMessageDebugInfoResponses[keyof GetMessageDebugInfoResponses];

export type GetMessageCostBreakdownData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Message Id
     *
     * Message ID
     */
    message_id: string;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/messages/cost";
};

export type GetMessageCostBreakdownErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetMessageCostBreakdownError =
  GetMessageCostBreakdownErrors[keyof GetMessageCostBreakdownErrors];

export type GetMessageCostBreakdownResponses = {
  /**
   * Successful Response
   */
  200: MessageCostBreakdown;
};

export type GetMessageCostBreakdownResponse =
  GetMessageCostBreakdownResponses[keyof GetMessageCostBreakdownResponses];

export type RemoveMessageFeedbackData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Message Id
     */
    message_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/messages/{message_id}/feedback";
};

export type RemoveMessageFeedbackErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RemoveMessageFeedbackError =
  RemoveMessageFeedbackErrors[keyof RemoveMessageFeedbackErrors];

export type RemoveMessageFeedbackResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type RemoveMessageFeedbackResponse =
  RemoveMessageFeedbackResponses[keyof RemoveMessageFeedbackResponses];

export type AddMessageFeedbackData = {
  body: MessageFeedback;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Message Id
     */
    message_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/messages/{message_id}/feedback";
};

export type AddMessageFeedbackErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddMessageFeedbackError =
  AddMessageFeedbackErrors[keyof AddMessageFeedbackErrors];

export type AddMessageFeedbackResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type AddMessageFeedbackResponse =
  AddMessageFeedbackResponses[keyof AddMessageFeedbackResponses];

export type AddCsatResponseData = {
  body: CsatFeedback;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/csat";
};

export type AddCsatResponseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddCsatResponseError =
  AddCsatResponseErrors[keyof AddCsatResponseErrors];

export type AddCsatResponseResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type AddCsatResponseResponse =
  AddCsatResponseResponses[keyof AddCsatResponseResponses];

export type DeferCsatResponseData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/csat/defer";
};

export type DeferCsatResponseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeferCsatResponseError =
  DeferCsatResponseErrors[keyof DeferCsatResponseErrors];

export type DeferCsatResponseResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeferCsatResponseResponse =
  DeferCsatResponseResponses[keyof DeferCsatResponseResponses];

export type TriggerCsatDebugData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/{conversation_id}/debug/trigger-csat";
};

export type TriggerCsatDebugErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TriggerCsatDebugError =
  TriggerCsatDebugErrors[keyof TriggerCsatDebugErrors];

export type TriggerCsatDebugResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type TriggerCsatDebugResponse =
  TriggerCsatDebugResponses[keyof TriggerCsatDebugResponses];

export type ConversationEventsData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Etag
     */
    etag?: string;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/events";
};

export type ConversationEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ConversationEventsError =
  ConversationEventsErrors[keyof ConversationEventsErrors];

export type ConversationEventsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type VercelEventsResumeData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/vercel-events";
};

export type VercelEventsResumeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type VercelEventsResumeError =
  VercelEventsResumeErrors[keyof VercelEventsResumeErrors];

export type VercelEventsResumeResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type VercelEventsData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/vercel-events";
};

export type VercelEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type VercelEventsError = VercelEventsErrors[keyof VercelEventsErrors];

export type VercelEventsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type StopConversationData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/stop";
};

export type StopConversationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StopConversationError =
  StopConversationErrors[keyof StopConversationErrors];

export type StopConversationResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetConversationExecutionLogsData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Force Source
     *
     * Force reading from a specific source: 'hot' (Redis) or 'cold' (S3)
     */
    force_source?: string | null;
  };
  url: "/groups/{group_id}/conversation/{conversation_id}/execution-logs";
};

export type GetConversationExecutionLogsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConversationExecutionLogsError =
  GetConversationExecutionLogsErrors[keyof GetConversationExecutionLogsErrors];

export type GetConversationExecutionLogsResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetConversationSpansData = {
  body?: never;
  path: {
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/conversation/{conversation_id}/spans";
};

export type GetConversationSpansErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConversationSpansError =
  GetConversationSpansErrors[keyof GetConversationSpansErrors];

export type GetConversationSpansResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ClientAuthorizeData = {
  body: AuthorizeRequest;
  path?: never;
  query?: never;
  url: "/clients/authorize";
};

export type ClientAuthorizeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ClientAuthorizeError =
  ClientAuthorizeErrors[keyof ClientAuthorizeErrors];

export type ClientAuthorizeResponses = {
  /**
   * Successful Response
   */
  200: AuthorizeResponse;
};

export type ClientAuthorizeResponse =
  ClientAuthorizeResponses[keyof ClientAuthorizeResponses];

export type ClientTokenData = {
  body: TokenRequest;
  path?: never;
  query?: never;
  url: "/clients/token";
};

export type ClientTokenErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ClientTokenError = ClientTokenErrors[keyof ClientTokenErrors];

export type ClientTokenResponses = {
  /**
   * Successful Response
   */
  200: SpotflowBackendApiClientsTokenResponse;
};

export type ClientTokenResponse =
  ClientTokenResponses[keyof ClientTokenResponses];

export type ListClientsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/clients";
};

export type ListClientsResponses = {
  /**
   * Response List Clients
   *
   * Successful Response
   */
  200: Array<ClientItem>;
};

export type ListClientsResponse =
  ListClientsResponses[keyof ListClientsResponses];

export type RevokeClientData = {
  body?: never;
  path: {
    /**
     * Client Id
     */
    client_id: string;
  };
  query?: never;
  url: "/clients/{client_id}/revoke";
};

export type RevokeClientErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RevokeClientError = RevokeClientErrors[keyof RevokeClientErrors];

export type RevokeClientResponses = {
  /**
   * Response Revoke Client
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type RevokeClientResponse =
  RevokeClientResponses[keyof RevokeClientResponses];

export type RevokeAllClientsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/clients/revoke-all";
};

export type RevokeAllClientsResponses = {
  /**
   * Response Revoke All Clients
   *
   * Successful Response
   */
  200: {
    [key: string]: boolean;
  };
};

export type RevokeAllClientsResponse =
  RevokeAllClientsResponses[keyof RevokeAllClientsResponses];

export type AuthorizeEmbedData = {
  body: EmbedAuthorizeRequest;
  path?: never;
  query?: never;
  url: "/clients/embed-authorize";
};

export type AuthorizeEmbedErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AuthorizeEmbedError =
  AuthorizeEmbedErrors[keyof AuthorizeEmbedErrors];

export type AuthorizeEmbedResponses = {
  /**
   * Successful Response
   */
  200: SpotflowBackendApiClientsTokenResponse;
};

export type AuthorizeEmbedResponse =
  AuthorizeEmbedResponses[keyof AuthorizeEmbedResponses];

export type ExchangeEmbedSessionData = {
  body: ExchangeRequest;
  headers?: {
    /**
     * X-Embed-Token
     */
    "x-embed-token"?: string | null;
  };
  path?: never;
  query?: never;
  url: "/embed/session";
};

export type ExchangeEmbedSessionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExchangeEmbedSessionError =
  ExchangeEmbedSessionErrors[keyof ExchangeEmbedSessionErrors];

export type ExchangeEmbedSessionResponses = {
  /**
   * Successful Response
   */
  200: SessionResponse;
};

export type ExchangeEmbedSessionResponse =
  ExchangeEmbedSessionResponses[keyof ExchangeEmbedSessionResponses];

export type RefreshEmbedSessionData = {
  body: RefreshRequest;
  path?: never;
  query?: never;
  url: "/embed/session/refresh";
};

export type RefreshEmbedSessionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RefreshEmbedSessionError =
  RefreshEmbedSessionErrors[keyof RefreshEmbedSessionErrors];

export type RefreshEmbedSessionResponses = {
  /**
   * Successful Response
   */
  200: SessionResponse;
};

export type RefreshEmbedSessionResponse =
  RefreshEmbedSessionResponses[keyof RefreshEmbedSessionResponses];

export type RevokeEmbedSessionsData = {
  body: RevokeRequest;
  headers?: {
    /**
     * X-Embed-Token
     */
    "x-embed-token"?: string | null;
  };
  path?: never;
  query?: never;
  url: "/embed/session/revoke";
};

export type RevokeEmbedSessionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RevokeEmbedSessionsError =
  RevokeEmbedSessionsErrors[keyof RevokeEmbedSessionsErrors];

export type RevokeEmbedSessionsResponses = {
  /**
   * Successful Response
   */
  200: RevokeResponse;
};

export type RevokeEmbedSessionsResponse =
  RevokeEmbedSessionsResponses[keyof RevokeEmbedSessionsResponses];

export type GetAssistantsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Query
     */
    query?: string | null;
    /**
     * Tags
     */
    tags?: string | null;
    /**
     * Status
     */
    status?: string | null;
    /**
     * Ids
     */
    ids?: string | null;
    /**
     * Ignore Ids
     */
    ignore_ids?: string | null;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Include Genie
     */
    include_genie?: boolean;
    /**
     * Include Deleted
     */
    include_deleted?: boolean;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/assistants";
};

export type GetAssistantsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantsError = GetAssistantsErrors[keyof GetAssistantsErrors];

export type GetAssistantsResponses = {
  /**
   * Successful Response
   */
  200: PageAssistantWithTags;
};

export type GetAssistantsResponse =
  GetAssistantsResponses[keyof GetAssistantsResponses];

export type AddAssistantData = {
  body: CreateAssistant;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants";
};

export type AddAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AddAssistantError = AddAssistantErrors[keyof AddAssistantErrors];

export type AddAssistantResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type AddAssistantResponse =
  AddAssistantResponses[keyof AddAssistantResponses];

export type GetOrCreateGenieAssistantData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/genie/assistant";
};

export type GetOrCreateGenieAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetOrCreateGenieAssistantError =
  GetOrCreateGenieAssistantErrors[keyof GetOrCreateGenieAssistantErrors];

export type GetOrCreateGenieAssistantResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type GetOrCreateGenieAssistantResponse =
  GetOrCreateGenieAssistantResponses[keyof GetOrCreateGenieAssistantResponses];

export type DeleteAssistantData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}";
};

export type DeleteAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteAssistantError =
  DeleteAssistantErrors[keyof DeleteAssistantErrors];

export type DeleteAssistantResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type DeleteAssistantResponse =
  DeleteAssistantResponses[keyof DeleteAssistantResponses];

export type GetAssistantData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}";
};

export type GetAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantError = GetAssistantErrors[keyof GetAssistantErrors];

export type GetAssistantResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type GetAssistantResponse =
  GetAssistantResponses[keyof GetAssistantResponses];

export type UpdateAssistantData = {
  body: UpdateAssistant;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}";
};

export type UpdateAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateAssistantError =
  UpdateAssistantErrors[keyof UpdateAssistantErrors];

export type UpdateAssistantResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type UpdateAssistantResponse =
  UpdateAssistantResponses[keyof UpdateAssistantResponses];

export type DuplicateAssistantData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/duplicate";
};

export type DuplicateAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DuplicateAssistantError =
  DuplicateAssistantErrors[keyof DuplicateAssistantErrors];

export type DuplicateAssistantResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type DuplicateAssistantResponse =
  DuplicateAssistantResponses[keyof DuplicateAssistantResponses];

export type GetAssistantPublicData = {
  body?: never;
  path: {
    /**
     * Assistantid
     */
    assistantId: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Assistant Id
     */
    assistant_id?: string | null;
    /**
     * Token
     *
     * Public access token
     */
    token?: string | null;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
  };
  url: "/groups/{group_id}/assistants/{assistantId}/public";
};

export type GetAssistantPublicErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantPublicError =
  GetAssistantPublicErrors[keyof GetAssistantPublicErrors];

export type GetAssistantPublicResponses = {
  /**
   * Successful Response
   */
  200: PublicAssistant;
};

export type GetAssistantPublicResponse =
  GetAssistantPublicResponses[keyof GetAssistantPublicResponses];

export type ExportAssistantPreviewData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/export/preview";
};

export type ExportAssistantPreviewErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportAssistantPreviewError =
  ExportAssistantPreviewErrors[keyof ExportAssistantPreviewErrors];

export type ExportAssistantPreviewResponses = {
  /**
   * Successful Response
   */
  200: ExportPreviewResponse;
};

export type ExportAssistantPreviewResponse =
  ExportAssistantPreviewResponses[keyof ExportAssistantPreviewResponses];

export type ExportAssistantData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
    /**
     * Export wire format. `auto` (default) emits the legacy base64-encoded bundle for back-compat; pass `v4` for plaintext multi-doc YAML (`.nx`), or `v3` for base64 explicitly.
     */
    version?: ExportFormat;
    /**
     * Set Active On Import
     */
    set_active_on_import?: boolean;
    /**
     * Include Dependencies
     *
     * Bundle referenced sub-flows / KBs / files into the .nx.
     */
    include_dependencies?: boolean;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/export";
};

export type ExportAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExportAssistantError =
  ExportAssistantErrors[keyof ExportAssistantErrors];

export type ExportAssistantResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type RestoreAssistantData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/restore";
};

export type RestoreAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RestoreAssistantError =
  RestoreAssistantErrors[keyof RestoreAssistantErrors];

export type RestoreAssistantResponses = {
  /**
   * Successful Response
   */
  200: Status;
};

export type RestoreAssistantResponse =
  RestoreAssistantResponses[keyof RestoreAssistantResponses];

export type GetAssistantTriggerConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Trigger Type
     */
    trigger_type: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/trigger/{trigger_type}/config";
};

export type GetAssistantTriggerConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantTriggerConfigError =
  GetAssistantTriggerConfigErrors[keyof GetAssistantTriggerConfigErrors];

export type GetAssistantTriggerConfigResponses = {
  /**
   * Successful Response
   */
  200: TriggerDefinition;
};

export type GetAssistantTriggerConfigResponse =
  GetAssistantTriggerConfigResponses[keyof GetAssistantTriggerConfigResponses];

export type GetAssistantTriggerCustomizationData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Trigger Type
     */
    trigger_type: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/trigger/{trigger_type}/customization";
};

export type GetAssistantTriggerCustomizationErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantTriggerCustomizationError =
  GetAssistantTriggerCustomizationErrors[keyof GetAssistantTriggerCustomizationErrors];

export type GetAssistantTriggerCustomizationResponses = {
  /**
   * Response Get Assistant Trigger Customization
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAssistantTriggerCustomizationResponse =
  GetAssistantTriggerCustomizationResponses[keyof GetAssistantTriggerCustomizationResponses];

export type GetAssistantVersionsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/versions";
};

export type GetAssistantVersionsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantVersionsError =
  GetAssistantVersionsErrors[keyof GetAssistantVersionsErrors];

export type GetAssistantVersionsResponses = {
  /**
   * Successful Response
   */
  200: PageAssistantVersion;
};

export type GetAssistantVersionsResponse =
  GetAssistantVersionsResponses[keyof GetAssistantVersionsResponses];

export type CreateAssistantVersionData = {
  body: CreateAssistantVersionRequest;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/versions";
};

export type CreateAssistantVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateAssistantVersionError =
  CreateAssistantVersionErrors[keyof CreateAssistantVersionErrors];

export type CreateAssistantVersionResponses = {
  /**
   * Successful Response
   */
  200: AssistantVersion;
};

export type CreateAssistantVersionResponse =
  CreateAssistantVersionResponses[keyof CreateAssistantVersionResponses];

export type GetAssistantVersionData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/versions/{version_id}";
};

export type GetAssistantVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantVersionError =
  GetAssistantVersionErrors[keyof GetAssistantVersionErrors];

export type GetAssistantVersionResponses = {
  /**
   * Successful Response
   */
  200: AssistantVersion;
};

export type GetAssistantVersionResponse =
  GetAssistantVersionResponses[keyof GetAssistantVersionResponses];

export type UpdateAssistantVersionData = {
  body: CreateAssistantVersionRequest;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/versions/{version_id}";
};

export type UpdateAssistantVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateAssistantVersionError =
  UpdateAssistantVersionErrors[keyof UpdateAssistantVersionErrors];

export type UpdateAssistantVersionResponses = {
  /**
   * Successful Response
   */
  200: AssistantVersion;
};

export type UpdateAssistantVersionResponse =
  UpdateAssistantVersionResponses[keyof UpdateAssistantVersionResponses];

export type GetIsValidAssistantData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/valid";
};

export type GetIsValidAssistantErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetIsValidAssistantError =
  GetIsValidAssistantErrors[keyof GetIsValidAssistantErrors];

export type GetIsValidAssistantResponses = {
  /**
   * Successful Response
   */
  200: IsValidResponse;
};

export type GetIsValidAssistantResponse =
  GetIsValidAssistantResponses[keyof GetIsValidAssistantResponses];

export type GetAssistantVersionUsagesData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/versions/{version_id}/usages";
};

export type GetAssistantVersionUsagesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantVersionUsagesError =
  GetAssistantVersionUsagesErrors[keyof GetAssistantVersionUsagesErrors];

export type GetAssistantVersionUsagesResponses = {
  /**
   * Successful Response
   */
  200: AssistantVersionUsages;
};

export type GetAssistantVersionUsagesResponse =
  GetAssistantVersionUsagesResponses[keyof GetAssistantVersionUsagesResponses];

export type RestoreAssistantVersionData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Version Id
     */
    version_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/versions/{version_id}/restore";
};

export type RestoreAssistantVersionErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RestoreAssistantVersionError =
  RestoreAssistantVersionErrors[keyof RestoreAssistantVersionErrors];

export type RestoreAssistantVersionResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type RestoreAssistantVersionResponse =
  RestoreAssistantVersionResponses[keyof RestoreAssistantVersionResponses];

export type GetAssistantCapabilityUpdatesData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/capability-updates";
};

export type GetAssistantCapabilityUpdatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantCapabilityUpdatesError =
  GetAssistantCapabilityUpdatesErrors[keyof GetAssistantCapabilityUpdatesErrors];

export type GetAssistantCapabilityUpdatesResponses = {
  /**
   * Response Get Assistant Capability Updates
   *
   * Successful Response
   */
  200: Array<CapabilityStatus>;
};

export type GetAssistantCapabilityUpdatesResponse =
  GetAssistantCapabilityUpdatesResponses[keyof GetAssistantCapabilityUpdatesResponses];

export type ApplyAssistantCapabilityUpdatesData = {
  /**
   * Req
   */
  body: Array<CapabilitySelection>;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/capability-updates";
};

export type ApplyAssistantCapabilityUpdatesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ApplyAssistantCapabilityUpdatesError =
  ApplyAssistantCapabilityUpdatesErrors[keyof ApplyAssistantCapabilityUpdatesErrors];

export type ApplyAssistantCapabilityUpdatesResponses = {
  /**
   * Successful Response
   */
  200: Assistant;
};

export type ApplyAssistantCapabilityUpdatesResponse =
  ApplyAssistantCapabilityUpdatesResponses[keyof ApplyAssistantCapabilityUpdatesResponses];

export type PreviewNewAssistantChangesData = {
  body: AgentChangesRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/changes";
};

export type PreviewNewAssistantChangesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PreviewNewAssistantChangesError =
  PreviewNewAssistantChangesErrors[keyof PreviewNewAssistantChangesErrors];

export type PreviewNewAssistantChangesResponses = {
  /**
   * Successful Response
   */
  200: AgentChanges;
};

export type PreviewNewAssistantChangesResponse =
  PreviewNewAssistantChangesResponses[keyof PreviewNewAssistantChangesResponses];

export type PreviewAssistantChangesData = {
  body: AgentChangesRequest;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/changes";
};

export type PreviewAssistantChangesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PreviewAssistantChangesError =
  PreviewAssistantChangesErrors[keyof PreviewAssistantChangesErrors];

export type PreviewAssistantChangesResponses = {
  /**
   * Successful Response
   */
  200: AgentChanges;
};

export type PreviewAssistantChangesResponse =
  PreviewAssistantChangesResponses[keyof PreviewAssistantChangesResponses];

export type GetAssistantCapabilitiesData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/capabilities";
};

export type GetAssistantCapabilitiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantCapabilitiesError =
  GetAssistantCapabilitiesErrors[keyof GetAssistantCapabilitiesErrors];

export type GetAssistantCapabilitiesResponses = {
  /**
   * Response Get Assistant Capabilities
   *
   * Successful Response
   */
  200: Array<CapabilityStatus>;
};

export type GetAssistantCapabilitiesResponse =
  GetAssistantCapabilitiesResponses[keyof GetAssistantCapabilitiesResponses];

export type ListAssistantToolsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Version Id
     */
    version_id?: string | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/tools";
};

export type ListAssistantToolsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListAssistantToolsError =
  ListAssistantToolsErrors[keyof ListAssistantToolsErrors];

export type ListAssistantToolsResponses = {
  /**
   * Successful Response
   */
  200: AssistantTools;
};

export type ListAssistantToolsResponse =
  ListAssistantToolsResponses[keyof ListAssistantToolsResponses];

export type ListChannelsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/channels";
};

export type ListChannelsResponses = {
  /**
   * Response List Channels
   *
   * Successful Response
   */
  200: Array<ChannelMetadata>;
};

export type ListChannelsResponse =
  ListChannelsResponses[keyof ListChannelsResponses];

export type ListDeploymentsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments";
};

export type ListDeploymentsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListDeploymentsError =
  ListDeploymentsErrors[keyof ListDeploymentsErrors];

export type ListDeploymentsResponses = {
  /**
   * Response List Deployments
   *
   * Successful Response
   */
  200: Array<DeploymentResponse>;
};

export type ListDeploymentsResponse =
  ListDeploymentsResponses[keyof ListDeploymentsResponses];

export type CreateDeploymentData = {
  body: DeploymentCreate;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments";
};

export type CreateDeploymentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateDeploymentError =
  CreateDeploymentErrors[keyof CreateDeploymentErrors];

export type CreateDeploymentResponses = {
  /**
   * Successful Response
   */
  201: DeploymentResponse;
};

export type CreateDeploymentResponse =
  CreateDeploymentResponses[keyof CreateDeploymentResponses];

export type DeleteDeploymentData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}";
};

export type DeleteDeploymentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteDeploymentError =
  DeleteDeploymentErrors[keyof DeleteDeploymentErrors];

export type DeleteDeploymentResponses = {
  /**
   * Retirement started; the deployment disappears once the Teams app and its Azure resources are removed.
   */
  202: unknown;
  /**
   * Successful Response
   */
  204: void;
};

export type DeleteDeploymentResponse =
  DeleteDeploymentResponses[keyof DeleteDeploymentResponses];

export type GetDeploymentData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}";
};

export type GetDeploymentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDeploymentError = GetDeploymentErrors[keyof GetDeploymentErrors];

export type GetDeploymentResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type GetDeploymentResponse =
  GetDeploymentResponses[keyof GetDeploymentResponses];

export type UpdateDeploymentData = {
  body: DeploymentUpdate;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}";
};

export type UpdateDeploymentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateDeploymentError =
  UpdateDeploymentErrors[keyof UpdateDeploymentErrors];

export type UpdateDeploymentResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type UpdateDeploymentResponse =
  UpdateDeploymentResponses[keyof UpdateDeploymentResponses];

export type ActivateDeploymentData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/activate";
};

export type ActivateDeploymentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ActivateDeploymentError =
  ActivateDeploymentErrors[keyof ActivateDeploymentErrors];

export type ActivateDeploymentResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type ActivateDeploymentResponse =
  ActivateDeploymentResponses[keyof ActivateDeploymentResponses];

export type DeactivateDeploymentData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/deactivate";
};

export type DeactivateDeploymentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeactivateDeploymentError =
  DeactivateDeploymentErrors[keyof DeactivateDeploymentErrors];

export type DeactivateDeploymentResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type DeactivateDeploymentResponse =
  DeactivateDeploymentResponses[keyof DeactivateDeploymentResponses];

export type PauseTeamsAppData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/teams-app/pause";
};

export type PauseTeamsAppErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type PauseTeamsAppError = PauseTeamsAppErrors[keyof PauseTeamsAppErrors];

export type PauseTeamsAppResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type PauseTeamsAppResponse =
  PauseTeamsAppResponses[keyof PauseTeamsAppResponses];

export type ResumeTeamsAppData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/teams-app/resume";
};

export type ResumeTeamsAppErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResumeTeamsAppError =
  ResumeTeamsAppErrors[keyof ResumeTeamsAppErrors];

export type ResumeTeamsAppResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type ResumeTeamsAppResponse =
  ResumeTeamsAppResponses[keyof ResumeTeamsAppResponses];

export type RetryTeamsAppData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/teams-app/retry";
};

export type RetryTeamsAppErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type RetryTeamsAppError = RetryTeamsAppErrors[keyof RetryTeamsAppErrors];

export type RetryTeamsAppResponses = {
  /**
   * Successful Response
   */
  200: DeploymentResponse;
};

export type RetryTeamsAppResponse =
  RetryTeamsAppResponses[keyof RetryTeamsAppResponses];

export type GetDeploymentConfigData = {
  /**
   * Config
   */
  body: {
    [key: string]: unknown;
  };
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/config";
};

export type GetDeploymentConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDeploymentConfigError =
  GetDeploymentConfigErrors[keyof GetDeploymentConfigErrors];

export type GetDeploymentConfigResponses = {
  /**
   * Response Get Deployment Config
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetDeploymentConfigResponse =
  GetDeploymentConfigResponses[keyof GetDeploymentConfigResponses];

export type GetDeploymentEventsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Deployment Id
     */
    deployment_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/deployments/{deployment_id}/events";
};

export type GetDeploymentEventsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDeploymentEventsError =
  GetDeploymentEventsErrors[keyof GetDeploymentEventsErrors];

export type GetDeploymentEventsResponses = {
  /**
   * Successful Response
   */
  200: PageDeploymentEvent;
};

export type GetDeploymentEventsResponse =
  GetDeploymentEventsResponses[keyof GetDeploymentEventsResponses];

export type GetDeploymentAppByAliasData = {
  body?: never;
  path: {
    /**
     * Alias
     */
    alias: string;
  };
  query?: never;
  url: "/deployments/by-alias/{alias}/app";
};

export type GetDeploymentAppByAliasErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDeploymentAppByAliasError =
  GetDeploymentAppByAliasErrors[keyof GetDeploymentAppByAliasErrors];

export type GetDeploymentAppByAliasResponses = {
  /**
   * Response Get Deployment App By Alias
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetDeploymentAppByAliasResponse =
  GetDeploymentAppByAliasResponses[keyof GetDeploymentAppByAliasResponses];

export type GetDeploymentAppData = {
  body?: never;
  path: {
    /**
     * Deployment Id
     */
    deployment_id: string;
  };
  query?: never;
  url: "/deployments/{deployment_id}/app";
};

export type GetDeploymentAppErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetDeploymentAppError =
  GetDeploymentAppErrors[keyof GetDeploymentAppErrors];

export type GetDeploymentAppResponses = {
  /**
   * Response Get Deployment App
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetDeploymentAppResponse =
  GetDeploymentAppResponses[keyof GetDeploymentAppResponses];

export type ListPeriodicTasksData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/periodic-tasks";
};

export type ListPeriodicTasksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPeriodicTasksError =
  ListPeriodicTasksErrors[keyof ListPeriodicTasksErrors];

export type ListPeriodicTasksResponses = {
  /**
   * Response List Periodic Tasks
   *
   * Successful Response
   */
  200: Array<PeriodicTaskResponse>;
};

export type ListPeriodicTasksResponse =
  ListPeriodicTasksResponses[keyof ListPeriodicTasksResponses];

export type CreatePeriodicTaskData = {
  body: CreatePeriodicTaskRequest;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/periodic-tasks";
};

export type CreatePeriodicTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePeriodicTaskError =
  CreatePeriodicTaskErrors[keyof CreatePeriodicTaskErrors];

export type CreatePeriodicTaskResponses = {
  /**
   * Successful Response
   */
  200: PeriodicTaskResponse;
};

export type CreatePeriodicTaskResponse =
  CreatePeriodicTaskResponses[keyof CreatePeriodicTaskResponses];

export type DeletePeriodicTaskData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/periodic-tasks/{task_id}";
};

export type DeletePeriodicTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePeriodicTaskError =
  DeletePeriodicTaskErrors[keyof DeletePeriodicTaskErrors];

export type DeletePeriodicTaskResponses = {
  /**
   * Successful Response
   */
  200: PeriodicTaskResponse;
};

export type DeletePeriodicTaskResponse =
  DeletePeriodicTaskResponses[keyof DeletePeriodicTaskResponses];

export type UpdatePeriodicTaskData = {
  body: UpdatePeriodicTaskRequest;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Task Id
     */
    task_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/periodic-tasks/{task_id}";
};

export type UpdatePeriodicTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePeriodicTaskError =
  UpdatePeriodicTaskErrors[keyof UpdatePeriodicTaskErrors];

export type UpdatePeriodicTaskResponses = {
  /**
   * Successful Response
   */
  200: PeriodicTaskResponse;
};

export type UpdatePeriodicTaskResponse =
  UpdatePeriodicTaskResponses[keyof UpdatePeriodicTaskResponses];

export type GetWorkflowAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/workflows/metadata";
};

export type GetWorkflowAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetWorkflowAnalyticsItemsResponse =
  GetWorkflowAnalyticsItemsResponses[keyof GetWorkflowAnalyticsItemsResponses];

export type GetApiAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/api/metadata";
};

export type GetApiAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetApiAnalyticsItemsResponse =
  GetApiAnalyticsItemsResponses[keyof GetApiAnalyticsItemsResponses];

export type GetChatAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/chat/metadata";
};

export type GetChatAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetChatAnalyticsItemsResponse =
  GetChatAnalyticsItemsResponses[keyof GetChatAnalyticsItemsResponses];

export type GetWorkspaceAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/workspace/metadata";
};

export type GetWorkspaceAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetWorkspaceAnalyticsItemsResponse =
  GetWorkspaceAnalyticsItemsResponses[keyof GetWorkspaceAnalyticsItemsResponses];

export type GetBillingAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/billing/metadata";
};

export type GetBillingAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetBillingAnalyticsItemsResponse =
  GetBillingAnalyticsItemsResponses[keyof GetBillingAnalyticsItemsResponses];

export type GetKbAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/kb/metadata";
};

export type GetKbAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetKbAnalyticsItemsResponse =
  GetKbAnalyticsItemsResponses[keyof GetKbAnalyticsItemsResponses];

export type GetTenantAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/tenant/metadata";
};

export type GetTenantAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetTenantAnalyticsItemsResponse =
  GetTenantAnalyticsItemsResponses[keyof GetTenantAnalyticsItemsResponses];

export type GetControlCenterAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/control-center/metadata";
};

export type GetControlCenterAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetControlCenterAnalyticsItemsResponse =
  GetControlCenterAnalyticsItemsResponses[keyof GetControlCenterAnalyticsItemsResponses];

export type GetWorkflowAnalyticsData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    analytics_key: AnalyticsKey;
  };
  query: {
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
    /**
     * Filter Key
     */
    filter_key?: string | null;
    /**
     * Page
     */
    page?: number | null;
    /**
     * Page Size
     */
    page_size?: number | null;
  };
  url: "/analytics/workflows/{workflow_id}/{analytics_key}";
};

export type GetWorkflowAnalyticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetWorkflowAnalyticsError =
  GetWorkflowAnalyticsErrors[keyof GetWorkflowAnalyticsErrors];

export type GetWorkflowAnalyticsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsResponse;
};

export type GetWorkflowAnalyticsResponse =
  GetWorkflowAnalyticsResponses[keyof GetWorkflowAnalyticsResponses];

export type GetAssistantAnalyticsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    analytics_key: AnalyticsKey;
  };
  query: {
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
    /**
     * Filter Key
     */
    filter_key?: string | null;
    /**
     * Page
     */
    page?: number | null;
    /**
     * Page Size
     */
    page_size?: number | null;
  };
  url: "/analytics/assistants/{assistant_id}/{analytics_key}";
};

export type GetAssistantAnalyticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAssistantAnalyticsError =
  GetAssistantAnalyticsErrors[keyof GetAssistantAnalyticsErrors];

export type GetAssistantAnalyticsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsResponse;
};

export type GetAssistantAnalyticsResponse =
  GetAssistantAnalyticsResponses[keyof GetAssistantAnalyticsResponses];

export type GetTenantAnalyticsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string | "all";
    analytics_key: AnalyticsKey;
  };
  query: {
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
    /**
     * Filter Key
     */
    filter_key?: string | null;
    /**
     * Page
     */
    page?: number | null;
    /**
     * Page Size
     */
    page_size?: number | null;
  };
  url: "/analytics/groups/{group_id}/{analytics_key}";
};

export type GetTenantAnalyticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTenantAnalyticsError =
  GetTenantAnalyticsErrors[keyof GetTenantAnalyticsErrors];

export type GetTenantAnalyticsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsResponse;
};

export type GetTenantAnalyticsResponse =
  GetTenantAnalyticsResponses[keyof GetTenantAnalyticsResponses];

export type GetApiAnalyticsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string | "all";
    analytics_key: AnalyticsKey;
  };
  query: {
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
    /**
     * Filter Key
     */
    filter_key?: string | null;
    /**
     * Page
     */
    page?: number | null;
    /**
     * Page Size
     */
    page_size?: number | null;
  };
  url: "/analytics/api/{group_id}/{analytics_key}";
};

export type GetApiAnalyticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetApiAnalyticsError =
  GetApiAnalyticsErrors[keyof GetApiAnalyticsErrors];

export type GetApiAnalyticsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsResponse;
};

export type GetApiAnalyticsResponse =
  GetApiAnalyticsResponses[keyof GetApiAnalyticsResponses];

export type GetKbAnalyticsData = {
  body?: never;
  path: {
    /**
     * Kb Id
     */
    kb_id: string;
    analytics_key: AnalyticsKey;
  };
  query: {
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
    /**
     * Filter Key
     */
    filter_key?: string | null;
    /**
     * Page
     */
    page?: number | null;
    /**
     * Page Size
     */
    page_size?: number | null;
  };
  url: "/analytics/kb/{kb_id}/{analytics_key}";
};

export type GetKbAnalyticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKbAnalyticsError =
  GetKbAnalyticsErrors[keyof GetKbAnalyticsErrors];

export type GetKbAnalyticsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsResponse;
};

export type GetKbAnalyticsResponse =
  GetKbAnalyticsResponses[keyof GetKbAnalyticsResponses];

export type GetInfrastructureAnalyticsItemsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/analytics/infrastructure/metadata";
};

export type GetInfrastructureAnalyticsItemsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsMetadataItems;
};

export type GetInfrastructureAnalyticsItemsResponse =
  GetInfrastructureAnalyticsItemsResponses[keyof GetInfrastructureAnalyticsItemsResponses];

export type GetInfrastructureAnalyticsData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Group Id
     */
    group_id: string | "all";
    analytics_key: AnalyticsKey;
    /**
     * Time Start
     */
    time_start: string;
    /**
     * Time End
     */
    time_end: string;
    /**
     * Filter Key
     */
    filter_key?: string | null;
    /**
     * Page
     */
    page?: number | null;
    /**
     * Page Size
     */
    page_size?: number | null;
  };
  url: "/analytics/infrastructure";
};

export type GetInfrastructureAnalyticsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInfrastructureAnalyticsError =
  GetInfrastructureAnalyticsErrors[keyof GetInfrastructureAnalyticsErrors];

export type GetInfrastructureAnalyticsResponses = {
  /**
   * Successful Response
   */
  200: AnalyticsResponse;
};

export type GetInfrastructureAnalyticsResponse =
  GetInfrastructureAnalyticsResponses[keyof GetInfrastructureAnalyticsResponses];

export type GetSentimentOverTimeData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/sentiment-over-time";
};

export type GetSentimentOverTimeErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSentimentOverTimeError =
  GetSentimentOverTimeErrors[keyof GetSentimentOverTimeErrors];

export type GetSentimentOverTimeResponses = {
  /**
   * Successful Response
   */
  200: SentimentOverTime;
};

export type GetSentimentOverTimeResponse =
  GetSentimentOverTimeResponses[keyof GetSentimentOverTimeResponses];

export type GetRatingDriversData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/rating-drivers";
};

export type GetRatingDriversErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetRatingDriversError =
  GetRatingDriversErrors[keyof GetRatingDriversErrors];

export type GetRatingDriversResponses = {
  /**
   * Successful Response
   */
  200: RatingDrivers;
};

export type GetRatingDriversResponse =
  GetRatingDriversResponses[keyof GetRatingDriversResponses];

export type GetCustomInsightsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/custom-insights";
};

export type GetCustomInsightsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCustomInsightsError =
  GetCustomInsightsErrors[keyof GetCustomInsightsErrors];

export type GetCustomInsightsResponses = {
  /**
   * Successful Response
   */
  200: CustomInsightCounts;
};

export type GetCustomInsightsResponse =
  GetCustomInsightsResponses[keyof GetCustomInsightsResponses];

export type GetInsightConversationsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Kind
     */
    kind: "topic" | "subtopic" | "driver" | "custom" | "cx";
    /**
     * Key
     */
    key: string;
    /**
     * Days
     */
    days?: number;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/conversations";
};

export type GetInsightConversationsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetInsightConversationsError =
  GetInsightConversationsErrors[keyof GetInsightConversationsErrors];

export type GetInsightConversationsResponses = {
  /**
   * Successful Response
   */
  200: InsightConversations;
};

export type GetInsightConversationsResponse =
  GetInsightConversationsResponses[keyof GetInsightConversationsResponses];

export type GetSubTopicsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Parent
     */
    parent?: string | null;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/sub-topics";
};

export type GetSubTopicsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSubTopicsError = GetSubTopicsErrors[keyof GetSubTopicsErrors];

export type GetSubTopicsResponses = {
  /**
   * Successful Response
   */
  200: SubTopics;
};

export type GetSubTopicsResponse =
  GetSubTopicsResponses[keyof GetSubTopicsResponses];

export type GetNoticedData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Limit
     */
    limit?: number;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/noticed";
};

export type GetNoticedErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetNoticedError = GetNoticedErrors[keyof GetNoticedErrors];

export type GetNoticedResponses = {
  /**
   * Successful Response
   */
  200: NoticedFeed;
};

export type GetNoticedResponse = GetNoticedResponses[keyof GetNoticedResponses];

export type GetTopTopicsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Limit
     */
    limit?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/top-topics";
};

export type GetTopTopicsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTopTopicsError = GetTopTopicsErrors[keyof GetTopTopicsErrors];

export type GetTopTopicsResponses = {
  /**
   * Successful Response
   */
  200: TopTopics;
};

export type GetTopTopicsResponse =
  GetTopTopicsResponses[keyof GetTopTopicsResponses];

export type GetConversationFunnelData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/conversation-funnel";
};

export type GetConversationFunnelErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetConversationFunnelError =
  GetConversationFunnelErrors[keyof GetConversationFunnelErrors];

export type GetConversationFunnelResponses = {
  /**
   * Successful Response
   */
  200: ConversationFunnel;
};

export type GetConversationFunnelResponse =
  GetConversationFunnelResponses[keyof GetConversationFunnelResponses];

export type GetCsatScoreData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Days
     */
    days?: number;
    /**
     * Deployment Id
     */
    deployment_id?: string | null;
    /**
     * Message Length
     */
    message_length?: "short" | "medium" | "long" | null;
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/csat-score";
};

export type GetCsatScoreErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCsatScoreError = GetCsatScoreErrors[keyof GetCsatScoreErrors];

export type GetCsatScoreResponses = {
  /**
   * Successful Response
   */
  200: CsatScore;
};

export type GetCsatScoreResponse =
  GetCsatScoreResponses[keyof GetCsatScoreResponses];

export type GetBootstrapStatusData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/bootstrap";
};

export type GetBootstrapStatusErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetBootstrapStatusError =
  GetBootstrapStatusErrors[keyof GetBootstrapStatusErrors];

export type GetBootstrapStatusResponses = {
  /**
   * Successful Response
   */
  200: BootstrapStatus;
};

export type GetBootstrapStatusResponse =
  GetBootstrapStatusResponses[keyof GetBootstrapStatusResponses];

export type TriggerInsightsBootstrapData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/bootstrap";
};

export type TriggerInsightsBootstrapErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TriggerInsightsBootstrapError =
  TriggerInsightsBootstrapErrors[keyof TriggerInsightsBootstrapErrors];

export type TriggerInsightsBootstrapResponses = {
  /**
   * Successful Response
   */
  200: InsightsBootstrapResponse;
};

export type TriggerInsightsBootstrapResponse =
  TriggerInsightsBootstrapResponses[keyof TriggerInsightsBootstrapResponses];

export type GetCalculatingInsightsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/calculating";
};

export type GetCalculatingInsightsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCalculatingInsightsError =
  GetCalculatingInsightsErrors[keyof GetCalculatingInsightsErrors];

export type GetCalculatingInsightsResponses = {
  /**
   * Successful Response
   */
  200: InsightsCalculating;
};

export type GetCalculatingInsightsResponse =
  GetCalculatingInsightsResponses[keyof GetCalculatingInsightsResponses];

export type DebugRecomputeInsightsData = {
  body?: never;
  path: {
    /**
     * Assistant Id
     */
    assistant_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Target
     */
    target?:
      | "sentiment"
      | "funnel"
      | "topics"
      | "drivers"
      | "noticed"
      | "custom";
  };
  url: "/groups/{group_id}/assistants/{assistant_id}/insights/debug/recompute";
};

export type DebugRecomputeInsightsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DebugRecomputeInsightsError =
  DebugRecomputeInsightsErrors[keyof DebugRecomputeInsightsErrors];

export type DebugRecomputeInsightsResponses = {
  /**
   * Successful Response
   */
  200: DebugRecomputeResponse;
};

export type DebugRecomputeInsightsResponse =
  DebugRecomputeInsightsResponses[keyof DebugRecomputeInsightsResponses];

export type GetLabellingTasksData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Status
     */
    status?: LabellingStatus | null;
    /**
     * Order By
     */
    order_by?: "created_at" | "priority";
    /**
     * Order Direction
     */
    order_direction?: "asc" | "desc";
    /**
     * Assignee Ids
     */
    assignee_ids?: Array<string> | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/labelling_tasks";
};

export type GetLabellingTasksErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetLabellingTasksError =
  GetLabellingTasksErrors[keyof GetLabellingTasksErrors];

export type GetLabellingTasksResponses = {
  /**
   * Successful Response
   */
  200: PageLabellingTask;
};

export type GetLabellingTasksResponse =
  GetLabellingTasksResponses[keyof GetLabellingTasksResponses];

export type UpdateLabellingTaskData = {
  body: UpdateLabellingTask;
  path: {
    /**
     * Labelling Task Id
     */
    labelling_task_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/labelling_tasks/{labelling_task_id}";
};

export type UpdateLabellingTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateLabellingTaskError =
  UpdateLabellingTaskErrors[keyof UpdateLabellingTaskErrors];

export type UpdateLabellingTaskResponses = {
  /**
   * Response Update Labelling Task
   *
   * Successful Response
   */
  200: boolean;
};

export type UpdateLabellingTaskResponse =
  UpdateLabellingTaskResponses[keyof UpdateLabellingTaskResponses];

export type AssignLabellingTaskData = {
  body: AssignLabellingTaskRequest;
  path: {
    /**
     * Labelling Task Id
     */
    labelling_task_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/labelling_tasks/{labelling_task_id}/assign";
};

export type AssignLabellingTaskErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type AssignLabellingTaskError =
  AssignLabellingTaskErrors[keyof AssignLabellingTaskErrors];

export type AssignLabellingTaskResponses = {
  /**
   * Response Assign Labelling Task
   *
   * Successful Response
   */
  200: boolean;
};

export type AssignLabellingTaskResponse =
  AssignLabellingTaskResponses[keyof AssignLabellingTaskResponses];

export type GetPiiSafetyDataData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Config Type
     */
    config_type?: "tenant" | "workspace" | "coworker" | null;
  };
  url: "/safety/pii";
};

export type GetPiiSafetyDataErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPiiSafetyDataError =
  GetPiiSafetyDataErrors[keyof GetPiiSafetyDataErrors];

export type GetPiiSafetyDataResponses = {
  /**
   * Response Get Pii Safety Data
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetPiiSafetyDataResponse =
  GetPiiSafetyDataResponses[keyof GetPiiSafetyDataResponses];

export type GetAiSafetyDataData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/safety/ai";
};

export type GetAiSafetyDataResponses = {
  /**
   * Response Get Ai Safety Data
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAiSafetyDataResponse =
  GetAiSafetyDataResponses[keyof GetAiSafetyDataResponses];

export type GetCoworkerFormData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Pins
     */
    pins?: string;
  };
  url: "/safety/coworker";
};

export type GetCoworkerFormErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCoworkerFormError =
  GetCoworkerFormErrors[keyof GetCoworkerFormErrors];

export type GetCoworkerFormResponses = {
  /**
   * Response Get Coworker Form
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetCoworkerFormResponse =
  GetCoworkerFormResponses[keyof GetCoworkerFormResponses];

export type GetAgentFormData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/safety/agent";
};

export type GetAgentFormResponses = {
  /**
   * Response Get Agent Form
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAgentFormResponse =
  GetAgentFormResponses[keyof GetAgentFormResponses];

export type GetAgentToolFormData = {
  body?: never;
  path: {
    /**
     * Tool Type
     */
    tool_type: string;
  };
  query?: never;
  url: "/safety/agent/tool/{tool_type}";
};

export type GetAgentToolFormErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAgentToolFormError =
  GetAgentToolFormErrors[keyof GetAgentToolFormErrors];

export type GetAgentToolFormResponses = {
  /**
   * Response Get Agent Tool Form
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAgentToolFormResponse =
  GetAgentToolFormResponses[keyof GetAgentToolFormResponses];

export type GetDisabledAgentToolsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/safety/agent/disabled-tools";
};

export type GetDisabledAgentToolsResponses = {
  /**
   * Successful Response
   */
  200: DisabledAgentToolsResponse;
};

export type GetDisabledAgentToolsResponse =
  GetDisabledAgentToolsResponses[keyof GetDisabledAgentToolsResponses];

export type GetAgentCoworkerToolFormData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/safety/agent/coworker-tool";
};

export type GetAgentCoworkerToolFormResponses = {
  /**
   * Response Get Agent Coworker Tool Form
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAgentCoworkerToolFormResponse =
  GetAgentCoworkerToolFormResponses[keyof GetAgentCoworkerToolFormResponses];

export type GetKbFormData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/safety/kb";
};

export type GetKbFormResponses = {
  /**
   * Response Get Kb Form
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetKbFormResponse = GetKbFormResponses[keyof GetKbFormResponses];

export type GetFeatureFlagsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/tenant/feature-flags";
};

export type GetFeatureFlagsResponses = {
  /**
   * Successful Response
   */
  200: FeatureFlags;
};

export type GetFeatureFlagsResponse =
  GetFeatureFlagsResponses[keyof GetFeatureFlagsResponses];

export type GetAllFeatureFlagsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/feature-flags";
};

export type GetAllFeatureFlagsResponses = {
  /**
   * Response Get All Feature Flags
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAllFeatureFlagsResponse =
  GetAllFeatureFlagsResponses[keyof GetAllFeatureFlagsResponses];

export type GetEnabledBetaFeaturesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/beta-features";
};

export type GetEnabledBetaFeaturesResponses = {
  /**
   * Response Get Enabled Beta Features
   *
   * Successful Response
   */
  200: Array<"genie" | "agent_tests">;
};

export type GetEnabledBetaFeaturesResponse =
  GetEnabledBetaFeaturesResponses[keyof GetEnabledBetaFeaturesResponses];

export type GetBetaFeatureStatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/admin/beta-features";
};

export type GetBetaFeatureStatesResponses = {
  /**
   * Response Get Beta Feature States
   *
   * Successful Response
   */
  200: Array<BetaFeatureState>;
};

export type GetBetaFeatureStatesResponse =
  GetBetaFeatureStatesResponses[keyof GetBetaFeatureStatesResponses];

export type SetBetaFeatureData = {
  body: BetaFeatureUpdate;
  path: {
    /**
     * Feature
     */
    feature: "genie" | "agent_tests";
  };
  query?: never;
  url: "/admin/beta-features/{feature}";
};

export type SetBetaFeatureErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetBetaFeatureError =
  SetBetaFeatureErrors[keyof SetBetaFeatureErrors];

export type SetBetaFeatureResponses = {
  /**
   * Response Set Beta Feature
   *
   * Successful Response
   */
  200: Array<BetaFeatureState>;
};

export type SetBetaFeatureResponse =
  SetBetaFeatureResponses[keyof SetBetaFeatureResponses];

export type GetAppConfigData = {
  body?: never;
  path: {
    /**
     * App Type
     */
    app_type: string;
  };
  query?: never;
  url: "/apps/config/{app_type}";
};

export type GetAppConfigErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAppConfigError = GetAppConfigErrors[keyof GetAppConfigErrors];

export type GetAppConfigResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ListAppsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Name
     */
    name?: string;
    /**
     * Order By
     */
    order_by?: string;
    /**
     * Sort
     */
    sort?: string;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/apps";
};

export type ListAppsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListAppsError = ListAppsErrors[keyof ListAppsErrors];

export type ListAppsResponses = {
  /**
   * Successful Response
   */
  200: PageApp;
};

export type ListAppsResponse = ListAppsResponses[keyof ListAppsResponses];

export type ExtractDocumentData = {
  body: DocumentExtractionRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/document-extraction";
};

export type ExtractDocumentErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ExtractDocumentError =
  ExtractDocumentErrors[keyof ExtractDocumentErrors];

export type ExtractDocumentResponses = {
  /**
   * Successful Response
   */
  200: DocumentExtraction;
};

export type ExtractDocumentResponse =
  ExtractDocumentResponses[keyof ExtractDocumentResponses];

export type ListTagsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Search
     *
     * Search tags by name
     */
    search?: string | null;
  };
  url: "/groups/{group_id}/tags";
};

export type ListTagsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTagsError = ListTagsErrors[keyof ListTagsErrors];

export type ListTagsResponses = {
  /**
   * Response List Tags
   *
   * Successful Response
   */
  200: Array<TagResponse>;
};

export type ListTagsResponse = ListTagsResponses[keyof ListTagsResponses];

export type CreateTagData = {
  body: CreateTagRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tags";
};

export type CreateTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTagError = CreateTagErrors[keyof CreateTagErrors];

export type CreateTagResponses = {
  /**
   * Successful Response
   */
  200: TagResponse;
};

export type CreateTagResponse = CreateTagResponses[keyof CreateTagResponses];

export type DeleteTagData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Tag Id
     */
    tag_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tags/{tag_id}";
};

export type DeleteTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTagError = DeleteTagErrors[keyof DeleteTagErrors];

export type DeleteTagResponses = {
  /**
   * Response Delete Tag
   *
   * Successful Response
   */
  200: boolean;
};

export type DeleteTagResponse = DeleteTagResponses[keyof DeleteTagResponses];

export type GetTagData = {
  body?: never;
  path: {
    /**
     * Tag Id
     */
    tag_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tags/{tag_id}";
};

export type GetTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTagError = GetTagErrors[keyof GetTagErrors];

export type GetTagResponses = {
  /**
   * Successful Response
   */
  200: TagResponse;
};

export type GetTagResponse = GetTagResponses[keyof GetTagResponses];

export type UpdateTagData = {
  body: UpdateTagRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
    /**
     * Tag Id
     */
    tag_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tags/{tag_id}";
};

export type UpdateTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTagError = UpdateTagErrors[keyof UpdateTagErrors];

export type UpdateTagResponses = {
  /**
   * Successful Response
   */
  200: TagResponse;
};

export type UpdateTagResponse = UpdateTagResponses[keyof UpdateTagResponses];

export type GetEntitiesByTagData = {
  body?: never;
  path: {
    /**
     * Tag Id
     */
    tag_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Entity Type
     *
     * Filter by entity type
     */
    entity_type?: EntityType | null;
  };
  url: "/groups/{group_id}/tags/{tag_id}/entities";
};

export type GetEntitiesByTagErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEntitiesByTagError =
  GetEntitiesByTagErrors[keyof GetEntitiesByTagErrors];

export type GetEntitiesByTagResponses = {
  /**
   * Successful Response
   */
  200: TaggedEntitiesResponse;
};

export type GetEntitiesByTagResponse =
  GetEntitiesByTagResponses[keyof GetEntitiesByTagResponses];

export type TagEntityData = {
  body: TagEntityRequest;
  path: {
    /**
     * Tag Id
     */
    tag_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tags/{tag_id}/entities";
};

export type TagEntityErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type TagEntityError = TagEntityErrors[keyof TagEntityErrors];

export type TagEntityResponses = {
  /**
   * Successful Response
   */
  200: EntityTagAssociationResponse;
};

export type TagEntityResponse = TagEntityResponses[keyof TagEntityResponses];

export type UntagEntityData = {
  body?: never;
  path: {
    /**
     * Tag Id
     */
    tag_id: string;
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Type of entity
     */
    entity_type: EntityType;
  };
  url: "/groups/{group_id}/tags/{tag_id}/entities/{entity_id}";
};

export type UntagEntityErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UntagEntityError = UntagEntityErrors[keyof UntagEntityErrors];

export type UntagEntityResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetEntityTagsData = {
  body?: never;
  path: {
    /**
     * Entity Id
     */
    entity_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Type of entity
     */
    entity_type: EntityType;
  };
  url: "/groups/{group_id}/entities/{entity_id}/tags";
};

export type GetEntityTagsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEntityTagsError = GetEntityTagsErrors[keyof GetEntityTagsErrors];

export type GetEntityTagsResponses = {
  /**
   * Successful Response
   */
  200: EntityTagsResponse;
};

export type GetEntityTagsResponse =
  GetEntityTagsResponses[keyof GetEntityTagsResponses];

export type BulkTagEntitiesData = {
  body: BulkTagEntitiesRequest;
  path: {
    /**
     * Tag Id
     */
    tag_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/tags/{tag_id}/entities/bulk";
};

export type BulkTagEntitiesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type BulkTagEntitiesError =
  BulkTagEntitiesErrors[keyof BulkTagEntitiesErrors];

export type BulkTagEntitiesResponses = {
  /**
   * Response Bulk Tag Entities
   *
   * Successful Response
   */
  200: Array<EntityTagAssociationResponse>;
};

export type BulkTagEntitiesResponse =
  BulkTagEntitiesResponses[keyof BulkTagEntitiesResponses];

export type GetEvaluatorDefinitionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/evaluator-definitions";
};

export type GetEvaluatorDefinitionsResponses = {
  /**
   * Response Get Evaluator Definitions
   *
   * Successful Response
   */
  200: Array<EvaluatorDefinition>;
};

export type GetEvaluatorDefinitionsResponse =
  GetEvaluatorDefinitionsResponses[keyof GetEvaluatorDefinitionsResponses];

export type GetWorkflowEvaluatorDefinitionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/evaluator-definitions/workflow";
};

export type GetWorkflowEvaluatorDefinitionsResponses = {
  /**
   * Response Get Workflow Evaluator Definitions
   *
   * Successful Response
   */
  200: Array<EvaluatorDefinition>;
};

export type GetWorkflowEvaluatorDefinitionsResponse =
  GetWorkflowEvaluatorDefinitionsResponses[keyof GetWorkflowEvaluatorDefinitionsResponses];

export type GetAgentEvaluatorDefinitionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/evaluator-definitions/agent";
};

export type GetAgentEvaluatorDefinitionsResponses = {
  /**
   * Response Get Agent Evaluator Definitions
   *
   * Successful Response
   */
  200: Array<EvaluatorDefinition>;
};

export type GetAgentEvaluatorDefinitionsResponse =
  GetAgentEvaluatorDefinitionsResponses[keyof GetAgentEvaluatorDefinitionsResponses];

export type GetKnowledgeBaseEvaluatorDefinitionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: "/evaluator-definitions/knowledge_base";
};

export type GetKnowledgeBaseEvaluatorDefinitionsResponses = {
  /**
   * Response Get Knowledge Base Evaluator Definitions
   *
   * Successful Response
   */
  200: Array<EvaluatorDefinition>;
};

export type GetKnowledgeBaseEvaluatorDefinitionsResponse =
  GetKnowledgeBaseEvaluatorDefinitionsResponses[keyof GetKnowledgeBaseEvaluatorDefinitionsResponses];

export type ListWorkflowTestSuitesData = {
  body?: never;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Workflow Version Id
     *
     * Filter stats by version
     */
    workflow_version_id?: string | null;
  };
  url: "/groups/{group_id}/workflows/{workflow_id}/test-suites";
};

export type ListWorkflowTestSuitesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListWorkflowTestSuitesError =
  ListWorkflowTestSuitesErrors[keyof ListWorkflowTestSuitesErrors];

export type ListWorkflowTestSuitesResponses = {
  /**
   * Response List Workflow Test Suites
   *
   * Successful Response
   */
  200: Array<TestSuiteWithStats>;
};

export type ListWorkflowTestSuitesResponse =
  ListWorkflowTestSuitesResponses[keyof ListWorkflowTestSuitesResponses];

export type CreateWorkflowTestSuiteData = {
  body: TestSuiteCreate;
  path: {
    /**
     * Workflow Id
     */
    workflow_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/workflows/{workflow_id}/test-suites";
};

export type CreateWorkflowTestSuiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateWorkflowTestSuiteError =
  CreateWorkflowTestSuiteErrors[keyof CreateWorkflowTestSuiteErrors];

export type CreateWorkflowTestSuiteResponses = {
  /**
   * Successful Response
   */
  200: TestSuiteResponse;
};

export type CreateWorkflowTestSuiteResponse =
  CreateWorkflowTestSuiteResponses[keyof CreateWorkflowTestSuiteResponses];

export type DeleteTestSuiteData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}";
};

export type DeleteTestSuiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTestSuiteError =
  DeleteTestSuiteErrors[keyof DeleteTestSuiteErrors];

export type DeleteTestSuiteResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetTestSuiteData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Workflow Version Id
     *
     * Filter stats by version
     */
    workflow_version_id?: string | null;
  };
  url: "/groups/{group_id}/test-suites/{test_suite_id}";
};

export type GetTestSuiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTestSuiteError = GetTestSuiteErrors[keyof GetTestSuiteErrors];

export type GetTestSuiteResponses = {
  /**
   * Successful Response
   */
  200: TestSuiteWithStats;
};

export type GetTestSuiteResponse =
  GetTestSuiteResponses[keyof GetTestSuiteResponses];

export type UpdateTestSuiteData = {
  body: TestSuiteUpdate;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}";
};

export type UpdateTestSuiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTestSuiteError =
  UpdateTestSuiteErrors[keyof UpdateTestSuiteErrors];

export type UpdateTestSuiteResponses = {
  /**
   * Successful Response
   */
  200: TestSuiteResponse;
};

export type UpdateTestSuiteResponse =
  UpdateTestSuiteResponses[keyof UpdateTestSuiteResponses];

export type ListAgentTestSuitesData = {
  body?: never;
  path: {
    /**
     * Agent Id
     */
    agent_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Agent Version Id
     *
     * Filter stats by version
     */
    agent_version_id?: string | null;
  };
  url: "/groups/{group_id}/agents/{agent_id}/test-suites";
};

export type ListAgentTestSuitesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListAgentTestSuitesError =
  ListAgentTestSuitesErrors[keyof ListAgentTestSuitesErrors];

export type ListAgentTestSuitesResponses = {
  /**
   * Response List Agent Test Suites
   *
   * Successful Response
   */
  200: Array<TestSuiteWithStats>;
};

export type ListAgentTestSuitesResponse =
  ListAgentTestSuitesResponses[keyof ListAgentTestSuitesResponses];

export type CreateAgentTestSuiteData = {
  body: TestSuiteCreate;
  path: {
    /**
     * Agent Id
     */
    agent_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/agents/{agent_id}/test-suites";
};

export type CreateAgentTestSuiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateAgentTestSuiteError =
  CreateAgentTestSuiteErrors[keyof CreateAgentTestSuiteErrors];

export type CreateAgentTestSuiteResponses = {
  /**
   * Successful Response
   */
  200: TestSuiteResponse;
};

export type CreateAgentTestSuiteResponse =
  CreateAgentTestSuiteResponses[keyof CreateAgentTestSuiteResponses];

export type ListKbTestSuitesData = {
  body?: never;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/knowledge-bases/{knowledge_base_id}/test-suites";
};

export type ListKbTestSuitesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListKbTestSuitesError =
  ListKbTestSuitesErrors[keyof ListKbTestSuitesErrors];

export type ListKbTestSuitesResponses = {
  /**
   * Response List Kb Test Suites
   *
   * Successful Response
   */
  200: Array<TestSuiteWithStats>;
};

export type ListKbTestSuitesResponse =
  ListKbTestSuitesResponses[keyof ListKbTestSuitesResponses];

export type CreateKbTestSuiteData = {
  body: KbTestSuiteCreate;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/knowledge-bases/{knowledge_base_id}/test-suites";
};

export type CreateKbTestSuiteErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateKbTestSuiteError =
  CreateKbTestSuiteErrors[keyof CreateKbTestSuiteErrors];

export type CreateKbTestSuiteResponses = {
  /**
   * Successful Response
   */
  200: TestSuiteResponse;
};

export type CreateKbTestSuiteResponse =
  CreateKbTestSuiteResponses[keyof CreateKbTestSuiteResponses];

export type StartKbEvaluationRunData = {
  body: KbEvaluationRunRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/kb-run";
};

export type StartKbEvaluationRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartKbEvaluationRunError =
  StartKbEvaluationRunErrors[keyof StartKbEvaluationRunErrors];

export type StartKbEvaluationRunResponses = {
  /**
   * Successful Response
   */
  200: KbEvaluationRunResponse;
};

export type StartKbEvaluationRunResponse =
  StartKbEvaluationRunResponses[keyof StartKbEvaluationRunResponses];

export type GenerateKbSyntheticDatasetData = {
  body: KbSyntheticDatasetRequest;
  path: {
    /**
     * Knowledge Base Id
     */
    knowledge_base_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/knowledge-bases/{knowledge_base_id}/synthetic-datasets";
};

export type GenerateKbSyntheticDatasetErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateKbSyntheticDatasetError =
  GenerateKbSyntheticDatasetErrors[keyof GenerateKbSyntheticDatasetErrors];

export type GenerateKbSyntheticDatasetResponses = {
  /**
   * Successful Response
   */
  200: KbSyntheticDatasetResponse;
};

export type GenerateKbSyntheticDatasetResponse =
  GenerateKbSyntheticDatasetResponses[keyof GenerateKbSyntheticDatasetResponses];

export type GetKbEvaluationComparisonData = {
  body?: never;
  path: {
    /**
     * Evaluation Run Id
     */
    evaluation_run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/kb-evaluation-runs/{evaluation_run_id}/comparison";
};

export type GetKbEvaluationComparisonErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetKbEvaluationComparisonError =
  GetKbEvaluationComparisonErrors[keyof GetKbEvaluationComparisonErrors];

export type GetKbEvaluationComparisonResponses = {
  /**
   * Successful Response
   */
  200: RunComparison;
};

export type GetKbEvaluationComparisonResponse =
  GetKbEvaluationComparisonResponses[keyof GetKbEvaluationComparisonResponses];

export type GenerateAnswerQualityDatasetData = {
  body: AnswerQualityDatasetRequest;
  path: {
    /**
     * Agent Id
     */
    agent_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/agents/{agent_id}/answer-quality-datasets";
};

export type GenerateAnswerQualityDatasetErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GenerateAnswerQualityDatasetError =
  GenerateAnswerQualityDatasetErrors[keyof GenerateAnswerQualityDatasetErrors];

export type GenerateAnswerQualityDatasetResponses = {
  /**
   * Successful Response
   */
  200: AnswerQualityDatasetResponse;
};

export type GenerateAnswerQualityDatasetResponse =
  GenerateAnswerQualityDatasetResponses[keyof GenerateAnswerQualityDatasetResponses];

export type GetAnswerQualitySummaryData = {
  body?: never;
  path: {
    /**
     * Evaluation Run Id
     */
    evaluation_run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Tolerance
     */
    tolerance?: number;
  };
  url: "/groups/{group_id}/evaluation-runs/{evaluation_run_id}/answer-quality-summary";
};

export type GetAnswerQualitySummaryErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetAnswerQualitySummaryError =
  GetAnswerQualitySummaryErrors[keyof GetAnswerQualitySummaryErrors];

export type GetAnswerQualitySummaryResponses = {
  /**
   * Successful Response
   */
  200: AnswerQualityRunSummary;
};

export type GetAnswerQualitySummaryResponse =
  GetAnswerQualitySummaryResponses[keyof GetAnswerQualitySummaryResponses];

export type GetCombinedReportData = {
  body?: never;
  path: {
    /**
     * Evaluation Run Id
     */
    evaluation_run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query: {
    /**
     * Retrieval Run Id
     */
    retrieval_run_id: string;
    /**
     * Retrieval Config Hash
     *
     * Select one config from a multi-config retrieval sweep
     */
    retrieval_config_hash?: string | null;
  };
  url: "/groups/{group_id}/evaluation-runs/{evaluation_run_id}/combined-report";
};

export type GetCombinedReportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetCombinedReportError =
  GetCombinedReportErrors[keyof GetCombinedReportErrors];

export type GetCombinedReportResponses = {
  /**
   * Successful Response
   */
  200: CombinedReport;
};

export type GetCombinedReportResponse =
  GetCombinedReportResponses[keyof GetCombinedReportResponses];

export type SetTestSuiteBaselineData = {
  body: SetBaselineRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/baseline";
};

export type SetTestSuiteBaselineErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type SetTestSuiteBaselineError =
  SetTestSuiteBaselineErrors[keyof SetTestSuiteBaselineErrors];

export type SetTestSuiteBaselineResponses = {
  /**
   * Successful Response
   */
  200: TestSuiteResponse;
};

export type SetTestSuiteBaselineResponse =
  SetTestSuiteBaselineResponses[keyof SetTestSuiteBaselineResponses];

export type ListEvaluationRunsData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Limit
     */
    limit?: number | null;
  };
  url: "/groups/{group_id}/test-suites/{test_suite_id}/evaluation-runs";
};

export type ListEvaluationRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListEvaluationRunsError =
  ListEvaluationRunsErrors[keyof ListEvaluationRunsErrors];

export type ListEvaluationRunsResponses = {
  /**
   * Response List Evaluation Runs
   *
   * Successful Response
   */
  200: Array<EvaluationRunListItem>;
};

export type ListEvaluationRunsResponse =
  ListEvaluationRunsResponses[keyof ListEvaluationRunsResponses];

export type GetEvaluationRunReportData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Evaluation Run Id
     */
    evaluation_run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/evaluation-runs/{evaluation_run_id}/report";
};

export type GetEvaluationRunReportErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEvaluationRunReportError =
  GetEvaluationRunReportErrors[keyof GetEvaluationRunReportErrors];

export type GetEvaluationRunReportResponses = {
  /**
   * Successful Response
   */
  200: EvaluationRunReport;
};

export type GetEvaluationRunReportResponse =
  GetEvaluationRunReportResponses[keyof GetEvaluationRunReportResponses];

export type ListEvaluatorsData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Test Case Id
     *
     * Only the checks of this agent test
     */
    test_case_id?: string | null;
  };
  url: "/groups/{group_id}/test-suites/{test_suite_id}/evaluators";
};

export type ListEvaluatorsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListEvaluatorsError =
  ListEvaluatorsErrors[keyof ListEvaluatorsErrors];

export type ListEvaluatorsResponses = {
  /**
   * Response List Evaluators
   *
   * Successful Response
   */
  200: Array<EvaluatorResponse>;
};

export type ListEvaluatorsResponse =
  ListEvaluatorsResponses[keyof ListEvaluatorsResponses];

export type CreateEvaluatorData = {
  body: CreateEvaluatorRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/evaluators";
};

export type CreateEvaluatorErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateEvaluatorError =
  CreateEvaluatorErrors[keyof CreateEvaluatorErrors];

export type CreateEvaluatorResponses = {
  /**
   * Successful Response
   */
  200: EvaluatorResponse;
};

export type CreateEvaluatorResponse =
  CreateEvaluatorResponses[keyof CreateEvaluatorResponses];

export type DeleteEvaluatorData = {
  body?: never;
  path: {
    /**
     * Evaluator Id
     */
    evaluator_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/evaluators/{evaluator_id}";
};

export type DeleteEvaluatorErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteEvaluatorError =
  DeleteEvaluatorErrors[keyof DeleteEvaluatorErrors];

export type DeleteEvaluatorResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetEvaluatorData = {
  body?: never;
  path: {
    /**
     * Evaluator Id
     */
    evaluator_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/evaluators/{evaluator_id}";
};

export type GetEvaluatorErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEvaluatorError = GetEvaluatorErrors[keyof GetEvaluatorErrors];

export type GetEvaluatorResponses = {
  /**
   * Successful Response
   */
  200: EvaluatorResponse;
};

export type GetEvaluatorResponse =
  GetEvaluatorResponses[keyof GetEvaluatorResponses];

export type UpdateEvaluatorData = {
  body: UpdateEvaluatorRequest;
  path: {
    /**
     * Evaluator Id
     */
    evaluator_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/evaluators/{evaluator_id}";
};

export type UpdateEvaluatorErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateEvaluatorError =
  UpdateEvaluatorErrors[keyof UpdateEvaluatorErrors];

export type UpdateEvaluatorResponses = {
  /**
   * Successful Response
   */
  200: EvaluatorResponse;
};

export type UpdateEvaluatorResponse =
  UpdateEvaluatorResponses[keyof UpdateEvaluatorResponses];

export type ListTestCasesData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Name
     *
     * Filter by name
     */
    name?: string | null;
    /**
     * Version Id
     *
     * Version ID for outdated check
     */
    version_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/test-suites/{test_suite_id}/test-cases";
};

export type ListTestCasesErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListTestCasesError = ListTestCasesErrors[keyof ListTestCasesErrors];

export type ListTestCasesResponses = {
  /**
   * Successful Response
   */
  200: PageTestCaseWEvals;
};

export type ListTestCasesResponse =
  ListTestCasesResponses[keyof ListTestCasesResponses];

export type CreateTestCaseData = {
  body: CreateTestCaseRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/test-cases";
};

export type CreateTestCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTestCaseError =
  CreateTestCaseErrors[keyof CreateTestCaseErrors];

export type CreateTestCaseResponses = {
  /**
   * Successful Response
   */
  200: TestCaseResponse;
};

export type CreateTestCaseResponse =
  CreateTestCaseResponses[keyof CreateTestCaseResponses];

export type DeleteTestCaseData = {
  body?: never;
  path: {
    /**
     * Test Case Id
     */
    test_case_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-cases/{test_case_id}";
};

export type DeleteTestCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeleteTestCaseError =
  DeleteTestCaseErrors[keyof DeleteTestCaseErrors];

export type DeleteTestCaseResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetTestCaseData = {
  body?: never;
  path: {
    /**
     * Test Case Id
     */
    test_case_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-cases/{test_case_id}";
};

export type GetTestCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTestCaseError = GetTestCaseErrors[keyof GetTestCaseErrors];

export type GetTestCaseResponses = {
  /**
   * Successful Response
   */
  200: TestCaseWEvals;
};

export type GetTestCaseResponse =
  GetTestCaseResponses[keyof GetTestCaseResponses];

export type UpdateTestCaseData = {
  body: UpdateTestCaseRequest;
  path: {
    /**
     * Test Case Id
     */
    test_case_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-cases/{test_case_id}";
};

export type UpdateTestCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdateTestCaseError =
  UpdateTestCaseErrors[keyof UpdateTestCaseErrors];

export type UpdateTestCaseResponses = {
  /**
   * Successful Response
   */
  200: TestCaseResponse;
};

export type UpdateTestCaseResponse =
  UpdateTestCaseResponses[keyof UpdateTestCaseResponses];

export type DuplicateTestCaseData = {
  body?: never;
  path: {
    /**
     * Test Case Id
     */
    test_case_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-cases/{test_case_id}/duplicate";
};

export type DuplicateTestCaseErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DuplicateTestCaseError =
  DuplicateTestCaseErrors[keyof DuplicateTestCaseErrors];

export type DuplicateTestCaseResponses = {
  /**
   * Successful Response
   */
  200: TestCaseResponse;
};

export type DuplicateTestCaseResponse =
  DuplicateTestCaseResponses[keyof DuplicateTestCaseResponses];

export type GetTestCaseRunsData = {
  body?: never;
  path: {
    /**
     * Test Case Id
     */
    test_case_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-cases/{test_case_id}/runs";
};

export type GetTestCaseRunsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetTestCaseRunsError =
  GetTestCaseRunsErrors[keyof GetTestCaseRunsErrors];

export type GetTestCaseRunsResponses = {
  /**
   * Response Get Test Case Runs
   *
   * Successful Response
   */
  200: Array<TestCaseRunResponse>;
};

export type GetTestCaseRunsResponse =
  GetTestCaseRunsResponses[keyof GetTestCaseRunsResponses];

export type CreateTestCaseFromRunData = {
  body: CreateTestCaseFromRunRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Run Id
     */
    run_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/test-cases/from-run/{run_id}";
};

export type CreateTestCaseFromRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTestCaseFromRunError =
  CreateTestCaseFromRunErrors[keyof CreateTestCaseFromRunErrors];

export type CreateTestCaseFromRunResponses = {
  /**
   * Successful Response
   */
  200: TestCaseResponse;
};

export type CreateTestCaseFromRunResponse =
  CreateTestCaseFromRunResponses[keyof CreateTestCaseFromRunResponses];

export type CreateTestCaseFromMessageData = {
  body: CreateTestCaseFromMessageRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Conversation Id
     */
    conversation_id: string;
    /**
     * Message Id
     */
    message_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/test-cases/from-conversation/{conversation_id}/message/{message_id}";
};

export type CreateTestCaseFromMessageErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreateTestCaseFromMessageError =
  CreateTestCaseFromMessageErrors[keyof CreateTestCaseFromMessageErrors];

export type CreateTestCaseFromMessageResponses = {
  /**
   * Successful Response
   */
  200: TestCaseResponse;
};

export type CreateTestCaseFromMessageResponse =
  CreateTestCaseFromMessageResponses[keyof CreateTestCaseFromMessageResponses];

export type GetEvaluationResultData = {
  body?: never;
  path: {
    /**
     * Evaluation Result Id
     */
    evaluation_result_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/evaluation-results/{evaluation_result_id}";
};

export type GetEvaluationResultErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEvaluationResultError =
  GetEvaluationResultErrors[keyof GetEvaluationResultErrors];

export type GetEvaluationResultResponses = {
  /**
   * Successful Response
   */
  200: EvaluatorResultResponse;
};

export type GetEvaluationResultResponse =
  GetEvaluationResultResponses[keyof GetEvaluationResultResponses];

export type GetEvaluationResultDetailData = {
  body?: never;
  path: {
    /**
     * Evaluation Result Id
     */
    evaluation_result_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/evaluation-results/{evaluation_result_id}/detail";
};

export type GetEvaluationResultDetailErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetEvaluationResultDetailError =
  GetEvaluationResultDetailErrors[keyof GetEvaluationResultDetailErrors];

export type GetEvaluationResultDetailResponses = {
  /**
   * Successful Response
   */
  200: EvaluationResultDetailResponse;
};

export type GetEvaluationResultDetailResponse =
  GetEvaluationResultDetailResponses[keyof GetEvaluationResultDetailResponses];

export type GetSuiteEvaluationResultsData = {
  body?: never;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Workflow Version Id
     *
     * Filter by version
     */
    workflow_version_id?: string | null;
    /**
     * Page
     *
     * Page number
     */
    page?: number;
    /**
     * Size
     *
     * Page size
     */
    size?: number;
  };
  url: "/groups/{group_id}/test-suites/{test_suite_id}/evaluation-results";
};

export type GetSuiteEvaluationResultsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetSuiteEvaluationResultsError =
  GetSuiteEvaluationResultsErrors[keyof GetSuiteEvaluationResultsErrors];

export type GetSuiteEvaluationResultsResponses = {
  /**
   * Successful Response
   */
  200: PageEvaluatorResultResponse;
};

export type GetSuiteEvaluationResultsResponse =
  GetSuiteEvaluationResultsResponses[keyof GetSuiteEvaluationResultsResponses];

export type StartEvaluationRunData = {
  body: StartEvaluationRunRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/run-evaluation";
};

export type StartEvaluationRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type StartEvaluationRunError =
  StartEvaluationRunErrors[keyof StartEvaluationRunErrors];

export type StartEvaluationRunResponses = {
  /**
   * Successful Response
   */
  200: StartedEvaluation;
};

export type StartEvaluationRunResponse =
  StartEvaluationRunResponses[keyof StartEvaluationRunResponses];

export type CancelEvaluationRunData = {
  body: CancelEvaluationRunRequest;
  path: {
    /**
     * Test Suite Id
     */
    test_suite_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/test-suites/{test_suite_id}/cancel-evaluation";
};

export type CancelEvaluationRunErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CancelEvaluationRunError =
  CancelEvaluationRunErrors[keyof CancelEvaluationRunErrors];

export type CancelEvaluationRunResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type ReceiveEmailData = {
  body?: BodyReceiveEmail;
  headers?: {
    /**
     * Host
     */
    host?: string | null;
  };
  path?: never;
  query?: {
    /**
     * Token
     */
    token?: string | null;
  };
  url: "/inbox/webhook";
};

export type ReceiveEmailErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ReceiveEmailError = ReceiveEmailErrors[keyof ReceiveEmailErrors];

export type ReceiveEmailResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type GetPresetConfigFormData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Mode
     */
    mode?: "create" | "edit";
  };
  url: "/groups/{group_id}/models/presets/config-form";
};

export type GetPresetConfigFormErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPresetConfigFormError =
  GetPresetConfigFormErrors[keyof GetPresetConfigFormErrors];

export type GetPresetConfigFormResponses = {
  /**
   * Response Get Preset Config Form
   *
   * Successful Response
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetPresetConfigFormResponse =
  GetPresetConfigFormResponses[keyof GetPresetConfigFormResponses];

export type ListPresetsData = {
  body?: never;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: {
    /**
     * Include Inactive
     */
    include_inactive?: boolean;
  };
  url: "/groups/{group_id}/models/presets";
};

export type ListPresetsErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ListPresetsError = ListPresetsErrors[keyof ListPresetsErrors];

export type ListPresetsResponses = {
  /**
   * Response List Presets
   *
   * Successful Response
   */
  200: Array<PresetResponse>;
};

export type ListPresetsResponse =
  ListPresetsResponses[keyof ListPresetsResponses];

export type CreatePresetData = {
  body: PresetCreateRequest;
  path: {
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/presets";
};

export type CreatePresetErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type CreatePresetError = CreatePresetErrors[keyof CreatePresetErrors];

export type CreatePresetResponses = {
  /**
   * Successful Response
   */
  200: PresetResponse;
};

export type CreatePresetResponse =
  CreatePresetResponses[keyof CreatePresetResponses];

export type GetPresetByHandleData = {
  body?: never;
  path: {
    /**
     * Handle
     */
    handle: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/presets/{handle}";
};

export type GetPresetByHandleErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type GetPresetByHandleError =
  GetPresetByHandleErrors[keyof GetPresetByHandleErrors];

export type GetPresetByHandleResponses = {
  /**
   * Successful Response
   */
  200: PresetResponse;
};

export type GetPresetByHandleResponse =
  GetPresetByHandleResponses[keyof GetPresetByHandleResponses];

export type DeletePresetData = {
  body?: never;
  path: {
    /**
     * Preset Id
     */
    preset_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/presets/{preset_id}";
};

export type DeletePresetErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type DeletePresetError = DeletePresetErrors[keyof DeletePresetErrors];

export type DeletePresetResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};

export type UpdatePresetData = {
  body: PresetUpdateRequest;
  path: {
    /**
     * Preset Id
     */
    preset_id: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/presets/{preset_id}";
};

export type UpdatePresetErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type UpdatePresetError = UpdatePresetErrors[keyof UpdatePresetErrors];

export type UpdatePresetResponses = {
  /**
   * Successful Response
   */
  200: PresetResponse;
};

export type UpdatePresetResponse =
  UpdatePresetResponses[keyof UpdatePresetResponses];

export type ResetPresetToDefaultData = {
  body?: never;
  path: {
    /**
     * Handle
     */
    handle: string;
    /**
     * Group Id
     */
    group_id: string;
  };
  query?: never;
  url: "/groups/{group_id}/models/presets/{handle}/reset";
};

export type ResetPresetToDefaultErrors = {
  /**
   * Validation Error
   */
  422: HttpValidationError;
};

export type ResetPresetToDefaultError =
  ResetPresetToDefaultErrors[keyof ResetPresetToDefaultErrors];

export type ResetPresetToDefaultResponses = {
  /**
   * Successful Response
   */
  200: unknown;
};
